import { VSS_TRANSLATION_KEYS, vssTranslations } from "./vssTranslations";

// Supported languages metadata for VBLive.
// Keys are language codes; values provide the display label and flag emoji.
export const LANGUAGES = {
  en: { label: "English", flag: "🇬🇧" },
  no: { label: "Norsk", flag: "🇳🇴" },
  de: { label: "Deutsch", flag: "🇩🇪" },
  fr: { label: "Français", flag: "🇫🇷" },
  nl: { label: "Nederlands", flag: "🇳🇱" },
  it: { label: "Italiano", flag: "🇮🇹" },
  es: { label: "Español", flag: "🇪🇸" },
  ko: { label: "한국어", flag: "🇰🇷" },
  ja: { label: "日本語", flag: "🇯🇵" },
  zh: { label: "中文", flag: "🇨🇳" },
  vi: { label: "Tiếng Việt", flag: "🇻🇳" },
  ru: { label: "Русский", flag: "🇷🇺" },
  pt: { label: "Português (BR)", flag: "🇧🇷" },
  pl: { label: "Polski", flag: "🇵🇱" },
  th: { label: "ภาษาไทย", flag: "🇹🇭" },
  tr: { label: "Türkçe", flag: "🇹🇷" },
  id: { label: "Bahasa Indonesia", flag: "🇮🇩" },
};

/**
 * Translation dictionary.
 * Keys are the English strings (used as the default/fallback).
 * Only non-English languages need to be defined here; anything missing
 * falls back to the English key itself.
 *
 * Users can build additional languages with the in-app Translation editor,
 * which stores custom language packages locally and (optionally) in Firestore.
 */
export const translations = {
  zh: {
    // Information page
    Information: "信息",
    "Release notes": "发布说明",
    "Contact us": "联系我们",
    "Questions, feedback or suggestions are most welcome.": "欢迎提出问题、反馈或建议。",
    // DV match coding
    Lineups: "阵容",
    Point: "得分",
    Video: "视频",
    Defaults: "默认设置",
    "[team][NN][skill][type?][eval][zoneFrom?[szFrom?][zoneTo?[szTo?]]]": "[队][号码][技术][类型?][评价][起始区?[起始子区?][目标区?[目标子区?]]]",
    Vertical: "垂直",
    Horizontal: "水平",
    "Swap sides": "交换场地",
    Subs: "换人",
    Substitutions: "换人",
    Court: "球场",
    Home: "主队",
    Away: "客队",
    Games: "局数",
    Enter: "确定",
    Undo: "撤销",
    "Match saved": "比赛已保存",
    "Please sign in to save this match.": "请登录以保存此比赛。",
    "Nothing to save yet.": "暂无可保存的内容。",
    "Could not save the match. Please try again.": "无法保存比赛，请重试。",
    Discard: "放弃更改",
    "Resumed autosaved coding": "已恢复自动保存的记录",
    "Changes discarded": "更改已放弃",
    "Discard all changes and revert to the starting point?":
      "放弃所有更改并恢复到起始状态？",
    Clear: "清除",
    "Prev set": "上一局",
    "Next set": "下一局",
    "Match Setup": "比赛设置",
    "First serve": "首发球",
    "Start Match": "开始比赛",
    "Resume Match": "继续比赛",
    Skip: "跳过",
    "Select an event then drag to draw ball path": "选择一个事件然后拖动以绘制球路",
    "Drag on court to set ball path": "在球场上拖动以设置球路",
    "No events yet – enter a DV code above and press Enter.": "暂无事件 – 在上方输入 DV 代码并按确定。",
    "DataVolley code reference": "DataVolley 代码参考",
    Format: "格式",
    Skills: "技术",
    Evaluation: "评价",
    "Skill / type omission": "技术 / 类型省略",
    "Skill and/or type may be omitted; defaults fill in the rest.": "可省略技术和/或类型；其余由默认值填充。",
    "Dot-coding (S→R and A→B pairs)": "点连编码（S→R 和 A→B 配对）",
    "Chaining & aliases": "串联与别名",
    "team:": "队伍：",
    "omit → defaults to A": "省略 → 默认为 A",
    "NN: 1 or 2 digit jersey, e.g. 1, 01, 12": "NN：1 或 2 位球衣号码，例如 1、01、12",
    "sub-zone: a b c d appended to zone digit, e.g. 5b or 5b1a": "子区域：a b c d 附加在区域数字后，例如 5b 或 5b1a",
    "S Serve · R Reception · E Set · A Attack · B Block · D Dig · F Freeball": "S 发球 · R 接发 · E 二传 · A 进攻 · B 拦网 · D 防守 · F 自由球",
    "# kill/ace · + positive · ! ok · - negative · / overpass · = error": "# 得分/直接得分 · + 正面 · ! 一般 · - 负面 · / 过网 · = 失误",
    "E.g.": "例如",
    "serve with skill=A (default), type=H, eval=+, zone=5.": "发球，skill=A（默认），type=H，eval=+，zone=5。",
    "type-only (no skill marker), defaults skill from context.": "仅类型（无技术标记），技术由上下文默认。",
    "serve coded, reception auto-linked.": "发球已编码，接发自动关联。",
    "attack coded, block auto-linked.": "进攻已编码，拦网自动关联。",
    "Dot connects the reception eval to the corresponding serve eval via the serve→receive mapping.": "点号通过发球→接发映射将接发评价与对应的发球评价关联。",
    "two events in one string.": "一个字符串中包含两个事件。",
    "manual point A/B.": "手动得分 A/B。",
    "serve ace/error.": "发球直接得分/失误。",
    "serve with eval.": "带评价的发球。",
    // Stats, sideouts, filters & video tooltips
    "Block Kills": "拦网得分",
    "Side Out Percentage": "一攻成功率",
    "All First Ball": "全部第一球",
    "First Ball Kills": "第一球扣球得分",
    "Good Serve": "到位发球",
    "Break Points": "破发得分",
    Game: "局",
    Row: "轮",
    "First Ball Sideouts": "第一球一攻",
    "All Sideouts": "全部一攻",
    All: "全部",
    None: "无",
    Receiver: "接发球员",
    "# Passes": "接发数",
    "Perfect %": "完美率",
    "Positive %": "到位率",
    "Sideout %": "一攻率",
    "FB Sideout %": "第一球一攻率",
    "Pass Hits": "接发后进攻",
    "Pass Hit Kills": "接发后扣球得分",
    "Pass Hit Errors": "接发后扣球失误",
    "Show Passes": "显示接发",
    "Show Attacks": "显示进攻",
    "Error Pass": "接发失误",
    "1 Pass": "一级接发",
    "2 Pass": "二级接发",
    "Perfect Pass": "完美接发",
    "Successful SO": "一攻成功",
    "First Ball SO": "第一球一攻",
    "Unsuccessful SO": "一攻失败",
    RECEIVES: "接发",
    Sets: "局次",
    Rotations: "轮次",
    "Attack Combos": "进攻战术",
    Stages: "阶段",
    Display: "显示",
    Setters: "二传",
    Attackers: "进攻手",
    Results: "结果",
    Source: "来源",
    Cones: "落点",
    "Select None": "取消选择",
    "All Serves": "全部发球",
    "All Returns": "全部接发",
    Filters: "筛选",
    "Reset all filters": "重置所有筛选",
    "Show video clips of filtered attacks": "显示筛选进攻的视频片段",
    "Spikes for the selected player": "所选球员的扣球",
    "Click on the zones to view video of attacks": "点击区域查看进攻视频",
    "Expand All": "全部展开",
    "Collapse All": "全部折叠",
    "Import Radar File": "导入 Radar 文件",
    "Select local video file": "选择本地视频文件",
    "Save to database": "保存到数据库",
    "Load and play online video": "加载并播放在线视频",
    "Synchronise video with events": "将视频与事件同步",
    "Rewind 10 seconds": "后退10秒",
    "Rewind 1 second": "后退1秒",
    "Forward 1 second": "前进1秒",
    "Forward 10 seconds": "前进10秒",
    "Set timing for events": "为事件设置时间",

    // Navigation
    Matches: "比赛",
    Live: "直播",
    Import: "导入",
    Playlists: "播放列表",
    "New Match": "新建比赛",
    // New Match / Match Coding gate
    "Match Coding": "比赛记录",
    "Match Coding — Coming Soon": "比赛记录 — 即将推出",
    "Live match coding is on the way. Here's a preview of what's coming.":
      "实时比赛记录即将上线。以下是即将推出功能的预览。",
    Settings: "设置",
    Language: "语言",
    "Your profile": "个人资料",
    "Sign out": "退出登录",
    "Translation Editor": "翻译编辑器",

    // Common actions
    Save: "保存",
    Cancel: "取消",
    Delete: "删除",
    Share: "分享",
    Edit: "编辑",
    New: "新建",
    Refresh: "刷新",
    Search: "搜索",
    Reset: "重置",
    Close: "关闭",
    "Sub for": "替换",
    "No players available": "没有可用球员",
    "Show full roster": "显示全部名单",
    "Available only": "仅可用球员",
    "Maximum substitutions reached": "已达最大换人次数",
    Back: "返回",
    "Download PDF": "下载 PDF",
    "Loading...": "加载中…",
    "Select All": "全选",
    "Unselect All": "取消全选",
    Yes: "是",
    No: "否",
    Add: "添加",
    Load: "加载",
    Confirm: "确认",
    Open: "打开",
    show: "显示",

    // Auth
    "Sign In": "登录",
    "Sign Up": "注册",
    "Sign Out": "退出",
    Email: "电子邮箱",
    Password: "密码",
    "Confirm Password": "确认密码",
    "Forgot password?": "忘记密码？",
    "Forgot Password": "忘记密码",
    "Don't have an account?": "还没有账户？",
    "Sign up for free!": "免费注册！",
    "All ready signed up?": "已经注册了？",
    "Error Signing In": "登录出错",
    "Signed up successfully": "注册成功",
    "Error signing up": "注册出错",
    "Enter your email": "请输入您的电子邮箱",
    "Send Reset Email": "发送重置邮件",
    "Email was sent to": "邮件已发送至",
    "Could not send reset email": "无法发送重置邮件",

    // Matches list
    "Team 1": "队伍 1",
    "Team 2": "队伍 2",
    Tournament: "赛事",
    Scores: "比分",
    Date: "日期",
    Actions: "操作",
    Owner: "所有者",
    Description: "描述",
    Comments: "备注",
    Tags: "标签",
    "Add player": "添加球员",
    "Add team": "添加球队",
    "Edit playlist": "编辑播放列表",
    "Merge Teams": "合并球队",
    "Roster differences detected between teams:": "检测到球队之间的阵容差异：",
    "Review the roster in the next step before merging.": "在合并之前，请在下一步检查阵容。",
    "At least 2 team records must exist in your Teams list for the selected matches.": "所选比赛至少需要 2 支球队记录存在于您的球队列表中。",
    "Merge...": "合并...",
    "Select 2 or more team records to merge into one.": "选择 2 个或更多球队记录合并为一个。",
    "Filter teams...": "筛选球队...",
    "teams selected": "支球队已选择",
    "Select Teams": "选择球队",
    "Reconcile Roster": "整合阵容",
    "Confirm Merge": "确认合并",
    "Merged team name": "合并后的球队名称",
    "shirt number(s) have conflicting names — review below.": "个号码有名称冲突，请在下方检查。",
    "Shirt": "球衣号",
    "players included": "名球员已包含",
    "No players to reconcile.": "没有需要整合的球员。",
    "Use this name": "使用此名称",
    "Create merged team": "创建合并球队",
    "with": "共",
    "duplicate team records": "个重复球队记录",
    "Consolidate duplicate player records and reassign playlist links": "合并重复球员记录并重新分配播放列表链接",
    "This cannot be undone.": "此操作无法撤销。",
    "Teams being merged:": "正在合并的球队：",
    "Step": "步骤",
    "of": "共",
    "Merging...": "合并中...",
    "Players & Teams": "球员与球队",
    "No players or teams assigned.": "未关联球员或球队。",
    "Search players...": "搜索球员...",
    "Search teams...": "搜索球队...",
    "Saving...": "保存中...",
    "My Matches": "我的比赛",
    "Shared Matches": "共享比赛",
    "Search team...": "搜索队伍…",
    "All Teams": "所有队伍",
    "Multi Match Reports": "多场比赛报告",
    "Delete match": "删除比赛",
    "Remove from my shared matches": "从我的共享比赛中移除",
    "Share match": "分享比赛",
    "Edit match": "编辑比赛",
    "Open video analysis": "打开视频分析",
    "Delete Match": "删除比赛",
    "Are you sure you want to delete the selected match?":
      "确定要删除所选的比赛吗？",
    "No matches selected.": "未选择任何比赛。",

    // New Match
    "← Back": "← 返回",
    "Team A (Home)": "A 队（主场）",
    "Team B (Away)": "B 队（客场）",
    "Select team…": "选择队伍…",
    "Tournament (optional)": "赛事（可选）",
    "Tournament / competition name": "赛事 / 比赛名称",
    "Match date": "比赛日期",
    "Start Coding →": "开始记录 →",

    // Reports
    Summary: "总览",
    "Box Score": "数据统计表",
    "Side-outs Report": "一攻报告",
    "Serve Receives": "接发球",
    "Attack Zones": "进攻区域",
    "Hitting Charts": "扣球图表",
    "Video Analysis": "视频分析",
    Dashboard: "仪表盘",
    Charts: "图表",
    "AI Report": "AI 报告",
    // AI Reports page
    "AI Reports": "AI 报告",
    "AI Reports — Coming Soon": "AI 报告 — 即将推出",
    "AI-powered match reports and deep analysis are on the way. Here are some sample reports to show what's coming.":
      "AI 驱动的比赛报告和深度分析即将上线。以下是一些示例报告，先睹为快。",
    "Preview not available.": "无法预览。",
    "Open sample": "打开示例",
    "No matches were loaded. Select one or more matches and try again.":
      "未加载任何比赛。请选择一场或多场比赛后重试。",
    "Attack Analysis (English)": "进攻分析（英文）",
    "Blocking Analysis (Vietnamese)": "拦网分析（越南文）",
    "Opponent Tendencies (Japanese)": "对手倾向（日文）",
    // Deep AI analysis
    "Deep AI Analysis": "深度 AI 分析",
    "AI Deep Analysis": "AI 深度分析",
    "Load at least one match with coded events to generate the analysis.": "加载至少一场带有编码事件的比赛以生成分析。",
    "Run the deep analysis one section at a time to keep each request small. Edit or add questions below, then generate. Best used with the Anthropic (Claude) provider.":
      "逐个部分运行深度分析，以保持每次请求较小。可在下方编辑或添加问题，然后生成。建议搭配 Anthropic (Claude) 使用。",
    "Download CSV": "下载 CSV",
    "Analysis section": "分析部分",
    analysing: "正在分析",
    "All sections (uses the most credits)": "所有部分（消耗最多额度）",
    "All sections": "所有部分",
    Questions: "问题",
    "one per line, edit or add your own": "每行一个，可编辑或添加自己的问题",
    "Reset to defaults": "恢复默认",
    "Add a question…": "添加问题…",
    "Runs every section using the questions you've set for each. This sends one large request and uses the most credits.":
      "使用你为每个部分设置的问题运行所有部分。这会发送一个大型请求并消耗最多额度。",
    "Generate full analysis": "生成完整分析",
    "Generate section analysis": "生成本部分分析",
    Serving: "发球",
    Reception: "接发球",
    Setting: "二传",
    Attack: "进攻",
    Blocking: "拦网",
    "Defence / Digging": "防守 / 救球",
    "Rotation Analysis": "轮转分析",
    "Opponent Tendencies (Scouting)": "对手倾向（侦察）",
    "Overall Team Metrics": "球队总体指标",
    "Serve efficiency (aces, errors, and pressured receptions forced).": "发球效率（发球得分、失误以及造成的接发球压力）。",
    "Which servers are most effective, and against which reception zones?": "哪些发球队员最有效？针对哪些接发球区域？",
    "Which serve types (jump, float, jump-float) yield the best results?": "哪种发球类型（跳发、飘球、跳飘）效果最好？",
    "Reception quality breakdown (perfect, positive, negative, error).": "接发球质量分析（完美、正面、负面、失误）。",
    "Which players struggle most under jump serves vs. float serves?": "哪些球员在跳发与飘球面前最为吃力？",
    "How does poor reception correlate with side-out efficiency?": "接发球不佳与一边过效率有何关联？",
    "Set distribution by zone and by player.": "按区域和球员划分的二传分配。",
    "How does the setter perform under pressure (bad-pass situations)?": "二传在压力下（接发球不到位时）表现如何？",
    "Tempo breakdown (quick, medium, high ball) and which yields the best attack outcome.": "节奏分析（快球、中球、高球）以及哪种进攻效果最好。",
    "Kill percentage and error rate per player, per rotation, and per zone.": "每名球员、每个轮次及每个区域的扣球成功率和失误率。",
    "Which attack zones (pipe, cross, line) have the highest efficiency?": "哪些进攻区域（路线球、斜线、直线）效率最高？",
    "How attack efficiency changes in transition vs. side-out.": "进攻效率在攻防转换与一边过中的变化。",
    "Block-per-set rate per player and rotation.": "每名球员和每个轮次的每局拦网数。",
    "Which opposing attack zones are most successfully blocked?": "哪些对手进攻区域被成功拦网最多？",
    "How often blocks result in kills vs. touch-outs.": "拦网转化为得分与拦出界的比例。",
    "Dig success rate per player and per court zone.": "每名球员和每个场区的防守成功率。",
    "Which attack types (tip, roll, hard swing) are hardest to defend?": "哪些进攻类型（轻扣、推球、重扣）最难防守？",
    "How defensive efficiency affects transition attack success.": "防守效率如何影响攻防转换的进攻成功率。",
    "Which rotations have the highest and lowest point differential?": "哪些轮次的得失分差最高和最低？",
    "Side-out and break-point percentages per rotation.": "每个轮次的一边过和发球得分率。",
    "Rotation-specific vulnerabilities (e.g. a weak serving or passing rotation).": "特定轮次的弱点（例如发球或接发球较弱的轮次）。",
    "The opponent's most frequently used attack zones and combinations.": "对手最常使用的进攻区域和战术配合。",
    "The opponent's go-to attackers in high-pressure situations.": "对手在高压局面下的首选进攻手。",
    "The serve-receive patterns the opponent favours.": "对手偏爱的发接发球模式。",
    "Overall rally-win probability in different score situations.": "不同比分局面下的总体回合胜率。",
    "How performance changes in sets 1–3 vs. sets 4–5 (fatigue/pressure effects).": "表现在第1–3局与第4–5局的变化（疲劳/压力影响）。",
    "The correlation between first-ball side-out percentage and match outcome.": "一传一边过率与比赛结果之间的关联。",
    Report: "报告",

    // Import page
    "Load DVW file(s)...": "加载 DVW 文件…",
    "Load PSVB file(s)...": "加载 PSVB 文件…",
    "Load VSM file(s)...": "加载 VSM 文件…",
    "Load playlist file...": "加载播放列表文件…",
    "load one or more DataVolley files": "加载一个或多个 DataVolley 文件",
    "load one or more VBStats files": "加载一个或多个 VBStats 文件",
    "load one or more VolleyStation VSM files": "加载一个或多个 VolleyStation VSM 文件",
    "Convert DVW": "转换 DVW",
    "VSM": "VSM",
    "Convert DVW to VSM...": "将 DVW 转换为 VSM…",
    "pick a DataVolley .dvw file to download as .vsm": "选择一个 DataVolley .dvw 文件以下载为 .vsm",
    "Convert VSM to DVW...": "将 VSM 转换为 DVW…",
    "pick a VolleyStation .vsm file to download as .dvw": "选择一个 VolleyStation .vsm 文件以下载为 .dvw",
    "Converting…": "转换中…",
    "Converted": "已转换",
    "Failed to convert": "转换失败",
    "Beta testers only — coming soon for everyone else.": "仅限测试用户 — 其他用户即将推出。",
    "load a playlist": "加载播放列表",
    "Use Shift-JIS Encoder": "使用 Shift-JIS 编码",
    "Importing…": "导入中…",
    "Save to Database?": "保存到数据库？",
    "Always save imports automatically": "始终自动保存导入的文件",
    "View Only": "仅查看",
    "Save to Database": "保存到数据库",
    "Error saving to database": "保存到数据库时出错",
    "Saved to database": "已保存到数据库",
    "Saving to database…": "正在保存到数据库…",
    "Upload them to the database now?": "现在将它们上传到数据库吗？",

    // Settings / Options
    Theme: "主题",
    Options: "选项",
    "Rotation naming convention": "轮转命名规则",
    "Choose how rotations are labelled in reports and analysis.":
      "选择报告和分析中轮转的标注方式。",
    "R1–R6 (rotation sequence)": "R1–R6（轮转顺序）",
    "S1–S6 (setter court position)": "S1–S6（二传场上位置）",
    "R1: setter in position 1, R2: setter in 6, R3: setter in 5, R4: setter in 4, R5: setter in 3, R6: setter in 2.":
      "R1：二传在 1 号位，R2：二传在 6 号位，R3：二传在 5 号位，R4：二传在 4 号位，R5：二传在 3 号位，R6：二传在 2 号位。",
    "S1: setter in 1, S2: setter in 2, S3: setter in 3, S4: setter in 4, S5: setter in 5, S6: setter in 6.":
      "S1：二传在 1 号位，S2：二传在 2 号位，S3：二传在 3 号位，S4：二传在 4 号位，S5：二传在 5 号位，S6：二传在 6 号位。",
    "Labels:": "标签：",
    "Current theme:": "当前主题：",
    "Choose the language for the app interface.": "选择应用界面的语言。",
    "Create your own language package or edit a community one, with AI-assisted translation.":
      "创建您自己的语言包或编辑社区语言包，支持 AI 辅助翻译。",

    // About / Footer
    "Copyright © Perana Sports {year}. All rights reserved.":
      "版权所有 © Perana Sports {year}。保留所有权利。",
    "VBLive is free to use right now. However Perana Sports reserves the right to charge a subscription fee in due course.":
      "VBLive 目前免费使用。但 Perana Sports 保留在适当时候收取订阅费用的权利。",
    "Querries / suggestions are most welcomed.": "欢迎提出疑问 / 建议。",

    // Live
    "Please enter server name to search": "请输入要搜索的服务器名称",
    "Broadcast Server": "广播服务器",
    "Enter DVMateLive Server Name": "输入 DVMateLive 服务器名称",
    LIVE: "直播中",

    // Playlists list
    "Delete Playlist": "删除播放列表",
    "Are you sure you want to delete the selected playlist?":
      "确定要删除所选的播放列表吗？",
    "My Playlists": "我的播放列表",
    "Shared Playlists": "共享播放列表",
    "Tags:": "标签：",
    "Select...": "选择…",
    "All Tags": "所有标签",

    // Share
    "User already added": "用户已添加",
    "Please enter a valid email address": "请输入有效的电子邮箱地址",
    "Please select at least one user to share with":
      "请至少选择一位要分享的用户",
    "Not Shared": "未分享",
    "Available for everyone": "对所有人开放",
    "Available to selected users": "仅对所选用户开放",
    "Enter VBLive user email address": "输入 VBLive 用户的电子邮箱地址",
    "Selected users:": "已选用户：",
    "Save List": "保存列表",
    "Clear List": "清空列表",
    "Clear Users List": "清空用户列表",
    "Do you really want to clear all users from list?":
      "确定要从列表中清除所有用户吗？",
    "Do you want to save changes before closing?": "关闭前是否保存更改？",
    "Enter name to save list to:": "输入要保存列表的名称：",
    "Enter name to save list to": "输入要保存列表的名称",

    // AI panels
    "AI settings": "AI 设置",
    "Setup needed": "需要设置",
    "Generate report": "生成报告",
    "Generate AI report": "生成 AI 报告",
    "Generate general report": "生成总报告",
    "Translate to…": "翻译为…",
    "Follow-up questions": "后续问题",
    "Ask a follow-up question…": "提出后续问题…",
    "Ask a follow-up question": "提出后续问题",
    "AI provider": "AI 提供商",
    Model: "模型",
    Hide: "隐藏",
    Show: "显示",
    "Your key is stored only in this browser and sent directly to the selected provider.":
      "您的密钥仅存储在此浏览器中，并直接发送到所选的提供商。",
    "Please enter your API key.": "请输入您的 API 密钥。",
    "Not enough data to analyse.": "数据不足，无法分析。",
    "Select a player with recorded spikes first.": "请先选择有扣球记录的球员。",
    Player: "球员",
    "Select a player…": "选择球员…",
    "No players with recorded spikes in the selected matches.":
      "所选比赛中没有有扣球记录的球员。",
    "Analyse with AI": "使用 AI 分析",
    "AI scouting report": "AI 球探报告",
    "Q:": "问：",
    Ask: "提问",
    "e.g. Which rotation should we target on serve?":
      "例如：我们应该针对哪个轮转发球？",
    "Saved questions:": "已保存的问题：",
    "Click to ask again": "点击再次提问",
    "Remove saved question": "移除已保存的问题",

    // AI language options
    Spanish: "西班牙语",
    French: "法语",
    German: "德语",
    Italian: "意大利语",
    Portuguese: "葡萄牙语",
    Dutch: "荷兰语",
    Polish: "波兰语",
    Russian: "俄语",
    Turkish: "土耳其语",
    Arabic: "阿拉伯语",
    "Chinese (Simplified)": "简体中文",
    Japanese: "日语",
    Korean: "韩语",
    Vietnamese: "越南语",
    Thai: "泰语",

    // Box score
    "Show Full Headers": "显示完整表头",
    "Show Short Headers": "显示简短表头",
    "Show Less Players": "显示更少球员",
    "Show All Players": "显示所有球员",
    "Attack Kills": "扣球得分",
    "Attack Errors": "扣球失误",
    "Total Attempts": "总扣球次数",
    "Hitting Efficiency": "扣球效率",
    "Serve Aces": "发球得分",
    "Serve Errors": "发球失误",
    "Pass Errors": "接发失误",
    "% Perfect Passes": "一传到位率",
    "Passing Average": "一传平均分",
    "Total Passes": "一传总数",
    "Set Assists": "二传助攻",
    Digs: "防守起球",
    "Momentum Chart": "气势图",
    "Key event": "关键球",
    "Break Point %": "破发得分率",
    "First Ball Sideout %": "第一球一攻率",
    "Sideout Transition %": "防反一攻率",
    "FB Sideout Kills %": "第一球强攻得分率",
    "Sideout Opposition Error %": "对方失误一攻率",
    Plus: "正分",
    Minus: "失分",
    Blocks: "拦网得分",
    "Positive Serve %": "发球得分率",
    Sideout: "一攻",
    "Break point": "破发得分",
    "Ace / Kill": "发球得分 / 扣球得分",
    "In play": "比赛进行中",
    Error: "失误",
    "Sideout on a kill": "强攻得分一攻",
    "Sideout on an error": "对方失误一攻",
    "Top half": "上半部分",
    "Bottom half": "下半部分",
    "The centre line steps toward the team that wins each point.": "中线会朝获得该分的球队方向移动。",
    "No point-by-point data available for this match.": "本场比赛暂无逐分数据。",
    "No match data available.": "暂无比赛数据。",
    "This report isn't supported on this browser yet — please use Safari.": "此报告暂不支持此浏览器 — 请使用 Safari。",
    "No video available for this match.": "本场比赛暂无视频。",
    passes: "次接发",
    "eff.": "效率",
    Grade: "等级",
    LEFT: "左",
    CENTRE: "中",
    RIGHT: "右",
    SELECTED: "已选",
    Passers: "接发球员",
    Calls: "二传呼叫",
    "SIDE OUT": "一攻",
    TRANSITION: "转换",
    "No events in selection.": "当前选择中没有事件。",
    "Serve Receives By Players": "按球员分接发球",
    "Serve Receive By Zones": "按区域分接发球",
    "Dig Errors": "防守失误",
    "Dig Average": "防守平均分",
    "Block Solos": "单人拦网",
    "Block Assists": "协助拦网",
    "Block Errors": "拦网失误",
    "Points Scored": "得分",
    TEAM: "球队",

    // Sideout report
    "ALL ROWS": "所有轮次",
    "ROWS ": "轮次 ",
    "ROW ": "轮次 ",
    "FB Kills": "一攻扣球得分",
    "Opp. Serve Errors": "对方发球失误",
    "FB Opp. Errors": "一攻对方失误",
    "Kills/Blocks": "扣球/拦网",
    "Opp. Errors": "对方失误",
    Unsuccessful: "未成功",

    // Attack zones
    "No events in selected zone.": "所选区域内没有事件。",
    "Show all attacks. Or click on a zone to show attacks in that zone.":
      "显示所有进攻。或点击某个区域以显示该区域内的进攻。",
    "OPPONENTS (": "对手（",

    // Video analysis filters
    "All Types": "所有类型",
    Serve: "发球",
    "Serve-Receive": "接发球",
    Set: "二传",
    "Setter's Call": "二传战术",
    "Disable this skill": "禁用此技术",
    "Enable this skill": "启用此技术",
    "Teams & Players": "球队与球员",
    "Match State": "比赛状态",
    "Custom Code": "自定义代码",
    "Search... (comma-separated for multiple)": "搜索…（多个请用逗号分隔）",
    vs: "对",
    "Rewind 60 seconds": "后退60秒",
    "Rewind 1 frame": "后退1帧",
    "Forward 1 frame": "前进1帧",
    "Forward 60 seconds": "前进60秒",
    "Previous Clip": "上一个片段",
    Replay: "重播",
    "Next Clip": "下一个片段",
    Phases: "阶段",
    "Score Range": "比分区间",
    "Set Winner": "局胜方",
    Type: "类型",
    Strength: "力度",
    "Setter Calls": "二传战术",
    "Setter Call": "二传战术",
    Freeball: "自由球",
    "Close (0-2)": "接近 (0-2)",
    "Moderate (3-5)": "适中 (3-5)",
    "Lopsided (6+)": "悬殊 (6+)",
    "Focus Team": "关注球队",
    Oppositions: "对方",
    "Focus Team Won": "关注球队获胜",
    "Oppositions Won": "对方获胜",
    "Advanced Filters": "高级筛选",
    Players: "球员",
    Opponents: "对手",
    "Event Type": "事件类型",
    "Event Result": "事件结果",
    "Attack Type": "进攻类型",
    "Hit Type": "击球类型",
    "Block Type": "拦网类型",
    "Attack Combo": "进攻组合",
    Apply: "应用",
    Rotation: "轮转",
    Skill: "技术动作",
    "Lead Time": "提前时间",
    Duration: "持续时间",
    Timing: "时间设置",
    "Go Live": "前往直播",
    "Playback has fallen behind the live broadcast — click to jump to now.": "播放已落后于直播 — 点击跳转到当前时间。",
    "Video timing": "视频时间设置",
    "Default lead time (seconds before an event to start playback) and clip duration, per skill.": "默认提前时间（事件开始播放前的秒数）和每个技术动作的片段时长。",
    Cloud: "云端",
    Local: "本地",
    "Relay host": "中继主机",
    "IP or hostname of the machine running the local relay.": "运行本地中继服务的机器的 IP 或主机名。",
    Spike: "扣球",
    Block: "拦网",
    Defence: "防守",
    "All Results": "所有结果",
    Hard: "重扣",
    Soft: "轻扣",
    Tip: "吊球",
    "Not Specified": "未指定",
    "H - High": "H - 高球",
    "M- Medium": "M - 中球",
    "Q - Quick": "Q - 快球",
    "T - Tense": "T - 平拉开",
    "U - Super": "U - 超手",
    "F - Fast": "F - 快攻",
    "O - Other": "O - 其他",
    "B0 - No Block": "B0 - 无拦网",
    "B1 - 1 Block": "B1 - 单人拦网",
    "B2 - 2 Blocks": "B2 - 双人拦网",
    "B3 - 3 Blocks": "B3 - 三人拦网",
    "BS - Seams": "BS - 拦网空隙",

    // All filters panel
    ALL: "全部",
    NONE: "无",

    // Playlist editor
    "Remove Item": "移除项目",
    "Do you really want to remove the selected item?": "确定要移除所选项目吗？",
    "Description is required": "描述为必填项",
    "Comments are required": "评论为必填项",
    "Tags are required": "标签为必填项",
    "Playlist saved successfully": "播放列表保存成功",
    "Error saving playlist": "保存播放列表出错",

    // Language editor
    "My saved packages": "我保存的语言包",
    "Loading shared languages…": "正在加载共享语言…",
    "Community shared languages": "社区共享语言",
    by: "作者",
    "based on": "基于",
    "or create new": "或新建",
    Use: "使用",
    "New Language Package": "新建语言包",
    "Language Name": "语言名称",
    "Language Code": "语言代码",
    "Flag Emoji": "国旗表情",
    "Base language (reference)": "基础语言（参考）",
    "If not English, both English and the reference language will be shown.":
      "若非英语，则同时显示英语和参考语言。",
    "Start Editing": "开始编辑",
    "Save Key": "保存密钥",
    "AI key set": "已设置 AI 密钥",
    "Set API key for AI translation": "设置用于 AI 翻译的 API 密钥",
    "AI key ✓": "AI 密钥 ✓",
    "Set AI key": "设置 AI 密钥",
    "AI: Translate All": "AI：全部翻译",
    "Translating…": "翻译中…",
    "AI translate section": "AI 翻译此部分",
    "Clear All": "全部清除",
    "Saving…": "保存中…",
    English: "英语",
    new: "新",
    "Enter translation…": "输入翻译…",
    "Share with other VBLive users?": "与其他 VBLive 用户分享吗？",
    "Would you like to share the": "您是否愿意分享",
    "language package with all VBLive users? Anyone can then copy and build upon it.":
      "语言包给所有 VBLive 用户？任何人都可以复制并在此基础上进行扩展。",
    "No, just save for me": "不，仅为我保存",
    "Yes, share it!": "是的，分享它！",
    "Language package saved and shared with all VBLive users!":
      "语言包已保存并与所有 VBLive 用户分享！",
    "Language package saved!": "语言包已保存！",
    "Clear all translations? This cannot be undone.":
      "清除所有翻译？此操作无法撤销。",

    // Additional UI phrases
    Teams: "球队",
    "Shared By": "分享者",
    "View Activity": "查看记录",
    Viewer: "查看者",
    "Date / Time": "日期/时间",
    "No views recorded yet.": "暂无查看记录。",
    "Date / Scores": "日期/比分",
    "Stats API source": "数据接口来源",
    "load one or more AVC VIS files": "加载一个或多个 AVC VIS 文件",
    "Points to close a set": "结束一局所需分数",
    "Require 2-point advantage": "需要净胜2分",
    Roster: "球员名单",
    Clone: "复制",
    "Edit team": "编辑球队",
    "New player": "新建球员",
    "Search player...": "搜索球员…",
    "Match Centre": "比赛中心",
    "Points Won": "得分",
    "Opp. Error": "对方失误",
    Total: "总计",
    "Match Skills": "比赛技术",
    Dig: "防守",
    "Best Scorers": "得分王",
    "Power Play": "强攻局",
    Match: "比赛",
    "Player scouting": "球员侦察",
    "Team deep analysis": "球队深度分析",
    "All Players": "所有球员",
    "All Sets": "所有局次",
    "All Rotations": "所有轮次",
    "All Combos": "所有战术",
    "All Calls": "所有二传呼叫",
    "All Events": "所有事件",
    "Select all items": "全选项目",
    "Unselect all items": "取消全选",
    "Create play list": "创建播放列表",
    "Point by Point": "逐分战况",
    // i18n backfill pass — previously English-fallback-only keys
    "Access denied": "访问被拒绝",
    "Aces": "发球得分",
    "ACES": "发球得分",
    "Add report": "添加报告",
    "Admin": "管理员",
    "Admins": "管理员",
    "Advanced": "进阶",
    "All set!": "一切就绪！",
    "Analyse team with AI": "使用 AI 分析球队",
    "Are you sure you want to delete the selected play list?": "确定要删除所选播放列表吗？",
    "Assign": "分配",
    "Assign playlist": "分配播放列表",
    "Assigned": "已分配",
    "At least one admin is required.": "至少需要一名管理员。",
    "Attack Combo / Setter Call": "进攻组合 / 二传叫位",
    "Attack diagram": "进攻示意图",
    "Attacks": "进攻",
    "ATTACKS": "进攻",
    "Auto-detected a custom set-closing score for": "已自动检测到自定义结束局分，适用于",
    "BLOCK": "拦网",
    "BLOCKS": "拦网",
    "Beta testers": "测试用户",
    "Clear filter": "清除筛选",
    "Close Set": "结束本局",
    "Close menu": "关闭菜单",
    "Code": "代码",
    "Coding defaults & shortcuts": "记分默认设置与快捷键",
    "Continue": "继续",
    "Could not load match data. The match may no longer be available.": "无法加载比赛数据，该比赛可能已不可用。",
    "Could not load stats for these matches.": "无法加载这些比赛的统计数据。",
    "Could not load this match.": "无法加载此比赛。",
    "Could not load this playlist.": "无法加载此播放列表。",
    "Could not load this report.": "无法加载此报告。",
    "Could not save the report.": "无法保存报告。",
    "Could not share the report. Please try again.": "无法分享报告，请重试。",
    "Court horizontal (net vertical)": "球场横向（球网纵向）",
    "Court vertical (net horizontal)": "球场纵向（球网横向）",
    "Create": "创建",
    "Create a new team from this roster": "根据此名单创建新球队",
    "DIFFERENCE": "差值",
    "DV Code": "DV 代码",
    "Delete this report?": "删除此报告？",
    "Detected a custom set-closing score of": "检测到自定义结束局分为",
    "Double Sub": "双重换人",
    "Double Sub: now pick a bench player for the flashing OPPOSITE slot…": "双重换人：现在为闪烁的接应位置选择替补球员…",
    "Double Sub: pick a bench player for the flashing SETTER slot…": "双重换人：为闪烁的二传位置选择替补球员…",
    "Double Substitution — swap the Setter and Opposite for two incoming players": "双重换人 —— 用两名替补球员同时换下二传和接应",
    "Drag to draw the ball path; pause briefly to mark a touch (reception/dig/block), release to finish.": "拖动以绘制球路；短暂停顿可标记一次触球（接发球/防守/拦网），松开结束。",
    "Draw": "绘制",
    "Draw & Code": "绘图记分",
    "Draw & Code — draw ball paths on the court to code the rally": "绘图记分 —— 在球场上绘制球路以记录回合",
    "Edit Lineup": "编辑阵容",
    "Edit Lineup — freely rearrange the starting lineup before the set begins": "编辑阵容 —— 在本局开始前可自由调整首发阵容",
    "Emails in this list can manage beta testers and other admins.": "此列表中的邮箱可以管理测试用户和其他管理员。",
    "Emails in this list get early access to AI Reports and other gated features. Changes take effect immediately.": "此列表中的邮箱可提前使用 AI 报告及其他受限功能。更改会立即生效。",
    "Enter a valid email address.": "请输入有效的邮箱地址。",
    "Exit fullscreen": "退出全屏",
    "Extended": "扩展",
    "Failed to generate PDF.": "生成 PDF 失败。",
    "Failed to save changes.": "保存更改失败。",
    "Filtered by": "筛选条件：",
    "First name": "名字",
    "For stronger security later, consider moving admin checks to a Firebase Auth custom claim instead of this email list.": "为了更高的安全性，建议日后将管理员校验迁移到 Firebase Auth 自定义声明，而非此邮箱列表。",
    "Fullscreen": "全屏",
    "Generate an AI report from these matches": "根据这些比赛生成 AI 报告",
    "How often (in seconds) the events list is updated during a live session.": "直播过程中事件列表的刷新频率（秒）。",
    "Last name": "姓氏",
    "Leave blank to use standard rules (25 points, 15 for a 5th/deciding set)": "留空则使用标准规则（25 分制，决胜局 15 分）",
    "Live session refresh interval": "直播会话刷新间隔",
    "Load VIS file(s)...": "加载 VIS 文件…",
    "Load this team's last-saved starting lineup (positions, setter and liberos)": "加载该队上次保存的首发阵容（位置、二传与自由人）",
    "Loading…": "加载中…",
    "Manually end the current set at its current score, regardless of the configured winning score / 2-point advantage rule": "手动以当前比分结束本局，不受所设定的获胜分数/两分优势规则限制",
    "Migration finished with some issues": "迁移完成，但存在一些问题",
    "Most of your data was migrated, but some items could not be moved. You can retry now or continue and try again later.": "您的大部分数据已迁移，但部分项目未能移动。您可以立即重试，也可以继续并稍后再试。",
    "Mute": "静音",
    "New team": "新建球队",
    "Next": "下一个",
    "Nickname": "昵称",
    "No Power Play": "无强力发球",
    "No admins yet.": "暂无管理员。",
    "No beta testers yet.": "暂无测试用户。",
    "No coded events to analyse.": "没有可供分析的已记录事件。",
    "No coded events to export.": "没有可供导出的已记录事件。",
    "No matches.": "没有比赛。",
    "No players added yet.": "尚未添加球员。",
    "No players recorded.": "没有已记录的球员。",
    "No players yet.": "暂无球员。",
    "No playlists assigned.": "未分配任何播放列表。",
    "No playlists found.": "未找到播放列表。",
    "No reports saved.": "未保存任何报告。",
    "No roster players available.": "没有可用的名单球员。",
    "No substitutions": "无换人",
    "No teams found.": "未找到球队。",
    "No teams yet.": "暂无球队。",
    "No teams.": "没有球队。",
    "Nominate Setter": "指定二传",
    "Notify these users by email (optional)": "通过邮件通知这些用户（可选）",
    "OPP REC": "对方接发",
    "OPP. ERRORS": "对方失误",
    "Open / Download": "打开 / 下载",
    "Open local file": "打开本地文件",
    "Open menu": "打开菜单",
    "Opposition Passing — use the opposition's reception formation on the diagram": "对方接发阵型 —— 在示意图上使用对方的接发球站位",
    "Paste the report (Markdown supported)…": "粘贴报告内容（支持 Markdown）…",
    "Pause": "暂停",
    "Play": "播放",
    "Playing squad": "比赛名单",
    "Playing squad is full. Remove a player from the squad before adding another.": "比赛名单已满。请先移除一名球员再添加新球员。",
    "Please sign in to save reports.": "请登录以保存报告。",
    "Power Play Only": "仅限强力发球",
    "Record a team error (no player) — edit later to add the player and event type": "记录一次球队失误（无具体球员）——可稍后编辑以添加球员和事件类型",
    "Record at least one event before making substitutions": "请先记录至少一个事件再进行换人",
    "Redo": "重做",
    "Remove": "移除",
    "Remove share": "移除分享",
    "Remove yourself as admin? You will immediately lose access to this page.": "确定要移除自己的管理员身份吗？您将立即失去此页面的访问权限。",
    "Report saved and shared.": "报告已保存并分享。",
    "Report saved to the player's profile.": "报告已保存至该球员的个人资料。",
    "Report title": "报告标题",
    "Reports": "报告",
    "Result": "结果",
    "Retry": "重试",
    "Return to live coding": "返回实时记分",
    "Rotation is not permitted while making substitutions": "换人时不允许轮转",
    "SERVE": "发球",
    "SETS": "局数",
    "Save & Share": "保存并分享",
    "Save this report so you can share it with other users": "保存此报告，以便与其他用户分享",
    "Save this report to the player's profile": "将此报告保存至该球员的个人资料",
    "Save to player": "保存至球员",
    "Saved lineup": "已保存的阵容",
    "Scroll, drag or swipe to scrub": "滚动、拖动或滑动以快进快退",
    "Search existing players...": "搜索现有球员…",
    "Search playlists…": "搜索播放列表…",
    "Select a match video to view while entering lineups.": "选择比赛视频以便在输入阵容时观看。",
    "Select a player.": "选择一名球员。",
    "Select a team.": "选择一支球队。",
    "Setting up your account": "正在设置您的账户",
    "Shaded rows are played under a Power Play — HP/AP marks the calling team, bold gold scores are bonus (double) points.": "阴影行表示该分在强力发球期间打出 —— HP/AP 标记发起强力发球的球队，加粗金色比分为奖励（双倍）得分。",
    "Shared by": "分享者：",
    "Shared with me": "分享给我的",
    "Show help": "显示帮助",
    "Show public playlists": "显示公开播放列表",
    "Standard": "标准",
    "Substitutions made — tap to view": "已进行换人 —— 点击查看",
    "Tap the blocking player.": "点击拦网球员。",
    "Team colour": "球队颜色",
    "Team name": "球队名称",
    "That email is already in the list.": "该邮箱已在列表中。",
    "The report is too large to save. Try a single match or fewer sets.": "报告过大，无法保存。请尝试单场比赛或减少局数。",
    "This match has no Power Play calls.": "本场比赛没有强力发球记录。",
    "This player has no name to save the report against.": "该球员没有姓名，无法据此保存报告。",
    "This team has no designated setter. Pick the player who is setting.": "该队尚未指定二传。请选择正在打二传的球员。",
    "Toggle video player": "切换视频播放器",
    "Unmute": "取消静音",
    "Untitled playlist": "未命名播放列表",
    "Video URL…": "视频网址…",
    "We're moving your data to a faster, more reliable home. This happens only once.": "我们正在将您的数据迁移到更快、更可靠的存储位置。此操作仅进行一次。",
    "You don't have permission to view this page.": "您没有权限查看此页面。",
    "Your data has been migrated successfully.": "您的数据已成功迁移。",
    "Your teams and players will appear here after your data is migrated.": "数据迁移完成后，您的球队和球员将显示在此处。",
    "bonus point": "奖励得分",
    "bonus points": "奖励得分",
    "event": "事件",
    "events": "事件",
    "file(s).": "个文件。",
    "is not on this team's roster.": "不在该队名单中。",
    "loaded": "已加载",
    "matches": "场比赛",
    "more": "更多",
    "players": "名球员",
    "points (no value was entered) — applied automatically.": "分（未输入数值）——已自动应用。",
    "roster": "名单",
    "seconds": "秒",
    "selected": "已选择",
    "session(s)?": "个会话？",
    "showing": "显示",
    "tournaments": "赛事",
    "…and": "……还有",
    "● marks which team served the point.": "● 标记发球得分的球队。",
    // i18n backfill pass (Catapult + misc)
    "Centimetres (cm)": "厘米 (cm)",
    "Inches (in)": "英寸 (in)",
    "Jump height units": "跳跃高度单位",
    "Choose how Catapult jump heights are displayed and filtered.": "选择 Catapult 跳跃高度的显示和筛选方式。",
    "Select Catapult Focus file...": "选择 Catapult Focus 文件…",
    "Select a Catapult Focus CSV export to view jump data for this match.": "选择一个 Catapult Focus CSV 导出文件，以查看本场比赛的跳跃数据。",
    "Loaded from saved match data": "已从已保存的比赛数据加载",
    "No jump data found in this file.": "此文件中未找到跳跃数据。",
    "Could not read this file. Please check it is a Catapult Focus CSV export.": "无法读取此文件。请确认这是一个 Catapult Focus CSV 导出文件。",
    "Could not save jump data with this match.": "无法将跳跃数据保存到此场比赛。",
    "Jump data saved with this match.": "跳跃数据已随此场比赛保存。",
    "Show players with no match stats": "显示没有比赛数据的球员",
    "hidden": "已隐藏",
    "jumps": "次跳跃",
    "periods": "时段",
    "Sync offset": "同步偏移",
    "matched": "已匹配",
    "match events now show jump height/duration in Video Analysis": "场比赛事件现在会在视频分析中显示跳跃高度/时长",
    "Team": "球队",
    "Team A": "A队",
    "Team B": "B队",
    "Which team is this Catapult file for?": "此 Catapult 文件属于哪支球队？",
    "We couldn't confidently match the players in this Focus file to one team. Please select which team it belongs to.": "我们无法确定此 Focus 文件中的球员属于哪支球队。请选择该文件所属的球队。",
    "This match has no video attached yet.": "此场比赛尚未附加视频。",
    "No synced jump events found for this player.": "未找到此球员的同步跳跃事件。",
    "No match loaded.": "未加载比赛。",
    "avg": "平均",
    "max": "最大",
    "Sync": "同步",
    "Jump Report": "跳跃报告",
    "Watch Video": "观看视频",
    "Save PDF": "保存 PDF",
    "Generated by VBLive": "由 VBLive 生成",
    "Deep analysis of {team} from ball-by-ball event data, covering serving, reception, setting, attack, blocking, defence, rotations and opponent tendencies. Best used with the Anthropic (Claude) provider.": "基于逐球事件数据对 {team} 进行深入分析，涵盖发球、一传、二传、进攻、拦网、防守、轮转及对手倾向。建议搭配 Anthropic（Claude）提供商使用。",
    "teams": "支球队",
  },

  de: {
    // Information page
    Information: "Information",
    "Release notes": "Versionshinweise",
    "Contact us": "Kontakt",
    "Questions, feedback or suggestions are most welcome.": "Fragen, Feedback oder Vorschläge sind jederzeit willkommen.",
    // DV match coding
    Lineups: "Aufstellungen",
    Point: "Punkt",
    Video: "Video",
    Defaults: "Standardwerte",
    "[team][NN][skill][type?][eval][zoneFrom?[szFrom?][zoneTo?[szTo?]]]": "[Team][NN][Fähigkeit][Typ?][Bewertung][ZoneVon?[UZVon?][ZoneNach?[UZNach?]]]",
    Vertical: "Vertikal",
    Horizontal: "Horizontal",
    "Swap sides": "Seiten tauschen",
    Subs: "Wechsel",
    Substitutions: "Wechsel",
    Court: "Feld",
    Home: "Heim",
    Away: "Gast",
    Games: "Sätze",
    Enter: "Eingabe",
    Undo: "Rückgängig",
    "Match saved": "Spiel gespeichert",
    "Please sign in to save this match.": "Bitte melden Sie sich an, um dieses Spiel zu speichern.",
    "Nothing to save yet.": "Noch nichts zu speichern.",
    "Could not save the match. Please try again.": "Das Spiel konnte nicht gespeichert werden. Bitte versuchen Sie es erneut.",
    Discard: "Verwerfen",
    "Resumed autosaved coding": "Automatisch gespeicherte Erfassung fortgesetzt",
    "Changes discarded": "Änderungen verworfen",
    "Discard all changes and revert to the starting point?":
      "Alle Änderungen verwerfen und zum Ausgangspunkt zurückkehren?",
    Clear: "Löschen",
    "Prev set": "Vorheriger Satz",
    "Next set": "Nächster Satz",
    "Match Setup": "Spiel einrichten",
    "First serve": "Erster Aufschlag",
    "Start Match": "Spiel starten",
    "Resume Match": "Spiel fortsetzen",
    Skip: "Überspringen",
    "Select an event then drag to draw ball path": "Ereignis auswählen und ziehen, um die Ballbahn zu zeichnen",
    "Drag on court to set ball path": "Auf dem Feld ziehen, um die Ballbahn festzulegen",
    "No events yet – enter a DV code above and press Enter.": "Noch keine Ereignisse – gib oben einen DV-Code ein und drücke Eingabe.",
    "DataVolley code reference": "DataVolley-Code-Referenz",
    Format: "Format",
    Skills: "Fähigkeiten",
    Evaluation: "Bewertung",
    "Skill / type omission": "Fähigkeit / Typ weglassen",
    "Skill and/or type may be omitted; defaults fill in the rest.": "Fähigkeit und/oder Typ können weggelassen werden; Standardwerte füllen den Rest aus.",
    "Dot-coding (S→R and A→B pairs)": "Punkt-Codierung (S→R- und A→B-Paare)",
    "Chaining & aliases": "Verkettung & Aliase",
    "team:": "Team:",
    "omit → defaults to A": "weglassen → Standard ist A",
    "NN: 1 or 2 digit jersey, e.g. 1, 01, 12": "NN: 1- oder 2-stellige Trikotnummer, z. B. 1, 01, 12",
    "sub-zone: a b c d appended to zone digit, e.g. 5b or 5b1a": "Unterzone: a b c d an die Zonenziffer angehängt, z. B. 5b oder 5b1a",
    "S Serve · R Reception · E Set · A Attack · B Block · D Dig · F Freeball": "S Aufschlag · R Annahme · E Zuspiel · A Angriff · B Block · D Abwehr · F Freeball",
    "# kill/ace · + positive · ! ok · - negative · / overpass · = error": "# Punkt/Ass · + positiv · ! ok · - negativ · / Überball · = Fehler",
    "E.g.": "Z. B.",
    "serve with skill=A (default), type=H, eval=+, zone=5.": "Aufschlag mit skill=A (Standard), type=H, eval=+, zone=5.",
    "type-only (no skill marker), defaults skill from context.": "nur Typ (kein Fähigkeitskürzel), Fähigkeit aus dem Kontext.",
    "serve coded, reception auto-linked.": "Aufschlag codiert, Annahme automatisch verknüpft.",
    "attack coded, block auto-linked.": "Angriff codiert, Block automatisch verknüpft.",
    "Dot connects the reception eval to the corresponding serve eval via the serve→receive mapping.": "Der Punkt verbindet die Annahmebewertung mit der entsprechenden Aufschlagbewertung über die Aufschlag→Annahme-Zuordnung.",
    "two events in one string.": "zwei Ereignisse in einer Zeichenkette.",
    "manual point A/B.": "manueller Punkt A/B.",
    "serve ace/error.": "Aufschlag-Ass/-Fehler.",
    "serve with eval.": "Aufschlag mit Bewertung.",
    // Stats, sideouts, filters & video tooltips
    "Block Kills": "Blockpunkte",
    "Side Out Percentage": "Sideout-Prozentsatz",
    "All First Ball": "Alle ersten Bälle",
    "First Ball Kills": "Erste-Ball-Punkte",
    "Good Serve": "Guter Aufschlag",
    "Break Points": "Breakpunkte",
    Game: "Satz",
    Row: "Position",
    "First Ball Sideouts": "Erste-Ball-Sideouts",
    "All Sideouts": "Alle Sideouts",
    All: "Alle",
    None: "Keine",
    Receiver: "Annahmespieler",
    "# Passes": "Anz. Annahmen",
    "Perfect %": "Perfekt %",
    "Positive %": "Positiv %",
    "Sideout %": "Sideout %",
    "FB Sideout %": "EB-Sideout %",
    "Pass Hits": "Angriffe nach Annahme",
    "Pass Hit Kills": "Punkte nach Annahme",
    "Pass Hit Errors": "Fehler nach Annahme",
    "Show Passes": "Annahmen anzeigen",
    "Show Attacks": "Angriffe anzeigen",
    "Error Pass": "Fehlannahme",
    "1 Pass": "1er-Annahme",
    "2 Pass": "2er-Annahme",
    "Perfect Pass": "Perfekte Annahme",
    "Successful SO": "Erfolgreicher Sideout",
    "First Ball SO": "Erste-Ball-Sideout",
    "Unsuccessful SO": "Erfolgloser Sideout",
    RECEIVES: "ANNAHMEN",
    Sets: "Sätze",
    Rotations: "Rotationen",
    "Attack Combos": "Angriffskombinationen",
    Stages: "Phasen",
    Display: "Anzeige",
    Setters: "Zuspieler",
    Attackers: "Angreifer",
    Results: "Ergebnisse",
    Source: "Quelle",
    Cones: "Zonen",
    "Select None": "Keine auswählen",
    "All Serves": "Alle Aufschläge",
    "All Returns": "Alle Annahmen",
    Filters: "Filter",
    "Reset all filters": "Alle Filter zurücksetzen",
    "Show video clips of filtered attacks": "Videoclips der gefilterten Angriffe anzeigen",
    "Spikes for the selected player": "Angriffe des ausgewählten Spielers",
    "Click on the zones to view video of attacks": "Klicke auf die Zonen, um Videos der Angriffe anzusehen",
    "Expand All": "Alle aufklappen",
    "Collapse All": "Alle zuklappen",
    "Import Radar File": "Radar-Datei importieren",
    "Select local video file": "Lokale Videodatei auswählen",
    "Save to database": "In Datenbank speichern",
    "Load and play online video": "Online-Video laden und abspielen",
    "Synchronise video with events": "Video mit Ereignissen synchronisieren",
    "Rewind 10 seconds": "10 Sekunden zurück",
    "Rewind 1 second": "1 Sekunde zurück",
    "Forward 1 second": "1 Sekunde vor",
    "Forward 10 seconds": "10 Sekunden vor",
    "Set timing for events": "Timing für Ereignisse festlegen",

    // Navigation
    Matches: "Spiele",
    Live: "Live",
    Import: "Import",
    Playlists: "Wiedergabelisten",
    "New Match": "Neues Spiel",
    // New Match / Match Coding gate
    "Match Coding": "Spielerfassung",
    "Match Coding — Coming Soon": "Spielerfassung — Demnächst verfügbar",
    "Live match coding is on the way. Here's a preview of what's coming.":
      "Die Live-Spielerfassung ist in Arbeit. Hier ist eine Vorschau auf das, was kommt.",
    Settings: "Einstellungen",
    Language: "Sprache",
    "Your profile": "Dein Profil",
    "Sign out": "Abmelden",
    "Translation Editor": "Übersetzungseditor",

    // Common actions
    Save: "Speichern",
    Cancel: "Abbrechen",
    Delete: "Löschen",
    Share: "Teilen",
    Edit: "Bearbeiten",
    New: "Neu",
    Refresh: "Aktualisieren",
    Search: "Suchen",
    Reset: "Zurücksetzen",
    Close: "Schließen",
    "Sub for": "Wechsel für",
    "No players available": "Keine Spieler verfügbar",
    "Show full roster": "Gesamten Kader anzeigen",
    "Available only": "Nur verfügbare",
    "Maximum substitutions reached": "Maximale Auswechslungen erreicht",
    Back: "Zurück",
    "Download PDF": "PDF herunterladen",
    "Loading...": "Wird geladen…",
    "Select All": "Alle auswählen",
    "Unselect All": "Alle abwählen",
    Yes: "Ja",
    No: "Nein",
    Add: "Hinzufügen",
    Load: "Laden",
    Confirm: "Bestätigen",
    Open: "Öffnen",
    show: "anzeigen",

    // Auth
    "Sign In": "Anmelden",
    "Sign Up": "Registrieren",
    "Sign Out": "Abmelden",
    Email: "E-Mail",
    Password: "Passwort",
    "Confirm Password": "Passwort bestätigen",
    "Forgot password?": "Passwort vergessen?",
    "Forgot Password": "Passwort vergessen",
    "Don't have an account?": "Noch kein Konto?",
    "Sign up for free!": "Kostenlos registrieren!",
    "All ready signed up?": "Bereits registriert?",
    "Error Signing In": "Fehler beim Anmelden",
    "Signed up successfully": "Erfolgreich registriert",
    "Error signing up": "Fehler bei der Registrierung",
    "Enter your email": "Gib deine E-Mail ein",
    "Send Reset Email": "Zurücksetzungs-E-Mail senden",
    "Email was sent to": "E-Mail wurde gesendet an",
    "Could not send reset email":
      "Zurücksetzungs-E-Mail konnte nicht gesendet werden",

    // Matches list
    "Team 1": "Team 1",
    "Team 2": "Team 2",
    Tournament: "Turnier",
    Scores: "Ergebnisse",
    Date: "Datum",
    Actions: "Aktionen",
    Owner: "Besitzer",
    Description: "Beschreibung",
    Comments: "Kommentare",
    Tags: "Tags",
    "Add player": "Spieler hinzufügen",
    "Add team": "Team hinzufügen",
    "Edit playlist": "Wiedergabeliste bearbeiten",
    "Merge Teams": "Teams zusammenführen",
    "Roster differences detected between teams:": "Kaderunterschiede zwischen Teams erkannt:",
    "Review the roster in the next step before merging.": "Überprüfen Sie den Kader im nächsten Schritt vor dem Zusammenführen.",
    "At least 2 team records must exist in your Teams list for the selected matches.": "Für die ausgewählten Spiele müssen mindestens 2 Teamdatensätze in Ihrer Teamliste vorhanden sein.",
    "Merge...": "Zusammenführen...",
    "Select 2 or more team records to merge into one.": "Wählen Sie 2 oder mehr Teamdatensätze zum Zusammenführen aus.",
    "Filter teams...": "Teams filtern...",
    "teams selected": "Teams ausgewählt",
    "Select Teams": "Teams auswählen",
    "Reconcile Roster": "Kader abgleichen",
    "Confirm Merge": "Zusammenführen bestätigen",
    "Merged team name": "Name des zusammengeführten Teams",
    "shirt number(s) have conflicting names — review below.": "Trikotnummer(n) haben widersprüchliche Namen – unten prüfen.",
    "Shirt": "Trikot",
    "players included": "Spieler einbezogen",
    "No players to reconcile.": "Keine Spieler zum Abgleichen.",
    "Use this name": "Diesen Namen verwenden",
    "Create merged team": "Zusammengeführtes Team erstellen",
    "with": "mit",
    "duplicate team records": "doppelte Teamdatensätze",
    "Consolidate duplicate player records and reassign playlist links": "Doppelte Spielerdatensätze zusammenführen und Playlist-Links neu zuweisen",
    "This cannot be undone.": "Dies kann nicht rückgängig gemacht werden.",
    "Teams being merged:": "Zusammenzuführende Teams:",
    "Step": "Schritt",
    "of": "von",
    "Merging...": "Zusammenführen...",
    "Players & Teams": "Spieler & Teams",
    "No players or teams assigned.": "Keine Spieler oder Teams zugeordnet.",
    "Search players...": "Spieler suchen...",
    "Search teams...": "Teams suchen...",
    "Saving...": "Speichern...",
    "My Matches": "Meine Spiele",
    "Shared Matches": "Geteilte Spiele",
    "Search team...": "Team suchen…",
    "All Teams": "Alle Teams",
    "Multi Match Reports": "Mehrspiel-Berichte",
    "Delete match": "Spiel löschen",
    "Remove from my shared matches": "Aus meinen geteilten Spielen entfernen",
    "Share match": "Spiel teilen",
    "Edit match": "Spiel bearbeiten",
    "Open video analysis": "Videoanalyse öffnen",
    "Delete Match": "Spiel löschen",
    "Are you sure you want to delete the selected match?":
      "Möchtest du das ausgewählte Spiel wirklich löschen?",
    "No matches selected.": "Keine Spiele ausgewählt.",

    // New Match
    "← Back": "← Zurück",
    "Team A (Home)": "Team A (Heim)",
    "Team B (Away)": "Team B (Auswärts)",
    "Select team…": "Team auswählen…",
    "Tournament (optional)": "Turnier (optional)",
    "Tournament / competition name": "Turnier- / Wettbewerbsname",
    "Match date": "Spieldatum",
    "Start Coding →": "Erfassung starten →",

    // Reports
    Summary: "Zusammenfassung",
    "Box Score": "Statistikübersicht",
    "Side-outs Report": "Side-out-Bericht",
    "Serve Receives": "Annahmen",
    "Attack Zones": "Angriffszonen",
    "Hitting Charts": "Angriffsdiagramme",
    "Video Analysis": "Videoanalyse",
    Dashboard: "Dashboard",
    Charts: "Diagramme",
    "AI Report": "KI-Bericht",
    // AI Reports page
    "AI Reports": "KI-Berichte",
    "AI Reports — Coming Soon": "KI-Berichte — Demnächst verfügbar",
    "AI-powered match reports and deep analysis are on the way. Here are some sample reports to show what's coming.":
      "KI-gestützte Spielberichte und Tiefenanalysen sind in Arbeit. Hier sind einige Beispielberichte als Vorschau.",
    "Preview not available.": "Vorschau nicht verfügbar.",
    "Open sample": "Beispiel öffnen",
    "No matches were loaded. Select one or more matches and try again.":
      "Es wurden keine Spiele geladen. Wählen Sie ein oder mehrere Spiele aus und versuchen Sie es erneut.",
    "Attack Analysis (English)": "Angriffsanalyse (Englisch)",
    "Blocking Analysis (Vietnamese)": "Blockanalyse (Vietnamesisch)",
    "Opponent Tendencies (Japanese)": "Gegnertendenzen (Japanisch)",
    // Deep AI analysis
    "Deep AI Analysis": "Tiefe KI-Analyse",
    "AI Deep Analysis": "KI-Tiefenanalyse",
    "Load at least one match with coded events to generate the analysis.": "Laden Sie mindestens ein Spiel mit codierten Aktionen, um die Analyse zu erstellen.",
    "Run the deep analysis one section at a time to keep each request small. Edit or add questions below, then generate. Best used with the Anthropic (Claude) provider.":
      "Führen Sie die Tiefenanalyse Abschnitt für Abschnitt aus, damit jede Anfrage klein bleibt. Bearbeiten oder ergänzen Sie die Fragen unten und generieren Sie dann. Am besten mit dem Anbieter Anthropic (Claude) nutzen.",
    "Download CSV": "CSV herunterladen",
    "Analysis section": "Analyseabschnitt",
    analysing: "analysiere",
    "All sections (uses the most credits)": "Alle Abschnitte (verbraucht die meisten Credits)",
    "All sections": "Alle Abschnitte",
    Questions: "Fragen",
    "one per line, edit or add your own": "eine pro Zeile, bearbeiten oder eigene hinzufügen",
    "Reset to defaults": "Auf Standard zurücksetzen",
    "Add a question…": "Frage hinzufügen…",
    "Runs every section using the questions you've set for each. This sends one large request and uses the most credits.":
      "Führt alle Abschnitte mit den von Ihnen festgelegten Fragen aus. Dies sendet eine große Anfrage und verbraucht die meisten Credits.",
    "Generate full analysis": "Vollständige Analyse erstellen",
    "Generate section analysis": "Abschnittsanalyse erstellen",
    Serving: "Aufschlag",
    Reception: "Annahme",
    Setting: "Zuspiel",
    Attack: "Angriff",
    Blocking: "Block",
    "Defence / Digging": "Abwehr / Baggern",
    "Rotation Analysis": "Rotationsanalyse",
    "Opponent Tendencies (Scouting)": "Gegnertendenzen (Scouting)",
    "Overall Team Metrics": "Gesamt-Teamkennzahlen",
    "Serve efficiency (aces, errors, and pressured receptions forced).": "Aufschlageffizienz (Asse, Fehler und erzwungener Annahmedruck).",
    "Which servers are most effective, and against which reception zones?": "Welche Aufschläger sind am effektivsten und gegen welche Annahmezonen?",
    "Which serve types (jump, float, jump-float) yield the best results?": "Welche Aufschlagarten (Sprung, Flatter, Sprungflatter) liefern die besten Ergebnisse?",
    "Reception quality breakdown (perfect, positive, negative, error).": "Aufschlüsselung der Annahmequalität (perfekt, positiv, negativ, Fehler).",
    "Which players struggle most under jump serves vs. float serves?": "Welche Spieler haben bei Sprungaufschlägen im Vergleich zu Flatteraufschlägen die größten Probleme?",
    "How does poor reception correlate with side-out efficiency?": "Wie hängt schlechte Annahme mit der Sideout-Effizienz zusammen?",
    "Set distribution by zone and by player.": "Zuspielverteilung nach Zone und Spieler.",
    "How does the setter perform under pressure (bad-pass situations)?": "Wie agiert der Zuspieler unter Druck (bei schlechter Annahme)?",
    "Tempo breakdown (quick, medium, high ball) and which yields the best attack outcome.": "Tempo-Aufschlüsselung (Schnell-, Mittel-, Hochball) und welches das beste Angriffsergebnis liefert.",
    "Kill percentage and error rate per player, per rotation, and per zone.": "Punktquote und Fehlerquote pro Spieler, pro Rotation und pro Zone.",
    "Which attack zones (pipe, cross, line) have the highest efficiency?": "Welche Angriffszonen (Pipe, Diagonal, Linie) haben die höchste Effizienz?",
    "How attack efficiency changes in transition vs. side-out.": "Wie sich die Angriffseffizienz in der Transition im Vergleich zum Sideout ändert.",
    "Block-per-set rate per player and rotation.": "Blöcke pro Satz je Spieler und Rotation.",
    "Which opposing attack zones are most successfully blocked?": "Welche gegnerischen Angriffszonen werden am erfolgreichsten geblockt?",
    "How often blocks result in kills vs. touch-outs.": "Wie oft führen Blöcke zu Punkten im Vergleich zu Block-Outs.",
    "Dig success rate per player and per court zone.": "Abwehrquote pro Spieler und Feldzone.",
    "Which attack types (tip, roll, hard swing) are hardest to defend?": "Welche Angriffsarten (Lob, Drive, harter Schlag) sind am schwersten zu verteidigen?",
    "How defensive efficiency affects transition attack success.": "Wie die Abwehreffizienz den Erfolg des Transition-Angriffs beeinflusst.",
    "Which rotations have the highest and lowest point differential?": "Welche Rotationen haben die höchste und niedrigste Punktdifferenz?",
    "Side-out and break-point percentages per rotation.": "Sideout- und Breakpoint-Quoten pro Rotation.",
    "Rotation-specific vulnerabilities (e.g. a weak serving or passing rotation).": "Rotationsspezifische Schwachstellen (z. B. eine schwache Aufschlag- oder Annahmerotation).",
    "The opponent's most frequently used attack zones and combinations.": "Die am häufigsten genutzten Angriffszonen und Kombinationen des Gegners.",
    "The opponent's go-to attackers in high-pressure situations.": "Die bevorzugten Angreifer des Gegners in Drucksituationen.",
    "The serve-receive patterns the opponent favours.": "Die vom Gegner bevorzugten Annahmemuster.",
    "Overall rally-win probability in different score situations.": "Gesamte Ballwechsel-Gewinnwahrscheinlichkeit in verschiedenen Spielstandsituationen.",
    "How performance changes in sets 1–3 vs. sets 4–5 (fatigue/pressure effects).": "Wie sich die Leistung in den Sätzen 1–3 im Vergleich zu den Sätzen 4–5 ändert (Ermüdungs-/Druckeffekte).",
    "The correlation between first-ball side-out percentage and match outcome.": "Der Zusammenhang zwischen der First-Ball-Sideout-Quote und dem Spielergebnis.",
    Report: "Bericht",

    // Import page
    "Load DVW file(s)...": "DVW-Datei(en) laden…",
    "Load PSVB file(s)...": "PSVB-Datei(en) laden…",
    "Load VSM file(s)...": "VSM-Datei(en) laden…",
    "Load playlist file...": "Wiedergabelisten-Datei laden…",
    "load one or more DataVolley files":
      "eine oder mehrere DataVolley-Dateien laden",
    "load one or more VBStats files":
      "eine oder mehrere VBStats-Dateien laden",
    "load one or more VolleyStation VSM files":
      "eine oder mehrere VolleyStation-VSM-Dateien laden",
    "Convert DVW": "DVW konvertieren",
    "VSM": "VSM",
    "Convert DVW to VSM...": "DVW zu VSM konvertieren…",
    "pick a DataVolley .dvw file to download as .vsm":
      "eine DataVolley-.dvw-Datei auswählen, um sie als .vsm herunterzuladen",
    "Convert VSM to DVW...": "VSM zu DVW konvertieren…",
    "pick a VolleyStation .vsm file to download as .dvw":
      "eine VolleyStation-.vsm-Datei auswählen, um sie als .dvw herunterzuladen",
    "Converting…": "Wird konvertiert…",
    "Converted": "Konvertiert",
    "Failed to convert": "Konvertierung fehlgeschlagen",
    "Beta testers only — coming soon for everyone else.":
      "Nur für Beta-Tester — demnächst für alle verfügbar.",
    "load a playlist": "eine Wiedergabeliste laden",
    "Use Shift-JIS Encoder": "Shift-JIS-Encoder verwenden",
    "Importing…": "Wird importiert…",
    "Save to Database?": "In Datenbank speichern?",
    "Always save imports automatically": "Importe immer automatisch speichern",
    "View Only": "Nur anzeigen",
    "Save to Database": "In Datenbank speichern",
    "Error saving to database": "Fehler beim Speichern in der Datenbank",
    "Saved to database": "In Datenbank gespeichert",
    "Saving to database…": "Wird in Datenbank gespeichert…",
    "Upload them to the database now?": "Jetzt in die Datenbank hochladen?",

    // Settings / Options
    Theme: "Design",
    Options: "Optionen",
    "Rotation naming convention": "Benennung der Rotationen",
    "Choose how rotations are labelled in reports and analysis.":
      "Lege fest, wie Rotationen in Berichten und Analysen benannt werden.",
    "R1–R6 (rotation sequence)": "R1–R6 (Rotationsreihenfolge)",
    "S1–S6 (setter court position)": "S1–S6 (Zuspieler-Feldposition)",
    "R1: setter in position 1, R2: setter in 6, R3: setter in 5, R4: setter in 4, R5: setter in 3, R6: setter in 2.":
      "R1: Zuspieler auf Position 1, R2: Zuspieler auf 6, R3: Zuspieler auf 5, R4: Zuspieler auf 4, R5: Zuspieler auf 3, R6: Zuspieler auf 2.",
    "S1: setter in 1, S2: setter in 2, S3: setter in 3, S4: setter in 4, S5: setter in 5, S6: setter in 6.":
      "S1: Zuspieler auf 1, S2: Zuspieler auf 2, S3: Zuspieler auf 3, S4: Zuspieler auf 4, S5: Zuspieler auf 5, S6: Zuspieler auf 6.",
    "Labels:": "Beschriftungen:",
    "Current theme:": "Aktuelles Design:",
    "Choose the language for the app interface.":
      "Wähle die Sprache für die App-Oberfläche.",
    "Create your own language package or edit a community one, with AI-assisted translation.":
      "Erstelle dein eigenes Sprachpaket oder bearbeite ein Community-Paket mit KI-gestützter Übersetzung.",

    // About / Footer
    "Copyright © Perana Sports {year}. All rights reserved.":
      "Copyright © Perana Sports {year}. Alle Rechte vorbehalten.",
    "VBLive is free to use right now. However Perana Sports reserves the right to charge a subscription fee in due course.":
      "VBLive ist derzeit kostenlos nutzbar. Perana Sports behält sich jedoch das Recht vor, zu gegebener Zeit eine Abonnementgebühr zu erheben.",
    "Querries / suggestions are most welcomed.":
      "Fragen / Vorschläge sind sehr willkommen.",

    // Live
    "Please enter server name to search":
      "Bitte gib den Servernamen zur Suche ein",
    "Broadcast Server": "Broadcast-Server",
    "Enter DVMateLive Server Name": "DVMateLive-Servernamen eingeben",
    LIVE: "LIVE",

    // Playlists list
    "Delete Playlist": "Wiedergabeliste löschen",
    "Are you sure you want to delete the selected playlist?":
      "Möchtest du die ausgewählte Wiedergabeliste wirklich löschen?",
    "My Playlists": "Meine Wiedergabelisten",
    "Shared Playlists": "Geteilte Wiedergabelisten",
    "Tags:": "Tags:",
    "Select...": "Auswählen…",
    "All Tags": "Alle Tags",

    // Share
    "User already added": "Benutzer bereits hinzugefügt",
    "Please enter a valid email address":
      "Bitte gib eine gültige E-Mail-Adresse ein",
    "Please select at least one user to share with":
      "Bitte wähle mindestens einen Benutzer zum Teilen aus",
    "Not Shared": "Nicht geteilt",
    "Available for everyone": "Für alle verfügbar",
    "Available to selected users": "Für ausgewählte Benutzer verfügbar",
    "Enter VBLive user email address":
      "VBLive-Benutzer-E-Mail-Adresse eingeben",
    "Selected users:": "Ausgewählte Benutzer:",
    "Save List": "Liste speichern",
    "Clear List": "Liste leeren",
    "Clear Users List": "Benutzerliste leeren",
    "Do you really want to clear all users from list?":
      "Möchtest du wirklich alle Benutzer aus der Liste entfernen?",
    "Do you want to save changes before closing?":
      "Möchtest du die Änderungen vor dem Schließen speichern?",
    "Enter name to save list to:":
      "Namen eingeben, unter dem die Liste gespeichert werden soll:",
    "Enter name to save list to":
      "Namen eingeben, unter dem die Liste gespeichert werden soll",

    // AI panels
    "AI settings": "KI-Einstellungen",
    "Setup needed": "Einrichtung erforderlich",
    "Generate report": "Bericht erstellen",
    "Generate AI report": "KI-Bericht erstellen",
    "Generate general report": "Allgemeinen Bericht erstellen",
    "Translate to…": "Übersetzen nach…",
    "Follow-up questions": "Anschlussfragen",
    "Ask a follow-up question…": "Stelle eine Anschlussfrage…",
    "Ask a follow-up question": "Stelle eine Anschlussfrage",
    "AI provider": "KI-Anbieter",
    Model: "Modell",
    Hide: "Ausblenden",
    Show: "Anzeigen",
    "Your key is stored only in this browser and sent directly to the selected provider.":
      "Dein Schlüssel wird nur in diesem Browser gespeichert und direkt an den ausgewählten Anbieter gesendet.",
    "Please enter your API key.": "Bitte gib deinen API-Schlüssel ein.",
    "Not enough data to analyse.": "Nicht genügend Daten zur Analyse.",
    "Select a player with recorded spikes first.":
      "Wähle zuerst einen Spieler mit erfassten Angriffen aus.",
    Player: "Spieler",
    "Select a player…": "Spieler auswählen…",
    "No players with recorded spikes in the selected matches.":
      "Keine Spieler mit erfassten Angriffen in den ausgewählten Spielen.",
    "Analyse with AI": "Mit KI analysieren",
    "AI scouting report": "KI-Scouting-Bericht",
    "Q:": "F:",
    Ask: "Fragen",
    "e.g. Which rotation should we target on serve?":
      "z. B. Welche Rotation sollten wir beim Aufschlag angreifen?",
    "Saved questions:": "Gespeicherte Fragen:",
    "Click to ask again": "Klicken, um erneut zu fragen",
    "Remove saved question": "Gespeicherte Frage entfernen",

    // AI language options
    Spanish: "Spanisch",
    French: "Französisch",
    German: "Deutsch",
    Italian: "Italienisch",
    Portuguese: "Portugiesisch",
    Dutch: "Niederländisch",
    Polish: "Polnisch",
    Russian: "Russisch",
    Turkish: "Türkisch",
    Arabic: "Arabisch",
    "Chinese (Simplified)": "Chinesisch (vereinfacht)",
    Japanese: "Japanisch",
    Korean: "Koreanisch",
    Vietnamese: "Vietnamesisch",
    Thai: "Thailändisch",

    // Box score
    "Show Full Headers": "Vollständige Überschriften anzeigen",
    "Show Short Headers": "Kurze Überschriften anzeigen",
    "Show Less Players": "Weniger Spieler anzeigen",
    "Show All Players": "Alle Spieler anzeigen",
    "Attack Kills": "Angriffspunkte",
    "Attack Errors": "Angriffsfehler",
    "Total Attempts": "Gesamtversuche",
    "Hitting Efficiency": "Angriffseffizienz",
    "Serve Aces": "Ass-Aufschläge",
    "Serve Errors": "Aufschlagfehler",
    "Pass Errors": "Annahmefehler",
    "% Perfect Passes": "% perfekte Annahmen",
    "Passing Average": "Annahmedurchschnitt",
    "Total Passes": "Annahmen gesamt",
    "Set Assists": "Zuspiel-Assists",
    Digs: "Abwehraktionen",
    "Momentum Chart": "Momentum-Diagramm",
    "Key event": "Schlüsselaktion",
    "Break Point %": "Breakpunkt %",
    "First Ball Sideout %": "Erste-Ball-Sideout %",
    "Sideout Transition %": "Sideout in der Transition %",
    "FB Sideout Kills %": "Erste-Ball-Angriffspunkte %",
    "Sideout Opposition Error %": "Sideout durch Gegnerfehler %",
    Plus: "Plus",
    Minus: "Minus",
    Blocks: "Blockpunkte",
    "Positive Serve %": "Positive Aufschläge %",
    Sideout: "Sideout",
    "Break point": "Breakpunkt",
    "Ace / Kill": "Ass / Punktangriff",
    "In play": "Im Spiel",
    Error: "Fehler",
    "Sideout on a kill": "Sideout durch Angriffspunkt",
    "Sideout on an error": "Sideout durch Fehler",
    "Top half": "Obere Hälfte",
    "Bottom half": "Untere Hälfte",
    "The centre line steps toward the team that wins each point.": "Die Mittellinie bewegt sich zu dem Team, das den jeweiligen Punkt gewinnt.",
    "No point-by-point data available for this match.": "Für dieses Spiel sind keine Punkt-für-Punkt-Daten verfügbar.",
    "No match data available.": "Keine Spieldaten verfügbar.",
    "This report isn't supported on this browser yet — please use Safari.": "Dieser Bericht wird in diesem Browser noch nicht unterstützt – bitte verwenden Sie Safari.",
    "No video available for this match.": "Kein Video für dieses Spiel verfügbar.",
    passes: "Annahmen",
    "eff.": "Eff.",
    Grade: "Note",
    LEFT: "LINKS",
    CENTRE: "MITTE",
    RIGHT: "RECHTS",
    SELECTED: "AUSGEWÄHLT",
    Passers: "Annahmespieler",
    Calls: "Zuspielansagen",
    "SIDE OUT": "SIDEOUT",
    TRANSITION: "ÜBERGANG",
    "No events in selection.": "Keine Ereignisse in der aktuellen Auswahl.",
    "Serve Receives By Players": "Annahmen nach Spielern",
    "Serve Receive By Zones": "Annahme nach Zonen",
    "Dig Errors": "Abwehrfehler",
    "Dig Average": "Abwehrdurchschnitt",
    "Block Solos": "Einzelblocks",
    "Block Assists": "Block-Assists",
    "Block Errors": "Blockfehler",
    "Points Scored": "Erzielte Punkte",
    TEAM: "TEAM",

    // Sideout report
    "ALL ROWS": "ALLE REIHEN",
    "ROWS ": "REIHEN ",
    "ROW ": "REIHE ",
    "FB Kills": "FB-Angriffspunkte",
    "Opp. Serve Errors": "Aufschlagfehler Gegner",
    "FB Opp. Errors": "FB-Fehler Gegner",
    "Kills/Blocks": "Angriffe/Blocks",
    "Opp. Errors": "Fehler Gegner",
    Unsuccessful: "Erfolglos",

    // Attack zones
    "No events in selected zone.":
      "Keine Ereignisse in der ausgewählten Zone.",
    "Show all attacks. Or click on a zone to show attacks in that zone.":
      "Alle Angriffe anzeigen. Oder klicke auf eine Zone, um die Angriffe in dieser Zone anzuzeigen.",
    "OPPONENTS (": "GEGNER (",

    // Video analysis filters
    "All Types": "Alle Typen",
    Serve: "Aufschlag",
    "Serve-Receive": "Annahme",
    Set: "Zuspiel",
    "Setter's Call": "Zuspieler-Ansage",
    "Disable this skill": "Diese Fertigkeit deaktivieren",
    "Enable this skill": "Diese Fertigkeit aktivieren",
    "Teams & Players": "Teams & Spieler",
    "Match State": "Spielstatus",
    "Custom Code": "Benutzerdefinierter Code",
    "Search... (comma-separated for multiple)": "Suchen … (mehrere durch Komma getrennt)",
    vs: "vs.",
    "Rewind 60 seconds": "60 Sekunden zurück",
    "Rewind 1 frame": "1 Einzelbild zurück",
    "Forward 1 frame": "1 Einzelbild vor",
    "Forward 60 seconds": "60 Sekunden vor",
    "Previous Clip": "Vorheriger Clip",
    Replay: "Wiederholen",
    "Next Clip": "Nächster Clip",
    Phases: "Phasen",
    "Score Range": "Punktebereich",
    "Set Winner": "Satzgewinner",
    Type: "Typ",
    Strength: "Stärke",
    "Setter Calls": "Zuspieler-Ansagen",
    "Setter Call": "Zuspieler-Ansage",
    Freeball: "Freeball",
    "Close (0-2)": "Knapp (0-2)",
    "Moderate (3-5)": "Mäßig (3-5)",
    "Lopsided (6+)": "Deutlich (6+)",
    "Focus Team": "Fokus-Team",
    Oppositions: "Gegner",
    "Focus Team Won": "Fokus-Team hat gewonnen",
    "Oppositions Won": "Gegner hat gewonnen",
    "Advanced Filters": "Erweiterte Filter",
    Players: "Spieler",
    Opponents: "Gegner",
    "Event Type": "Ereignistyp",
    "Event Result": "Ereignisergebnis",
    "Attack Type": "Angriffsart",
    "Hit Type": "Schlagart",
    "Block Type": "Blockart",
    "Attack Combo": "Angriffskombination",
    Apply: "Anwenden",
    Rotation: "Rotation",
    Skill: "Fertigkeit",
    "Lead Time": "Vorlaufzeit",
    Duration: "Dauer",
    Timing: "Timing",
    "Go Live": "Zum Livestream",
    "Playback has fallen behind the live broadcast — click to jump to now.": "Die Wiedergabe liegt hinter der Live-Übertragung zurück — klicken Sie, um zum aktuellen Zeitpunkt zu springen.",
    "Video timing": "Video-Timing",
    "Default lead time (seconds before an event to start playback) and clip duration, per skill.": "Standard-Vorlaufzeit (Sekunden vor einem Ereignis, ab denen die Wiedergabe beginnt) und Clip-Dauer, pro Fertigkeit.",
    Cloud: "Cloud",
    Local: "Lokal",
    "Relay host": "Relay-Host",
    "IP or hostname of the machine running the local relay.": "IP-Adresse oder Hostname des Computers, auf dem der lokale Relay läuft.",
    Spike: "Angriff",
    Block: "Block",
    Defence: "Abwehr",
    "All Results": "Alle Ergebnisse",
    Hard: "Hart",
    Soft: "Weich",
    Tip: "Lob",
    "Not Specified": "Nicht angegeben",
    "H - High": "H - Hoch",
    "M- Medium": "M - Mittel",
    "Q - Quick": "Q - Schnell",
    "T - Tense": "T - Flach",
    "U - Super": "U - Super",
    "F - Fast": "F - Schnell",
    "O - Other": "O - Andere",
    "B0 - No Block": "B0 - Kein Block",
    "B1 - 1 Block": "B1 - 1 Block",
    "B2 - 2 Blocks": "B2 - 2 Blocks",
    "B3 - 3 Blocks": "B3 - 3 Blocks",
    "BS - Seams": "BS - Lücken",

    // All filters panel
    ALL: "ALLE",
    NONE: "KEINE",

    // Playlist editor
    "Remove Item": "Element entfernen",
    "Do you really want to remove the selected item?":
      "Möchtest du das ausgewählte Element wirklich entfernen?",
    "Description is required": "Beschreibung ist erforderlich",
    "Comments are required": "Kommentare sind erforderlich",
    "Tags are required": "Tags sind erforderlich",
    "Playlist saved successfully": "Wiedergabeliste erfolgreich gespeichert",
    "Error saving playlist": "Fehler beim Speichern der Wiedergabeliste",

    // Language editor
    "My saved packages": "Meine gespeicherten Pakete",
    "Loading shared languages…": "Geteilte Sprachen werden geladen…",
    "Community shared languages": "Von der Community geteilte Sprachen",
    by: "von",
    "based on": "basierend auf",
    "or create new": "oder neu erstellen",
    Use: "Verwenden",
    "New Language Package": "Neues Sprachpaket",
    "Language Name": "Sprachname",
    "Language Code": "Sprachcode",
    "Flag Emoji": "Flaggen-Emoji",
    "Base language (reference)": "Basissprache (Referenz)",
    "If not English, both English and the reference language will be shown.":
      "Wenn nicht Englisch, werden sowohl Englisch als auch die Referenzsprache angezeigt.",
    "Start Editing": "Bearbeitung starten",
    "Save Key": "Schlüssel speichern",
    "AI key set": "KI-Schlüssel gesetzt",
    "Set API key for AI translation":
      "API-Schlüssel für KI-Übersetzung festlegen",
    "AI key ✓": "KI-Schlüssel ✓",
    "Set AI key": "KI-Schlüssel festlegen",
    "AI: Translate All": "KI: Alles übersetzen",
    "Translating…": "Wird übersetzt…",
    "AI translate section": "Abschnitt mit KI übersetzen",
    "Clear All": "Alles löschen",
    "Saving…": "Wird gespeichert…",
    English: "Englisch",
    new: "neu",
    "Enter translation…": "Übersetzung eingeben…",
    "Share with other VBLive users?":
      "Mit anderen VBLive-Benutzern teilen?",
    "Would you like to share the": "Möchtest du das",
    "language package with all VBLive users? Anyone can then copy and build upon it.":
      "Sprachpaket mit allen VBLive-Benutzern teilen? Jeder kann es dann kopieren und darauf aufbauen.",
    "No, just save for me": "Nein, nur für mich speichern",
    "Yes, share it!": "Ja, teilen!",
    "Language package saved and shared with all VBLive users!":
      "Sprachpaket gespeichert und mit allen VBLive-Benutzern geteilt!",
    "Language package saved!": "Sprachpaket gespeichert!",
    "Clear all translations? This cannot be undone.":
      "Alle Übersetzungen löschen? Dies kann nicht rückgängig gemacht werden.",

    // Additional UI phrases
    Teams: "Teams",
    "Shared By": "Geteilt von",
    "View Activity": "Ansichtsverlauf",
    Viewer: "Betrachter",
    "Date / Time": "Datum / Uhrzeit",
    "No views recorded yet.": "Noch keine Ansichten erfasst.",
    "Date / Scores": "Datum / Ergebnisse",
    "Stats API source": "Statistik-API-Quelle",
    "load one or more AVC VIS files": "eine oder mehrere AVC-VIS-Dateien laden",
    "Points to close a set": "Punkte zum Satzgewinn",
    "Require 2-point advantage": "2-Punkte-Vorsprung erforderlich",
    Roster: "Kader",
    Clone: "Duplizieren",
    "Edit team": "Team bearbeiten",
    "New player": "Neuer Spieler",
    "Search player...": "Spieler suchen…",
    "Match Centre": "Spielzentrale",
    "Points Won": "Gewonnene Punkte",
    "Opp. Error": "Gegnerfehler",
    Total: "Gesamt",
    "Match Skills": "Spieltechniken",
    Dig: "Abwehr",
    "Best Scorers": "Beste Punktesammler",
    "Power Play": "Power Play",
    Match: "Spiel",
    "Player scouting": "Spieler-Scouting",
    "Team deep analysis": "Team-Tiefenanalyse",
    "All Players": "Alle Spieler",
    "All Sets": "Alle Sätze",
    "All Rotations": "Alle Rotationen",
    "All Combos": "Alle Kombinationen",
    "All Calls": "Alle Zuspielansagen",
    "All Events": "Alle Ereignisse",
    "Select all items": "Alle Einträge auswählen",
    "Unselect all items": "Alle Einträge abwählen",
    "Create play list": "Playlist erstellen",
    "Point by Point": "Punkt für Punkt",
    // i18n backfill pass — previously English-fallback-only keys
    "Access denied": "Zugriff verweigert",
    "Aces": "Asse",
    "ACES": "ASSE",
    "Add report": "Bericht hinzufügen",
    "Admin": "Admin",
    "Admins": "Admins",
    "Advanced": "Erweitert",
    "All set!": "Alles bereit!",
    "Analyse team with AI": "Team mit KI analysieren",
    "Are you sure you want to delete the selected play list?": "Möchten Sie die ausgewählte Playlist wirklich löschen?",
    "Assign": "Zuweisen",
    "Assign playlist": "Playlist zuweisen",
    "Assigned": "Zugewiesen",
    "At least one admin is required.": "Mindestens ein Admin ist erforderlich.",
    "Attack Combo / Setter Call": "Angriffskombination / Zuspielansage",
    "Attack diagram": "Angriffsdiagramm",
    "Attacks": "Angriffe",
    "ATTACKS": "ANGRIFFE",
    "Auto-detected a custom set-closing score for": "Automatisch erkannter benutzerdefinierter Satzendstand für",
    "BLOCK": "BLOCK",
    "BLOCKS": "BLÖCKE",
    "Beta testers": "Betatester",
    "Clear filter": "Filter zurücksetzen",
    "Close Set": "Satz beenden",
    "Close menu": "Menü schließen",
    "Code": "Code",
    "Coding defaults & shortcuts": "Scouting-Standardwerte & Tastenkürzel",
    "Continue": "Weiter",
    "Could not load match data. The match may no longer be available.": "Spieldaten konnten nicht geladen werden. Das Spiel ist möglicherweise nicht mehr verfügbar.",
    "Could not load stats for these matches.": "Statistiken für diese Spiele konnten nicht geladen werden.",
    "Could not load this match.": "Dieses Spiel konnte nicht geladen werden.",
    "Could not load this playlist.": "Diese Playlist konnte nicht geladen werden.",
    "Could not load this report.": "Dieser Bericht konnte nicht geladen werden.",
    "Could not save the report.": "Der Bericht konnte nicht gespeichert werden.",
    "Could not share the report. Please try again.": "Der Bericht konnte nicht geteilt werden. Bitte versuchen Sie es erneut.",
    "Court horizontal (net vertical)": "Spielfeld horizontal (Netz vertikal)",
    "Court vertical (net horizontal)": "Spielfeld vertikal (Netz horizontal)",
    "Create": "Erstellen",
    "Create a new team from this roster": "Neues Team aus diesem Kader erstellen",
    "DIFFERENCE": "DIFFERENZ",
    "DV Code": "DV-Code",
    "Delete this report?": "Diesen Bericht löschen?",
    "Detected a custom set-closing score of": "Benutzerdefinierter Satzendstand erkannt:",
    "Double Sub": "Doppelwechsel",
    "Double Sub: now pick a bench player for the flashing OPPOSITE slot…": "Doppelwechsel: Wählen Sie jetzt einen Ersatzspieler für die blinkende Diagonalspieler-Position…",
    "Double Sub: pick a bench player for the flashing SETTER slot…": "Doppelwechsel: Wählen Sie einen Ersatzspieler für die blinkende Zuspieler-Position…",
    "Double Substitution — swap the Setter and Opposite for two incoming players": "Doppelwechsel — Zuspieler und Diagonalspieler gegen zwei neue Spieler austauschen",
    "Drag to draw the ball path; pause briefly to mark a touch (reception/dig/block), release to finish.": "Ziehen Sie, um den Ballweg zu zeichnen; kurz anhalten, um eine Berührung (Annahme/Abwehr/Block) zu markieren, loslassen zum Beenden.",
    "Draw": "Zeichnen",
    "Draw & Code": "Zeichnen & Codieren",
    "Draw & Code — draw ball paths on the court to code the rally": "Zeichnen & Codieren — Ballwege auf dem Spielfeld zeichnen, um den Ballwechsel zu codieren",
    "Edit Lineup": "Aufstellung bearbeiten",
    "Edit Lineup — freely rearrange the starting lineup before the set begins": "Aufstellung bearbeiten — Startaufstellung vor Satzbeginn frei anpassen",
    "Emails in this list can manage beta testers and other admins.": "E-Mail-Adressen in dieser Liste können Betatester und andere Admins verwalten.",
    "Emails in this list get early access to AI Reports and other gated features. Changes take effect immediately.": "E-Mail-Adressen in dieser Liste erhalten frühzeitigen Zugriff auf KI-Berichte und andere eingeschränkte Funktionen. Änderungen werden sofort wirksam.",
    "Enter a valid email address.": "Bitte geben Sie eine gültige E-Mail-Adresse ein.",
    "Exit fullscreen": "Vollbild beenden",
    "Extended": "Erweitert",
    "Failed to generate PDF.": "PDF-Erstellung fehlgeschlagen.",
    "Failed to save changes.": "Änderungen konnten nicht gespeichert werden.",
    "Filtered by": "Gefiltert nach",
    "First name": "Vorname",
    "For stronger security later, consider moving admin checks to a Firebase Auth custom claim instead of this email list.": "Für mehr Sicherheit in Zukunft sollten Admin-Prüfungen zu einem benutzerdefinierten Firebase Auth-Claim statt dieser E-Mail-Liste verschoben werden.",
    "Fullscreen": "Vollbild",
    "Generate an AI report from these matches": "KI-Bericht aus diesen Spielen erstellen",
    "How often (in seconds) the events list is updated during a live session.": "Wie oft (in Sekunden) die Ereignisliste während einer Live-Sitzung aktualisiert wird.",
    "Last name": "Nachname",
    "Leave blank to use standard rules (25 points, 15 for a 5th/deciding set)": "Leer lassen, um Standardregeln zu verwenden (25 Punkte, 15 im 5. Entscheidungssatz)",
    "Live session refresh interval": "Aktualisierungsintervall der Live-Sitzung",
    "Load VIS file(s)...": "VIS-Datei(en) laden…",
    "Load this team's last-saved starting lineup (positions, setter and liberos)": "Zuletzt gespeicherte Startaufstellung dieses Teams laden (Positionen, Zuspieler und Libero)",
    "Loading…": "Wird geladen…",
    "Manually end the current set at its current score, regardless of the configured winning score / 2-point advantage rule": "Aktuellen Satz manuell mit dem aktuellen Stand beenden, unabhängig von der konfigurierten Gewinnpunktzahl / 2-Punkte-Vorsprung-Regel",
    "Migration finished with some issues": "Migration mit einigen Problemen abgeschlossen",
    "Most of your data was migrated, but some items could not be moved. You can retry now or continue and try again later.": "Der Großteil Ihrer Daten wurde migriert, einige Elemente konnten jedoch nicht verschoben werden. Sie können es jetzt erneut versuchen oder fortfahren und es später erneut versuchen.",
    "Mute": "Stumm",
    "New team": "Neues Team",
    "Next": "Weiter",
    "Nickname": "Spitzname",
    "No Power Play": "Kein Power Play",
    "No admins yet.": "Noch keine Admins.",
    "No beta testers yet.": "Noch keine Betatester.",
    "No coded events to analyse.": "Keine codierten Ereignisse zum Analysieren.",
    "No coded events to export.": "Keine codierten Ereignisse zum Exportieren.",
    "No matches.": "Keine Spiele.",
    "No players added yet.": "Noch keine Spieler hinzugefügt.",
    "No players recorded.": "Keine Spieler erfasst.",
    "No players yet.": "Noch keine Spieler.",
    "No playlists assigned.": "Keine Playlists zugewiesen.",
    "No playlists found.": "Keine Playlists gefunden.",
    "No reports saved.": "Keine Berichte gespeichert.",
    "No roster players available.": "Keine Kaderspieler verfügbar.",
    "No substitutions": "Keine Wechsel",
    "No teams found.": "Keine Teams gefunden.",
    "No teams yet.": "Noch keine Teams.",
    "No teams.": "Keine Teams.",
    "Nominate Setter": "Zuspieler bestimmen",
    "Notify these users by email (optional)": "Diese Benutzer per E-Mail benachrichtigen (optional)",
    "OPP REC": "GEGN. ANNAHME",
    "OPP. ERRORS": "GEGN. FEHLER",
    "Open / Download": "Öffnen / Herunterladen",
    "Open local file": "Lokale Datei öffnen",
    "Open menu": "Menü öffnen",
    "Opposition Passing — use the opposition's reception formation on the diagram": "Gegnerische Annahme — die Annahmeformation des Gegners im Diagramm verwenden",
    "Paste the report (Markdown supported)…": "Bericht einfügen (Markdown wird unterstützt)…",
    "Pause": "Pause",
    "Play": "Abspielen",
    "Playing squad": "Spielkader",
    "Playing squad is full. Remove a player from the squad before adding another.": "Der Spielkader ist voll. Entfernen Sie einen Spieler aus dem Kader, bevor Sie einen weiteren hinzufügen.",
    "Please sign in to save reports.": "Bitte melden Sie sich an, um Berichte zu speichern.",
    "Power Play Only": "Nur Power Play",
    "Record a team error (no player) — edit later to add the player and event type": "Einen Teamfehler erfassen (ohne Spieler) — später bearbeiten, um Spieler und Ereignistyp hinzuzufügen",
    "Record at least one event before making substitutions": "Erfassen Sie mindestens ein Ereignis, bevor Sie Wechsel vornehmen",
    "Redo": "Wiederholen",
    "Remove": "Entfernen",
    "Remove share": "Freigabe entfernen",
    "Remove yourself as admin? You will immediately lose access to this page.": "Sich selbst als Admin entfernen? Sie verlieren sofort den Zugriff auf diese Seite.",
    "Report saved and shared.": "Bericht gespeichert und geteilt.",
    "Report saved to the player's profile.": "Bericht im Spielerprofil gespeichert.",
    "Report title": "Berichtstitel",
    "Reports": "Berichte",
    "Result": "Ergebnis",
    "Retry": "Erneut versuchen",
    "Return to live coding": "Zurück zur Live-Erfassung",
    "Rotation is not permitted while making substitutions": "Rotation ist während der Durchführung von Wechseln nicht erlaubt",
    "SERVE": "AUFSCHLAG",
    "SETS": "SÄTZE",
    "Save & Share": "Speichern & Teilen",
    "Save this report so you can share it with other users": "Diesen Bericht speichern, damit Sie ihn mit anderen Benutzern teilen können",
    "Save this report to the player's profile": "Diesen Bericht im Spielerprofil speichern",
    "Save to player": "Für Spieler speichern",
    "Saved lineup": "Gespeicherte Aufstellung",
    "Scroll, drag or swipe to scrub": "Scrollen, Ziehen oder Wischen zum Spulen",
    "Search existing players...": "Vorhandene Spieler suchen…",
    "Search playlists…": "Playlists durchsuchen…",
    "Select a match video to view while entering lineups.": "Wählen Sie ein Spielvideo aus, das Sie beim Eingeben der Aufstellungen ansehen möchten.",
    "Select a player.": "Wählen Sie einen Spieler aus.",
    "Select a team.": "Wählen Sie ein Team aus.",
    "Setting up your account": "Ihr Konto wird eingerichtet",
    "Shaded rows are played under a Power Play — HP/AP marks the calling team, bold gold scores are bonus (double) points.": "Schattierte Zeilen wurden während eines Power Plays gespielt — HP/AP markiert das aufrufende Team, fett goldene Punktzahlen sind Bonus-(Doppel-)Punkte.",
    "Shared by": "Geteilt von",
    "Shared with me": "Mit mir geteilt",
    "Show help": "Hilfe anzeigen",
    "Show public playlists": "Öffentliche Playlists anzeigen",
    "Standard": "Standard",
    "Substitutions made — tap to view": "Wechsel durchgeführt — zum Ansehen tippen",
    "Tap the blocking player.": "Tippen Sie auf den blockierenden Spieler.",
    "Team colour": "Teamfarbe",
    "Team name": "Teamname",
    "That email is already in the list.": "Diese E-Mail-Adresse ist bereits in der Liste.",
    "The report is too large to save. Try a single match or fewer sets.": "Der Bericht ist zu groß zum Speichern. Versuchen Sie ein einzelnes Spiel oder weniger Sätze.",
    "This match has no Power Play calls.": "Dieses Spiel hat keine Power-Play-Aufrufe.",
    "This player has no name to save the report against.": "Dieser Spieler hat keinen Namen, gegen den der Bericht gespeichert werden könnte.",
    "This team has no designated setter. Pick the player who is setting.": "Dieses Team hat keinen festgelegten Zuspieler. Wählen Sie den Spieler aus, der zuspielt.",
    "Toggle video player": "Videoplayer umschalten",
    "Unmute": "Stummschaltung aufheben",
    "Untitled playlist": "Unbenannte Playlist",
    "Video URL…": "Video-URL…",
    "We're moving your data to a faster, more reliable home. This happens only once.": "Wir verschieben Ihre Daten an einen schnelleren, zuverlässigeren Ort. Dies geschieht nur einmal.",
    "You don't have permission to view this page.": "Sie haben keine Berechtigung, diese Seite anzuzeigen.",
    "Your data has been migrated successfully.": "Ihre Daten wurden erfolgreich migriert.",
    "Your teams and players will appear here after your data is migrated.": "Ihre Teams und Spieler werden hier angezeigt, sobald Ihre Daten migriert wurden.",
    "bonus point": "Bonuspunkt",
    "bonus points": "Bonuspunkte",
    "event": "Ereignis",
    "events": "Ereignisse",
    "file(s).": "Datei(en).",
    "is not on this team's roster.": "steht nicht im Kader dieses Teams.",
    "loaded": "geladen",
    "matches": "Spiele",
    "more": "weitere",
    "players": "Spieler",
    "points (no value was entered) — applied automatically.": "Punkte (es wurde kein Wert eingegeben) — automatisch angewendet.",
    "roster": "Kader",
    "seconds": "Sekunden",
    "selected": "ausgewählt",
    "session(s)?": "Sitzung(en)?",
    "showing": "zeigt",
    "tournaments": "Turniere",
    "…and": "…und",
    "● marks which team served the point.": "● markiert das Team, das den Punkt serviert hat.",
    // i18n backfill pass (Catapult + misc)
    "Centimetres (cm)": "Zentimeter (cm)",
    "Inches (in)": "Zoll (in)",
    "Jump height units": "Sprunghöheneinheiten",
    "Choose how Catapult jump heights are displayed and filtered.": "Wählen Sie, wie Catapult-Sprunghöhen angezeigt und gefiltert werden.",
    "Select Catapult Focus file...": "Catapult-Focus-Datei auswählen…",
    "Select a Catapult Focus CSV export to view jump data for this match.": "Wählen Sie einen Catapult-Focus-CSV-Export aus, um die Sprungdaten für dieses Spiel anzuzeigen.",
    "Loaded from saved match data": "Aus gespeicherten Spieldaten geladen",
    "No jump data found in this file.": "In dieser Datei wurden keine Sprungdaten gefunden.",
    "Could not read this file. Please check it is a Catapult Focus CSV export.": "Diese Datei konnte nicht gelesen werden. Bitte prüfen Sie, ob es sich um einen Catapult-Focus-CSV-Export handelt.",
    "Could not save jump data with this match.": "Sprungdaten konnten diesem Spiel nicht zugeordnet gespeichert werden.",
    "Jump data saved with this match.": "Sprungdaten wurden mit diesem Spiel gespeichert.",
    "Show players with no match stats": "Spieler ohne Spielstatistiken anzeigen",
    "hidden": "ausgeblendet",
    "jumps": "Sprünge",
    "periods": "Perioden",
    "Sync offset": "Synchronisierungsversatz",
    "matched": "übereinstimmend",
    "match events now show jump height/duration in Video Analysis": "Spielereignisse zeigen jetzt Sprunghöhe/-dauer in der Videoanalyse an",
    "Team": "Team",
    "Team A": "Team A",
    "Team B": "Team B",
    "Which team is this Catapult file for?": "Für welches Team ist diese Catapult-Datei?",
    "We couldn't confidently match the players in this Focus file to one team. Please select which team it belongs to.": "Wir konnten die Spieler in dieser Focus-Datei nicht eindeutig einem Team zuordnen. Bitte wählen Sie, zu welchem Team sie gehört.",
    "This match has no video attached yet.": "Diesem Spiel ist noch kein Video zugeordnet.",
    "No synced jump events found for this player.": "Für diesen Spieler wurden keine synchronisierten Sprungereignisse gefunden.",
    "No match loaded.": "Kein Spiel geladen.",
    "avg": "Ø",
    "max": "max.",
    "Sync": "Synchronisation",
    "Jump Report": "Sprungbericht",
    "Watch Video": "Video ansehen",
    "Save PDF": "PDF speichern",
    "Generated by VBLive": "Erstellt mit VBLive",
    "Deep analysis of {team} from ball-by-ball event data, covering serving, reception, setting, attack, blocking, defence, rotations and opponent tendencies. Best used with the Anthropic (Claude) provider.": "Tiefgehende Analyse von {team} basierend auf Ballwechsel-für-Ballwechsel-Daten, einschließlich Aufschlag, Annahme, Zuspiel, Angriff, Block, Abwehr, Rotationen und gegnerischen Tendenzen. Am besten mit dem Anthropic-(Claude)-Anbieter verwendet.",
    "teams": "Teams",
  },

  fr: {
    // Information page
    Information: "Informations",
    "Release notes": "Notes de version",
    "Contact us": "Nous contacter",
    "Questions, feedback or suggestions are most welcome.": "Vos questions, retours ou suggestions sont les bienvenus.",
    // DV match coding
    Lineups: "Compositions",
    Point: "Point",
    Video: "Vidéo",
    Defaults: "Valeurs par défaut",
    "[team][NN][skill][type?][eval][zoneFrom?[szFrom?][zoneTo?[szTo?]]]": "[équipe][NN][geste][type?][éval][zoneDe?[szDe?][zoneVers?[szVers?]]]",
    Vertical: "Vertical",
    Horizontal: "Horizontal",
    "Swap sides": "Inverser les côtés",
    Subs: "Remplacements",
    Substitutions: "Remplacements",
    Court: "Terrain",
    Home: "Domicile",
    Away: "Extérieur",
    Games: "Sets",
    Enter: "Entrer",
    Undo: "Annuler",
    "Match saved": "Match enregistré",
    "Please sign in to save this match.": "Veuillez vous connecter pour enregistrer ce match.",
    "Nothing to save yet.": "Rien à enregistrer pour l'instant.",
    "Could not save the match. Please try again.": "Impossible d'enregistrer le match. Veuillez réessayer.",
    Discard: "Annuler les modifications",
    "Resumed autosaved coding": "Reprise du codage enregistré automatiquement",
    "Changes discarded": "Modifications annulées",
    "Discard all changes and revert to the starting point?":
      "Annuler toutes les modifications et revenir au point de départ ?",
    Clear: "Effacer",
    "Prev set": "Set précédent",
    "Next set": "Set suivant",
    "Match Setup": "Configuration du match",
    "First serve": "Premier service",
    "Start Match": "Démarrer le match",
    "Resume Match": "Reprendre le match",
    Skip: "Passer",
    "Select an event then drag to draw ball path": "Sélectionnez un événement puis faites glisser pour tracer la trajectoire du ballon",
    "Drag on court to set ball path": "Faites glisser sur le terrain pour définir la trajectoire du ballon",
    "No events yet – enter a DV code above and press Enter.": "Aucun événement – saisissez un code DV ci-dessus et appuyez sur Entrer.",
    "DataVolley code reference": "Référence du code DataVolley",
    Format: "Format",
    Skills: "Gestes",
    Evaluation: "Évaluation",
    "Skill / type omission": "Omission du geste / type",
    "Skill and/or type may be omitted; defaults fill in the rest.": "Le geste et/ou le type peuvent être omis ; les valeurs par défaut complètent le reste.",
    "Dot-coding (S→R and A→B pairs)": "Codage par point (paires S→R et A→B)",
    "Chaining & aliases": "Enchaînement et alias",
    "team:": "équipe :",
    "omit → defaults to A": "omettre → A par défaut",
    "NN: 1 or 2 digit jersey, e.g. 1, 01, 12": "NN : numéro de maillot à 1 ou 2 chiffres, ex. 1, 01, 12",
    "sub-zone: a b c d appended to zone digit, e.g. 5b or 5b1a": "sous-zone : a b c d ajouté au chiffre de zone, ex. 5b ou 5b1a",
    "S Serve · R Reception · E Set · A Attack · B Block · D Dig · F Freeball": "S Service · R Réception · E Passe · A Attaque · B Contre · D Défense · F Balle libre",
    "# kill/ace · + positive · ! ok · - negative · / overpass · = error": "# point/ace · + positif · ! correct · - négatif · / renvoi · = faute",
    "E.g.": "Ex.",
    "serve with skill=A (default), type=H, eval=+, zone=5.": "service avec skill=A (par défaut), type=H, eval=+, zone=5.",
    "type-only (no skill marker), defaults skill from context.": "type seul (sans marqueur de geste), geste par défaut selon le contexte.",
    "serve coded, reception auto-linked.": "service codé, réception liée automatiquement.",
    "attack coded, block auto-linked.": "attaque codée, contre lié automatiquement.",
    "Dot connects the reception eval to the corresponding serve eval via the serve→receive mapping.": "Le point relie l'évaluation de la réception à l'évaluation de service correspondante via la correspondance service→réception.",
    "two events in one string.": "deux événements en une seule chaîne.",
    "manual point A/B.": "point manuel A/B.",
    "serve ace/error.": "ace/faute de service.",
    "serve with eval.": "service avec évaluation.",
    // Stats, sideouts, filters & video tooltips
    "Block Kills": "Points de bloc",
    "Side Out Percentage": "Pourcentage de side-out",
    "All First Ball": "Toutes premières balles",
    "First Ball Kills": "Points sur première balle",
    "Good Serve": "Bon service",
    "Break Points": "Points de break",
    Game: "Set",
    Row: "Position",
    "First Ball Sideouts": "Side-outs première balle",
    "All Sideouts": "Tous les side-outs",
    All: "Tous",
    None: "Aucun",
    Receiver: "Réceptionneur",
    "# Passes": "Nb réceptions",
    "Perfect %": "% parfait",
    "Positive %": "% positif",
    "Sideout %": "% side-out",
    "FB Sideout %": "% side-out 1re balle",
    "Pass Hits": "Attaques après réception",
    "Pass Hit Kills": "Points après réception",
    "Pass Hit Errors": "Fautes après réception",
    "Show Passes": "Afficher les réceptions",
    "Show Attacks": "Afficher les attaques",
    "Error Pass": "Réception ratée",
    "1 Pass": "Réception 1",
    "2 Pass": "Réception 2",
    "Perfect Pass": "Réception parfaite",
    "Successful SO": "Side-out réussi",
    "First Ball SO": "Side-out 1re balle",
    "Unsuccessful SO": "Side-out manqué",
    RECEIVES: "RÉCEPTIONS",
    Sets: "Sets",
    Rotations: "Rotations",
    "Attack Combos": "Combinaisons d'attaque",
    Stages: "Phases",
    Display: "Affichage",
    Setters: "Passeurs",
    Attackers: "Attaquants",
    Results: "Résultats",
    Source: "Source",
    Cones: "Zones",
    "Select None": "Tout désélectionner",
    "All Serves": "Tous les services",
    "All Returns": "Toutes les réceptions",
    Filters: "Filtres",
    "Reset all filters": "Réinitialiser tous les filtres",
    "Show video clips of filtered attacks": "Afficher les clips vidéo des attaques filtrées",
    "Spikes for the selected player": "Attaques du joueur sélectionné",
    "Click on the zones to view video of attacks": "Cliquez sur les zones pour voir les vidéos des attaques",
    "Expand All": "Tout développer",
    "Collapse All": "Tout réduire",
    "Import Radar File": "Importer un fichier Radar",
    "Select local video file": "Sélectionner un fichier vidéo local",
    "Save to database": "Enregistrer dans la base de données",
    "Load and play online video": "Charger et lire la vidéo en ligne",
    "Synchronise video with events": "Synchroniser la vidéo avec les événements",
    "Rewind 10 seconds": "Reculer de 10 secondes",
    "Rewind 1 second": "Reculer d'1 seconde",
    "Forward 1 second": "Avancer d'1 seconde",
    "Forward 10 seconds": "Avancer de 10 secondes",
    "Set timing for events": "Définir le minutage des événements",

    // Navigation
    Matches: "Matchs",
    Live: "Direct",
    Import: "Importer",
    Playlists: "Listes de lecture",
    "New Match": "Nouveau match",
    // New Match / Match Coding gate
    "Match Coding": "Codage du match",
    "Match Coding — Coming Soon": "Codage du match — Bientôt disponible",
    "Live match coding is on the way. Here's a preview of what's coming.":
      "Le codage de match en direct arrive bientôt. Voici un aperçu de ce qui vous attend.",
    Settings: "Paramètres",
    Language: "Langue",
    "Your profile": "Votre profil",
    "Sign out": "Se déconnecter",
    "Translation Editor": "Éditeur de traduction",

    // Common actions
    Save: "Enregistrer",
    Cancel: "Annuler",
    Delete: "Supprimer",
    Share: "Partager",
    Edit: "Modifier",
    New: "Nouveau",
    Refresh: "Actualiser",
    Search: "Rechercher",
    Reset: "Réinitialiser",
    Close: "Fermer",
    "Sub for": "Remplacer",
    "No players available": "Aucun joueur disponible",
    "Show full roster": "Afficher tout l'effectif",
    "Available only": "Disponibles uniquement",
    "Maximum substitutions reached": "Nombre maximal de remplacements atteint",
    Back: "Retour",
    "Download PDF": "Télécharger le PDF",
    "Loading...": "Chargement…",
    "Select All": "Tout sélectionner",
    "Unselect All": "Tout désélectionner",
    Yes: "Oui",
    No: "Non",
    Add: "Ajouter",
    Load: "Charger",
    Confirm: "Confirmer",
    Open: "Ouvrir",
    show: "afficher",

    // Auth
    "Sign In": "Se connecter",
    "Sign Up": "S'inscrire",
    "Sign Out": "Se déconnecter",
    Email: "E-mail",
    Password: "Mot de passe",
    "Confirm Password": "Confirmer le mot de passe",
    "Forgot password?": "Mot de passe oublié ?",
    "Forgot Password": "Mot de passe oublié",
    "Don't have an account?": "Vous n'avez pas de compte ?",
    "Sign up for free!": "Inscrivez-vous gratuitement !",
    "All ready signed up?": "Déjà inscrit ?",
    "Error Signing In": "Erreur de connexion",
    "Signed up successfully": "Inscription réussie",
    "Error signing up": "Erreur lors de l'inscription",
    "Enter your email": "Saisissez votre e-mail",
    "Send Reset Email": "Envoyer l'e-mail de réinitialisation",
    "Email was sent to": "L'e-mail a été envoyé à",
    "Could not send reset email":
      "Impossible d'envoyer l'e-mail de réinitialisation",

    // Matches list
    "Team 1": "Équipe 1",
    "Team 2": "Équipe 2",
    Tournament: "Tournoi",
    Scores: "Scores",
    Date: "Date",
    Actions: "Actions",
    Owner: "Propriétaire",
    Description: "Description",
    Comments: "Commentaires",
    Tags: "Tags",
    "Add player": "Ajouter un joueur",
    "Add team": "Ajouter une équipe",
    "Edit playlist": "Modifier la liste de lecture",
    "Merge Teams": "Fusionner les équipes",
    "Roster differences detected between teams:": "Différences de composition détectées entre les équipes :",
    "Review the roster in the next step before merging.": "Vérifiez l'effectif à l'étape suivante avant de fusionner.",
    "At least 2 team records must exist in your Teams list for the selected matches.": "Au moins 2 enregistrements d'équipe doivent exister dans votre liste pour les matchs sélectionnés.",
    "Merge...": "Fusionner...",
    "Select 2 or more team records to merge into one.": "Sélectionnez 2 enregistrements d'équipe ou plus à fusionner.",
    "Filter teams...": "Filtrer les équipes...",
    "teams selected": "équipes sélectionnées",
    "Select Teams": "Sélectionner les équipes",
    "Reconcile Roster": "Réconcilier l'effectif",
    "Confirm Merge": "Confirmer la fusion",
    "Merged team name": "Nom de l'équipe fusionnée",
    "shirt number(s) have conflicting names — review below.": "numéro(s) ont des noms conflictuels — vérifier ci-dessous.",
    "Shirt": "Maillot",
    "players included": "joueurs inclus",
    "No players to reconcile.": "Aucun joueur à réconcilier.",
    "Use this name": "Utiliser ce nom",
    "Create merged team": "Créer l'équipe fusionnée",
    "with": "avec",
    "duplicate team records": "enregistrements d'équipe en double",
    "Consolidate duplicate player records and reassign playlist links": "Consolider les doublons de joueurs et réaffecter les liens de liste de lecture",
    "This cannot be undone.": "Cette action est irréversible.",
    "Teams being merged:": "Équipes en cours de fusion :",
    "Step": "Étape",
    "of": "sur",
    "Merging...": "Fusion en cours...",
    "Players & Teams": "Joueurs & Équipes",
    "No players or teams assigned.": "Aucun joueur ou équipe associé.",
    "Search players...": "Rechercher des joueurs...",
    "Search teams...": "Rechercher des équipes...",
    "Saving...": "Enregistrement...",
    "My Matches": "Mes matchs",
    "Shared Matches": "Matchs partagés",
    "Search team...": "Rechercher une équipe…",
    "All Teams": "Toutes les équipes",
    "Multi Match Reports": "Rapports multi-matchs",
    "Delete match": "Supprimer le match",
    "Remove from my shared matches": "Retirer de mes matchs partagés",
    "Share match": "Partager le match",
    "Edit match": "Modifier le match",
    "Open video analysis": "Ouvrir l'analyse vidéo",
    "Delete Match": "Supprimer le match",
    "Are you sure you want to delete the selected match?":
      "Voulez-vous vraiment supprimer le match sélectionné ?",
    "No matches selected.": "Aucun match sélectionné.",

    // New Match
    "← Back": "← Retour",
    "Team A (Home)": "Équipe A (domicile)",
    "Team B (Away)": "Équipe B (extérieur)",
    "Select team…": "Sélectionner une équipe…",
    "Tournament (optional)": "Tournoi (facultatif)",
    "Tournament / competition name": "Nom du tournoi / de la compétition",
    "Match date": "Date du match",
    "Start Coding →": "Commencer le codage →",

    // Reports
    Summary: "Résumé",
    "Box Score": "Feuille de stats",
    "Side-outs Report": "Rapport de side-outs",
    "Serve Receives": "Réceptions",
    "Attack Zones": "Zones d'attaque",
    "Hitting Charts": "Graphiques d'attaque",
    "Video Analysis": "Analyse vidéo",
    Dashboard: "Tableau de bord",
    Charts: "Graphiques",
    "AI Report": "Rapport IA",
    // AI Reports page
    "AI Reports": "Rapports IA",
    "AI Reports — Coming Soon": "Rapports IA — Bientôt disponible",
    "AI-powered match reports and deep analysis are on the way. Here are some sample reports to show what's coming.":
      "Les rapports de match et analyses approfondies par IA arrivent bientôt. Voici quelques exemples de rapports en avant-première.",
    "Preview not available.": "Aperçu non disponible.",
    "Open sample": "Ouvrir l'exemple",
    "No matches were loaded. Select one or more matches and try again.":
      "Aucun match n'a été chargé. Sélectionnez un ou plusieurs matchs et réessayez.",
    "Attack Analysis (English)": "Analyse d'attaque (anglais)",
    "Blocking Analysis (Vietnamese)": "Analyse de contre (vietnamien)",
    "Opponent Tendencies (Japanese)": "Tendances de l'adversaire (japonais)",
    // Deep AI analysis
    "Deep AI Analysis": "Analyse IA approfondie",
    "AI Deep Analysis": "Analyse approfondie par IA",
    "Load at least one match with coded events to generate the analysis.": "Chargez au moins un match avec des actions codées pour générer l'analyse.",
    "Run the deep analysis one section at a time to keep each request small. Edit or add questions below, then generate. Best used with the Anthropic (Claude) provider.":
      "Lancez l'analyse approfondie section par section pour garder chaque requête de petite taille. Modifiez ou ajoutez des questions ci-dessous, puis générez. À utiliser de préférence avec le fournisseur Anthropic (Claude).",
    "Download CSV": "Télécharger le CSV",
    "Analysis section": "Section d'analyse",
    analysing: "analyse de",
    "All sections (uses the most credits)": "Toutes les sections (consomme le plus de crédits)",
    "All sections": "Toutes les sections",
    Questions: "Questions",
    "one per line, edit or add your own": "une par ligne, modifiez ou ajoutez les vôtres",
    "Reset to defaults": "Réinitialiser les valeurs par défaut",
    "Add a question…": "Ajouter une question…",
    "Runs every section using the questions you've set for each. This sends one large request and uses the most credits.":
      "Exécute toutes les sections avec les questions que vous avez définies pour chacune. Cela envoie une seule grande requête et consomme le plus de crédits.",
    "Generate full analysis": "Générer l'analyse complète",
    "Generate section analysis": "Générer l'analyse de section",
    Serving: "Service",
    Reception: "Réception",
    Setting: "Passe",
    Attack: "Attaque",
    Blocking: "Contre",
    "Defence / Digging": "Défense / Récupération",
    "Rotation Analysis": "Analyse des rotations",
    "Opponent Tendencies (Scouting)": "Tendances de l'adversaire (scouting)",
    "Overall Team Metrics": "Indicateurs généraux de l'équipe",
    "Serve efficiency (aces, errors, and pressured receptions forced).": "Efficacité au service (aces, fautes et réceptions sous pression provoquées).",
    "Which servers are most effective, and against which reception zones?": "Quels serveurs sont les plus efficaces, et contre quelles zones de réception ?",
    "Which serve types (jump, float, jump-float) yield the best results?": "Quels types de service (sméché, flottant, flottant sauté) donnent les meilleurs résultats ?",
    "Reception quality breakdown (perfect, positive, negative, error).": "Répartition de la qualité de réception (parfaite, positive, négative, faute).",
    "Which players struggle most under jump serves vs. float serves?": "Quels joueurs sont le plus en difficulté sur les services sméchés par rapport aux flottants ?",
    "How does poor reception correlate with side-out efficiency?": "Comment une mauvaise réception est-elle liée à l'efficacité en side-out ?",
    "Set distribution by zone and by player.": "Répartition des passes par zone et par joueur.",
    "How does the setter perform under pressure (bad-pass situations)?": "Comment le passeur performe-t-il sous pression (situations de mauvaise réception) ?",
    "Tempo breakdown (quick, medium, high ball) and which yields the best attack outcome.": "Répartition des tempos (rapide, demi, haut) et lequel donne le meilleur résultat à l'attaque.",
    "Kill percentage and error rate per player, per rotation, and per zone.": "Pourcentage de points et taux de fautes par joueur, par rotation et par zone.",
    "Which attack zones (pipe, cross, line) have the highest efficiency?": "Quelles zones d'attaque (pipe, diagonale, ligne) ont la meilleure efficacité ?",
    "How attack efficiency changes in transition vs. side-out.": "Comment l'efficacité à l'attaque évolue en transition par rapport au side-out.",
    "Block-per-set rate per player and rotation.": "Nombre de contres par set par joueur et par rotation.",
    "Which opposing attack zones are most successfully blocked?": "Quelles zones d'attaque adverses sont contrées avec le plus de succès ?",
    "How often blocks result in kills vs. touch-outs.": "À quelle fréquence les contres se traduisent par des points contre des touches sorties.",
    "Dig success rate per player and per court zone.": "Taux de réussite en défense par joueur et par zone du terrain.",
    "Which attack types (tip, roll, hard swing) are hardest to defend?": "Quels types d'attaque (feinte, roulé, frappe puissante) sont les plus difficiles à défendre ?",
    "How defensive efficiency affects transition attack success.": "Comment l'efficacité défensive influe sur la réussite de l'attaque en transition.",
    "Which rotations have the highest and lowest point differential?": "Quelles rotations ont le différentiel de points le plus élevé et le plus bas ?",
    "Side-out and break-point percentages per rotation.": "Pourcentages de side-out et de break-point par rotation.",
    "Rotation-specific vulnerabilities (e.g. a weak serving or passing rotation).": "Faiblesses propres à certaines rotations (p. ex. une rotation faible au service ou en réception).",
    "The opponent's most frequently used attack zones and combinations.": "Les zones d'attaque et combinaisons les plus utilisées par l'adversaire.",
    "The opponent's go-to attackers in high-pressure situations.": "Les attaquants privilégiés de l'adversaire dans les situations de forte pression.",
    "The serve-receive patterns the opponent favours.": "Les schémas de réception que l'adversaire privilégie.",
    "Overall rally-win probability in different score situations.": "Probabilité globale de gagner l'échange dans différentes situations de score.",
    "How performance changes in sets 1–3 vs. sets 4–5 (fatigue/pressure effects).": "Comment la performance évolue dans les sets 1–3 par rapport aux sets 4–5 (effets de fatigue/pression).",
    "The correlation between first-ball side-out percentage and match outcome.": "La corrélation entre le pourcentage de side-out sur première balle et le résultat du match.",
    Report: "Rapport",

    // Import page
    "Load DVW file(s)...": "Charger des fichiers DVW…",
    "Load PSVB file(s)...": "Charger des fichiers PSVB…",
    "Load VSM file(s)...": "Charger des fichiers VSM…",
    "Load playlist file...": "Charger un fichier de liste de lecture…",
    "load one or more DataVolley files":
      "charger un ou plusieurs fichiers DataVolley",
    "load one or more VBStats files":
      "charger un ou plusieurs fichiers VBStats",
    "load one or more VolleyStation VSM files":
      "charger un ou plusieurs fichiers VolleyStation VSM",
    "Convert DVW": "Convertir DVW",
    "VSM": "VSM",
    "Convert DVW to VSM...": "Convertir DVW en VSM…",
    "pick a DataVolley .dvw file to download as .vsm":
      "choisir un fichier DataVolley .dvw à télécharger au format .vsm",
    "Convert VSM to DVW...": "Convertir VSM en DVW…",
    "pick a VolleyStation .vsm file to download as .dvw":
      "choisir un fichier VolleyStation .vsm à télécharger au format .dvw",
    "Converting…": "Conversion en cours…",
    "Converted": "Converti",
    "Failed to convert": "Échec de la conversion",
    "Beta testers only — coming soon for everyone else.":
      "Réservé aux testeurs bêta — bientôt disponible pour tous.",
    "load a playlist": "charger une liste de lecture",
    "Use Shift-JIS Encoder": "Utiliser l'encodeur Shift-JIS",
    "Importing…": "Importation…",
    "Save to Database?": "Enregistrer dans la base de données ?",
    "Always save imports automatically": "Toujours enregistrer les imports automatiquement",
    "View Only": "Afficher seulement",
    "Save to Database": "Enregistrer dans la base de données",
    "Error saving to database": "Erreur lors de l'enregistrement dans la base de données",
    "Saved to database": "Enregistré dans la base de données",
    "Saving to database…": "Enregistrement dans la base de données…",
    "Upload them to the database now?":
      "Les téléverser dans la base de données maintenant ?",

    // Settings / Options
    Theme: "Thème",
    Options: "Options",
    "Rotation naming convention": "Convention de nommage des rotations",
    "Choose how rotations are labelled in reports and analysis.":
      "Choisissez comment les rotations sont nommées dans les rapports et analyses.",
    "R1–R6 (rotation sequence)": "R1–R6 (séquence de rotation)",
    "S1–S6 (setter court position)": "S1–S6 (position du passeur)",
    "R1: setter in position 1, R2: setter in 6, R3: setter in 5, R4: setter in 4, R5: setter in 3, R6: setter in 2.":
      "R1 : passeur en position 1, R2 : passeur en 6, R3 : passeur en 5, R4 : passeur en 4, R5 : passeur en 3, R6 : passeur en 2.",
    "S1: setter in 1, S2: setter in 2, S3: setter in 3, S4: setter in 4, S5: setter in 5, S6: setter in 6.":
      "S1 : passeur en 1, S2 : passeur en 2, S3 : passeur en 3, S4 : passeur en 4, S5 : passeur en 5, S6 : passeur en 6.",
    "Labels:": "Étiquettes :",
    "Current theme:": "Thème actuel :",
    "Choose the language for the app interface.":
      "Choisissez la langue de l'interface de l'application.",
    "Create your own language package or edit a community one, with AI-assisted translation.":
      "Créez votre propre pack de langue ou modifiez-en un de la communauté, avec une traduction assistée par IA.",

    // About / Footer
    "Copyright © Perana Sports {year}. All rights reserved.":
      "Copyright © Perana Sports {year}. Tous droits réservés.",
    "VBLive is free to use right now. However Perana Sports reserves the right to charge a subscription fee in due course.":
      "VBLive est gratuit pour le moment. Cependant, Perana Sports se réserve le droit de facturer un abonnement en temps voulu.",
    "Querries / suggestions are most welcomed.":
      "Les questions / suggestions sont les bienvenues.",

    // Live
    "Please enter server name to search":
      "Veuillez saisir le nom du serveur à rechercher",
    "Broadcast Server": "Serveur de diffusion",
    "Enter DVMateLive Server Name": "Saisir le nom du serveur DVMateLive",
    LIVE: "EN DIRECT",

    // Playlists list
    "Delete Playlist": "Supprimer la liste de lecture",
    "Are you sure you want to delete the selected playlist?":
      "Voulez-vous vraiment supprimer la liste de lecture sélectionnée ?",
    "My Playlists": "Mes listes de lecture",
    "Shared Playlists": "Listes de lecture partagées",
    "Tags:": "Tags :",
    "Select...": "Sélectionner…",
    "All Tags": "Tous les tags",

    // Share
    "User already added": "Utilisateur déjà ajouté",
    "Please enter a valid email address":
      "Veuillez saisir une adresse e-mail valide",
    "Please select at least one user to share with":
      "Veuillez sélectionner au moins un utilisateur avec qui partager",
    "Not Shared": "Non partagé",
    "Available for everyone": "Accessible à tous",
    "Available to selected users":
      "Accessible aux utilisateurs sélectionnés",
    "Enter VBLive user email address":
      "Saisir l'adresse e-mail de l'utilisateur VBLive",
    "Selected users:": "Utilisateurs sélectionnés :",
    "Save List": "Enregistrer la liste",
    "Clear List": "Vider la liste",
    "Clear Users List": "Vider la liste des utilisateurs",
    "Do you really want to clear all users from list?":
      "Voulez-vous vraiment retirer tous les utilisateurs de la liste ?",
    "Do you want to save changes before closing?":
      "Voulez-vous enregistrer les modifications avant de fermer ?",
    "Enter name to save list to:":
      "Saisissez un nom pour enregistrer la liste :",
    "Enter name to save list to":
      "Saisissez un nom pour enregistrer la liste",

    // AI panels
    "AI settings": "Paramètres IA",
    "Setup needed": "Configuration requise",
    "Generate report": "Générer le rapport",
    "Generate AI report": "Générer le rapport IA",
    "Generate general report": "Générer le rapport général",
    "Translate to…": "Traduire en…",
    "Follow-up questions": "Questions complémentaires",
    "Ask a follow-up question…": "Poser une question complémentaire…",
    "Ask a follow-up question": "Poser une question complémentaire",
    "AI provider": "Fournisseur IA",
    Model: "Modèle",
    Hide: "Masquer",
    Show: "Afficher",
    "Your key is stored only in this browser and sent directly to the selected provider.":
      "Votre clé est stockée uniquement dans ce navigateur et envoyée directement au fournisseur sélectionné.",
    "Please enter your API key.": "Veuillez saisir votre clé API.",
    "Not enough data to analyse.": "Données insuffisantes pour l'analyse.",
    "Select a player with recorded spikes first.":
      "Sélectionnez d'abord un joueur avec des attaques enregistrées.",
    Player: "Joueur",
    "Select a player…": "Sélectionner un joueur…",
    "No players with recorded spikes in the selected matches.":
      "Aucun joueur avec des attaques enregistrées dans les matchs sélectionnés.",
    "Analyse with AI": "Analyser avec l'IA",
    "AI scouting report": "Rapport de scouting IA",
    "Q:": "Q :",
    Ask: "Demander",
    "e.g. Which rotation should we target on serve?":
      "ex. : Quelle rotation devrions-nous viser au service ?",
    "Saved questions:": "Questions enregistrées :",
    "Click to ask again": "Cliquez pour redemander",
    "Remove saved question": "Supprimer la question enregistrée",

    // AI language options
    Spanish: "Espagnol",
    French: "Français",
    German: "Allemand",
    Italian: "Italien",
    Portuguese: "Portugais",
    Dutch: "Néerlandais",
    Polish: "Polonais",
    Russian: "Russe",
    Turkish: "Turc",
    Arabic: "Arabe",
    "Chinese (Simplified)": "Chinois (simplifié)",
    Japanese: "Japonais",
    Korean: "Coréen",
    Vietnamese: "Vietnamien",
    Thai: "Thaï",

    // Box score
    "Show Full Headers": "Afficher les en-têtes complets",
    "Show Short Headers": "Afficher les en-têtes courts",
    "Show Less Players": "Afficher moins de joueurs",
    "Show All Players": "Afficher tous les joueurs",
    "Attack Kills": "Attaques gagnantes",
    "Attack Errors": "Fautes d'attaque",
    "Total Attempts": "Total des tentatives",
    "Hitting Efficiency": "Efficacité d'attaque",
    "Serve Aces": "Aces au service",
    "Serve Errors": "Fautes au service",
    "Pass Errors": "Fautes de réception",
    "% Perfect Passes": "% réceptions parfaites",
    "Passing Average": "Moyenne de réception",
    "Total Passes": "Total des réceptions",
    "Set Assists": "Passes décisives",
    Digs: "Défenses",
    "Momentum Chart": "Graphique de momentum",
    "Key event": "Action clé",
    "Break Point %": "% point de break",
    "First Ball Sideout %": "% side-out première balle",
    "Sideout Transition %": "% side-out en transition",
    "FB Sideout Kills %": "% attaque gagnante première balle",
    "Sideout Opposition Error %": "% side-out sur erreur adverse",
    Plus: "Plus",
    Minus: "Moins",
    Blocks: "Contres gagnants",
    "Positive Serve %": "% services positifs",
    Sideout: "Side-out",
    "Break point": "Point de break",
    "Ace / Kill": "Ace / Attaque gagnante",
    "In play": "En jeu",
    Error: "Erreur",
    "Sideout on a kill": "Side-out sur attaque gagnante",
    "Sideout on an error": "Side-out sur erreur adverse",
    "Top half": "Moitié supérieure",
    "Bottom half": "Moitié inférieure",
    "The centre line steps toward the team that wins each point.": "La ligne centrale se déplace vers l'équipe qui remporte chaque point.",
    "No point-by-point data available for this match.": "Aucune donnée point par point disponible pour ce match.",
    "No match data available.": "Aucune donnée de match disponible.",
    "This report isn't supported on this browser yet — please use Safari.": "Ce rapport n'est pas encore pris en charge par ce navigateur — veuillez utiliser Safari.",
    "No video available for this match.": "Aucune vidéo disponible pour ce match.",
    passes: "réceptions",
    "eff.": "eff.",
    Grade: "Note",
    LEFT: "GAUCHE",
    CENTRE: "CENTRE",
    RIGHT: "DROITE",
    SELECTED: "SÉLECTIONNÉ",
    Passers: "Réceptionneurs",
    Calls: "Appels",
    "SIDE OUT": "SIDE-OUT",
    TRANSITION: "TRANSITION",
    "No events in selection.": "Aucun événement dans la sélection actuelle.",
    "Serve Receives By Players": "Réceptions par joueur",
    "Serve Receive By Zones": "Réception par zones",
    "Dig Errors": "Fautes de défense",
    "Dig Average": "Moyenne de défense",
    "Block Solos": "Blocs solos",
    "Block Assists": "Blocs assistés",
    "Block Errors": "Fautes de bloc",
    "Points Scored": "Points marqués",
    TEAM: "ÉQUIPE",

    // Sideout report
    "ALL ROWS": "TOUTES LES LIGNES",
    "ROWS ": "LIGNES ",
    "ROW ": "LIGNE ",
    "FB Kills": "Attaques gagnantes FB",
    "Opp. Serve Errors": "Fautes de service adv.",
    "FB Opp. Errors": "Fautes adv. FB",
    "Kills/Blocks": "Attaques/Blocs",
    "Opp. Errors": "Fautes adv.",
    Unsuccessful: "Échec",

    // Attack zones
    "No events in selected zone.":
      "Aucun événement dans la zone sélectionnée.",
    "Show all attacks. Or click on a zone to show attacks in that zone.":
      "Afficher toutes les attaques. Ou cliquez sur une zone pour afficher les attaques de cette zone.",
    "OPPONENTS (": "ADVERSAIRES (",

    // Video analysis filters
    "All Types": "Tous les types",
    Serve: "Service",
    "Serve-Receive": "Réception",
    Set: "Passe",
    "Setter's Call": "Appel du passeur",
    "Disable this skill": "Désactiver cette compétence",
    "Enable this skill": "Activer cette compétence",
    "Teams & Players": "Équipes et joueurs",
    "Match State": "État du match",
    "Custom Code": "Code personnalisé",
    "Search... (comma-separated for multiple)": "Rechercher… (séparez par des virgules pour plusieurs)",
    vs: "contre",
    "Rewind 60 seconds": "Reculer de 60 secondes",
    "Rewind 1 frame": "Reculer d'une image",
    "Forward 1 frame": "Avancer d'une image",
    "Forward 60 seconds": "Avancer de 60 secondes",
    "Previous Clip": "Clip précédent",
    Replay: "Revoir",
    "Next Clip": "Clip suivant",
    Phases: "Phases",
    "Score Range": "Plage de score",
    "Set Winner": "Vainqueur du set",
    Type: "Type",
    Strength: "Puissance",
    "Setter Calls": "Appels du passeur",
    "Setter Call": "Appel du passeur",
    Freeball: "Balle libre",
    "Close (0-2)": "Serré (0-2)",
    "Moderate (3-5)": "Modéré (3-5)",
    "Lopsided (6+)": "Écart important (6+)",
    "Focus Team": "Équipe suivie",
    Oppositions: "Adversaires",
    "Focus Team Won": "Équipe suivie gagnante",
    "Oppositions Won": "Adversaires gagnants",
    "Advanced Filters": "Filtres avancés",
    Players: "Joueurs",
    Opponents: "Adversaires",
    "Event Type": "Type d'événement",
    "Event Result": "Résultat de l'événement",
    "Attack Type": "Type d'attaque",
    "Hit Type": "Type de frappe",
    "Block Type": "Type de bloc",
    "Attack Combo": "Combinaison d'attaque",
    Apply: "Appliquer",
    Rotation: "Rotation",
    Skill: "Compétence",
    "Lead Time": "Temps d'avance",
    Duration: "Durée",
    Timing: "Minutage",
    "Go Live": "Revenir au direct",
    "Playback has fallen behind the live broadcast — click to jump to now.": "La lecture a pris du retard sur la diffusion en direct — cliquez pour revenir au direct.",
    "Video timing": "Minutage vidéo",
    "Default lead time (seconds before an event to start playback) and clip duration, per skill.": "Temps d'avance par défaut (secondes avant un événement pour démarrer la lecture) et durée du clip, par compétence.",
    Cloud: "Cloud",
    Local: "Local",
    "Relay host": "Hôte du relais",
    "IP or hostname of the machine running the local relay.": "Adresse IP ou nom d'hôte de la machine exécutant le relais local.",
    Spike: "Attaque",
    Block: "Bloc",
    Defence: "Défense",
    "All Results": "Tous les résultats",
    Hard: "Puissant",
    Soft: "Amorti",
    Tip: "Feinte",
    "Not Specified": "Non spécifié",
    "H - High": "H - Haute",
    "M- Medium": "M - Moyenne",
    "Q - Quick": "Q - Rapide",
    "T - Tense": "T - Tendue",
    "U - Super": "U - Super",
    "F - Fast": "F - Rapide",
    "O - Other": "O - Autre",
    "B0 - No Block": "B0 - Pas de bloc",
    "B1 - 1 Block": "B1 - 1 bloqueur",
    "B2 - 2 Blocks": "B2 - 2 bloqueurs",
    "B3 - 3 Blocks": "B3 - 3 bloqueurs",
    "BS - Seams": "BS - Intervalles",

    // All filters panel
    ALL: "TOUS",
    NONE: "AUCUN",

    // Playlist editor
    "Remove Item": "Supprimer l'élément",
    "Do you really want to remove the selected item?":
      "Voulez-vous vraiment supprimer l'élément sélectionné ?",
    "Description is required": "La description est obligatoire",
    "Comments are required": "Les commentaires sont obligatoires",
    "Tags are required": "Les tags sont obligatoires",
    "Playlist saved successfully":
      "Liste de lecture enregistrée avec succès",
    "Error saving playlist":
      "Erreur lors de l'enregistrement de la liste de lecture",

    // Language editor
    "My saved packages": "Mes packs enregistrés",
    "Loading shared languages…": "Chargement des langues partagées…",
    "Community shared languages": "Langues partagées par la communauté",
    by: "par",
    "based on": "basé sur",
    "or create new": "ou en créer un nouveau",
    Use: "Utiliser",
    "New Language Package": "Nouveau pack de langue",
    "Language Name": "Nom de la langue",
    "Language Code": "Code de langue",
    "Flag Emoji": "Emoji du drapeau",
    "Base language (reference)": "Langue de base (référence)",
    "If not English, both English and the reference language will be shown.":
      "Si ce n'est pas l'anglais, l'anglais et la langue de référence seront affichés.",
    "Start Editing": "Commencer l'édition",
    "Save Key": "Enregistrer la clé",
    "AI key set": "Clé IA définie",
    "Set API key for AI translation":
      "Définir la clé API pour la traduction IA",
    "AI key ✓": "Clé IA ✓",
    "Set AI key": "Définir la clé IA",
    "AI: Translate All": "IA : Tout traduire",
    "Translating…": "Traduction…",
    "AI translate section": "Traduire la section avec l'IA",
    "Clear All": "Tout effacer",
    "Saving…": "Enregistrement…",
    English: "Anglais",
    new: "nouveau",
    "Enter translation…": "Saisir la traduction…",
    "Share with other VBLive users?":
      "Partager avec d'autres utilisateurs VBLive ?",
    "Would you like to share the": "Souhaitez-vous partager le",
    "language package with all VBLive users? Anyone can then copy and build upon it.":
      "pack de langue avec tous les utilisateurs VBLive ? Chacun pourra alors le copier et l'enrichir.",
    "No, just save for me": "Non, enregistrer uniquement pour moi",
    "Yes, share it!": "Oui, le partager !",
    "Language package saved and shared with all VBLive users!":
      "Pack de langue enregistré et partagé avec tous les utilisateurs VBLive !",
    "Language package saved!": "Pack de langue enregistré !",
    "Clear all translations? This cannot be undone.":
      "Effacer toutes les traductions ? Cette action est irréversible.",

    // Additional UI phrases
    Teams: "Équipes",
    "Shared By": "Partagé par",
    "View Activity": "Activité de consultation",
    Viewer: "Spectateur",
    "Date / Time": "Date / Heure",
    "No views recorded yet.": "Aucune consultation enregistrée pour le moment.",
    "Date / Scores": "Date / Scores",
    "Stats API source": "Source de l'API de statistiques",
    "load one or more AVC VIS files": "charger un ou plusieurs fichiers AVC VIS",
    "Points to close a set": "Points pour terminer un set",
    "Require 2-point advantage": "Exiger un écart de 2 points",
    Roster: "Effectif",
    Clone: "Dupliquer",
    "Edit team": "Modifier l'équipe",
    "New player": "Nouveau joueur",
    "Search player...": "Rechercher un joueur…",
    "Match Centre": "Centre du match",
    "Points Won": "Points gagnés",
    "Opp. Error": "Erreur adverse",
    Total: "Total",
    "Match Skills": "Compétences du match",
    Dig: "Défense",
    "Best Scorers": "Meilleurs marqueurs",
    "Power Play": "Power Play",
    Match: "Match",
    "Player scouting": "Analyse des joueurs",
    "Team deep analysis": "Analyse approfondie d'équipe",
    "All Players": "Tous les joueurs",
    "All Sets": "Tous les sets",
    "All Rotations": "Toutes les rotations",
    "All Combos": "Toutes les combinaisons",
    "All Calls": "Tous les appels",
    "All Events": "Tous les événements",
    "Select all items": "Sélectionner tous les éléments",
    "Unselect all items": "Désélectionner tous les éléments",
    "Create play list": "Créer une playlist",
    "Point by Point": "Point par point",
    // i18n backfill pass — previously English-fallback-only keys
    "Access denied": "Accès refusé",
    "Aces": "Aces",
    "ACES": "ACES",
    "Add report": "Ajouter un rapport",
    "Admin": "Admin",
    "Admins": "Administrateurs",
    "Advanced": "Avancé",
    "All set!": "Tout est prêt !",
    "Analyse team with AI": "Analyser l'équipe avec l'IA",
    "Are you sure you want to delete the selected play list?": "Voulez-vous vraiment supprimer la playlist sélectionnée ?",
    "Assign": "Assigner",
    "Assign playlist": "Assigner une playlist",
    "Assigned": "Assigné",
    "At least one admin is required.": "Au moins un administrateur est requis.",
    "Attack Combo / Setter Call": "Combinaison d'attaque / Appel de passeur",
    "Attack diagram": "Schéma d'attaque",
    "Attacks": "Attaques",
    "ATTACKS": "ATTAQUES",
    "Auto-detected a custom set-closing score for": "Score de fin de set personnalisé détecté automatiquement pour",
    "BLOCK": "BLOC",
    "BLOCKS": "BLOCS",
    "Beta testers": "Testeurs bêta",
    "Clear filter": "Effacer le filtre",
    "Close Set": "Clore le set",
    "Close menu": "Fermer le menu",
    "Code": "Code",
    "Coding defaults & shortcuts": "Valeurs par défaut et raccourcis de saisie",
    "Continue": "Continuer",
    "Could not load match data. The match may no longer be available.": "Impossible de charger les données du match. Le match n'est peut-être plus disponible.",
    "Could not load stats for these matches.": "Impossible de charger les statistiques de ces matchs.",
    "Could not load this match.": "Impossible de charger ce match.",
    "Could not load this playlist.": "Impossible de charger cette playlist.",
    "Could not load this report.": "Impossible de charger ce rapport.",
    "Could not save the report.": "Impossible d'enregistrer le rapport.",
    "Could not share the report. Please try again.": "Impossible de partager le rapport. Veuillez réessayer.",
    "Court horizontal (net vertical)": "Terrain horizontal (filet vertical)",
    "Court vertical (net horizontal)": "Terrain vertical (filet horizontal)",
    "Create": "Créer",
    "Create a new team from this roster": "Créer une nouvelle équipe à partir de cet effectif",
    "DIFFERENCE": "DIFFÉRENCE",
    "DV Code": "Code DV",
    "Delete this report?": "Supprimer ce rapport ?",
    "Detected a custom set-closing score of": "Score de fin de set personnalisé détecté :",
    "Double Sub": "Double remplacement",
    "Double Sub: now pick a bench player for the flashing OPPOSITE slot…": "Double remplacement : choisissez maintenant un joueur du banc pour le poste de pointu clignotant…",
    "Double Sub: pick a bench player for the flashing SETTER slot…": "Double remplacement : choisissez un joueur du banc pour le poste de passeur clignotant…",
    "Double Substitution — swap the Setter and Opposite for two incoming players": "Double remplacement — échanger le passeur et le pointu contre deux joueurs entrants",
    "Drag to draw the ball path; pause briefly to mark a touch (reception/dig/block), release to finish.": "Faites glisser pour dessiner la trajectoire du ballon ; marquez une pause brève pour indiquer un toucher (réception/défense/bloc), relâchez pour terminer.",
    "Draw": "Dessiner",
    "Draw & Code": "Dessiner et coder",
    "Draw & Code — draw ball paths on the court to code the rally": "Dessiner et coder — dessinez les trajectoires du ballon sur le terrain pour coder l'échange",
    "Edit Lineup": "Modifier la composition",
    "Edit Lineup — freely rearrange the starting lineup before the set begins": "Modifier la composition — réorganisez librement la composition de départ avant le début du set",
    "Emails in this list can manage beta testers and other admins.": "Les adresses e-mail de cette liste peuvent gérer les testeurs bêta et les autres administrateurs.",
    "Emails in this list get early access to AI Reports and other gated features. Changes take effect immediately.": "Les adresses e-mail de cette liste ont un accès anticipé aux rapports IA et aux autres fonctionnalités restreintes. Les modifications prennent effet immédiatement.",
    "Enter a valid email address.": "Veuillez saisir une adresse e-mail valide.",
    "Exit fullscreen": "Quitter le plein écran",
    "Extended": "Étendu",
    "Failed to generate PDF.": "Échec de la génération du PDF.",
    "Failed to save changes.": "Échec de l'enregistrement des modifications.",
    "Filtered by": "Filtré par",
    "First name": "Prénom",
    "For stronger security later, consider moving admin checks to a Firebase Auth custom claim instead of this email list.": "Pour une meilleure sécurité à l'avenir, envisagez de déplacer les vérifications d'administrateur vers un claim personnalisé Firebase Auth plutôt que cette liste d'e-mails.",
    "Fullscreen": "Plein écran",
    "Generate an AI report from these matches": "Générer un rapport IA à partir de ces matchs",
    "How often (in seconds) the events list is updated during a live session.": "Fréquence (en secondes) de mise à jour de la liste des événements pendant une session en direct.",
    "Last name": "Nom",
    "Leave blank to use standard rules (25 points, 15 for a 5th/deciding set)": "Laisser vide pour utiliser les règles standard (25 points, 15 pour un 5e set décisif)",
    "Live session refresh interval": "Intervalle de rafraîchissement de la session en direct",
    "Load VIS file(s)...": "Charger des fichiers VIS…",
    "Load this team's last-saved starting lineup (positions, setter and liberos)": "Charger la dernière composition de départ enregistrée de cette équipe (postes, passeur et libéros)",
    "Loading…": "Chargement…",
    "Manually end the current set at its current score, regardless of the configured winning score / 2-point advantage rule": "Terminer manuellement le set en cours au score actuel, quelle que soit la règle configurée de score gagnant / avance de 2 points",
    "Migration finished with some issues": "Migration terminée avec quelques problèmes",
    "Most of your data was migrated, but some items could not be moved. You can retry now or continue and try again later.": "La plupart de vos données ont été migrées, mais certains éléments n'ont pas pu être déplacés. Vous pouvez réessayer maintenant ou continuer et réessayer plus tard.",
    "Mute": "Muet",
    "New team": "Nouvelle équipe",
    "Next": "Suivant",
    "Nickname": "Surnom",
    "No Power Play": "Pas de Power Play",
    "No admins yet.": "Aucun administrateur pour le moment.",
    "No beta testers yet.": "Aucun testeur bêta pour le moment.",
    "No coded events to analyse.": "Aucun événement codé à analyser.",
    "No coded events to export.": "Aucun événement codé à exporter.",
    "No matches.": "Aucun match.",
    "No players added yet.": "Aucun joueur ajouté pour le moment.",
    "No players recorded.": "Aucun joueur enregistré.",
    "No players yet.": "Aucun joueur pour le moment.",
    "No playlists assigned.": "Aucune playlist assignée.",
    "No playlists found.": "Aucune playlist trouvée.",
    "No reports saved.": "Aucun rapport enregistré.",
    "No roster players available.": "Aucun joueur d'effectif disponible.",
    "No substitutions": "Aucun remplacement",
    "No teams found.": "Aucune équipe trouvée.",
    "No teams yet.": "Aucune équipe pour le moment.",
    "No teams.": "Aucune équipe.",
    "Nominate Setter": "Désigner le passeur",
    "Notify these users by email (optional)": "Avertir ces utilisateurs par e-mail (facultatif)",
    "OPP REC": "RÉC. ADV.",
    "OPP. ERRORS": "ERREURS ADV.",
    "Open / Download": "Ouvrir / Télécharger",
    "Open local file": "Ouvrir un fichier local",
    "Open menu": "Ouvrir le menu",
    "Opposition Passing — use the opposition's reception formation on the diagram": "Réception adverse — utiliser la formation de réception de l'adversaire sur le schéma",
    "Paste the report (Markdown supported)…": "Collez le rapport (Markdown pris en charge)…",
    "Pause": "Pause",
    "Play": "Lecture",
    "Playing squad": "Effectif de match",
    "Playing squad is full. Remove a player from the squad before adding another.": "L'effectif de match est complet. Retirez un joueur de l'effectif avant d'en ajouter un autre.",
    "Please sign in to save reports.": "Veuillez vous connecter pour enregistrer des rapports.",
    "Power Play Only": "Power Play uniquement",
    "Record a team error (no player) — edit later to add the player and event type": "Enregistrer une erreur d'équipe (sans joueur) — modifiez plus tard pour ajouter le joueur et le type d'événement",
    "Record at least one event before making substitutions": "Enregistrez au moins un événement avant d'effectuer des remplacements",
    "Redo": "Rétablir",
    "Remove": "Retirer",
    "Remove share": "Supprimer le partage",
    "Remove yourself as admin? You will immediately lose access to this page.": "Vous retirer en tant qu'administrateur ? Vous perdrez immédiatement l'accès à cette page.",
    "Report saved and shared.": "Rapport enregistré et partagé.",
    "Report saved to the player's profile.": "Rapport enregistré dans le profil du joueur.",
    "Report title": "Titre du rapport",
    "Reports": "Rapports",
    "Result": "Résultat",
    "Retry": "Réessayer",
    "Return to live coding": "Revenir à la saisie en direct",
    "Rotation is not permitted while making substitutions": "La rotation n'est pas autorisée pendant les remplacements",
    "SERVE": "SERVICE",
    "SETS": "SETS",
    "Save & Share": "Enregistrer et partager",
    "Save this report so you can share it with other users": "Enregistrer ce rapport pour pouvoir le partager avec d'autres utilisateurs",
    "Save this report to the player's profile": "Enregistrer ce rapport dans le profil du joueur",
    "Save to player": "Enregistrer pour le joueur",
    "Saved lineup": "Composition enregistrée",
    "Scroll, drag or swipe to scrub": "Faites défiler, glissez ou balayez pour avancer/reculer",
    "Search existing players...": "Rechercher des joueurs existants…",
    "Search playlists…": "Rechercher des playlists…",
    "Select a match video to view while entering lineups.": "Sélectionnez une vidéo de match à visionner pendant la saisie des compositions.",
    "Select a player.": "Sélectionnez un joueur.",
    "Select a team.": "Sélectionnez une équipe.",
    "Setting up your account": "Configuration de votre compte",
    "Shaded rows are played under a Power Play — HP/AP marks the calling team, bold gold scores are bonus (double) points.": "Les lignes ombrées ont été jouées pendant un Power Play — HP/AP indique l'équipe qui l'a appelé, les scores en gras doré sont des points bonus (doublés).",
    "Shared by": "Partagé par",
    "Shared with me": "Partagé avec moi",
    "Show help": "Afficher l'aide",
    "Show public playlists": "Afficher les playlists publiques",
    "Standard": "Standard",
    "Substitutions made — tap to view": "Remplacements effectués — appuyez pour afficher",
    "Tap the blocking player.": "Appuyez sur le joueur qui bloque.",
    "Team colour": "Couleur de l'équipe",
    "Team name": "Nom de l'équipe",
    "That email is already in the list.": "Cette adresse e-mail est déjà dans la liste.",
    "The report is too large to save. Try a single match or fewer sets.": "Le rapport est trop volumineux pour être enregistré. Essayez un seul match ou moins de sets.",
    "This match has no Power Play calls.": "Ce match n'a aucun appel de Power Play.",
    "This player has no name to save the report against.": "Ce joueur n'a pas de nom pour lequel enregistrer le rapport.",
    "This team has no designated setter. Pick the player who is setting.": "Cette équipe n'a pas de passeur désigné. Choisissez le joueur qui joue passeur.",
    "Toggle video player": "Basculer le lecteur vidéo",
    "Unmute": "Réactiver le son",
    "Untitled playlist": "Playlist sans titre",
    "Video URL…": "URL de la vidéo…",
    "We're moving your data to a faster, more reliable home. This happens only once.": "Nous déplaçons vos données vers un emplacement plus rapide et plus fiable. Cela ne se produit qu'une seule fois.",
    "You don't have permission to view this page.": "Vous n'avez pas l'autorisation d'afficher cette page.",
    "Your data has been migrated successfully.": "Vos données ont été migrées avec succès.",
    "Your teams and players will appear here after your data is migrated.": "Vos équipes et vos joueurs apparaîtront ici une fois vos données migrées.",
    "bonus point": "point bonus",
    "bonus points": "points bonus",
    "event": "événement",
    "events": "événements",
    "file(s).": "fichier(s).",
    "is not on this team's roster.": "ne fait pas partie de l'effectif de cette équipe.",
    "loaded": "chargé",
    "matches": "matchs",
    "more": "de plus",
    "players": "joueurs",
    "points (no value was entered) — applied automatically.": "points (aucune valeur saisie) — appliqué automatiquement.",
    "roster": "effectif",
    "seconds": "secondes",
    "selected": "sélectionné(s)",
    "session(s)?": "session(s) ?",
    "showing": "affichage de",
    "tournaments": "tournois",
    "…and": "…et",
    "● marks which team served the point.": "● indique quelle équipe a servi le point.",
    // i18n backfill pass (Catapult + misc)
    "Centimetres (cm)": "Centimètres (cm)",
    "Inches (in)": "Pouces (po)",
    "Jump height units": "Unités de hauteur de saut",
    "Choose how Catapult jump heights are displayed and filtered.": "Choisissez comment les hauteurs de saut Catapult sont affichées et filtrées.",
    "Select Catapult Focus file...": "Sélectionner un fichier Catapult Focus…",
    "Select a Catapult Focus CSV export to view jump data for this match.": "Sélectionnez un export CSV Catapult Focus pour afficher les données de saut de ce match.",
    "Loaded from saved match data": "Chargé à partir des données de match enregistrées",
    "No jump data found in this file.": "Aucune donnée de saut trouvée dans ce fichier.",
    "Could not read this file. Please check it is a Catapult Focus CSV export.": "Impossible de lire ce fichier. Vérifiez qu'il s'agit bien d'un export CSV Catapult Focus.",
    "Could not save jump data with this match.": "Impossible d'enregistrer les données de saut avec ce match.",
    "Jump data saved with this match.": "Les données de saut ont été enregistrées avec ce match.",
    "Show players with no match stats": "Afficher les joueurs sans statistiques de match",
    "hidden": "masqué(s)",
    "jumps": "sauts",
    "periods": "périodes",
    "Sync offset": "Décalage de synchronisation",
    "matched": "correspondances",
    "match events now show jump height/duration in Video Analysis": "événements de match affichent désormais la hauteur/durée de saut dans l'analyse vidéo",
    "Team": "Équipe",
    "Team A": "Équipe A",
    "Team B": "Équipe B",
    "Which team is this Catapult file for?": "À quelle équipe appartient ce fichier Catapult ?",
    "We couldn't confidently match the players in this Focus file to one team. Please select which team it belongs to.": "Nous n'avons pas pu associer avec certitude les joueurs de ce fichier Focus à une équipe. Veuillez indiquer à quelle équipe il appartient.",
    "This match has no video attached yet.": "Aucune vidéo n'est encore associée à ce match.",
    "No synced jump events found for this player.": "Aucun événement de saut synchronisé trouvé pour ce joueur.",
    "No match loaded.": "Aucun match chargé.",
    "avg": "moy.",
    "max": "max.",
    "Sync": "Synchro",
    "Jump Report": "Rapport de saut",
    "Watch Video": "Regarder la vidéo",
    "Save PDF": "Enregistrer en PDF",
    "Generated by VBLive": "Généré par VBLive",
    "Deep analysis of {team} from ball-by-ball event data, covering serving, reception, setting, attack, blocking, defence, rotations and opponent tendencies. Best used with the Anthropic (Claude) provider.": "Analyse approfondie de {team} à partir des données événement par événement, couvrant le service, la réception, la passe, l'attaque, le contre, la défense, les rotations et les tendances adverses. Idéal avec le fournisseur Anthropic (Claude).",
    "teams": "équipes",
  },

  es: {
    // Information page
    Information: "Información",
    "Release notes": "Notas de la versión",
    "Contact us": "Contáctanos",
    "Questions, feedback or suggestions are most welcome.": "Preguntas, comentarios o sugerencias son siempre bienvenidos.",
    // DV match coding
    Lineups: "Alineaciones",
    Point: "Punto",
    Video: "Vídeo",
    Defaults: "Valores predeterminados",
    "[team][NN][skill][type?][eval][zoneFrom?[szFrom?][zoneTo?[szTo?]]]": "[equipo][NN][acción][tipo?][eval][zonaDesde?[szDesde?][zonaHacia?[szHacia?]]]",
    Vertical: "Vertical",
    Horizontal: "Horizontal",
    "Swap sides": "Cambiar lados",
    Subs: "Cambios",
    Substitutions: "Sustituciones",
    Court: "Cancha",
    Home: "Local",
    Away: "Visitante",
    Games: "Sets",
    Enter: "Entrar",
    Undo: "Deshacer",
    "Match saved": "Partido guardado",
    "Please sign in to save this match.": "Inicia sesión para guardar este partido.",
    "Nothing to save yet.": "Nada que guardar todavía.",
    "Could not save the match. Please try again.": "No se pudo guardar el partido. Inténtalo de nuevo.",
    Discard: "Descartar",
    "Resumed autosaved coding": "Se reanudó la codificación guardada automáticamente",
    "Changes discarded": "Cambios descartados",
    "Discard all changes and revert to the starting point?":
      "¿Descartar todos los cambios y volver al punto de partida?",
    Clear: "Borrar",
    "Prev set": "Set anterior",
    "Next set": "Set siguiente",
    "Match Setup": "Configuración del partido",
    "First serve": "Primer saque",
    "Start Match": "Iniciar partido",
    "Resume Match": "Reanudar partido",
    Skip: "Omitir",
    "Select an event then drag to draw ball path": "Selecciona un evento y arrastra para trazar la trayectoria del balón",
    "Drag on court to set ball path": "Arrastra en la cancha para definir la trayectoria del balón",
    "No events yet – enter a DV code above and press Enter.": "Aún no hay eventos: introduce un código DV arriba y pulsa Entrar.",
    "DataVolley code reference": "Referencia de código DataVolley",
    Format: "Formato",
    Skills: "Acciones",
    Evaluation: "Evaluación",
    "Skill / type omission": "Omisión de acción / tipo",
    "Skill and/or type may be omitted; defaults fill in the rest.": "La acción y/o el tipo pueden omitirse; los valores predeterminados completan el resto.",
    "Dot-coding (S→R and A→B pairs)": "Codificación con punto (pares S→R y A→B)",
    "Chaining & aliases": "Encadenamiento y alias",
    "team:": "equipo:",
    "omit → defaults to A": "omitir → predeterminado A",
    "NN: 1 or 2 digit jersey, e.g. 1, 01, 12": "NN: dorsal de 1 o 2 dígitos, p. ej. 1, 01, 12",
    "sub-zone: a b c d appended to zone digit, e.g. 5b or 5b1a": "subzona: a b c d añadido al dígito de zona, p. ej. 5b o 5b1a",
    "S Serve · R Reception · E Set · A Attack · B Block · D Dig · F Freeball": "S Saque · R Recepción · E Colocación · A Ataque · B Bloqueo · D Defensa · F Balón libre",
    "# kill/ace · + positive · ! ok · - negative · / overpass · = error": "# punto/ace · + positivo · ! regular · - negativo · / pase libre · = error",
    "E.g.": "P. ej.",
    "serve with skill=A (default), type=H, eval=+, zone=5.": "saque con skill=A (predeterminado), type=H, eval=+, zone=5.",
    "type-only (no skill marker), defaults skill from context.": "solo tipo (sin marcador de acción), acción predeterminada según el contexto.",
    "serve coded, reception auto-linked.": "saque codificado, recepción enlazada automáticamente.",
    "attack coded, block auto-linked.": "ataque codificado, bloqueo enlazado automáticamente.",
    "Dot connects the reception eval to the corresponding serve eval via the serve→receive mapping.": "El punto conecta la evaluación de la recepción con la evaluación de saque correspondiente mediante la correspondencia saque→recepción.",
    "two events in one string.": "dos eventos en una sola cadena.",
    "manual point A/B.": "punto manual A/B.",
    "serve ace/error.": "ace/error de saque.",
    "serve with eval.": "saque con evaluación.",
    // Stats, sideouts, filters & video tooltips
    "Block Kills": "Puntos de bloqueo",
    "Side Out Percentage": "Porcentaje de side out",
    "All First Ball": "Todos los primeros balones",
    "First Ball Kills": "Puntos de primer balón",
    "Good Serve": "Buen saque",
    "Break Points": "Puntos de break",
    Game: "Set",
    Row: "Posición",
    "First Ball Sideouts": "Side outs de primer balón",
    "All Sideouts": "Todos los side outs",
    All: "Todos",
    None: "Ninguno",
    Receiver: "Receptor",
    "# Passes": "N.º recepciones",
    "Perfect %": "% perfecto",
    "Positive %": "% positivo",
    "Sideout %": "% side out",
    "FB Sideout %": "% side out 1er balón",
    "Pass Hits": "Ataques tras recepción",
    "Pass Hit Kills": "Puntos tras recepción",
    "Pass Hit Errors": "Errores tras recepción",
    "Show Passes": "Mostrar recepciones",
    "Show Attacks": "Mostrar ataques",
    "Error Pass": "Recepción errada",
    "1 Pass": "Recepción 1",
    "2 Pass": "Recepción 2",
    "Perfect Pass": "Recepción perfecta",
    "Successful SO": "Side out exitoso",
    "First Ball SO": "Side out 1er balón",
    "Unsuccessful SO": "Side out fallido",
    RECEIVES: "RECEPCIONES",
    Sets: "Sets",
    Rotations: "Rotaciones",
    "Attack Combos": "Combinaciones de ataque",
    Stages: "Fases",
    Display: "Visualización",
    Setters: "Colocadores",
    Attackers: "Atacantes",
    Results: "Resultados",
    Source: "Fuente",
    Cones: "Zonas",
    "Select None": "No seleccionar ninguno",
    "All Serves": "Todos los saques",
    "All Returns": "Todas las recepciones",
    Filters: "Filtros",
    "Reset all filters": "Restablecer todos los filtros",
    "Show video clips of filtered attacks": "Mostrar clips de vídeo de los ataques filtrados",
    "Spikes for the selected player": "Ataques del jugador seleccionado",
    "Click on the zones to view video of attacks": "Haz clic en las zonas para ver los vídeos de los ataques",
    "Expand All": "Expandir todo",
    "Collapse All": "Contraer todo",
    "Import Radar File": "Importar archivo Radar",
    "Select local video file": "Seleccionar archivo de vídeo local",
    "Save to database": "Guardar en la base de datos",
    "Load and play online video": "Cargar y reproducir vídeo en línea",
    "Synchronise video with events": "Sincronizar vídeo con eventos",
    "Rewind 10 seconds": "Retroceder 10 segundos",
    "Rewind 1 second": "Retroceder 1 segundo",
    "Forward 1 second": "Avanzar 1 segundo",
    "Forward 10 seconds": "Avanzar 10 segundos",
    "Set timing for events": "Ajustar el tiempo de los eventos",

    // Navigation
    Matches: "Partidos",
    Live: "En vivo",
    Import: "Importar",
    Playlists: "Listas de reproducción",
    "New Match": "Nuevo partido",
    // New Match / Match Coding gate
    "Match Coding": "Codificación del partido",
    "Match Coding — Coming Soon": "Codificación del partido — Próximamente",
    "Live match coding is on the way. Here's a preview of what's coming.":
      "La codificación de partidos en vivo está en camino. Aquí tienes un adelanto de lo que viene.",
    Settings: "Ajustes",
    Language: "Idioma",
    "Your profile": "Tu perfil",
    "Sign out": "Cerrar sesión",
    "Translation Editor": "Editor de traducción",

    // Common actions
    Save: "Guardar",
    Cancel: "Cancelar",
    Delete: "Eliminar",
    Share: "Compartir",
    Edit: "Editar",
    New: "Nuevo",
    Refresh: "Actualizar",
    Search: "Buscar",
    Reset: "Restablecer",
    Close: "Cerrar",
    "Sub for": "Cambiar por",
    "No players available": "No hay jugadores disponibles",
    "Show full roster": "Mostrar plantilla completa",
    "Available only": "Solo disponibles",
    "Maximum substitutions reached": "Máximo de cambios alcanzado",
    Back: "Atrás",
    "Download PDF": "Descargar PDF",
    "Loading...": "Cargando…",
    "Select All": "Seleccionar todo",
    "Unselect All": "Deseleccionar todo",
    Yes: "Sí",
    No: "No",
    Add: "Añadir",
    Load: "Cargar",
    Confirm: "Confirmar",
    Open: "Abrir",
    show: "mostrar",

    // Auth
    "Sign In": "Iniciar sesión",
    "Sign Up": "Registrarse",
    "Sign Out": "Cerrar sesión",
    Email: "Correo electrónico",
    Password: "Contraseña",
    "Confirm Password": "Confirmar contraseña",
    "Forgot password?": "¿Olvidaste tu contraseña?",
    "Forgot Password": "Olvidé mi contraseña",
    "Don't have an account?": "¿No tienes una cuenta?",
    "Sign up for free!": "¡Regístrate gratis!",
    "All ready signed up?": "¿Ya estás registrado?",
    "Error Signing In": "Error al iniciar sesión",
    "Signed up successfully": "Registro exitoso",
    "Error signing up": "Error al registrarse",
    "Enter your email": "Introduce tu correo electrónico",
    "Send Reset Email": "Enviar correo de restablecimiento",
    "Email was sent to": "El correo se envió a",
    "Could not send reset email":
      "No se pudo enviar el correo de restablecimiento",

    // Matches list
    "Team 1": "Equipo 1",
    "Team 2": "Equipo 2",
    Tournament: "Torneo",
    Scores: "Marcadores",
    Date: "Fecha",
    Actions: "Acciones",
    Owner: "Propietario",
    Description: "Descripción",
    Comments: "Comentarios",
    Tags: "Etiquetas",
    "Add player": "Añadir jugador",
    "Add team": "Añadir equipo",
    "Edit playlist": "Editar lista de reproducción",
    "Merge Teams": "Fusionar equipos",
    "Roster differences detected between teams:": "Se detectaron diferencias de plantilla entre equipos:",
    "Review the roster in the next step before merging.": "Revisa la plantilla en el siguiente paso antes de fusionar.",
    "At least 2 team records must exist in your Teams list for the selected matches.": "Deben existir al menos 2 registros de equipo en tu lista para los partidos seleccionados.",
    "Merge...": "Fusionar...",
    "Select 2 or more team records to merge into one.": "Selecciona 2 o más registros de equipo para fusionar en uno.",
    "Filter teams...": "Filtrar equipos...",
    "teams selected": "equipos seleccionados",
    "Select Teams": "Seleccionar equipos",
    "Reconcile Roster": "Reconciliar plantilla",
    "Confirm Merge": "Confirmar fusión",
    "Merged team name": "Nombre del equipo fusionado",
    "shirt number(s) have conflicting names — review below.": "número(s) tienen nombres conflictivos — revisa abajo.",
    "Shirt": "Dorsal",
    "players included": "jugadores incluidos",
    "No players to reconcile.": "No hay jugadores que reconciliar.",
    "Use this name": "Usar este nombre",
    "Create merged team": "Crear equipo fusionado",
    "with": "con",
    "duplicate team records": "registros de equipo duplicados",
    "Consolidate duplicate player records and reassign playlist links": "Consolidar registros de jugadores duplicados y reasignar enlaces de listas",
    "This cannot be undone.": "Esta acción no se puede deshacer.",
    "Teams being merged:": "Equipos que se fusionan:",
    "Step": "Paso",
    "of": "de",
    "Merging...": "Fusionando...",
    "Players & Teams": "Jugadores & Equipos",
    "No players or teams assigned.": "No hay jugadores ni equipos asignados.",
    "Search players...": "Buscar jugadores...",
    "Search teams...": "Buscar equipos...",
    "Saving...": "Guardando...",
    "My Matches": "Mis partidos",
    "Shared Matches": "Partidos compartidos",
    "Search team...": "Buscar equipo…",
    "All Teams": "Todos los equipos",
    "Multi Match Reports": "Informes de varios partidos",
    "Delete match": "Eliminar partido",
    "Remove from my shared matches": "Quitar de mis partidos compartidos",
    "Share match": "Compartir partido",
    "Edit match": "Editar partido",
    "Open video analysis": "Abrir análisis de vídeo",
    "Delete Match": "Eliminar partido",
    "Are you sure you want to delete the selected match?":
      "¿Seguro que quieres eliminar el partido seleccionado?",
    "No matches selected.": "No hay partidos seleccionados.",

    // New Match
    "← Back": "← Atrás",
    "Team A (Home)": "Equipo A (local)",
    "Team B (Away)": "Equipo B (visitante)",
    "Select team…": "Seleccionar equipo…",
    "Tournament (optional)": "Torneo (opcional)",
    "Tournament / competition name": "Nombre del torneo / competición",
    "Match date": "Fecha del partido",
    "Start Coding →": "Comenzar codificación →",

    // Reports
    Summary: "Resumen",
    "Box Score": "Hoja de estadísticas",
    "Side-outs Report": "Informe de side-outs",
    "Serve Receives": "Recepciones",
    "Attack Zones": "Zonas de ataque",
    "Hitting Charts": "Gráficos de ataque",
    "Video Analysis": "Análisis de vídeo",
    Dashboard: "Panel",
    Charts: "Gráficos",
    "AI Report": "Informe de IA",
    // AI Reports page
    "AI Reports": "Informes de IA",
    "AI Reports — Coming Soon": "Informes de IA — Próximamente",
    "AI-powered match reports and deep analysis are on the way. Here are some sample reports to show what's coming.":
      "Los informes de partido y el análisis profundo con IA están en camino. Aquí tienes algunos informes de muestra como adelanto.",
    "Preview not available.": "Vista previa no disponible.",
    "Open sample": "Abrir muestra",
    "No matches were loaded. Select one or more matches and try again.":
      "No se cargó ningún partido. Selecciona uno o más partidos e inténtalo de nuevo.",
    "Attack Analysis (English)": "Análisis de ataque (inglés)",
    "Blocking Analysis (Vietnamese)": "Análisis de bloqueo (vietnamita)",
    "Opponent Tendencies (Japanese)": "Tendencias del rival (japonés)",
    // Deep AI analysis
    "Deep AI Analysis": "Análisis profundo con IA",
    "AI Deep Analysis": "Análisis profundo de IA",
    "Load at least one match with coded events to generate the analysis.": "Carga al menos un partido con eventos codificados para generar el análisis.",
    "Run the deep analysis one section at a time to keep each request small. Edit or add questions below, then generate. Best used with the Anthropic (Claude) provider.":
      "Ejecuta el análisis profundo sección por sección para que cada solicitud sea pequeña. Edita o añade preguntas abajo y luego genera. Se recomienda usar con el proveedor Anthropic (Claude).",
    "Download CSV": "Descargar CSV",
    "Analysis section": "Sección de análisis",
    analysing: "analizando",
    "All sections (uses the most credits)": "Todas las secciones (consume más créditos)",
    "All sections": "Todas las secciones",
    Questions: "Preguntas",
    "one per line, edit or add your own": "una por línea, edita o añade las tuyas",
    "Reset to defaults": "Restablecer valores predeterminados",
    "Add a question…": "Añadir una pregunta…",
    "Runs every section using the questions you've set for each. This sends one large request and uses the most credits.":
      "Ejecuta todas las secciones con las preguntas que hayas definido para cada una. Esto envía una solicitud grande y consume más créditos.",
    "Generate full analysis": "Generar análisis completo",
    "Generate section analysis": "Generar análisis de sección",
    Serving: "Saque",
    Reception: "Recepción",
    Setting: "Colocación",
    Attack: "Ataque",
    Blocking: "Bloqueo",
    "Defence / Digging": "Defensa / Recuperación",
    "Rotation Analysis": "Análisis de rotaciones",
    "Opponent Tendencies (Scouting)": "Tendencias del rival (scouting)",
    "Overall Team Metrics": "Métricas generales del equipo",
    "Serve efficiency (aces, errors, and pressured receptions forced).": "Eficacia al servicio (aces, errores y recepciones forzadas bajo presión).",
    "Which servers are most effective, and against which reception zones?": "¿Qué sacadores son más eficaces y contra qué zonas de recepción?",
    "Which serve types (jump, float, jump-float) yield the best results?": "¿Qué tipos de saque (potencia, flotante, flotante en salto) dan los mejores resultados?",
    "Reception quality breakdown (perfect, positive, negative, error).": "Desglose de la calidad de recepción (perfecta, positiva, negativa, error).",
    "Which players struggle most under jump serves vs. float serves?": "¿Qué jugadores tienen más dificultades ante saques en salto frente a saques flotantes?",
    "How does poor reception correlate with side-out efficiency?": "¿Cómo se relaciona una mala recepción con la eficacia en el side-out?",
    "Set distribution by zone and by player.": "Distribución del colocador por zona y por jugador.",
    "How does the setter perform under pressure (bad-pass situations)?": "¿Cómo rinde el colocador bajo presión (situaciones de mala recepción)?",
    "Tempo breakdown (quick, medium, high ball) and which yields the best attack outcome.": "Desglose de tiempos (rápido, medio, alto) y cuál da el mejor resultado en ataque.",
    "Kill percentage and error rate per player, per rotation, and per zone.": "Porcentaje de puntos y tasa de error por jugador, por rotación y por zona.",
    "Which attack zones (pipe, cross, line) have the highest efficiency?": "¿Qué zonas de ataque (pipe, diagonal, paralela) tienen la mayor eficacia?",
    "How attack efficiency changes in transition vs. side-out.": "Cómo cambia la eficacia de ataque en transición frente al side-out.",
    "Block-per-set rate per player and rotation.": "Bloqueos por set por jugador y rotación.",
    "Which opposing attack zones are most successfully blocked?": "¿Qué zonas de ataque rivales se bloquean con más éxito?",
    "How often blocks result in kills vs. touch-outs.": "Con qué frecuencia los bloqueos resultan en puntos frente a toques fuera.",
    "Dig success rate per player and per court zone.": "Tasa de éxito en defensa por jugador y por zona de la cancha.",
    "Which attack types (tip, roll, hard swing) are hardest to defend?": "¿Qué tipos de ataque (finta, globo, remate fuerte) son más difíciles de defender?",
    "How defensive efficiency affects transition attack success.": "Cómo afecta la eficacia defensiva al éxito del ataque en transición.",
    "Which rotations have the highest and lowest point differential?": "¿Qué rotaciones tienen el diferencial de puntos más alto y más bajo?",
    "Side-out and break-point percentages per rotation.": "Porcentajes de side-out y break-point por rotación.",
    "Rotation-specific vulnerabilities (e.g. a weak serving or passing rotation).": "Vulnerabilidades específicas de cada rotación (p. ej. una rotación débil al servicio o en recepción).",
    "The opponent's most frequently used attack zones and combinations.": "Las zonas de ataque y combinaciones más usadas por el rival.",
    "The opponent's go-to attackers in high-pressure situations.": "Los atacantes preferidos del rival en situaciones de alta presión.",
    "The serve-receive patterns the opponent favours.": "Los patrones de recepción que prefiere el rival.",
    "Overall rally-win probability in different score situations.": "Probabilidad global de ganar el punto en distintas situaciones de marcador.",
    "How performance changes in sets 1–3 vs. sets 4–5 (fatigue/pressure effects).": "Cómo cambia el rendimiento en los sets 1–3 frente a los sets 4–5 (efectos de fatiga/presión).",
    "The correlation between first-ball side-out percentage and match outcome.": "La correlación entre el porcentaje de side-out de primera bola y el resultado del partido.",
    Report: "Informe",

    // Import page
    "Load DVW file(s)...": "Cargar archivo(s) DVW…",
    "Load PSVB file(s)...": "Cargar archivo(s) PSVB…",
    "Load VSM file(s)...": "Cargar archivo(s) VSM…",
    "Load playlist file...": "Cargar archivo de lista de reproducción…",
    "load one or more DataVolley files":
      "cargar uno o más archivos DataVolley",
    "load one or more VBStats files": "cargar uno o más archivos VBStats",
    "load one or more VolleyStation VSM files": "cargar uno o más archivos VolleyStation VSM",
    "Convert DVW": "Convertir DVW",
    "VSM": "VSM",
    "Convert DVW to VSM...": "Convertir DVW a VSM…",
    "pick a DataVolley .dvw file to download as .vsm":
      "elegir un archivo DataVolley .dvw para descargar como .vsm",
    "Convert VSM to DVW...": "Convertir VSM a DVW…",
    "pick a VolleyStation .vsm file to download as .dvw":
      "elegir un archivo VolleyStation .vsm para descargar como .dvw",
    "Converting…": "Convirtiendo…",
    "Converted": "Convertido",
    "Failed to convert": "Error al convertir",
    "Beta testers only — coming soon for everyone else.":
      "Solo para testers beta — próximamente para todos.",
    "load a playlist": "cargar una lista de reproducción",
    "Use Shift-JIS Encoder": "Usar codificador Shift-JIS",
    "Importing…": "Importando…",
    "Save to Database?": "¿Guardar en la base de datos?",
    "Always save imports automatically": "Guardar siempre las importaciones automáticamente",
    "View Only": "Solo ver",
    "Save to Database": "Guardar en la base de datos",
    "Error saving to database": "Error al guardar en la base de datos",
    "Saved to database": "Guardado en la base de datos",
    "Saving to database…": "Guardando en la base de datos…",
    "Upload them to the database now?":
      "¿Subirlos a la base de datos ahora?",

    // Settings / Options
    Theme: "Tema",
    Options: "Opciones",
    "Rotation naming convention": "Convención de nombres de rotaciones",
    "Choose how rotations are labelled in reports and analysis.":
      "Elige cómo se etiquetan las rotaciones en los informes y análisis.",
    "R1–R6 (rotation sequence)": "R1–R6 (secuencia de rotación)",
    "S1–S6 (setter court position)":
      "S1–S6 (posición del colocador en cancha)",
    "R1: setter in position 1, R2: setter in 6, R3: setter in 5, R4: setter in 4, R5: setter in 3, R6: setter in 2.":
      "R1: colocador en la posición 1, R2: colocador en 6, R3: colocador en 5, R4: colocador en 4, R5: colocador en 3, R6: colocador en 2.",
    "S1: setter in 1, S2: setter in 2, S3: setter in 3, S4: setter in 4, S5: setter in 5, S6: setter in 6.":
      "S1: colocador en 1, S2: colocador en 2, S3: colocador en 3, S4: colocador en 4, S5: colocador en 5, S6: colocador en 6.",
    "Labels:": "Etiquetas:",
    "Current theme:": "Tema actual:",
    "Choose the language for the app interface.":
      "Elige el idioma de la interfaz de la aplicación.",
    "Create your own language package or edit a community one, with AI-assisted translation.":
      "Crea tu propio paquete de idioma o edita uno de la comunidad, con traducción asistida por IA.",

    // About / Footer
    "Copyright © Perana Sports {year}. All rights reserved.":
      "Copyright © Perana Sports {year}. Todos los derechos reservados.",
    "VBLive is free to use right now. However Perana Sports reserves the right to charge a subscription fee in due course.":
      "VBLive es de uso gratuito por ahora. Sin embargo, Perana Sports se reserva el derecho de cobrar una cuota de suscripción en su momento.",
    "Querries / suggestions are most welcomed.":
      "Las consultas / sugerencias son muy bienvenidas.",

    // Live
    "Please enter server name to search":
      "Introduce el nombre del servidor para buscar",
    "Broadcast Server": "Servidor de transmisión",
    "Enter DVMateLive Server Name":
      "Introduce el nombre del servidor DVMateLive",
    LIVE: "EN VIVO",

    // Playlists list
    "Delete Playlist": "Eliminar lista de reproducción",
    "Are you sure you want to delete the selected playlist?":
      "¿Seguro que quieres eliminar la lista de reproducción seleccionada?",
    "My Playlists": "Mis listas de reproducción",
    "Shared Playlists": "Listas de reproducción compartidas",
    "Tags:": "Etiquetas:",
    "Select...": "Seleccionar…",
    "All Tags": "Todas las etiquetas",

    // Share
    "User already added": "Usuario ya añadido",
    "Please enter a valid email address":
      "Introduce una dirección de correo válida",
    "Please select at least one user to share with":
      "Selecciona al menos un usuario con quien compartir",
    "Not Shared": "No compartido",
    "Available for everyone": "Disponible para todos",
    "Available to selected users":
      "Disponible para usuarios seleccionados",
    "Enter VBLive user email address":
      "Introduce la dirección de correo del usuario de VBLive",
    "Selected users:": "Usuarios seleccionados:",
    "Save List": "Guardar lista",
    "Clear List": "Vaciar lista",
    "Clear Users List": "Vaciar lista de usuarios",
    "Do you really want to clear all users from list?":
      "¿Seguro que quieres eliminar a todos los usuarios de la lista?",
    "Do you want to save changes before closing?":
      "¿Quieres guardar los cambios antes de cerrar?",
    "Enter name to save list to:":
      "Introduce un nombre para guardar la lista:",
    "Enter name to save list to":
      "Introduce un nombre para guardar la lista",

    // AI panels
    "AI settings": "Ajustes de IA",
    "Setup needed": "Configuración necesaria",
    "Generate report": "Generar informe",
    "Generate AI report": "Generar informe de IA",
    "Generate general report": "Generar informe general",
    "Translate to…": "Traducir a…",
    "Follow-up questions": "Preguntas de seguimiento",
    "Ask a follow-up question…": "Haz una pregunta de seguimiento…",
    "Ask a follow-up question": "Haz una pregunta de seguimiento",
    "AI provider": "Proveedor de IA",
    Model: "Modelo",
    Hide: "Ocultar",
    Show: "Mostrar",
    "Your key is stored only in this browser and sent directly to the selected provider.":
      "Tu clave se almacena solo en este navegador y se envía directamente al proveedor seleccionado.",
    "Please enter your API key.": "Introduce tu clave API.",
    "Not enough data to analyse.": "No hay datos suficientes para analizar.",
    "Select a player with recorded spikes first.":
      "Primero selecciona un jugador con ataques registrados.",
    Player: "Jugador",
    "Select a player…": "Seleccionar un jugador…",
    "No players with recorded spikes in the selected matches.":
      "No hay jugadores con ataques registrados en los partidos seleccionados.",
    "Analyse with AI": "Analizar con IA",
    "AI scouting report": "Informe de scouting de IA",
    "Q:": "P:",
    Ask: "Preguntar",
    "e.g. Which rotation should we target on serve?":
      "p. ej., ¿A qué rotación deberíamos apuntar al sacar?",
    "Saved questions:": "Preguntas guardadas:",
    "Click to ask again": "Haz clic para volver a preguntar",
    "Remove saved question": "Eliminar pregunta guardada",

    // AI language options
    Spanish: "Español",
    French: "Francés",
    German: "Alemán",
    Italian: "Italiano",
    Portuguese: "Portugués",
    Dutch: "Neerlandés",
    Polish: "Polaco",
    Russian: "Ruso",
    Turkish: "Turco",
    Arabic: "Árabe",
    "Chinese (Simplified)": "Chino (simplificado)",
    Japanese: "Japonés",
    Korean: "Coreano",
    Vietnamese: "Vietnamita",
    Thai: "Tailandés",

    // Box score
    "Show Full Headers": "Mostrar encabezados completos",
    "Show Short Headers": "Mostrar encabezados cortos",
    "Show Less Players": "Mostrar menos jugadores",
    "Show All Players": "Mostrar todos los jugadores",
    "Attack Kills": "Ataques punto",
    "Attack Errors": "Errores de ataque",
    "Total Attempts": "Intentos totales",
    "Hitting Efficiency": "Eficiencia de ataque",
    "Serve Aces": "Aces de saque",
    "Serve Errors": "Errores de saque",
    "Pass Errors": "Errores de recepción",
    "% Perfect Passes": "% recepciones perfectas",
    "Passing Average": "Promedio de recepción",
    "Total Passes": "Recepciones totales",
    "Set Assists": "Asistencias de colocación",
    Digs: "Defensas",
    "Momentum Chart": "Gráfico de momentum",
    "Key event": "Jugada clave",
    "Break Point %": "% punto de break",
    "First Ball Sideout %": "% side out primer balón",
    "Sideout Transition %": "% side out en transición",
    "FB Sideout Kills %": "% remate ganador primer balón",
    "Sideout Opposition Error %": "% side out por error rival",
    Plus: "Más",
    Minus: "Menos",
    Blocks: "Bloqueos directos",
    "Positive Serve %": "% saques positivos",
    Sideout: "Side out",
    "Break point": "Punto de break",
    "Ace / Kill": "Ace / Remate ganador",
    "In play": "En juego",
    Error: "Error",
    "Sideout on a kill": "Side out por remate ganador",
    "Sideout on an error": "Side out por error rival",
    "Top half": "Mitad superior",
    "Bottom half": "Mitad inferior",
    "The centre line steps toward the team that wins each point.": "La línea central se mueve hacia el equipo que gana cada punto.",
    "No point-by-point data available for this match.": "No hay datos punto por punto disponibles para este partido.",
    "No match data available.": "No hay datos del partido disponibles.",
    "This report isn't supported on this browser yet — please use Safari.": "Este informe aún no es compatible con este navegador. Utilice Safari.",
    "No video available for this match.": "No hay vídeo disponible para este partido.",
    passes: "recepciones",
    "eff.": "ef.",
    Grade: "Calificación",
    LEFT: "IZQUIERDA",
    CENTRE: "CENTRO",
    RIGHT: "DERECHA",
    SELECTED: "SELECCIONADO",
    Passers: "Receptores",
    Calls: "Llamadas",
    "SIDE OUT": "SIDE OUT",
    TRANSITION: "TRANSICIÓN",
    "No events in selection.": "No hay eventos en la selección actual.",
    "Serve Receives By Players": "Recepciones por jugador",
    "Serve Receive By Zones": "Recepción por zonas",
    "Dig Errors": "Errores de defensa",
    "Dig Average": "Promedio de defensa",
    "Block Solos": "Bloqueos individuales",
    "Block Assists": "Bloqueos asistidos",
    "Block Errors": "Errores de bloqueo",
    "Points Scored": "Puntos anotados",
    TEAM: "EQUIPO",

    // Sideout report
    "ALL ROWS": "TODAS LAS FILAS",
    "ROWS ": "FILAS ",
    "ROW ": "FILA ",
    "FB Kills": "Ataques punto FB",
    "Opp. Serve Errors": "Errores de saque rival",
    "FB Opp. Errors": "Errores rival FB",
    "Kills/Blocks": "Ataques/Bloqueos",
    "Opp. Errors": "Errores rival",
    Unsuccessful: "Sin éxito",

    // Attack zones
    "No events in selected zone.":
      "No hay eventos en la zona seleccionada.",
    "Show all attacks. Or click on a zone to show attacks in that zone.":
      "Mostrar todos los ataques. O haz clic en una zona para mostrar los ataques de esa zona.",
    "OPPONENTS (": "RIVALES (",

    // Video analysis filters
    "All Types": "Todos los tipos",
    Serve: "Saque",
    "Serve-Receive": "Recepción",
    Set: "Colocación",
    "Setter's Call": "Indicación del colocador",
    "Disable this skill": "Desactivar esta habilidad",
    "Enable this skill": "Activar esta habilidad",
    "Teams & Players": "Equipos y jugadores",
    "Match State": "Estado del partido",
    "Custom Code": "Código personalizado",
    "Search... (comma-separated for multiple)": "Buscar… (separar con comas para varios)",
    vs: "vs.",
    "Rewind 60 seconds": "Retroceder 60 segundos",
    "Rewind 1 frame": "Retroceder 1 fotograma",
    "Forward 1 frame": "Avanzar 1 fotograma",
    "Forward 60 seconds": "Avanzar 60 segundos",
    "Previous Clip": "Clip anterior",
    Replay: "Repetir",
    "Next Clip": "Clip siguiente",
    Phases: "Fases",
    "Score Range": "Rango de puntuación",
    "Set Winner": "Ganador del set",
    Type: "Tipo",
    Strength: "Potencia",
    "Setter Calls": "Indicaciones del colocador",
    "Setter Call": "Indicación del colocador",
    Freeball: "Balón libre",
    "Close (0-2)": "Ajustado (0-2)",
    "Moderate (3-5)": "Moderado (3-5)",
    "Lopsided (6+)": "Amplio (6+)",
    "Focus Team": "Equipo enfocado",
    Oppositions: "Oposición",
    "Focus Team Won": "Equipo enfocado ganó",
    "Oppositions Won": "Oposición ganó",
    "Advanced Filters": "Filtros avanzados",
    Players: "Jugadores",
    Opponents: "Oponentes",
    "Event Type": "Tipo de evento",
    "Event Result": "Resultado del evento",
    "Attack Type": "Tipo de ataque",
    "Hit Type": "Tipo de golpe",
    "Block Type": "Tipo de bloqueo",
    "Attack Combo": "Combinación de ataque",
    Apply: "Aplicar",
    Rotation: "Rotación",
    Skill: "Habilidad",
    "Lead Time": "Tiempo de anticipación",
    Duration: "Duración",
    Timing: "Sincronización",
    "Go Live": "Ir a directo",
    "Playback has fallen behind the live broadcast — click to jump to now.": "La reproducción se ha quedado atrás de la transmisión en vivo — haga clic para saltar al momento actual.",
    "Video timing": "Sincronización de vídeo",
    "Default lead time (seconds before an event to start playback) and clip duration, per skill.": "Tiempo de anticipación predeterminado (segundos antes de un evento para iniciar la reproducción) y duración del clip, por habilidad.",
    Cloud: "Nube",
    Local: "Local",
    "Relay host": "Host del relé",
    "IP or hostname of the machine running the local relay.": "Dirección IP o nombre de host de la máquina que ejecuta el relé local.",
    Spike: "Ataque",
    Block: "Bloqueo",
    Defence: "Defensa",
    "All Results": "Todos los resultados",
    Hard: "Potente",
    Soft: "Suave",
    Tip: "Finta",
    "Not Specified": "No especificado",
    "H - High": "H - Alta",
    "M- Medium": "M - Media",
    "Q - Quick": "Q - Rápida",
    "T - Tense": "T - Tensa",
    "U - Super": "U - Súper",
    "F - Fast": "F - Veloz",
    "O - Other": "O - Otra",
    "B0 - No Block": "B0 - Sin bloqueo",
    "B1 - 1 Block": "B1 - 1 bloqueador",
    "B2 - 2 Blocks": "B2 - 2 bloqueadores",
    "B3 - 3 Blocks": "B3 - 3 bloqueadores",
    "BS - Seams": "BS - Huecos",

    // All filters panel
    ALL: "TODOS",
    NONE: "NINGUNO",

    // Playlist editor
    "Remove Item": "Eliminar elemento",
    "Do you really want to remove the selected item?":
      "¿Seguro que quieres eliminar el elemento seleccionado?",
    "Description is required": "La descripción es obligatoria",
    "Comments are required": "Los comentarios son obligatorios",
    "Tags are required": "Las etiquetas son obligatorias",
    "Playlist saved successfully":
      "Lista de reproducción guardada correctamente",
    "Error saving playlist":
      "Error al guardar la lista de reproducción",

    // Language editor
    "My saved packages": "Mis paquetes guardados",
    "Loading shared languages…": "Cargando idiomas compartidos…",
    "Community shared languages": "Idiomas compartidos por la comunidad",
    by: "por",
    "based on": "basado en",
    "or create new": "o crear uno nuevo",
    Use: "Usar",
    "New Language Package": "Nuevo paquete de idioma",
    "Language Name": "Nombre del idioma",
    "Language Code": "Código de idioma",
    "Flag Emoji": "Emoji de bandera",
    "Base language (reference)": "Idioma base (referencia)",
    "If not English, both English and the reference language will be shown.":
      "Si no es inglés, se mostrarán tanto el inglés como el idioma de referencia.",
    "Start Editing": "Empezar a editar",
    "Save Key": "Guardar clave",
    "AI key set": "Clave de IA establecida",
    "Set API key for AI translation":
      "Establecer clave API para traducción con IA",
    "AI key ✓": "Clave de IA ✓",
    "Set AI key": "Establecer clave de IA",
    "AI: Translate All": "IA: Traducir todo",
    "Translating…": "Traduciendo…",
    "AI translate section": "Traducir sección con IA",
    "Clear All": "Borrar todo",
    "Saving…": "Guardando…",
    English: "Inglés",
    new: "nuevo",
    "Enter translation…": "Introduce la traducción…",
    "Share with other VBLive users?":
      "¿Compartir con otros usuarios de VBLive?",
    "Would you like to share the": "¿Te gustaría compartir el",
    "language package with all VBLive users? Anyone can then copy and build upon it.":
      "paquete de idioma con todos los usuarios de VBLive? Cualquiera podrá copiarlo y ampliarlo.",
    "No, just save for me": "No, solo guardarlo para mí",
    "Yes, share it!": "¡Sí, compartirlo!",
    "Language package saved and shared with all VBLive users!":
      "¡Paquete de idioma guardado y compartido con todos los usuarios de VBLive!",
    "Language package saved!": "¡Paquete de idioma guardado!",
    "Clear all translations? This cannot be undone.":
      "¿Borrar todas las traducciones? Esta acción no se puede deshacer.",

    // Additional UI phrases
    Teams: "Equipos",
    "Shared By": "Compartido por",
    "View Activity": "Actividad de visualización",
    Viewer: "Espectador",
    "Date / Time": "Fecha / Hora",
    "No views recorded yet.": "Aún no hay visualizaciones registradas.",
    "Date / Scores": "Fecha / Marcadores",
    "Stats API source": "Origen de la API de estadísticas",
    "load one or more AVC VIS files": "cargar uno o más archivos AVC VIS",
    "Points to close a set": "Puntos para cerrar un set",
    "Require 2-point advantage": "Requerir ventaja de 2 puntos",
    Roster: "Plantilla",
    Clone: "Clonar",
    "Edit team": "Editar equipo",
    "New player": "Nuevo jugador",
    "Search player...": "Buscar jugador…",
    "Match Centre": "Centro del partido",
    "Points Won": "Puntos ganados",
    "Opp. Error": "Error rival",
    Total: "Total",
    "Match Skills": "Habilidades del partido",
    Dig: "Defensa",
    "Best Scorers": "Mejores anotadores",
    "Power Play": "Power Play",
    Match: "Partido",
    "Player scouting": "Scouting de jugadores",
    "Team deep analysis": "Análisis profundo del equipo",
    "All Players": "Todos los jugadores",
    "All Sets": "Todos los sets",
    "All Rotations": "Todas las rotaciones",
    "All Combos": "Todas las combinaciones",
    "All Calls": "Todas las llamadas",
    "All Events": "Todos los eventos",
    "Select all items": "Seleccionar todos los elementos",
    "Unselect all items": "Deseleccionar todos los elementos",
    "Create play list": "Crear lista de reproducción",
    "Point by Point": "Punto por punto",
    // i18n backfill pass — previously English-fallback-only keys
    "Access denied": "Acceso denegado",
    "Aces": "Aces",
    "ACES": "ACES",
    "Add report": "Añadir informe",
    "Admin": "Administración",
    "Admins": "Administradores",
    "Advanced": "Avanzado",
    "All set!": "¡Todo listo!",
    "Analyse team with AI": "Analizar equipo con IA",
    "Are you sure you want to delete the selected play list?": "¿Seguro que quieres eliminar la lista de reproducción seleccionada?",
    "Assign": "Asignar",
    "Assign playlist": "Asignar lista de reproducción",
    "Assigned": "Asignado",
    "At least one admin is required.": "Se requiere al menos un administrador.",
    "Attack Combo / Setter Call": "Combinación de ataque / Llamada de colocador",
    "Attack diagram": "Diagrama de ataque",
    "Attacks": "Ataques",
    "ATTACKS": "ATAQUES",
    "Auto-detected a custom set-closing score for": "Se detectó automáticamente una puntuación de cierre de set personalizada para",
    "BLOCK": "BLOQUEO",
    "BLOCKS": "BLOQUEOS",
    "Beta testers": "Probadores beta",
    "Clear filter": "Borrar filtro",
    "Close Set": "Cerrar set",
    "Close menu": "Cerrar menú",
    "Code": "Código",
    "Coding defaults & shortcuts": "Valores predeterminados y atajos de anotación",
    "Continue": "Continuar",
    "Could not load match data. The match may no longer be available.": "No se pudieron cargar los datos del partido. Puede que el partido ya no esté disponible.",
    "Could not load stats for these matches.": "No se pudieron cargar las estadísticas de estos partidos.",
    "Could not load this match.": "No se pudo cargar este partido.",
    "Could not load this playlist.": "No se pudo cargar esta lista de reproducción.",
    "Could not load this report.": "No se pudo cargar este informe.",
    "Could not save the report.": "No se pudo guardar el informe.",
    "Could not share the report. Please try again.": "No se pudo compartir el informe. Inténtalo de nuevo.",
    "Court horizontal (net vertical)": "Cancha horizontal (red vertical)",
    "Court vertical (net horizontal)": "Cancha vertical (red horizontal)",
    "Create": "Crear",
    "Create a new team from this roster": "Crear un nuevo equipo a partir de esta plantilla",
    "DIFFERENCE": "DIFERENCIA",
    "DV Code": "Código DV",
    "Delete this report?": "¿Eliminar este informe?",
    "Detected a custom set-closing score of": "Se detectó una puntuación de cierre de set personalizada de",
    "Double Sub": "Doble sustitución",
    "Double Sub: now pick a bench player for the flashing OPPOSITE slot…": "Doble sustitución: elige ahora un jugador de banquillo para la posición de opuesto que parpadea…",
    "Double Sub: pick a bench player for the flashing SETTER slot…": "Doble sustitución: elige un jugador de banquillo para la posición de colocador que parpadea…",
    "Double Substitution — swap the Setter and Opposite for two incoming players": "Doble sustitución: cambia al colocador y al opuesto por dos jugadores entrantes",
    "Drag to draw the ball path; pause briefly to mark a touch (reception/dig/block), release to finish.": "Arrastra para dibujar la trayectoria del balón; haz una pausa breve para marcar un toque (recepción/defensa/bloqueo), suelta para terminar.",
    "Draw": "Dibujar",
    "Draw & Code": "Dibujar y codificar",
    "Draw & Code — draw ball paths on the court to code the rally": "Dibujar y codificar: dibuja las trayectorias del balón en la cancha para codificar la jugada",
    "Edit Lineup": "Editar alineación",
    "Edit Lineup — freely rearrange the starting lineup before the set begins": "Editar alineación: reorganiza libremente la alineación inicial antes de que comience el set",
    "Emails in this list can manage beta testers and other admins.": "Los correos de esta lista pueden gestionar a los probadores beta y a otros administradores.",
    "Emails in this list get early access to AI Reports and other gated features. Changes take effect immediately.": "Los correos de esta lista obtienen acceso anticipado a los informes de IA y otras funciones restringidas. Los cambios surten efecto de inmediato.",
    "Enter a valid email address.": "Introduce una dirección de correo válida.",
    "Exit fullscreen": "Salir de pantalla completa",
    "Extended": "Extendido",
    "Failed to generate PDF.": "Error al generar el PDF.",
    "Failed to save changes.": "Error al guardar los cambios.",
    "Filtered by": "Filtrado por",
    "First name": "Nombre",
    "For stronger security later, consider moving admin checks to a Firebase Auth custom claim instead of this email list.": "Para mayor seguridad en el futuro, considera trasladar las verificaciones de administrador a un claim personalizado de Firebase Auth en lugar de esta lista de correos.",
    "Fullscreen": "Pantalla completa",
    "Generate an AI report from these matches": "Generar un informe de IA a partir de estos partidos",
    "How often (in seconds) the events list is updated during a live session.": "Con qué frecuencia (en segundos) se actualiza la lista de eventos durante una sesión en vivo.",
    "Last name": "Apellido",
    "Leave blank to use standard rules (25 points, 15 for a 5th/deciding set)": "Déjalo en blanco para usar las reglas estándar (25 puntos, 15 en un 5º set decisivo)",
    "Live session refresh interval": "Intervalo de actualización de la sesión en vivo",
    "Load VIS file(s)...": "Cargar archivo(s) VIS…",
    "Load this team's last-saved starting lineup (positions, setter and liberos)": "Cargar la última alineación inicial guardada de este equipo (posiciones, colocador y líberos)",
    "Loading…": "Cargando…",
    "Manually end the current set at its current score, regardless of the configured winning score / 2-point advantage rule": "Finalizar manualmente el set actual con el marcador actual, sin importar la puntuación ganadora configurada ni la regla de ventaja de 2 puntos",
    "Migration finished with some issues": "La migración finalizó con algunos problemas",
    "Most of your data was migrated, but some items could not be moved. You can retry now or continue and try again later.": "La mayoría de tus datos se migraron, pero algunos elementos no se pudieron mover. Puedes reintentar ahora o continuar e intentarlo más tarde.",
    "Mute": "Silenciar",
    "New team": "Nuevo equipo",
    "Next": "Siguiente",
    "Nickname": "Apodo",
    "No Power Play": "Sin Power Play",
    "No admins yet.": "Aún no hay administradores.",
    "No beta testers yet.": "Aún no hay probadores beta.",
    "No coded events to analyse.": "No hay eventos codificados para analizar.",
    "No coded events to export.": "No hay eventos codificados para exportar.",
    "No matches.": "Sin partidos.",
    "No players added yet.": "Aún no se han añadido jugadores.",
    "No players recorded.": "No hay jugadores registrados.",
    "No players yet.": "Aún no hay jugadores.",
    "No playlists assigned.": "No hay listas de reproducción asignadas.",
    "No playlists found.": "No se encontraron listas de reproducción.",
    "No reports saved.": "No hay informes guardados.",
    "No roster players available.": "No hay jugadores de la plantilla disponibles.",
    "No substitutions": "Sin sustituciones",
    "No teams found.": "No se encontraron equipos.",
    "No teams yet.": "Aún no hay equipos.",
    "No teams.": "Sin equipos.",
    "Nominate Setter": "Designar colocador",
    "Notify these users by email (optional)": "Notificar a estos usuarios por correo electrónico (opcional)",
    "OPP REC": "REC. OPOS.",
    "OPP. ERRORS": "ERRORES RIVAL",
    "Open / Download": "Abrir / Descargar",
    "Open local file": "Abrir archivo local",
    "Open menu": "Abrir menú",
    "Opposition Passing — use the opposition's reception formation on the diagram": "Recepción del rival — usar la formación de recepción del rival en el diagrama",
    "Paste the report (Markdown supported)…": "Pega el informe (se admite Markdown)…",
    "Pause": "Pausar",
    "Play": "Reproducir",
    "Playing squad": "Plantilla del partido",
    "Playing squad is full. Remove a player from the squad before adding another.": "La plantilla del partido está completa. Elimina a un jugador de la plantilla antes de añadir otro.",
    "Please sign in to save reports.": "Inicia sesión para guardar informes.",
    "Power Play Only": "Solo Power Play",
    "Record a team error (no player) — edit later to add the player and event type": "Registrar un error de equipo (sin jugador); edítalo después para añadir el jugador y el tipo de evento",
    "Record at least one event before making substitutions": "Registra al menos un evento antes de hacer sustituciones",
    "Redo": "Rehacer",
    "Remove": "Quitar",
    "Remove share": "Quitar compartición",
    "Remove yourself as admin? You will immediately lose access to this page.": "¿Quitarte a ti mismo como administrador? Perderás el acceso a esta página de inmediato.",
    "Report saved and shared.": "Informe guardado y compartido.",
    "Report saved to the player's profile.": "Informe guardado en el perfil del jugador.",
    "Report title": "Título del informe",
    "Reports": "Informes",
    "Result": "Resultado",
    "Retry": "Reintentar",
    "Return to live coding": "Volver a la anotación en vivo",
    "Rotation is not permitted while making substitutions": "No se permite la rotación mientras se realizan sustituciones",
    "SERVE": "SAQUE",
    "SETS": "SETS",
    "Save & Share": "Guardar y compartir",
    "Save this report so you can share it with other users": "Guarda este informe para poder compartirlo con otros usuarios",
    "Save this report to the player's profile": "Guardar este informe en el perfil del jugador",
    "Save to player": "Guardar en el jugador",
    "Saved lineup": "Alineación guardada",
    "Scroll, drag or swipe to scrub": "Desplaza, arrastra o desliza para avanzar o retroceder",
    "Search existing players...": "Buscar jugadores existentes…",
    "Search playlists…": "Buscar listas de reproducción…",
    "Select a match video to view while entering lineups.": "Selecciona un video del partido para verlo mientras introduces las alineaciones.",
    "Select a player.": "Selecciona un jugador.",
    "Select a team.": "Selecciona un equipo.",
    "Setting up your account": "Configurando tu cuenta",
    "Shaded rows are played under a Power Play — HP/AP marks the calling team, bold gold scores are bonus (double) points.": "Las filas sombreadas se jugaron bajo un Power Play; HP/AP indica el equipo que lo activó, las puntuaciones en negrita dorada son puntos de bonificación (dobles).",
    "Shared by": "Compartido por",
    "Shared with me": "Compartido conmigo",
    "Show help": "Mostrar ayuda",
    "Show public playlists": "Mostrar listas de reproducción públicas",
    "Standard": "Estándar",
    "Substitutions made — tap to view": "Sustituciones realizadas: toca para ver",
    "Tap the blocking player.": "Toca al jugador que bloquea.",
    "Team colour": "Color del equipo",
    "Team name": "Nombre del equipo",
    "That email is already in the list.": "Ese correo ya está en la lista.",
    "The report is too large to save. Try a single match or fewer sets.": "El informe es demasiado grande para guardarlo. Prueba con un solo partido o menos sets.",
    "This match has no Power Play calls.": "Este partido no tiene llamadas de Power Play.",
    "This player has no name to save the report against.": "Este jugador no tiene un nombre con el que guardar el informe.",
    "This team has no designated setter. Pick the player who is setting.": "Este equipo no tiene un colocador designado. Elige al jugador que está colocando.",
    "Toggle video player": "Alternar reproductor de video",
    "Unmute": "Quitar silencio",
    "Untitled playlist": "Lista de reproducción sin título",
    "Video URL…": "URL del video…",
    "We're moving your data to a faster, more reliable home. This happens only once.": "Estamos moviendo tus datos a un lugar más rápido y confiable. Esto ocurre solo una vez.",
    "You don't have permission to view this page.": "No tienes permiso para ver esta página.",
    "Your data has been migrated successfully.": "Tus datos se han migrado correctamente.",
    "Your teams and players will appear here after your data is migrated.": "Tus equipos y jugadores aparecerán aquí una vez que tus datos se hayan migrado.",
    "bonus point": "punto de bonificación",
    "bonus points": "puntos de bonificación",
    "event": "evento",
    "events": "eventos",
    "file(s).": "archivo(s).",
    "is not on this team's roster.": "no está en la plantilla de este equipo.",
    "loaded": "cargado",
    "matches": "partidos",
    "more": "más",
    "players": "jugadores",
    "points (no value was entered) — applied automatically.": "puntos (no se introdujo ningún valor); aplicado automáticamente.",
    "roster": "plantilla",
    "seconds": "segundos",
    "selected": "seleccionado(s)",
    "session(s)?": "sesión(es)?",
    "showing": "mostrando",
    "tournaments": "torneos",
    "…and": "…y",
    "● marks which team served the point.": "● marca qué equipo sacó el punto.",
    // i18n backfill pass (Catapult + misc)
    "Centimetres (cm)": "Centímetros (cm)",
    "Inches (in)": "Pulgadas (in)",
    "Jump height units": "Unidades de altura de salto",
    "Choose how Catapult jump heights are displayed and filtered.": "Elige cómo se muestran y filtran las alturas de salto de Catapult.",
    "Select Catapult Focus file...": "Seleccionar archivo Catapult Focus…",
    "Select a Catapult Focus CSV export to view jump data for this match.": "Selecciona una exportación CSV de Catapult Focus para ver los datos de salto de este partido.",
    "Loaded from saved match data": "Cargado desde los datos guardados del partido",
    "No jump data found in this file.": "No se encontraron datos de salto en este archivo.",
    "Could not read this file. Please check it is a Catapult Focus CSV export.": "No se pudo leer este archivo. Comprueba que sea una exportación CSV de Catapult Focus.",
    "Could not save jump data with this match.": "No se pudieron guardar los datos de salto con este partido.",
    "Jump data saved with this match.": "Los datos de salto se guardaron con este partido.",
    "Show players with no match stats": "Mostrar jugadores sin estadísticas del partido",
    "hidden": "ocultos",
    "jumps": "saltos",
    "periods": "períodos",
    "Sync offset": "Desfase de sincronización",
    "matched": "coincidencias",
    "match events now show jump height/duration in Video Analysis": "eventos del partido ahora muestran la altura/duración del salto en el Análisis de Vídeo",
    "Team": "Equipo",
    "Team A": "Equipo A",
    "Team B": "Equipo B",
    "Which team is this Catapult file for?": "¿Para qué equipo es este archivo de Catapult?",
    "We couldn't confidently match the players in this Focus file to one team. Please select which team it belongs to.": "No pudimos identificar con certeza a qué equipo pertenecen los jugadores de este archivo Focus. Selecciona a qué equipo pertenece.",
    "This match has no video attached yet.": "Este partido aún no tiene un vídeo adjunto.",
    "No synced jump events found for this player.": "No se encontraron eventos de salto sincronizados para este jugador.",
    "No match loaded.": "No se ha cargado ningún partido.",
    "avg": "prom.",
    "max": "máx.",
    "Sync": "Sincronización",
    "Jump Report": "Informe de salto",
    "Watch Video": "Ver vídeo",
    "Save PDF": "Guardar PDF",
    "Generated by VBLive": "Generado por VBLive",
    "Deep analysis of {team} from ball-by-ball event data, covering serving, reception, setting, attack, blocking, defence, rotations and opponent tendencies. Best used with the Anthropic (Claude) provider.": "Análisis en profundidad de {team} a partir de los datos evento a evento, que abarca saque, recepción, colocación, ataque, bloqueo, defensa, rotaciones y tendencias del rival. Mejor usado con el proveedor Anthropic (Claude).",
    "teams": "equipos",
  },

  it: {
    // Information page
    Information: "Informazioni",
    "Release notes": "Note di rilascio",
    "Contact us": "Contattaci",
    "Questions, feedback or suggestions are most welcome.": "Domande, feedback o suggerimenti sono sempre benvenuti.",
    // DV match coding
    Lineups: "Formazioni",
    Point: "Punto",
    Video: "Video",
    Defaults: "Valori predefiniti",
    "[team][NN][skill][type?][eval][zoneFrom?[szFrom?][zoneTo?[szTo?]]]": "[squadra][NN][fondamentale][tipo?][val][zonaDa?[szDa?][zonaA?[szA?]]]",
    Vertical: "Verticale",
    Horizontal: "Orizzontale",
    "Swap sides": "Scambia lati",
    Subs: "Cambi",
    Substitutions: "Sostituzioni",
    Court: "Campo",
    Home: "Casa",
    Away: "Ospite",
    Games: "Set",
    Enter: "Invio",
    Undo: "Annulla",
    "Match saved": "Partita salvata",
    "Please sign in to save this match.": "Accedi per salvare questa partita.",
    "Nothing to save yet.": "Niente da salvare per ora.",
    "Could not save the match. Please try again.": "Impossibile salvare la partita. Riprova.",
    Discard: "Annulla modifiche",
    "Resumed autosaved coding": "Ripresa della codifica salvata automaticamente",
    "Changes discarded": "Modifiche annullate",
    "Discard all changes and revert to the starting point?":
      "Annullare tutte le modifiche e tornare al punto di partenza?",
    Clear: "Cancella",
    "Prev set": "Set precedente",
    "Next set": "Set successivo",
    "Match Setup": "Impostazione partita",
    "First serve": "Primo servizio",
    "Start Match": "Inizia partita",
    "Resume Match": "Riprendi partita",
    Skip: "Salta",
    "Select an event then drag to draw ball path": "Seleziona un evento poi trascina per disegnare la traiettoria della palla",
    "Drag on court to set ball path": "Trascina sul campo per impostare la traiettoria della palla",
    "No events yet – enter a DV code above and press Enter.": "Nessun evento – inserisci un codice DV sopra e premi Invio.",
    "DataVolley code reference": "Riferimento codice DataVolley",
    Format: "Formato",
    Skills: "Fondamentali",
    Evaluation: "Valutazione",
    "Skill / type omission": "Omissione fondamentale / tipo",
    "Skill and/or type may be omitted; defaults fill in the rest.": "Il fondamentale e/o il tipo possono essere omessi; i valori predefiniti completano il resto.",
    "Dot-coding (S→R and A→B pairs)": "Codifica con punto (coppie S→R e A→B)",
    "Chaining & aliases": "Concatenazione e alias",
    "team:": "squadra:",
    "omit → defaults to A": "ometti → predefinito A",
    "NN: 1 or 2 digit jersey, e.g. 1, 01, 12": "NN: numero di maglia a 1 o 2 cifre, es. 1, 01, 12",
    "sub-zone: a b c d appended to zone digit, e.g. 5b or 5b1a": "sotto-zona: a b c d aggiunto alla cifra di zona, es. 5b o 5b1a",
    "S Serve · R Reception · E Set · A Attack · B Block · D Dig · F Freeball": "S Battuta · R Ricezione · E Alzata · A Attacco · B Muro · D Difesa · F Palla libera",
    "# kill/ace · + positive · ! ok · - negative · / overpass · = error": "# punto/ace · + positivo · ! discreto · - negativo · / sopra · = errore",
    "E.g.": "Es.",
    "serve with skill=A (default), type=H, eval=+, zone=5.": "battuta con skill=A (predefinito), type=H, eval=+, zone=5.",
    "type-only (no skill marker), defaults skill from context.": "solo tipo (nessun marcatore di fondamentale), fondamentale predefinito dal contesto.",
    "serve coded, reception auto-linked.": "battuta codificata, ricezione collegata automaticamente.",
    "attack coded, block auto-linked.": "attacco codificato, muro collegato automaticamente.",
    "Dot connects the reception eval to the corresponding serve eval via the serve→receive mapping.": "Il punto collega la valutazione della ricezione alla valutazione della battuta corrispondente tramite la mappatura battuta→ricezione.",
    "two events in one string.": "due eventi in una sola stringa.",
    "manual point A/B.": "punto manuale A/B.",
    "serve ace/error.": "ace/errore in battuta.",
    "serve with eval.": "battuta con valutazione.",
    // Stats, sideouts, filters & video tooltips
    "Block Kills": "Punti a muro",
    "Side Out Percentage": "Percentuale di cambio palla",
    "All First Ball": "Tutti i primi palloni",
    "First Ball Kills": "Punti sul primo pallone",
    "Good Serve": "Buona battuta",
    "Break Points": "Break point",
    Game: "Set",
    Row: "Posizione",
    "First Ball Sideouts": "Cambi palla primo pallone",
    "All Sideouts": "Tutti i cambi palla",
    All: "Tutti",
    None: "Nessuno",
    Receiver: "Ricettore",
    "# Passes": "N. ricezioni",
    "Perfect %": "% perfetta",
    "Positive %": "% positiva",
    "Sideout %": "% cambio palla",
    "FB Sideout %": "% cambio palla 1° pallone",
    "Pass Hits": "Attacchi dopo ricezione",
    "Pass Hit Kills": "Punti dopo ricezione",
    "Pass Hit Errors": "Errori dopo ricezione",
    "Show Passes": "Mostra ricezioni",
    "Show Attacks": "Mostra attacchi",
    "Error Pass": "Ricezione errata",
    "1 Pass": "Ricezione 1",
    "2 Pass": "Ricezione 2",
    "Perfect Pass": "Ricezione perfetta",
    "Successful SO": "Cambio palla riuscito",
    "First Ball SO": "Cambio palla 1° pallone",
    "Unsuccessful SO": "Cambio palla fallito",
    RECEIVES: "RICEZIONI",
    Sets: "Set",
    Rotations: "Rotazioni",
    "Attack Combos": "Combinazioni d'attacco",
    Stages: "Fasi",
    Display: "Visualizzazione",
    Setters: "Palleggiatori",
    Attackers: "Attaccanti",
    Results: "Risultati",
    Source: "Origine",
    Cones: "Zone",
    "Select None": "Deseleziona tutto",
    "All Serves": "Tutte le battute",
    "All Returns": "Tutte le ricezioni",
    Filters: "Filtri",
    "Reset all filters": "Reimposta tutti i filtri",
    "Show video clips of filtered attacks": "Mostra i video degli attacchi filtrati",
    "Spikes for the selected player": "Attacchi del giocatore selezionato",
    "Click on the zones to view video of attacks": "Clicca sulle zone per vedere i video degli attacchi",
    "Expand All": "Espandi tutto",
    "Collapse All": "Comprimi tutto",
    "Import Radar File": "Importa file Radar",
    "Select local video file": "Seleziona file video locale",
    "Save to database": "Salva nel database",
    "Load and play online video": "Carica e riproduci video online",
    "Synchronise video with events": "Sincronizza il video con gli eventi",
    "Rewind 10 seconds": "Indietro di 10 secondi",
    "Rewind 1 second": "Indietro di 1 secondo",
    "Forward 1 second": "Avanti di 1 secondo",
    "Forward 10 seconds": "Avanti di 10 secondi",
    "Set timing for events": "Imposta i tempi degli eventi",

    // Navigation
    Matches: "Partite",
    Live: "Diretta",
    Import: "Importa",
    Playlists: "Playlist",
    "New Match": "Nuova partita",
    // New Match / Match Coding gate
    "Match Coding": "Codifica della partita",
    "Match Coding — Coming Soon": "Codifica della partita — Prossimamente",
    "Live match coding is on the way. Here's a preview of what's coming.":
      "La codifica delle partite in tempo reale è in arrivo. Ecco un'anteprima di ciò che ti aspetta.",
    Settings: "Impostazioni",
    Language: "Lingua",
    "Your profile": "Il tuo profilo",
    "Sign out": "Esci",
    "Translation Editor": "Editor di traduzione",

    // Common actions
    Save: "Salva",
    Cancel: "Annulla",
    Delete: "Elimina",
    Share: "Condividi",
    Edit: "Modifica",
    New: "Nuovo",
    Refresh: "Aggiorna",
    Search: "Cerca",
    Reset: "Reimposta",
    Close: "Chiudi",
    "Sub for": "Sostituisci",
    "No players available": "Nessun giocatore disponibile",
    "Show full roster": "Mostra rosa completa",
    "Available only": "Solo disponibili",
    "Maximum substitutions reached": "Numero massimo di cambi raggiunto",
    Back: "Indietro",
    "Download PDF": "Scarica PDF",
    "Loading...": "Caricamento…",
    "Select All": "Seleziona tutto",
    "Unselect All": "Deseleziona tutto",
    Yes: "Sì",
    No: "No",
    Add: "Aggiungi",
    Load: "Carica",
    Confirm: "Conferma",
    Open: "Apri",
    show: "mostra",

    // Auth
    "Sign In": "Accedi",
    "Sign Up": "Registrati",
    "Sign Out": "Esci",
    Email: "E-mail",
    Password: "Password",
    "Confirm Password": "Conferma password",
    "Forgot password?": "Password dimenticata?",
    "Forgot Password": "Password dimenticata",
    "Don't have an account?": "Non hai un account?",
    "Sign up for free!": "Registrati gratis!",
    "All ready signed up?": "Già registrato?",
    "Error Signing In": "Errore di accesso",
    "Signed up successfully": "Registrazione riuscita",
    "Error signing up": "Errore durante la registrazione",
    "Enter your email": "Inserisci la tua e-mail",
    "Send Reset Email": "Invia e-mail di reimpostazione",
    "Email was sent to": "L'e-mail è stata inviata a",
    "Could not send reset email":
      "Impossibile inviare l'e-mail di reimpostazione",

    // Matches list
    "Team 1": "Squadra 1",
    "Team 2": "Squadra 2",
    Tournament: "Torneo",
    Scores: "Punteggi",
    Date: "Data",
    Actions: "Azioni",
    Owner: "Proprietario",
    Description: "Descrizione",
    Comments: "Commenti",
    Tags: "Tag",
    "Add player": "Aggiungi giocatore",
    "Add team": "Aggiungi squadra",
    "Edit playlist": "Modifica playlist",
    "Merge Teams": "Unisci squadre",
    "Roster differences detected between teams:": "Differenze di rosa rilevate tra squadre:",
    "Review the roster in the next step before merging.": "Esamina la rosa nel passaggio successivo prima di unire.",
    "At least 2 team records must exist in your Teams list for the selected matches.": "Devono esistere almeno 2 record di squadra nell'elenco per le partite selezionate.",
    "Merge...": "Unisci...",
    "Select 2 or more team records to merge into one.": "Seleziona 2 o più record di squadra da unire in uno.",
    "Filter teams...": "Filtra squadre...",
    "teams selected": "squadre selezionate",
    "Select Teams": "Seleziona squadre",
    "Reconcile Roster": "Riconcilia rosa",
    "Confirm Merge": "Conferma unione",
    "Merged team name": "Nome squadra unita",
    "shirt number(s) have conflicting names — review below.": "numero/i con nomi in conflitto — controlla sotto.",
    "Shirt": "Maglia",
    "players included": "giocatori inclusi",
    "No players to reconcile.": "Nessun giocatore da riconciliare.",
    "Use this name": "Usa questo nome",
    "Create merged team": "Crea squadra unita",
    "with": "con",
    "duplicate team records": "record di squadra duplicati",
    "Consolidate duplicate player records and reassign playlist links": "Consolida record giocatori duplicati e riassegna i link delle playlist",
    "This cannot be undone.": "Questa azione non può essere annullata.",
    "Teams being merged:": "Squadre da unire:",
    "Step": "Passo",
    "of": "di",
    "Merging...": "Unione in corso...",
    "Players & Teams": "Giocatori & Squadre",
    "No players or teams assigned.": "Nessun giocatore o squadra assegnati.",
    "Search players...": "Cerca giocatori...",
    "Search teams...": "Cerca squadre...",
    "Saving...": "Salvataggio...",
    "My Matches": "Le mie partite",
    "Shared Matches": "Partite condivise",
    "Search team...": "Cerca squadra…",
    "All Teams": "Tutte le squadre",
    "Multi Match Reports": "Report multi-partita",
    "Delete match": "Elimina partita",
    "Remove from my shared matches": "Rimuovi dalle mie partite condivise",
    "Share match": "Condividi partita",
    "Edit match": "Modifica partita",
    "Open video analysis": "Apri analisi video",
    "Delete Match": "Elimina partita",
    "Are you sure you want to delete the selected match?":
      "Sei sicuro di voler eliminare la partita selezionata?",
    "No matches selected.": "Nessuna partita selezionata.",

    // New Match
    "← Back": "← Indietro",
    "Team A (Home)": "Squadra A (casa)",
    "Team B (Away)": "Squadra B (trasferta)",
    "Select team…": "Seleziona squadra…",
    "Tournament (optional)": "Torneo (facoltativo)",
    "Tournament / competition name": "Nome torneo / competizione",
    "Match date": "Data della partita",
    "Start Coding →": "Inizia codifica →",

    // Reports
    Summary: "Riepilogo",
    "Box Score": "Tabellino",
    "Side-outs Report": "Report cambi palla",
    "Serve Receives": "Ricezioni",
    "Attack Zones": "Zone d'attacco",
    "Hitting Charts": "Grafici d'attacco",
    "Video Analysis": "Analisi video",
    Dashboard: "Dashboard",
    Charts: "Grafici",
    "AI Report": "Report IA",
    // AI Reports page
    "AI Reports": "Report IA",
    "AI Reports — Coming Soon": "Report IA — Prossimamente",
    "AI-powered match reports and deep analysis are on the way. Here are some sample reports to show what's coming.":
      "I report delle partite e le analisi approfondite con IA sono in arrivo. Ecco alcuni report di esempio in anteprima.",
    "Preview not available.": "Anteprima non disponibile.",
    "Open sample": "Apri esempio",
    "No matches were loaded. Select one or more matches and try again.":
      "Nessuna partita caricata. Seleziona una o più partite e riprova.",
    "Attack Analysis (English)": "Analisi d'attacco (inglese)",
    "Blocking Analysis (Vietnamese)": "Analisi del muro (vietnamita)",
    "Opponent Tendencies (Japanese)": "Tendenze dell'avversario (giapponese)",
    // Deep AI analysis
    "Deep AI Analysis": "Analisi IA approfondita",
    "AI Deep Analysis": "Analisi approfondita IA",
    "Load at least one match with coded events to generate the analysis.": "Carica almeno una partita con eventi codificati per generare l'analisi.",
    "Run the deep analysis one section at a time to keep each request small. Edit or add questions below, then generate. Best used with the Anthropic (Claude) provider.":
      "Esegui l'analisi approfondita una sezione alla volta per mantenere piccola ogni richiesta. Modifica o aggiungi domande qui sotto, poi genera. Si consiglia di usarla con il provider Anthropic (Claude).",
    "Download CSV": "Scarica CSV",
    "Analysis section": "Sezione di analisi",
    analysing: "analisi di",
    "All sections (uses the most credits)": "Tutte le sezioni (consuma più crediti)",
    "All sections": "Tutte le sezioni",
    Questions: "Domande",
    "one per line, edit or add your own": "una per riga, modifica o aggiungi le tue",
    "Reset to defaults": "Ripristina valori predefiniti",
    "Add a question…": "Aggiungi una domanda…",
    "Runs every section using the questions you've set for each. This sends one large request and uses the most credits.":
      "Esegue tutte le sezioni con le domande impostate per ciascuna. Invia un'unica richiesta grande e consuma più crediti.",
    "Generate full analysis": "Genera analisi completa",
    "Generate section analysis": "Genera analisi della sezione",
    Serving: "Battuta",
    Reception: "Ricezione",
    Setting: "Alzata",
    Attack: "Attacco",
    Blocking: "Muro",
    "Defence / Digging": "Difesa / Recupero",
    "Rotation Analysis": "Analisi delle rotazioni",
    "Opponent Tendencies (Scouting)": "Tendenze dell'avversario (scouting)",
    "Overall Team Metrics": "Metriche generali della squadra",
    "Serve efficiency (aces, errors, and pressured receptions forced).": "Efficienza al servizio (ace, errori e ricezioni forzate sotto pressione).",
    "Which servers are most effective, and against which reception zones?": "Quali battitori sono più efficaci e contro quali zone di ricezione?",
    "Which serve types (jump, float, jump-float) yield the best results?": "Quali tipi di servizio (in salto, flottante, flottante in salto) danno i risultati migliori?",
    "Reception quality breakdown (perfect, positive, negative, error).": "Analisi della qualità di ricezione (perfetta, positiva, negativa, errore).",
    "Which players struggle most under jump serves vs. float serves?": "Quali giocatori sono più in difficoltà con i servizi in salto rispetto ai flottanti?",
    "How does poor reception correlate with side-out efficiency?": "Come si correla una ricezione scarsa con l'efficienza del cambio palla?",
    "Set distribution by zone and by player.": "Distribuzione dell'alzata per zona e per giocatore.",
    "How does the setter perform under pressure (bad-pass situations)?": "Come rende il palleggiatore sotto pressione (situazioni di ricezione scarsa)?",
    "Tempo breakdown (quick, medium, high ball) and which yields the best attack outcome.": "Analisi dei tempi (veloce, medio, alto) e quale dà il miglior esito in attacco.",
    "Kill percentage and error rate per player, per rotation, and per zone.": "Percentuale di punti e tasso di errore per giocatore, per rotazione e per zona.",
    "Which attack zones (pipe, cross, line) have the highest efficiency?": "Quali zone d'attacco (pipe, diagonale, lungolinea) hanno la massima efficienza?",
    "How attack efficiency changes in transition vs. side-out.": "Come cambia l'efficienza in attacco in transizione rispetto al cambio palla.",
    "Block-per-set rate per player and rotation.": "Muri per set per giocatore e rotazione.",
    "Which opposing attack zones are most successfully blocked?": "Quali zone d'attacco avversarie vengono murate con più successo?",
    "How often blocks result in kills vs. touch-outs.": "Con quale frequenza i muri si traducono in punti rispetto ai muri-out.",
    "Dig success rate per player and per court zone.": "Percentuale di successo in difesa per giocatore e per zona del campo.",
    "Which attack types (tip, roll, hard swing) are hardest to defend?": "Quali tipi di attacco (pallonetto, piazzata, schiacciata potente) sono più difficili da difendere?",
    "How defensive efficiency affects transition attack success.": "Come l'efficienza difensiva influisce sul successo dell'attacco in transizione.",
    "Which rotations have the highest and lowest point differential?": "Quali rotazioni hanno il differenziale punti più alto e più basso?",
    "Side-out and break-point percentages per rotation.": "Percentuali di cambio palla e break point per rotazione.",
    "Rotation-specific vulnerabilities (e.g. a weak serving or passing rotation).": "Vulnerabilità specifiche di rotazione (ad es. una rotazione debole al servizio o in ricezione).",
    "The opponent's most frequently used attack zones and combinations.": "Le zone d'attacco e le combinazioni più usate dall'avversario.",
    "The opponent's go-to attackers in high-pressure situations.": "Gli attaccanti di riferimento dell'avversario nelle situazioni di alta pressione.",
    "The serve-receive patterns the opponent favours.": "Gli schemi di ricezione preferiti dall'avversario.",
    "Overall rally-win probability in different score situations.": "Probabilità complessiva di vincere lo scambio in diverse situazioni di punteggio.",
    "How performance changes in sets 1–3 vs. sets 4–5 (fatigue/pressure effects).": "Come cambia il rendimento nei set 1–3 rispetto ai set 4–5 (effetti di fatica/pressione).",
    "The correlation between first-ball side-out percentage and match outcome.": "La correlazione tra la percentuale di cambio palla sulla prima palla e l'esito della partita.",
    Report: "Report",

    // Import page
    "Load DVW file(s)...": "Carica file DVW…",
    "Load PSVB file(s)...": "Carica file PSVB…",
    "Load VSM file(s)...": "Carica file VSM…",
    "Load playlist file...": "Carica file playlist…",
    "load one or more DataVolley files":
      "carica uno o più file DataVolley",
    "load one or more VBStats files": "carica uno o più file VBStats",
    "load one or more VolleyStation VSM files": "carica uno o più file VolleyStation VSM",
    "Convert DVW": "Converti DVW",
    "VSM": "VSM",
    "Convert DVW to VSM...": "Converti DVW in VSM…",
    "pick a DataVolley .dvw file to download as .vsm":
      "scegli un file DataVolley .dvw da scaricare come .vsm",
    "Convert VSM to DVW...": "Converti VSM in DVW…",
    "pick a VolleyStation .vsm file to download as .dvw":
      "scegli un file VolleyStation .vsm da scaricare come .dvw",
    "Converting…": "Conversione in corso…",
    "Converted": "Convertito",
    "Failed to convert": "Conversione non riuscita",
    "Beta testers only — coming soon for everyone else.":
      "Solo per i beta tester — in arrivo presto per tutti.",
    "load a playlist": "carica una playlist",
    "Use Shift-JIS Encoder": "Usa encoder Shift-JIS",
    "Importing…": "Importazione…",
    "Save to Database?": "Salvare nel database?",
    "Always save imports automatically": "Salva sempre le importazioni automaticamente",
    "View Only": "Solo visualizzazione",
    "Save to Database": "Salva nel database",
    "Error saving to database": "Errore durante il salvataggio nel database",
    "Saved to database": "Salvato nel database",
    "Saving to database…": "Salvataggio nel database…",
    "Upload them to the database now?":
      "Caricarli ora nel database?",

    // Settings / Options
    Theme: "Tema",
    Options: "Opzioni",
    "Rotation naming convention": "Convenzione di denominazione rotazioni",
    "Choose how rotations are labelled in reports and analysis.":
      "Scegli come vengono etichettate le rotazioni nei report e nelle analisi.",
    "R1–R6 (rotation sequence)": "R1–R6 (sequenza di rotazione)",
    "S1–S6 (setter court position)": "S1–S6 (posizione del palleggiatore)",
    "R1: setter in position 1, R2: setter in 6, R3: setter in 5, R4: setter in 4, R5: setter in 3, R6: setter in 2.":
      "R1: palleggiatore in posizione 1, R2: palleggiatore in 6, R3: palleggiatore in 5, R4: palleggiatore in 4, R5: palleggiatore in 3, R6: palleggiatore in 2.",
    "S1: setter in 1, S2: setter in 2, S3: setter in 3, S4: setter in 4, S5: setter in 5, S6: setter in 6.":
      "S1: palleggiatore in 1, S2: palleggiatore in 2, S3: palleggiatore in 3, S4: palleggiatore in 4, S5: palleggiatore in 5, S6: palleggiatore in 6.",
    "Labels:": "Etichette:",
    "Current theme:": "Tema attuale:",
    "Choose the language for the app interface.":
      "Scegli la lingua dell'interfaccia dell'app.",
    "Create your own language package or edit a community one, with AI-assisted translation.":
      "Crea il tuo pacchetto lingua o modificane uno della community, con traduzione assistita dall'IA.",

    // About / Footer
    "Copyright © Perana Sports {year}. All rights reserved.":
      "Copyright © Perana Sports {year}. Tutti i diritti riservati.",
    "VBLive is free to use right now. However Perana Sports reserves the right to charge a subscription fee in due course.":
      "VBLive è gratuito al momento. Tuttavia Perana Sports si riserva il diritto di applicare un canone di abbonamento a tempo debito.",
    "Querries / suggestions are most welcomed.":
      "Domande / suggerimenti sono i benvenuti.",

    // Live
    "Please enter server name to search":
      "Inserisci il nome del server da cercare",
    "Broadcast Server": "Server di trasmissione",
    "Enter DVMateLive Server Name": "Inserisci il nome del server DVMateLive",
    LIVE: "IN DIRETTA",

    // Playlists list
    "Delete Playlist": "Elimina playlist",
    "Are you sure you want to delete the selected playlist?":
      "Sei sicuro di voler eliminare la playlist selezionata?",
    "My Playlists": "Le mie playlist",
    "Shared Playlists": "Playlist condivise",
    "Tags:": "Tag:",
    "Select...": "Seleziona…",
    "All Tags": "Tutti i tag",

    // Share
    "User already added": "Utente già aggiunto",
    "Please enter a valid email address":
      "Inserisci un indirizzo e-mail valido",
    "Please select at least one user to share with":
      "Seleziona almeno un utente con cui condividere",
    "Not Shared": "Non condiviso",
    "Available for everyone": "Disponibile per tutti",
    "Available to selected users":
      "Disponibile per gli utenti selezionati",
    "Enter VBLive user email address":
      "Inserisci l'indirizzo e-mail dell'utente VBLive",
    "Selected users:": "Utenti selezionati:",
    "Save List": "Salva elenco",
    "Clear List": "Svuota elenco",
    "Clear Users List": "Svuota elenco utenti",
    "Do you really want to clear all users from list?":
      "Vuoi davvero rimuovere tutti gli utenti dall'elenco?",
    "Do you want to save changes before closing?":
      "Vuoi salvare le modifiche prima di chiudere?",
    "Enter name to save list to:":
      "Inserisci un nome per salvare l'elenco:",
    "Enter name to save list to":
      "Inserisci un nome per salvare l'elenco",

    // AI panels
    "AI settings": "Impostazioni IA",
    "Setup needed": "Configurazione necessaria",
    "Generate report": "Genera report",
    "Generate AI report": "Genera report IA",
    "Generate general report": "Genera report generale",
    "Translate to…": "Traduci in…",
    "Follow-up questions": "Domande di approfondimento",
    "Ask a follow-up question…": "Poni una domanda di approfondimento…",
    "Ask a follow-up question": "Poni una domanda di approfondimento",
    "AI provider": "Fornitore IA",
    Model: "Modello",
    Hide: "Nascondi",
    Show: "Mostra",
    "Your key is stored only in this browser and sent directly to the selected provider.":
      "La tua chiave è memorizzata solo in questo browser e inviata direttamente al fornitore selezionato.",
    "Please enter your API key.": "Inserisci la tua chiave API.",
    "Not enough data to analyse.":
      "Dati insufficienti per l'analisi.",
    "Select a player with recorded spikes first.":
      "Seleziona prima un giocatore con attacchi registrati.",
    Player: "Giocatore",
    "Select a player…": "Seleziona un giocatore…",
    "No players with recorded spikes in the selected matches.":
      "Nessun giocatore con attacchi registrati nelle partite selezionate.",
    "Analyse with AI": "Analizza con l'IA",
    "AI scouting report": "Report di scouting IA",
    "Q:": "D:",
    Ask: "Chiedi",
    "e.g. Which rotation should we target on serve?":
      "es. Quale rotazione dovremmo puntare in battuta?",
    "Saved questions:": "Domande salvate:",
    "Click to ask again": "Clicca per chiedere di nuovo",
    "Remove saved question": "Rimuovi domanda salvata",

    // AI language options
    Spanish: "Spagnolo",
    French: "Francese",
    German: "Tedesco",
    Italian: "Italiano",
    Portuguese: "Portoghese",
    Dutch: "Olandese",
    Polish: "Polacco",
    Russian: "Russo",
    Turkish: "Turco",
    Arabic: "Arabo",
    "Chinese (Simplified)": "Cinese (semplificato)",
    Japanese: "Giapponese",
    Korean: "Coreano",
    Vietnamese: "Vietnamita",
    Thai: "Thailandese",

    // Box score
    "Show Full Headers": "Mostra intestazioni complete",
    "Show Short Headers": "Mostra intestazioni brevi",
    "Show Less Players": "Mostra meno giocatori",
    "Show All Players": "Mostra tutti i giocatori",
    "Attack Kills": "Attacchi vincenti",
    "Attack Errors": "Errori d'attacco",
    "Total Attempts": "Tentativi totali",
    "Hitting Efficiency": "Efficienza d'attacco",
    "Serve Aces": "Ace al servizio",
    "Serve Errors": "Errori al servizio",
    "Pass Errors": "Errori di ricezione",
    "% Perfect Passes": "% ricezioni perfette",
    "Passing Average": "Media ricezione",
    "Total Passes": "Ricezioni totali",
    "Set Assists": "Assist di palleggio",
    Digs: "Difese",
    "Momentum Chart": "Grafico del momentum",
    "Key event": "Azione chiave",
    "Break Point %": "% break point",
    "First Ball Sideout %": "% cambio palla primo pallone",
    "Sideout Transition %": "% cambio palla in transizione",
    "FB Sideout Kills %": "% attacco vincente primo pallone",
    "Sideout Opposition Error %": "% cambio palla su errore avversario",
    Plus: "Più",
    Minus: "Meno",
    Blocks: "Muri punto",
    "Positive Serve %": "% battute positive",
    Sideout: "Cambio palla",
    "Break point": "Break point",
    "Ace / Kill": "Ace / Attacco vincente",
    "In play": "In gioco",
    Error: "Errore",
    "Sideout on a kill": "Cambio palla su attacco vincente",
    "Sideout on an error": "Cambio palla su errore avversario",
    "Top half": "Metà superiore",
    "Bottom half": "Metà inferiore",
    "The centre line steps toward the team that wins each point.": "La linea centrale si sposta verso la squadra che vince ogni punto.",
    "No point-by-point data available for this match.": "Nessun dato punto per punto disponibile per questa partita.",
    "No match data available.": "Nessun dato della partita disponibile.",
    "This report isn't supported on this browser yet — please use Safari.": "Questo report non è ancora supportato su questo browser — utilizza Safari.",
    "No video available for this match.": "Nessun video disponibile per questa partita.",
    passes: "ricezioni",
    "eff.": "eff.",
    Grade: "Valutazione",
    LEFT: "SINISTRA",
    CENTRE: "CENTRO",
    RIGHT: "DESTRA",
    SELECTED: "SELEZIONATO",
    Passers: "Ricettori",
    Calls: "Chiamate",
    "SIDE OUT": "CAMBIO PALLA",
    TRANSITION: "TRANSIZIONE",
    "No events in selection.": "Nessun evento nella selezione corrente.",
    "Serve Receives By Players": "Ricezioni per giocatore",
    "Serve Receive By Zones": "Ricezione per zone",
    "Dig Errors": "Errori di difesa",
    "Dig Average": "Media difesa",
    "Block Solos": "Muri singoli",
    "Block Assists": "Muri assistiti",
    "Block Errors": "Errori di muro",
    "Points Scored": "Punti realizzati",
    TEAM: "SQUADRA",

    // Sideout report
    "ALL ROWS": "TUTTE LE RIGHE",
    "ROWS ": "RIGHE ",
    "ROW ": "RIGA ",
    "FB Kills": "Attacchi vincenti FB",
    "Opp. Serve Errors": "Errori battuta avv.",
    "FB Opp. Errors": "Errori avv. FB",
    "Kills/Blocks": "Attacchi/Muri",
    "Opp. Errors": "Errori avv.",
    Unsuccessful: "Senza successo",

    // Attack zones
    "No events in selected zone.":
      "Nessun evento nella zona selezionata.",
    "Show all attacks. Or click on a zone to show attacks in that zone.":
      "Mostra tutti gli attacchi. Oppure clicca su una zona per mostrare gli attacchi di quella zona.",
    "OPPONENTS (": "AVVERSARI (",

    // Video analysis filters
    "All Types": "Tutti i tipi",
    Serve: "Servizio",
    "Serve-Receive": "Ricezione",
    Set: "Palleggio",
    "Setter's Call": "Chiamata del palleggiatore",
    "Disable this skill": "Disattiva questa abilità",
    "Enable this skill": "Attiva questa abilità",
    "Teams & Players": "Squadre e giocatori",
    "Match State": "Stato della partita",
    "Custom Code": "Codice personalizzato",
    "Search... (comma-separated for multiple)": "Cerca… (separare con virgole per più valori)",
    vs: "contro",
    "Rewind 60 seconds": "Indietro di 60 secondi",
    "Rewind 1 frame": "Indietro di 1 fotogramma",
    "Forward 1 frame": "Avanti di 1 fotogramma",
    "Forward 60 seconds": "Avanti di 60 secondi",
    "Previous Clip": "Clip precedente",
    Replay: "Riproduci di nuovo",
    "Next Clip": "Clip successiva",
    Phases: "Fasi",
    "Score Range": "Intervallo punteggio",
    "Set Winner": "Vincitore del set",
    Type: "Tipo",
    Strength: "Potenza",
    "Setter Calls": "Chiamate del palleggiatore",
    "Setter Call": "Chiamata del palleggiatore",
    Freeball: "Palla libera",
    "Close (0-2)": "Equilibrato (0-2)",
    "Moderate (3-5)": "Moderato (3-5)",
    "Lopsided (6+)": "Ampio (6+)",
    "Focus Team": "Squadra monitorata",
    Oppositions: "Avversari",
    "Focus Team Won": "Squadra monitorata vincitrice",
    "Oppositions Won": "Avversari vincitori",
    "Advanced Filters": "Filtri avanzati",
    Players: "Giocatori",
    Opponents: "Avversari",
    "Event Type": "Tipo di evento",
    "Event Result": "Risultato dell'evento",
    "Attack Type": "Tipo di attacco",
    "Hit Type": "Tipo di colpo",
    "Block Type": "Tipo di muro",
    "Attack Combo": "Combinazione d'attacco",
    Apply: "Applica",
    Rotation: "Rotazione",
    Skill: "Fondamentale",
    "Lead Time": "Tempo di anticipo",
    Duration: "Durata",
    Timing: "Tempistica",
    "Go Live": "Vai al live",
    "Playback has fallen behind the live broadcast — click to jump to now.": "La riproduzione è rimasta indietro rispetto alla diretta — fai clic per passare al momento attuale.",
    "Video timing": "Tempistica video",
    "Default lead time (seconds before an event to start playback) and clip duration, per skill.": "Tempo di anticipo predefinito (secondi prima di un evento per avviare la riproduzione) e durata della clip, per fondamentale.",
    Cloud: "Cloud",
    Local: "Locale",
    "Relay host": "Host del relay",
    "IP or hostname of the machine running the local relay.": "Indirizzo IP o nome host della macchina su cui è in esecuzione il relay locale.",
    Spike: "Attacco",
    Block: "Muro",
    Defence: "Difesa",
    "All Results": "Tutti i risultati",
    Hard: "Potente",
    Soft: "Smorzata",
    Tip: "Pallonetto",
    "Not Specified": "Non specificato",
    "H - High": "H - Alta",
    "M- Medium": "M - Media",
    "Q - Quick": "Q - Veloce",
    "T - Tense": "T - Tesa",
    "U - Super": "U - Super",
    "F - Fast": "F - Rapida",
    "O - Other": "O - Altro",
    "B0 - No Block": "B0 - Nessun muro",
    "B1 - 1 Block": "B1 - 1 muro",
    "B2 - 2 Blocks": "B2 - 2 muri",
    "B3 - 3 Blocks": "B3 - 3 muri",
    "BS - Seams": "BS - Varchi",

    // All filters panel
    ALL: "TUTTI",
    NONE: "NESSUNO",

    // Playlist editor
    "Remove Item": "Rimuovi elemento",
    "Do you really want to remove the selected item?":
      "Vuoi davvero rimuovere l'elemento selezionato?",
    "Description is required": "La descrizione è obbligatoria",
    "Comments are required": "I commenti sono obbligatori",
    "Tags are required": "I tag sono obbligatori",
    "Playlist saved successfully": "Playlist salvata con successo",
    "Error saving playlist": "Errore durante il salvataggio della playlist",

    // Language editor
    "My saved packages": "I miei pacchetti salvati",
    "Loading shared languages…": "Caricamento lingue condivise…",
    "Community shared languages": "Lingue condivise dalla community",
    by: "di",
    "based on": "basato su",
    "or create new": "o creane uno nuovo",
    Use: "Usa",
    "New Language Package": "Nuovo pacchetto lingua",
    "Language Name": "Nome lingua",
    "Language Code": "Codice lingua",
    "Flag Emoji": "Emoji bandiera",
    "Base language (reference)": "Lingua di base (riferimento)",
    "If not English, both English and the reference language will be shown.":
      "Se non è inglese, verranno mostrati sia l'inglese sia la lingua di riferimento.",
    "Start Editing": "Inizia a modificare",
    "Save Key": "Salva chiave",
    "AI key set": "Chiave IA impostata",
    "Set API key for AI translation":
      "Imposta la chiave API per la traduzione IA",
    "AI key ✓": "Chiave IA ✓",
    "Set AI key": "Imposta chiave IA",
    "AI: Translate All": "IA: Traduci tutto",
    "Translating…": "Traduzione…",
    "AI translate section": "Traduci sezione con IA",
    "Clear All": "Cancella tutto",
    "Saving…": "Salvataggio…",
    English: "Inglese",
    new: "nuovo",
    "Enter translation…": "Inserisci la traduzione…",
    "Share with other VBLive users?":
      "Condividere con altri utenti VBLive?",
    "Would you like to share the": "Vuoi condividere il",
    "language package with all VBLive users? Anyone can then copy and build upon it.":
      "pacchetto lingua con tutti gli utenti VBLive? Chiunque potrà copiarlo e ampliarlo.",
    "No, just save for me": "No, salvalo solo per me",
    "Yes, share it!": "Sì, condividilo!",
    "Language package saved and shared with all VBLive users!":
      "Pacchetto lingua salvato e condiviso con tutti gli utenti VBLive!",
    "Language package saved!": "Pacchetto lingua salvato!",
    "Clear all translations? This cannot be undone.":
      "Cancellare tutte le traduzioni? L'operazione non può essere annullata.",

    // Additional UI phrases
    Teams: "Squadre",
    "Shared By": "Condiviso da",
    "View Activity": "Attività di visualizzazione",
    Viewer: "Spettatore",
    "Date / Time": "Data / Ora",
    "No views recorded yet.": "Nessuna visualizzazione registrata.",
    "Date / Scores": "Data / Punteggi",
    "Stats API source": "Origine API statistiche",
    "load one or more AVC VIS files": "carica uno o più file AVC VIS",
    "Points to close a set": "Punti per chiudere un set",
    "Require 2-point advantage": "Richiedi vantaggio di 2 punti",
    Roster: "Rosa",
    Clone: "Duplica",
    "Edit team": "Modifica squadra",
    "New player": "Nuovo giocatore",
    "Search player...": "Cerca giocatore…",
    "Match Centre": "Centro partita",
    "Points Won": "Punti vinti",
    "Opp. Error": "Errore avversario",
    Total: "Totale",
    "Match Skills": "Fondamentali di gara",
    Dig: "Difesa",
    "Best Scorers": "Migliori marcatori",
    "Power Play": "Power Play",
    Match: "Partita",
    "Player scouting": "Scouting giocatori",
    "Team deep analysis": "Analisi approfondita squadra",
    "All Players": "Tutti i giocatori",
    "All Sets": "Tutti i set",
    "All Rotations": "Tutte le rotazioni",
    "All Combos": "Tutte le combinazioni",
    "All Calls": "Tutte le chiamate",
    "All Events": "Tutti gli eventi",
    "Select all items": "Seleziona tutti gli elementi",
    "Unselect all items": "Deseleziona tutti gli elementi",
    "Create play list": "Crea playlist",
    "Point by Point": "Punto per punto",
    // i18n backfill pass — previously English-fallback-only keys
    "Access denied": "Accesso negato",
    "Aces": "Ace",
    "ACES": "ACE",
    "Add report": "Aggiungi report",
    "Admin": "Amministrazione",
    "Admins": "Amministratori",
    "Advanced": "Avanzato",
    "All set!": "Tutto pronto!",
    "Analyse team with AI": "Analizza la squadra con l'IA",
    "Are you sure you want to delete the selected play list?": "Vuoi davvero eliminare la playlist selezionata?",
    "Assign": "Assegna",
    "Assign playlist": "Assegna playlist",
    "Assigned": "Assegnato",
    "At least one admin is required.": "È richiesto almeno un amministratore.",
    "Attack Combo / Setter Call": "Combinazione d'attacco / Chiamata palleggiatore",
    "Attack diagram": "Diagramma d'attacco",
    "Attacks": "Attacchi",
    "ATTACKS": "ATTACCHI",
    "Auto-detected a custom set-closing score for": "Rilevato automaticamente un punteggio di chiusura set personalizzato per",
    "BLOCK": "MURO",
    "BLOCKS": "MURI",
    "Beta testers": "Tester beta",
    "Clear filter": "Cancella filtro",
    "Close Set": "Chiudi set",
    "Close menu": "Chiudi menu",
    "Code": "Codice",
    "Coding defaults & shortcuts": "Impostazioni predefinite e scorciatoie di scouting",
    "Continue": "Continua",
    "Could not load match data. The match may no longer be available.": "Impossibile caricare i dati della partita. La partita potrebbe non essere più disponibile.",
    "Could not load stats for these matches.": "Impossibile caricare le statistiche di queste partite.",
    "Could not load this match.": "Impossibile caricare questa partita.",
    "Could not load this playlist.": "Impossibile caricare questa playlist.",
    "Could not load this report.": "Impossibile caricare questo report.",
    "Could not save the report.": "Impossibile salvare il report.",
    "Could not share the report. Please try again.": "Impossibile condividere il report. Riprova.",
    "Court horizontal (net vertical)": "Campo orizzontale (rete verticale)",
    "Court vertical (net horizontal)": "Campo verticale (rete orizzontale)",
    "Create": "Crea",
    "Create a new team from this roster": "Crea una nuova squadra da questa rosa",
    "DIFFERENCE": "DIFFERENZA",
    "DV Code": "Codice DV",
    "Delete this report?": "Eliminare questo report?",
    "Detected a custom set-closing score of": "Rilevato un punteggio di chiusura set personalizzato di",
    "Double Sub": "Doppia sostituzione",
    "Double Sub: now pick a bench player for the flashing OPPOSITE slot…": "Doppia sostituzione: scegli ora un giocatore in panchina per la posizione lampeggiante dell'opposto…",
    "Double Sub: pick a bench player for the flashing SETTER slot…": "Doppia sostituzione: scegli un giocatore in panchina per la posizione lampeggiante del palleggiatore…",
    "Double Substitution — swap the Setter and Opposite for two incoming players": "Doppia sostituzione: scambia il palleggiatore e l'opposto con due giocatori in entrata",
    "Drag to draw the ball path; pause briefly to mark a touch (reception/dig/block), release to finish.": "Trascina per disegnare la traiettoria della palla; fai una breve pausa per segnare un tocco (ricezione/difesa/muro), rilascia per terminare.",
    "Draw": "Disegna",
    "Draw & Code": "Disegna e codifica",
    "Draw & Code — draw ball paths on the court to code the rally": "Disegna e codifica — disegna le traiettorie della palla sul campo per codificare lo scambio",
    "Edit Lineup": "Modifica formazione",
    "Edit Lineup — freely rearrange the starting lineup before the set begins": "Modifica formazione — riorganizza liberamente il sestetto iniziale prima che inizi il set",
    "Emails in this list can manage beta testers and other admins.": "Le email in questo elenco possono gestire i tester beta e altri amministratori.",
    "Emails in this list get early access to AI Reports and other gated features. Changes take effect immediately.": "Le email in questo elenco ottengono accesso anticipato ai report IA e ad altre funzioni limitate. Le modifiche hanno effetto immediato.",
    "Enter a valid email address.": "Inserisci un indirizzo email valido.",
    "Exit fullscreen": "Esci dallo schermo intero",
    "Extended": "Esteso",
    "Failed to generate PDF.": "Generazione del PDF non riuscita.",
    "Failed to save changes.": "Salvataggio delle modifiche non riuscito.",
    "Filtered by": "Filtrato per",
    "First name": "Nome",
    "For stronger security later, consider moving admin checks to a Firebase Auth custom claim instead of this email list.": "Per una maggiore sicurezza in futuro, valuta di spostare i controlli amministratore su un claim personalizzato di Firebase Auth invece di questo elenco di email.",
    "Fullscreen": "Schermo intero",
    "Generate an AI report from these matches": "Genera un report IA da queste partite",
    "How often (in seconds) the events list is updated during a live session.": "Con quale frequenza (in secondi) l'elenco degli eventi viene aggiornato durante una sessione live.",
    "Last name": "Cognome",
    "Leave blank to use standard rules (25 points, 15 for a 5th/deciding set)": "Lascia vuoto per usare le regole standard (25 punti, 15 nel 5° set decisivo)",
    "Live session refresh interval": "Intervallo di aggiornamento della sessione live",
    "Load VIS file(s)...": "Carica file VIS…",
    "Load this team's last-saved starting lineup (positions, setter and liberos)": "Carica l'ultima formazione iniziale salvata di questa squadra (posizioni, palleggiatore e liberi)",
    "Loading…": "Caricamento…",
    "Manually end the current set at its current score, regardless of the configured winning score / 2-point advantage rule": "Termina manualmente il set corrente al punteggio attuale, indipendentemente dal punteggio vincente configurato / regola del vantaggio di 2 punti",
    "Migration finished with some issues": "Migrazione completata con alcuni problemi",
    "Most of your data was migrated, but some items could not be moved. You can retry now or continue and try again later.": "La maggior parte dei tuoi dati è stata migrata, ma alcuni elementi non sono stati spostati. Puoi riprovare ora o continuare e riprovare più tardi.",
    "Mute": "Disattiva audio",
    "New team": "Nuova squadra",
    "Next": "Successivo",
    "Nickname": "Soprannome",
    "No Power Play": "Nessun Power Play",
    "No admins yet.": "Ancora nessun amministratore.",
    "No beta testers yet.": "Ancora nessun tester beta.",
    "No coded events to analyse.": "Nessun evento codificato da analizzare.",
    "No coded events to export.": "Nessun evento codificato da esportare.",
    "No matches.": "Nessuna partita.",
    "No players added yet.": "Ancora nessun giocatore aggiunto.",
    "No players recorded.": "Nessun giocatore registrato.",
    "No players yet.": "Ancora nessun giocatore.",
    "No playlists assigned.": "Nessuna playlist assegnata.",
    "No playlists found.": "Nessuna playlist trovata.",
    "No reports saved.": "Nessun report salvato.",
    "No roster players available.": "Nessun giocatore in rosa disponibile.",
    "No substitutions": "Nessuna sostituzione",
    "No teams found.": "Nessuna squadra trovata.",
    "No teams yet.": "Ancora nessuna squadra.",
    "No teams.": "Nessuna squadra.",
    "Nominate Setter": "Nomina palleggiatore",
    "Notify these users by email (optional)": "Notifica questi utenti via email (facoltativo)",
    "OPP REC": "RIC. AVV.",
    "OPP. ERRORS": "ERRORI AVV.",
    "Open / Download": "Apri / Scarica",
    "Open local file": "Apri file locale",
    "Open menu": "Apri menu",
    "Opposition Passing — use the opposition's reception formation on the diagram": "Ricezione avversaria — usa la formazione di ricezione dell'avversario nel diagramma",
    "Paste the report (Markdown supported)…": "Incolla il report (supporta Markdown)…",
    "Pause": "Pausa",
    "Play": "Riproduci",
    "Playing squad": "Rosa di partita",
    "Playing squad is full. Remove a player from the squad before adding another.": "La rosa di partita è al completo. Rimuovi un giocatore dalla rosa prima di aggiungerne un altro.",
    "Please sign in to save reports.": "Accedi per salvare i report.",
    "Power Play Only": "Solo Power Play",
    "Record a team error (no player) — edit later to add the player and event type": "Registra un errore di squadra (senza giocatore): modifica in seguito per aggiungere il giocatore e il tipo di evento",
    "Record at least one event before making substitutions": "Registra almeno un evento prima di effettuare sostituzioni",
    "Redo": "Ripeti",
    "Remove": "Rimuovi",
    "Remove share": "Rimuovi condivisione",
    "Remove yourself as admin? You will immediately lose access to this page.": "Rimuovere te stesso come amministratore? Perderai immediatamente l'accesso a questa pagina.",
    "Report saved and shared.": "Report salvato e condiviso.",
    "Report saved to the player's profile.": "Report salvato nel profilo del giocatore.",
    "Report title": "Titolo del report",
    "Reports": "Report",
    "Result": "Risultato",
    "Retry": "Riprova",
    "Return to live coding": "Torna allo scouting live",
    "Rotation is not permitted while making substitutions": "La rotazione non è consentita durante le sostituzioni",
    "SERVE": "SERVIZIO",
    "SETS": "SET",
    "Save & Share": "Salva e condividi",
    "Save this report so you can share it with other users": "Salva questo report per poterlo condividere con altri utenti",
    "Save this report to the player's profile": "Salva questo report nel profilo del giocatore",
    "Save to player": "Salva per il giocatore",
    "Saved lineup": "Formazione salvata",
    "Scroll, drag or swipe to scrub": "Scorri, trascina o scorri col dito per avanzare/riavvolgere",
    "Search existing players...": "Cerca giocatori esistenti…",
    "Search playlists…": "Cerca playlist…",
    "Select a match video to view while entering lineups.": "Seleziona un video della partita da guardare mentre inserisci le formazioni.",
    "Select a player.": "Seleziona un giocatore.",
    "Select a team.": "Seleziona una squadra.",
    "Setting up your account": "Configurazione del tuo account in corso",
    "Shaded rows are played under a Power Play — HP/AP marks the calling team, bold gold scores are bonus (double) points.": "Le righe ombreggiate sono state giocate durante un Power Play — HP/AP indica la squadra che lo ha chiamato, i punteggi in grassetto oro sono punti bonus (doppi).",
    "Shared by": "Condiviso da",
    "Shared with me": "Condiviso con me",
    "Show help": "Mostra guida",
    "Show public playlists": "Mostra playlist pubbliche",
    "Standard": "Standard",
    "Substitutions made — tap to view": "Sostituzioni effettuate: tocca per visualizzare",
    "Tap the blocking player.": "Tocca il giocatore che effettua il muro.",
    "Team colour": "Colore della squadra",
    "Team name": "Nome della squadra",
    "That email is already in the list.": "Quell'email è già nell'elenco.",
    "The report is too large to save. Try a single match or fewer sets.": "Il report è troppo grande per essere salvato. Prova con una sola partita o meno set.",
    "This match has no Power Play calls.": "Questa partita non ha chiamate di Power Play.",
    "This player has no name to save the report against.": "Questo giocatore non ha un nome a cui associare il report salvato.",
    "This team has no designated setter. Pick the player who is setting.": "Questa squadra non ha un palleggiatore designato. Scegli il giocatore che sta palleggiando.",
    "Toggle video player": "Attiva/disattiva il lettore video",
    "Unmute": "Riattiva audio",
    "Untitled playlist": "Playlist senza titolo",
    "Video URL…": "URL del video…",
    "We're moving your data to a faster, more reliable home. This happens only once.": "Stiamo spostando i tuoi dati in una sede più veloce e affidabile. Questo accade solo una volta.",
    "You don't have permission to view this page.": "Non hai il permesso di visualizzare questa pagina.",
    "Your data has been migrated successfully.": "I tuoi dati sono stati migrati con successo.",
    "Your teams and players will appear here after your data is migrated.": "Le tue squadre e i tuoi giocatori appariranno qui dopo la migrazione dei dati.",
    "bonus point": "punto bonus",
    "bonus points": "punti bonus",
    "event": "evento",
    "events": "eventi",
    "file(s).": "file.",
    "is not on this team's roster.": "non è nella rosa di questa squadra.",
    "loaded": "caricato",
    "matches": "partite",
    "more": "altri",
    "players": "giocatori",
    "points (no value was entered) — applied automatically.": "punti (nessun valore inserito) — applicato automaticamente.",
    "roster": "rosa",
    "seconds": "secondi",
    "selected": "selezionati",
    "session(s)?": "sessione/i?",
    "showing": "visualizzazione di",
    "tournaments": "tornei",
    "…and": "…e",
    "● marks which team served the point.": "● indica quale squadra ha servito il punto.",
    // i18n backfill pass (Catapult + misc)
    "Centimetres (cm)": "Centimetri (cm)",
    "Inches (in)": "Pollici (in)",
    "Jump height units": "Unità di altezza del salto",
    "Choose how Catapult jump heights are displayed and filtered.": "Scegli come vengono visualizzate e filtrate le altezze di salto Catapult.",
    "Select Catapult Focus file...": "Seleziona file Catapult Focus…",
    "Select a Catapult Focus CSV export to view jump data for this match.": "Seleziona un'esportazione CSV di Catapult Focus per visualizzare i dati di salto di questa partita.",
    "Loaded from saved match data": "Caricato dai dati della partita salvati",
    "No jump data found in this file.": "Nessun dato di salto trovato in questo file.",
    "Could not read this file. Please check it is a Catapult Focus CSV export.": "Impossibile leggere questo file. Verifica che sia un'esportazione CSV di Catapult Focus.",
    "Could not save jump data with this match.": "Impossibile salvare i dati di salto con questa partita.",
    "Jump data saved with this match.": "Dati di salto salvati con questa partita.",
    "Show players with no match stats": "Mostra i giocatori senza statistiche della partita",
    "hidden": "nascosti",
    "jumps": "salti",
    "periods": "periodi",
    "Sync offset": "Scostamento di sincronizzazione",
    "matched": "corrispondenze",
    "match events now show jump height/duration in Video Analysis": "eventi della partita ora mostrano altezza/durata del salto nell'Analisi Video",
    "Team": "Squadra",
    "Team A": "Squadra A",
    "Team B": "Squadra B",
    "Which team is this Catapult file for?": "Per quale squadra è questo file Catapult?",
    "We couldn't confidently match the players in this Focus file to one team. Please select which team it belongs to.": "Non siamo riusciti ad associare con certezza i giocatori di questo file Focus a una squadra. Seleziona a quale squadra appartiene.",
    "This match has no video attached yet.": "A questa partita non è ancora stato collegato alcun video.",
    "No synced jump events found for this player.": "Nessun evento di salto sincronizzato trovato per questo giocatore.",
    "No match loaded.": "Nessuna partita caricata.",
    "avg": "media",
    "max": "max",
    "Sync": "Sincronizzazione",
    "Jump Report": "Report salti",
    "Watch Video": "Guarda video",
    "Save PDF": "Salva PDF",
    "Generated by VBLive": "Generato da VBLive",
    "Deep analysis of {team} from ball-by-ball event data, covering serving, reception, setting, attack, blocking, defence, rotations and opponent tendencies. Best used with the Anthropic (Claude) provider.": "Analisi approfondita di {team} basata sui dati evento per evento, che copre battuta, ricezione, alzata, attacco, muro, difesa, rotazioni e tendenze dell'avversario. Consigliato con il provider Anthropic (Claude).",
    "teams": "squadre",
  },

  nl: {
    // Information page
    Information: "Informatie",
    "Release notes": "Release-opmerkingen",
    "Contact us": "Contact",
    "Questions, feedback or suggestions are most welcome.": "Vragen, feedback of suggesties zijn van harte welkom.",
    // DV match coding
    Lineups: "Opstellingen",
    Point: "Punt",
    Video: "Video",
    Defaults: "Standaardwaarden",
    "[team][NN][skill][type?][eval][zoneFrom?[szFrom?][zoneTo?[szTo?]]]": "[team][NN][vaardigheid][type?][eval][zoneVan?[szVan?][zoneNaar?[szNaar?]]]",
    Vertical: "Verticaal",
    Horizontal: "Horizontaal",
    "Swap sides": "Wissel kanten",
    Subs: "Wissels",
    Substitutions: "Wissels",
    Court: "Veld",
    Home: "Thuis",
    Away: "Uit",
    Games: "Sets",
    Enter: "Invoeren",
    Undo: "Ongedaan maken",
    "Match saved": "Wedstrijd opgeslagen",
    "Please sign in to save this match.": "Log in om deze wedstrijd op te slaan.",
    "Nothing to save yet.": "Nog niets om op te slaan.",
    "Could not save the match. Please try again.": "Kan de wedstrijd niet opslaan. Probeer het opnieuw.",
    Discard: "Verwerpen",
    "Resumed autosaved coding": "Automatisch opgeslagen codering hervat",
    "Changes discarded": "Wijzigingen verworpen",
    "Discard all changes and revert to the starting point?":
      "Alle wijzigingen verwerpen en terugkeren naar het beginpunt?",
    Clear: "Wissen",
    "Prev set": "Vorige set",
    "Next set": "Volgende set",
    "Match Setup": "Wedstrijd instellen",
    "First serve": "Eerste opslag",
    "Start Match": "Wedstrijd starten",
    "Resume Match": "Wedstrijd hervatten",
    Skip: "Overslaan",
    "Select an event then drag to draw ball path": "Selecteer een gebeurtenis en sleep om de balbaan te tekenen",
    "Drag on court to set ball path": "Sleep op het veld om de balbaan in te stellen",
    "No events yet – enter a DV code above and press Enter.": "Nog geen gebeurtenissen – voer hierboven een DV-code in en druk op Invoeren.",
    "DataVolley code reference": "DataVolley-codereferentie",
    Format: "Formaat",
    Skills: "Vaardigheden",
    Evaluation: "Beoordeling",
    "Skill / type omission": "Vaardigheid / type weglaten",
    "Skill and/or type may be omitted; defaults fill in the rest.": "Vaardigheid en/of type kunnen worden weggelaten; standaardwaarden vullen de rest in.",
    "Dot-coding (S→R and A→B pairs)": "Punt-codering (S→R- en A→B-paren)",
    "Chaining & aliases": "Koppeling & aliassen",
    "team:": "team:",
    "omit → defaults to A": "weglaten → standaard A",
    "NN: 1 or 2 digit jersey, e.g. 1, 01, 12": "NN: rugnummer van 1 of 2 cijfers, bijv. 1, 01, 12",
    "sub-zone: a b c d appended to zone digit, e.g. 5b or 5b1a": "subzone: a b c d toegevoegd aan het zonecijfer, bijv. 5b of 5b1a",
    "S Serve · R Reception · E Set · A Attack · B Block · D Dig · F Freeball": "S Opslag · R Pass · E Set-up · A Aanval · B Blok · D Verdediging · F Vrije bal",
    "# kill/ace · + positive · ! ok · - negative · / overpass · = error": "# punt/ace · + positief · ! oké · - negatief · / overpass · = fout",
    "E.g.": "Bijv.",
    "serve with skill=A (default), type=H, eval=+, zone=5.": "opslag met skill=A (standaard), type=H, eval=+, zone=5.",
    "type-only (no skill marker), defaults skill from context.": "alleen type (geen vaardigheidsmarkering), vaardigheid standaard uit context.",
    "serve coded, reception auto-linked.": "opslag gecodeerd, pass automatisch gekoppeld.",
    "attack coded, block auto-linked.": "aanval gecodeerd, blok automatisch gekoppeld.",
    "Dot connects the reception eval to the corresponding serve eval via the serve→receive mapping.": "De punt verbindt de pass-beoordeling met de bijbehorende opslag-beoordeling via de opslag→pass-koppeling.",
    "two events in one string.": "twee gebeurtenissen in één tekenreeks.",
    "manual point A/B.": "handmatig punt A/B.",
    "serve ace/error.": "opslag ace/fout.",
    "serve with eval.": "opslag met beoordeling.",
    // Stats, sideouts, filters & video tooltips
    "Block Kills": "Blokpunten",
    "Side Out Percentage": "Sideout-percentage",
    "All First Ball": "Alle eerste ballen",
    "First Ball Kills": "Eerste-bal-punten",
    "Good Serve": "Goede service",
    "Break Points": "Breakpunten",
    Game: "Set",
    Row: "Positie",
    "First Ball Sideouts": "Eerste-bal-sideouts",
    "All Sideouts": "Alle sideouts",
    All: "Alle",
    None: "Geen",
    Receiver: "Passer",
    "# Passes": "Aantal passes",
    "Perfect %": "Perfect %",
    "Positive %": "Positief %",
    "Sideout %": "Sideout %",
    "FB Sideout %": "EB-sideout %",
    "Pass Hits": "Aanvallen na pass",
    "Pass Hit Kills": "Punten na pass",
    "Pass Hit Errors": "Fouten na pass",
    "Show Passes": "Passes tonen",
    "Show Attacks": "Aanvallen tonen",
    "Error Pass": "Foutpass",
    "1 Pass": "1-pass",
    "2 Pass": "2-pass",
    "Perfect Pass": "Perfecte pass",
    "Successful SO": "Geslaagde sideout",
    "First Ball SO": "Eerste-bal-sideout",
    "Unsuccessful SO": "Mislukte sideout",
    RECEIVES: "PASSES",
    Sets: "Sets",
    Rotations: "Rotaties",
    "Attack Combos": "Aanvalscombinaties",
    Stages: "Fasen",
    Display: "Weergave",
    Setters: "Spelverdelers",
    Attackers: "Aanvallers",
    Results: "Resultaten",
    Source: "Bron",
    Cones: "Zones",
    "Select None": "Niets selecteren",
    "All Serves": "Alle services",
    "All Returns": "Alle passes",
    Filters: "Filters",
    "Reset all filters": "Alle filters resetten",
    "Show video clips of filtered attacks": "Videoclips van gefilterde aanvallen tonen",
    "Spikes for the selected player": "Aanvallen van de geselecteerde speler",
    "Click on the zones to view video of attacks": "Klik op de zones om video's van aanvallen te bekijken",
    "Expand All": "Alles uitvouwen",
    "Collapse All": "Alles invouwen",
    "Import Radar File": "Radar-bestand importeren",
    "Select local video file": "Lokaal videobestand selecteren",
    "Save to database": "Opslaan in database",
    "Load and play online video": "Online video laden en afspelen",
    "Synchronise video with events": "Video met gebeurtenissen synchroniseren",
    "Rewind 10 seconds": "10 seconden terug",
    "Rewind 1 second": "1 seconde terug",
    "Forward 1 second": "1 seconde vooruit",
    "Forward 10 seconds": "10 seconden vooruit",
    "Set timing for events": "Timing voor gebeurtenissen instellen",

    // Navigation
    Matches: "Wedstrijden",
    Live: "Live",
    Import: "Importeren",
    Playlists: "Afspeellijsten",
    "New Match": "Nieuwe wedstrijd",
    // New Match / Match Coding gate
    "Match Coding": "Wedstrijd coderen",
    "Match Coding — Coming Soon": "Wedstrijd coderen — Binnenkort beschikbaar",
    "Live match coding is on the way. Here's a preview of what's coming.":
      "Live wedstrijden coderen komt eraan. Hier is een voorproefje van wat komen gaat.",
    Settings: "Instellingen",
    Language: "Taal",
    "Your profile": "Je profiel",
    "Sign out": "Afmelden",
    "Translation Editor": "Vertaaleditor",

    // Common actions
    Save: "Opslaan",
    Cancel: "Annuleren",
    Delete: "Verwijderen",
    Share: "Delen",
    Edit: "Bewerken",
    New: "Nieuw",
    Refresh: "Vernieuwen",
    Search: "Zoeken",
    Reset: "Resetten",
    Close: "Sluiten",
    "Sub for": "Wisselen voor",
    "No players available": "Geen spelers beschikbaar",
    "Show full roster": "Volledige selectie tonen",
    "Available only": "Alleen beschikbaar",
    "Maximum substitutions reached": "Maximaal aantal wissels bereikt",
    Back: "Terug",
    "Download PDF": "PDF downloaden",
    "Loading...": "Laden…",
    "Select All": "Alles selecteren",
    "Unselect All": "Alles deselecteren",
    Yes: "Ja",
    No: "Nee",
    Add: "Toevoegen",
    Load: "Laden",
    Confirm: "Bevestigen",
    Open: "Openen",
    show: "tonen",

    // Auth
    "Sign In": "Aanmelden",
    "Sign Up": "Registreren",
    "Sign Out": "Afmelden",
    Email: "E-mail",
    Password: "Wachtwoord",
    "Confirm Password": "Wachtwoord bevestigen",
    "Forgot password?": "Wachtwoord vergeten?",
    "Forgot Password": "Wachtwoord vergeten",
    "Don't have an account?": "Heb je nog geen account?",
    "Sign up for free!": "Registreer gratis!",
    "All ready signed up?": "Al geregistreerd?",
    "Error Signing In": "Fout bij aanmelden",
    "Signed up successfully": "Registratie geslaagd",
    "Error signing up": "Fout bij registreren",
    "Enter your email": "Voer je e-mail in",
    "Send Reset Email": "Reset-e-mail verzenden",
    "Email was sent to": "E-mail is verzonden naar",
    "Could not send reset email":
      "Kan reset-e-mail niet verzenden",

    // Matches list
    "Team 1": "Team 1",
    "Team 2": "Team 2",
    Tournament: "Toernooi",
    Scores: "Scores",
    Date: "Datum",
    Actions: "Acties",
    Owner: "Eigenaar",
    Description: "Beschrijving",
    Comments: "Opmerkingen",
    Tags: "Tags",
    "Add player": "Speler toevoegen",
    "Add team": "Team toevoegen",
    "Edit playlist": "Afspeellijst bewerken",
    "Merge Teams": "Teams samenvoegen",
    "Roster differences detected between teams:": "Verschillen in selectie gedetecteerd tussen teams:",
    "Review the roster in the next step before merging.": "Controleer de selectie in de volgende stap vóór het samenvoegen.",
    "At least 2 team records must exist in your Teams list for the selected matches.": "Er moeten minimaal 2 teamrecords in uw teamlijst staan voor de geselecteerde wedstrijden.",
    "Merge...": "Samenvoegen...",
    "Select 2 or more team records to merge into one.": "Selecteer 2 of meer teamrecords om samen te voegen.",
    "Filter teams...": "Teams filteren...",
    "teams selected": "teams geselecteerd",
    "Select Teams": "Teams selecteren",
    "Reconcile Roster": "Selectie afstemmen",
    "Confirm Merge": "Samenvoegen bevestigen",
    "Merged team name": "Naam van samengevoegd team",
    "shirt number(s) have conflicting names — review below.": "rugnummer(s) hebben conflicterende namen — controleer hieronder.",
    "Shirt": "Shirt",
    "players included": "spelers inbegrepen",
    "No players to reconcile.": "Geen spelers om af te stemmen.",
    "Use this name": "Gebruik deze naam",
    "Create merged team": "Samengevoegd team aanmaken",
    "with": "met",
    "duplicate team records": "dubbele teamrecords",
    "Consolidate duplicate player records and reassign playlist links": "Dubbele spelersrecords samenvoegen en afspeellijstlinks opnieuw toewijzen",
    "This cannot be undone.": "Dit kan niet ongedaan worden gemaakt.",
    "Teams being merged:": "Teams die worden samengevoegd:",
    "Step": "Stap",
    "of": "van",
    "Merging...": "Samenvoegen...",
    "Players & Teams": "Spelers & Teams",
    "No players or teams assigned.": "Geen spelers of teams toegewezen.",
    "Search players...": "Zoek spelers...",
    "Search teams...": "Zoek teams...",
    "Saving...": "Opslaan...",
    "My Matches": "Mijn wedstrijden",
    "Shared Matches": "Gedeelde wedstrijden",
    "Search team...": "Team zoeken…",
    "All Teams": "Alle teams",
    "Multi Match Reports": "Rapporten meerdere wedstrijden",
    "Delete match": "Wedstrijd verwijderen",
    "Remove from my shared matches": "Verwijderen uit mijn gedeelde wedstrijden",
    "Share match": "Wedstrijd delen",
    "Edit match": "Wedstrijd bewerken",
    "Open video analysis": "Video-analyse openen",
    "Delete Match": "Wedstrijd verwijderen",
    "Are you sure you want to delete the selected match?":
      "Weet je zeker dat je de geselecteerde wedstrijd wilt verwijderen?",
    "No matches selected.": "Geen wedstrijden geselecteerd.",

    // New Match
    "← Back": "← Terug",
    "Team A (Home)": "Team A (thuis)",
    "Team B (Away)": "Team B (uit)",
    "Select team…": "Team selecteren…",
    "Tournament (optional)": "Toernooi (optioneel)",
    "Tournament / competition name": "Toernooi- / competitienaam",
    "Match date": "Wedstrijddatum",
    "Start Coding →": "Coderen starten →",

    // Reports
    Summary: "Samenvatting",
    "Box Score": "Statistiekenoverzicht",
    "Side-outs Report": "Side-out-rapport",
    "Serve Receives": "Serveontvangsten",
    "Attack Zones": "Aanvalszones",
    "Hitting Charts": "Aanvalsdiagrammen",
    "Video Analysis": "Video-analyse",
    Dashboard: "Dashboard",
    Charts: "Diagrammen",
    "AI Report": "AI-rapport",
    // AI Reports page
    "AI Reports": "AI-rapporten",
    "AI Reports — Coming Soon": "AI-rapporten — Binnenkort beschikbaar",
    "AI-powered match reports and deep analysis are on the way. Here are some sample reports to show what's coming.":
      "AI-gestuurde wedstrijdrapporten en diepgaande analyses komen eraan. Hier zijn enkele voorbeeldrapporten als voorproefje.",
    "Preview not available.": "Voorbeeld niet beschikbaar.",
    "Open sample": "Voorbeeld openen",
    "No matches were loaded. Select one or more matches and try again.":
      "Er zijn geen wedstrijden geladen. Selecteer een of meer wedstrijden en probeer het opnieuw.",
    "Attack Analysis (English)": "Aanvalsanalyse (Engels)",
    "Blocking Analysis (Vietnamese)": "Blokanalyse (Vietnamees)",
    "Opponent Tendencies (Japanese)": "Tegenstandertendensen (Japans)",
    // Deep AI analysis
    "Deep AI Analysis": "Diepgaande AI-analyse",
    "AI Deep Analysis": "AI-diepteanalyse",
    "Load at least one match with coded events to generate the analysis.": "Laad minstens één wedstrijd met gecodeerde acties om de analyse te genereren.",
    "Run the deep analysis one section at a time to keep each request small. Edit or add questions below, then generate. Best used with the Anthropic (Claude) provider.":
      "Voer de diepteanalyse sectie voor sectie uit om elke aanvraag klein te houden. Bewerk of voeg hieronder vragen toe en genereer daarna. Gebruik bij voorkeur met de provider Anthropic (Claude).",
    "Download CSV": "CSV downloaden",
    "Analysis section": "Analysesectie",
    analysing: "analyse van",
    "All sections (uses the most credits)": "Alle secties (gebruikt de meeste credits)",
    "All sections": "Alle secties",
    Questions: "Vragen",
    "one per line, edit or add your own": "één per regel, bewerk of voeg je eigen toe",
    "Reset to defaults": "Standaardwaarden herstellen",
    "Add a question…": "Een vraag toevoegen…",
    "Runs every section using the questions you've set for each. This sends one large request and uses the most credits.":
      "Voert alle secties uit met de vragen die je voor elke sectie hebt ingesteld. Dit verstuurt één grote aanvraag en gebruikt de meeste credits.",
    "Generate full analysis": "Volledige analyse genereren",
    "Generate section analysis": "Sectieanalyse genereren",
    Serving: "Opslag",
    Reception: "Receptie",
    Setting: "Setten",
    Attack: "Aanval",
    Blocking: "Blok",
    "Defence / Digging": "Verdediging / Verdedigen",
    "Rotation Analysis": "Rotatieanalyse",
    "Opponent Tendencies (Scouting)": "Tendensen tegenstander (scouting)",
    "Overall Team Metrics": "Algemene teamstatistieken",
    "Serve efficiency (aces, errors, and pressured receptions forced).": "Opslagefficiëntie (aces, fouten en afgedwongen passes onder druk).",
    "Which servers are most effective, and against which reception zones?": "Welke opslagspelers zijn het effectiefst, en tegen welke passzones?",
    "Which serve types (jump, float, jump-float) yield the best results?": "Welke opslagtypes (sprong, float, sprongfloat) leveren de beste resultaten op?",
    "Reception quality breakdown (perfect, positive, negative, error).": "Uitsplitsing van de paskwaliteit (perfect, positief, negatief, fout).",
    "Which players struggle most under jump serves vs. float serves?": "Welke spelers hebben de meeste moeite met sprongopslagen versus floatopslagen?",
    "How does poor reception correlate with side-out efficiency?": "Hoe hangt een slechte pass samen met de side-out-efficiëntie?",
    "Set distribution by zone and by player.": "Setverdeling per zone en per speler.",
    "How does the setter perform under pressure (bad-pass situations)?": "Hoe presteert de spelverdeler onder druk (situaties met een slechte pass)?",
    "Tempo breakdown (quick, medium, high ball) and which yields the best attack outcome.": "Tempo-uitsplitsing (snel, medium, hoge bal) en welke het beste aanvalsresultaat oplevert.",
    "Kill percentage and error rate per player, per rotation, and per zone.": "Scoringspercentage en foutpercentage per speler, per rotatie en per zone.",
    "Which attack zones (pipe, cross, line) have the highest efficiency?": "Welke aanvalszones (pipe, diagonaal, lijn) hebben de hoogste efficiëntie?",
    "How attack efficiency changes in transition vs. side-out.": "Hoe de aanvalsefficiëntie verandert in transitie versus side-out.",
    "Block-per-set rate per player and rotation.": "Blokken per set per speler en rotatie.",
    "Which opposing attack zones are most successfully blocked?": "Welke aanvalszones van de tegenstander worden het succesvolst geblokt?",
    "How often blocks result in kills vs. touch-outs.": "Hoe vaak blokken leiden tot punten versus blok-outs.",
    "Dig success rate per player and per court zone.": "Verdedigingssuccespercentage per speler en per veldzone.",
    "Which attack types (tip, roll, hard swing) are hardest to defend?": "Welke aanvalstypes (prik, lob, harde slag) zijn het moeilijkst te verdedigen?",
    "How defensive efficiency affects transition attack success.": "Hoe de verdedigingsefficiëntie het succes van de transitieaanval beïnvloedt.",
    "Which rotations have the highest and lowest point differential?": "Welke rotaties hebben het hoogste en laagste puntenverschil?",
    "Side-out and break-point percentages per rotation.": "Side-out- en breakpointpercentages per rotatie.",
    "Rotation-specific vulnerabilities (e.g. a weak serving or passing rotation).": "Rotatiespecifieke zwakke plekken (bijv. een zwakke opslag- of passrotatie).",
    "The opponent's most frequently used attack zones and combinations.": "De meest gebruikte aanvalszones en combinaties van de tegenstander.",
    "The opponent's go-to attackers in high-pressure situations.": "De vaste aanvallers van de tegenstander in situaties met hoge druk.",
    "The serve-receive patterns the opponent favours.": "De passpatronen die de tegenstander verkiest.",
    "Overall rally-win probability in different score situations.": "Algemene kans om de rally te winnen in verschillende standsituaties.",
    "How performance changes in sets 1–3 vs. sets 4–5 (fatigue/pressure effects).": "Hoe de prestaties veranderen in sets 1–3 versus sets 4–5 (vermoeidheids-/drukeffecten).",
    "The correlation between first-ball side-out percentage and match outcome.": "De correlatie tussen het first-ball-side-outpercentage en de wedstrijduitslag.",
    Report: "Rapport",

    // Import page
    "Load DVW file(s)...": "DVW-bestand(en) laden…",
    "Load PSVB file(s)...": "PSVB-bestand(en) laden…",
    "Load VSM file(s)...": "VSM-bestand(en) laden…",
    "Load playlist file...": "Afspeellijstbestand laden…",
    "load one or more DataVolley files":
      "een of meer DataVolley-bestanden laden",
    "load one or more VBStats files": "een of meer VBStats-bestanden laden",
    "load one or more VolleyStation VSM files": "een of meer VolleyStation VSM-bestanden laden",
    "Convert DVW": "DVW converteren",
    "VSM": "VSM",
    "Convert DVW to VSM...": "DVW naar VSM converteren…",
    "pick a DataVolley .dvw file to download as .vsm":
      "kies een DataVolley .dvw-bestand om als .vsm te downloaden",
    "Convert VSM to DVW...": "VSM naar DVW converteren…",
    "pick a VolleyStation .vsm file to download as .dvw":
      "kies een VolleyStation .vsm-bestand om als .dvw te downloaden",
    "Converting…": "Converteren…",
    "Converted": "Geconverteerd",
    "Failed to convert": "Converteren mislukt",
    "Beta testers only — coming soon for everyone else.":
      "Alleen voor bètatesters — binnenkort voor iedereen beschikbaar.",
    "load a playlist": "een afspeellijst laden",
    "Use Shift-JIS Encoder": "Shift-JIS-encoder gebruiken",
    "Importing…": "Importeren…",
    "Save to Database?": "Opslaan in database?",
    "Always save imports automatically": "Imports altijd automatisch opslaan",
    "View Only": "Alleen bekijken",
    "Save to Database": "Opslaan in database",
    "Error saving to database": "Fout bij opslaan in database",
    "Saved to database": "Opgeslagen in database",
    "Saving to database…": "Opslaan in database…",
    "Upload them to the database now?": "Nu uploaden naar de database?",

    // Settings / Options
    Theme: "Thema",
    Options: "Opties",
    "Rotation naming convention": "Naamgevingsconventie voor rotaties",
    "Choose how rotations are labelled in reports and analysis.":
      "Kies hoe rotaties worden gelabeld in rapporten en analyses.",
    "R1–R6 (rotation sequence)": "R1–R6 (rotatievolgorde)",
    "S1–S6 (setter court position)": "S1–S6 (spelverdelerpositie)",
    "R1: setter in position 1, R2: setter in 6, R3: setter in 5, R4: setter in 4, R5: setter in 3, R6: setter in 2.":
      "R1: spelverdeler op positie 1, R2: spelverdeler op 6, R3: spelverdeler op 5, R4: spelverdeler op 4, R5: spelverdeler op 3, R6: spelverdeler op 2.",
    "S1: setter in 1, S2: setter in 2, S3: setter in 3, S4: setter in 4, S5: setter in 5, S6: setter in 6.":
      "S1: spelverdeler op 1, S2: spelverdeler op 2, S3: spelverdeler op 3, S4: spelverdeler op 4, S5: spelverdeler op 5, S6: spelverdeler op 6.",
    "Labels:": "Labels:",
    "Current theme:": "Huidig thema:",
    "Choose the language for the app interface.":
      "Kies de taal voor de app-interface.",
    "Create your own language package or edit a community one, with AI-assisted translation.":
      "Maak je eigen taalpakket of bewerk een community-pakket, met AI-ondersteunde vertaling.",

    // About / Footer
    "Copyright © Perana Sports {year}. All rights reserved.":
      "Copyright © Perana Sports {year}. Alle rechten voorbehouden.",
    "VBLive is free to use right now. However Perana Sports reserves the right to charge a subscription fee in due course.":
      "VBLive is op dit moment gratis te gebruiken. Perana Sports behoudt zich echter het recht voor om te zijner tijd een abonnementsbedrag in rekening te brengen.",
    "Querries / suggestions are most welcomed.":
      "Vragen / suggesties zijn van harte welkom.",

    // Live
    "Please enter server name to search":
      "Voer de servernaam in om te zoeken",
    "Broadcast Server": "Uitzendserver",
    "Enter DVMateLive Server Name": "Voer DVMateLive-servernaam in",
    LIVE: "LIVE",

    // Playlists list
    "Delete Playlist": "Afspeellijst verwijderen",
    "Are you sure you want to delete the selected playlist?":
      "Weet je zeker dat je de geselecteerde afspeellijst wilt verwijderen?",
    "My Playlists": "Mijn afspeellijsten",
    "Shared Playlists": "Gedeelde afspeellijsten",
    "Tags:": "Tags:",
    "Select...": "Selecteren…",
    "All Tags": "Alle tags",

    // Share
    "User already added": "Gebruiker al toegevoegd",
    "Please enter a valid email address":
      "Voer een geldig e-mailadres in",
    "Please select at least one user to share with":
      "Selecteer ten minste één gebruiker om mee te delen",
    "Not Shared": "Niet gedeeld",
    "Available for everyone": "Beschikbaar voor iedereen",
    "Available to selected users":
      "Beschikbaar voor geselecteerde gebruikers",
    "Enter VBLive user email address":
      "Voer het e-mailadres van de VBLive-gebruiker in",
    "Selected users:": "Geselecteerde gebruikers:",
    "Save List": "Lijst opslaan",
    "Clear List": "Lijst wissen",
    "Clear Users List": "Gebruikerslijst wissen",
    "Do you really want to clear all users from list?":
      "Wil je echt alle gebruikers uit de lijst verwijderen?",
    "Do you want to save changes before closing?":
      "Wil je wijzigingen opslaan voordat je sluit?",
    "Enter name to save list to:":
      "Voer een naam in om de lijst onder op te slaan:",
    "Enter name to save list to":
      "Voer een naam in om de lijst onder op te slaan",

    // AI panels
    "AI settings": "AI-instellingen",
    "Setup needed": "Configuratie vereist",
    "Generate report": "Rapport genereren",
    "Generate AI report": "AI-rapport genereren",
    "Generate general report": "Algemeen rapport genereren",
    "Translate to…": "Vertalen naar…",
    "Follow-up questions": "Vervolgvragen",
    "Ask a follow-up question…": "Stel een vervolgvraag…",
    "Ask a follow-up question": "Stel een vervolgvraag",
    "AI provider": "AI-aanbieder",
    Model: "Model",
    Hide: "Verbergen",
    Show: "Tonen",
    "Your key is stored only in this browser and sent directly to the selected provider.":
      "Je sleutel wordt alleen in deze browser opgeslagen en rechtstreeks naar de geselecteerde aanbieder verzonden.",
    "Please enter your API key.": "Voer je API-sleutel in.",
    "Not enough data to analyse.":
      "Onvoldoende gegevens om te analyseren.",
    "Select a player with recorded spikes first.":
      "Selecteer eerst een speler met geregistreerde aanvallen.",
    Player: "Speler",
    "Select a player…": "Selecteer een speler…",
    "No players with recorded spikes in the selected matches.":
      "Geen spelers met geregistreerde aanvallen in de geselecteerde wedstrijden.",
    "Analyse with AI": "Analyseren met AI",
    "AI scouting report": "AI-scoutingrapport",
    "Q:": "V:",
    Ask: "Vragen",
    "e.g. Which rotation should we target on serve?":
      "bijv. Op welke rotatie moeten we mikken bij de service?",
    "Saved questions:": "Opgeslagen vragen:",
    "Click to ask again": "Klik om opnieuw te vragen",
    "Remove saved question": "Opgeslagen vraag verwijderen",

    // AI language options
    Spanish: "Spaans",
    French: "Frans",
    German: "Duits",
    Italian: "Italiaans",
    Portuguese: "Portugees",
    Dutch: "Nederlands",
    Polish: "Pools",
    Russian: "Russisch",
    Turkish: "Turks",
    Arabic: "Arabisch",
    "Chinese (Simplified)": "Chinees (vereenvoudigd)",
    Japanese: "Japans",
    Korean: "Koreaans",
    Vietnamese: "Vietnamees",
    Thai: "Thais",

    // Box score
    "Show Full Headers": "Volledige koppen tonen",
    "Show Short Headers": "Korte koppen tonen",
    "Show Less Players": "Minder spelers tonen",
    "Show All Players": "Alle spelers tonen",
    "Attack Kills": "Aanvalspunten",
    "Attack Errors": "Aanvalsfouten",
    "Total Attempts": "Totaal pogingen",
    "Hitting Efficiency": "Aanvalsefficiëntie",
    "Serve Aces": "Service-aces",
    "Serve Errors": "Servicefouten",
    "Pass Errors": "Passfouten",
    "% Perfect Passes": "% perfecte passes",
    "Passing Average": "Pasgemiddelde",
    "Total Passes": "Totaal passes",
    "Set Assists": "Set-assists",
    Digs: "Verdedigingen",
    "Momentum Chart": "Momentumgrafiek",
    "Key event": "Sleutelmoment",
    "Break Point %": "Breakpunt %",
    "First Ball Sideout %": "Eerste-bal-sideout %",
    "Sideout Transition %": "Sideout in transitie %",
    "FB Sideout Kills %": "Eerste-bal-aanvalspunten %",
    "Sideout Opposition Error %": "Sideout door fout tegenstander %",
    Plus: "Plus",
    Minus: "Min",
    Blocks: "Blockpunten",
    "Positive Serve %": "Positieve services %",
    Sideout: "Sideout",
    "Break point": "Breakpunt",
    "Ace / Kill": "Ace / Punt aanval",
    "In play": "In het spel",
    Error: "Fout",
    "Sideout on a kill": "Sideout op puntaanval",
    "Sideout on an error": "Sideout op fout tegenstander",
    "Top half": "Bovenste helft",
    "Bottom half": "Onderste helft",
    "The centre line steps toward the team that wins each point.": "De middellijn beweegt naar het team dat elk punt wint.",
    "No point-by-point data available for this match.": "Geen punt-voor-punt gegevens beschikbaar voor deze wedstrijd.",
    "No match data available.": "Geen wedstrijdgegevens beschikbaar.",
    "This report isn't supported on this browser yet — please use Safari.": "Dit rapport wordt nog niet ondersteund in deze browser — gebruik Safari.",
    "No video available for this match.": "Geen video beschikbaar voor deze wedstrijd.",
    passes: "passes",
    "eff.": "eff.",
    Grade: "Cijfer",
    LEFT: "LINKS",
    CENTRE: "MIDDEN",
    RIGHT: "RECHTS",
    SELECTED: "GESELECTEERD",
    Passers: "Passers",
    Calls: "Oproepen",
    "SIDE OUT": "SIDEOUT",
    TRANSITION: "TRANSITIE",
    "No events in selection.": "Geen gebeurtenissen in de huidige selectie.",
    "Serve Receives By Players": "Serveontvangsten per speler",
    "Serve Receive By Zones": "Serveontvangst per zone",
    "Dig Errors": "Verdedigingsfouten",
    "Dig Average": "Verdedigingsgemiddelde",
    "Block Solos": "Soloblocks",
    "Block Assists": "Block-assists",
    "Block Errors": "Blockfouten",
    "Points Scored": "Gescoorde punten",
    TEAM: "TEAM",

    // Sideout report
    "ALL ROWS": "ALLE RIJEN",
    "ROWS ": "RIJEN ",
    "ROW ": "RIJ ",
    "FB Kills": "FB-aanvalspunten",
    "Opp. Serve Errors": "Servicefouten tegenst.",
    "FB Opp. Errors": "FB-fouten tegenst.",
    "Kills/Blocks": "Aanvallen/Blocks",
    "Opp. Errors": "Fouten tegenst.",
    Unsuccessful: "Niet geslaagd",

    // Attack zones
    "No events in selected zone.":
      "Geen gebeurtenissen in de geselecteerde zone.",
    "Show all attacks. Or click on a zone to show attacks in that zone.":
      "Toon alle aanvallen. Of klik op een zone om de aanvallen in die zone te tonen.",
    "OPPONENTS (": "TEGENSTANDERS (",

    // Video analysis filters
    "All Types": "Alle typen",
    Serve: "Service",
    "Serve-Receive": "Serveontvangst",
    Set: "Set",
    "Setter's Call": "Aanwijzing spelverdeler",
    "Disable this skill": "Deze vaardigheid uitschakelen",
    "Enable this skill": "Deze vaardigheid inschakelen",
    "Teams & Players": "Teams & spelers",
    "Match State": "Wedstrijdstatus",
    "Custom Code": "Aangepaste code",
    "Search... (comma-separated for multiple)": "Zoeken… (gescheiden door komma's voor meerdere)",
    vs: "vs.",
    "Rewind 60 seconds": "60 seconden terug",
    "Rewind 1 frame": "1 beeld terug",
    "Forward 1 frame": "1 beeld vooruit",
    "Forward 60 seconds": "60 seconden vooruit",
    "Previous Clip": "Vorige clip",
    Replay: "Opnieuw afspelen",
    "Next Clip": "Volgende clip",
    Phases: "Fasen",
    "Score Range": "Scorebereik",
    "Set Winner": "Setwinnaar",
    Type: "Type",
    Strength: "Kracht",
    "Setter Calls": "Aanwijzingen spelverdeler",
    "Setter Call": "Aanwijzing spelverdeler",
    Freeball: "Vrije bal",
    "Close (0-2)": "Krap (0-2)",
    "Moderate (3-5)": "Gemiddeld (3-5)",
    "Lopsided (6+)": "Groot verschil (6+)",
    "Focus Team": "Gevolgd team",
    Oppositions: "Tegenstanders",
    "Focus Team Won": "Gevolgd team gewonnen",
    "Oppositions Won": "Tegenstanders gewonnen",
    "Advanced Filters": "Geavanceerde filters",
    Players: "Spelers",
    Opponents: "Tegenstanders",
    "Event Type": "Gebeurtenistype",
    "Event Result": "Gebeurtenisresultaat",
    "Attack Type": "Aanvalstype",
    "Hit Type": "Slagtype",
    "Block Type": "Bloktype",
    "Attack Combo": "Aanvalscombinatie",
    Apply: "Toepassen",
    Rotation: "Rotatie",
    Skill: "Vaardigheid",
    "Lead Time": "Voorlooptijd",
    Duration: "Duur",
    Timing: "Timing",
    "Go Live": "Naar live",
    "Playback has fallen behind the live broadcast — click to jump to now.": "De weergave loopt achter op de livestream — klik om naar nu te springen.",
    "Video timing": "Video-timing",
    "Default lead time (seconds before an event to start playback) and clip duration, per skill.": "Standaard voorlooptijd (seconden vóór een gebeurtenis om de weergave te starten) en clipduur, per vaardigheid.",
    Cloud: "Cloud",
    Local: "Lokaal",
    "Relay host": "Relayhost",
    "IP or hostname of the machine running the local relay.": "IP-adres of hostnaam van de machine waarop de lokale relay draait.",
    Spike: "Aanval",
    Block: "Block",
    Defence: "Verdediging",
    "All Results": "Alle resultaten",
    Hard: "Hard",
    Soft: "Zacht",
    Tip: "Tip",
    "Not Specified": "Niet opgegeven",
    "H - High": "H - Hoog",
    "M- Medium": "M - Middel",
    "Q - Quick": "Q - Snel",
    "T - Tense": "T - Strak",
    "U - Super": "U - Super",
    "F - Fast": "F - Snel",
    "O - Other": "O - Overig",
    "B0 - No Block": "B0 - Geen block",
    "B1 - 1 Block": "B1 - 1 blocker",
    "B2 - 2 Blocks": "B2 - 2 blockers",
    "B3 - 3 Blocks": "B3 - 3 blockers",
    "BS - Seams": "BS - Naden",

    // All filters panel
    ALL: "ALLE",
    NONE: "GEEN",

    // Playlist editor
    "Remove Item": "Item verwijderen",
    "Do you really want to remove the selected item?":
      "Wil je het geselecteerde item echt verwijderen?",
    "Description is required": "Beschrijving is verplicht",
    "Comments are required": "Opmerkingen zijn verplicht",
    "Tags are required": "Tags zijn verplicht",
    "Playlist saved successfully": "Afspeellijst succesvol opgeslagen",
    "Error saving playlist": "Fout bij opslaan van afspeellijst",

    // Language editor
    "My saved packages": "Mijn opgeslagen pakketten",
    "Loading shared languages…": "Gedeelde talen laden…",
    "Community shared languages": "Door de community gedeelde talen",
    by: "door",
    "based on": "gebaseerd op",
    "or create new": "of maak een nieuwe",
    Use: "Gebruiken",
    "New Language Package": "Nieuw taalpakket",
    "Language Name": "Taalnaam",
    "Language Code": "Taalcode",
    "Flag Emoji": "Vlag-emoji",
    "Base language (reference)": "Basistaal (referentie)",
    "If not English, both English and the reference language will be shown.":
      "Als het niet Engels is, worden zowel Engels als de referentietaal getoond.",
    "Start Editing": "Beginnen met bewerken",
    "Save Key": "Sleutel opslaan",
    "AI key set": "AI-sleutel ingesteld",
    "Set API key for AI translation":
      "API-sleutel instellen voor AI-vertaling",
    "AI key ✓": "AI-sleutel ✓",
    "Set AI key": "AI-sleutel instellen",
    "AI: Translate All": "AI: Alles vertalen",
    "Translating…": "Vertalen…",
    "AI translate section": "Sectie vertalen met AI",
    "Clear All": "Alles wissen",
    "Saving…": "Opslaan…",
    English: "Engels",
    new: "nieuw",
    "Enter translation…": "Voer vertaling in…",
    "Share with other VBLive users?":
      "Delen met andere VBLive-gebruikers?",
    "Would you like to share the": "Wil je het",
    "language package with all VBLive users? Anyone can then copy and build upon it.":
      "taalpakket delen met alle VBLive-gebruikers? Iedereen kan het dan kopiëren en uitbreiden.",
    "No, just save for me": "Nee, alleen voor mij opslaan",
    "Yes, share it!": "Ja, delen!",
    "Language package saved and shared with all VBLive users!":
      "Taalpakket opgeslagen en gedeeld met alle VBLive-gebruikers!",
    "Language package saved!": "Taalpakket opgeslagen!",
    "Clear all translations? This cannot be undone.":
      "Alle vertalingen wissen? Dit kan niet ongedaan worden gemaakt.",

    // Additional UI phrases
    Teams: "Teams",
    "Shared By": "Gedeeld door",
    "View Activity": "Weergaveactiviteit",
    Viewer: "Kijker",
    "Date / Time": "Datum / Tijd",
    "No views recorded yet.": "Nog geen weergaven geregistreerd.",
    "Date / Scores": "Datum / Scores",
    "Stats API source": "Statistieken-API-bron",
    "load one or more AVC VIS files": "een of meer AVC VIS-bestanden laden",
    "Points to close a set": "Punten om een set te sluiten",
    "Require 2-point advantage": "2-punts voorsprong vereisen",
    Roster: "Selectie",
    Clone: "Dupliceren",
    "Edit team": "Team bewerken",
    "New player": "Nieuwe speler",
    "Search player...": "Speler zoeken…",
    "Match Centre": "Wedstrijdcentrum",
    "Points Won": "Gewonnen punten",
    "Opp. Error": "Fout tegenstander",
    Total: "Totaal",
    "Match Skills": "Wedstrijdvaardigheden",
    Dig: "Verdediging",
    "Best Scorers": "Beste scorers",
    "Power Play": "Power Play",
    Match: "Wedstrijd",
    "Player scouting": "Spelersscouting",
    "Team deep analysis": "Diepgaande teamanalyse",
    "All Players": "Alle spelers",
    "All Sets": "Alle sets",
    "All Rotations": "Alle rotaties",
    "All Combos": "Alle combinaties",
    "All Calls": "Alle oproepen",
    "All Events": "Alle gebeurtenissen",
    "Select all items": "Alle items selecteren",
    "Unselect all items": "Alle items deselecteren",
    "Create play list": "Afspeellijst maken",
    "Point by Point": "Punt voor punt",
    // i18n backfill pass — previously English-fallback-only keys
    "Access denied": "Toegang geweigerd",
    "Aces": "Aces",
    "ACES": "ACES",
    "Add report": "Rapport toevoegen",
    "Admin": "Beheer",
    "Admins": "Beheerders",
    "Advanced": "Geavanceerd",
    "All set!": "Helemaal klaar!",
    "Analyse team with AI": "Team analyseren met AI",
    "Are you sure you want to delete the selected play list?": "Weet je zeker dat je de geselecteerde afspeellijst wilt verwijderen?",
    "Assign": "Toewijzen",
    "Assign playlist": "Afspeellijst toewijzen",
    "Assigned": "Toegewezen",
    "At least one admin is required.": "Er is minstens één beheerder vereist.",
    "Attack Combo / Setter Call": "Aanvalscombinatie / Spelverdeleraanroep",
    "Attack diagram": "Aanvalsdiagram",
    "Attacks": "Aanvallen",
    "ATTACKS": "AANVALLEN",
    "Auto-detected a custom set-closing score for": "Automatisch gedetecteerde aangepaste eindstand voor",
    "BLOCK": "BLOK",
    "BLOCKS": "BLOKKEN",
    "Beta testers": "Bètatesters",
    "Clear filter": "Filter wissen",
    "Close Set": "Set afsluiten",
    "Close menu": "Menu sluiten",
    "Code": "Code",
    "Coding defaults & shortcuts": "Standaardinstellingen en sneltoetsen voor scouting",
    "Continue": "Doorgaan",
    "Could not load match data. The match may no longer be available.": "Kan wedstrijdgegevens niet laden. De wedstrijd is mogelijk niet meer beschikbaar.",
    "Could not load stats for these matches.": "Kan statistieken voor deze wedstrijden niet laden.",
    "Could not load this match.": "Kan deze wedstrijd niet laden.",
    "Could not load this playlist.": "Kan deze afspeellijst niet laden.",
    "Could not load this report.": "Kan dit rapport niet laden.",
    "Could not save the report.": "Kan het rapport niet opslaan.",
    "Could not share the report. Please try again.": "Kan het rapport niet delen. Probeer het opnieuw.",
    "Court horizontal (net vertical)": "Veld horizontaal (net verticaal)",
    "Court vertical (net horizontal)": "Veld verticaal (net horizontaal)",
    "Create": "Aanmaken",
    "Create a new team from this roster": "Nieuw team maken op basis van deze selectie",
    "DIFFERENCE": "VERSCHIL",
    "DV Code": "DV-code",
    "Delete this report?": "Dit rapport verwijderen?",
    "Detected a custom set-closing score of": "Aangepaste eindstand gedetecteerd van",
    "Double Sub": "Dubbele wissel",
    "Double Sub: now pick a bench player for the flashing OPPOSITE slot…": "Dubbele wissel: kies nu een bankspeler voor de knipperende diagonaalspeler-positie…",
    "Double Sub: pick a bench player for the flashing SETTER slot…": "Dubbele wissel: kies een bankspeler voor de knipperende spelverdeler-positie…",
    "Double Substitution — swap the Setter and Opposite for two incoming players": "Dubbele wissel — vervang de spelverdeler en de diagonaalspeler door twee invallers",
    "Drag to draw the ball path; pause briefly to mark a touch (reception/dig/block), release to finish.": "Sleep om het balpad te tekenen; pauzeer even om een aanraking (aanname/verdediging/blok) te markeren, laat los om te voltooien.",
    "Draw": "Tekenen",
    "Draw & Code": "Tekenen en coderen",
    "Draw & Code — draw ball paths on the court to code the rally": "Tekenen en coderen — teken balpaden op het veld om de rally te coderen",
    "Edit Lineup": "Opstelling bewerken",
    "Edit Lineup — freely rearrange the starting lineup before the set begins": "Opstelling bewerken — herschik de startopstelling vrij voordat de set begint",
    "Emails in this list can manage beta testers and other admins.": "E-mails in deze lijst kunnen bètatesters en andere beheerders beheren.",
    "Emails in this list get early access to AI Reports and other gated features. Changes take effect immediately.": "E-mails in deze lijst krijgen vroege toegang tot AI-rapporten en andere beperkte functies. Wijzigingen zijn direct van kracht.",
    "Enter a valid email address.": "Voer een geldig e-mailadres in.",
    "Exit fullscreen": "Volledig scherm verlaten",
    "Extended": "Uitgebreid",
    "Failed to generate PDF.": "PDF genereren mislukt.",
    "Failed to save changes.": "Wijzigingen opslaan mislukt.",
    "Filtered by": "Gefilterd op",
    "First name": "Voornaam",
    "For stronger security later, consider moving admin checks to a Firebase Auth custom claim instead of this email list.": "Overweeg voor meer veiligheid later om beheerderscontroles te verplaatsen naar een aangepaste Firebase Auth-claim in plaats van deze e-maillijst.",
    "Fullscreen": "Volledig scherm",
    "Generate an AI report from these matches": "Genereer een AI-rapport op basis van deze wedstrijden",
    "How often (in seconds) the events list is updated during a live session.": "Hoe vaak (in seconden) de gebeurtenissenlijst wordt bijgewerkt tijdens een live-sessie.",
    "Last name": "Achternaam",
    "Leave blank to use standard rules (25 points, 15 for a 5th/deciding set)": "Laat leeg om standaardregels te gebruiken (25 punten, 15 voor een beslissende 5e set)",
    "Live session refresh interval": "Vernieuwingsinterval live-sessie",
    "Load VIS file(s)...": "VIS-bestand(en) laden…",
    "Load this team's last-saved starting lineup (positions, setter and liberos)": "Laad de laatst opgeslagen startopstelling van dit team (posities, spelverdeler en libero's)",
    "Loading…": "Laden…",
    "Manually end the current set at its current score, regardless of the configured winning score / 2-point advantage rule": "Beëindig de huidige set handmatig bij de huidige stand, ongeacht de ingestelde winnende score / 2-puntsvoorsprongregel",
    "Migration finished with some issues": "Migratie voltooid met enkele problemen",
    "Most of your data was migrated, but some items could not be moved. You can retry now or continue and try again later.": "Het grootste deel van je gegevens is gemigreerd, maar sommige items konden niet worden verplaatst. Je kunt het nu opnieuw proberen of doorgaan en het later opnieuw proberen.",
    "Mute": "Dempen",
    "New team": "Nieuw team",
    "Next": "Volgende",
    "Nickname": "Bijnaam",
    "No Power Play": "Geen Power Play",
    "No admins yet.": "Nog geen beheerders.",
    "No beta testers yet.": "Nog geen bètatesters.",
    "No coded events to analyse.": "Geen gecodeerde gebeurtenissen om te analyseren.",
    "No coded events to export.": "Geen gecodeerde gebeurtenissen om te exporteren.",
    "No matches.": "Geen wedstrijden.",
    "No players added yet.": "Nog geen spelers toegevoegd.",
    "No players recorded.": "Geen spelers geregistreerd.",
    "No players yet.": "Nog geen spelers.",
    "No playlists assigned.": "Geen afspeellijsten toegewezen.",
    "No playlists found.": "Geen afspeellijsten gevonden.",
    "No reports saved.": "Geen rapporten opgeslagen.",
    "No roster players available.": "Geen selectiespelers beschikbaar.",
    "No substitutions": "Geen wissels",
    "No teams found.": "Geen teams gevonden.",
    "No teams yet.": "Nog geen teams.",
    "No teams.": "Geen teams.",
    "Nominate Setter": "Spelverdeler aanwijzen",
    "Notify these users by email (optional)": "Deze gebruikers per e-mail op de hoogte stellen (optioneel)",
    "OPP REC": "TEG. AANNAME",
    "OPP. ERRORS": "TEG. FOUTEN",
    "Open / Download": "Openen / Downloaden",
    "Open local file": "Lokaal bestand openen",
    "Open menu": "Menu openen",
    "Opposition Passing — use the opposition's reception formation on the diagram": "Tegenstander aanname — gebruik de aannameformatie van de tegenstander in het diagram",
    "Paste the report (Markdown supported)…": "Plak het rapport (Markdown wordt ondersteund)…",
    "Pause": "Pauzeren",
    "Play": "Afspelen",
    "Playing squad": "Wedstrijdselectie",
    "Playing squad is full. Remove a player from the squad before adding another.": "De wedstrijdselectie is vol. Verwijder een speler uit de selectie voordat je er nog een toevoegt.",
    "Please sign in to save reports.": "Log in om rapporten op te slaan.",
    "Power Play Only": "Alleen Power Play",
    "Record a team error (no player) — edit later to add the player and event type": "Een teamfout registreren (zonder speler) — later bewerken om de speler en het gebeurtenistype toe te voegen",
    "Record at least one event before making substitutions": "Registreer minstens één gebeurtenis voordat je wissels doorvoert",
    "Redo": "Opnieuw",
    "Remove": "Verwijderen",
    "Remove share": "Delen verwijderen",
    "Remove yourself as admin? You will immediately lose access to this page.": "Jezelf verwijderen als beheerder? Je verliest direct de toegang tot deze pagina.",
    "Report saved and shared.": "Rapport opgeslagen en gedeeld.",
    "Report saved to the player's profile.": "Rapport opgeslagen in het spelersprofiel.",
    "Report title": "Rapporttitel",
    "Reports": "Rapporten",
    "Result": "Resultaat",
    "Retry": "Opnieuw proberen",
    "Return to live coding": "Terug naar live scouting",
    "Rotation is not permitted while making substitutions": "Rotatie is niet toegestaan tijdens het wisselen",
    "SERVE": "SERVICE",
    "SETS": "SETS",
    "Save & Share": "Opslaan en delen",
    "Save this report so you can share it with other users": "Sla dit rapport op zodat je het met andere gebruikers kunt delen",
    "Save this report to the player's profile": "Sla dit rapport op in het spelersprofiel",
    "Save to player": "Opslaan voor speler",
    "Saved lineup": "Opgeslagen opstelling",
    "Scroll, drag or swipe to scrub": "Scrollen, slepen of vegen om te spoelen",
    "Search existing players...": "Bestaande spelers zoeken…",
    "Search playlists…": "Afspeellijsten zoeken…",
    "Select a match video to view while entering lineups.": "Selecteer een wedstrijdvideo om te bekijken tijdens het invoeren van opstellingen.",
    "Select a player.": "Selecteer een speler.",
    "Select a team.": "Selecteer een team.",
    "Setting up your account": "Je account wordt ingesteld",
    "Shaded rows are played under a Power Play — HP/AP marks the calling team, bold gold scores are bonus (double) points.": "Gearceerde rijen werden gespeeld onder een Power Play — HP/AP markeert het aanroepende team, vetgedrukte goudkleurige scores zijn bonus(dubbele) punten.",
    "Shared by": "Gedeeld door",
    "Shared with me": "Met mij gedeeld",
    "Show help": "Help weergeven",
    "Show public playlists": "Openbare afspeellijsten weergeven",
    "Standard": "Standaard",
    "Substitutions made — tap to view": "Wissels uitgevoerd — tik om te bekijken",
    "Tap the blocking player.": "Tik op de blokkerende speler.",
    "Team colour": "Teamkleur",
    "Team name": "Teamnaam",
    "That email is already in the list.": "Dat e-mailadres staat al in de lijst.",
    "The report is too large to save. Try a single match or fewer sets.": "Het rapport is te groot om op te slaan. Probeer één wedstrijd of minder sets.",
    "This match has no Power Play calls.": "Deze wedstrijd heeft geen Power Play-oproepen.",
    "This player has no name to save the report against.": "Deze speler heeft geen naam om het rapport tegen op te slaan.",
    "This team has no designated setter. Pick the player who is setting.": "Dit team heeft geen aangewezen spelverdeler. Kies de speler die toespeelt.",
    "Toggle video player": "Videospeler in-/uitschakelen",
    "Unmute": "Dempen opheffen",
    "Untitled playlist": "Naamloze afspeellijst",
    "Video URL…": "Video-URL…",
    "We're moving your data to a faster, more reliable home. This happens only once.": "We verplaatsen je gegevens naar een snellere, betrouwbaardere plek. Dit gebeurt slechts één keer.",
    "You don't have permission to view this page.": "Je hebt geen toestemming om deze pagina te bekijken.",
    "Your data has been migrated successfully.": "Je gegevens zijn succesvol gemigreerd.",
    "Your teams and players will appear here after your data is migrated.": "Je teams en spelers verschijnen hier zodra je gegevens zijn gemigreerd.",
    "bonus point": "bonuspunt",
    "bonus points": "bonuspunten",
    "event": "gebeurtenis",
    "events": "gebeurtenissen",
    "file(s).": "bestand(en).",
    "is not on this team's roster.": "staat niet in de selectie van dit team.",
    "loaded": "geladen",
    "matches": "wedstrijden",
    "more": "meer",
    "players": "spelers",
    "points (no value was entered) — applied automatically.": "punten (geen waarde ingevoerd) — automatisch toegepast.",
    "roster": "selectie",
    "seconds": "seconden",
    "selected": "geselecteerd",
    "session(s)?": "sessie(s)?",
    "showing": "toont",
    "tournaments": "toernooien",
    "…and": "…en",
    "● marks which team served the point.": "● geeft aan welk team het punt serveerde.",
    // i18n backfill pass (Catapult + misc)
    "Centimetres (cm)": "Centimeters (cm)",
    "Inches (in)": "Inches (in)",
    "Jump height units": "Spronghoogte-eenheden",
    "Choose how Catapult jump heights are displayed and filtered.": "Kies hoe Catapult-spronghoogtes worden weergegeven en gefilterd.",
    "Select Catapult Focus file...": "Catapult Focus-bestand selecteren…",
    "Select a Catapult Focus CSV export to view jump data for this match.": "Selecteer een Catapult Focus CSV-export om de sprongdata voor deze wedstrijd te bekijken.",
    "Loaded from saved match data": "Geladen uit opgeslagen wedstrijdgegevens",
    "No jump data found in this file.": "Geen sprongdata gevonden in dit bestand.",
    "Could not read this file. Please check it is a Catapult Focus CSV export.": "Kan dit bestand niet lezen. Controleer of het een Catapult Focus CSV-export is.",
    "Could not save jump data with this match.": "Kan sprongdata niet opslaan bij deze wedstrijd.",
    "Jump data saved with this match.": "Sprongdata opgeslagen bij deze wedstrijd.",
    "Show players with no match stats": "Spelers zonder wedstrijdstatistieken weergeven",
    "hidden": "verborgen",
    "jumps": "sprongen",
    "periods": "periodes",
    "Sync offset": "Synchronisatie-offset",
    "matched": "overeenkomend",
    "match events now show jump height/duration in Video Analysis": "wedstrijdgebeurtenissen tonen nu sprong hoogte/duur in Video-analyse",
    "Team": "Team",
    "Team A": "Team A",
    "Team B": "Team B",
    "Which team is this Catapult file for?": "Voor welk team is dit Catapult-bestand?",
    "We couldn't confidently match the players in this Focus file to one team. Please select which team it belongs to.": "We konden de spelers in dit Focus-bestand niet met zekerheid aan één team koppelen. Selecteer tot welk team het behoort.",
    "This match has no video attached yet.": "Aan deze wedstrijd is nog geen video gekoppeld.",
    "No synced jump events found for this player.": "Geen gesynchroniseerde sprongacties gevonden voor deze speler.",
    "No match loaded.": "Geen wedstrijd geladen.",
    "avg": "gem.",
    "max": "max.",
    "Sync": "Synchronisatie",
    "Jump Report": "Sprongrapport",
    "Watch Video": "Video bekijken",
    "Save PDF": "PDF opslaan",
    "Generated by VBLive": "Gegenereerd door VBLive",
    "Deep analysis of {team} from ball-by-ball event data, covering serving, reception, setting, attack, blocking, defence, rotations and opponent tendencies. Best used with the Anthropic (Claude) provider.": "Diepgaande analyse van {team} op basis van bal-voor-bal gebeurtenisdata, met aanval, service, passing, set, blok, verdediging, rotaties en tegenstandertendensen. Het beste te gebruiken met de Anthropic (Claude) provider.",
    "teams": "teams",
  },

  pt: {
    // Information page
    Information: "Informações",
    "Release notes": "Notas de versão",
    "Contact us": "Fale conosco",
    "Questions, feedback or suggestions are most welcome.": "Perguntas, comentários ou sugestões são bem-vindos.",
    // DV match coding
    Lineups: "Escalações",
    Point: "Ponto",
    Video: "Vídeo",
    Defaults: "Valores padrão",
    "[team][NN][skill][type?][eval][zoneFrom?[szFrom?][zoneTo?[szTo?]]]": "[equipe][NN][fundamento][tipo?][aval][zonaDe?[szDe?][zonaPara?[szPara?]]]",
    Vertical: "Vertical",
    Horizontal: "Horizontal",
    "Swap sides": "Trocar lados",
    Subs: "Substituições",
    Substitutions: "Substituições",
    Court: "Quadra",
    Home: "Casa",
    Away: "Visitante",
    Games: "Sets",
    Enter: "Inserir",
    Undo: "Desfazer",
    "Match saved": "Partida salva",
    "Please sign in to save this match.": "Faça login para salvar esta partida.",
    "Nothing to save yet.": "Nada para salvar ainda.",
    "Could not save the match. Please try again.": "Não foi possível salvar a partida. Tente novamente.",
    Discard: "Descartar",
    "Resumed autosaved coding": "Codificação salva automaticamente retomada",
    "Changes discarded": "Alterações descartadas",
    "Discard all changes and revert to the starting point?":
      "Descartar todas as alterações e voltar ao ponto de partida?",
    Clear: "Limpar",
    "Prev set": "Set anterior",
    "Next set": "Próximo set",
    "Match Setup": "Configuração da partida",
    "First serve": "Primeiro saque",
    "Start Match": "Iniciar partida",
    "Resume Match": "Retomar partida",
    Skip: "Pular",
    "Select an event then drag to draw ball path": "Selecione um evento e arraste para desenhar a trajetória da bola",
    "Drag on court to set ball path": "Arraste na quadra para definir a trajetória da bola",
    "No events yet – enter a DV code above and press Enter.": "Nenhum evento ainda – insira um código DV acima e pressione Inserir.",
    "DataVolley code reference": "Referência de código DataVolley",
    Format: "Formato",
    Skills: "Fundamentos",
    Evaluation: "Avaliação",
    "Skill / type omission": "Omissão de fundamento / tipo",
    "Skill and/or type may be omitted; defaults fill in the rest.": "O fundamento e/ou o tipo podem ser omitidos; os valores padrão preenchem o restante.",
    "Dot-coding (S→R and A→B pairs)": "Codificação com ponto (pares S→R e A→B)",
    "Chaining & aliases": "Encadeamento e aliases",
    "team:": "time:",
    "omit → defaults to A": "omitir → padrão A",
    "NN: 1 or 2 digit jersey, e.g. 1, 01, 12": "NN: número de camisa de 1 ou 2 dígitos, ex. 1, 01, 12",
    "sub-zone: a b c d appended to zone digit, e.g. 5b or 5b1a": "subzona: a b c d anexado ao dígito da zona, ex. 5b ou 5b1a",
    "S Serve · R Reception · E Set · A Attack · B Block · D Dig · F Freeball": "S Saque · R Recepção · E Levantamento · A Ataque · B Bloqueio · D Defesa · F Bola livre",
    "# kill/ace · + positive · ! ok · - negative · / overpass · = error": "# ponto/ace · + positivo · ! ok · - negativo · / sobra · = erro",
    "E.g.": "Ex.",
    "serve with skill=A (default), type=H, eval=+, zone=5.": "saque com skill=A (padrão), type=H, eval=+, zone=5.",
    "type-only (no skill marker), defaults skill from context.": "apenas tipo (sem marcador de fundamento), fundamento padrão pelo contexto.",
    "serve coded, reception auto-linked.": "saque codificado, recepção vinculada automaticamente.",
    "attack coded, block auto-linked.": "ataque codificado, bloqueio vinculado automaticamente.",
    "Dot connects the reception eval to the corresponding serve eval via the serve→receive mapping.": "O ponto conecta a avaliação da recepção à avaliação de saque correspondente pelo mapeamento saque→recepção.",
    "two events in one string.": "dois eventos em uma única string.",
    "manual point A/B.": "ponto manual A/B.",
    "serve ace/error.": "ace/erro de saque.",
    "serve with eval.": "saque com avaliação.",
    // Stats, sideouts, filters & video tooltips
    "Block Kills": "Pontos de bloqueio",
    "Side Out Percentage": "Porcentagem de side out",
    "All First Ball": "Todas as primeiras bolas",
    "First Ball Kills": "Pontos de primeira bola",
    "Good Serve": "Bom saque",
    "Break Points": "Pontos de quebra",
    Game: "Set",
    Row: "Posição",
    "First Ball Sideouts": "Side outs de primeira bola",
    "All Sideouts": "Todos os side outs",
    All: "Todos",
    None: "Nenhum",
    Receiver: "Passador",
    "# Passes": "Nº recepções",
    "Perfect %": "% perfeito",
    "Positive %": "% positivo",
    "Sideout %": "% side out",
    "FB Sideout %": "% side out 1ª bola",
    "Pass Hits": "Ataques após recepção",
    "Pass Hit Kills": "Pontos após recepção",
    "Pass Hit Errors": "Erros após recepção",
    "Show Passes": "Mostrar recepções",
    "Show Attacks": "Mostrar ataques",
    "Error Pass": "Recepção com erro",
    "1 Pass": "Recepção 1",
    "2 Pass": "Recepção 2",
    "Perfect Pass": "Recepção perfeita",
    "Successful SO": "Side out bem-sucedido",
    "First Ball SO": "Side out 1ª bola",
    "Unsuccessful SO": "Side out malsucedido",
    RECEIVES: "RECEPÇÕES",
    Sets: "Sets",
    Rotations: "Rotações",
    "Attack Combos": "Combinações de ataque",
    Stages: "Fases",
    Display: "Exibição",
    Setters: "Levantadores",
    Attackers: "Atacantes",
    Results: "Resultados",
    Source: "Origem",
    Cones: "Zonas",
    "Select None": "Não selecionar nenhum",
    "All Serves": "Todos os saques",
    "All Returns": "Todas as recepções",
    Filters: "Filtros",
    "Reset all filters": "Redefinir todos os filtros",
    "Show video clips of filtered attacks": "Mostrar clipes de vídeo dos ataques filtrados",
    "Spikes for the selected player": "Ataques do jogador selecionado",
    "Click on the zones to view video of attacks": "Clique nas zonas para ver os vídeos dos ataques",
    "Expand All": "Expandir tudo",
    "Collapse All": "Recolher tudo",
    "Import Radar File": "Importar arquivo Radar",
    "Select local video file": "Selecionar arquivo de vídeo local",
    "Save to database": "Salvar no banco de dados",
    "Load and play online video": "Carregar e reproduzir vídeo online",
    "Synchronise video with events": "Sincronizar vídeo com os eventos",
    "Rewind 10 seconds": "Voltar 10 segundos",
    "Rewind 1 second": "Voltar 1 segundo",
    "Forward 1 second": "Avançar 1 segundo",
    "Forward 10 seconds": "Avançar 10 segundos",
    "Set timing for events": "Definir o tempo dos eventos",

    // Navigation
    Matches: "Partidas",
    Live: "Ao vivo",
    Import: "Importar",
    Playlists: "Listas de reprodução",
    "New Match": "Nova partida",
    // New Match / Match Coding gate
    "Match Coding": "Codificação da partida",
    "Match Coding — Coming Soon": "Codificação da partida — Em breve",
    "Live match coding is on the way. Here's a preview of what's coming.":
      "A codificação de partidas ao vivo está chegando. Veja uma prévia do que está por vir.",
    Settings: "Configurações",
    Language: "Idioma",
    "Your profile": "Seu perfil",
    "Sign out": "Sair",
    "Translation Editor": "Editor de tradução",

    // Common actions
    Save: "Salvar",
    Cancel: "Cancelar",
    Delete: "Excluir",
    Share: "Compartilhar",
    Edit: "Editar",
    New: "Novo",
    Refresh: "Atualizar",
    Search: "Pesquisar",
    Reset: "Redefinir",
    Close: "Fechar",
    "Sub for": "Substituir",
    "No players available": "Nenhum jogador disponível",
    "Show full roster": "Mostrar elenco completo",
    "Available only": "Apenas disponíveis",
    "Maximum substitutions reached": "Máximo de substituições atingido",
    Back: "Voltar",
    "Download PDF": "Baixar PDF",
    "Loading...": "Carregando…",
    "Select All": "Selecionar tudo",
    "Unselect All": "Desmarcar tudo",
    Yes: "Sim",
    No: "Não",
    Add: "Adicionar",
    Load: "Carregar",
    Confirm: "Confirmar",
    Open: "Abrir",
    show: "mostrar",

    // Auth
    "Sign In": "Entrar",
    "Sign Up": "Cadastrar",
    "Sign Out": "Sair",
    Email: "E-mail",
    Password: "Senha",
    "Confirm Password": "Confirmar senha",
    "Forgot password?": "Esqueceu a senha?",
    "Forgot Password": "Esqueci a senha",
    "Don't have an account?": "Não tem uma conta?",
    "Sign up for free!": "Cadastre-se gratuitamente!",
    "All ready signed up?": "Já se cadastrou?",
    "Error Signing In": "Erro ao entrar",
    "Signed up successfully": "Cadastro realizado com sucesso",
    "Error signing up": "Erro ao cadastrar",
    "Enter your email": "Digite seu e-mail",
    "Send Reset Email": "Enviar e-mail de redefinição",
    "Email was sent to": "O e-mail foi enviado para",
    "Could not send reset email":
      "Não foi possível enviar o e-mail de redefinição",

    // Matches list
    "Team 1": "Equipe 1",
    "Team 2": "Equipe 2",
    Tournament: "Torneio",
    Scores: "Placares",
    Date: "Data",
    Actions: "Ações",
    Owner: "Proprietário",
    Description: "Descrição",
    Comments: "Comentários",
    Tags: "Tags",
    "Add player": "Adicionar jogador",
    "Add team": "Adicionar equipe",
    "Edit playlist": "Editar lista de reprodução",
    "Merge Teams": "Mesclar equipes",
    "Roster differences detected between teams:": "Diferenças de elenco detectadas entre equipes:",
    "Review the roster in the next step before merging.": "Revise o elenco na próxima etapa antes de mesclar.",
    "At least 2 team records must exist in your Teams list for the selected matches.": "Pelo menos 2 registros de equipe devem existir na sua lista para as partidas selecionadas.",
    "Merge...": "Mesclar...",
    "Select 2 or more team records to merge into one.": "Selecione 2 ou mais registros de equipe para mesclar em um.",
    "Filter teams...": "Filtrar equipes...",
    "teams selected": "equipes selecionadas",
    "Select Teams": "Selecionar equipes",
    "Reconcile Roster": "Reconciliar elenco",
    "Confirm Merge": "Confirmar mesclagem",
    "Merged team name": "Nome da equipe mesclada",
    "shirt number(s) have conflicting names — review below.": "número(s) têm nomes conflitantes — revise abaixo.",
    "Shirt": "Camisa",
    "players included": "jogadores incluídos",
    "No players to reconcile.": "Nenhum jogador para reconciliar.",
    "Use this name": "Usar este nome",
    "Create merged team": "Criar equipe mesclada",
    "with": "com",
    "duplicate team records": "registros de equipe duplicados",
    "Consolidate duplicate player records and reassign playlist links": "Consolidar registros de jogadores duplicados e reatribuir links de playlist",
    "This cannot be undone.": "Esta ação não pode ser desfeita.",
    "Teams being merged:": "Equipes sendo mescladas:",
    "Step": "Passo",
    "of": "de",
    "Merging...": "Mesclando...",
    "Players & Teams": "Jogadores & Equipes",
    "No players or teams assigned.": "Nenhum jogador ou equipe associado.",
    "Search players...": "Buscar jogadores...",
    "Search teams...": "Buscar equipes...",
    "Saving...": "Salvando...",
    "My Matches": "Minhas partidas",
    "Shared Matches": "Partidas compartilhadas",
    "Search team...": "Pesquisar equipe…",
    "All Teams": "Todas as equipes",
    "Multi Match Reports": "Relatórios de várias partidas",
    "Delete match": "Excluir partida",
    "Remove from my shared matches": "Remover das minhas partidas compartilhadas",
    "Share match": "Compartilhar partida",
    "Edit match": "Editar partida",
    "Open video analysis": "Abrir análise de vídeo",
    "Delete Match": "Excluir partida",
    "Are you sure you want to delete the selected match?":
      "Tem certeza de que deseja excluir a partida selecionada?",
    "No matches selected.": "Nenhuma partida selecionada.",

    // New Match
    "← Back": "← Voltar",
    "Team A (Home)": "Equipe A (casa)",
    "Team B (Away)": "Equipe B (fora)",
    "Select team…": "Selecionar equipe…",
    "Tournament (optional)": "Torneio (opcional)",
    "Tournament / competition name": "Nome do torneio / competição",
    "Match date": "Data da partida",
    "Start Coding →": "Iniciar codificação →",

    // Reports
    Summary: "Resumo",
    "Box Score": "Súmula de estatísticas",
    "Side-outs Report": "Relatório de side-outs",
    "Serve Receives": "Recepções",
    "Attack Zones": "Zonas de ataque",
    "Hitting Charts": "Gráficos de ataque",
    "Video Analysis": "Análise de vídeo",
    Dashboard: "Painel",
    Charts: "Gráficos",
    "AI Report": "Relatório de IA",
    // AI Reports page
    "AI Reports": "Relatórios de IA",
    "AI Reports — Coming Soon": "Relatórios de IA — Em breve",
    "AI-powered match reports and deep analysis are on the way. Here are some sample reports to show what's coming.":
      "Relatórios de partidas e análises aprofundadas com IA estão chegando. Veja alguns relatórios de exemplo como prévia.",
    "Preview not available.": "Pré-visualização indisponível.",
    "Open sample": "Abrir exemplo",
    "No matches were loaded. Select one or more matches and try again.":
      "Nenhuma partida foi carregada. Selecione uma ou mais partidas e tente novamente.",
    "Attack Analysis (English)": "Análise de ataque (inglês)",
    "Blocking Analysis (Vietnamese)": "Análise de bloqueio (vietnamita)",
    "Opponent Tendencies (Japanese)": "Tendências do adversário (japonês)",
    // Deep AI analysis
    "Deep AI Analysis": "Análise profunda com IA",
    "AI Deep Analysis": "Análise profunda de IA",
    "Load at least one match with coded events to generate the analysis.": "Carregue pelo menos uma partida com eventos codificados para gerar a análise.",
    "Run the deep analysis one section at a time to keep each request small. Edit or add questions below, then generate. Best used with the Anthropic (Claude) provider.":
      "Execute a análise profunda uma seção por vez para manter cada solicitação pequena. Edite ou adicione perguntas abaixo e depois gere. Melhor utilizada com o provedor Anthropic (Claude).",
    "Download CSV": "Baixar CSV",
    "Analysis section": "Seção de análise",
    analysing: "analisando",
    "All sections (uses the most credits)": "Todas as seções (usa mais créditos)",
    "All sections": "Todas as seções",
    Questions: "Perguntas",
    "one per line, edit or add your own": "uma por linha, edite ou adicione as suas",
    "Reset to defaults": "Restaurar padrões",
    "Add a question…": "Adicionar uma pergunta…",
    "Runs every section using the questions you've set for each. This sends one large request and uses the most credits.":
      "Executa todas as seções com as perguntas que você definiu para cada uma. Isso envia uma solicitação grande e usa mais créditos.",
    "Generate full analysis": "Gerar análise completa",
    "Generate section analysis": "Gerar análise da seção",
    Serving: "Saque",
    Reception: "Recepção",
    Setting: "Levantamento",
    Attack: "Ataque",
    Blocking: "Bloqueio",
    "Defence / Digging": "Defesa / Recuperação",
    "Rotation Analysis": "Análise de rotações",
    "Opponent Tendencies (Scouting)": "Tendências do adversário (scouting)",
    "Overall Team Metrics": "Métricas gerais da equipe",
    "Serve efficiency (aces, errors, and pressured receptions forced).": "Eficiência no saque (aces, erros e recepções pressionadas forçadas).",
    "Which servers are most effective, and against which reception zones?": "Quais sacadores são mais eficazes e contra quais zonas de recepção?",
    "Which serve types (jump, float, jump-float) yield the best results?": "Quais tipos de saque (viagem, flutuante, flutuante em suspensão) dão os melhores resultados?",
    "Reception quality breakdown (perfect, positive, negative, error).": "Detalhamento da qualidade de recepção (perfeita, positiva, negativa, erro).",
    "Which players struggle most under jump serves vs. float serves?": "Quais jogadores têm mais dificuldade com saques viagem em comparação com flutuantes?",
    "How does poor reception correlate with side-out efficiency?": "Como uma recepção ruim se relaciona com a eficiência de side-out?",
    "Set distribution by zone and by player.": "Distribuição do levantamento por zona e por jogador.",
    "How does the setter perform under pressure (bad-pass situations)?": "Como o levantador rende sob pressão (situações de recepção ruim)?",
    "Tempo breakdown (quick, medium, high ball) and which yields the best attack outcome.": "Detalhamento de tempos (rápido, médio, bola alta) e qual gera o melhor resultado de ataque.",
    "Kill percentage and error rate per player, per rotation, and per zone.": "Percentual de pontos e taxa de erro por jogador, por rotação e por zona.",
    "Which attack zones (pipe, cross, line) have the highest efficiency?": "Quais zonas de ataque (pipe, diagonal, paralela) têm a maior eficiência?",
    "How attack efficiency changes in transition vs. side-out.": "Como a eficiência de ataque muda em transição versus side-out.",
    "Block-per-set rate per player and rotation.": "Bloqueios por set por jogador e rotação.",
    "Which opposing attack zones are most successfully blocked?": "Quais zonas de ataque adversárias são bloqueadas com mais sucesso?",
    "How often blocks result in kills vs. touch-outs.": "Com que frequência os bloqueios resultam em pontos versus toques para fora.",
    "Dig success rate per player and per court zone.": "Taxa de sucesso na defesa por jogador e por zona da quadra.",
    "Which attack types (tip, roll, hard swing) are hardest to defend?": "Quais tipos de ataque (largada, embolação, ataque forte) são mais difíceis de defender?",
    "How defensive efficiency affects transition attack success.": "Como a eficiência defensiva afeta o sucesso do ataque em transição.",
    "Which rotations have the highest and lowest point differential?": "Quais rotações têm o maior e o menor saldo de pontos?",
    "Side-out and break-point percentages per rotation.": "Percentuais de side-out e break-point por rotação.",
    "Rotation-specific vulnerabilities (e.g. a weak serving or passing rotation).": "Vulnerabilidades específicas de rotação (por exemplo, uma rotação fraca no saque ou na recepção).",
    "The opponent's most frequently used attack zones and combinations.": "As zonas de ataque e combinações mais usadas pelo adversário.",
    "The opponent's go-to attackers in high-pressure situations.": "Os atacantes preferidos do adversário em situações de alta pressão.",
    "The serve-receive patterns the opponent favours.": "Os padrões de recepção que o adversário prefere.",
    "Overall rally-win probability in different score situations.": "Probabilidade geral de vencer o rally em diferentes situações de placar.",
    "How performance changes in sets 1–3 vs. sets 4–5 (fatigue/pressure effects).": "Como o desempenho muda nos sets 1–3 versus sets 4–5 (efeitos de fadiga/pressão).",
    "The correlation between first-ball side-out percentage and match outcome.": "A correlação entre o percentual de side-out de primeira bola e o resultado da partida.",
    Report: "Relatório",

    // Import page
    "Load DVW file(s)...": "Carregar arquivo(s) DVW…",
    "Load PSVB file(s)...": "Carregar arquivo(s) PSVB…",
    "Load VSM file(s)...": "Carregar arquivo(s) VSM…",
    "Load playlist file...": "Carregar arquivo de lista de reprodução…",
    "load one or more DataVolley files":
      "carregar um ou mais arquivos DataVolley",
    "load one or more VBStats files": "carregar um ou mais arquivos VBStats",
    "load one or more VolleyStation VSM files": "carregar um ou mais arquivos VolleyStation VSM",
    "Convert DVW": "Converter DVW",
    "VSM": "VSM",
    "Convert DVW to VSM...": "Converter DVW para VSM…",
    "pick a DataVolley .dvw file to download as .vsm":
      "escolher um arquivo DataVolley .dvw para baixar como .vsm",
    "Convert VSM to DVW...": "Converter VSM para DVW…",
    "pick a VolleyStation .vsm file to download as .dvw":
      "escolher um arquivo VolleyStation .vsm para baixar como .dvw",
    "Converting…": "Convertendo…",
    "Converted": "Convertido",
    "Failed to convert": "Falha ao converter",
    "Beta testers only — coming soon for everyone else.":
      "Somente para testadores beta — em breve para todos.",
    "load a playlist": "carregar uma lista de reprodução",
    "Use Shift-JIS Encoder": "Usar codificador Shift-JIS",
    "Importing…": "Importando…",
    "Save to Database?": "Salvar no banco de dados?",
    "Always save imports automatically": "Sempre salvar importações automaticamente",
    "View Only": "Somente visualizar",
    "Save to Database": "Salvar no banco de dados",
    "Error saving to database": "Erro ao salvar no banco de dados",
    "Saved to database": "Salvo no banco de dados",
    "Saving to database…": "Salvando no banco de dados…",
    "Upload them to the database now?":
      "Enviar para o banco de dados agora?",

    // Settings / Options
    Theme: "Tema",
    Options: "Opções",
    "Rotation naming convention": "Convenção de nomenclatura de rotações",
    "Choose how rotations are labelled in reports and analysis.":
      "Escolha como as rotações são identificadas nos relatórios e análises.",
    "R1–R6 (rotation sequence)": "R1–R6 (sequência de rotação)",
    "S1–S6 (setter court position)":
      "S1–S6 (posição do levantador na quadra)",
    "R1: setter in position 1, R2: setter in 6, R3: setter in 5, R4: setter in 4, R5: setter in 3, R6: setter in 2.":
      "R1: levantador na posição 1, R2: levantador na 6, R3: levantador na 5, R4: levantador na 4, R5: levantador na 3, R6: levantador na 2.",
    "S1: setter in 1, S2: setter in 2, S3: setter in 3, S4: setter in 4, S5: setter in 5, S6: setter in 6.":
      "S1: levantador na 1, S2: levantador na 2, S3: levantador na 3, S4: levantador na 4, S5: levantador na 5, S6: levantador na 6.",
    "Labels:": "Rótulos:",
    "Current theme:": "Tema atual:",
    "Choose the language for the app interface.":
      "Escolha o idioma da interface do aplicativo.",
    "Create your own language package or edit a community one, with AI-assisted translation.":
      "Crie seu próprio pacote de idioma ou edite um da comunidade, com tradução assistida por IA.",

    // About / Footer
    "Copyright © Perana Sports {year}. All rights reserved.":
      "Copyright © Perana Sports {year}. Todos os direitos reservados.",
    "VBLive is free to use right now. However Perana Sports reserves the right to charge a subscription fee in due course.":
      "O VBLive é gratuito no momento. No entanto, a Perana Sports reserva-se o direito de cobrar uma assinatura no devido tempo.",
    "Querries / suggestions are most welcomed.":
      "Dúvidas / sugestões são muito bem-vindas.",

    // Live
    "Please enter server name to search":
      "Digite o nome do servidor para pesquisar",
    "Broadcast Server": "Servidor de transmissão",
    "Enter DVMateLive Server Name": "Digite o nome do servidor DVMateLive",
    LIVE: "AO VIVO",

    // Playlists list
    "Delete Playlist": "Excluir lista de reprodução",
    "Are you sure you want to delete the selected playlist?":
      "Tem certeza de que deseja excluir a lista de reprodução selecionada?",
    "My Playlists": "Minhas listas de reprodução",
    "Shared Playlists": "Listas de reprodução compartilhadas",
    "Tags:": "Tags:",
    "Select...": "Selecionar…",
    "All Tags": "Todas as tags",

    // Share
    "User already added": "Usuário já adicionado",
    "Please enter a valid email address":
      "Digite um endereço de e-mail válido",
    "Please select at least one user to share with":
      "Selecione pelo menos um usuário para compartilhar",
    "Not Shared": "Não compartilhado",
    "Available for everyone": "Disponível para todos",
    "Available to selected users":
      "Disponível para usuários selecionados",
    "Enter VBLive user email address":
      "Digite o endereço de e-mail do usuário VBLive",
    "Selected users:": "Usuários selecionados:",
    "Save List": "Salvar lista",
    "Clear List": "Limpar lista",
    "Clear Users List": "Limpar lista de usuários",
    "Do you really want to clear all users from list?":
      "Deseja mesmo remover todos os usuários da lista?",
    "Do you want to save changes before closing?":
      "Deseja salvar as alterações antes de fechar?",
    "Enter name to save list to:":
      "Digite um nome para salvar a lista:",
    "Enter name to save list to":
      "Digite um nome para salvar a lista",

    // AI panels
    "AI settings": "Configurações de IA",
    "Setup needed": "Configuração necessária",
    "Generate report": "Gerar relatório",
    "Generate AI report": "Gerar relatório de IA",
    "Generate general report": "Gerar relatório geral",
    "Translate to…": "Traduzir para…",
    "Follow-up questions": "Perguntas de acompanhamento",
    "Ask a follow-up question…": "Faça uma pergunta de acompanhamento…",
    "Ask a follow-up question": "Faça uma pergunta de acompanhamento",
    "AI provider": "Provedor de IA",
    Model: "Modelo",
    Hide: "Ocultar",
    Show: "Mostrar",
    "Your key is stored only in this browser and sent directly to the selected provider.":
      "Sua chave é armazenada apenas neste navegador e enviada diretamente ao provedor selecionado.",
    "Please enter your API key.": "Digite sua chave de API.",
    "Not enough data to analyse.":
      "Dados insuficientes para analisar.",
    "Select a player with recorded spikes first.":
      "Primeiro selecione um jogador com ataques registrados.",
    Player: "Jogador",
    "Select a player…": "Selecionar um jogador…",
    "No players with recorded spikes in the selected matches.":
      "Nenhum jogador com ataques registrados nas partidas selecionadas.",
    "Analyse with AI": "Analisar com IA",
    "AI scouting report": "Relatório de scouting de IA",
    "Q:": "P:",
    Ask: "Perguntar",
    "e.g. Which rotation should we target on serve?":
      "ex.: Qual rotação devemos atacar no saque?",
    "Saved questions:": "Perguntas salvas:",
    "Click to ask again": "Clique para perguntar novamente",
    "Remove saved question": "Remover pergunta salva",

    // AI language options
    Spanish: "Espanhol",
    French: "Francês",
    German: "Alemão",
    Italian: "Italiano",
    Portuguese: "Português",
    Dutch: "Holandês",
    Polish: "Polonês",
    Russian: "Russo",
    Turkish: "Turco",
    Arabic: "Árabe",
    "Chinese (Simplified)": "Chinês (simplificado)",
    Japanese: "Japonês",
    Korean: "Coreano",
    Vietnamese: "Vietnamita",
    Thai: "Tailandês",

    // Box score
    "Show Full Headers": "Mostrar cabeçalhos completos",
    "Show Short Headers": "Mostrar cabeçalhos curtos",
    "Show Less Players": "Mostrar menos jogadores",
    "Show All Players": "Mostrar todos os jogadores",
    "Attack Kills": "Pontos de ataque",
    "Attack Errors": "Erros de ataque",
    "Total Attempts": "Total de tentativas",
    "Hitting Efficiency": "Eficiência de ataque",
    "Serve Aces": "Aces de saque",
    "Serve Errors": "Erros de saque",
    "Pass Errors": "Erros de recepção",
    "% Perfect Passes": "% recepções perfeitas",
    "Passing Average": "Média de recepção",
    "Total Passes": "Total de recepções",
    "Set Assists": "Assistências de levantamento",
    Digs: "Defesas",
    "Momentum Chart": "Gráfico de momentum",
    "Key event": "Jogada chave",
    "Break Point %": "% ponto de quebra",
    "First Ball Sideout %": "% side out primeira bola",
    "Sideout Transition %": "% side out em transição",
    "FB Sideout Kills %": "% ataque vencedor primeira bola",
    "Sideout Opposition Error %": "% side out por erro adversário",
    Plus: "Mais",
    Minus: "Menos",
    Blocks: "Bloqueios de ponto",
    "Positive Serve %": "% saques positivos",
    Sideout: "Side out",
    "Break point": "Ponto de quebra",
    "Ace / Kill": "Ace / Ataque vencedor",
    "In play": "Em jogo",
    Error: "Erro",
    "Sideout on a kill": "Side out por ataque vencedor",
    "Sideout on an error": "Side out por erro adversário",
    "Top half": "Metade superior",
    "Bottom half": "Metade inferior",
    "The centre line steps toward the team that wins each point.": "A linha central se move em direção à equipe que vence cada ponto.",
    "No point-by-point data available for this match.": "Nenhum dado ponto a ponto disponível para esta partida.",
    "No match data available.": "Nenhum dado da partida disponível.",
    "This report isn't supported on this browser yet — please use Safari.": "Este relatório ainda não é compatível com este navegador — use o Safari.",
    "No video available for this match.": "Nenhum vídeo disponível para esta partida.",
    passes: "recepções",
    "eff.": "ef.",
    Grade: "Nota",
    LEFT: "ESQUERDA",
    CENTRE: "CENTRO",
    RIGHT: "DIREITA",
    SELECTED: "SELECIONADO",
    Passers: "Passadores",
    Calls: "Chamadas",
    "SIDE OUT": "SIDE OUT",
    TRANSITION: "TRANSIÇÃO",
    "No events in selection.": "Nenhum evento na seleção atual.",
    "Serve Receives By Players": "Recepções por jogador",
    "Serve Receive By Zones": "Recepção por zonas",
    "Dig Errors": "Erros de defesa",
    "Dig Average": "Média de defesa",
    "Block Solos": "Bloqueios individuais",
    "Block Assists": "Bloqueios assistidos",
    "Block Errors": "Erros de bloqueio",
    "Points Scored": "Pontos marcados",
    TEAM: "EQUIPE",

    // Sideout report
    "ALL ROWS": "TODAS AS LINHAS",
    "ROWS ": "LINHAS ",
    "ROW ": "LINHA ",
    "FB Kills": "Pontos de ataque FB",
    "Opp. Serve Errors": "Erros de saque adv.",
    "FB Opp. Errors": "Erros adv. FB",
    "Kills/Blocks": "Ataques/Bloqueios",
    "Opp. Errors": "Erros adv.",
    Unsuccessful: "Sem sucesso",

    // Attack zones
    "No events in selected zone.":
      "Nenhum evento na zona selecionada.",
    "Show all attacks. Or click on a zone to show attacks in that zone.":
      "Mostrar todos os ataques. Ou clique em uma zona para mostrar os ataques dessa zona.",
    "OPPONENTS (": "ADVERSÁRIOS (",

    // Video analysis filters
    "All Types": "Todos os tipos",
    Serve: "Saque",
    "Serve-Receive": "Recepção",
    Set: "Levantamento",
    "Setter's Call": "Chamada do levantador",
    "Disable this skill": "Desativar esta habilidade",
    "Enable this skill": "Ativar esta habilidade",
    "Teams & Players": "Equipes e jogadores",
    "Match State": "Estado da partida",
    "Custom Code": "Código personalizado",
    "Search... (comma-separated for multiple)": "Pesquisar… (separe por vírgulas para vários)",
    vs: "vs.",
    "Rewind 60 seconds": "Voltar 60 segundos",
    "Rewind 1 frame": "Voltar 1 quadro",
    "Forward 1 frame": "Avançar 1 quadro",
    "Forward 60 seconds": "Avançar 60 segundos",
    "Previous Clip": "Clipe anterior",
    Replay: "Repetir",
    "Next Clip": "Próximo clipe",
    Phases: "Fases",
    "Score Range": "Intervalo de pontuação",
    "Set Winner": "Vencedor do set",
    Type: "Tipo",
    Strength: "Força",
    "Setter Calls": "Chamadas do levantador",
    "Setter Call": "Chamada do levantador",
    Freeball: "Bola livre",
    "Close (0-2)": "Apertado (0-2)",
    "Moderate (3-5)": "Moderado (3-5)",
    "Lopsided (6+)": "Amplo (6+)",
    "Focus Team": "Equipe em foco",
    Oppositions: "Adversários",
    "Focus Team Won": "Equipe em foco venceu",
    "Oppositions Won": "Adversários venceram",
    "Advanced Filters": "Filtros avançados",
    Players: "Jogadores",
    Opponents: "Adversários",
    "Event Type": "Tipo de evento",
    "Event Result": "Resultado do evento",
    "Attack Type": "Tipo de ataque",
    "Hit Type": "Tipo de batida",
    "Block Type": "Tipo de bloqueio",
    "Attack Combo": "Combinação de ataque",
    Apply: "Aplicar",
    Rotation: "Rotação",
    Skill: "Fundamento",
    "Lead Time": "Tempo de antecedência",
    Duration: "Duração",
    Timing: "Cronometragem",
    "Go Live": "Ir para o ao vivo",
    "Playback has fallen behind the live broadcast — click to jump to now.": "A reprodução ficou atrás da transmissão ao vivo — clique para saltar para agora.",
    "Video timing": "Cronometragem de vídeo",
    "Default lead time (seconds before an event to start playback) and clip duration, per skill.": "Tempo de antecedência padrão (segundos antes de um evento para iniciar a reprodução) e duração do clipe, por fundamento.",
    Cloud: "Nuvem",
    Local: "Local",
    "Relay host": "Host do relé",
    "IP or hostname of the machine running the local relay.": "Endereço IP ou nome de host da máquina que executa o relé local.",
    Spike: "Ataque",
    Block: "Bloqueio",
    Defence: "Defesa",
    "All Results": "Todos os resultados",
    Hard: "Forte",
    Soft: "Suave",
    Tip: "Largada",
    "Not Specified": "Não especificado",
    "H - High": "H - Alta",
    "M- Medium": "M - Média",
    "Q - Quick": "Q - Rápida",
    "T - Tense": "T - Tensa",
    "U - Super": "U - Super",
    "F - Fast": "F - Veloz",
    "O - Other": "O - Outra",
    "B0 - No Block": "B0 - Sem bloqueio",
    "B1 - 1 Block": "B1 - 1 bloqueador",
    "B2 - 2 Blocks": "B2 - 2 bloqueadores",
    "B3 - 3 Blocks": "B3 - 3 bloqueadores",
    "BS - Seams": "BS - Brechas",

    // All filters panel
    ALL: "TODOS",
    NONE: "NENHUM",

    // Playlist editor
    "Remove Item": "Remover item",
    "Do you really want to remove the selected item?":
      "Deseja mesmo remover o item selecionado?",
    "Description is required": "A descrição é obrigatória",
    "Comments are required": "Os comentários são obrigatórios",
    "Tags are required": "As tags são obrigatórias",
    "Playlist saved successfully":
      "Lista de reprodução salva com sucesso",
    "Error saving playlist":
      "Erro ao salvar a lista de reprodução",

    // Language editor
    "My saved packages": "Meus pacotes salvos",
    "Loading shared languages…": "Carregando idiomas compartilhados…",
    "Community shared languages": "Idiomas compartilhados pela comunidade",
    by: "por",
    "based on": "baseado em",
    "or create new": "ou criar um novo",
    Use: "Usar",
    "New Language Package": "Novo pacote de idioma",
    "Language Name": "Nome do idioma",
    "Language Code": "Código do idioma",
    "Flag Emoji": "Emoji de bandeira",
    "Base language (reference)": "Idioma base (referência)",
    "If not English, both English and the reference language will be shown.":
      "Se não for inglês, serão exibidos o inglês e o idioma de referência.",
    "Start Editing": "Começar a editar",
    "Save Key": "Salvar chave",
    "AI key set": "Chave de IA definida",
    "Set API key for AI translation":
      "Definir chave de API para tradução com IA",
    "AI key ✓": "Chave de IA ✓",
    "Set AI key": "Definir chave de IA",
    "AI: Translate All": "IA: Traduzir tudo",
    "Translating…": "Traduzindo…",
    "AI translate section": "Traduzir seção com IA",
    "Clear All": "Limpar tudo",
    "Saving…": "Salvando…",
    English: "Inglês",
    new: "novo",
    "Enter translation…": "Digite a tradução…",
    "Share with other VBLive users?":
      "Compartilhar com outros usuários do VBLive?",
    "Would you like to share the": "Deseja compartilhar o",
    "language package with all VBLive users? Anyone can then copy and build upon it.":
      "pacote de idioma com todos os usuários do VBLive? Qualquer pessoa poderá copiá-lo e ampliá-lo.",
    "No, just save for me": "Não, salvar apenas para mim",
    "Yes, share it!": "Sim, compartilhar!",
    "Language package saved and shared with all VBLive users!":
      "Pacote de idioma salvo e compartilhado com todos os usuários do VBLive!",
    "Language package saved!": "Pacote de idioma salvo!",
    "Clear all translations? This cannot be undone.":
      "Limpar todas as traduções? Esta ação não pode ser desfeita.",

    // Additional UI phrases
    Teams: "Equipes",
    "Shared By": "Compartilhado por",
    "View Activity": "Atividade de visualização",
    Viewer: "Visualizador",
    "Date / Time": "Data / Hora",
    "No views recorded yet.": "Nenhuma visualização registrada ainda.",
    "Date / Scores": "Data / Placares",
    "Stats API source": "Origem da API de estatísticas",
    "load one or more AVC VIS files": "carregar um ou mais arquivos AVC VIS",
    "Points to close a set": "Pontos para fechar um set",
    "Require 2-point advantage": "Exigir vantagem de 2 pontos",
    Roster: "Elenco",
    Clone: "Clonar",
    "Edit team": "Editar equipe",
    "New player": "Novo jogador",
    "Search player...": "Buscar jogador…",
    "Match Centre": "Central da partida",
    "Points Won": "Pontos ganhos",
    "Opp. Error": "Erro adversário",
    Total: "Total",
    "Match Skills": "Habilidades da partida",
    Dig: "Defesa",
    "Best Scorers": "Melhores pontuadores",
    "Power Play": "Power Play",
    Match: "Partida",
    "Player scouting": "Scouting de jogadores",
    "Team deep analysis": "Análise aprofundada da equipe",
    "All Players": "Todos os jogadores",
    "All Sets": "Todos os sets",
    "All Rotations": "Todos os rodízios",
    "All Combos": "Todas as combinações",
    "All Calls": "Todas as chamadas",
    "All Events": "Todos os eventos",
    "Select all items": "Selecionar todos os itens",
    "Unselect all items": "Desmarcar todos os itens",
    "Create play list": "Criar lista de reprodução",
    "Point by Point": "Ponto a ponto",
    // i18n backfill pass — previously English-fallback-only keys
    "Access denied": "Acesso negado",
    "Aces": "Aces",
    "ACES": "ACES",
    "Add report": "Adicionar relatório",
    "Admin": "Administração",
    "Admins": "Administradores",
    "Advanced": "Avançado",
    "All set!": "Tudo pronto!",
    "Analyse team with AI": "Analisar equipe com IA",
    "Are you sure you want to delete the selected play list?": "Tem certeza de que deseja excluir a playlist selecionada?",
    "Assign": "Atribuir",
    "Assign playlist": "Atribuir playlist",
    "Assigned": "Atribuído",
    "At least one admin is required.": "É necessário pelo menos um administrador.",
    "Attack Combo / Setter Call": "Combinação de ataque / Chamada de levantador",
    "Attack diagram": "Diagrama de ataque",
    "Attacks": "Ataques",
    "ATTACKS": "ATAQUES",
    "Auto-detected a custom set-closing score for": "Detectada automaticamente uma pontuação de encerramento de set personalizada para",
    "BLOCK": "BLOQUEIO",
    "BLOCKS": "BLOQUEIOS",
    "Beta testers": "Testadores beta",
    "Clear filter": "Limpar filtro",
    "Close Set": "Encerrar set",
    "Close menu": "Fechar menu",
    "Code": "Código",
    "Coding defaults & shortcuts": "Padrões e atalhos de anotação",
    "Continue": "Continuar",
    "Could not load match data. The match may no longer be available.": "Não foi possível carregar os dados da partida. A partida pode não estar mais disponível.",
    "Could not load stats for these matches.": "Não foi possível carregar as estatísticas dessas partidas.",
    "Could not load this match.": "Não foi possível carregar esta partida.",
    "Could not load this playlist.": "Não foi possível carregar esta playlist.",
    "Could not load this report.": "Não foi possível carregar este relatório.",
    "Could not save the report.": "Não foi possível salvar o relatório.",
    "Could not share the report. Please try again.": "Não foi possível compartilhar o relatório. Tente novamente.",
    "Court horizontal (net vertical)": "Quadra horizontal (rede vertical)",
    "Court vertical (net horizontal)": "Quadra vertical (rede horizontal)",
    "Create": "Criar",
    "Create a new team from this roster": "Criar uma nova equipe a partir deste elenco",
    "DIFFERENCE": "DIFERENÇA",
    "DV Code": "Código DV",
    "Delete this report?": "Excluir este relatório?",
    "Detected a custom set-closing score of": "Detectada uma pontuação de encerramento de set personalizada de",
    "Double Sub": "Dupla substituição",
    "Double Sub: now pick a bench player for the flashing OPPOSITE slot…": "Dupla substituição: escolha agora um jogador do banco para a posição do oposto que está piscando…",
    "Double Sub: pick a bench player for the flashing SETTER slot…": "Dupla substituição: escolha um jogador do banco para a posição do levantador que está piscando…",
    "Double Substitution — swap the Setter and Opposite for two incoming players": "Dupla substituição — troque o levantador e o oposto por dois jogadores que entram",
    "Drag to draw the ball path; pause briefly to mark a touch (reception/dig/block), release to finish.": "Arraste para desenhar a trajetória da bola; pause brevemente para marcar um toque (recepção/defesa/bloqueio), solte para finalizar.",
    "Draw": "Desenhar",
    "Draw & Code": "Desenhar e codificar",
    "Draw & Code — draw ball paths on the court to code the rally": "Desenhar e codificar — desenhe as trajetórias da bola na quadra para codificar a jogada",
    "Edit Lineup": "Editar escalação",
    "Edit Lineup — freely rearrange the starting lineup before the set begins": "Editar escalação — reorganize livremente a escalação inicial antes do início do set",
    "Emails in this list can manage beta testers and other admins.": "Os e-mails desta lista podem gerenciar testadores beta e outros administradores.",
    "Emails in this list get early access to AI Reports and other gated features. Changes take effect immediately.": "Os e-mails desta lista têm acesso antecipado aos relatórios de IA e a outros recursos restritos. As alterações têm efeito imediato.",
    "Enter a valid email address.": "Insira um endereço de e-mail válido.",
    "Exit fullscreen": "Sair da tela cheia",
    "Extended": "Estendido",
    "Failed to generate PDF.": "Falha ao gerar o PDF.",
    "Failed to save changes.": "Falha ao salvar as alterações.",
    "Filtered by": "Filtrado por",
    "First name": "Nome",
    "For stronger security later, consider moving admin checks to a Firebase Auth custom claim instead of this email list.": "Para maior segurança no futuro, considere mover as verificações de administrador para uma claim personalizada do Firebase Auth em vez desta lista de e-mails.",
    "Fullscreen": "Tela cheia",
    "Generate an AI report from these matches": "Gerar um relatório de IA a partir destas partidas",
    "How often (in seconds) the events list is updated during a live session.": "Com que frequência (em segundos) a lista de eventos é atualizada durante uma sessão ao vivo.",
    "Last name": "Sobrenome",
    "Leave blank to use standard rules (25 points, 15 for a 5th/deciding set)": "Deixe em branco para usar as regras padrão (25 pontos, 15 no 5º set decisivo)",
    "Live session refresh interval": "Intervalo de atualização da sessão ao vivo",
    "Load VIS file(s)...": "Carregar arquivo(s) VIS…",
    "Load this team's last-saved starting lineup (positions, setter and liberos)": "Carregar a última escalação inicial salva desta equipe (posições, levantador e líberos)",
    "Loading…": "Carregando…",
    "Manually end the current set at its current score, regardless of the configured winning score / 2-point advantage rule": "Encerrar manualmente o set atual com a pontuação atual, independentemente da pontuação vencedora configurada / regra de vantagem de 2 pontos",
    "Migration finished with some issues": "Migração concluída com alguns problemas",
    "Most of your data was migrated, but some items could not be moved. You can retry now or continue and try again later.": "A maior parte dos seus dados foi migrada, mas alguns itens não puderam ser movidos. Você pode tentar novamente agora ou continuar e tentar novamente mais tarde.",
    "Mute": "Mudo",
    "New team": "Nova equipe",
    "Next": "Próximo",
    "Nickname": "Apelido",
    "No Power Play": "Sem Power Play",
    "No admins yet.": "Ainda não há administradores.",
    "No beta testers yet.": "Ainda não há testadores beta.",
    "No coded events to analyse.": "Nenhum evento codificado para analisar.",
    "No coded events to export.": "Nenhum evento codificado para exportar.",
    "No matches.": "Nenhuma partida.",
    "No players added yet.": "Ainda não há jogadores adicionados.",
    "No players recorded.": "Nenhum jogador registrado.",
    "No players yet.": "Ainda não há jogadores.",
    "No playlists assigned.": "Nenhuma playlist atribuída.",
    "No playlists found.": "Nenhuma playlist encontrada.",
    "No reports saved.": "Nenhum relatório salvo.",
    "No roster players available.": "Nenhum jogador do elenco disponível.",
    "No substitutions": "Sem substituições",
    "No teams found.": "Nenhuma equipe encontrada.",
    "No teams yet.": "Ainda não há equipes.",
    "No teams.": "Nenhuma equipe.",
    "Nominate Setter": "Nomear levantador",
    "Notify these users by email (optional)": "Notificar esses usuários por e-mail (opcional)",
    "OPP REC": "REC. OPOS.",
    "OPP. ERRORS": "ERROS ADV.",
    "Open / Download": "Abrir / Baixar",
    "Open local file": "Abrir arquivo local",
    "Open menu": "Abrir menu",
    "Opposition Passing — use the opposition's reception formation on the diagram": "Recepção do adversário — usar a formação de recepção do adversário no diagrama",
    "Paste the report (Markdown supported)…": "Cole o relatório (Markdown é suportado)…",
    "Pause": "Pausar",
    "Play": "Reproduzir",
    "Playing squad": "Elenco do jogo",
    "Playing squad is full. Remove a player from the squad before adding another.": "O elenco do jogo está completo. Remova um jogador do elenco antes de adicionar outro.",
    "Please sign in to save reports.": "Faça login para salvar relatórios.",
    "Power Play Only": "Somente Power Play",
    "Record a team error (no player) — edit later to add the player and event type": "Registrar um erro de equipe (sem jogador) — edite depois para adicionar o jogador e o tipo de evento",
    "Record at least one event before making substitutions": "Registre pelo menos um evento antes de fazer substituições",
    "Redo": "Refazer",
    "Remove": "Remover",
    "Remove share": "Remover compartilhamento",
    "Remove yourself as admin? You will immediately lose access to this page.": "Remover a si mesmo como administrador? Você perderá imediatamente o acesso a esta página.",
    "Report saved and shared.": "Relatório salvo e compartilhado.",
    "Report saved to the player's profile.": "Relatório salvo no perfil do jogador.",
    "Report title": "Título do relatório",
    "Reports": "Relatórios",
    "Result": "Resultado",
    "Retry": "Tentar novamente",
    "Return to live coding": "Voltar para a anotação ao vivo",
    "Rotation is not permitted while making substitutions": "A rotação não é permitida ao fazer substituições",
    "SERVE": "SAQUE",
    "SETS": "SETS",
    "Save & Share": "Salvar e compartilhar",
    "Save this report so you can share it with other users": "Salve este relatório para compartilhá-lo com outros usuários",
    "Save this report to the player's profile": "Salvar este relatório no perfil do jogador",
    "Save to player": "Salvar no jogador",
    "Saved lineup": "Escalação salva",
    "Scroll, drag or swipe to scrub": "Role, arraste ou deslize para avançar/retroceder",
    "Search existing players...": "Pesquisar jogadores existentes…",
    "Search playlists…": "Pesquisar playlists…",
    "Select a match video to view while entering lineups.": "Selecione um vídeo da partida para assistir enquanto insere as escalações.",
    "Select a player.": "Selecione um jogador.",
    "Select a team.": "Selecione uma equipe.",
    "Setting up your account": "Configurando sua conta",
    "Shaded rows are played under a Power Play — HP/AP marks the calling team, bold gold scores are bonus (double) points.": "Linhas sombreadas foram jogadas sob um Power Play — HP/AP indica a equipe que o ativou, pontuações em negrito dourado são pontos de bônus (dobrados).",
    "Shared by": "Compartilhado por",
    "Shared with me": "Compartilhado comigo",
    "Show help": "Mostrar ajuda",
    "Show public playlists": "Mostrar playlists públicas",
    "Standard": "Padrão",
    "Substitutions made — tap to view": "Substituições realizadas — toque para ver",
    "Tap the blocking player.": "Toque no jogador que está bloqueando.",
    "Team colour": "Cor da equipe",
    "Team name": "Nome da equipe",
    "That email is already in the list.": "Esse e-mail já está na lista.",
    "The report is too large to save. Try a single match or fewer sets.": "O relatório é muito grande para salvar. Tente uma única partida ou menos sets.",
    "This match has no Power Play calls.": "Esta partida não tem chamadas de Power Play.",
    "This player has no name to save the report against.": "Este jogador não tem um nome para salvar o relatório.",
    "This team has no designated setter. Pick the player who is setting.": "Esta equipe não tem um levantador designado. Escolha o jogador que está levantando.",
    "Toggle video player": "Alternar o player de vídeo",
    "Unmute": "Reativar som",
    "Untitled playlist": "Playlist sem título",
    "Video URL…": "URL do vídeo…",
    "We're moving your data to a faster, more reliable home. This happens only once.": "Estamos movendo seus dados para um local mais rápido e confiável. Isso acontece apenas uma vez.",
    "You don't have permission to view this page.": "Você não tem permissão para ver esta página.",
    "Your data has been migrated successfully.": "Seus dados foram migrados com sucesso.",
    "Your teams and players will appear here after your data is migrated.": "Suas equipes e jogadores aparecerão aqui depois que seus dados forem migrados.",
    "bonus point": "ponto de bônus",
    "bonus points": "pontos de bônus",
    "event": "evento",
    "events": "eventos",
    "file(s).": "arquivo(s).",
    "is not on this team's roster.": "não está no elenco desta equipe.",
    "loaded": "carregado",
    "matches": "partidas",
    "more": "mais",
    "players": "jogadores",
    "points (no value was entered) — applied automatically.": "pontos (nenhum valor foi inserido) — aplicado automaticamente.",
    "roster": "elenco",
    "seconds": "segundos",
    "selected": "selecionado(s)",
    "session(s)?": "sessão(ões)?",
    "showing": "mostrando",
    "tournaments": "torneios",
    "…and": "…e",
    "● marks which team served the point.": "● indica qual equipe sacou o ponto.",
    // i18n backfill pass (Catapult + misc)
    "Centimetres (cm)": "Centímetros (cm)",
    "Inches (in)": "Polegadas (in)",
    "Jump height units": "Unidades de altura de salto",
    "Choose how Catapult jump heights are displayed and filtered.": "Escolha como as alturas de salto do Catapult são exibidas e filtradas.",
    "Select Catapult Focus file...": "Selecionar arquivo Catapult Focus…",
    "Select a Catapult Focus CSV export to view jump data for this match.": "Selecione uma exportação CSV do Catapult Focus para ver os dados de salto desta partida.",
    "Loaded from saved match data": "Carregado a partir dos dados salvos da partida",
    "No jump data found in this file.": "Nenhum dado de salto encontrado neste arquivo.",
    "Could not read this file. Please check it is a Catapult Focus CSV export.": "Não foi possível ler este arquivo. Verifique se é uma exportação CSV do Catapult Focus.",
    "Could not save jump data with this match.": "Não foi possível salvar os dados de salto com esta partida.",
    "Jump data saved with this match.": "Dados de salto salvos com esta partida.",
    "Show players with no match stats": "Mostrar jogadores sem estatísticas da partida",
    "hidden": "ocultos",
    "jumps": "saltos",
    "periods": "períodos",
    "Sync offset": "Deslocamento de sincronização",
    "matched": "correspondências",
    "match events now show jump height/duration in Video Analysis": "eventos da partida agora mostram altura/duração do salto na Análise de Vídeo",
    "Team": "Equipe",
    "Team A": "Equipe A",
    "Team B": "Equipe B",
    "Which team is this Catapult file for?": "Para qual equipe é este arquivo Catapult?",
    "We couldn't confidently match the players in this Focus file to one team. Please select which team it belongs to.": "Não conseguimos identificar com confiança a qual equipe pertencem os jogadores deste arquivo Focus. Selecione a qual equipe ele pertence.",
    "This match has no video attached yet.": "Esta partida ainda não possui um vídeo anexado.",
    "No synced jump events found for this player.": "Nenhum evento de salto sincronizado encontrado para este jogador.",
    "No match loaded.": "Nenhuma partida carregada.",
    "avg": "méd.",
    "max": "máx.",
    "Sync": "Sincronização",
    "Jump Report": "Relatório de salto",
    "Watch Video": "Assistir vídeo",
    "Save PDF": "Salvar PDF",
    "Generated by VBLive": "Gerado por VBLive",
    "Deep analysis of {team} from ball-by-ball event data, covering serving, reception, setting, attack, blocking, defence, rotations and opponent tendencies. Best used with the Anthropic (Claude) provider.": "Análise aprofundada de {team} a partir dos dados evento a evento, abrangendo saque, recepção, levantamento, ataque, bloqueio, defesa, rotações e tendências do adversário. Melhor usado com o provedor Anthropic (Claude).",
    "teams": "equipes",
  },

  pl: {
    // Information page
    Information: "Informacje",
    "Release notes": "Informacje o wersji",
    "Contact us": "Kontakt",
    "Questions, feedback or suggestions are most welcome.": "Pytania, opinie lub sugestie są mile widziane.",
    // DV match coding
    Lineups: "Składy",
    Point: "Punkt",
    Video: "Wideo",
    Defaults: "Wartości domyślne",
    "[team][NN][skill][type?][eval][zoneFrom?[szFrom?][zoneTo?[szTo?]]]": "[zespół][NN][element][typ?][ocena][strefaOd?[psOd?][strefaDo?[psDo?]]]",
    Vertical: "Pionowo",
    Horizontal: "Poziomo",
    "Swap sides": "Zamień strony",
    Subs: "Zmiany",
    Substitutions: "Zmiany",
    Court: "Boisko",
    Home: "Gospodarze",
    Away: "Goście",
    Games: "Sety",
    Enter: "Wprowadź",
    Undo: "Cofnij",
    "Match saved": "Mecz zapisany",
    "Please sign in to save this match.": "Zaloguj się, aby zapisać ten mecz.",
    "Nothing to save yet.": "Nie ma jeszcze nic do zapisania.",
    "Could not save the match. Please try again.": "Nie udało się zapisać meczu. Spróbuj ponownie.",
    Discard: "Odrzuć",
    "Resumed autosaved coding": "Wznowiono automatycznie zapisane kodowanie",
    "Changes discarded": "Zmiany odrzucone",
    "Discard all changes and revert to the starting point?":
      "Odrzucić wszystkie zmiany i wrócić do punktu początkowego?",
    Clear: "Wyczyść",
    "Prev set": "Poprzedni set",
    "Next set": "Następny set",
    "Match Setup": "Ustawienia meczu",
    "First serve": "Pierwszy serwis",
    "Start Match": "Rozpocznij mecz",
    "Resume Match": "Wznów mecz",
    Skip: "Pomiń",
    "Select an event then drag to draw ball path": "Wybierz zdarzenie, a następnie przeciągnij, aby narysować tor piłki",
    "Drag on court to set ball path": "Przeciągnij na boisku, aby ustawić tor piłki",
    "No events yet – enter a DV code above and press Enter.": "Brak zdarzeń – wpisz kod DV powyżej i naciśnij Wprowadź.",
    "DataVolley code reference": "Dokumentacja kodu DataVolley",
    Format: "Format",
    Skills: "Elementy",
    Evaluation: "Ocena",
    "Skill / type omission": "Pominięcie elementu / typu",
    "Skill and/or type may be omitted; defaults fill in the rest.": "Element i/lub typ można pominąć; wartości domyślne uzupełniają resztę.",
    "Dot-coding (S→R and A→B pairs)": "Kodowanie z kropką (pary S→R i A→B)",
    "Chaining & aliases": "Łączenie i aliasy",
    "team:": "drużyna:",
    "omit → defaults to A": "pomiń → domyślnie A",
    "NN: 1 or 2 digit jersey, e.g. 1, 01, 12": "NN: 1- lub 2-cyfrowy numer koszulki, np. 1, 01, 12",
    "sub-zone: a b c d appended to zone digit, e.g. 5b or 5b1a": "podstrefa: a b c d dodane do cyfry strefy, np. 5b lub 5b1a",
    "S Serve · R Reception · E Set · A Attack · B Block · D Dig · F Freeball": "S Zagrywka · R Przyjęcie · E Wystawienie · A Atak · B Blok · D Obrona · F Piłka wolna",
    "# kill/ace · + positive · ! ok · - negative · / overpass · = error": "# punkt/as · + pozytywne · ! ok · - negatywne · / przebicie · = błąd",
    "E.g.": "Np.",
    "serve with skill=A (default), type=H, eval=+, zone=5.": "zagrywka z skill=A (domyślnie), type=H, eval=+, zone=5.",
    "type-only (no skill marker), defaults skill from context.": "tylko typ (bez znacznika elementu), element domyślny z kontekstu.",
    "serve coded, reception auto-linked.": "zagrywka zakodowana, przyjęcie połączone automatycznie.",
    "attack coded, block auto-linked.": "atak zakodowany, blok połączony automatycznie.",
    "Dot connects the reception eval to the corresponding serve eval via the serve→receive mapping.": "Kropka łączy ocenę przyjęcia z odpowiadającą oceną zagrywki poprzez mapowanie zagrywka→przyjęcie.",
    "two events in one string.": "dwa zdarzenia w jednym ciągu.",
    "manual point A/B.": "ręczny punkt A/B.",
    "serve ace/error.": "as/błąd zagrywki.",
    "serve with eval.": "zagrywka z oceną.",
    // Stats, sideouts, filters & video tooltips
    "Block Kills": "Punkty blokiem",
    "Side Out Percentage": "Procent side-out",
    "All First Ball": "Wszystkie pierwsze piłki",
    "First Ball Kills": "Punkty z pierwszej piłki",
    "Good Serve": "Dobra zagrywka",
    "Break Points": "Punkty przełamania",
    Game: "Set",
    Row: "Pozycja",
    "First Ball Sideouts": "Side-outy z pierwszej piłki",
    "All Sideouts": "Wszystkie side-outy",
    All: "Wszystkie",
    None: "Brak",
    Receiver: "Przyjmujący",
    "# Passes": "Liczba przyjęć",
    "Perfect %": "% perfekcyjne",
    "Positive %": "% pozytywne",
    "Sideout %": "% side-out",
    "FB Sideout %": "% side-out 1. piłka",
    "Pass Hits": "Ataki po przyjęciu",
    "Pass Hit Kills": "Punkty po przyjęciu",
    "Pass Hit Errors": "Błędy po przyjęciu",
    "Show Passes": "Pokaż przyjęcia",
    "Show Attacks": "Pokaż ataki",
    "Error Pass": "Błędne przyjęcie",
    "1 Pass": "Przyjęcie 1",
    "2 Pass": "Przyjęcie 2",
    "Perfect Pass": "Perfekcyjne przyjęcie",
    "Successful SO": "Udany side-out",
    "First Ball SO": "Side-out 1. piłka",
    "Unsuccessful SO": "Nieudany side-out",
    RECEIVES: "PRZYJĘCIA",
    Sets: "Sety",
    Rotations: "Rotacje",
    "Attack Combos": "Kombinacje ataku",
    Stages: "Etapy",
    Display: "Wyświetlanie",
    Setters: "Rozgrywający",
    Attackers: "Atakujący",
    Results: "Wyniki",
    Source: "Źródło",
    Cones: "Strefy",
    "Select None": "Odznacz wszystko",
    "All Serves": "Wszystkie zagrywki",
    "All Returns": "Wszystkie przyjęcia",
    Filters: "Filtry",
    "Reset all filters": "Zresetuj wszystkie filtry",
    "Show video clips of filtered attacks": "Pokaż klipy wideo filtrowanych ataków",
    "Spikes for the selected player": "Ataki wybranego zawodnika",
    "Click on the zones to view video of attacks": "Kliknij strefy, aby zobaczyć wideo ataków",
    "Expand All": "Rozwiń wszystko",
    "Collapse All": "Zwiń wszystko",
    "Import Radar File": "Importuj plik Radar",
    "Select local video file": "Wybierz lokalny plik wideo",
    "Save to database": "Zapisz w bazie danych",
    "Load and play online video": "Wczytaj i odtwórz wideo online",
    "Synchronise video with events": "Zsynchronizuj wideo z wydarzeniami",
    "Rewind 10 seconds": "Cofnij o 10 sekund",
    "Rewind 1 second": "Cofnij o 1 sekundę",
    "Forward 1 second": "Do przodu o 1 sekundę",
    "Forward 10 seconds": "Do przodu o 10 sekund",
    "Set timing for events": "Ustaw czas dla wydarzeń",

    // Navigation
    Matches: "Mecze",
    Live: "Na żywo",
    Import: "Importuj",
    Playlists: "Playlisty",
    "New Match": "Nowy mecz",
    // New Match / Match Coding gate
    "Match Coding": "Kodowanie meczu",
    "Match Coding — Coming Soon": "Kodowanie meczu — Już wkrótce",
    "Live match coding is on the way. Here's a preview of what's coming.":
      "Kodowanie meczu na żywo jest w drodze. Oto zapowiedź tego, co nadchodzi.",
    Settings: "Ustawienia",
    Language: "Język",
    "Your profile": "Twój profil",
    "Sign out": "Wyloguj się",
    "Translation Editor": "Edytor tłumaczeń",

    // Common actions
    Save: "Zapisz",
    Cancel: "Anuluj",
    Delete: "Usuń",
    Share: "Udostępnij",
    Edit: "Edytuj",
    New: "Nowy",
    Refresh: "Odśwież",
    Search: "Szukaj",
    Reset: "Resetuj",
    Close: "Zamknij",
    "Sub for": "Zmiana za",
    "No players available": "Brak dostępnych zawodników",
    "Show full roster": "Pokaż pełny skład",
    "Available only": "Tylko dostępni",
    "Maximum substitutions reached": "Osiągnięto maksymalną liczbę zmian",
    Back: "Wstecz",
    "Download PDF": "Pobierz PDF",
    "Loading...": "Ładowanie…",
    "Select All": "Zaznacz wszystko",
    "Unselect All": "Odznacz wszystko",
    Yes: "Tak",
    No: "Nie",
    Add: "Dodaj",
    Load: "Wczytaj",
    Confirm: "Potwierdź",
    Open: "Otwórz",
    show: "pokaż",

    // Auth
    "Sign In": "Zaloguj się",
    "Sign Up": "Zarejestruj się",
    "Sign Out": "Wyloguj się",
    Email: "E-mail",
    Password: "Hasło",
    "Confirm Password": "Potwierdź hasło",
    "Forgot password?": "Nie pamiętasz hasła?",
    "Forgot Password": "Nie pamiętam hasła",
    "Don't have an account?": "Nie masz konta?",
    "Sign up for free!": "Zarejestruj się za darmo!",
    "All ready signed up?": "Masz już konto?",
    "Error Signing In": "Błąd logowania",
    "Signed up successfully": "Rejestracja zakończona pomyślnie",
    "Error signing up": "Błąd podczas rejestracji",
    "Enter your email": "Wpisz swój e-mail",
    "Send Reset Email": "Wyślij e-mail resetujący",
    "Email was sent to": "E-mail został wysłany na adres",
    "Could not send reset email":
      "Nie udało się wysłać e-maila resetującego",

    // Matches list
    "Team 1": "Drużyna 1",
    "Team 2": "Drużyna 2",
    Tournament: "Turniej",
    Scores: "Wyniki",
    Date: "Data",
    Actions: "Akcje",
    Owner: "Właściciel",
    Description: "Opis",
    Comments: "Komentarze",
    Tags: "Tagi",
    "Add player": "Dodaj gracza",
    "Add team": "Dodaj drużynę",
    "Edit playlist": "Edytuj playlistę",
    "Merge Teams": "Scal drużyny",
    "Roster differences detected between teams:": "Wykryto różnice w składach drużyn:",
    "Review the roster in the next step before merging.": "Sprawdź skład w następnym kroku przed scaleniem.",
    "At least 2 team records must exist in your Teams list for the selected matches.": "Co najmniej 2 rekordy drużyn muszą istnieć na Twojej liście dla wybranych meczów.",
    "Merge...": "Scal...",
    "Select 2 or more team records to merge into one.": "Wybierz 2 lub więcej rekordów drużyn do scalenia.",
    "Filter teams...": "Filtruj drużyny...",
    "teams selected": "drużyny wybrane",
    "Select Teams": "Wybierz drużyny",
    "Reconcile Roster": "Uzgodnij skład",
    "Confirm Merge": "Potwierdź scalenie",
    "Merged team name": "Nazwa scalonej drużyny",
    "shirt number(s) have conflicting names — review below.": "numer(y) mają sprzeczne nazwy — sprawdź poniżej.",
    "Shirt": "Numer",
    "players included": "graczy uwzględnionych",
    "No players to reconcile.": "Brak graczy do uzgodnienia.",
    "Use this name": "Użyj tej nazwy",
    "Create merged team": "Utwórz scaloną drużynę",
    "with": "z",
    "duplicate team records": "zduplikowane rekordy drużyn",
    "Consolidate duplicate player records and reassign playlist links": "Scal zduplikowane rekordy graczy i przypisz ponownie playlisty",
    "This cannot be undone.": "Tej operacji nie można cofnąć.",
    "Teams being merged:": "Scalane drużyny:",
    "Step": "Krok",
    "of": "z",
    "Merging...": "Scalanie...",
    "Players & Teams": "Gracze i drużyny",
    "No players or teams assigned.": "Brak przypisanych graczy lub drużyn.",
    "Search players...": "Szukaj graczy...",
    "Search teams...": "Szukaj drużyn...",
    "Saving...": "Zapisywanie...",
    "My Matches": "Moje mecze",
    "Shared Matches": "Udostępnione mecze",
    "Search team...": "Szukaj drużyny…",
    "All Teams": "Wszystkie drużyny",
    "Multi Match Reports": "Raporty wielu meczów",
    "Delete match": "Usuń mecz",
    "Remove from my shared matches": "Usuń z moich udostępnionych meczów",
    "Share match": "Udostępnij mecz",
    "Edit match": "Edytuj mecz",
    "Open video analysis": "Otwórz analizę wideo",
    "Delete Match": "Usuń mecz",
    "Are you sure you want to delete the selected match?":
      "Czy na pewno chcesz usunąć wybrany mecz?",
    "No matches selected.": "Nie wybrano żadnego meczu.",

    // New Match
    "← Back": "← Wstecz",
    "Team A (Home)": "Drużyna A (gospodarz)",
    "Team B (Away)": "Drużyna B (gość)",
    "Select team…": "Wybierz drużynę…",
    "Tournament (optional)": "Turniej (opcjonalnie)",
    "Tournament / competition name": "Nazwa turnieju / rozgrywek",
    "Match date": "Data meczu",
    "Start Coding →": "Rozpocznij kodowanie →",

    // Reports
    Summary: "Podsumowanie",
    "Box Score": "Statystyki",
    "Side-outs Report": "Raport przebić",
    "Serve Receives": "Przyjęcia",
    "Attack Zones": "Strefy ataku",
    "Hitting Charts": "Wykresy ataku",
    "Video Analysis": "Analiza wideo",
    Dashboard: "Panel",
    Charts: "Wykresy",
    "AI Report": "Raport AI",
    // AI Reports page
    "AI Reports": "Raporty AI",
    "AI Reports — Coming Soon": "Raporty AI — Już wkrótce",
    "AI-powered match reports and deep analysis are on the way. Here are some sample reports to show what's coming.":
      "Raporty meczowe i pogłębione analizy oparte na AI są w drodze. Oto kilka przykładowych raportów na zapowiedź.",
    "Preview not available.": "Podgląd niedostępny.",
    "Open sample": "Otwórz przykład",
    "No matches were loaded. Select one or more matches and try again.":
      "Nie wczytano żadnych meczów. Wybierz jeden lub więcej meczów i spróbuj ponownie.",
    "Attack Analysis (English)": "Analiza ataku (angielski)",
    "Blocking Analysis (Vietnamese)": "Analiza bloku (wietnamski)",
    "Opponent Tendencies (Japanese)": "Tendencje przeciwnika (japoński)",
    // Deep AI analysis
    "Deep AI Analysis": "Pogłębiona analiza AI",
    "AI Deep Analysis": "Analiza pogłębiona AI",
    "Load at least one match with coded events to generate the analysis.": "Wczytaj co najmniej jeden mecz z zakodowanymi akcjami, aby wygenerować analizę.",
    "Run the deep analysis one section at a time to keep each request small. Edit or add questions below, then generate. Best used with the Anthropic (Claude) provider.":
      "Uruchamiaj analizę pogłębioną sekcja po sekcji, aby każde zapytanie było małe. Edytuj lub dodaj pytania poniżej, a następnie wygeneruj. Najlepiej używać z dostawcą Anthropic (Claude).",
    "Download CSV": "Pobierz CSV",
    "Analysis section": "Sekcja analizy",
    analysing: "analiza",
    "All sections (uses the most credits)": "Wszystkie sekcje (zużywa najwięcej kredytów)",
    "All sections": "Wszystkie sekcje",
    Questions: "Pytania",
    "one per line, edit or add your own": "jedno w wierszu, edytuj lub dodaj własne",
    "Reset to defaults": "Przywróć domyślne",
    "Add a question…": "Dodaj pytanie…",
    "Runs every section using the questions you've set for each. This sends one large request and uses the most credits.":
      "Uruchamia wszystkie sekcje z pytaniami ustawionymi dla każdej z nich. Wysyła jedno duże zapytanie i zużywa najwięcej kredytów.",
    "Generate full analysis": "Wygeneruj pełną analizę",
    "Generate section analysis": "Wygeneruj analizę sekcji",
    Serving: "Zagrywka",
    Reception: "Przyjęcie",
    Setting: "Rozegranie",
    Attack: "Atak",
    Blocking: "Blok",
    "Defence / Digging": "Obrona / Wykopywanie",
    "Rotation Analysis": "Analiza rotacji",
    "Opponent Tendencies (Scouting)": "Tendencje przeciwnika (skauting)",
    "Overall Team Metrics": "Ogólne wskaźniki zespołu",
    "Serve efficiency (aces, errors, and pressured receptions forced).": "Skuteczność zagrywki (asy, błędy i wymuszone trudne przyjęcia).",
    "Which servers are most effective, and against which reception zones?": "Którzy zawodnicy zagrywają najskuteczniej i przeciwko którym strefom przyjęcia?",
    "Which serve types (jump, float, jump-float) yield the best results?": "Które rodzaje zagrywki (z wyskoku, flotowa, flotowa z wyskoku) dają najlepsze rezultaty?",
    "Reception quality breakdown (perfect, positive, negative, error).": "Analiza jakości przyjęcia (perfekcyjne, pozytywne, negatywne, błąd).",
    "Which players struggle most under jump serves vs. float serves?": "Którzy zawodnicy mają najwięcej trudności przy zagrywce z wyskoku w porównaniu z flotową?",
    "How does poor reception correlate with side-out efficiency?": "Jak słabe przyjęcie wpływa na skuteczność side-outu?",
    "Set distribution by zone and by player.": "Rozkład wystawienia według strefy i zawodnika.",
    "How does the setter perform under pressure (bad-pass situations)?": "Jak rozgrywający radzi sobie pod presją (sytuacje słabego przyjęcia)?",
    "Tempo breakdown (quick, medium, high ball) and which yields the best attack outcome.": "Analiza temp (szybka, średnia, wysoka piłka) i które daje najlepszy efekt ataku.",
    "Kill percentage and error rate per player, per rotation, and per zone.": "Procent punktowych ataków i współczynnik błędów na zawodnika, na rotację i na strefę.",
    "Which attack zones (pipe, cross, line) have the highest efficiency?": "Które strefy ataku (pipe, skośny, prosty) mają najwyższą skuteczność?",
    "How attack efficiency changes in transition vs. side-out.": "Jak zmienia się skuteczność ataku w kontrataku w porównaniu z side-outem.",
    "Block-per-set rate per player and rotation.": "Liczba bloków na seta na zawodnika i rotację.",
    "Which opposing attack zones are most successfully blocked?": "Które strefy ataku przeciwnika są blokowane najskuteczniej?",
    "How often blocks result in kills vs. touch-outs.": "Jak często bloki kończą się punktem w porównaniu z blokiem-aut.",
    "Dig success rate per player and per court zone.": "Skuteczność obrony na zawodnika i na strefę boiska.",
    "Which attack types (tip, roll, hard swing) are hardest to defend?": "Które rodzaje ataku (kiwnięcie, plasé, mocne uderzenie) najtrudniej obronić?",
    "How defensive efficiency affects transition attack success.": "Jak skuteczność obrony wpływa na powodzenie ataku w kontrze.",
    "Which rotations have the highest and lowest point differential?": "Które rotacje mają najwyższy i najniższy bilans punktów?",
    "Side-out and break-point percentages per rotation.": "Procenty side-outu i break-pointu na rotację.",
    "Rotation-specific vulnerabilities (e.g. a weak serving or passing rotation).": "Słabe punkty poszczególnych rotacji (np. słaba rotacja w zagrywce lub przyjęciu).",
    "The opponent's most frequently used attack zones and combinations.": "Najczęściej wykorzystywane przez przeciwnika strefy ataku i kombinacje.",
    "The opponent's go-to attackers in high-pressure situations.": "Preferowani atakujący przeciwnika w sytuacjach dużej presji.",
    "The serve-receive patterns the opponent favours.": "Schematy przyjęcia preferowane przez przeciwnika.",
    "Overall rally-win probability in different score situations.": "Ogólne prawdopodobieństwo wygrania wymiany w różnych sytuacjach punktowych.",
    "How performance changes in sets 1–3 vs. sets 4–5 (fatigue/pressure effects).": "Jak zmienia się gra w setach 1–3 w porównaniu z setami 4–5 (efekty zmęczenia/presji).",
    "The correlation between first-ball side-out percentage and match outcome.": "Korelacja między procentem side-outu z pierwszej piłki a wynikiem meczu.",
    Report: "Raport",

    // Import page
    "Load DVW file(s)...": "Wczytaj plik(i) DVW…",
    "Load PSVB file(s)...": "Wczytaj plik(i) PSVB…",
    "Load VSM file(s)...": "Wczytaj plik(i) VSM…",
    "Load playlist file...": "Wczytaj plik playlisty…",
    "load one or more DataVolley files":
      "wczytaj jeden lub więcej plików DataVolley",
    "load one or more VBStats files":
      "wczytaj jeden lub więcej plików VBStats",
    "load one or more VolleyStation VSM files":
      "wczytaj jeden lub więcej plików VolleyStation VSM",
    "Convert DVW": "Konwertuj DVW",
    "VSM": "VSM",
    "Convert DVW to VSM...": "Konwertuj DVW na VSM…",
    "pick a DataVolley .dvw file to download as .vsm":
      "wybierz plik DataVolley .dvw, aby pobrać jako .vsm",
    "Convert VSM to DVW...": "Konwertuj VSM na DVW…",
    "pick a VolleyStation .vsm file to download as .dvw":
      "wybierz plik VolleyStation .vsm, aby pobrać jako .dvw",
    "Converting…": "Konwertowanie…",
    "Converted": "Przekonwertowano",
    "Failed to convert": "Konwersja nie powiodła się",
    "Beta testers only — coming soon for everyone else.":
      "Tylko dla testerów beta — wkrótce dla wszystkich.",
    "load a playlist": "wczytaj playlistę",
    "Use Shift-JIS Encoder": "Użyj kodera Shift-JIS",
    "Importing…": "Importowanie…",
    "Save to Database?": "Zapisać w bazie danych?",
    "Always save imports automatically": "Zawsze zapisuj importy automatycznie",
    "View Only": "Tylko podgląd",
    "Save to Database": "Zapisz w bazie danych",
    "Error saving to database": "Błąd zapisywania do bazy danych",
    "Saved to database": "Zapisano w bazie danych",
    "Saving to database…": "Zapisywanie do bazy danych…",
    "Upload them to the database now?":
      "Przesłać je teraz do bazy danych?",

    // Settings / Options
    Theme: "Motyw",
    Options: "Opcje",
    "Rotation naming convention": "Konwencja nazewnictwa rotacji",
    "Choose how rotations are labelled in reports and analysis.":
      "Wybierz, jak rotacje są oznaczane w raportach i analizach.",
    "R1–R6 (rotation sequence)": "R1–R6 (kolejność rotacji)",
    "S1–S6 (setter court position)": "S1–S6 (pozycja rozgrywającego)",
    "R1: setter in position 1, R2: setter in 6, R3: setter in 5, R4: setter in 4, R5: setter in 3, R6: setter in 2.":
      "R1: rozgrywający na pozycji 1, R2: rozgrywający na 6, R3: rozgrywający na 5, R4: rozgrywający na 4, R5: rozgrywający na 3, R6: rozgrywający na 2.",
    "S1: setter in 1, S2: setter in 2, S3: setter in 3, S4: setter in 4, S5: setter in 5, S6: setter in 6.":
      "S1: rozgrywający na 1, S2: rozgrywający na 2, S3: rozgrywający na 3, S4: rozgrywający na 4, S5: rozgrywający na 5, S6: rozgrywający na 6.",
    "Labels:": "Etykiety:",
    "Current theme:": "Bieżący motyw:",
    "Choose the language for the app interface.":
      "Wybierz język interfejsu aplikacji.",
    "Create your own language package or edit a community one, with AI-assisted translation.":
      "Utwórz własny pakiet językowy lub edytuj pakiet społeczności z tłumaczeniem wspomaganym przez AI.",

    // About / Footer
    "Copyright © Perana Sports {year}. All rights reserved.":
      "Copyright © Perana Sports {year}. Wszelkie prawa zastrzeżone.",
    "VBLive is free to use right now. However Perana Sports reserves the right to charge a subscription fee in due course.":
      "VBLive jest obecnie bezpłatny. Perana Sports zastrzega sobie jednak prawo do pobierania opłaty abonamentowej w stosownym czasie.",
    "Querries / suggestions are most welcomed.":
      "Pytania / sugestie są mile widziane.",

    // Live
    "Please enter server name to search":
      "Wpisz nazwę serwera, aby wyszukać",
    "Broadcast Server": "Serwer transmisji",
    "Enter DVMateLive Server Name": "Wpisz nazwę serwera DVMateLive",
    LIVE: "NA ŻYWO",

    // Playlists list
    "Delete Playlist": "Usuń playlistę",
    "Are you sure you want to delete the selected playlist?":
      "Czy na pewno chcesz usunąć wybraną playlistę?",
    "My Playlists": "Moje playlisty",
    "Shared Playlists": "Udostępnione playlisty",
    "Tags:": "Tagi:",
    "Select...": "Wybierz…",
    "All Tags": "Wszystkie tagi",

    // Share
    "User already added": "Użytkownik już dodany",
    "Please enter a valid email address":
      "Wpisz prawidłowy adres e-mail",
    "Please select at least one user to share with":
      "Wybierz co najmniej jednego użytkownika do udostępnienia",
    "Not Shared": "Nieudostępnione",
    "Available for everyone": "Dostępne dla wszystkich",
    "Available to selected users":
      "Dostępne dla wybranych użytkowników",
    "Enter VBLive user email address":
      "Wpisz adres e-mail użytkownika VBLive",
    "Selected users:": "Wybrani użytkownicy:",
    "Save List": "Zapisz listę",
    "Clear List": "Wyczyść listę",
    "Clear Users List": "Wyczyść listę użytkowników",
    "Do you really want to clear all users from list?":
      "Czy na pewno chcesz usunąć wszystkich użytkowników z listy?",
    "Do you want to save changes before closing?":
      "Czy chcesz zapisać zmiany przed zamknięciem?",
    "Enter name to save list to:":
      "Wpisz nazwę, pod którą chcesz zapisać listę:",
    "Enter name to save list to":
      "Wpisz nazwę, pod którą chcesz zapisać listę",

    // AI panels
    "AI settings": "Ustawienia AI",
    "Setup needed": "Wymagana konfiguracja",
    "Generate report": "Wygeneruj raport",
    "Generate AI report": "Wygeneruj raport AI",
    "Generate general report": "Wygeneruj raport ogólny",
    "Translate to…": "Przetłumacz na…",
    "Follow-up questions": "Pytania uzupełniające",
    "Ask a follow-up question…": "Zadaj pytanie uzupełniające…",
    "Ask a follow-up question": "Zadaj pytanie uzupełniające",
    "AI provider": "Dostawca AI",
    Model: "Model",
    Hide: "Ukryj",
    Show: "Pokaż",
    "Your key is stored only in this browser and sent directly to the selected provider.":
      "Twój klucz jest przechowywany tylko w tej przeglądarce i wysyłany bezpośrednio do wybranego dostawcy.",
    "Please enter your API key.": "Wpisz swój klucz API.",
    "Not enough data to analyse.":
      "Za mało danych do analizy.",
    "Select a player with recorded spikes first.":
      "Najpierw wybierz zawodnika z zarejestrowanymi atakami.",
    Player: "Zawodnik",
    "Select a player…": "Wybierz zawodnika…",
    "No players with recorded spikes in the selected matches.":
      "Brak zawodników z zarejestrowanymi atakami w wybranych meczach.",
    "Analyse with AI": "Analizuj z AI",
    "AI scouting report": "Raport skautingowy AI",
    "Q:": "P:",
    Ask: "Zapytaj",
    "e.g. Which rotation should we target on serve?":
      "np. Którą rotację powinniśmy atakować przy zagrywce?",
    "Saved questions:": "Zapisane pytania:",
    "Click to ask again": "Kliknij, aby zapytać ponownie",
    "Remove saved question": "Usuń zapisane pytanie",

    // AI language options
    Spanish: "Hiszpański",
    French: "Francuski",
    German: "Niemiecki",
    Italian: "Włoski",
    Portuguese: "Portugalski",
    Dutch: "Holenderski",
    Polish: "Polski",
    Russian: "Rosyjski",
    Turkish: "Turecki",
    Arabic: "Arabski",
    "Chinese (Simplified)": "Chiński (uproszczony)",
    Japanese: "Japoński",
    Korean: "Koreański",
    Vietnamese: "Wietnamski",
    Thai: "Tajski",

    // Box score
    "Show Full Headers": "Pokaż pełne nagłówki",
    "Show Short Headers": "Pokaż krótkie nagłówki",
    "Show Less Players": "Pokaż mniej zawodników",
    "Show All Players": "Pokaż wszystkich zawodników",
    "Attack Kills": "Punkty z ataku",
    "Attack Errors": "Błędy w ataku",
    "Total Attempts": "Łączna liczba prób",
    "Hitting Efficiency": "Skuteczność ataku",
    "Serve Aces": "Asy serwisowe",
    "Serve Errors": "Błędy serwisowe",
    "Pass Errors": "Błędy przyjęcia",
    "% Perfect Passes": "% idealnych przyjęć",
    "Passing Average": "Średnia przyjęcia",
    "Total Passes": "Łączne przyjęcia",
    "Set Assists": "Asysty rozegrania",
    Digs: "Obrony",
    "Momentum Chart": "Wykres momentum",
    "Key event": "Kluczowa akcja",
    "Break Point %": "% punkt przełamania",
    "First Ball Sideout %": "% side-out z pierwszej piłki",
    "Sideout Transition %": "% side-out w kontrataku",
    "FB Sideout Kills %": "% atak kończący z pierwszej piłki",
    "Sideout Opposition Error %": "% side-out na błędzie przeciwnika",
    Plus: "Plus",
    Minus: "Minus",
    Blocks: "Punkty blokiem",
    "Positive Serve %": "% zagrywek pozytywnych",
    Sideout: "Side-out",
    "Break point": "Punkt przełamania",
    "Ace / Kill": "As / Atak kończący",
    "In play": "W grze",
    Error: "Błąd",
    "Sideout on a kill": "Side-out atakiem kończącym",
    "Sideout on an error": "Side-out na błędzie przeciwnika",
    "Top half": "Górna połowa",
    "Bottom half": "Dolna połowa",
    "The centre line steps toward the team that wins each point.": "Linia środkowa przesuwa się w stronę drużyny, która wygrywa dany punkt.",
    "No point-by-point data available for this match.": "Brak danych punkt po punkcie dla tego meczu.",
    "No match data available.": "Brak dostępnych danych meczu.",
    "This report isn't supported on this browser yet — please use Safari.": "Ten raport nie jest jeszcze obsługiwany w tej przeglądarce — użyj Safari.",
    "No video available for this match.": "Brak dostępnego wideo dla tego meczu.",
    passes: "przyjęcia",
    "eff.": "skut.",
    Grade: "Ocena",
    LEFT: "LEWO",
    CENTRE: "ŚRODEK",
    RIGHT: "PRAWO",
    SELECTED: "WYBRANE",
    Passers: "Przyjmujący",
    Calls: "Zawołania",
    "SIDE OUT": "SIDE-OUT",
    TRANSITION: "TRANZYCJA",
    "No events in selection.": "Brak zdarzeń w bieżącym wyborze.",
    "Serve Receives By Players": "Przyjęcia według zawodników",
    "Serve Receive By Zones": "Przyjęcie według stref",
    "Dig Errors": "Błędy w obronie",
    "Dig Average": "Średnia obrony",
    "Block Solos": "Bloki pojedyncze",
    "Block Assists": "Bloki wspomagane",
    "Block Errors": "Błędy bloku",
    "Points Scored": "Zdobyte punkty",
    TEAM: "DRUŻYNA",

    // Sideout report
    "ALL ROWS": "WSZYSTKIE WIERSZE",
    "ROWS ": "WIERSZE ",
    "ROW ": "WIERSZ ",
    "FB Kills": "Punkty z ataku FB",
    "Opp. Serve Errors": "Błędy serwisowe przeciw.",
    "FB Opp. Errors": "Błędy przeciw. FB",
    "Kills/Blocks": "Ataki/Bloki",
    "Opp. Errors": "Błędy przeciw.",
    Unsuccessful: "Nieudane",

    // Attack zones
    "No events in selected zone.":
      "Brak zdarzeń w wybranej strefie.",
    "Show all attacks. Or click on a zone to show attacks in that zone.":
      "Pokaż wszystkie ataki. Lub kliknij strefę, aby pokazać ataki w tej strefie.",
    "OPPONENTS (": "PRZECIWNICY (",

    // Video analysis filters
    "All Types": "Wszystkie typy",
    Serve: "Zagrywka",
    "Serve-Receive": "Przyjęcie",
    Set: "Rozegranie",
    "Setter's Call": "Wywołanie rozgrywającego",
    "Disable this skill": "Wyłącz tę umiejętność",
    "Enable this skill": "Włącz tę umiejętność",
    "Teams & Players": "Zespoły i zawodnicy",
    "Match State": "Stan meczu",
    "Custom Code": "Niestandardowy kod",
    "Search... (comma-separated for multiple)": "Szukaj… (oddziel przecinkami, aby wyszukać kilka)",
    vs: "vs",
    "Rewind 60 seconds": "Cofnij o 60 sekund",
    "Rewind 1 frame": "Cofnij o 1 klatkę",
    "Forward 1 frame": "Do przodu o 1 klatkę",
    "Forward 60 seconds": "Do przodu o 60 sekund",
    "Previous Clip": "Poprzedni klip",
    Replay: "Powtórz",
    "Next Clip": "Następny klip",
    Phases: "Fazy",
    "Score Range": "Zakres wyniku",
    "Set Winner": "Zwycięzca seta",
    Type: "Typ",
    Strength: "Siła",
    "Setter Calls": "Wywołania rozgrywającego",
    "Setter Call": "Wywołanie rozgrywającego",
    Freeball: "Piłka wolna",
    "Close (0-2)": "Wyrównany (0-2)",
    "Moderate (3-5)": "Umiarkowany (3-5)",
    "Lopsided (6+)": "Duża przewaga (6+)",
    "Focus Team": "Zespół śledzony",
    Oppositions: "Przeciwnicy",
    "Focus Team Won": "Zespół śledzony wygrał",
    "Oppositions Won": "Przeciwnicy wygrali",
    "Advanced Filters": "Filtry zaawansowane",
    Players: "Zawodnicy",
    Opponents: "Przeciwnicy",
    "Event Type": "Typ zdarzenia",
    "Event Result": "Wynik zdarzenia",
    "Attack Type": "Typ ataku",
    "Hit Type": "Typ uderzenia",
    "Block Type": "Typ bloku",
    "Attack Combo": "Kombinacja ataku",
    Apply: "Zastosuj",
    Rotation: "Rotacja",
    Skill: "Umiejętność",
    "Lead Time": "Czas wyprzedzenia",
    Duration: "Czas trwania",
    Timing: "Ustawienia czasowe",
    "Go Live": "Przejdź na żywo",
    "Playback has fallen behind the live broadcast — click to jump to now.": "Odtwarzanie pozostaje w tyle za transmisją na żywo — kliknij, aby przejść do bieżącej chwili.",
    "Video timing": "Ustawienia czasowe wideo",
    "Default lead time (seconds before an event to start playback) and clip duration, per skill.": "Domyślny czas wyprzedzenia (liczba sekund przed zdarzeniem, od której zaczyna się odtwarzanie) i czas trwania klipu, dla każdej umiejętności.",
    Cloud: "Chmura",
    Local: "Lokalny",
    "Relay host": "Host przekaźnika",
    "IP or hostname of the machine running the local relay.": "Adres IP lub nazwa hosta komputera, na którym działa lokalny przekaźnik.",
    Spike: "Atak",
    Block: "Blok",
    Defence: "Obrona",
    "All Results": "Wszystkie wyniki",
    Hard: "Mocny",
    Soft: "Słaby",
    Tip: "Kiwnięcie",
    "Not Specified": "Nieokreślony",
    "H - High": "H - Wysoka",
    "M- Medium": "M - Średnia",
    "Q - Quick": "Q - Szybka",
    "T - Tense": "T - Napięta",
    "U - Super": "U - Super",
    "F - Fast": "F - Szybka",
    "O - Other": "O - Inna",
    "B0 - No Block": "B0 - Brak bloku",
    "B1 - 1 Block": "B1 - 1 blokujący",
    "B2 - 2 Blocks": "B2 - 2 blokujących",
    "B3 - 3 Blocks": "B3 - 3 blokujących",
    "BS - Seams": "BS - Szczeliny",

    // All filters panel
    ALL: "WSZYSTKIE",
    NONE: "BRAK",

    // Playlist editor
    "Remove Item": "Usuń element",
    "Do you really want to remove the selected item?":
      "Czy na pewno chcesz usunąć wybrany element?",
    "Description is required": "Opis jest wymagany",
    "Comments are required": "Komentarze są wymagane",
    "Tags are required": "Tagi są wymagane",
    "Playlist saved successfully": "Playlista zapisana pomyślnie",
    "Error saving playlist": "Błąd podczas zapisywania playlisty",

    // Language editor
    "My saved packages": "Moje zapisane pakiety",
    "Loading shared languages…": "Ładowanie udostępnionych języków…",
    "Community shared languages": "Języki udostępnione przez społeczność",
    by: "autor",
    "based on": "na podstawie",
    "or create new": "lub utwórz nowy",
    Use: "Użyj",
    "New Language Package": "Nowy pakiet językowy",
    "Language Name": "Nazwa języka",
    "Language Code": "Kod języka",
    "Flag Emoji": "Emoji flagi",
    "Base language (reference)": "Język bazowy (referencyjny)",
    "If not English, both English and the reference language will be shown.":
      "Jeśli to nie angielski, zostaną wyświetlone zarówno angielski, jak i język referencyjny.",
    "Start Editing": "Rozpocznij edycję",
    "Save Key": "Zapisz klucz",
    "AI key set": "Klucz AI ustawiony",
    "Set API key for AI translation":
      "Ustaw klucz API do tłumaczenia AI",
    "AI key ✓": "Klucz AI ✓",
    "Set AI key": "Ustaw klucz AI",
    "AI: Translate All": "AI: Przetłumacz wszystko",
    "Translating…": "Tłumaczenie…",
    "AI translate section": "Przetłumacz sekcję z AI",
    "Clear All": "Wyczyść wszystko",
    "Saving…": "Zapisywanie…",
    English: "Angielski",
    new: "nowy",
    "Enter translation…": "Wpisz tłumaczenie…",
    "Share with other VBLive users?":
      "Udostępnić innym użytkownikom VBLive?",
    "Would you like to share the": "Czy chcesz udostępnić",
    "language package with all VBLive users? Anyone can then copy and build upon it.":
      "pakiet językowy wszystkim użytkownikom VBLive? Każdy będzie mógł go skopiować i rozbudować.",
    "No, just save for me": "Nie, zapisz tylko dla mnie",
    "Yes, share it!": "Tak, udostępnij!",
    "Language package saved and shared with all VBLive users!":
      "Pakiet językowy zapisany i udostępniony wszystkim użytkownikom VBLive!",
    "Language package saved!": "Pakiet językowy zapisany!",
    "Clear all translations? This cannot be undone.":
      "Wyczyścić wszystkie tłumaczenia? Tej operacji nie można cofnąć.",

    // Additional UI phrases
    Teams: "Drużyny",
    "Shared By": "Udostępnione przez",
    "View Activity": "Aktywność wyświetleń",
    Viewer: "Widz",
    "Date / Time": "Data / Godzina",
    "No views recorded yet.": "Brak zarejestrowanych wyświetleń.",
    "Date / Scores": "Data / Wyniki",
    "Stats API source": "Źródło API statystyk",
    "load one or more AVC VIS files": "wczytaj jeden lub więcej plików AVC VIS",
    "Points to close a set": "Punkty do zakończenia seta",
    "Require 2-point advantage": "Wymagaj przewagi 2 punktów",
    Roster: "Skład",
    Clone: "Klonuj",
    "Edit team": "Edytuj drużynę",
    "New player": "Nowy zawodnik",
    "Search player...": "Szukaj zawodnika…",
    "Match Centre": "Centrum meczu",
    "Points Won": "Zdobyte punkty",
    "Opp. Error": "Błąd przeciwnika",
    Total: "Razem",
    "Match Skills": "Elementy meczu",
    Dig: "Obrona",
    "Best Scorers": "Najlepsi strzelcy",
    "Power Play": "Power Play",
    Match: "Mecz",
    "Player scouting": "Scouting zawodników",
    "Team deep analysis": "Dogłębna analiza drużyny",
    "All Players": "Wszyscy zawodnicy",
    "All Sets": "Wszystkie sety",
    "All Rotations": "Wszystkie rotacje",
    "All Combos": "Wszystkie kombinacje",
    "All Calls": "Wszystkie zawołania",
    "All Events": "Wszystkie zdarzenia",
    "Select all items": "Zaznacz wszystkie elementy",
    "Unselect all items": "Odznacz wszystkie elementy",
    "Create play list": "Utwórz playlistę",
    "Point by Point": "Punkt po punkcie",
    // i18n backfill pass — previously English-fallback-only keys
    "Access denied": "Odmowa dostępu",
    "Aces": "Asy",
    "ACES": "ASY",
    "Add report": "Dodaj raport",
    "Admin": "Administracja",
    "Admins": "Administratorzy",
    "Advanced": "Zaawansowane",
    "All set!": "Wszystko gotowe!",
    "Analyse team with AI": "Analizuj zespół za pomocą AI",
    "Are you sure you want to delete the selected play list?": "Czy na pewno chcesz usunąć wybraną listę odtwarzania?",
    "Assign": "Przypisz",
    "Assign playlist": "Przypisz listę odtwarzania",
    "Assigned": "Przypisano",
    "At least one admin is required.": "Wymagany jest co najmniej jeden administrator.",
    "Attack Combo / Setter Call": "Kombinacja ataku / Zawołanie rozgrywającego",
    "Attack diagram": "Diagram ataku",
    "Attacks": "Ataki",
    "ATTACKS": "ATAKI",
    "Auto-detected a custom set-closing score for": "Automatycznie wykryto niestandardowy wynik kończący seta dla",
    "BLOCK": "BLOK",
    "BLOCKS": "BLOKI",
    "Beta testers": "Testerzy beta",
    "Clear filter": "Wyczyść filtr",
    "Close Set": "Zamknij seta",
    "Close menu": "Zamknij menu",
    "Code": "Kod",
    "Coding defaults & shortcuts": "Ustawienia domyślne i skróty kodowania",
    "Continue": "Kontynuuj",
    "Could not load match data. The match may no longer be available.": "Nie można załadować danych meczu. Mecz może być już niedostępny.",
    "Could not load stats for these matches.": "Nie można załadować statystyk dla tych meczów.",
    "Could not load this match.": "Nie można załadować tego meczu.",
    "Could not load this playlist.": "Nie można załadować tej listy odtwarzania.",
    "Could not load this report.": "Nie można załadować tego raportu.",
    "Could not save the report.": "Nie można zapisać raportu.",
    "Could not share the report. Please try again.": "Nie można udostępnić raportu. Spróbuj ponownie.",
    "Court horizontal (net vertical)": "Boisko poziomo (siatka pionowo)",
    "Court vertical (net horizontal)": "Boisko pionowo (siatka poziomo)",
    "Create": "Utwórz",
    "Create a new team from this roster": "Utwórz nowy zespół na podstawie tego składu",
    "DIFFERENCE": "RÓŻNICA",
    "DV Code": "Kod DV",
    "Delete this report?": "Usunąć ten raport?",
    "Detected a custom set-closing score of": "Wykryto niestandardowy wynik kończący seta wynoszący",
    "Double Sub": "Podwójna zmiana",
    "Double Sub: now pick a bench player for the flashing OPPOSITE slot…": "Podwójna zmiana: wybierz teraz zawodnika z ławki na migającą pozycję przyjmującego…",
    "Double Sub: pick a bench player for the flashing SETTER slot…": "Podwójna zmiana: wybierz zawodnika z ławki na migającą pozycję rozgrywającego…",
    "Double Substitution — swap the Setter and Opposite for two incoming players": "Podwójna zmiana — zamień rozgrywającego i przyjmującego na dwóch wchodzących zawodników",
    "Drag to draw the ball path; pause briefly to mark a touch (reception/dig/block), release to finish.": "Przeciągnij, aby narysować tor piłki; zatrzymaj się na chwilę, aby oznaczyć kontakt (przyjęcie/obrona/blok), puść, aby zakończyć.",
    "Draw": "Rysuj",
    "Draw & Code": "Rysuj i koduj",
    "Draw & Code — draw ball paths on the court to code the rally": "Rysuj i koduj — rysuj tory piłki na boisku, aby zakodować akcję",
    "Edit Lineup": "Edytuj skład",
    "Edit Lineup — freely rearrange the starting lineup before the set begins": "Edytuj skład — swobodnie zmień wyjściowy skład przed rozpoczęciem seta",
    "Emails in this list can manage beta testers and other admins.": "Adresy e-mail na tej liście mogą zarządzać testerami beta i innymi administratorami.",
    "Emails in this list get early access to AI Reports and other gated features. Changes take effect immediately.": "Adresy e-mail na tej liście uzyskują wczesny dostęp do raportów AI i innych funkcji objętych ograniczeniami. Zmiany obowiązują natychmiast.",
    "Enter a valid email address.": "Wprowadź prawidłowy adres e-mail.",
    "Exit fullscreen": "Wyjdź z pełnego ekranu",
    "Extended": "Rozszerzone",
    "Failed to generate PDF.": "Nie udało się wygenerować pliku PDF.",
    "Failed to save changes.": "Nie udało się zapisać zmian.",
    "Filtered by": "Filtrowane według",
    "First name": "Imię",
    "For stronger security later, consider moving admin checks to a Firebase Auth custom claim instead of this email list.": "Dla większego bezpieczeństwa w przyszłości rozważ przeniesienie weryfikacji administratora do niestandardowego roszczenia Firebase Auth zamiast tej listy e-mail.",
    "Fullscreen": "Pełny ekran",
    "Generate an AI report from these matches": "Wygeneruj raport AI na podstawie tych meczów",
    "How often (in seconds) the events list is updated during a live session.": "Jak często (w sekundach) lista zdarzeń jest aktualizowana podczas sesji na żywo.",
    "Last name": "Nazwisko",
    "Leave blank to use standard rules (25 points, 15 for a 5th/deciding set)": "Pozostaw puste, aby użyć standardowych zasad (25 punktów, 15 w 5. secie decydującym)",
    "Live session refresh interval": "Interwał odświeżania sesji na żywo",
    "Load VIS file(s)...": "Wczytaj plik(i) VIS…",
    "Load this team's last-saved starting lineup (positions, setter and liberos)": "Wczytaj ostatnio zapisany wyjściowy skład tej drużyny (pozycje, rozgrywający i libero)",
    "Loading…": "Ładowanie…",
    "Manually end the current set at its current score, regardless of the configured winning score / 2-point advantage rule": "Ręcznie zakończ bieżącego seta przy aktualnym wyniku, niezależnie od skonfigurowanego wyniku zwycięskiego / zasady przewagi 2 punktów",
    "Migration finished with some issues": "Migracja zakończona z pewnymi problemami",
    "Most of your data was migrated, but some items could not be moved. You can retry now or continue and try again later.": "Większość Twoich danych została zmigrowana, ale niektórych elementów nie udało się przenieść. Możesz spróbować ponownie teraz lub kontynuować i spróbować ponownie później.",
    "Mute": "Wycisz",
    "New team": "Nowy zespół",
    "Next": "Dalej",
    "Nickname": "Pseudonim",
    "No Power Play": "Brak Power Play",
    "No admins yet.": "Brak administratorów.",
    "No beta testers yet.": "Brak testerów beta.",
    "No coded events to analyse.": "Brak zakodowanych zdarzeń do analizy.",
    "No coded events to export.": "Brak zakodowanych zdarzeń do wyeksportowania.",
    "No matches.": "Brak meczów.",
    "No players added yet.": "Nie dodano jeszcze zawodników.",
    "No players recorded.": "Brak zarejestrowanych zawodników.",
    "No players yet.": "Brak zawodników.",
    "No playlists assigned.": "Nie przypisano żadnej listy odtwarzania.",
    "No playlists found.": "Nie znaleziono żadnej listy odtwarzania.",
    "No reports saved.": "Nie zapisano żadnego raportu.",
    "No roster players available.": "Brak dostępnych zawodników ze składu.",
    "No substitutions": "Brak zmian",
    "No teams found.": "Nie znaleziono żadnego zespołu.",
    "No teams yet.": "Brak zespołów.",
    "No teams.": "Brak zespołów.",
    "Nominate Setter": "Wyznacz rozgrywającego",
    "Notify these users by email (optional)": "Powiadom tych użytkowników e-mailem (opcjonalnie)",
    "OPP REC": "PRZYJ. PRZ.",
    "OPP. ERRORS": "BŁĘDY PRZ.",
    "Open / Download": "Otwórz / Pobierz",
    "Open local file": "Otwórz plik lokalny",
    "Open menu": "Otwórz menu",
    "Opposition Passing — use the opposition's reception formation on the diagram": "Przyjęcie przeciwnika — użyj formacji przyjęcia przeciwnika na diagramie",
    "Paste the report (Markdown supported)…": "Wklej raport (obsługiwany jest Markdown)…",
    "Pause": "Pauza",
    "Play": "Odtwórz",
    "Playing squad": "Skład meczowy",
    "Playing squad is full. Remove a player from the squad before adding another.": "Skład meczowy jest pełny. Usuń zawodnika ze składu przed dodaniem kolejnego.",
    "Please sign in to save reports.": "Zaloguj się, aby zapisywać raporty.",
    "Power Play Only": "Tylko Power Play",
    "Record a team error (no player) — edit later to add the player and event type": "Zarejestruj błąd zespołowy (bez zawodnika) — edytuj później, aby dodać zawodnika i typ zdarzenia",
    "Record at least one event before making substitutions": "Zarejestruj co najmniej jedno zdarzenie przed dokonaniem zmian",
    "Redo": "Ponów",
    "Remove": "Usuń",
    "Remove share": "Usuń udostępnienie",
    "Remove yourself as admin? You will immediately lose access to this page.": "Usunąć siebie z listy administratorów? Natychmiast stracisz dostęp do tej strony.",
    "Report saved and shared.": "Raport zapisany i udostępniony.",
    "Report saved to the player's profile.": "Raport zapisany w profilu zawodnika.",
    "Report title": "Tytuł raportu",
    "Reports": "Raporty",
    "Result": "Wynik",
    "Retry": "Spróbuj ponownie",
    "Return to live coding": "Powrót do kodowania na żywo",
    "Rotation is not permitted while making substitutions": "Rotacja nie jest dozwolona podczas dokonywania zmian",
    "SERVE": "ZAGRYWKA",
    "SETS": "SETY",
    "Save & Share": "Zapisz i udostępnij",
    "Save this report so you can share it with other users": "Zapisz ten raport, aby móc udostępnić go innym użytkownikom",
    "Save this report to the player's profile": "Zapisz ten raport w profilu zawodnika",
    "Save to player": "Zapisz dla zawodnika",
    "Saved lineup": "Zapisany skład",
    "Scroll, drag or swipe to scrub": "Przewiń, przeciągnij lub przesuń, aby przewinąć",
    "Search existing players...": "Szukaj istniejących zawodników…",
    "Search playlists…": "Szukaj list odtwarzania…",
    "Select a match video to view while entering lineups.": "Wybierz wideo z meczu do obejrzenia podczas wprowadzania składów.",
    "Select a player.": "Wybierz zawodnika.",
    "Select a team.": "Wybierz zespół.",
    "Setting up your account": "Konfigurowanie konta",
    "Shaded rows are played under a Power Play — HP/AP marks the calling team, bold gold scores are bonus (double) points.": "Zacieniowane wiersze zostały rozegrane podczas Power Play — HP/AP oznacza zespół wywołujący, pogrubione złote wyniki to punkty bonusowe (podwójne).",
    "Shared by": "Udostępnione przez",
    "Shared with me": "Udostępnione mi",
    "Show help": "Pokaż pomoc",
    "Show public playlists": "Pokaż publiczne listy odtwarzania",
    "Standard": "Standard",
    "Substitutions made — tap to view": "Dokonano zmian — dotknij, aby zobaczyć",
    "Tap the blocking player.": "Dotknij zawodnika blokującego.",
    "Team colour": "Kolor zespołu",
    "Team name": "Nazwa zespołu",
    "That email is already in the list.": "Ten adres e-mail jest już na liście.",
    "The report is too large to save. Try a single match or fewer sets.": "Raport jest zbyt duży, aby go zapisać. Spróbuj dla jednego meczu lub mniejszej liczby setów.",
    "This match has no Power Play calls.": "Ten mecz nie ma wywołań Power Play.",
    "This player has no name to save the report against.": "Ten zawodnik nie ma nazwiska, pod którym można zapisać raport.",
    "This team has no designated setter. Pick the player who is setting.": "Ten zespół nie ma wyznaczonego rozgrywającego. Wybierz zawodnika, który rozgrywa.",
    "Toggle video player": "Przełącz odtwarzacz wideo",
    "Unmute": "Włącz dźwięk",
    "Untitled playlist": "Lista odtwarzania bez tytułu",
    "Video URL…": "Adres URL wideo…",
    "We're moving your data to a faster, more reliable home. This happens only once.": "Przenosimy Twoje dane do szybszego, bardziej niezawodnego miejsca. Dzieje się to tylko raz.",
    "You don't have permission to view this page.": "Nie masz uprawnień do wyświetlenia tej strony.",
    "Your data has been migrated successfully.": "Twoje dane zostały pomyślnie zmigrowane.",
    "Your teams and players will appear here after your data is migrated.": "Twoje zespoły i zawodnicy pojawią się tutaj po zmigrowaniu danych.",
    "bonus point": "punkt bonusowy",
    "bonus points": "punkty bonusowe",
    "event": "zdarzenie",
    "events": "zdarzenia",
    "file(s).": "plik(ów).",
    "is not on this team's roster.": "nie znajduje się w składzie tej drużyny.",
    "loaded": "wczytano",
    "matches": "mecze",
    "more": "więcej",
    "players": "zawodników",
    "points (no value was entered) — applied automatically.": "punktów (nie wprowadzono wartości) — zastosowano automatycznie.",
    "roster": "skład",
    "seconds": "sekund",
    "selected": "wybrano",
    "session(s)?": "sesję/sesji?",
    "showing": "wyświetlono",
    "tournaments": "turnieje",
    "…and": "…i",
    "● marks which team served the point.": "● oznacza, który zespół zagrywał tę akcję.",
    // i18n backfill pass (Catapult + misc)
    "Centimetres (cm)": "Centymetry (cm)",
    "Inches (in)": "Cale (in)",
    "Jump height units": "Jednostki wysokości skoku",
    "Choose how Catapult jump heights are displayed and filtered.": "Wybierz sposób wyświetlania i filtrowania wysokości skoków Catapult.",
    "Select Catapult Focus file...": "Wybierz plik Catapult Focus…",
    "Select a Catapult Focus CSV export to view jump data for this match.": "Wybierz eksport CSV Catapult Focus, aby wyświetlić dane skoków dla tego meczu.",
    "Loaded from saved match data": "Wczytano z zapisanych danych meczu",
    "No jump data found in this file.": "Nie znaleziono danych o skokach w tym pliku.",
    "Could not read this file. Please check it is a Catapult Focus CSV export.": "Nie można odczytać tego pliku. Sprawdź, czy jest to eksport CSV Catapult Focus.",
    "Could not save jump data with this match.": "Nie udało się zapisać danych o skokach z tym meczem.",
    "Jump data saved with this match.": "Dane o skokach zapisano z tym meczem.",
    "Show players with no match stats": "Pokaż zawodników bez statystyk meczu",
    "hidden": "ukryte",
    "jumps": "skoki",
    "periods": "okresy",
    "Sync offset": "Przesunięcie synchronizacji",
    "matched": "dopasowane",
    "match events now show jump height/duration in Video Analysis": "zdarzeń meczowych pokazuje teraz wysokość/czas skoku w Analizie wideo",
    "Team": "Drużyna",
    "Team A": "Drużyna A",
    "Team B": "Drużyna B",
    "Which team is this Catapult file for?": "Dla której drużyny jest ten plik Catapult?",
    "We couldn't confidently match the players in this Focus file to one team. Please select which team it belongs to.": "Nie udało się jednoznacznie dopasować zawodników z tego pliku Focus do jednej drużyny. Wybierz, do której drużyny należy.",
    "This match has no video attached yet.": "Do tego meczu nie dołączono jeszcze filmu.",
    "No synced jump events found for this player.": "Nie znaleziono zsynchronizowanych zdarzeń skoku dla tego zawodnika.",
    "No match loaded.": "Nie wczytano meczu.",
    "avg": "śr.",
    "max": "maks.",
    "Sync": "Synchronizacja",
    "Jump Report": "Raport skoków",
    "Watch Video": "Obejrzyj wideo",
    "Save PDF": "Zapisz PDF",
    "Generated by VBLive": "Wygenerowano przez VBLive",
    "Deep analysis of {team} from ball-by-ball event data, covering serving, reception, setting, attack, blocking, defence, rotations and opponent tendencies. Best used with the Anthropic (Claude) provider.": "Dogłębna analiza drużyny {team} na podstawie danych zdarzenie po zdarzeniu, obejmująca zagrywkę, przyjęcie, rozegranie, atak, blok, obronę, rotacje i tendencje przeciwnika. Najlepiej używać z dostawcą Anthropic (Claude).",
    "teams": "drużyny",
  },

  no: {
    // Information page
    Information: "Informasjon",
    "Release notes": "Versjonsmerknader",
    "Contact us": "Kontakt oss",
    "Questions, feedback or suggestions are most welcome.": "Spørsmål, tilbakemeldinger eller forslag er hjertelig velkommen.",
    // DV match coding
    Lineups: "Oppstillinger",
    Point: "Poeng",
    Video: "Video",
    Defaults: "Standardverdier",
    "[team][NN][skill][type?][eval][zoneFrom?[szFrom?][zoneTo?[szTo?]]]": "[lag][NN][ferdighet][type?][vurd][soneFra?[uzFra?][soneTil?[uzTil?]]]",
    Vertical: "Vertikal",
    Horizontal: "Horisontal",
    "Swap sides": "Bytt side",
    Subs: "Bytter",
    Substitutions: "Bytter",
    Court: "Bane",
    Home: "Hjemme",
    Away: "Borte",
    Games: "Sett",
    Enter: "Bekreft",
    Undo: "Angre",
    "Match saved": "Kamp lagret",
    "Please sign in to save this match.": "Logg inn for å lagre denne kampen.",
    "Nothing to save yet.": "Ingenting å lagre ennå.",
    "Could not save the match. Please try again.": "Kunne ikke lagre kampen. Prøv igjen.",
    Discard: "Forkast",
    "Resumed autosaved coding": "Gjenopptok automatisk lagret koding",
    "Changes discarded": "Endringer forkastet",
    "Discard all changes and revert to the starting point?":
      "Forkaste alle endringer og gå tilbake til utgangspunktet?",
    Clear: "Tøm",
    "Prev set": "Forrige sett",
    "Next set": "Neste sett",
    "Match Setup": "Kampoppsett",
    "First serve": "Første serve",
    "Start Match": "Start kamp",
    "Resume Match": "Fortsett kamp",
    Skip: "Hopp over",
    "Select an event then drag to draw ball path": "Velg en hendelse og dra for å tegne ballbanen",
    "Drag on court to set ball path": "Dra på banen for å sette ballbanen",
    "No events yet – enter a DV code above and press Enter.": "Ingen hendelser ennå – skriv inn en DV-kode over og trykk Bekreft.",
    "DataVolley code reference": "DataVolley-kodereferanse",
    Format: "Format",
    Skills: "Ferdigheter",
    Evaluation: "Vurdering",
    "Skill / type omission": "Utelatelse av ferdighet / type",
    "Skill and/or type may be omitted; defaults fill in the rest.": "Ferdighet og/eller type kan utelates; standardverdier fyller inn resten.",
    "Dot-coding (S→R and A→B pairs)": "Punktkoding (S→R- og A→B-par)",
    "Chaining & aliases": "Sammenkjeding og aliaser",
    "team:": "lag:",
    "omit → defaults to A": "utelat → standard A",
    "NN: 1 or 2 digit jersey, e.g. 1, 01, 12": "NN: 1- eller 2-sifret draktnummer, f.eks. 1, 01, 12",
    "sub-zone: a b c d appended to zone digit, e.g. 5b or 5b1a": "undersone: a b c d lagt til sonesifferet, f.eks. 5b eller 5b1a",
    "S Serve · R Reception · E Set · A Attack · B Block · D Dig · F Freeball": "S Serve · R Mottak · E Pasning · A Angrep · B Blokk · D Forsvar · F Friball",
    "# kill/ace · + positive · ! ok · - negative · / overpass · = error": "# poeng/ess · + positiv · ! ok · - negativ · / overball · = feil",
    "E.g.": "F.eks.",
    "serve with skill=A (default), type=H, eval=+, zone=5.": "serve med skill=A (standard), type=H, eval=+, zone=5.",
    "type-only (no skill marker), defaults skill from context.": "kun type (ingen ferdighetsmarkør), ferdighet fra kontekst som standard.",
    "serve coded, reception auto-linked.": "serve kodet, mottak automatisk koblet.",
    "attack coded, block auto-linked.": "angrep kodet, blokk automatisk koblet.",
    "Dot connects the reception eval to the corresponding serve eval via the serve→receive mapping.": "Punktet kobler mottaksvurderingen til den tilsvarende servevurderingen via serve→mottak-tilordningen.",
    "two events in one string.": "to hendelser i én streng.",
    "manual point A/B.": "manuelt poeng A/B.",
    "serve ace/error.": "serve ess/feil.",
    "serve with eval.": "serve med vurdering.",
    // Stats, sideouts, filters & video tooltips
    "Block Kills": "Blokkpoeng",
    "Side Out Percentage": "Sideout-prosent",
    "All First Ball": "Alle førsteballer",
    "First Ball Kills": "Førsteball-poeng",
    "Good Serve": "God serve",
    "Break Points": "Breakpoeng",
    Game: "Sett",
    Row: "Posisjon",
    "First Ball Sideouts": "Førsteball-sideouts",
    "All Sideouts": "Alle sideouts",
    All: "Alle",
    None: "Ingen",
    Receiver: "Mottaker",
    "# Passes": "Ant. mottak",
    "Perfect %": "Perfekt %",
    "Positive %": "Positiv %",
    "Sideout %": "Sideout %",
    "FB Sideout %": "FB-sideout %",
    "Pass Hits": "Angrep etter mottak",
    "Pass Hit Kills": "Poeng etter mottak",
    "Pass Hit Errors": "Feil etter mottak",
    "Show Passes": "Vis mottak",
    "Show Attacks": "Vis angrep",
    "Error Pass": "Feilmottak",
    "1 Pass": "1-mottak",
    "2 Pass": "2-mottak",
    "Perfect Pass": "Perfekt mottak",
    "Successful SO": "Vellykket sideout",
    "First Ball SO": "Førsteball-sideout",
    "Unsuccessful SO": "Mislykket sideout",
    RECEIVES: "MOTTAK",
    Sets: "Sett",
    Rotations: "Rotasjoner",
    "Attack Combos": "Angrepskombinasjoner",
    Stages: "Faser",
    Display: "Visning",
    Setters: "Oppspillere",
    Attackers: "Angripere",
    Results: "Resultater",
    Source: "Kilde",
    Cones: "Soner",
    "Select None": "Velg ingen",
    "All Serves": "Alle server",
    "All Returns": "Alle mottak",
    Filters: "Filtre",
    "Reset all filters": "Tilbakestill alle filtre",
    "Show video clips of filtered attacks": "Vis videoklipp av filtrerte angrep",
    "Spikes for the selected player": "Angrep for den valgte spilleren",
    "Click on the zones to view video of attacks": "Klikk på sonene for å se video av angrep",
    "Expand All": "Utvid alle",
    "Collapse All": "Skjul alle",
    "Import Radar File": "Importer Radar-fil",
    "Select local video file": "Velg lokal videofil",
    "Save to database": "Lagre i database",
    "Load and play online video": "Last inn og spill av nettvideo",
    "Synchronise video with events": "Synkroniser video med hendelser",
    "Rewind 10 seconds": "Spol 10 sekunder tilbake",
    "Rewind 1 second": "Spol 1 sekund tilbake",
    "Forward 1 second": "1 sekund fram",
    "Forward 10 seconds": "10 sekunder fram",
    "Set timing for events": "Angi timing for hendelser",

    // Navigation
    Matches: "Kamper",
    Live: "Direkte",
    Import: "Importer",
    Playlists: "Spillelister",
    "New Match": "Ny kamp",
    // New Match / Match Coding gate
    "Match Coding": "Kampkoding",
    "Match Coding — Coming Soon": "Kampkoding — Kommer snart",
    "Live match coding is on the way. Here's a preview of what's coming.":
      "Live kampkoding er på vei. Her er en forhåndsvisning av det som kommer.",
    Settings: "Innstillinger",
    Language: "Språk",
    "Your profile": "Din profil",
    "Sign out": "Logg ut",
    "Translation Editor": "Oversettelseseditor",

    // Common actions
    Save: "Lagre",
    Cancel: "Avbryt",
    Delete: "Slett",
    Share: "Del",
    Edit: "Rediger",
    New: "Ny",
    Refresh: "Oppdater",
    Search: "Søk",
    Reset: "Tilbakestill",
    Close: "Lukk",
    "Sub for": "Bytt for",
    "No players available": "Ingen spillere tilgjengelig",
    "Show full roster": "Vis hele troppen",
    "Available only": "Kun tilgjengelige",
    "Maximum substitutions reached": "Maks antall bytter nådd",
    Back: "Tilbake",
    "Download PDF": "Last ned PDF",
    "Loading...": "Laster…",
    "Select All": "Velg alle",
    "Unselect All": "Fjern alle valg",
    Yes: "Ja",
    No: "Nei",
    Add: "Legg til",
    Load: "Last inn",
    Confirm: "Bekreft",
    Open: "Åpne",
    show: "vis",

    // Auth
    "Sign In": "Logg inn",
    "Sign Up": "Registrer deg",
    "Sign Out": "Logg ut",
    Email: "E-post",
    Password: "Passord",
    "Confirm Password": "Bekreft passord",
    "Forgot password?": "Glemt passord?",
    "Forgot Password": "Glemt passord",
    "Don't have an account?": "Har du ikke en konto?",
    "Sign up for free!": "Registrer deg gratis!",
    "All ready signed up?": "Allerede registrert?",
    "Error Signing In": "Feil ved innlogging",
    "Signed up successfully": "Registrering vellykket",
    "Error signing up": "Feil ved registrering",
    "Enter your email": "Skriv inn e-posten din",
    "Send Reset Email": "Send tilbakestillings-e-post",
    "Email was sent to": "E-post ble sendt til",
    "Could not send reset email":
      "Kunne ikke sende tilbakestillings-e-post",

    // Matches list
    "Team 1": "Lag 1",
    "Team 2": "Lag 2",
    Tournament: "Turnering",
    Scores: "Resultater",
    Date: "Dato",
    Actions: "Handlinger",
    Owner: "Eier",
    Description: "Beskrivelse",
    Comments: "Kommentarer",
    Tags: "Tags",
    "Add player": "Legg til spiller",
    "Add team": "Legg til lag",
    "Edit playlist": "Rediger spilleliste",
    "Merge Teams": "Slå sammen lag",
    "Roster differences detected between teams:": "Kaderulikheter oppdaget mellom lag:",
    "Review the roster in the next step before merging.": "Gjennomgå spillerstallen i neste trinn før sammenslåing.",
    "At least 2 team records must exist in your Teams list for the selected matches.": "Minst 2 lagposter må finnes i laglisten din for de valgte kampene.",
    "Merge...": "Slå sammen...",
    "Select 2 or more team records to merge into one.": "Velg 2 eller flere lagposter som skal slås sammen til én.",
    "Filter teams...": "Filtrer lag...",
    "teams selected": "lag valgt",
    "Select Teams": "Velg lag",
    "Reconcile Roster": "Tilpass spillerstall",
    "Confirm Merge": "Bekreft sammenslåing",
    "Merged team name": "Navn på sammenslått lag",
    "shirt number(s) have conflicting names — review below.": "nummer(e) har motstridende navn — sjekk nedenfor.",
    "Shirt": "Drakt",
    "players included": "spillere inkludert",
    "No players to reconcile.": "Ingen spillere å tilpasse.",
    "Use this name": "Bruk dette navnet",
    "Create merged team": "Opprett sammenslått lag",
    "with": "med",
    "duplicate team records": "duplikate lagposter",
    "Consolidate duplicate player records and reassign playlist links": "Slå sammen duplikate spillerposter og tildel spillelistelenker på nytt",
    "This cannot be undone.": "Dette kan ikke angres.",
    "Teams being merged:": "Lag som slås sammen:",
    "Step": "Trinn",
    "of": "av",
    "Merging...": "Slår sammen...",
    "Players & Teams": "Spillere & Lag",
    "No players or teams assigned.": "Ingen spillere eller lag er tilordnet.",
    "Search players...": "Søk spillere...",
    "Search teams...": "Søk lag...",
    "Saving...": "Lagrer...",
    "My Matches": "Mine kamper",
    "Shared Matches": "Delte kamper",
    "Search team...": "Søk lag…",
    "All Teams": "Alle lag",
    "Multi Match Reports": "Rapporter for flere kamper",
    "Delete match": "Slett kamp",
    "Remove from my shared matches": "Fjern fra mine delte kamper",
    "Share match": "Del kamp",
    "Edit match": "Rediger kamp",
    "Open video analysis": "Åpne videoanalyse",
    "Delete Match": "Slett kamp",
    "Are you sure you want to delete the selected match?":
      "Er du sikker på at du vil slette den valgte kampen?",
    "No matches selected.": "Ingen kamper valgt.",

    // New Match
    "← Back": "← Tilbake",
    "Team A (Home)": "Lag A (hjemme)",
    "Team B (Away)": "Lag B (borte)",
    "Select team…": "Velg lag…",
    "Tournament (optional)": "Turnering (valgfritt)",
    "Tournament / competition name": "Turnerings- / konkurransenavn",
    "Match date": "Kampdato",
    "Start Coding →": "Start koding →",

    // Reports
    Summary: "Sammendrag",
    "Box Score": "Statistikkoversikt",
    "Side-outs Report": "Side-out-rapport",
    "Serve Receives": "Mottak",
    "Attack Zones": "Angrepssoner",
    "Hitting Charts": "Angrepsdiagrammer",
    "Video Analysis": "Videoanalyse",
    Dashboard: "Dashbord",
    Charts: "Diagrammer",
    "AI Report": "AI-rapport",
    // AI Reports page
    "AI Reports": "AI-rapporter",
    "AI Reports — Coming Soon": "AI-rapporter — Kommer snart",
    "AI-powered match reports and deep analysis are on the way. Here are some sample reports to show what's coming.":
      "AI-drevne kamprapporter og dyp analyse er på vei. Her er noen eksempelrapporter som en forhåndsvisning.",
    "Preview not available.": "Forhåndsvisning er ikke tilgjengelig.",
    "Open sample": "Åpne eksempel",
    "No matches were loaded. Select one or more matches and try again.":
      "Ingen kamper ble lastet inn. Velg én eller flere kamper og prøv igjen.",
    "Attack Analysis (English)": "Angrepsanalyse (engelsk)",
    "Blocking Analysis (Vietnamese)": "Blokkanalyse (vietnamesisk)",
    "Opponent Tendencies (Japanese)": "Motstandertendenser (japansk)",
    // Deep AI analysis
    "Deep AI Analysis": "Dyp AI-analyse",
    "AI Deep Analysis": "AI-dybdeanalyse",
    "Load at least one match with coded events to generate the analysis.": "Last inn minst én kamp med kodede hendelser for å generere analysen.",
    "Run the deep analysis one section at a time to keep each request small. Edit or add questions below, then generate. Best used with the Anthropic (Claude) provider.":
      "Kjør dybdeanalysen én del om gangen for å holde hver forespørsel liten. Rediger eller legg til spørsmål nedenfor, og generer deretter. Brukes best med leverandøren Anthropic (Claude).",
    "Download CSV": "Last ned CSV",
    "Analysis section": "Analysedel",
    analysing: "analyserer",
    "All sections (uses the most credits)": "Alle deler (bruker mest kreditter)",
    "All sections": "Alle deler",
    Questions: "Spørsmål",
    "one per line, edit or add your own": "ett per linje, rediger eller legg til egne",
    "Reset to defaults": "Tilbakestill til standard",
    "Add a question…": "Legg til et spørsmål…",
    "Runs every section using the questions you've set for each. This sends one large request and uses the most credits.":
      "Kjører alle deler med spørsmålene du har angitt for hver. Dette sender én stor forespørsel og bruker mest kreditter.",
    "Generate full analysis": "Generer full analyse",
    "Generate section analysis": "Generer delanalyse",
    Serving: "Serve",
    Reception: "Mottak",
    Setting: "Oppspill",
    Attack: "Angrep",
    Blocking: "Blokk",
    "Defence / Digging": "Forsvar / Oppgraving",
    "Rotation Analysis": "Rotasjonsanalyse",
    "Opponent Tendencies (Scouting)": "Motstanderens tendenser (speiding)",
    "Overall Team Metrics": "Generelle lagstatistikker",
    "Serve efficiency (aces, errors, and pressured receptions forced).": "Serveeffektivitet (ess, feil og fremtvungne pressede mottak).",
    "Which servers are most effective, and against which reception zones?": "Hvilke servere er mest effektive, og mot hvilke mottakssoner?",
    "Which serve types (jump, float, jump-float) yield the best results?": "Hvilke servetyper (hopp, float, hoppfloat) gir de beste resultatene?",
    "Reception quality breakdown (perfect, positive, negative, error).": "Oppdeling av mottakskvalitet (perfekt, positiv, negativ, feil).",
    "Which players struggle most under jump serves vs. float serves?": "Hvilke spillere sliter mest mot hoppserver kontra floatserver?",
    "How does poor reception correlate with side-out efficiency?": "Hvordan henger dårlig mottak sammen med side-out-effektivitet?",
    "Set distribution by zone and by player.": "Fordeling av pasninger per sone og per spiller.",
    "How does the setter perform under pressure (bad-pass situations)?": "Hvordan presterer oppspilleren under press (situasjoner med dårlig mottak)?",
    "Tempo breakdown (quick, medium, high ball) and which yields the best attack outcome.": "Tempooppdeling (rask, medium, høy ball) og hvilken som gir best angrepsresultat.",
    "Kill percentage and error rate per player, per rotation, and per zone.": "Poengprosent og feilrate per spiller, per rotasjon og per sone.",
    "Which attack zones (pipe, cross, line) have the highest efficiency?": "Hvilke angrepssoner (pipe, diagonal, linje) har høyest effektivitet?",
    "How attack efficiency changes in transition vs. side-out.": "Hvordan angrepseffektiviteten endrer seg i overgang kontra side-out.",
    "Block-per-set rate per player and rotation.": "Blokker per sett per spiller og rotasjon.",
    "Which opposing attack zones are most successfully blocked?": "Hvilke av motstanderens angrepssoner blokkeres mest vellykket?",
    "How often blocks result in kills vs. touch-outs.": "Hvor ofte blokker fører til poeng kontra blokk-ut.",
    "Dig success rate per player and per court zone.": "Forsvarssuksessrate per spiller og per banesone.",
    "Which attack types (tip, roll, hard swing) are hardest to defend?": "Hvilke angrepstyper (lobb, rull, hardt slag) er vanskeligst å forsvare?",
    "How defensive efficiency affects transition attack success.": "Hvordan forsvarseffektivitet påvirker suksessen til overgangsangrep.",
    "Which rotations have the highest and lowest point differential?": "Hvilke rotasjoner har høyest og lavest poengdifferanse?",
    "Side-out and break-point percentages per rotation.": "Side-out- og breakpoint-prosenter per rotasjon.",
    "Rotation-specific vulnerabilities (e.g. a weak serving or passing rotation).": "Rotasjonsspesifikke svakheter (f.eks. en svak serve- eller mottaksrotasjon).",
    "The opponent's most frequently used attack zones and combinations.": "Motstanderens mest brukte angrepssoner og kombinasjoner.",
    "The opponent's go-to attackers in high-pressure situations.": "Motstanderens foretrukne angripere i pressede situasjoner.",
    "The serve-receive patterns the opponent favours.": "Mottaksmønstrene motstanderen foretrekker.",
    "Overall rally-win probability in different score situations.": "Samlet sannsynlighet for å vinne ballvekslingen i ulike stillingssituasjoner.",
    "How performance changes in sets 1–3 vs. sets 4–5 (fatigue/pressure effects).": "Hvordan prestasjonen endrer seg i sett 1–3 kontra sett 4–5 (tretthets-/presseffekter).",
    "The correlation between first-ball side-out percentage and match outcome.": "Sammenhengen mellom first-ball-side-out-prosent og kampresultat.",
    Report: "Rapport",

    // Import page
    "Load DVW file(s)...": "Last inn DVW-fil(er)…",
    "Load PSVB file(s)...": "Last inn PSVB-fil(er)…",
    "Load VSM file(s)...": "Last inn VSM-fil(er)…",
    "Load playlist file...": "Last inn spillelistefil…",
    "load one or more DataVolley files":
      "last inn én eller flere DataVolley-filer",
    "load one or more VBStats files":
      "last inn én eller flere VBStats-filer",
    "load one or more VolleyStation VSM files":
      "last inn én eller flere VolleyStation VSM-filer",
    "Convert DVW": "Konverter DVW",
    "VSM": "VSM",
    "Convert DVW to VSM...": "Konverter DVW til VSM…",
    "pick a DataVolley .dvw file to download as .vsm":
      "velg en DataVolley .dvw-fil å laste ned som .vsm",
    "Convert VSM to DVW...": "Konverter VSM til DVW…",
    "pick a VolleyStation .vsm file to download as .dvw":
      "velg en VolleyStation .vsm-fil å laste ned som .dvw",
    "Converting…": "Konverterer…",
    "Converted": "Konvertert",
    "Failed to convert": "Konvertering mislyktes",
    "Beta testers only — coming soon for everyone else.":
      "Kun for betatestere — kommer snart for alle.",
    "load a playlist": "last inn en spilleliste",
    "Use Shift-JIS Encoder": "Bruk Shift-JIS-koder",
    "Importing…": "Importerer…",
    "Save to Database?": "Lagre i database?",
    "Always save imports automatically": "Lagre alltid importer automatisk",
    "View Only": "Kun visning",
    "Save to Database": "Lagre i database",
    "Error saving to database": "Feil ved lagring i database",
    "Saved to database": "Lagret i database",
    "Saving to database…": "Lagrer i database…",
    "Upload them to the database now?":
      "Laste dem opp til databasen nå?",

    // Settings / Options
    Theme: "Tema",
    Options: "Alternativer",
    "Rotation naming convention": "Navnekonvensjon for rotasjoner",
    "Choose how rotations are labelled in reports and analysis.":
      "Velg hvordan rotasjoner merkes i rapporter og analyser.",
    "R1–R6 (rotation sequence)": "R1–R6 (rotasjonsrekkefølge)",
    "S1–S6 (setter court position)": "S1–S6 (oppspillerens baneposisjon)",
    "R1: setter in position 1, R2: setter in 6, R3: setter in 5, R4: setter in 4, R5: setter in 3, R6: setter in 2.":
      "R1: oppspiller på posisjon 1, R2: oppspiller på 6, R3: oppspiller på 5, R4: oppspiller på 4, R5: oppspiller på 3, R6: oppspiller på 2.",
    "S1: setter in 1, S2: setter in 2, S3: setter in 3, S4: setter in 4, S5: setter in 5, S6: setter in 6.":
      "S1: oppspiller på 1, S2: oppspiller på 2, S3: oppspiller på 3, S4: oppspiller på 4, S5: oppspiller på 5, S6: oppspiller på 6.",
    "Labels:": "Etiketter:",
    "Current theme:": "Gjeldende tema:",
    "Choose the language for the app interface.":
      "Velg språk for appgrensesnittet.",
    "Create your own language package or edit a community one, with AI-assisted translation.":
      "Lag din egen språkpakke eller rediger en fra fellesskapet, med AI-assistert oversettelse.",

    // About / Footer
    "Copyright © Perana Sports {year}. All rights reserved.":
      "Copyright © Perana Sports {year}. Med enerett.",
    "VBLive is free to use right now. However Perana Sports reserves the right to charge a subscription fee in due course.":
      "VBLive er gratis å bruke nå. Perana Sports forbeholder seg imidlertid retten til å ta en abonnementsavgift på et senere tidspunkt.",
    "Querries / suggestions are most welcomed.":
      "Spørsmål / forslag er hjertelig velkomne.",

    // Live
    "Please enter server name to search":
      "Skriv inn servernavn for å søke",
    "Broadcast Server": "Kringkastingsserver",
    "Enter DVMateLive Server Name": "Skriv inn DVMateLive-servernavn",
    LIVE: "DIREKTE",

    // Playlists list
    "Delete Playlist": "Slett spilleliste",
    "Are you sure you want to delete the selected playlist?":
      "Er du sikker på at du vil slette den valgte spillelisten?",
    "My Playlists": "Mine spillelister",
    "Shared Playlists": "Delte spillelister",
    "Tags:": "Tagger:",
    "Select...": "Velg…",
    "All Tags": "Alle tagger",

    // Share
    "User already added": "Bruker allerede lagt til",
    "Please enter a valid email address":
      "Skriv inn en gyldig e-postadresse",
    "Please select at least one user to share with":
      "Velg minst én bruker å dele med",
    "Not Shared": "Ikke delt",
    "Available for everyone": "Tilgjengelig for alle",
    "Available to selected users":
      "Tilgjengelig for utvalgte brukere",
    "Enter VBLive user email address":
      "Skriv inn e-postadressen til VBLive-brukeren",
    "Selected users:": "Valgte brukere:",
    "Save List": "Lagre liste",
    "Clear List": "Tøm liste",
    "Clear Users List": "Tøm brukerliste",
    "Do you really want to clear all users from list?":
      "Vil du virkelig fjerne alle brukere fra listen?",
    "Do you want to save changes before closing?":
      "Vil du lagre endringene før du lukker?",
    "Enter name to save list to:":
      "Skriv inn navn for å lagre listen:",
    "Enter name to save list to":
      "Skriv inn navn for å lagre listen",

    // AI panels
    "AI settings": "AI-innstillinger",
    "Setup needed": "Oppsett kreves",
    "Generate report": "Generer rapport",
    "Generate AI report": "Generer AI-rapport",
    "Generate general report": "Generer generell rapport",
    "Translate to…": "Oversett til…",
    "Follow-up questions": "Oppfølgingsspørsmål",
    "Ask a follow-up question…": "Still et oppfølgingsspørsmål…",
    "Ask a follow-up question": "Still et oppfølgingsspørsmål",
    "AI provider": "AI-leverandør",
    Model: "Modell",
    Hide: "Skjul",
    Show: "Vis",
    "Your key is stored only in this browser and sent directly to the selected provider.":
      "Nøkkelen din lagres bare i denne nettleseren og sendes direkte til den valgte leverandøren.",
    "Please enter your API key.": "Skriv inn API-nøkkelen din.",
    "Not enough data to analyse.":
      "Ikke nok data til å analysere.",
    "Select a player with recorded spikes first.":
      "Velg først en spiller med registrerte angrep.",
    Player: "Spiller",
    "Select a player…": "Velg en spiller…",
    "No players with recorded spikes in the selected matches.":
      "Ingen spillere med registrerte angrep i de valgte kampene.",
    "Analyse with AI": "Analyser med AI",
    "AI scouting report": "AI-speidingsrapport",
    "Q:": "Sp:",
    Ask: "Spør",
    "e.g. Which rotation should we target on serve?":
      "f.eks. Hvilken rotasjon bør vi angripe på serve?",
    "Saved questions:": "Lagrede spørsmål:",
    "Click to ask again": "Klikk for å spørre igjen",
    "Remove saved question": "Fjern lagret spørsmål",

    // AI language options
    Spanish: "Spansk",
    French: "Fransk",
    German: "Tysk",
    Italian: "Italiensk",
    Portuguese: "Portugisisk",
    Dutch: "Nederlandsk",
    Polish: "Polsk",
    Russian: "Russisk",
    Turkish: "Tyrkisk",
    Arabic: "Arabisk",
    "Chinese (Simplified)": "Kinesisk (forenklet)",
    Japanese: "Japansk",
    Korean: "Koreansk",
    Vietnamese: "Vietnamesisk",
    Thai: "Thai",

    // Box score
    "Show Full Headers": "Vis fulle overskrifter",
    "Show Short Headers": "Vis korte overskrifter",
    "Show Less Players": "Vis færre spillere",
    "Show All Players": "Vis alle spillere",
    "Attack Kills": "Angrepspoeng",
    "Attack Errors": "Angrepsfeil",
    "Total Attempts": "Totalt antall forsøk",
    "Hitting Efficiency": "Angrepseffektivitet",
    "Serve Aces": "Serveess",
    "Serve Errors": "Servefeil",
    "Pass Errors": "Mottaksfeil",
    "% Perfect Passes": "% perfekte mottak",
    "Passing Average": "Mottaksgjennomsnitt",
    "Total Passes": "Totalt mottak",
    "Set Assists": "Oppspillsassist",
    Digs: "Forsvar",
    "Momentum Chart": "Momentumgraf",
    "Key event": "Nøkkelaksjon",
    "Break Point %": "Breakpoeng %",
    "First Ball Sideout %": "Førsteball-sideout %",
    "Sideout Transition %": "Sideout i overgang %",
    "FB Sideout Kills %": "Førsteball-angrepspoeng %",
    "Sideout Opposition Error %": "Sideout på motstanderfeil %",
    Plus: "Pluss",
    Minus: "Minus",
    Blocks: "Blokkpoeng",
    "Positive Serve %": "Positive server %",
    Sideout: "Sideout",
    "Break point": "Breakpoeng",
    "Ace / Kill": "Ess / Angrepspoeng",
    "In play": "I spill",
    Error: "Feil",
    "Sideout on a kill": "Sideout på angrepspoeng",
    "Sideout on an error": "Sideout på motstanderfeil",
    "Top half": "Øvre halvdel",
    "Bottom half": "Nedre halvdel",
    "The centre line steps toward the team that wins each point.": "Midtlinjen beveger seg mot laget som vinner hvert poeng.",
    "No point-by-point data available for this match.": "Ingen poeng-for-poeng-data tilgjengelig for denne kampen.",
    "No match data available.": "Ingen kampdata tilgjengelig.",
    "This report isn't supported on this browser yet — please use Safari.": "Denne rapporten støttes ikke i denne nettleseren ennå – bruk Safari.",
    "No video available for this match.": "Ingen video tilgjengelig for denne kampen.",
    passes: "mottak",
    "eff.": "eff.",
    Grade: "Karakter",
    LEFT: "VENSTRE",
    CENTRE: "MIDTEN",
    RIGHT: "HØYRE",
    SELECTED: "VALGT",
    Passers: "Mottakere",
    Calls: "Kall",
    "SIDE OUT": "SIDEOUT",
    TRANSITION: "OVERGANG",
    "No events in selection.": "Ingen hendelser i gjeldende utvalg.",
    "Serve Receives By Players": "Mottak per spiller",
    "Serve Receive By Zones": "Mottak per sone",
    "Dig Errors": "Forsvarsfeil",
    "Dig Average": "Forsvarsgjennomsnitt",
    "Block Solos": "Soloblokker",
    "Block Assists": "Blokkassist",
    "Block Errors": "Blokkfeil",
    "Points Scored": "Poeng scoret",
    TEAM: "LAG",

    // Sideout report
    "ALL ROWS": "ALLE RADER",
    "ROWS ": "RADER ",
    "ROW ": "RAD ",
    "FB Kills": "FB-angrepspoeng",
    "Opp. Serve Errors": "Servefeil mots.",
    "FB Opp. Errors": "FB-feil mots.",
    "Kills/Blocks": "Angrep/Blokker",
    "Opp. Errors": "Feil mots.",
    Unsuccessful: "Mislykket",

    // Attack zones
    "No events in selected zone.":
      "Ingen hendelser i valgt sone.",
    "Show all attacks. Or click on a zone to show attacks in that zone.":
      "Vis alle angrep. Eller klikk på en sone for å vise angrep i den sonen.",
    "OPPONENTS (": "MOTSTANDERE (",

    // Video analysis filters
    "All Types": "Alle typer",
    Serve: "Serve",
    "Serve-Receive": "Mottak",
    Set: "Oppspill",
    "Setter's Call": "Oppspillerens kall",
    "Disable this skill": "Deaktiver denne ferdigheten",
    "Enable this skill": "Aktiver denne ferdigheten",
    "Teams & Players": "Lag og spillere",
    "Match State": "Kampstatus",
    "Custom Code": "Tilpasset kode",
    "Search... (comma-separated for multiple)": "Søk … (skill med komma for flere)",
    vs: "mot",
    "Rewind 60 seconds": "Spol 60 sekunder tilbake",
    "Rewind 1 frame": "Spol 1 bilde tilbake",
    "Forward 1 frame": "1 bilde fram",
    "Forward 60 seconds": "60 sekunder fram",
    "Previous Clip": "Forrige klipp",
    Replay: "Spill av på nytt",
    "Next Clip": "Neste klipp",
    Phases: "Faser",
    "Score Range": "Poengintervall",
    "Set Winner": "Settvinner",
    Type: "Type",
    Strength: "Styrke",
    "Setter Calls": "Oppspillerens kall",
    "Setter Call": "Oppspillerens kall",
    Freeball: "Friball",
    "Close (0-2)": "Jevnt (0-2)",
    "Moderate (3-5)": "Moderat (3-5)",
    "Lopsided (6+)": "Stor avstand (6+)",
    "Focus Team": "Fokuslag",
    Oppositions: "Motstandere",
    "Focus Team Won": "Fokuslag vant",
    "Oppositions Won": "Motstandere vant",
    "Advanced Filters": "Avanserte filtre",
    Players: "Spillere",
    Opponents: "Motstandere",
    "Event Type": "Hendelsestype",
    "Event Result": "Hendelsesresultat",
    "Attack Type": "Angrepstype",
    "Hit Type": "Slagtype",
    "Block Type": "Blokktype",
    "Attack Combo": "Angrepskombinasjon",
    Apply: "Bruk",
    Rotation: "Rotasjon",
    Skill: "Ferdighet",
    "Lead Time": "Ledetid",
    Duration: "Varighet",
    Timing: "Tidsinnstilling",
    "Go Live": "Gå til direkte",
    "Playback has fallen behind the live broadcast — click to jump to now.": "Avspillingen har havnet bak direktesendingen — klikk for å hoppe til nå.",
    "Video timing": "Videotidsinnstilling",
    "Default lead time (seconds before an event to start playback) and clip duration, per skill.": "Standard ledetid (sekunder før en hendelse for å starte avspillingen) og klippvarighet, per ferdighet.",
    Cloud: "Sky",
    Local: "Lokal",
    "Relay host": "Relévert",
    "IP or hostname of the machine running the local relay.": "IP-adresse eller vertsnavn til maskinen som kjører den lokale releen.",
    Spike: "Angrep",
    Block: "Blokk",
    Defence: "Forsvar",
    "All Results": "Alle resultater",
    Hard: "Hardt",
    Soft: "Mykt",
    Tip: "Lobb",
    "Not Specified": "Ikke spesifisert",
    "H - High": "H - Høy",
    "M- Medium": "M - Middels",
    "Q - Quick": "Q - Rask",
    "T - Tense": "T - Stram",
    "U - Super": "U - Super",
    "F - Fast": "F - Hurtig",
    "O - Other": "O - Annet",
    "B0 - No Block": "B0 - Ingen blokk",
    "B1 - 1 Block": "B1 - 1 blokk",
    "B2 - 2 Blocks": "B2 - 2 blokker",
    "B3 - 3 Blocks": "B3 - 3 blokker",
    "BS - Seams": "BS - Åpninger",

    // All filters panel
    ALL: "ALLE",
    NONE: "INGEN",

    // Playlist editor
    "Remove Item": "Fjern element",
    "Do you really want to remove the selected item?":
      "Vil du virkelig fjerne det valgte elementet?",
    "Description is required": "Beskrivelse er påkrevd",
    "Comments are required": "Kommentarer er påkrevd",
    "Tags are required": "Tagger er påkrevd",
    "Playlist saved successfully": "Spilleliste lagret",
    "Error saving playlist": "Feil ved lagring av spilleliste",

    // Language editor
    "My saved packages": "Mine lagrede pakker",
    "Loading shared languages…": "Laster delte språk…",
    "Community shared languages": "Språk delt av fellesskapet",
    by: "av",
    "based on": "basert på",
    "or create new": "eller lag ny",
    Use: "Bruk",
    "New Language Package": "Ny språkpakke",
    "Language Name": "Språknavn",
    "Language Code": "Språkkode",
    "Flag Emoji": "Flagg-emoji",
    "Base language (reference)": "Basisspråk (referanse)",
    "If not English, both English and the reference language will be shown.":
      "Hvis ikke engelsk, vises både engelsk og referansespråket.",
    "Start Editing": "Start redigering",
    "Save Key": "Lagre nøkkel",
    "AI key set": "AI-nøkkel angitt",
    "Set API key for AI translation":
      "Angi API-nøkkel for AI-oversettelse",
    "AI key ✓": "AI-nøkkel ✓",
    "Set AI key": "Angi AI-nøkkel",
    "AI: Translate All": "AI: Oversett alle",
    "Translating…": "Oversetter…",
    "AI translate section": "Oversett seksjon med AI",
    "Clear All": "Tøm alt",
    "Saving…": "Lagrer…",
    English: "Engelsk",
    new: "ny",
    "Enter translation…": "Skriv inn oversettelse…",
    "Share with other VBLive users?":
      "Dele med andre VBLive-brukere?",
    "Would you like to share the": "Vil du dele",
    "language package with all VBLive users? Anyone can then copy and build upon it.":
      "språkpakken med alle VBLive-brukere? Hvem som helst kan deretter kopiere og bygge videre på den.",
    "No, just save for me": "Nei, bare lagre for meg",
    "Yes, share it!": "Ja, del den!",
    "Language package saved and shared with all VBLive users!":
      "Språkpakke lagret og delt med alle VBLive-brukere!",
    "Language package saved!": "Språkpakke lagret!",
    "Clear all translations? This cannot be undone.":
      "Tømme alle oversettelser? Dette kan ikke angres.",

    // Additional UI phrases
    Teams: "Lag",
    "Shared By": "Delt av",
    "View Activity": "Visningsaktivitet",
    Viewer: "Seer",
    "Date / Time": "Dato / Tid",
    "No views recorded yet.": "Ingen visninger registrert ennå.",
    "Date / Scores": "Dato / Resultater",
    "Stats API source": "Statistikk-API-kilde",
    "load one or more AVC VIS files": "last inn én eller flere AVC VIS-filer",
    "Points to close a set": "Poeng for å avslutte et sett",
    "Require 2-point advantage": "Krev 2-poengs ledelse",
    Roster: "Tropp",
    Clone: "Klon",
    "Edit team": "Rediger lag",
    "New player": "Ny spiller",
    "Search player...": "Søk spiller…",
    "Match Centre": "Kampsenter",
    "Points Won": "Vunne poeng",
    "Opp. Error": "Motstanderfeil",
    Total: "Totalt",
    "Match Skills": "Kampferdigheter",
    Dig: "Forsvar",
    "Best Scorers": "Beste poengscorere",
    "Power Play": "Power Play",
    Match: "Kamp",
    "Player scouting": "Spillerscouting",
    "Team deep analysis": "Dyptgående laganalyse",
    "All Players": "Alle spillere",
    "All Sets": "Alle sett",
    "All Rotations": "Alle rotasjoner",
    "All Combos": "Alle kombinasjoner",
    "All Calls": "Alle kall",
    "All Events": "Alle hendelser",
    "Select all items": "Velg alle elementer",
    "Unselect all items": "Fjern valg av alle elementer",
    "Create play list": "Opprett spilleliste",
    "Point by Point": "Poeng for poeng",
    // i18n backfill pass — previously English-fallback-only keys
    "Access denied": "Tilgang nektet",
    "Aces": "Ess",
    "ACES": "ESS",
    "Add report": "Legg til rapport",
    "Admin": "Admin",
    "Admins": "Administratorer",
    "Advanced": "Avansert",
    "All set!": "Alt klart!",
    "Analyse team with AI": "Analyser laget med AI",
    "Are you sure you want to delete the selected play list?": "Er du sikker på at du vil slette den valgte spillelisten?",
    "Assign": "Tildel",
    "Assign playlist": "Tildel spilleliste",
    "Assigned": "Tildelt",
    "At least one admin is required.": "Minst én administrator kreves.",
    "Attack Combo / Setter Call": "Angrepskombinasjon / Settertilrop",
    "Attack diagram": "Angrepsdiagram",
    "Attacks": "Angrep",
    "ATTACKS": "ANGREP",
    "Auto-detected a custom set-closing score for": "Automatisk oppdaget egendefinert settavslutningspoeng for",
    "BLOCK": "BLOKK",
    "BLOCKS": "BLOKKER",
    "Beta testers": "Betatestere",
    "Clear filter": "Fjern filter",
    "Close Set": "Avslutt sett",
    "Close menu": "Lukk meny",
    "Code": "Kode",
    "Coding defaults & shortcuts": "Standardverdier og snarveier for coding",
    "Continue": "Fortsett",
    "Could not load match data. The match may no longer be available.": "Kunne ikke laste kampdata. Kampen er kanskje ikke lenger tilgjengelig.",
    "Could not load stats for these matches.": "Kunne ikke laste statistikk for disse kampene.",
    "Could not load this match.": "Kunne ikke laste denne kampen.",
    "Could not load this playlist.": "Kunne ikke laste denne spillelisten.",
    "Could not load this report.": "Kunne ikke laste denne rapporten.",
    "Could not save the report.": "Kunne ikke lagre rapporten.",
    "Could not share the report. Please try again.": "Kunne ikke dele rapporten. Prøv igjen.",
    "Court horizontal (net vertical)": "Bane horisontalt (nett vertikalt)",
    "Court vertical (net horizontal)": "Bane vertikalt (nett horisontalt)",
    "Create": "Opprett",
    "Create a new team from this roster": "Opprett et nytt lag fra denne troppen",
    "DIFFERENCE": "DIFFERANSE",
    "DV Code": "DV-kode",
    "Delete this report?": "Slette denne rapporten?",
    "Detected a custom set-closing score of": "Oppdaget et egendefinert settavslutningspoeng på",
    "Double Sub": "Dobbeltbytte",
    "Double Sub: now pick a bench player for the flashing OPPOSITE slot…": "Dobbeltbytte: velg nå en benkespiller til den blinkende opposite-plassen…",
    "Double Sub: pick a bench player for the flashing SETTER slot…": "Dobbeltbytte: velg en benkespiller til den blinkende setter-plassen…",
    "Double Substitution — swap the Setter and Opposite for two incoming players": "Dobbeltbytte — bytt ut setter og opposite med to innkommende spillere",
    "Drag to draw the ball path; pause briefly to mark a touch (reception/dig/block), release to finish.": "Dra for å tegne ballbanen; pause kort for å markere en berøring (mottak/forsvar/blokk), slipp for å fullføre.",
    "Draw": "Tegn",
    "Draw & Code": "Tegn og kod",
    "Draw & Code — draw ball paths on the court to code the rally": "Tegn og kod — tegn ballbaner på banen for å kode ballvekslingen",
    "Edit Lineup": "Rediger startoppstilling",
    "Edit Lineup — freely rearrange the starting lineup before the set begins": "Rediger startoppstilling — omorganiser fritt startoppstillingen før settet begynner",
    "Emails in this list can manage beta testers and other admins.": "E-poster på denne listen kan administrere betatestere og andre administratorer.",
    "Emails in this list get early access to AI Reports and other gated features. Changes take effect immediately.": "E-poster på denne listen får tidlig tilgang til AI-rapporter og andre begrensede funksjoner. Endringer trer i kraft umiddelbart.",
    "Enter a valid email address.": "Angi en gyldig e-postadresse.",
    "Exit fullscreen": "Avslutt fullskjerm",
    "Extended": "Utvidet",
    "Failed to generate PDF.": "Kunne ikke generere PDF.",
    "Failed to save changes.": "Kunne ikke lagre endringer.",
    "Filtered by": "Filtrert etter",
    "First name": "Fornavn",
    "For stronger security later, consider moving admin checks to a Firebase Auth custom claim instead of this email list.": "For sterkere sikkerhet senere, bør du vurdere å flytte administratorsjekker til et egendefinert Firebase Auth-krav i stedet for denne e-postlisten.",
    "Fullscreen": "Fullskjerm",
    "Generate an AI report from these matches": "Generer en AI-rapport fra disse kampene",
    "How often (in seconds) the events list is updated during a live session.": "Hvor ofte (i sekunder) hendelseslisten oppdateres under en direktesending.",
    "Last name": "Etternavn",
    "Leave blank to use standard rules (25 points, 15 for a 5th/deciding set)": "La stå tomt for å bruke standardregler (25 poeng, 15 i et avgjørende 5. sett)",
    "Live session refresh interval": "Oppdateringsintervall for direktesending",
    "Load VIS file(s)...": "Last inn VIS-fil(er)…",
    "Load this team's last-saved starting lineup (positions, setter and liberos)": "Last inn lagets sist lagrede startoppstilling (posisjoner, setter og liberoer)",
    "Loading…": "Laster…",
    "Manually end the current set at its current score, regardless of the configured winning score / 2-point advantage rule": "Avslutt det gjeldende settet manuelt ved nåværende stilling, uavhengig av konfigurert vinnerpoeng / 2-poengs ledelsesregel",
    "Migration finished with some issues": "Migrering fullført med noen problemer",
    "Most of your data was migrated, but some items could not be moved. You can retry now or continue and try again later.": "Det meste av dataene dine ble migrert, men noen elementer kunne ikke flyttes. Du kan prøve igjen nå eller fortsette og prøve igjen senere.",
    "Mute": "Demp",
    "New team": "Nytt lag",
    "Next": "Neste",
    "Nickname": "Kallenavn",
    "No Power Play": "Ingen Power Play",
    "No admins yet.": "Ingen administratorer ennå.",
    "No beta testers yet.": "Ingen betatestere ennå.",
    "No coded events to analyse.": "Ingen kodede hendelser å analysere.",
    "No coded events to export.": "Ingen kodede hendelser å eksportere.",
    "No matches.": "Ingen kamper.",
    "No players added yet.": "Ingen spillere lagt til ennå.",
    "No players recorded.": "Ingen spillere registrert.",
    "No players yet.": "Ingen spillere ennå.",
    "No playlists assigned.": "Ingen spillelister tildelt.",
    "No playlists found.": "Ingen spillelister funnet.",
    "No reports saved.": "Ingen rapporter lagret.",
    "No roster players available.": "Ingen troppsspillere tilgjengelig.",
    "No substitutions": "Ingen bytter",
    "No teams found.": "Ingen lag funnet.",
    "No teams yet.": "Ingen lag ennå.",
    "No teams.": "Ingen lag.",
    "Nominate Setter": "Utnevn setter",
    "Notify these users by email (optional)": "Varsle disse brukerne via e-post (valgfritt)",
    "OPP REC": "MOTST. MOTTAK",
    "OPP. ERRORS": "MOTST. FEIL",
    "Open / Download": "Åpne / Last ned",
    "Open local file": "Åpne lokal fil",
    "Open menu": "Åpne meny",
    "Opposition Passing — use the opposition's reception formation on the diagram": "Motstanders mottak — bruk motstanderens mottaksformasjon i diagrammet",
    "Paste the report (Markdown supported)…": "Lim inn rapporten (Markdown støttes)…",
    "Pause": "Pause",
    "Play": "Spill av",
    "Playing squad": "Kamptropp",
    "Playing squad is full. Remove a player from the squad before adding another.": "Kamptroppen er full. Fjern en spiller fra troppen før du legger til en til.",
    "Please sign in to save reports.": "Logg inn for å lagre rapporter.",
    "Power Play Only": "Kun Power Play",
    "Record a team error (no player) — edit later to add the player and event type": "Registrer en lagfeil (uten spiller) — rediger senere for å legge til spiller og hendelsestype",
    "Record at least one event before making substitutions": "Registrer minst én hendelse før du gjør bytter",
    "Redo": "Gjør om",
    "Remove": "Fjern",
    "Remove share": "Fjern deling",
    "Remove yourself as admin? You will immediately lose access to this page.": "Fjerne deg selv som administrator? Du vil umiddelbart miste tilgang til denne siden.",
    "Report saved and shared.": "Rapport lagret og delt.",
    "Report saved to the player's profile.": "Rapport lagret i spillerprofilen.",
    "Report title": "Rapporttittel",
    "Reports": "Rapporter",
    "Result": "Resultat",
    "Retry": "Prøv igjen",
    "Return to live coding": "Tilbake til live-coding",
    "Rotation is not permitted while making substitutions": "Rotasjon er ikke tillatt mens du gjør bytter",
    "SERVE": "SERVE",
    "SETS": "SETT",
    "Save & Share": "Lagre og del",
    "Save this report so you can share it with other users": "Lagre denne rapporten slik at du kan dele den med andre brukere",
    "Save this report to the player's profile": "Lagre denne rapporten i spillerprofilen",
    "Save to player": "Lagre til spiller",
    "Saved lineup": "Lagret oppstilling",
    "Scroll, drag or swipe to scrub": "Rull, dra eller sveip for å spole",
    "Search existing players...": "Søk etter eksisterende spillere…",
    "Search playlists…": "Søk i spillelister…",
    "Select a match video to view while entering lineups.": "Velg en kampvideo å se mens du legger inn oppstillinger.",
    "Select a player.": "Velg en spiller.",
    "Select a team.": "Velg et lag.",
    "Setting up your account": "Setter opp kontoen din",
    "Shaded rows are played under a Power Play — HP/AP marks the calling team, bold gold scores are bonus (double) points.": "Skraverte rader ble spilt under en Power Play — HP/AP markerer laget som ropte den, fete gullfargede poeng er bonus-(dobbelt)poeng.",
    "Shared by": "Delt av",
    "Shared with me": "Delt med meg",
    "Show help": "Vis hjelp",
    "Show public playlists": "Vis offentlige spillelister",
    "Standard": "Standard",
    "Substitutions made — tap to view": "Bytter gjort — trykk for å vise",
    "Tap the blocking player.": "Trykk på blokkerende spiller.",
    "Team colour": "Lagfarge",
    "Team name": "Lagnavn",
    "That email is already in the list.": "Den e-posten er allerede på listen.",
    "The report is too large to save. Try a single match or fewer sets.": "Rapporten er for stor til å lagres. Prøv én kamp eller færre sett.",
    "This match has no Power Play calls.": "Denne kampen har ingen Power Play-rop.",
    "This player has no name to save the report against.": "Denne spilleren har ikke et navn å lagre rapporten mot.",
    "This team has no designated setter. Pick the player who is setting.": "Dette laget har ingen utpekt setter. Velg spilleren som setter.",
    "Toggle video player": "Veksle videospiller",
    "Unmute": "Fjern demping",
    "Untitled playlist": "Navnløs spilleliste",
    "Video URL…": "Video-URL…",
    "We're moving your data to a faster, more reliable home. This happens only once.": "Vi flytter dataene dine til et raskere, mer pålitelig sted. Dette skjer bare én gang.",
    "You don't have permission to view this page.": "Du har ikke tillatelse til å se denne siden.",
    "Your data has been migrated successfully.": "Dataene dine er migrert.",
    "Your teams and players will appear here after your data is migrated.": "Lagene og spillerne dine vises her etter at dataene dine er migrert.",
    "bonus point": "bonuspoeng",
    "bonus points": "bonuspoeng",
    "event": "hendelse",
    "events": "hendelser",
    "file(s).": "fil(er).",
    "is not on this team's roster.": "er ikke i troppen til dette laget.",
    "loaded": "lastet",
    "matches": "kamper",
    "more": "flere",
    "players": "spillere",
    "points (no value was entered) — applied automatically.": "poeng (ingen verdi ble angitt) — brukt automatisk.",
    "roster": "tropp",
    "seconds": "sekunder",
    "selected": "valgt",
    "session(s)?": "økt(er)?",
    "showing": "viser",
    "tournaments": "turneringer",
    "…and": "…og",
    "● marks which team served the point.": "● markerer hvilket lag som serverte poenget.",
    // i18n backfill pass (Catapult + misc)
    "Centimetres (cm)": "Centimeter (cm)",
    "Inches (in)": "Tommer (in)",
    "Jump height units": "Enheter for hoppehøyde",
    "Choose how Catapult jump heights are displayed and filtered.": "Velg hvordan Catapult-hoppehøyder vises og filtreres.",
    "Select Catapult Focus file...": "Velg Catapult Focus-fil…",
    "Select a Catapult Focus CSV export to view jump data for this match.": "Velg en Catapult Focus CSV-eksport for å se hoppdata for denne kampen.",
    "Loaded from saved match data": "Lastet fra lagrede kampdata",
    "No jump data found in this file.": "Fant ingen hoppdata i denne filen.",
    "Could not read this file. Please check it is a Catapult Focus CSV export.": "Kunne ikke lese denne filen. Kontroller at det er en Catapult Focus CSV-eksport.",
    "Could not save jump data with this match.": "Kunne ikke lagre hoppdata med denne kampen.",
    "Jump data saved with this match.": "Hoppdata lagret med denne kampen.",
    "Show players with no match stats": "Vis spillere uten kampstatistikk",
    "hidden": "skjult",
    "jumps": "hopp",
    "periods": "perioder",
    "Sync offset": "Synkroniseringsforskyvning",
    "matched": "samsvarende",
    "match events now show jump height/duration in Video Analysis": "kamphendelser viser nå hoppehøyde/-varighet i Videoanalyse",
    "Team": "Lag",
    "Team A": "Lag A",
    "Team B": "Lag B",
    "Which team is this Catapult file for?": "Hvilket lag gjelder denne Catapult-filen for?",
    "We couldn't confidently match the players in this Focus file to one team. Please select which team it belongs to.": "Vi kunne ikke sikkert matche spillerne i denne Focus-filen til ett lag. Velg hvilket lag den tilhører.",
    "This match has no video attached yet.": "Denne kampen har ingen video lagt til ennå.",
    "No synced jump events found for this player.": "Fant ingen synkroniserte hoppehendelser for denne spilleren.",
    "No match loaded.": "Ingen kamp lastet inn.",
    "avg": "snitt",
    "max": "maks.",
    "Sync": "Synkronisering",
    "Jump Report": "Hopperapport",
    "Watch Video": "Se video",
    "Save PDF": "Lagre PDF",
    "Generated by VBLive": "Generert av VBLive",
    "Deep analysis of {team} from ball-by-ball event data, covering serving, reception, setting, attack, blocking, defence, rotations and opponent tendencies. Best used with the Anthropic (Claude) provider.": "Dyptgående analyse av {team} basert på ball-for-ball-hendelsesdata, som dekker serve, mottak, opplegg, angrep, blokkering, forsvar, rotasjoner og motstandertendenser. Best brukt med Anthropic (Claude)-leverandøren.",
    "teams": "lag",
  },

  ru: {
    // Information page
    Information: "Информация",
    "Release notes": "Примечания к выпуску",
    "Contact us": "Связаться с нами",
    "Questions, feedback or suggestions are most welcome.": "Вопросы, отзывы и предложения всегда приветствуются.",
    // DV match coding
    Lineups: "Составы",
    Point: "Очко",
    Video: "Видео",
    Defaults: "Значения по умолчанию",
    "[team][NN][skill][type?][eval][zoneFrom?[szFrom?][zoneTo?[szTo?]]]": "[команда][NN][действие][тип?][оценка][зонаОт?[пзОт?][зонаДо?[пзДо?]]]",
    Vertical: "Вертикально",
    Horizontal: "Горизонтально",
    "Swap sides": "Поменять стороны",
    Subs: "Замены",
    Substitutions: "Замены",
    Court: "Площадка",
    Home: "Дома",
    Away: "В гостях",
    Games: "Партии",
    Enter: "Ввод",
    Undo: "Отменить",
    "Match saved": "Матч сохранён",
    "Please sign in to save this match.": "Войдите, чтобы сохранить этот матч.",
    "Nothing to save yet.": "Пока нечего сохранять.",
    "Could not save the match. Please try again.": "Не удалось сохранить матч. Попробуйте ещё раз.",
    Discard: "Отменить изменения",
    "Resumed autosaved coding": "Возобновлено автосохранённое кодирование",
    "Changes discarded": "Изменения отменены",
    "Discard all changes and revert to the starting point?":
      "Отменить все изменения и вернуться к исходной точке?",
    Clear: "Очистить",
    "Prev set": "Предыдущая партия",
    "Next set": "Следующая партия",
    "Match Setup": "Настройка матча",
    "First serve": "Первая подача",
    "Start Match": "Начать матч",
    "Resume Match": "Продолжить матч",
    Skip: "Пропустить",
    "Select an event then drag to draw ball path": "Выберите событие, затем перетащите, чтобы нарисовать траекторию мяча",
    "Drag on court to set ball path": "Перетащите по площадке, чтобы задать траекторию мяча",
    "No events yet – enter a DV code above and press Enter.": "Событий пока нет — введите код DV выше и нажмите Ввод.",
    "DataVolley code reference": "Справочник кодов DataVolley",
    Format: "Формат",
    Skills: "Действия",
    Evaluation: "Оценка",
    "Skill / type omission": "Пропуск действия / типа",
    "Skill and/or type may be omitted; defaults fill in the rest.": "Действие и/или тип можно опустить; остальное заполняется значениями по умолчанию.",
    "Dot-coding (S→R and A→B pairs)": "Кодирование точкой (пары S→R и A→B)",
    "Chaining & aliases": "Цепочки и псевдонимы",
    "team:": "команда:",
    "omit → defaults to A": "пропустить → по умолчанию A",
    "NN: 1 or 2 digit jersey, e.g. 1, 01, 12": "NN: 1- или 2-значный номер, напр. 1, 01, 12",
    "sub-zone: a b c d appended to zone digit, e.g. 5b or 5b1a": "подзона: a b c d добавляется к цифре зоны, напр. 5b или 5b1a",
    "S Serve · R Reception · E Set · A Attack · B Block · D Dig · F Freeball": "S Подача · R Приём · E Передача · A Атака · B Блок · D Защита · F Свободный мяч",
    "# kill/ace · + positive · ! ok · - negative · / overpass · = error": "# очко/эйс · + позитивно · ! нормально · - негативно · / перевод · = ошибка",
    "E.g.": "Напр.",
    "serve with skill=A (default), type=H, eval=+, zone=5.": "подача со skill=A (по умолчанию), type=H, eval=+, zone=5.",
    "type-only (no skill marker), defaults skill from context.": "только тип (без маркера действия), действие по умолчанию из контекста.",
    "serve coded, reception auto-linked.": "подача закодирована, приём связан автоматически.",
    "attack coded, block auto-linked.": "атака закодирована, блок связан автоматически.",
    "Dot connects the reception eval to the corresponding serve eval via the serve→receive mapping.": "Точка связывает оценку приёма с соответствующей оценкой подачи через сопоставление подача→приём.",
    "two events in one string.": "два события в одной строке.",
    "manual point A/B.": "ручное очко A/B.",
    "serve ace/error.": "эйс/ошибка подачи.",
    "serve with eval.": "подача с оценкой.",
    // Stats, sideouts, filters & video tooltips
    "Block Kills": "Очки на блоке",
    "Side Out Percentage": "Процент выигрыша на приёме",
    "All First Ball": "Все первые мячи",
    "First Ball Kills": "Очки с первого мяча",
    "Good Serve": "Хорошая подача",
    "Break Points": "Очки на брейке",
    Game: "Партия",
    Row: "Позиция",
    "First Ball Sideouts": "Выигрыш на приёме с первого мяча",
    "All Sideouts": "Все выигрыши на приёме",
    All: "Все",
    None: "Нет",
    Receiver: "Принимающий",
    "# Passes": "Кол-во приёмов",
    "Perfect %": "% идеальных",
    "Positive %": "% позитивных",
    "Sideout %": "% приёма",
    "FB Sideout %": "% приёма (1-й мяч)",
    "Pass Hits": "Атаки после приёма",
    "Pass Hit Kills": "Очки после приёма",
    "Pass Hit Errors": "Ошибки после приёма",
    "Show Passes": "Показать приёмы",
    "Show Attacks": "Показать атаки",
    "Error Pass": "Ошибка приёма",
    "1 Pass": "Приём 1",
    "2 Pass": "Приём 2",
    "Perfect Pass": "Идеальный приём",
    "Successful SO": "Успешный приём-розыгрыш",
    "First Ball SO": "Приём с первого мяча",
    "Unsuccessful SO": "Неудачный приём-розыгрыш",
    RECEIVES: "ПРИЁМЫ",
    Sets: "Партии",
    Rotations: "Расстановки",
    "Attack Combos": "Комбинации атаки",
    Stages: "Фазы",
    Display: "Отображение",
    Setters: "Связующие",
    Attackers: "Нападающие",
    Results: "Результаты",
    Source: "Источник",
    Cones: "Зоны",
    "Select None": "Снять выбор",
    "All Serves": "Все подачи",
    "All Returns": "Все приёмы",
    Filters: "Фильтры",
    "Reset all filters": "Сбросить все фильтры",
    "Show video clips of filtered attacks": "Показать видеоклипы отфильтрованных атак",
    "Spikes for the selected player": "Атаки выбранного игрока",
    "Click on the zones to view video of attacks": "Нажмите на зоны, чтобы посмотреть видео атак",
    "Expand All": "Развернуть всё",
    "Collapse All": "Свернуть всё",
    "Import Radar File": "Импортировать файл Radar",
    "Select local video file": "Выбрать локальный видеофайл",
    "Save to database": "Сохранить в базу данных",
    "Load and play online video": "Загрузить и воспроизвести онлайн-видео",
    "Synchronise video with events": "Синхронизировать видео с событиями",
    "Rewind 10 seconds": "Назад на 10 секунд",
    "Rewind 1 second": "Назад на 1 секунду",
    "Forward 1 second": "Вперёд на 1 секунду",
    "Forward 10 seconds": "Вперёд на 10 секунд",
    "Set timing for events": "Задать тайминг для событий",

    // Navigation
    Matches: "Матчи",
    Live: "Прямой эфир",
    Import: "Импорт",
    Playlists: "Плейлисты",
    "New Match": "Новый матч",
    // New Match / Match Coding gate
    "Match Coding": "Кодирование матча",
    "Match Coding — Coming Soon": "Кодирование матча — Скоро",
    "Live match coding is on the way. Here's a preview of what's coming.":
      "Кодирование матчей в реальном времени уже в пути. Вот предварительный просмотр того, что вас ждёт.",
    Settings: "Настройки",
    Language: "Язык",
    "Your profile": "Ваш профиль",
    "Sign out": "Выйти",
    "Translation Editor": "Редактор переводов",

    // Common actions
    Save: "Сохранить",
    Cancel: "Отмена",
    Delete: "Удалить",
    Share: "Поделиться",
    Edit: "Редактировать",
    New: "Новый",
    Refresh: "Обновить",
    Search: "Поиск",
    Reset: "Сбросить",
    Close: "Закрыть",
    "Sub for": "Замена для",
    "No players available": "Нет доступных игроков",
    "Show full roster": "Показать весь состав",
    "Available only": "Только доступные",
    "Maximum substitutions reached": "Достигнут максимум замен",
    Back: "Назад",
    "Download PDF": "Скачать PDF",
    "Loading...": "Загрузка…",
    "Select All": "Выбрать всё",
    "Unselect All": "Снять выделение",
    Yes: "Да",
    No: "Нет",
    Add: "Добавить",
    Load: "Загрузить",
    Confirm: "Подтвердить",
    Open: "Открыть",
    show: "показать",

    // Auth
    "Sign In": "Войти",
    "Sign Up": "Зарегистрироваться",
    "Sign Out": "Выйти",
    Email: "Эл. почта",
    Password: "Пароль",
    "Confirm Password": "Подтвердите пароль",
    "Forgot password?": "Забыли пароль?",
    "Forgot Password": "Забыли пароль",
    "Don't have an account?": "Нет учётной записи?",
    "Sign up for free!": "Зарегистрируйтесь бесплатно!",
    "All ready signed up?": "Уже зарегистрированы?",
    "Error Signing In": "Ошибка входа",
    "Signed up successfully": "Регистрация прошла успешно",
    "Error signing up": "Ошибка регистрации",
    "Enter your email": "Введите вашу эл. почту",
    "Send Reset Email": "Отправить письмо для сброса",
    "Email was sent to": "Письмо отправлено на",
    "Could not send reset email":
      "Не удалось отправить письмо для сброса",

    // Matches list
    "Team 1": "Команда 1",
    "Team 2": "Команда 2",
    Tournament: "Турнир",
    Scores: "Счёт",
    Date: "Дата",
    Actions: "Действия",
    Owner: "Владелец",
    Description: "Описание",
    Comments: "Комментарии",
    Tags: "Теги",
    "Add player": "Добавить игрока",
    "Add team": "Добавить команду",
    "Edit playlist": "Редактировать плейлист",
    "Merge Teams": "Объединить команды",
    "Roster differences detected between teams:": "Обнаружены различия в составах команд:",
    "Review the roster in the next step before merging.": "Проверьте состав на следующем шаге перед объединением.",
    "At least 2 team records must exist in your Teams list for the selected matches.": "В вашем списке команд должно быть не менее 2 записей для выбранных матчей.",
    "Merge...": "Объединить...",
    "Select 2 or more team records to merge into one.": "Выберите 2 или более записей команд для объединения.",
    "Filter teams...": "Фильтр команд...",
    "teams selected": "команды выбраны",
    "Select Teams": "Выбрать команды",
    "Reconcile Roster": "Согласовать состав",
    "Confirm Merge": "Подтвердить объединение",
    "Merged team name": "Название объединённой команды",
    "shirt number(s) have conflicting names — review below.": "номер(а) имеют конфликтующие имена — проверьте ниже.",
    "Shirt": "Номер",
    "players included": "игроков включено",
    "No players to reconcile.": "Нет игроков для согласования.",
    "Use this name": "Использовать это имя",
    "Create merged team": "Создать объединённую команду",
    "with": "с",
    "duplicate team records": "дублирующихся записей команд",
    "Consolidate duplicate player records and reassign playlist links": "Объединить дубликаты игроков и переназначить ссылки плейлистов",
    "This cannot be undone.": "Это действие нельзя отменить.",
    "Teams being merged:": "Объединяемые команды:",
    "Step": "Шаг",
    "of": "из",
    "Merging...": "Объединение...",
    "Players & Teams": "Игроки и команды",
    "No players or teams assigned.": "Игроки или команды не назначены.",
    "Search players...": "Поиск игроков...",
    "Search teams...": "Поиск команд...",
    "Saving...": "Сохранение...",
    "My Matches": "Мои матчи",
    "Shared Matches": "Общие матчи",
    "Search team...": "Поиск команды…",
    "All Teams": "Все команды",
    "Multi Match Reports": "Отчёты по нескольким матчам",
    "Delete match": "Удалить матч",
    "Remove from my shared matches": "Убрать из моих общих матчей",
    "Share match": "Поделиться матчем",
    "Edit match": "Редактировать матч",
    "Open video analysis": "Открыть видеоанализ",
    "Delete Match": "Удалить матч",
    "Are you sure you want to delete the selected match?":
      "Вы уверены, что хотите удалить выбранный матч?",
    "No matches selected.": "Матчи не выбраны.",

    // New Match
    "← Back": "← Назад",
    "Team A (Home)": "Команда A (дома)",
    "Team B (Away)": "Команда B (в гостях)",
    "Select team…": "Выберите команду…",
    "Tournament (optional)": "Турнир (необязательно)",
    "Tournament / competition name": "Название турнира / соревнования",
    "Match date": "Дата матча",
    "Start Coding →": "Начать кодирование →",

    // Reports
    Summary: "Сводка",
    "Box Score": "Таблица статистики",
    "Side-outs Report": "Отчёт по сайд-аутам",
    "Serve Receives": "Приёмы",
    "Attack Zones": "Зоны атаки",
    "Hitting Charts": "Графики атак",
    "Video Analysis": "Видеоанализ",
    Dashboard: "Панель",
    Charts: "Графики",
    "AI Report": "ИИ-отчёт",
    // AI Reports page
    "AI Reports": "ИИ-отчёты",
    "AI Reports — Coming Soon": "ИИ-отчёты — Скоро",
    "AI-powered match reports and deep analysis are on the way. Here are some sample reports to show what's coming.":
      "Отчёты о матчах и глубокий анализ на основе ИИ уже в пути. Вот несколько образцов отчётов для предварительного просмотра.",
    "Preview not available.": "Предпросмотр недоступен.",
    "Open sample": "Открыть образец",
    "No matches were loaded. Select one or more matches and try again.":
      "Матчи не загружены. Выберите один или несколько матчей и повторите попытку.",
    "Attack Analysis (English)": "Анализ атаки (английский)",
    "Blocking Analysis (Vietnamese)": "Анализ блока (вьетнамский)",
    "Opponent Tendencies (Japanese)": "Тенденции соперника (японский)",
    // Deep AI analysis
    "Deep AI Analysis": "Глубокий ИИ-анализ",
    "AI Deep Analysis": "Глубокий анализ ИИ",
    "Load at least one match with coded events to generate the analysis.": "Загрузите хотя бы один матч с закодированными действиями, чтобы создать анализ.",
    "Run the deep analysis one section at a time to keep each request small. Edit or add questions below, then generate. Best used with the Anthropic (Claude) provider.":
      "Запускайте глубокий анализ по одному разделу, чтобы каждый запрос оставался небольшим. Отредактируйте или добавьте вопросы ниже, затем сгенерируйте. Лучше всего использовать с провайдером Anthropic (Claude).",
    "Download CSV": "Скачать CSV",
    "Analysis section": "Раздел анализа",
    analysing: "анализ",
    "All sections (uses the most credits)": "Все разделы (расходует больше всего кредитов)",
    "All sections": "Все разделы",
    Questions: "Вопросы",
    "one per line, edit or add your own": "по одному в строке, измените или добавьте свои",
    "Reset to defaults": "Сбросить по умолчанию",
    "Add a question…": "Добавить вопрос…",
    "Runs every section using the questions you've set for each. This sends one large request and uses the most credits.":
      "Запускает все разделы с вопросами, заданными для каждого. Отправляет один большой запрос и расходует больше всего кредитов.",
    "Generate full analysis": "Сгенерировать полный анализ",
    "Generate section analysis": "Сгенерировать анализ раздела",
    Serving: "Подача",
    Reception: "Приём",
    Setting: "Передача",
    Attack: "Атака",
    Blocking: "Блок",
    "Defence / Digging": "Защита / Приём в защите",
    "Rotation Analysis": "Анализ расстановок",
    "Opponent Tendencies (Scouting)": "Тенденции соперника (скаутинг)",
    "Overall Team Metrics": "Общие показатели команды",
    "Serve efficiency (aces, errors, and pressured receptions forced).": "Эффективность подачи (эйсы, ошибки и вынужденный сложный приём).",
    "Which servers are most effective, and against which reception zones?": "Какие подающие наиболее эффективны и против каких зон приёма?",
    "Which serve types (jump, float, jump-float) yield the best results?": "Какие типы подачи (силовая в прыжке, планирующая, планирующая в прыжке) дают лучший результат?",
    "Reception quality breakdown (perfect, positive, negative, error).": "Разбивка качества приёма (идеальный, позитивный, негативный, ошибка).",
    "Which players struggle most under jump serves vs. float serves?": "Каким игрокам сложнее всего принимать силовые подачи в прыжке по сравнению с планирующими?",
    "How does poor reception correlate with side-out efficiency?": "Как плохой приём связан с эффективностью съёма подачи?",
    "Set distribution by zone and by player.": "Распределение передач по зонам и игрокам.",
    "How does the setter perform under pressure (bad-pass situations)?": "Как связующий действует под давлением (при плохом приёме)?",
    "Tempo breakdown (quick, medium, high ball) and which yields the best attack outcome.": "Разбивка по темпу (быстрый, средний, высокий мяч) и какой даёт лучший результат в атаке.",
    "Kill percentage and error rate per player, per rotation, and per zone.": "Процент выигранных атак и доля ошибок по игрокам, расстановкам и зонам.",
    "Which attack zones (pipe, cross, line) have the highest efficiency?": "Какие зоны атаки (пайп, по диагонали, по линии) наиболее эффективны?",
    "How attack efficiency changes in transition vs. side-out.": "Как эффективность атаки меняется в доигровке по сравнению со съёмом подачи.",
    "Block-per-set rate per player and rotation.": "Количество блоков за партию по игрокам и расстановкам.",
    "Which opposing attack zones are most successfully blocked?": "Какие зоны атаки соперника блокируются наиболее успешно?",
    "How often blocks result in kills vs. touch-outs.": "Как часто блоки приносят очко по сравнению с отскоком в аут.",
    "Dig success rate per player and per court zone.": "Эффективность защиты по игрокам и зонам площадки.",
    "Which attack types (tip, roll, hard swing) are hardest to defend?": "Какие типы атаки (скидка, обводка, сильный удар) сложнее всего защищать?",
    "How defensive efficiency affects transition attack success.": "Как эффективность защиты влияет на успех атаки в доигровке.",
    "Which rotations have the highest and lowest point differential?": "У каких расстановок самая высокая и самая низкая разница очков?",
    "Side-out and break-point percentages per rotation.": "Проценты съёма и брейк-пойнта по расстановкам.",
    "Rotation-specific vulnerabilities (e.g. a weak serving or passing rotation).": "Слабые места отдельных расстановок (например, слабая расстановка на подаче или приёме).",
    "The opponent's most frequently used attack zones and combinations.": "Наиболее часто используемые соперником зоны атаки и комбинации.",
    "The opponent's go-to attackers in high-pressure situations.": "Основные атакующие соперника в напряжённых ситуациях.",
    "The serve-receive patterns the opponent favours.": "Предпочитаемые соперником схемы приёма подачи.",
    "Overall rally-win probability in different score situations.": "Общая вероятность выиграть розыгрыш в разных счётных ситуациях.",
    "How performance changes in sets 1–3 vs. sets 4–5 (fatigue/pressure effects).": "Как меняется игра в партиях 1–3 по сравнению с партиями 4–5 (эффекты усталости/давления).",
    "The correlation between first-ball side-out percentage and match outcome.": "Связь между процентом съёма с первого мяча и исходом матча.",
    Report: "Отчёт",

    // Import page
    "Load DVW file(s)...": "Загрузить файл(ы) DVW…",
    "Load PSVB file(s)...": "Загрузить файл(ы) PSVB…",
    "Load VSM file(s)...": "Загрузить файл(ы) VSM…",
    "Load playlist file...": "Загрузить файл плейлиста…",
    "load one or more DataVolley files":
      "загрузить один или несколько файлов DataVolley",
    "load one or more VBStats files":
      "загрузить один или несколько файлов VBStats",
    "load one or more VolleyStation VSM files":
      "загрузить один или несколько файлов VolleyStation VSM",
    "Convert DVW": "Конвертировать DVW",
    "VSM": "VSM",
    "Convert DVW to VSM...": "Конвертировать DVW в VSM…",
    "pick a DataVolley .dvw file to download as .vsm":
      "выбрать файл DataVolley .dvw для скачивания как .vsm",
    "Convert VSM to DVW...": "Конвертировать VSM в DVW…",
    "pick a VolleyStation .vsm file to download as .dvw":
      "выбрать файл VolleyStation .vsm для скачивания как .dvw",
    "Converting…": "Конвертация…",
    "Converted": "Преобразовано",
    "Failed to convert": "Не удалось преобразовать",
    "Beta testers only — coming soon for everyone else.":
      "Только для бета-тестеров — скоро будет доступно всем.",
    "load a playlist": "загрузить плейлист",
    "Use Shift-JIS Encoder": "Использовать кодировщик Shift-JIS",
    "Importing…": "Импорт…",
    "Save to Database?": "Сохранить в базе данных?",
    "Always save imports automatically": "Всегда автоматически сохранять импорт",
    "View Only": "Только просмотр",
    "Save to Database": "Сохранить в базе данных",
    "Error saving to database": "Ошибка сохранения в базе данных",
    "Saved to database": "Сохранено в базе данных",
    "Saving to database…": "Сохранение в базе данных…",
    "Upload them to the database now?":
      "Загрузить их в базу данных сейчас?",

    // Settings / Options
    Theme: "Тема",
    Options: "Параметры",
    "Rotation naming convention": "Соглашение об именовании расстановок",
    "Choose how rotations are labelled in reports and analysis.":
      "Выберите, как расстановки обозначаются в отчётах и анализе.",
    "R1–R6 (rotation sequence)": "R1–R6 (последовательность расстановок)",
    "S1–S6 (setter court position)": "S1–S6 (позиция связующего)",
    "R1: setter in position 1, R2: setter in 6, R3: setter in 5, R4: setter in 4, R5: setter in 3, R6: setter in 2.":
      "R1: связующий на позиции 1, R2: связующий на 6, R3: связующий на 5, R4: связующий на 4, R5: связующий на 3, R6: связующий на 2.",
    "S1: setter in 1, S2: setter in 2, S3: setter in 3, S4: setter in 4, S5: setter in 5, S6: setter in 6.":
      "S1: связующий на 1, S2: связующий на 2, S3: связующий на 3, S4: связующий на 4, S5: связующий на 5, S6: связующий на 6.",
    "Labels:": "Метки:",
    "Current theme:": "Текущая тема:",
    "Choose the language for the app interface.":
      "Выберите язык интерфейса приложения.",
    "Create your own language package or edit a community one, with AI-assisted translation.":
      "Создайте свой языковой пакет или отредактируйте пакет сообщества с переводом с помощью ИИ.",

    // About / Footer
    "Copyright © Perana Sports {year}. All rights reserved.":
      "Copyright © Perana Sports {year}. Все права защищены.",
    "VBLive is free to use right now. However Perana Sports reserves the right to charge a subscription fee in due course.":
      "VBLive сейчас бесплатен. Однако Perana Sports оставляет за собой право со временем взимать плату за подписку.",
    "Querries / suggestions are most welcomed.":
      "Вопросы / предложения всегда приветствуются.",

    // Live
    "Please enter server name to search":
      "Введите имя сервера для поиска",
    "Broadcast Server": "Сервер вещания",
    "Enter DVMateLive Server Name": "Введите имя сервера DVMateLive",
    LIVE: "В ЭФИРЕ",

    // Playlists list
    "Delete Playlist": "Удалить плейлист",
    "Are you sure you want to delete the selected playlist?":
      "Вы уверены, что хотите удалить выбранный плейлист?",
    "My Playlists": "Мои плейлисты",
    "Shared Playlists": "Общие плейлисты",
    "Tags:": "Теги:",
    "Select...": "Выбрать…",
    "All Tags": "Все теги",

    // Share
    "User already added": "Пользователь уже добавлен",
    "Please enter a valid email address":
      "Введите действительный адрес эл. почты",
    "Please select at least one user to share with":
      "Выберите хотя бы одного пользователя для предоставления доступа",
    "Not Shared": "Не предоставлено",
    "Available for everyone": "Доступно всем",
    "Available to selected users":
      "Доступно выбранным пользователям",
    "Enter VBLive user email address":
      "Введите адрес эл. почты пользователя VBLive",
    "Selected users:": "Выбранные пользователи:",
    "Save List": "Сохранить список",
    "Clear List": "Очистить список",
    "Clear Users List": "Очистить список пользователей",
    "Do you really want to clear all users from list?":
      "Вы действительно хотите удалить всех пользователей из списка?",
    "Do you want to save changes before closing?":
      "Сохранить изменения перед закрытием?",
    "Enter name to save list to:":
      "Введите имя для сохранения списка:",
    "Enter name to save list to":
      "Введите имя для сохранения списка",

    // AI panels
    "AI settings": "Настройки ИИ",
    "Setup needed": "Требуется настройка",
    "Generate report": "Сформировать отчёт",
    "Generate AI report": "Сформировать ИИ-отчёт",
    "Generate general report": "Сформировать общий отчёт",
    "Translate to…": "Перевести на…",
    "Follow-up questions": "Дополнительные вопросы",
    "Ask a follow-up question…": "Задайте дополнительный вопрос…",
    "Ask a follow-up question": "Задайте дополнительный вопрос",
    "AI provider": "Поставщик ИИ",
    Model: "Модель",
    Hide: "Скрыть",
    Show: "Показать",
    "Your key is stored only in this browser and sent directly to the selected provider.":
      "Ваш ключ хранится только в этом браузере и отправляется напрямую выбранному поставщику.",
    "Please enter your API key.": "Введите ваш API-ключ.",
    "Not enough data to analyse.":
      "Недостаточно данных для анализа.",
    "Select a player with recorded spikes first.":
      "Сначала выберите игрока с записанными атаками.",
    Player: "Игрок",
    "Select a player…": "Выберите игрока…",
    "No players with recorded spikes in the selected matches.":
      "Нет игроков с записанными атаками в выбранных матчах.",
    "Analyse with AI": "Анализировать с ИИ",
    "AI scouting report": "ИИ-скаутинг отчёт",
    "Q:": "В:",
    Ask: "Спросить",
    "e.g. Which rotation should we target on serve?":
      "напр.: На какую расстановку нацеливаться при подаче?",
    "Saved questions:": "Сохранённые вопросы:",
    "Click to ask again": "Нажмите, чтобы спросить снова",
    "Remove saved question": "Удалить сохранённый вопрос",

    // AI language options
    Spanish: "Испанский",
    French: "Французский",
    German: "Немецкий",
    Italian: "Итальянский",
    Portuguese: "Португальский",
    Dutch: "Нидерландский",
    Polish: "Польский",
    Russian: "Русский",
    Turkish: "Турецкий",
    Arabic: "Арабский",
    "Chinese (Simplified)": "Китайский (упрощённый)",
    Japanese: "Японский",
    Korean: "Корейский",
    Vietnamese: "Вьетнамский",
    Thai: "Тайский",

    // Box score
    "Show Full Headers": "Показать полные заголовки",
    "Show Short Headers": "Показать краткие заголовки",
    "Show Less Players": "Показать меньше игроков",
    "Show All Players": "Показать всех игроков",
    "Attack Kills": "Очки в атаке",
    "Attack Errors": "Ошибки в атаке",
    "Total Attempts": "Всего попыток",
    "Hitting Efficiency": "Эффективность атаки",
    "Serve Aces": "Эйсы на подаче",
    "Serve Errors": "Ошибки на подаче",
    "Pass Errors": "Ошибки приёма",
    "% Perfect Passes": "% идеальных приёмов",
    "Passing Average": "Средний приём",
    "Total Passes": "Всего приёмов",
    "Set Assists": "Передачи (ассисты)",
    Digs: "Защита",
    "Momentum Chart": "График моментума",
    "Key event": "Ключевое действие",
    "Break Point %": "% очко на брейке",
    "First Ball Sideout %": "% приёма с первого мяча",
    "Sideout Transition %": "% приёма в доигровке",
    "FB Sideout Kills %": "% атак с первого мяча",
    "Sideout Opposition Error %": "% приёма на ошибке соперника",
    Plus: "Плюс",
    Minus: "Минус",
    Blocks: "Очки блоком",
    "Positive Serve %": "% результативных подач",
    Sideout: "Приём",
    "Break point": "Очко на брейке",
    "Ace / Kill": "Эйс / Атака в очко",
    "In play": "В игре",
    Error: "Ошибка",
    "Sideout on a kill": "Приём завершён атакой в очко",
    "Sideout on an error": "Приём завершён на ошибке соперника",
    "Top half": "Верхняя половина",
    "Bottom half": "Нижняя половина",
    "The centre line steps toward the team that wins each point.": "Центральная линия смещается в сторону команды, выигравшей очко.",
    "No point-by-point data available for this match.": "Для этого матча нет данных по очкам.",
    "No match data available.": "Нет данных о матче.",
    "This report isn't supported on this browser yet — please use Safari.": "Этот отчёт пока не поддерживается в этом браузере — используйте Safari.",
    "No video available for this match.": "Для этого матча нет видео.",
    passes: "приёмов",
    "eff.": "эфф.",
    Grade: "Оценка",
    LEFT: "ЛЕВО",
    CENTRE: "ЦЕНТР",
    RIGHT: "ПРАВО",
    SELECTED: "ВЫБРАНО",
    Passers: "Принимающие",
    Calls: "Вызовы",
    "SIDE OUT": "ПРИЁМ",
    TRANSITION: "ПЕРЕХОД",
    "No events in selection.": "Нет событий в текущем выборе.",
    "Serve Receives By Players": "Приёмы по игрокам",
    "Serve Receive By Zones": "Приём по зонам",
    "Dig Errors": "Ошибки в защите",
    "Dig Average": "Средняя защита",
    "Block Solos": "Одиночные блоки",
    "Block Assists": "Групповые блоки",
    "Block Errors": "Ошибки на блоке",
    "Points Scored": "Набрано очков",
    TEAM: "КОМАНДА",

    // Sideout report
    "ALL ROWS": "ВСЕ РЯДЫ",
    "ROWS ": "РЯДЫ ",
    "ROW ": "РЯД ",
    "FB Kills": "Очки в атаке FB",
    "Opp. Serve Errors": "Ошибки подачи соп.",
    "FB Opp. Errors": "Ошибки соп. FB",
    "Kills/Blocks": "Атаки/Блоки",
    "Opp. Errors": "Ошибки соп.",
    Unsuccessful: "Безуспешно",

    // Attack zones
    "No events in selected zone.":
      "Нет событий в выбранной зоне.",
    "Show all attacks. Or click on a zone to show attacks in that zone.":
      "Показать все атаки. Или щёлкните по зоне, чтобы показать атаки в этой зоне.",
    "OPPONENTS (": "СОПЕРНИКИ (",

    // Video analysis filters
    "All Types": "Все типы",
    Serve: "Подача",
    "Serve-Receive": "Приём",
    Set: "Передача",
    "Setter's Call": "Вызов связующего",
    "Disable this skill": "Отключить этот навык",
    "Enable this skill": "Включить этот навык",
    "Teams & Players": "Команды и игроки",
    "Match State": "Статус матча",
    "Custom Code": "Пользовательский код",
    "Search... (comma-separated for multiple)": "Поиск… (через запятую для нескольких значений)",
    vs: "против",
    "Rewind 60 seconds": "Назад на 60 секунд",
    "Rewind 1 frame": "Назад на 1 кадр",
    "Forward 1 frame": "Вперёд на 1 кадр",
    "Forward 60 seconds": "Вперёд на 60 секунд",
    "Previous Clip": "Предыдущий клип",
    Replay: "Повтор",
    "Next Clip": "Следующий клип",
    Phases: "Фазы",
    "Score Range": "Диапазон счёта",
    "Set Winner": "Победитель партии",
    Type: "Тип",
    Strength: "Сила удара",
    "Setter Calls": "Вызовы связующего",
    "Setter Call": "Вызов связующего",
    Freeball: "Свободный мяч",
    "Close (0-2)": "Близкий (0-2)",
    "Moderate (3-5)": "Умеренный (3-5)",
    "Lopsided (6+)": "Явный перевес (6+)",
    "Focus Team": "Отслеживаемая команда",
    Oppositions: "Соперники",
    "Focus Team Won": "Отслеживаемая команда выиграла",
    "Oppositions Won": "Соперники выиграли",
    "Advanced Filters": "Расширенные фильтры",
    Players: "Игроки",
    Opponents: "Соперники",
    "Event Type": "Тип события",
    "Event Result": "Результат события",
    "Attack Type": "Тип атаки",
    "Hit Type": "Тип удара",
    "Block Type": "Тип блока",
    "Attack Combo": "Комбинация атаки",
    Apply: "Применить",
    Rotation: "Расстановка",
    Skill: "Навык",
    "Lead Time": "Время опережения",
    Duration: "Продолжительность",
    Timing: "Настройка времени",
    "Go Live": "Перейти к прямому эфиру",
    "Playback has fallen behind the live broadcast — click to jump to now.": "Воспроизведение отстаёт от прямой трансляции — нажмите, чтобы перейти к текущему моменту.",
    "Video timing": "Настройка времени видео",
    "Default lead time (seconds before an event to start playback) and clip duration, per skill.": "Время опережения по умолчанию (секунды до события, с которых начинается воспроизведение) и длительность клипа для каждого навыка.",
    Cloud: "Облако",
    Local: "Локально",
    "Relay host": "Хост ретранслятора",
    "IP or hostname of the machine running the local relay.": "IP-адрес или имя хоста компьютера, на котором работает локальный ретранслятор.",
    Spike: "Атака",
    Block: "Блок",
    Defence: "Защита",
    "All Results": "Все результаты",
    Hard: "Сильный",
    Soft: "Слабый",
    Tip: "Скидка",
    "Not Specified": "Не указано",
    "H - High": "H - Высокая",
    "M- Medium": "M - Средняя",
    "Q - Quick": "Q - Быстрая",
    "T - Tense": "T - Прострел",
    "U - Super": "U - Супер",
    "F - Fast": "F - Скоростная",
    "O - Other": "O - Другое",
    "B0 - No Block": "B0 - Без блока",
    "B1 - 1 Block": "B1 - 1 блок",
    "B2 - 2 Blocks": "B2 - 2 блока",
    "B3 - 3 Blocks": "B3 - 3 блока",
    "BS - Seams": "BS - Швы",

    // All filters panel
    ALL: "ВСЕ",
    NONE: "НЕТ",

    // Playlist editor
    "Remove Item": "Удалить элемент",
    "Do you really want to remove the selected item?":
      "Вы действительно хотите удалить выбранный элемент?",
    "Description is required": "Описание обязательно",
    "Comments are required": "Комментарии обязательны",
    "Tags are required": "Теги обязательны",
    "Playlist saved successfully": "Плейлист успешно сохранён",
    "Error saving playlist": "Ошибка сохранения плейлиста",

    // Language editor
    "My saved packages": "Мои сохранённые пакеты",
    "Loading shared languages…": "Загрузка общих языков…",
    "Community shared languages": "Языки, опубликованные сообществом",
    by: "автор",
    "based on": "на основе",
    "or create new": "или создать новый",
    Use: "Использовать",
    "New Language Package": "Новый языковой пакет",
    "Language Name": "Название языка",
    "Language Code": "Код языка",
    "Flag Emoji": "Эмодзи флага",
    "Base language (reference)": "Базовый язык (эталон)",
    "If not English, both English and the reference language will be shown.":
      "Если не английский, будут показаны и английский, и эталонный язык.",
    "Start Editing": "Начать редактирование",
    "Save Key": "Сохранить ключ",
    "AI key set": "Ключ ИИ задан",
    "Set API key for AI translation":
      "Задать API-ключ для перевода ИИ",
    "AI key ✓": "Ключ ИИ ✓",
    "Set AI key": "Задать ключ ИИ",
    "AI: Translate All": "ИИ: Перевести всё",
    "Translating…": "Перевод…",
    "AI translate section": "Перевести раздел с ИИ",
    "Clear All": "Очистить всё",
    "Saving…": "Сохранение…",
    English: "Английский",
    new: "новый",
    "Enter translation…": "Введите перевод…",
    "Share with other VBLive users?":
      "Поделиться с другими пользователями VBLive?",
    "Would you like to share the": "Хотите поделиться",
    "language package with all VBLive users? Anyone can then copy and build upon it.":
      "языковым пакетом со всеми пользователями VBLive? Любой сможет скопировать и доработать его.",
    "No, just save for me": "Нет, сохранить только для меня",
    "Yes, share it!": "Да, поделиться!",
    "Language package saved and shared with all VBLive users!":
      "Языковой пакет сохранён и опубликован для всех пользователей VBLive!",
    "Language package saved!": "Языковой пакет сохранён!",
    "Clear all translations? This cannot be undone.":
      "Очистить все переводы? Это действие нельзя отменить.",

    // Additional UI phrases
    Teams: "Команды",
    "Shared By": "Поделился(-ась)",
    "View Activity": "Активность просмотров",
    Viewer: "Зритель",
    "Date / Time": "Дата / Время",
    "No views recorded yet.": "Просмотров пока не зафиксировано.",
    "Date / Scores": "Дата / Счёт",
    "Stats API source": "Источник API статистики",
    "load one or more AVC VIS files": "загрузить один или несколько файлов AVC VIS",
    "Points to close a set": "Очки для завершения партии",
    "Require 2-point advantage": "Требовать преимущество в 2 очка",
    Roster: "Состав",
    Clone: "Клонировать",
    "Edit team": "Редактировать команду",
    "New player": "Новый игрок",
    "Search player...": "Поиск игрока…",
    "Match Centre": "Центр матча",
    "Points Won": "Набранные очки",
    "Opp. Error": "Ошибка соперника",
    Total: "Всего",
    "Match Skills": "Игровые навыки",
    Dig: "Защита",
    "Best Scorers": "Лучшие бомбардиры",
    "Power Play": "Power Play",
    Match: "Матч",
    "Player scouting": "Скаутинг игроков",
    "Team deep analysis": "Глубокий анализ команды",
    "All Players": "Все игроки",
    "All Sets": "Все партии",
    "All Rotations": "Все расстановки",
    "All Combos": "Все комбинации",
    "All Calls": "Все вызовы",
    "All Events": "Все события",
    "Select all items": "Выбрать все элементы",
    "Unselect all items": "Снять выбор со всех элементов",
    "Create play list": "Создать плейлист",
    "Point by Point": "Очко за очком",
    // i18n backfill pass — previously English-fallback-only keys
    "Access denied": "Доступ запрещён",
    "Aces": "Эйсы",
    "ACES": "ЭЙСЫ",
    "Add report": "Добавить отчёт",
    "Admin": "Администратор",
    "Admins": "Администраторы",
    "Advanced": "Доп. код",
    "All set!": "Всё готово!",
    "Analyse team with AI": "Анализ команды с помощью ИИ",
    "Are you sure you want to delete the selected play list?": "Удалить выбранный плейлист?",
    "Assign": "Назначить",
    "Assign playlist": "Назначить плейлист",
    "Assigned": "Назначено",
    "At least one admin is required.": "Требуется как минимум один администратор.",
    "Attack Combo / Setter Call": "Атакующая комбинация / Вызов связующего",
    "Attack diagram": "Схема атаки",
    "Attacks": "Атаки",
    "ATTACKS": "АТАКИ",
    "Auto-detected a custom set-closing score for": "Автоматически определён пользовательский счёт окончания партии для",
    "BLOCK": "БЛОК",
    "BLOCKS": "БЛОКИ",
    "Beta testers": "Бета-тестеры",
    "Clear filter": "Сбросить фильтр",
    "Close Set": "Завершить партию",
    "Close menu": "Закрыть меню",
    "Code": "Код",
    "Coding defaults & shortcuts": "Настройки и горячие клавиши для разметки",
    "Continue": "Продолжить",
    "Could not load match data. The match may no longer be available.": "Не удалось загрузить данные матча. Возможно, матч больше недоступен.",
    "Could not load stats for these matches.": "Не удалось загрузить статистику для этих матчей.",
    "Could not load this match.": "Не удалось загрузить этот матч.",
    "Could not load this playlist.": "Не удалось загрузить этот плейлист.",
    "Could not load this report.": "Не удалось загрузить этот отчёт.",
    "Could not save the report.": "Не удалось сохранить отчёт.",
    "Could not share the report. Please try again.": "Не удалось поделиться отчётом. Попробуйте снова.",
    "Court horizontal (net vertical)": "Площадка горизонтально (сетка вертикально)",
    "Court vertical (net horizontal)": "Площадка вертикально (сетка горизонтально)",
    "Create": "Создать",
    "Create a new team from this roster": "Создать новую команду из этого состава",
    "DIFFERENCE": "РАЗНИЦА",
    "DV Code": "Код DV",
    "Delete this report?": "Удалить этот отчёт?",
    "Detected a custom set-closing score of": "Обнаружен пользовательский счёт окончания партии:",
    "Double Sub": "Двойная замена",
    "Double Sub: now pick a bench player for the flashing OPPOSITE slot…": "Двойная замена: теперь выберите игрока со скамейки на мигающую позицию диагонального…",
    "Double Sub: pick a bench player for the flashing SETTER slot…": "Двойная замена: выберите игрока со скамейки на мигающую позицию связующего…",
    "Double Substitution — swap the Setter and Opposite for two incoming players": "Двойная замена — замените связующего и диагонального двумя новыми игроками",
    "Drag to draw the ball path; pause briefly to mark a touch (reception/dig/block), release to finish.": "Перетащите, чтобы нарисовать траекторию мяча; сделайте короткую паузу, чтобы отметить касание (приём/защита/блок), отпустите для завершения.",
    "Draw": "Рисовать",
    "Draw & Code": "Рисование и разметка",
    "Draw & Code — draw ball paths on the court to code the rally": "Рисование и разметка — рисуйте траектории мяча на площадке, чтобы размечать розыгрыш",
    "Edit Lineup": "Изменить состав",
    "Edit Lineup — freely rearrange the starting lineup before the set begins": "Изменить состав — свободно переставьте стартовый состав перед началом партии",
    "Emails in this list can manage beta testers and other admins.": "Адреса из этого списка могут управлять бета-тестерами и другими администраторами.",
    "Emails in this list get early access to AI Reports and other gated features. Changes take effect immediately.": "Адреса из этого списка получают ранний доступ к AI-отчётам и другим ограниченным функциям. Изменения вступают в силу немедленно.",
    "Enter a valid email address.": "Введите корректный адрес электронной почты.",
    "Exit fullscreen": "Выйти из полноэкранного режима",
    "Extended": "Доп. код",
    "Failed to generate PDF.": "Не удалось создать PDF.",
    "Failed to save changes.": "Не удалось сохранить изменения.",
    "Filtered by": "Отфильтровано по",
    "First name": "Имя",
    "For stronger security later, consider moving admin checks to a Firebase Auth custom claim instead of this email list.": "Для повышения безопасности в будущем рекомендуется перенести проверки прав администратора на пользовательское утверждение (custom claim) Firebase Auth вместо этого списка email.",
    "Fullscreen": "Полноэкранный режим",
    "Generate an AI report from these matches": "Создать AI-отчёт по этим матчам",
    "How often (in seconds) the events list is updated during a live session.": "Как часто (в секундах) обновляется список событий во время прямой трансляции.",
    "Last name": "Фамилия",
    "Leave blank to use standard rules (25 points, 15 for a 5th/deciding set)": "Оставьте пустым, чтобы использовать стандартные правила (25 очков, 15 в решающей 5-й партии)",
    "Live session refresh interval": "Интервал обновления прямой трансляции",
    "Load VIS file(s)...": "Загрузить файл(ы) VIS…",
    "Load this team's last-saved starting lineup (positions, setter and liberos)": "Загрузить последний сохранённый стартовый состав этой команды (позиции, связующий и либеро)",
    "Loading…": "Загрузка…",
    "Manually end the current set at its current score, regardless of the configured winning score / 2-point advantage rule": "Вручную завершить текущую партию при текущем счёте, независимо от настроенного выигрышного счёта / правила преимущества в 2 очка",
    "Migration finished with some issues": "Миграция завершена с некоторыми проблемами",
    "Most of your data was migrated, but some items could not be moved. You can retry now or continue and try again later.": "Большая часть ваших данных перенесена, но некоторые элементы не удалось перенести. Вы можете повторить попытку сейчас или продолжить и попробовать позже.",
    "Mute": "Выключить звук",
    "New team": "Новая команда",
    "Next": "Далее",
    "Nickname": "Прозвище",
    "No Power Play": "Без Power Play",
    "No admins yet.": "Администраторов пока нет.",
    "No beta testers yet.": "Бета-тестеров пока нет.",
    "No coded events to analyse.": "Нет размеченных событий для анализа.",
    "No coded events to export.": "Нет размеченных событий для экспорта.",
    "No matches.": "Нет матчей.",
    "No players added yet.": "Игроки ещё не добавлены.",
    "No players recorded.": "Игроки не зарегистрированы.",
    "No players yet.": "Игроков пока нет.",
    "No playlists assigned.": "Плейлисты не назначены.",
    "No playlists found.": "Плейлисты не найдены.",
    "No reports saved.": "Отчёты не сохранены.",
    "No roster players available.": "Нет доступных игроков состава.",
    "No substitutions": "Нет замен",
    "No teams found.": "Команды не найдены.",
    "No teams yet.": "Команд пока нет.",
    "No teams.": "Нет команд.",
    "Nominate Setter": "Назначить связующего",
    "Notify these users by email (optional)": "Уведомить этих пользователей по электронной почте (необязательно)",
    "OPP REC": "ПРИЁМ СОПЕРН.",
    "OPP. ERRORS": "ОШИБКИ СОПЕРН.",
    "Open / Download": "Открыть / Скачать",
    "Open local file": "Открыть локальный файл",
    "Open menu": "Открыть меню",
    "Opposition Passing — use the opposition's reception formation on the diagram": "Приём соперника — использовать расстановку приёма соперника на схеме",
    "Paste the report (Markdown supported)…": "Вставьте отчёт (поддерживается Markdown)…",
    "Pause": "Пауза",
    "Play": "Воспроизвести",
    "Playing squad": "Игровой состав",
    "Playing squad is full. Remove a player from the squad before adding another.": "Игровой состав заполнен. Удалите игрока из состава, прежде чем добавлять другого.",
    "Please sign in to save reports.": "Войдите в систему, чтобы сохранять отчёты.",
    "Power Play Only": "Только Power Play",
    "Record a team error (no player) — edit later to add the player and event type": "Зафиксировать командную ошибку (без игрока) — отредактируйте позже, чтобы добавить игрока и тип события",
    "Record at least one event before making substitutions": "Зафиксируйте хотя бы одно событие перед заменами",
    "Redo": "Повторить",
    "Remove": "Удалить",
    "Remove share": "Удалить доступ",
    "Remove yourself as admin? You will immediately lose access to this page.": "Удалить себя из администраторов? Вы немедленно потеряете доступ к этой странице.",
    "Report saved and shared.": "Отчёт сохранён и передан.",
    "Report saved to the player's profile.": "Отчёт сохранён в профиле игрока.",
    "Report title": "Название отчёта",
    "Reports": "Отчёты",
    "Result": "Результат",
    "Retry": "Повторить",
    "Return to live coding": "Вернуться к разметке в реальном времени",
    "Rotation is not permitted while making substitutions": "Ротация не допускается во время замен",
    "SERVE": "ПОДАЧА",
    "SETS": "ПАРТИИ",
    "Save & Share": "Сохранить и поделиться",
    "Save this report so you can share it with other users": "Сохраните этот отчёт, чтобы поделиться им с другими пользователями",
    "Save this report to the player's profile": "Сохранить этот отчёт в профиле игрока",
    "Save to player": "Сохранить игроку",
    "Saved lineup": "Сохранённый состав",
    "Scroll, drag or swipe to scrub": "Прокрутите, перетащите или смахните для перемотки",
    "Search existing players...": "Поиск существующих игроков…",
    "Search playlists…": "Поиск плейлистов…",
    "Select a match video to view while entering lineups.": "Выберите видео матча для просмотра во время ввода составов.",
    "Select a player.": "Выберите игрока.",
    "Select a team.": "Выберите команду.",
    "Setting up your account": "Настройка вашей учётной записи",
    "Shaded rows are played under a Power Play — HP/AP marks the calling team, bold gold scores are bonus (double) points.": "Выделенные строки сыграны во время Power Play — HP/AP отмечает вызвавшую команду, жирный золотой счёт — бонусные (двойные) очки.",
    "Shared by": "Поделился(лась)",
    "Shared with me": "Доступно мне",
    "Show help": "Показать справку",
    "Show public playlists": "Показать публичные плейлисты",
    "Standard": "Стандарт",
    "Substitutions made — tap to view": "Произведены замены — нажмите для просмотра",
    "Tap the blocking player.": "Нажмите на блокирующего игрока.",
    "Team colour": "Цвет команды",
    "Team name": "Название команды",
    "That email is already in the list.": "Этот адрес уже есть в списке.",
    "The report is too large to save. Try a single match or fewer sets.": "Отчёт слишком большой для сохранения. Попробуйте один матч или меньше партий.",
    "This match has no Power Play calls.": "В этом матче нет вызовов Power Play.",
    "This player has no name to save the report against.": "У этого игрока нет имени, чтобы сохранить отчёт на его счёт.",
    "This team has no designated setter. Pick the player who is setting.": "В этой команде не назначен связующий. Выберите игрока, который играет связующим.",
    "Toggle video player": "Переключить видеоплеер",
    "Unmute": "Включить звук",
    "Untitled playlist": "Плейлист без названия",
    "Video URL…": "URL видео…",
    "We're moving your data to a faster, more reliable home. This happens only once.": "Мы переносим ваши данные в более быстрое и надёжное хранилище. Это происходит только один раз.",
    "You don't have permission to view this page.": "У вас нет прав для просмотра этой страницы.",
    "Your data has been migrated successfully.": "Ваши данные успешно перенесены.",
    "Your teams and players will appear here after your data is migrated.": "Ваши команды и игроки появятся здесь после переноса данных.",
    "bonus point": "бонусное очко",
    "bonus points": "бонусных очка",
    "event": "событие",
    "events": "события",
    "file(s).": "файл(ов).",
    "is not on this team's roster.": "не числится в составе этой команды.",
    "loaded": "загружено",
    "matches": "матчей",
    "more": "ещё",
    "players": "игроков",
    "points (no value was entered) — applied automatically.": "очков (значение не введено) — применено автоматически.",
    "roster": "состав",
    "seconds": "секунд",
    "selected": "выбрано",
    "session(s)?": "сеанс(ов)?",
    "showing": "показано",
    "tournaments": "турниров",
    "…and": "…и ещё",
    "● marks which team served the point.": "● отмечает, какая команда подавала на этом розыгрыше.",
    // i18n backfill pass (Catapult + misc)
    "Centimetres (cm)": "Сантиметры (см)",
    "Inches (in)": "Дюймы (in)",
    "Jump height units": "Единицы измерения высоты прыжка",
    "Choose how Catapult jump heights are displayed and filtered.": "Выберите, как отображаются и фильтруются высоты прыжков Catapult.",
    "Select Catapult Focus file...": "Выбрать файл Catapult Focus…",
    "Select a Catapult Focus CSV export to view jump data for this match.": "Выберите CSV-экспорт Catapult Focus, чтобы просмотреть данные прыжков для этого матча.",
    "Loaded from saved match data": "Загружено из сохранённых данных матча",
    "No jump data found in this file.": "В этом файле не найдены данные о прыжках.",
    "Could not read this file. Please check it is a Catapult Focus CSV export.": "Не удалось прочитать этот файл. Убедитесь, что это CSV-экспорт Catapult Focus.",
    "Could not save jump data with this match.": "Не удалось сохранить данные о прыжках вместе с этим матчем.",
    "Jump data saved with this match.": "Данные о прыжках сохранены вместе с этим матчем.",
    "Show players with no match stats": "Показать игроков без статистики матча",
    "hidden": "скрыто",
    "jumps": "прыжки",
    "periods": "периоды",
    "Sync offset": "Смещение синхронизации",
    "matched": "совпало",
    "match events now show jump height/duration in Video Analysis": "событий матча теперь показывают высоту/продолжительность прыжка в Видеоанализе",
    "Team": "Команда",
    "Team A": "Команда A",
    "Team B": "Команда B",
    "Which team is this Catapult file for?": "Для какой команды этот файл Catapult?",
    "We couldn't confidently match the players in this Focus file to one team. Please select which team it belongs to.": "Нам не удалось однозначно сопоставить игроков в этом файле Focus с одной командой. Выберите, какой команде он принадлежит.",
    "This match has no video attached yet.": "К этому матчу ещё не прикреплено видео.",
    "No synced jump events found for this player.": "Для этого игрока не найдено синхронизированных событий прыжков.",
    "No match loaded.": "Матч не загружен.",
    "avg": "ср.",
    "max": "макс.",
    "Sync": "Синхронизация",
    "Jump Report": "Отчёт о прыжках",
    "Watch Video": "Смотреть видео",
    "Save PDF": "Сохранить PDF",
    "Generated by VBLive": "Создано с помощью VBLive",
    "Deep analysis of {team} from ball-by-ball event data, covering serving, reception, setting, attack, blocking, defence, rotations and opponent tendencies. Best used with the Anthropic (Claude) provider.": "Глубокий анализ команды {team} на основе данных по каждому игровому эпизоду, включая подачу, приём, передачу, нападение, блок, защиту, ротации и тенденции соперника. Лучше всего использовать с провайдером Anthropic (Claude).",
    "teams": "команды",
  },

  tr: {
    // Information page
    Information: "Bilgi",
    "Release notes": "Sürüm notları",
    "Contact us": "Bize ulaşın",
    "Questions, feedback or suggestions are most welcome.": "Sorular, geri bildirim veya öneriler her zaman memnuniyetle karşılanır.",
    // DV match coding
    Lineups: "Dizilişler",
    Point: "Sayı",
    Video: "Video",
    Defaults: "Varsayılanlar",
    "[team][NN][skill][type?][eval][zoneFrom?[szFrom?][zoneTo?[szTo?]]]": "[takım][NN][beceri][tip?][değer][bölgeNeredn?[abNeredn?][bölgeNereye?[abNereye?]]]",
    Vertical: "Dikey",
    Horizontal: "Yatay",
    "Swap sides": "Tarafları değiştir",
    Subs: "Değişiklikler",
    Substitutions: "Değişiklikler",
    Court: "Saha",
    Home: "Ev sahibi",
    Away: "Deplasman",
    Games: "Setler",
    Enter: "Gir",
    Undo: "Geri al",
    "Match saved": "Maç kaydedildi",
    "Please sign in to save this match.": "Bu maçı kaydetmek için lütfen giriş yapın.",
    "Nothing to save yet.": "Henüz kaydedilecek bir şey yok.",
    "Could not save the match. Please try again.": "Maç kaydedilemedi. Lütfen tekrar deneyin.",
    Discard: "Vazgeç",
    "Resumed autosaved coding": "Otomatik kaydedilen kodlama devam ettirildi",
    "Changes discarded": "Değişiklikler iptal edildi",
    "Discard all changes and revert to the starting point?":
      "Tüm değişiklikleri iptal edip başlangıç noktasına dönülsün mü?",
    Clear: "Temizle",
    "Prev set": "Önceki set",
    "Next set": "Sonraki set",
    "Match Setup": "Maç kurulumu",
    "First serve": "İlk servis",
    "Start Match": "Maçı başlat",
    "Resume Match": "Maça devam et",
    Skip: "Atla",
    "Select an event then drag to draw ball path": "Bir olay seçin, ardından top yolunu çizmek için sürükleyin",
    "Drag on court to set ball path": "Top yolunu ayarlamak için sahada sürükleyin",
    "No events yet – enter a DV code above and press Enter.": "Henüz olay yok – yukarıya bir DV kodu girin ve Gir'e basın.",
    "DataVolley code reference": "DataVolley kod referansı",
    Format: "Biçim",
    Skills: "Beceriler",
    Evaluation: "Değerlendirme",
    "Skill / type omission": "Beceri / tip atlama",
    "Skill and/or type may be omitted; defaults fill in the rest.": "Beceri ve/veya tip atlanabilir; geri kalanı varsayılan değerler doldurur.",
    "Dot-coding (S→R and A→B pairs)": "Nokta kodlama (S→R ve A→B çiftleri)",
    "Chaining & aliases": "Zincirleme ve takma adlar",
    "team:": "takım:",
    "omit → defaults to A": "atla → varsayılan A",
    "NN: 1 or 2 digit jersey, e.g. 1, 01, 12": "NN: 1 veya 2 haneli forma numarası, örn. 1, 01, 12",
    "sub-zone: a b c d appended to zone digit, e.g. 5b or 5b1a": "alt bölge: bölge rakamına eklenen a b c d, örn. 5b veya 5b1a",
    "S Serve · R Reception · E Set · A Attack · B Block · D Dig · F Freeball": "S Servis · R Karşılama · E Pas · A Hücum · B Blok · D Savunma · F Serbest top",
    "# kill/ace · + positive · ! ok · - negative · / overpass · = error": "# sayı/ace · + pozitif · ! orta · - negatif · / karşıya · = hata",
    "E.g.": "Örn.",
    "serve with skill=A (default), type=H, eval=+, zone=5.": "skill=A (varsayılan), type=H, eval=+, zone=5 ile servis.",
    "type-only (no skill marker), defaults skill from context.": "yalnızca tip (beceri işareti yok), beceri bağlamdan varsayılan.",
    "serve coded, reception auto-linked.": "servis kodlandı, karşılama otomatik bağlandı.",
    "attack coded, block auto-linked.": "hücum kodlandı, blok otomatik bağlandı.",
    "Dot connects the reception eval to the corresponding serve eval via the serve→receive mapping.": "Nokta, servis→karşılama eşlemesi yoluyla karşılama değerlendirmesini ilgili servis değerlendirmesine bağlar.",
    "two events in one string.": "tek dizede iki olay.",
    "manual point A/B.": "manuel sayı A/B.",
    "serve ace/error.": "servis ace/hata.",
    "serve with eval.": "değerlendirmeli servis.",
    // Stats, sideouts, filters & video tooltips
    "Block Kills": "Blok sayıları",
    "Side Out Percentage": "Sideout yüzdesi",
    "All First Ball": "Tüm ilk toplar",
    "First Ball Kills": "İlk top sayıları",
    "Good Serve": "İyi servis",
    "Break Points": "Break sayıları",
    Game: "Set",
    Row: "Pozisyon",
    "First Ball Sideouts": "İlk top sideout'ları",
    "All Sideouts": "Tüm sideout'lar",
    All: "Tümü",
    None: "Hiçbiri",
    Receiver: "Karşılayıcı",
    "# Passes": "Karşılama sayısı",
    "Perfect %": "Mükemmel %",
    "Positive %": "Pozitif %",
    "Sideout %": "Sideout %",
    "FB Sideout %": "İlk top sideout %",
    "Pass Hits": "Karşılama sonrası hücumlar",
    "Pass Hit Kills": "Karşılama sonrası sayılar",
    "Pass Hit Errors": "Karşılama sonrası hatalar",
    "Show Passes": "Karşılamaları göster",
    "Show Attacks": "Hücumları göster",
    "Error Pass": "Hatalı karşılama",
    "1 Pass": "1 karşılama",
    "2 Pass": "2 karşılama",
    "Perfect Pass": "Mükemmel karşılama",
    "Successful SO": "Başarılı sideout",
    "First Ball SO": "İlk top sideout",
    "Unsuccessful SO": "Başarısız sideout",
    RECEIVES: "KARŞILAMALAR",
    Sets: "Setler",
    Rotations: "Rotasyonlar",
    "Attack Combos": "Hücum kombinasyonları",
    Stages: "Aşamalar",
    Display: "Görüntüleme",
    Setters: "Pasörler",
    Attackers: "Hücumcular",
    Results: "Sonuçlar",
    Source: "Kaynak",
    Cones: "Bölgeler",
    "Select None": "Hiçbirini seçme",
    "All Serves": "Tüm servisler",
    "All Returns": "Tüm karşılamalar",
    Filters: "Filtreler",
    "Reset all filters": "Tüm filtreleri sıfırla",
    "Show video clips of filtered attacks": "Filtrelenen hücumların video kliplerini göster",
    "Spikes for the selected player": "Seçilen oyuncunun hücumları",
    "Click on the zones to view video of attacks": "Hücum videolarını görmek için bölgelere tıklayın",
    "Expand All": "Tümünü genişlet",
    "Collapse All": "Tümünü daralt",
    "Import Radar File": "Radar dosyasını içe aktar",
    "Select local video file": "Yerel video dosyası seç",
    "Save to database": "Veritabanına kaydet",
    "Load and play online video": "Çevrimiçi videoyu yükle ve oynat",
    "Synchronise video with events": "Videoyu olaylarla senkronize et",
    "Rewind 10 seconds": "10 saniye geri",
    "Rewind 1 second": "1 saniye geri",
    "Forward 1 second": "1 saniye ileri",
    "Forward 10 seconds": "10 saniye ileri",
    "Set timing for events": "Olaylar için zamanlama ayarla",

    // Navigation
    Matches: "Maçlar",
    Live: "Canlı",
    Import: "İçe aktar",
    Playlists: "Oynatma listeleri",
    "New Match": "Yeni maç",
    // New Match / Match Coding gate
    "Match Coding": "Maç kodlama",
    "Match Coding — Coming Soon": "Maç kodlama — Yakında",
    "Live match coding is on the way. Here's a preview of what's coming.":
      "Canlı maç kodlama yolda. İşte sizi nelerin beklediğinin bir önizlemesi.",
    Settings: "Ayarlar",
    Language: "Dil",
    "Your profile": "Profiliniz",
    "Sign out": "Çıkış yap",
    "Translation Editor": "Çeviri düzenleyici",

    // Common actions
    Save: "Kaydet",
    Cancel: "İptal",
    Delete: "Sil",
    Share: "Paylaş",
    Edit: "Düzenle",
    New: "Yeni",
    Refresh: "Yenile",
    Search: "Ara",
    Reset: "Sıfırla",
    Close: "Kapat",
    "Sub for": "Değiştir",
    "No players available": "Uygun oyuncu yok",
    "Show full roster": "Tüm kadroyu göster",
    "Available only": "Yalnızca uygun",
    "Maximum substitutions reached": "Maksimum değişiklik sayısına ulaşıldı",
    Back: "Geri",
    "Download PDF": "PDF indir",
    "Loading...": "Yükleniyor…",
    "Select All": "Tümünü seç",
    "Unselect All": "Tüm seçimi kaldır",
    Yes: "Evet",
    No: "Hayır",
    Add: "Ekle",
    Load: "Yükle",
    Confirm: "Onayla",
    Open: "Aç",
    show: "göster",

    // Auth
    "Sign In": "Giriş yap",
    "Sign Up": "Kaydol",
    "Sign Out": "Çıkış yap",
    Email: "E-posta",
    Password: "Parola",
    "Confirm Password": "Parolayı onayla",
    "Forgot password?": "Parolanızı mı unuttunuz?",
    "Forgot Password": "Parolamı unuttum",
    "Don't have an account?": "Hesabınız yok mu?",
    "Sign up for free!": "Ücretsiz kaydolun!",
    "All ready signed up?": "Zaten kayıtlı mısınız?",
    "Error Signing In": "Giriş hatası",
    "Signed up successfully": "Kayıt başarılı",
    "Error signing up": "Kayıt hatası",
    "Enter your email": "E-postanızı girin",
    "Send Reset Email": "Sıfırlama e-postası gönder",
    "Email was sent to": "E-posta gönderildi:",
    "Could not send reset email":
      "Sıfırlama e-postası gönderilemedi",

    // Matches list
    "Team 1": "Takım 1",
    "Team 2": "Takım 2",
    Tournament: "Turnuva",
    Scores: "Skorlar",
    Date: "Tarih",
    Actions: "İşlemler",
    Owner: "Sahip",
    Description: "Açıklama",
    Comments: "Yorumlar",
    Tags: "Etiketler",
    "Add player": "Oyuncu ekle",
    "Add team": "Takım ekle",
    "Edit playlist": "Çalma listesini düzenle",
    "Merge Teams": "Takımları birleştir",
    "Roster differences detected between teams:": "Takımlar arasında kadro farklılıkları tespit edildi:",
    "Review the roster in the next step before merging.": "Birleştirmeden önce bir sonraki adımda kadroyu inceleyin.",
    "At least 2 team records must exist in your Teams list for the selected matches.": "Seçilen maçlar için Takımlar listenizde en az 2 takım kaydı bulunmalıdır.",
    "Merge...": "Birleştir...",
    "Select 2 or more team records to merge into one.": "Birleştirmek için 2 veya daha fazla takım kaydı seçin.",
    "Filter teams...": "Takımları filtrele...",
    "teams selected": "takım seçildi",
    "Select Teams": "Takım seç",
    "Reconcile Roster": "Kadroyu uzlaştır",
    "Confirm Merge": "Birleştirmeyi onayla",
    "Merged team name": "Birleştirilmiş takım adı",
    "shirt number(s) have conflicting names — review below.": "forma numarası/ları çakışan isimler içeriyor — aşağıyı inceleyin.",
    "Shirt": "Forma",
    "players included": "oyuncu dahil",
    "No players to reconcile.": "Uzlaştırılacak oyuncu yok.",
    "Use this name": "Bu adı kullan",
    "Create merged team": "Birleştirilmiş takımı oluştur",
    "with": "ile",
    "duplicate team records": "yinelenen takım kaydı",
    "Consolidate duplicate player records and reassign playlist links": "Yinelenen oyuncu kayıtlarını birleştir ve çalma listesi bağlantılarını yeniden ata",
    "This cannot be undone.": "Bu işlem geri alınamaz.",
    "Teams being merged:": "Birleştirilen takımlar:",
    "Step": "Adım",
    "of": "/",
    "Merging...": "Birleştiriliyor...",
    "Players & Teams": "Oyuncular ve Takımlar",
    "No players or teams assigned.": "Atanmış oyuncu veya takım yok.",
    "Search players...": "Oyuncu ara...",
    "Search teams...": "Takım ara...",
    "Saving...": "Kaydediliyor...",
    "My Matches": "Maçlarım",
    "Shared Matches": "Paylaşılan maçlar",
    "Search team...": "Takım ara…",
    "All Teams": "Tüm takımlar",
    "Multi Match Reports": "Çoklu maç raporları",
    "Delete match": "Maçı sil",
    "Remove from my shared matches": "Paylaşılan maçlarımdan kaldır",
    "Share match": "Maçı paylaş",
    "Edit match": "Maçı düzenle",
    "Open video analysis": "Video analizini aç",
    "Delete Match": "Maçı sil",
    "Are you sure you want to delete the selected match?":
      "Seçili maçı silmek istediğinizden emin misiniz?",
    "No matches selected.": "Maç seçilmedi.",

    // New Match
    "← Back": "← Geri",
    "Team A (Home)": "Takım A (ev sahibi)",
    "Team B (Away)": "Takım B (deplasman)",
    "Select team…": "Takım seç…",
    "Tournament (optional)": "Turnuva (isteğe bağlı)",
    "Tournament / competition name": "Turnuva / müsabaka adı",
    "Match date": "Maç tarihi",
    "Start Coding →": "Kodlamaya başla →",

    // Reports
    Summary: "Özet",
    "Box Score": "İstatistik tablosu",
    "Side-outs Report": "Side-out raporu",
    "Serve Receives": "Karşılamalar",
    "Attack Zones": "Hücum bölgeleri",
    "Hitting Charts": "Hücum grafikleri",
    "Video Analysis": "Video analizi",
    Dashboard: "Panel",
    Charts: "Grafikler",
    "AI Report": "Yapay zekâ raporu",
    // AI Reports page
    "AI Reports": "Yapay zekâ raporları",
    "AI Reports — Coming Soon": "Yapay zekâ raporları — Yakında",
    "AI-powered match reports and deep analysis are on the way. Here are some sample reports to show what's coming.":
      "Yapay zekâ destekli maç raporları ve derinlemesine analizler yolda. İşte önizleme olarak bazı örnek raporlar.",
    "Preview not available.": "Önizleme kullanılamıyor.",
    "Open sample": "Örneği aç",
    "No matches were loaded. Select one or more matches and try again.":
      "Hiç maç yüklenmedi. Bir veya daha fazla maç seçip tekrar deneyin.",
    "Attack Analysis (English)": "Hücum analizi (İngilizce)",
    "Blocking Analysis (Vietnamese)": "Blok analizi (Vietnamca)",
    "Opponent Tendencies (Japanese)": "Rakip eğilimleri (Japonca)",
    // Deep AI analysis
    "Deep AI Analysis": "Derin Yapay Zekâ Analizi",
    "AI Deep Analysis": "Yapay Zekâ Derin Analizi",
    "Load at least one match with coded events to generate the analysis.": "Analizi oluşturmak için kodlanmış olaylar içeren en az bir maç yükleyin.",
    "Run the deep analysis one section at a time to keep each request small. Edit or add questions below, then generate. Best used with the Anthropic (Claude) provider.":
      "Her isteği küçük tutmak için derin analizi bölüm bölüm çalıştırın. Aşağıdaki soruları düzenleyin veya ekleyin, ardından oluşturun. En iyi Anthropic (Claude) sağlayıcısıyla kullanılır.",
    "Download CSV": "CSV indir",
    "Analysis section": "Analiz bölümü",
    analysing: "analiz ediliyor",
    "All sections (uses the most credits)": "Tüm bölümler (en fazla krediyi kullanır)",
    "All sections": "Tüm bölümler",
    Questions: "Sorular",
    "one per line, edit or add your own": "her satıra bir tane, düzenleyin veya kendi sorunuzu ekleyin",
    "Reset to defaults": "Varsayılanlara sıfırla",
    "Add a question…": "Soru ekle…",
    "Runs every section using the questions you've set for each. This sends one large request and uses the most credits.":
      "Her bölüm için belirlediğiniz sorularla tüm bölümleri çalıştırır. Bu, tek bir büyük istek gönderir ve en fazla krediyi kullanır.",
    "Generate full analysis": "Tam analiz oluştur",
    "Generate section analysis": "Bölüm analizi oluştur",
    Serving: "Servis",
    Reception: "Karşılama",
    Setting: "Pas",
    Attack: "Hücum",
    Blocking: "Blok",
    "Defence / Digging": "Savunma / Kurtarış",
    "Rotation Analysis": "Rotasyon analizi",
    "Opponent Tendencies (Scouting)": "Rakip eğilimleri (gözlem)",
    "Overall Team Metrics": "Genel takım metrikleri",
    "Serve efficiency (aces, errors, and pressured receptions forced).": "Servis verimliliği (saçlar, hatalar ve zorlanan baskılı karşılamalar).",
    "Which servers are most effective, and against which reception zones?": "Hangi servis atıcıları en etkili ve hangi karşılama bölgelerine karşı?",
    "Which serve types (jump, float, jump-float) yield the best results?": "Hangi servis türleri (smaç servis, plancı, sıçramalı plancı) en iyi sonuçları verir?",
    "Reception quality breakdown (perfect, positive, negative, error).": "Karşılama kalitesi dökümü (mükemmel, olumlu, olumsuz, hata).",
    "Which players struggle most under jump serves vs. float serves?": "Hangi oyuncular smaç servislere karşı plancı servislere kıyasla en çok zorlanıyor?",
    "How does poor reception correlate with side-out efficiency?": "Kötü karşılama, sayı alıp servise geçme verimliliğiyle nasıl ilişkili?",
    "Set distribution by zone and by player.": "Bölgeye ve oyuncuya göre pas dağılımı.",
    "How does the setter perform under pressure (bad-pass situations)?": "Pasör baskı altında (kötü karşılama durumlarında) nasıl performans gösteriyor?",
    "Tempo breakdown (quick, medium, high ball) and which yields the best attack outcome.": "Tempo dökümü (hızlı, orta, yüksek top) ve hangisinin en iyi hücum sonucunu verdiği.",
    "Kill percentage and error rate per player, per rotation, and per zone.": "Oyuncu, rotasyon ve bölge başına sayı yüzdesi ve hata oranı.",
    "Which attack zones (pipe, cross, line) have the highest efficiency?": "Hangi hücum bölgeleri (pipe, çapraz, paralel) en yüksek verimliliğe sahip?",
    "How attack efficiency changes in transition vs. side-out.": "Hücum verimliliğinin geçiş hücumunda sayı alıp servise geçmeye kıyasla nasıl değiştiği.",
    "Block-per-set rate per player and rotation.": "Oyuncu ve rotasyon başına set başına blok sayısı.",
    "Which opposing attack zones are most successfully blocked?": "Rakibin hangi hücum bölgeleri en başarılı şekilde bloklanıyor?",
    "How often blocks result in kills vs. touch-outs.": "Blokların ne sıklıkla sayı, ne sıklıkla blok-out ile sonuçlandığı.",
    "Dig success rate per player and per court zone.": "Oyuncu ve saha bölgesi başına savunma başarı oranı.",
    "Which attack types (tip, roll, hard swing) are hardest to defend?": "Hangi hücum türlerinin (plase, yumuşak vuruş, sert vuruş) savunması en zor?",
    "How defensive efficiency affects transition attack success.": "Savunma verimliliğinin geçiş hücumu başarısını nasıl etkilediği.",
    "Which rotations have the highest and lowest point differential?": "Hangi rotasyonların en yüksek ve en düşük sayı farkına sahip olduğu.",
    "Side-out and break-point percentages per rotation.": "Rotasyon başına sayı alıp servise geçme ve break-point yüzdeleri.",
    "Rotation-specific vulnerabilities (e.g. a weak serving or passing rotation).": "Rotasyona özgü zayıflıklar (örneğin zayıf bir servis veya karşılama rotasyonu).",
    "The opponent's most frequently used attack zones and combinations.": "Rakibin en sık kullandığı hücum bölgeleri ve kombinasyonları.",
    "The opponent's go-to attackers in high-pressure situations.": "Rakibin yüksek baskı durumlarındaki başvurduğu hücum oyuncuları.",
    "The serve-receive patterns the opponent favours.": "Rakibin tercih ettiği karşılama düzenleri.",
    "Overall rally-win probability in different score situations.": "Farklı skor durumlarında genel ralı kazanma olasılığı.",
    "How performance changes in sets 1–3 vs. sets 4–5 (fatigue/pressure effects).": "Performansın 1–3. setlerde 4–5. setlere kıyasla nasıl değiştiği (yorgunluk/baskı etkileri).",
    "The correlation between first-ball side-out percentage and match outcome.": "İlk top sayı alıp servise geçme yüzdesi ile maç sonucu arasındaki ilişki.",
    Report: "Rapor",

    // Import page
    "Load DVW file(s)...": "DVW dosyası(ları) yükle…",
    "Load PSVB file(s)...": "PSVB dosyası(ları) yükle…",
    "Load VSM file(s)...": "VSM dosyası(ları) yükle…",
    "Load playlist file...": "Oynatma listesi dosyası yükle…",
    "load one or more DataVolley files":
      "bir veya daha fazla DataVolley dosyası yükle",
    "load one or more VBStats files":
      "bir veya daha fazla VBStats dosyası yükle",
    "load one or more VolleyStation VSM files":
      "bir veya daha fazla VolleyStation VSM dosyası yükle",
    "Convert DVW": "DVW'yi Dönüştür",
    "VSM": "VSM",
    "Convert DVW to VSM...": "DVW'yi VSM'ye Dönüştür…",
    "pick a DataVolley .dvw file to download as .vsm":
      "indirip .vsm olarak kaydetmek için bir DataVolley .dvw dosyası seç",
    "Convert VSM to DVW...": "VSM'yi DVW'ye Dönüştür…",
    "pick a VolleyStation .vsm file to download as .dvw":
      "indirip .dvw olarak kaydetmek için bir VolleyStation .vsm dosyası seç",
    "Converting…": "Dönüştürülüyor…",
    "Converted": "Dönüştürüldü",
    "Failed to convert": "Dönüştürme başarısız oldu",
    "Beta testers only — coming soon for everyone else.":
      "Yalnızca beta test kullanıcıları için — herkes için yakında.",
    "load a playlist": "bir oynatma listesi yükle",
    "Use Shift-JIS Encoder": "Shift-JIS kodlayıcı kullan",
    "Importing…": "İçe aktarılıyor…",
    "Save to Database?": "Veritabanına kaydedilsin mi?",
    "Always save imports automatically": "İçe aktarmaları her zaman otomatik kaydet",
    "View Only": "Yalnızca görüntüle",
    "Save to Database": "Veritabanına kaydet",
    "Error saving to database": "Veritabanına kaydederken hata oluştu",
    "Saved to database": "Veritabanına kaydedildi",
    "Saving to database…": "Veritabanına kaydediliyor…",
    "Upload them to the database now?":
      "Şimdi veritabanına yüklensin mi?",

    // Settings / Options
    Theme: "Tema",
    Options: "Seçenekler",
    "Rotation naming convention": "Rotasyon adlandırma kuralı",
    "Choose how rotations are labelled in reports and analysis.":
      "Rotasyonların raporlarda ve analizlerde nasıl etiketleneceğini seçin.",
    "R1–R6 (rotation sequence)": "R1–R6 (rotasyon sırası)",
    "S1–S6 (setter court position)": "S1–S6 (pasörün saha konumu)",
    "R1: setter in position 1, R2: setter in 6, R3: setter in 5, R4: setter in 4, R5: setter in 3, R6: setter in 2.":
      "R1: pasör 1. bölgede, R2: pasör 6'da, R3: pasör 5'te, R4: pasör 4'te, R5: pasör 3'te, R6: pasör 2'de.",
    "S1: setter in 1, S2: setter in 2, S3: setter in 3, S4: setter in 4, S5: setter in 5, S6: setter in 6.":
      "S1: pasör 1'de, S2: pasör 2'de, S3: pasör 3'te, S4: pasör 4'te, S5: pasör 5'te, S6: pasör 6'da.",
    "Labels:": "Etiketler:",
    "Current theme:": "Mevcut tema:",
    "Choose the language for the app interface.":
      "Uygulama arayüzünün dilini seçin.",
    "Create your own language package or edit a community one, with AI-assisted translation.":
      "Yapay zekâ destekli çeviriyle kendi dil paketinizi oluşturun veya bir topluluk paketini düzenleyin.",

    // About / Footer
    "Copyright © Perana Sports {year}. All rights reserved.":
      "Telif hakkı © Perana Sports {year}. Tüm hakları saklıdır.",
    "VBLive is free to use right now. However Perana Sports reserves the right to charge a subscription fee in due course.":
      "VBLive şu anda ücretsizdir. Ancak Perana Sports, ileride bir abonelik ücreti alma hakkını saklı tutar.",
    "Querries / suggestions are most welcomed.":
      "Sorular / öneriler memnuniyetle karşılanır.",

    // Live
    "Please enter server name to search":
      "Aramak için sunucu adını girin",
    "Broadcast Server": "Yayın sunucusu",
    "Enter DVMateLive Server Name": "DVMateLive sunucu adını girin",
    LIVE: "CANLI",

    // Playlists list
    "Delete Playlist": "Oynatma listesini sil",
    "Are you sure you want to delete the selected playlist?":
      "Seçili oynatma listesini silmek istediğinizden emin misiniz?",
    "My Playlists": "Oynatma listelerim",
    "Shared Playlists": "Paylaşılan oynatma listeleri",
    "Tags:": "Etiketler:",
    "Select...": "Seç…",
    "All Tags": "Tüm etiketler",

    // Share
    "User already added": "Kullanıcı zaten eklendi",
    "Please enter a valid email address":
      "Lütfen geçerli bir e-posta adresi girin",
    "Please select at least one user to share with":
      "Paylaşmak için en az bir kullanıcı seçin",
    "Not Shared": "Paylaşılmadı",
    "Available for everyone": "Herkese açık",
    "Available to selected users":
      "Seçili kullanıcılara açık",
    "Enter VBLive user email address":
      "VBLive kullanıcı e-posta adresini girin",
    "Selected users:": "Seçili kullanıcılar:",
    "Save List": "Listeyi kaydet",
    "Clear List": "Listeyi temizle",
    "Clear Users List": "Kullanıcı listesini temizle",
    "Do you really want to clear all users from list?":
      "Tüm kullanıcıları listeden kaldırmak istediğinizden emin misiniz?",
    "Do you want to save changes before closing?":
      "Kapatmadan önce değişiklikleri kaydetmek ister misiniz?",
    "Enter name to save list to:":
      "Listeyi kaydetmek için bir ad girin:",
    "Enter name to save list to":
      "Listeyi kaydetmek için bir ad girin",

    // AI panels
    "AI settings": "Yapay zekâ ayarları",
    "Setup needed": "Kurulum gerekli",
    "Generate report": "Rapor oluştur",
    "Generate AI report": "Yapay zekâ raporu oluştur",
    "Generate general report": "Genel rapor oluştur",
    "Translate to…": "Şuna çevir…",
    "Follow-up questions": "Takip soruları",
    "Ask a follow-up question…": "Bir takip sorusu sorun…",
    "Ask a follow-up question": "Bir takip sorusu sorun",
    "AI provider": "Yapay zekâ sağlayıcısı",
    Model: "Model",
    Hide: "Gizle",
    Show: "Göster",
    "Your key is stored only in this browser and sent directly to the selected provider.":
      "Anahtarınız yalnızca bu tarayıcıda saklanır ve doğrudan seçili sağlayıcıya gönderilir.",
    "Please enter your API key.": "Lütfen API anahtarınızı girin.",
    "Not enough data to analyse.":
      "Analiz için yeterli veri yok.",
    "Select a player with recorded spikes first.":
      "Önce kayıtlı hücumu olan bir oyuncu seçin.",
    Player: "Oyuncu",
    "Select a player…": "Bir oyuncu seçin…",
    "No players with recorded spikes in the selected matches.":
      "Seçili maçlarda kayıtlı hücumu olan oyuncu yok.",
    "Analyse with AI": "Yapay zekâ ile analiz et",
    "AI scouting report": "Yapay zekâ gözlem raporu",
    "Q:": "S:",
    Ask: "Sor",
    "e.g. Which rotation should we target on serve?":
      "ör. Serviste hangi rotasyonu hedeflemeliyiz?",
    "Saved questions:": "Kaydedilen sorular:",
    "Click to ask again": "Tekrar sormak için tıklayın",
    "Remove saved question": "Kaydedilen soruyu kaldır",

    // AI language options
    Spanish: "İspanyolca",
    French: "Fransızca",
    German: "Almanca",
    Italian: "İtalyanca",
    Portuguese: "Portekizce",
    Dutch: "Felemenkçe",
    Polish: "Lehçe",
    Russian: "Rusça",
    Turkish: "Türkçe",
    Arabic: "Arapça",
    "Chinese (Simplified)": "Çince (Basitleştirilmiş)",
    Japanese: "Japonca",
    Korean: "Korece",
    Vietnamese: "Vietnamca",
    Thai: "Tayca",

    // Box score
    "Show Full Headers": "Tam başlıkları göster",
    "Show Short Headers": "Kısa başlıkları göster",
    "Show Less Players": "Daha az oyuncu göster",
    "Show All Players": "Tüm oyuncuları göster",
    "Attack Kills": "Hücum sayıları",
    "Attack Errors": "Hücum hataları",
    "Total Attempts": "Toplam deneme",
    "Hitting Efficiency": "Hücum verimliliği",
    "Serve Aces": "Servis asları",
    "Serve Errors": "Servis hataları",
    "Pass Errors": "Karşılama hataları",
    "% Perfect Passes": "% kusursuz karşılama",
    "Passing Average": "Karşılama ortalaması",
    "Total Passes": "Toplam karşılama",
    "Set Assists": "Pas asistleri",
    Digs: "Savunmalar",
    "Momentum Chart": "Momentum Grafiği",
    "Key event": "Kilit aksiyon",
    "Break Point %": "Break sayısı %",
    "First Ball Sideout %": "İlk top sideout %",
    "Sideout Transition %": "Geçişte sideout %",
    "FB Sideout Kills %": "İlk top sayılık hücum %",
    "Sideout Opposition Error %": "Rakip hatasıyla sideout %",
    Plus: "Artı",
    Minus: "Eksi",
    Blocks: "Blok sayıları",
    "Positive Serve %": "Pozitif servis %",
    Sideout: "Sideout",
    "Break point": "Break sayısı",
    "Ace / Kill": "Ace / Sayılık vuruş",
    "In play": "Oyunda",
    Error: "Hata",
    "Sideout on a kill": "Sayılık vuruşla sideout",
    "Sideout on an error": "Rakip hatasıyla sideout",
    "Top half": "Üst yarı",
    "Bottom half": "Alt yarı",
    "The centre line steps toward the team that wins each point.": "Orta çizgi, sayıyı kazanan takıma doğru kayar.",
    "No point-by-point data available for this match.": "Bu maç için sayı sayı veri bulunmuyor.",
    "No match data available.": "Maç verisi bulunmuyor.",
    "This report isn't supported on this browser yet — please use Safari.": "Bu rapor bu tarayıcıda henüz desteklenmiyor — lütfen Safari kullanın.",
    "No video available for this match.": "Bu maç için video bulunmuyor.",
    passes: "karşılama",
    "eff.": "ver.",
    Grade: "Not",
    LEFT: "SOL",
    CENTRE: "ORTA",
    RIGHT: "SAĞ",
    SELECTED: "SEÇİLİ",
    Passers: "Karşılayıcılar",
    Calls: "Çağrılar",
    "SIDE OUT": "SIDEOUT",
    TRANSITION: "GEÇİŞ",
    "No events in selection.": "Geçerli seçimde olay yok.",
    "Serve Receives By Players": "Oyunculara göre karşılamalar",
    "Serve Receive By Zones": "Bölgeye göre karşılama",
    "Dig Errors": "Savunma hataları",
    "Dig Average": "Savunma ortalaması",
    "Block Solos": "Tekli bloklar",
    "Block Assists": "Blok asistleri",
    "Block Errors": "Blok hataları",
    "Points Scored": "Kazanılan sayılar",
    TEAM: "TAKIM",

    // Sideout report
    "ALL ROWS": "TÜM SIRALAR",
    "ROWS ": "SIRALAR ",
    "ROW ": "SIRA ",
    "FB Kills": "FB hücum sayıları",
    "Opp. Serve Errors": "Rakip servis hataları",
    "FB Opp. Errors": "FB rakip hataları",
    "Kills/Blocks": "Hücumlar/Bloklar",
    "Opp. Errors": "Rakip hataları",
    Unsuccessful: "Başarısız",

    // Attack zones
    "No events in selected zone.":
      "Seçili bölgede olay yok.",
    "Show all attacks. Or click on a zone to show attacks in that zone.":
      "Tüm hücumları göster. Veya o bölgedeki hücumları görmek için bir bölgeye tıklayın.",
    "OPPONENTS (": "RAKİPLER (",

    // Video analysis filters
    "All Types": "Tüm türler",
    Serve: "Servis",
    "Serve-Receive": "Karşılama",
    Set: "Pas",
    "Setter's Call": "Pasör çağrısı",
    "Disable this skill": "Bu beceriyi devre dışı bırak",
    "Enable this skill": "Bu beceriyi etkinleştir",
    "Teams & Players": "Takımlar ve Oyuncular",
    "Match State": "Maç Durumu",
    "Custom Code": "Özel Kod",
    "Search... (comma-separated for multiple)": "Ara... (birden fazla için virgülle ayırın)",
    vs: "vs",
    "Rewind 60 seconds": "60 saniye geri",
    "Rewind 1 frame": "1 kare geri",
    "Forward 1 frame": "1 kare ileri",
    "Forward 60 seconds": "60 saniye ileri",
    "Previous Clip": "Önceki Klip",
    Replay: "Tekrar Oynat",
    "Next Clip": "Sonraki Klip",
    Phases: "Aşamalar",
    "Score Range": "Skor Aralığı",
    "Set Winner": "Set Galibi",
    Type: "Tür",
    Strength: "Güç",
    "Setter Calls": "Pasör çağrıları",
    "Setter Call": "Pasör çağrısı",
    Freeball: "Serbest top",
    "Close (0-2)": "Yakın (0-2)",
    "Moderate (3-5)": "Orta (3-5)",
    "Lopsided (6+)": "Büyük Fark (6+)",
    "Focus Team": "Odak Takım",
    Oppositions: "Rakipler",
    "Focus Team Won": "Odak Takım Kazandı",
    "Oppositions Won": "Rakipler Kazandı",
    "Advanced Filters": "Gelişmiş Filtreler",
    Players: "Oyuncular",
    Opponents: "Rakipler",
    "Event Type": "Olay Türü",
    "Event Result": "Olay Sonucu",
    "Attack Type": "Hücum Türü",
    "Hit Type": "Vuruş Türü",
    "Block Type": "Blok Türü",
    "Attack Combo": "Hücum Kombinasyonu",
    Apply: "Uygula",
    Rotation: "Rotasyon",
    Skill: "Beceri",
    "Lead Time": "Başlangıç Süresi",
    Duration: "Süre",
    Timing: "Zamanlama",
    "Go Live": "Canlıya Git",
    "Playback has fallen behind the live broadcast — click to jump to now.": "Oynatma canlı yayının gerisinde kaldı — şimdiye atlamak için tıklayın.",
    "Video timing": "Video Zamanlaması",
    "Default lead time (seconds before an event to start playback) and clip duration, per skill.": "Varsayılan başlangıç süresi (oynatmanın başlaması için olaydan kaç saniye önce) ve beceriye göre klip süresi.",
    Cloud: "Bulut",
    Local: "Yerel",
    "Relay host": "Röle sunucusu",
    "IP or hostname of the machine running the local relay.": "Yerel röleyi çalıştıran makinenin IP adresi veya ana bilgisayar adı.",
    Spike: "Hücum",
    Block: "Blok",
    Defence: "Savunma",
    "All Results": "Tüm sonuçlar",
    Hard: "Sert",
    Soft: "Yumuşak",
    Tip: "Plase",
    "Not Specified": "Belirtilmemiş",
    "H - High": "H - Yüksek",
    "M- Medium": "M - Orta",
    "Q - Quick": "Q - Hızlı",
    "T - Tense": "T - Gergin",
    "U - Super": "U - Süper",
    "F - Fast": "F - Çabuk",
    "O - Other": "O - Diğer",
    "B0 - No Block": "B0 - Blok yok",
    "B1 - 1 Block": "B1 - 1 blok",
    "B2 - 2 Blocks": "B2 - 2 blok",
    "B3 - 3 Blocks": "B3 - 3 blok",
    "BS - Seams": "BS - Boşluklar",

    // All filters panel
    ALL: "TÜMÜ",
    NONE: "HİÇBİRİ",

    // Playlist editor
    "Remove Item": "Öğeyi kaldır",
    "Do you really want to remove the selected item?":
      "Seçili öğeyi kaldırmak istediğinizden emin misiniz?",
    "Description is required": "Açıklama gereklidir",
    "Comments are required": "Yorumlar gereklidir",
    "Tags are required": "Etiketler gereklidir",
    "Playlist saved successfully": "Oynatma listesi başarıyla kaydedildi",
    "Error saving playlist": "Oynatma listesi kaydedilirken hata oluştu",

    // Language editor
    "My saved packages": "Kaydettiğim paketler",
    "Loading shared languages…": "Paylaşılan diller yükleniyor…",
    "Community shared languages": "Toplulukça paylaşılan diller",
    by: "ekleyen",
    "based on": "temel alınan",
    "or create new": "veya yeni oluştur",
    Use: "Kullan",
    "New Language Package": "Yeni dil paketi",
    "Language Name": "Dil adı",
    "Language Code": "Dil kodu",
    "Flag Emoji": "Bayrak emojisi",
    "Base language (reference)": "Temel dil (referans)",
    "If not English, both English and the reference language will be shown.":
      "İngilizce değilse, hem İngilizce hem de referans dil gösterilir.",
    "Start Editing": "Düzenlemeye başla",
    "Save Key": "Anahtarı kaydet",
    "AI key set": "Yapay zekâ anahtarı ayarlandı",
    "Set API key for AI translation":
      "Yapay zekâ çevirisi için API anahtarı ayarla",
    "AI key ✓": "Yapay zekâ anahtarı ✓",
    "Set AI key": "Yapay zekâ anahtarı ayarla",
    "AI: Translate All": "Yapay zekâ: Tümünü çevir",
    "Translating…": "Çevriliyor…",
    "AI translate section": "Bölümü yapay zekâ ile çevir",
    "Clear All": "Tümünü temizle",
    "Saving…": "Kaydediliyor…",
    English: "İngilizce",
    new: "yeni",
    "Enter translation…": "Çeviriyi girin…",
    "Share with other VBLive users?":
      "Diğer VBLive kullanıcılarıyla paylaşılsın mı?",
    "Would you like to share the": "Şunu paylaşmak ister misiniz:",
    "language package with all VBLive users? Anyone can then copy and build upon it.":
      "dil paketini tüm VBLive kullanıcılarıyla? Herkes bunu kopyalayıp geliştirebilir.",
    "No, just save for me": "Hayır, yalnızca benim için kaydet",
    "Yes, share it!": "Evet, paylaş!",
    "Language package saved and shared with all VBLive users!":
      "Dil paketi kaydedildi ve tüm VBLive kullanıcılarıyla paylaşıldı!",
    "Language package saved!": "Dil paketi kaydedildi!",
    "Clear all translations? This cannot be undone.":
      "Tüm çeviriler temizlensin mi? Bu işlem geri alınamaz.",

    // Additional UI phrases
    Teams: "Takımlar",
    "Shared By": "Paylaşan",
    "View Activity": "Görüntüleme etkinliği",
    Viewer: "İzleyici",
    "Date / Time": "Tarih / Saat",
    "No views recorded yet.": "Henüz görüntüleme kaydedilmedi.",
    "Date / Scores": "Tarih / Skorlar",
    "Stats API source": "İstatistik API kaynağı",
    "load one or more AVC VIS files": "bir veya daha fazla AVC VIS dosyası yükle",
    "Points to close a set": "Seti bitirmek için gereken sayı",
    "Require 2-point advantage": "2 sayı farkı gerektir",
    Roster: "Kadro",
    Clone: "Çoğalt",
    "Edit team": "Takımı düzenle",
    "New player": "Yeni oyuncu",
    "Search player...": "Oyuncu ara…",
    "Match Centre": "Maç Merkezi",
    "Points Won": "Kazanılan sayılar",
    "Opp. Error": "Rakip hatası",
    Total: "Toplam",
    "Match Skills": "Maç becerileri",
    Dig: "Savunma",
    "Best Scorers": "En iyi sayı üreticiler",
    "Power Play": "Power Play",
    Match: "Maç",
    "Player scouting": "Oyuncu izleme",
    "Team deep analysis": "Derinlemesine takım analizi",
    "All Players": "Tüm oyuncular",
    "All Sets": "Tüm setler",
    "All Rotations": "Tüm rotasyonlar",
    "All Combos": "Tüm kombinasyonlar",
    "All Calls": "Tüm çağrılar",
    "All Events": "Tüm olaylar",
    "Select all items": "Tüm ögeleri seç",
    "Unselect all items": "Tüm ögelerin seçimini kaldır",
    "Create play list": "Oynatma listesi oluştur",
    "Point by Point": "Sayı sayı",
    // i18n backfill pass — previously English-fallback-only keys
    "Access denied": "Erişim reddedildi",
    "Aces": "Aslar",
    "ACES": "ASLAR",
    "Add report": "Rapor ekle",
    "Admin": "Yönetici",
    "Admins": "Yöneticiler",
    "Advanced": "Gelişmiş",
    "All set!": "Her şey hazır!",
    "Analyse team with AI": "Takımı yapay zekâ ile analiz et",
    "Are you sure you want to delete the selected play list?": "Seçili oynatma listesini silmek istediğinizden emin misiniz?",
    "Assign": "Ata",
    "Assign playlist": "Oynatma listesi ata",
    "Assigned": "Atandı",
    "At least one admin is required.": "En az bir yönetici gereklidir.",
    "Attack Combo / Setter Call": "Hücum kombinasyonu / Pasör çağrısı",
    "Attack diagram": "Hücum diyagramı",
    "Attacks": "Hücumlar",
    "ATTACKS": "HÜCUMLAR",
    "Auto-detected a custom set-closing score for": "Şunlar için özel set bitiş skoru otomatik olarak algılandı:",
    "BLOCK": "BLOK",
    "BLOCKS": "BLOKLAR",
    "Beta testers": "Beta test kullanıcıları",
    "Clear filter": "Filtreyi temizle",
    "Close Set": "Seti kapat",
    "Close menu": "Menüyü kapat",
    "Code": "Kod",
    "Coding defaults & shortcuts": "Kodlama varsayılanları ve kısayolları",
    "Continue": "Devam et",
    "Could not load match data. The match may no longer be available.": "Maç verileri yüklenemedi. Maç artık mevcut olmayabilir.",
    "Could not load stats for these matches.": "Bu maçlar için istatistikler yüklenemedi.",
    "Could not load this match.": "Bu maç yüklenemedi.",
    "Could not load this playlist.": "Bu oynatma listesi yüklenemedi.",
    "Could not load this report.": "Bu rapor yüklenemedi.",
    "Could not save the report.": "Rapor kaydedilemedi.",
    "Could not share the report. Please try again.": "Rapor paylaşılamadı. Lütfen tekrar deneyin.",
    "Court horizontal (net vertical)": "Saha yatay (file dikey)",
    "Court vertical (net horizontal)": "Saha dikey (file yatay)",
    "Create": "Oluştur",
    "Create a new team from this roster": "Bu kadrodan yeni bir takım oluştur",
    "DIFFERENCE": "FARK",
    "DV Code": "DV Kodu",
    "Delete this report?": "Bu rapor silinsin mi?",
    "Detected a custom set-closing score of": "Özel set bitiş skoru algılandı:",
    "Double Sub": "Çift değişiklik",
    "Double Sub: now pick a bench player for the flashing OPPOSITE slot…": "Çift değişiklik: şimdi yanıp sönen OPPOSITE (çaprazcı) pozisyonu için yedek bir oyuncu seçin…",
    "Double Sub: pick a bench player for the flashing SETTER slot…": "Çift değişiklik: yanıp sönen SETTER (pasör) pozisyonu için yedek bir oyuncu seçin…",
    "Double Substitution — swap the Setter and Opposite for two incoming players": "Çift değişiklik — Pasörü ve çaprazcıyı giren iki oyuncuyla değiştirin",
    "Drag to draw the ball path; pause briefly to mark a touch (reception/dig/block), release to finish.": "Top yolunu çizmek için sürükleyin; bir temas (karşılama/savunma/blok) işaretlemek için kısaca duraklatın, bitirmek için bırakın.",
    "Draw": "Çiz",
    "Draw & Code": "Çiz ve Kodla",
    "Draw & Code — draw ball paths on the court to code the rally": "Çiz ve Kodla — ralliyi kodlamak için sahada top yollarını çizin",
    "Edit Lineup": "Dizilişi düzenle",
    "Edit Lineup — freely rearrange the starting lineup before the set begins": "Dizilişi düzenle — set başlamadan önce başlangıç dizilişini serbestçe değiştirin",
    "Emails in this list can manage beta testers and other admins.": "Bu listedeki e-postalar beta test kullanıcılarını ve diğer yöneticileri yönetebilir.",
    "Emails in this list get early access to AI Reports and other gated features. Changes take effect immediately.": "Bu listedeki e-postalar, AI Raporlarına ve diğer kısıtlı özelliklere erken erişim sağlar. Değişiklikler hemen etkili olur.",
    "Enter a valid email address.": "Geçerli bir e-posta adresi girin.",
    "Exit fullscreen": "Tam ekrandan çık",
    "Extended": "Genişletilmiş",
    "Failed to generate PDF.": "PDF oluşturulamadı.",
    "Failed to save changes.": "Değişiklikler kaydedilemedi.",
    "Filtered by": "Filtrelendi:",
    "First name": "Ad",
    "For stronger security later, consider moving admin checks to a Firebase Auth custom claim instead of this email list.": "Daha güçlü güvenlik için ileride, yönetici kontrollerini bu e-posta listesi yerine özel bir Firebase Auth claim'ine taşımayı düşünün.",
    "Fullscreen": "Tam ekran",
    "Generate an AI report from these matches": "Bu maçlardan bir AI raporu oluştur",
    "How often (in seconds) the events list is updated during a live session.": "Canlı oturum sırasında olay listesinin kaç saniyede bir güncelleneceği.",
    "Last name": "Soyadı",
    "Leave blank to use standard rules (25 points, 15 for a 5th/deciding set)": "Standart kuralları kullanmak için boş bırakın (25 sayı, karar seti olan 5. set için 15)",
    "Live session refresh interval": "Canlı oturum yenileme aralığı",
    "Load VIS file(s)...": "VIS dosyası(ları) yükle…",
    "Load this team's last-saved starting lineup (positions, setter and liberos)": "Bu takımın son kaydedilen başlangıç dizilişini yükle (pozisyonlar, pasör ve liberolar)",
    "Loading…": "Yükleniyor…",
    "Manually end the current set at its current score, regardless of the configured winning score / 2-point advantage rule": "Ayarlanmış kazanma skoru / 2 sayı fark kuralından bağımsız olarak, mevcut seti geçerli skorda manuel olarak bitir",
    "Migration finished with some issues": "Geçiş bazı sorunlarla tamamlandı",
    "Most of your data was migrated, but some items could not be moved. You can retry now or continue and try again later.": "Verilerinizin çoğu taşındı, ancak bazı öğeler taşınamadı. Şimdi tekrar deneyebilir veya devam edip daha sonra tekrar deneyebilirsiniz.",
    "Mute": "Sessize al",
    "New team": "Yeni takım",
    "Next": "Sonraki",
    "Nickname": "Takma ad",
    "No Power Play": "Power Play yok",
    "No admins yet.": "Henüz yönetici yok.",
    "No beta testers yet.": "Henüz beta test kullanıcısı yok.",
    "No coded events to analyse.": "Analiz edilecek kodlanmış olay yok.",
    "No coded events to export.": "Dışa aktarılacak kodlanmış olay yok.",
    "No matches.": "Maç yok.",
    "No players added yet.": "Henüz oyuncu eklenmedi.",
    "No players recorded.": "Kayıtlı oyuncu yok.",
    "No players yet.": "Henüz oyuncu yok.",
    "No playlists assigned.": "Atanmış oynatma listesi yok.",
    "No playlists found.": "Oynatma listesi bulunamadı.",
    "No reports saved.": "Kaydedilmiş rapor yok.",
    "No roster players available.": "Kullanılabilir kadro oyuncusu yok.",
    "No substitutions": "Değişiklik yok",
    "No teams found.": "Takım bulunamadı.",
    "No teams yet.": "Henüz takım yok.",
    "No teams.": "Takım yok.",
    "Nominate Setter": "Pasör belirle",
    "Notify these users by email (optional)": "Bu kullanıcıları e-posta ile bilgilendir (isteğe bağlı)",
    "OPP REC": "RAK. KARŞ.",
    "OPP. ERRORS": "RAKİP HATALARI",
    "Open / Download": "Aç / İndir",
    "Open local file": "Yerel dosya aç",
    "Open menu": "Menüyü aç",
    "Opposition Passing — use the opposition's reception formation on the diagram": "Rakip karşılama — diyagramda rakibin karşılama düzenini kullan",
    "Paste the report (Markdown supported)…": "Raporu yapıştırın (Markdown desteklenir)…",
    "Pause": "Duraklat",
    "Play": "Oynat",
    "Playing squad": "Maç kadrosu",
    "Playing squad is full. Remove a player from the squad before adding another.": "Maç kadrosu dolu. Başka bir oyuncu eklemeden önce kadrodan bir oyuncu çıkarın.",
    "Please sign in to save reports.": "Rapor kaydetmek için oturum açın.",
    "Power Play Only": "Sadece Power Play",
    "Record a team error (no player) — edit later to add the player and event type": "Bir takım hatası kaydet (oyuncusuz) — oyuncuyu ve olay türünü eklemek için daha sonra düzenleyin",
    "Record at least one event before making substitutions": "Değişiklik yapmadan önce en az bir olay kaydedin",
    "Redo": "Yinele",
    "Remove": "Kaldır",
    "Remove share": "Paylaşımı kaldır",
    "Remove yourself as admin? You will immediately lose access to this page.": "Kendinizi yönetici olarak kaldırmak mı istiyorsunuz? Bu sayfaya erişiminizi hemen kaybedersiniz.",
    "Report saved and shared.": "Rapor kaydedildi ve paylaşıldı.",
    "Report saved to the player's profile.": "Rapor oyuncunun profiline kaydedildi.",
    "Report title": "Rapor başlığı",
    "Reports": "Raporlar",
    "Result": "Sonuç",
    "Retry": "Tekrar dene",
    "Return to live coding": "Canlı kodlamaya dön",
    "Rotation is not permitted while making substitutions": "Değişiklik yaparken rotasyona izin verilmez",
    "SERVE": "SERVİS",
    "SETS": "SETLER",
    "Save & Share": "Kaydet ve Paylaş",
    "Save this report so you can share it with other users": "Diğer kullanıcılarla paylaşabilmek için bu raporu kaydedin",
    "Save this report to the player's profile": "Bu raporu oyuncunun profiline kaydet",
    "Save to player": "Oyuncuya kaydet",
    "Saved lineup": "Kayıtlı diziliş",
    "Scroll, drag or swipe to scrub": "Kaydırmak için fareyle çevirin, sürükleyin veya kaydırın",
    "Search existing players...": "Mevcut oyuncuları ara…",
    "Search playlists…": "Oynatma listelerinde ara…",
    "Select a match video to view while entering lineups.": "Dizilişleri girerken izlemek için bir maç videosu seçin.",
    "Select a player.": "Bir oyuncu seçin.",
    "Select a team.": "Bir takım seçin.",
    "Setting up your account": "Hesabınız ayarlanıyor",
    "Shaded rows are played under a Power Play — HP/AP marks the calling team, bold gold scores are bonus (double) points.": "Gölgeli satırlar bir Power Play sırasında oynandı — HP/AP çağıran takımı belirtir, kalın altın renkli skorlar bonus (çift) puandır.",
    "Shared by": "Paylaşan:",
    "Shared with me": "Benimle paylaşılanlar",
    "Show help": "Yardımı göster",
    "Show public playlists": "Genel oynatma listelerini göster",
    "Standard": "Standart",
    "Substitutions made — tap to view": "Değişiklik yapıldı — görüntülemek için dokunun",
    "Tap the blocking player.": "Blok yapan oyuncuya dokunun.",
    "Team colour": "Takım rengi",
    "Team name": "Takım adı",
    "That email is already in the list.": "Bu e-posta zaten listede.",
    "The report is too large to save. Try a single match or fewer sets.": "Rapor kaydedilemeyecek kadar büyük. Tek bir maç veya daha az set deneyin.",
    "This match has no Power Play calls.": "Bu maçta Power Play çağrısı yok.",
    "This player has no name to save the report against.": "Bu oyuncunun raporun kaydedileceği bir adı yok.",
    "This team has no designated setter. Pick the player who is setting.": "Bu takımın belirlenmiş bir pasörü yok. Pasörlük yapan oyuncuyu seçin.",
    "Toggle video player": "Video oynatıcıyı aç/kapat",
    "Unmute": "Sesi aç",
    "Untitled playlist": "Adsız oynatma listesi",
    "Video URL…": "Video URL'si…",
    "We're moving your data to a faster, more reliable home. This happens only once.": "Verilerinizi daha hızlı ve güvenilir bir yere taşıyoruz. Bu yalnızca bir kez gerçekleşir.",
    "You don't have permission to view this page.": "Bu sayfayı görüntüleme izniniz yok.",
    "Your data has been migrated successfully.": "Verileriniz başarıyla taşındı.",
    "Your teams and players will appear here after your data is migrated.": "Verileriniz taşındıktan sonra takımlarınız ve oyuncularınız burada görünecek.",
    "bonus point": "bonus puan",
    "bonus points": "bonus puan",
    "event": "olay",
    "events": "olaylar",
    "file(s).": "dosya.",
    "is not on this team's roster.": "bu takımın kadrosunda yok.",
    "loaded": "yüklendi",
    "matches": "maç",
    "more": "daha",
    "players": "oyuncu",
    "points (no value was entered) — applied automatically.": "puan (değer girilmedi) — otomatik olarak uygulandı.",
    "roster": "kadro",
    "seconds": "saniye",
    "selected": "seçildi",
    "session(s)?": "oturum?",
    "showing": "gösteriliyor",
    "tournaments": "turnuva",
    "…and": "…ve",
    "● marks which team served the point.": "● hangi takımın sayıyı servis ettiğini gösterir.",
    // i18n backfill pass (Catapult + misc)
    "Centimetres (cm)": "Santimetre (cm)",
    "Inches (in)": "İnç (in)",
    "Jump height units": "Sıçrama yüksekliği birimleri",
    "Choose how Catapult jump heights are displayed and filtered.": "Catapult sıçrama yüksekliklerinin nasıl gösterileceğini ve filtreleneceğini seçin.",
    "Select Catapult Focus file...": "Catapult Focus dosyası seç…",
    "Select a Catapult Focus CSV export to view jump data for this match.": "Bu maç için sıçrama verilerini görüntülemek üzere bir Catapult Focus CSV dışa aktarımı seçin.",
    "Loaded from saved match data": "Kaydedilmiş maç verilerinden yüklendi",
    "No jump data found in this file.": "Bu dosyada sıçrama verisi bulunamadı.",
    "Could not read this file. Please check it is a Catapult Focus CSV export.": "Bu dosya okunamadı. Lütfen bir Catapult Focus CSV dışa aktarımı olduğundan emin olun.",
    "Could not save jump data with this match.": "Sıçrama verileri bu maçla birlikte kaydedilemedi.",
    "Jump data saved with this match.": "Sıçrama verileri bu maçla birlikte kaydedildi.",
    "Show players with no match stats": "Maç istatistiği olmayan oyuncuları göster",
    "hidden": "gizli",
    "jumps": "sıçrama",
    "periods": "periyot",
    "Sync offset": "Senkronizasyon kayması",
    "matched": "eşleşti",
    "match events now show jump height/duration in Video Analysis": "maç olayı artık Video Analizinde sıçrama yüksekliği/süresini gösteriyor",
    "Team": "Takım",
    "Team A": "Takım A",
    "Team B": "Takım B",
    "Which team is this Catapult file for?": "Bu Catapult dosyası hangi takım için?",
    "We couldn't confidently match the players in this Focus file to one team. Please select which team it belongs to.": "Bu Focus dosyasındaki oyuncuları güvenle bir takıma eşleştiremedik. Lütfen hangi takıma ait olduğunu seçin.",
    "This match has no video attached yet.": "Bu maça henüz video eklenmedi.",
    "No synced jump events found for this player.": "Bu oyuncu için senkronize edilmiş sıçrama olayı bulunamadı.",
    "No match loaded.": "Yüklenmiş bir maç yok.",
    "avg": "ort.",
    "max": "maks.",
    "Sync": "Senkronizasyon",
    "Jump Report": "Sıçrama Raporu",
    "Watch Video": "Videoyu İzle",
    "Save PDF": "PDF Kaydet",
    "Generated by VBLive": "VBLive tarafından oluşturuldu",
    "Deep analysis of {team} from ball-by-ball event data, covering serving, reception, setting, attack, blocking, defence, rotations and opponent tendencies. Best used with the Anthropic (Claude) provider.": "{team} takımının top top olay verilerine dayalı derinlemesine analizi; servis, karşılama, pas, hücum, blok, savunma, rotasyonlar ve rakip eğilimlerini kapsar. Anthropic (Claude) sağlayıcısıyla kullanılması önerilir.",
    "teams": "takım",
  },

  ko: {
    // Information page
    Information: "정보",
    "Release notes": "릴리스 노트",
    "Contact us": "문의하기",
    "Questions, feedback or suggestions are most welcome.": "질문, 의견 또는 제안을 언제든지 환영합니다.",
    // DV match coding
    Lineups: "라인업",
    Point: "득점",
    Video: "비디오",
    Defaults: "기본값",
    "[team][NN][skill][type?][eval][zoneFrom?[szFrom?][zoneTo?[szTo?]]]": "[팀][NN][기술][유형?][평가][시작구역?[시작하위구역?][도착구역?[도착하위구역?]]]",
    Vertical: "세로",
    Horizontal: "가로",
    "Swap sides": "코트 교체",
    Subs: "교체",
    Substitutions: "교체",
    Court: "코트",
    Home: "홈",
    Away: "원정",
    Games: "세트",
    Enter: "입력",
    Undo: "실행 취소",
    "Match saved": "경기가 저장되었습니다",
    "Please sign in to save this match.": "이 경기를 저장하려면 로그인하세요.",
    "Nothing to save yet.": "아직 저장할 내용이 없습니다.",
    "Could not save the match. Please try again.": "경기를 저장하지 못했습니다. 다시 시도하세요.",
    Discard: "변경 사항 취소",
    "Resumed autosaved coding": "자동 저장된 코딩을 복원했습니다",
    "Changes discarded": "변경 사항이 취소되었습니다",
    "Discard all changes and revert to the starting point?":
      "모든 변경 사항을 취소하고 시작 지점으로 되돌리시겠습니까?",
    Clear: "지우기",
    "Prev set": "이전 세트",
    "Next set": "다음 세트",
    "Match Setup": "경기 설정",
    "First serve": "첫 서브",
    "Start Match": "경기 시작",
    "Resume Match": "경기 재개",
    Skip: "건너뛰기",
    "Select an event then drag to draw ball path": "이벤트를 선택한 후 드래그하여 공 경로를 그리세요",
    "Drag on court to set ball path": "코트에서 드래그하여 공 경로를 설정하세요",
    "No events yet – enter a DV code above and press Enter.": "아직 이벤트가 없습니다 – 위에 DV 코드를 입력하고 입력을 누르세요.",
    "DataVolley code reference": "DataVolley 코드 참조",
    Format: "형식",
    Skills: "기술",
    Evaluation: "평가",
    "Skill / type omission": "기술 / 유형 생략",
    "Skill and/or type may be omitted; defaults fill in the rest.": "기술 및/또는 유형을 생략할 수 있으며 나머지는 기본값으로 채워집니다.",
    "Dot-coding (S→R and A→B pairs)": "점 코딩(S→R 및 A→B 쌍)",
    "Chaining & aliases": "연결 및 별칭",
    "team:": "팀:",
    "omit → defaults to A": "생략 → 기본값 A",
    "NN: 1 or 2 digit jersey, e.g. 1, 01, 12": "NN: 1~2자리 등번호, 예: 1, 01, 12",
    "sub-zone: a b c d appended to zone digit, e.g. 5b or 5b1a": "하위 구역: 구역 숫자 뒤에 a b c d 추가, 예: 5b 또는 5b1a",
    "S Serve · R Reception · E Set · A Attack · B Block · D Dig · F Freeball": "S 서브 · R 리시브 · E 세트 · A 공격 · B 블로킹 · D 디그 · F 프리볼",
    "# kill/ace · + positive · ! ok · - negative · / overpass · = error": "# 득점/에이스 · + 우수 · ! 보통 · - 미흡 · / 오버패스 · = 범실",
    "E.g.": "예:",
    "serve with skill=A (default), type=H, eval=+, zone=5.": "skill=A(기본값), type=H, eval=+, zone=5로 서브.",
    "type-only (no skill marker), defaults skill from context.": "유형만(기술 표시 없음), 기술은 맥락에서 기본 적용.",
    "serve coded, reception auto-linked.": "서브 코딩됨, 리시브 자동 연결됨.",
    "attack coded, block auto-linked.": "공격 코딩됨, 블로킹 자동 연결됨.",
    "Dot connects the reception eval to the corresponding serve eval via the serve→receive mapping.": "점은 서브→리시브 매핑을 통해 리시브 평가를 해당 서브 평가에 연결합니다.",
    "two events in one string.": "한 문자열에 두 이벤트.",
    "manual point A/B.": "수동 득점 A/B.",
    "serve ace/error.": "서브 에이스/범실.",
    "serve with eval.": "평가가 있는 서브.",
    // Stats, sideouts, filters & video tooltips
    "Block Kills": "블로킹 득점",
    "Side Out Percentage": "사이드아웃 성공률",
    "All First Ball": "전체 첫 공",
    "First Ball Kills": "첫 공 득점",
    "Good Serve": "좋은 서브",
    "Break Points": "브레이크 포인트",
    Game: "세트",
    Row: "포지션",
    "First Ball Sideouts": "첫 공 사이드아웃",
    "All Sideouts": "전체 사이드아웃",
    All: "전체",
    None: "없음",
    Receiver: "리시버",
    "# Passes": "리시브 수",
    "Perfect %": "퍼펙트 %",
    "Positive %": "정확 %",
    "Sideout %": "사이드아웃 %",
    "FB Sideout %": "첫 공 사이드아웃 %",
    "Pass Hits": "리시브 후 공격",
    "Pass Hit Kills": "리시브 후 득점",
    "Pass Hit Errors": "리시브 후 범실",
    "Show Passes": "리시브 표시",
    "Show Attacks": "공격 표시",
    "Error Pass": "리시브 실패",
    "1 Pass": "리시브 1",
    "2 Pass": "리시브 2",
    "Perfect Pass": "퍼펙트 리시브",
    "Successful SO": "사이드아웃 성공",
    "First Ball SO": "첫 공 사이드아웃",
    "Unsuccessful SO": "사이드아웃 실패",
    RECEIVES: "리시브",
    Sets: "세트",
    Rotations: "로테이션",
    "Attack Combos": "공격 콤비네이션",
    Stages: "단계",
    Display: "표시",
    Setters: "세터",
    Attackers: "공격수",
    Results: "결과",
    Source: "출처",
    Cones: "구역",
    "Select None": "선택 해제",
    "All Serves": "전체 서브",
    "All Returns": "전체 리시브",
    Filters: "필터",
    "Reset all filters": "모든 필터 초기화",
    "Show video clips of filtered attacks": "필터된 공격의 동영상 클립 표시",
    "Spikes for the selected player": "선택한 선수의 공격",
    "Click on the zones to view video of attacks": "구역을 클릭하여 공격 영상을 확인하세요",
    "Expand All": "모두 펼치기",
    "Collapse All": "모두 접기",
    "Import Radar File": "Radar 파일 가져오기",
    "Select local video file": "로컬 동영상 파일 선택",
    "Save to database": "데이터베이스에 저장",
    "Load and play online video": "온라인 동영상 불러와 재생",
    "Synchronise video with events": "동영상을 이벤트와 동기화",
    "Rewind 10 seconds": "10초 뒤로",
    "Rewind 1 second": "1초 뒤로",
    "Forward 1 second": "1초 앞으로",
    "Forward 10 seconds": "10초 앞으로",
    "Set timing for events": "이벤트 타이밍 설정",

    // Navigation
    Matches: "경기",
    Live: "라이브",
    Import: "가져오기",
    Playlists: "재생목록",
    "New Match": "새 경기",
    // New Match / Match Coding gate
    "Match Coding": "경기 코딩",
    "Match Coding — Coming Soon": "경기 코딩 — 곧 출시",
    "Live match coding is on the way. Here's a preview of what's coming.":
      "실시간 경기 코딩이 곧 제공됩니다. 다음은 곧 출시될 기능의 미리 보기입니다.",
    Settings: "설정",
    Language: "언어",
    "Your profile": "내 프로필",
    "Sign out": "로그아웃",
    "Translation Editor": "번역 편집기",

    // Common actions
    Save: "저장",
    Cancel: "취소",
    Delete: "삭제",
    Share: "공유",
    Edit: "편집",
    New: "새로 만들기",
    Refresh: "새로고침",
    Search: "검색",
    Reset: "초기화",
    Close: "닫기",
    "Sub for": "교체",
    "No players available": "사용 가능한 선수 없음",
    "Show full roster": "전체 명단 보기",
    "Available only": "가능한 선수만",
    "Maximum substitutions reached": "최대 교체 횟수 도달",
    Back: "뒤로",
    "Download PDF": "PDF 다운로드",
    "Loading...": "불러오는 중…",
    "Select All": "전체 선택",
    "Unselect All": "전체 선택 해제",
    Yes: "예",
    No: "아니요",
    Add: "추가",
    Load: "불러오기",
    Confirm: "확인",
    Open: "열기",
    show: "표시",

    // Auth
    "Sign In": "로그인",
    "Sign Up": "회원가입",
    "Sign Out": "로그아웃",
    Email: "이메일",
    Password: "비밀번호",
    "Confirm Password": "비밀번호 확인",
    "Forgot password?": "비밀번호를 잊으셨나요?",
    "Forgot Password": "비밀번호 찾기",
    "Don't have an account?": "계정이 없으신가요?",
    "Sign up for free!": "무료로 가입하세요!",
    "All ready signed up?": "이미 가입하셨나요?",
    "Error Signing In": "로그인 오류",
    "Signed up successfully": "가입이 완료되었습니다",
    "Error signing up": "가입 오류",
    "Enter your email": "이메일을 입력하세요",
    "Send Reset Email": "재설정 이메일 보내기",
    "Email was sent to": "이메일을 보낸 주소:",
    "Could not send reset email":
      "재설정 이메일을 보낼 수 없습니다",

    // Matches list
    "Team 1": "팀 1",
    "Team 2": "팀 2",
    Tournament: "대회",
    Scores: "점수",
    Date: "날짜",
    Actions: "작업",
    Owner: "소유자",
    Description: "설명",
    Comments: "댓글",
    Tags: "태그",
    "Add player": "선수 추가",
    "Add team": "팀 추가",
    "Edit playlist": "재생 목록 편집",
    "Merge Teams": "팀 합치기",
    "Roster differences detected between teams:": "팀 간 선수 명단 차이가 감지되었습니다:",
    "Review the roster in the next step before merging.": "합치기 전에 다음 단계에서 선수 명단을 검토하세요.",
    "At least 2 team records must exist in your Teams list for the selected matches.": "선택한 경기에 대해 팀 목록에 최소 2개의 팀 기록이 있어야 합니다.",
    "Merge...": "합치기...",
    "Select 2 or more team records to merge into one.": "합칠 팀 기록을 2개 이상 선택하세요.",
    "Filter teams...": "팀 필터...",
    "teams selected": "팀 선택됨",
    "Select Teams": "팀 선택",
    "Reconcile Roster": "선수 명단 통합",
    "Confirm Merge": "합치기 확인",
    "Merged team name": "합쳐진 팀 이름",
    "shirt number(s) have conflicting names — review below.": "등번호에 이름 충돌이 있습니다 — 아래를 확인하세요.",
    "Shirt": "등번호",
    "players included": "선수 포함됨",
    "No players to reconcile.": "통합할 선수가 없습니다.",
    "Use this name": "이 이름 사용",
    "Create merged team": "합쳐진 팀 만들기",
    "with": "포함",
    "duplicate team records": "개의 중복 팀 기록",
    "Consolidate duplicate player records and reassign playlist links": "중복 선수 기록 통합 및 재생목록 링크 재지정",
    "This cannot be undone.": "이 작업은 취소할 수 없습니다.",
    "Teams being merged:": "합쳐지는 팀:",
    "Step": "단계",
    "of": "/",
    "Merging...": "합치는 중...",
    "Players & Teams": "선수 및 팀",
    "No players or teams assigned.": "배정된 선수나 팀이 없습니다.",
    "Search players...": "선수 검색...",
    "Search teams...": "팀 검색...",
    "Saving...": "저장 중...",
    "My Matches": "내 경기",
    "Shared Matches": "공유된 경기",
    "Search team...": "팀 검색…",
    "All Teams": "모든 팀",
    "Multi Match Reports": "다중 경기 보고서",
    "Delete match": "경기 삭제",
    "Remove from my shared matches": "내 공유 경기에서 제거",
    "Share match": "경기 공유",
    "Edit match": "경기 편집",
    "Open video analysis": "영상 분석 열기",
    "Delete Match": "경기 삭제",
    "Are you sure you want to delete the selected match?":
      "선택한 경기를 삭제하시겠습니까?",
    "No matches selected.": "선택된 경기가 없습니다.",

    // New Match
    "← Back": "← 뒤로",
    "Team A (Home)": "팀 A (홈)",
    "Team B (Away)": "팀 B (원정)",
    "Select team…": "팀 선택…",
    "Tournament (optional)": "대회 (선택사항)",
    "Tournament / competition name": "대회 / 경기 이름",
    "Match date": "경기 날짜",
    "Start Coding →": "코딩 시작 →",

    // Reports
    Summary: "요약",
    "Box Score": "기록표",
    "Side-outs Report": "사이드아웃 보고서",
    "Serve Receives": "리시브",
    "Attack Zones": "공격 구역",
    "Hitting Charts": "공격 차트",
    "Video Analysis": "영상 분석",
    Dashboard: "대시보드",
    Charts: "차트",
    "AI Report": "AI 보고서",
    // AI Reports page
    "AI Reports": "AI 보고서",
    "AI Reports — Coming Soon": "AI 보고서 — 곧 출시",
    "AI-powered match reports and deep analysis are on the way. Here are some sample reports to show what's coming.":
      "AI 기반 경기 보고서와 심층 분석이 곧 제공됩니다. 다음은 미리 보여 드리는 샘플 보고서입니다.",
    "Preview not available.": "미리 보기를 사용할 수 없습니다.",
    "Open sample": "샘플 열기",
    "No matches were loaded. Select one or more matches and try again.":
      "불러온 경기가 없습니다. 하나 이상의 경기를 선택한 후 다시 시도하세요.",
    "Attack Analysis (English)": "공격 분석 (영어)",
    "Blocking Analysis (Vietnamese)": "블로킹 분석 (베트남어)",
    "Opponent Tendencies (Japanese)": "상대 성향 (일본어)",
    // Deep AI analysis
    "Deep AI Analysis": "AI 심층 분석",
    "AI Deep Analysis": "AI 심화 분석",
    "Load at least one match with coded events to generate the analysis.": "분석을 생성하려면 코딩된 이벤트가 있는 경기를 하나 이상 불러오세요.",
    "Run the deep analysis one section at a time to keep each request small. Edit or add questions below, then generate. Best used with the Anthropic (Claude) provider.":
      "각 요청을 작게 유지하려면 심층 분석을 섹션별로 실행하세요. 아래에서 질문을 편집하거나 추가한 다음 생성하세요. Anthropic (Claude) 제공자와 함께 사용하는 것이 가장 좋습니다.",
    "Download CSV": "CSV 다운로드",
    "Analysis section": "분석 섹션",
    analysing: "분석 중",
    "All sections (uses the most credits)": "모든 섹션 (크레딧을 가장 많이 사용)",
    "All sections": "모든 섹션",
    Questions: "질문",
    "one per line, edit or add your own": "한 줄에 하나씩, 편집하거나 직접 추가하세요",
    "Reset to defaults": "기본값으로 재설정",
    "Add a question…": "질문 추가…",
    "Runs every section using the questions you've set for each. This sends one large request and uses the most credits.":
      "각 섹션에 설정한 질문으로 모든 섹션을 실행합니다. 하나의 큰 요청을 보내며 크레딧을 가장 많이 사용합니다.",
    "Generate full analysis": "전체 분석 생성",
    "Generate section analysis": "섹션 분석 생성",
    Serving: "서브",
    Reception: "리시브",
    Setting: "세트",
    Attack: "공격",
    Blocking: "블로킹",
    "Defence / Digging": "수비 / 디그",
    "Rotation Analysis": "로테이션 분석",
    "Opponent Tendencies (Scouting)": "상대 성향 (스카우팅)",
    "Overall Team Metrics": "팀 전체 지표",
    "Serve efficiency (aces, errors, and pressured receptions forced).": "서브 효율(서브 득점, 범실, 유도한 압박 리시브).",
    "Which servers are most effective, and against which reception zones?": "어떤 서버가 가장 효과적이며, 어떤 리시브 구역을 공략해야 하는가?",
    "Which serve types (jump, float, jump-float) yield the best results?": "어떤 서브 유형(점프, 플로터, 점프 플로터)이 가장 좋은 결과를 내는가?",
    "Reception quality breakdown (perfect, positive, negative, error).": "리시브 퀄리티 분석(완벽, 구정, 부정, 범실).",
    "Which players struggle most under jump serves vs. float serves?": "점프 서브와 플로터 서브 중 어느 쪽에서 어떤 선수가 가장 힘들어하는가?",
    "How does poor reception correlate with side-out efficiency?": "부정확한 리시브가 사이드아웃 효율과 어떻게 연관되는가?",
    "Set distribution by zone and by player.": "구역별 및 선수별 세트 분배.",
    "How does the setter perform under pressure (bad-pass situations)?": "세터가 압박(나쁜 리시브 상황)에서 어떻게 수행하는가?",
    "Tempo breakdown (quick, medium, high ball) and which yields the best attack outcome.": "템포 분석(퀵, 미디엄, 하이볼) 및 어느 것이 가장 좋은 공격 결과를 내는가.",
    "Kill percentage and error rate per player, per rotation, and per zone.": "선수별, 로테이션별, 구역별 공격 성공률과 범실률.",
    "Which attack zones (pipe, cross, line) have the highest efficiency?": "어떤 공격 구역(파이프, 크로스, 라인)이 가장 효율이 높은가?",
    "How attack efficiency changes in transition vs. side-out.": "공격 효율이 트랜지션과 사이드아웃에서 어떻게 달라지는가.",
    "Block-per-set rate per player and rotation.": "선수별 및 로테이션별 세트당 블록 수.",
    "Which opposing attack zones are most successfully blocked?": "상대의 어느 공격 구역이 가장 성공적으로 블록되는가?",
    "How often blocks result in kills vs. touch-outs.": "블록이 득점으로 이어지는 비율과 블록 아웃 비율.",
    "Dig success rate per player and per court zone.": "선수별 및 코트 구역별 수비 성공률.",
    "Which attack types (tip, roll, hard swing) are hardest to defend?": "어떤 공격 유형(팁, 롤, 강타)이 가장 수비하기 어려운가?",
    "How defensive efficiency affects transition attack success.": "수비 효율이 트랜지션 공격 성공에 어떻게 영향을 미치는가.",
    "Which rotations have the highest and lowest point differential?": "어느 로테이션이 가장 높고 낮은 득실점 차이를 보이는가?",
    "Side-out and break-point percentages per rotation.": "로테이션별 사이드아웃 및 브레이크포인트 비율.",
    "Rotation-specific vulnerabilities (e.g. a weak serving or passing rotation).": "로테이션별 취약점(예: 서브나 리시브가 약한 로테이션).",
    "The opponent's most frequently used attack zones and combinations.": "상대가 가장 자주 사용하는 공격 구역과 조합.",
    "The opponent's go-to attackers in high-pressure situations.": "고압 상황에서 상대가 주로 찾는 공격수.",
    "The serve-receive patterns the opponent favours.": "상대가 선호하는 서브 리시브 패턴.",
    "Overall rally-win probability in different score situations.": "다양한 점수 상황에서의 전반적인 랠리 승리 확률.",
    "How performance changes in sets 1–3 vs. sets 4–5 (fatigue/pressure effects).": "1–3세트와 4–5세트에서 경기력이 어떻게 달라지는가(피로/압박 효과).",
    "The correlation between first-ball side-out percentage and match outcome.": "첫 공 사이드아웃 비율과 경기 결과 간의 상관관계.",
    Report: "보고서",

    // Import page
    "Load DVW file(s)...": "DVW 파일 불러오기…",
    "Load PSVB file(s)...": "PSVB 파일 불러오기…",
    "Load VSM file(s)...": "VSM 파일 불러오기…",
    "Load playlist file...": "재생목록 파일 불러오기…",
    "load one or more DataVolley files":
      "하나 이상의 DataVolley 파일 불러오기",
    "load one or more VBStats files":
      "하나 이상의 VBStats 파일 불러오기",
    "load one or more VolleyStation VSM files":
      "하나 이상의 VolleyStation VSM 파일 불러오기",
    "Convert DVW": "DVW 변환",
    "VSM": "VSM",
    "Convert DVW to VSM...": "DVW를 VSM으로 변환…",
    "pick a DataVolley .dvw file to download as .vsm":
      ".vsm으로 다운로드할 DataVolley .dvw 파일 선택",
    "Convert VSM to DVW...": "VSM을 DVW로 변환…",
    "pick a VolleyStation .vsm file to download as .dvw":
      ".dvw로 다운로드할 VolleyStation .vsm 파일 선택",
    "Converting…": "변환 중…",
    "Converted": "변환됨",
    "Failed to convert": "변환 실패",
    "Beta testers only — coming soon for everyone else.":
      "베타 테스터 전용 — 다른 사용자는 곧 제공될 예정입니다.",
    "load a playlist": "재생목록 불러오기",
    "Use Shift-JIS Encoder": "Shift-JIS 인코더 사용",
    "Importing…": "가져오는 중…",
    "Save to Database?": "데이터베이스에 저장하시겠습니까?",
    "Always save imports automatically": "항상 자동으로 가져오기 저장",
    "View Only": "보기만",
    "Save to Database": "데이터베이스에 저장",
    "Error saving to database": "데이터베이스 저장 오류",
    "Saved to database": "데이터베이스에 저장됨",
    "Saving to database…": "데이터베이스에 저장 중…",
    "Upload them to the database now?":
      "지금 데이터베이스에 업로드하시겠습니까?",

    // Settings / Options
    Theme: "테마",
    Options: "옵션",
    "Rotation naming convention": "로테이션 명명 규칙",
    "Choose how rotations are labelled in reports and analysis.":
      "보고서와 분석에서 로테이션을 표시하는 방식을 선택하세요.",
    "R1–R6 (rotation sequence)": "R1–R6 (로테이션 순서)",
    "S1–S6 (setter court position)": "S1–S6 (세터 코트 위치)",
    "R1: setter in position 1, R2: setter in 6, R3: setter in 5, R4: setter in 4, R5: setter in 3, R6: setter in 2.":
      "R1: 세터가 1번 위치, R2: 세터가 6번, R3: 세터가 5번, R4: 세터가 4번, R5: 세터가 3번, R6: 세터가 2번.",
    "S1: setter in 1, S2: setter in 2, S3: setter in 3, S4: setter in 4, S5: setter in 5, S6: setter in 6.":
      "S1: 세터가 1번, S2: 세터가 2번, S3: 세터가 3번, S4: 세터가 4번, S5: 세터가 5번, S6: 세터가 6번.",
    "Labels:": "라벨:",
    "Current theme:": "현재 테마:",
    "Choose the language for the app interface.":
      "앱 인터페이스의 언어를 선택하세요.",
    "Create your own language package or edit a community one, with AI-assisted translation.":
      "AI 보조 번역으로 나만의 언어 패키지를 만들거나 커뮤니티 패키지를 편집하세요.",

    // About / Footer
    "Copyright © Perana Sports {year}. All rights reserved.":
      "저작권 © Perana Sports {year}. 모든 권리 보유.",
    "VBLive is free to use right now. However Perana Sports reserves the right to charge a subscription fee in due course.":
      "VBLive는 현재 무료로 사용할 수 있습니다. 다만 Perana Sports는 추후 구독료를 부과할 권리를 보유합니다.",
    "Querries / suggestions are most welcomed.":
      "문의 / 제안은 언제나 환영합니다.",

    // Live
    "Please enter server name to search":
      "검색할 서버 이름을 입력하세요",
    "Broadcast Server": "방송 서버",
    "Enter DVMateLive Server Name": "DVMateLive 서버 이름 입력",
    LIVE: "라이브",

    // Playlists list
    "Delete Playlist": "재생목록 삭제",
    "Are you sure you want to delete the selected playlist?":
      "선택한 재생목록을 삭제하시겠습니까?",
    "My Playlists": "내 재생목록",
    "Shared Playlists": "공유된 재생목록",
    "Tags:": "태그:",
    "Select...": "선택…",
    "All Tags": "모든 태그",

    // Share
    "User already added": "이미 추가된 사용자입니다",
    "Please enter a valid email address":
      "유효한 이메일 주소를 입력하세요",
    "Please select at least one user to share with":
      "공유할 사용자를 한 명 이상 선택하세요",
    "Not Shared": "공유 안 함",
    "Available for everyone": "모두에게 공개",
    "Available to selected users":
      "선택한 사용자에게 공개",
    "Enter VBLive user email address":
      "VBLive 사용자 이메일 주소 입력",
    "Selected users:": "선택한 사용자:",
    "Save List": "목록 저장",
    "Clear List": "목록 지우기",
    "Clear Users List": "사용자 목록 지우기",
    "Do you really want to clear all users from list?":
      "목록에서 모든 사용자를 제거하시겠습니까?",
    "Do you want to save changes before closing?":
      "닫기 전에 변경 사항을 저장하시겠습니까?",
    "Enter name to save list to:":
      "목록을 저장할 이름을 입력하세요:",
    "Enter name to save list to":
      "목록을 저장할 이름을 입력하세요",

    // AI panels
    "AI settings": "AI 설정",
    "Setup needed": "설정 필요",
    "Generate report": "보고서 생성",
    "Generate AI report": "AI 보고서 생성",
    "Generate general report": "일반 보고서 생성",
    "Translate to…": "번역 대상…",
    "Follow-up questions": "후속 질문",
    "Ask a follow-up question…": "후속 질문을 입력하세요…",
    "Ask a follow-up question": "후속 질문하기",
    "AI provider": "AI 제공업체",
    Model: "모델",
    Hide: "숨기기",
    Show: "표시",
    "Your key is stored only in this browser and sent directly to the selected provider.":
      "키는 이 브라우저에만 저장되며 선택한 제공업체로 직접 전송됩니다.",
    "Please enter your API key.": "API 키를 입력하세요.",
    "Not enough data to analyse.":
      "분석할 데이터가 충분하지 않습니다.",
    "Select a player with recorded spikes first.":
      "먼저 공격 기록이 있는 선수를 선택하세요.",
    Player: "선수",
    "Select a player…": "선수 선택…",
    "No players with recorded spikes in the selected matches.":
      "선택한 경기에 공격 기록이 있는 선수가 없습니다.",
    "Analyse with AI": "AI로 분석",
    "AI scouting report": "AI 스카우팅 보고서",
    "Q:": "질문:",
    Ask: "질문",
    "e.g. Which rotation should we target on serve?":
      "예: 서브 시 어떤 로테이션을 노려야 할까요?",
    "Saved questions:": "저장된 질문:",
    "Click to ask again": "다시 질문하려면 클릭",
    "Remove saved question": "저장된 질문 제거",

    // AI language options
    Spanish: "스페인어",
    French: "프랑스어",
    German: "독일어",
    Italian: "이탈리아어",
    Portuguese: "포르투갈어",
    Dutch: "네덜란드어",
    Polish: "폴란드어",
    Russian: "러시아어",
    Turkish: "터키어",
    Arabic: "아랍어",
    "Chinese (Simplified)": "중국어(간체)",
    Japanese: "일본어",
    Korean: "한국어",
    Vietnamese: "베트남어",
    Thai: "태국어",

    // Box score
    "Show Full Headers": "전체 헤더 표시",
    "Show Short Headers": "짧은 헤더 표시",
    "Show Less Players": "선수 적게 표시",
    "Show All Players": "모든 선수 표시",
    "Attack Kills": "공격 득점",
    "Attack Errors": "공격 범실",
    "Total Attempts": "총 시도",
    "Hitting Efficiency": "공격 효율",
    "Serve Aces": "서브 에이스",
    "Serve Errors": "서브 범실",
    "Pass Errors": "리시브 범실",
    "% Perfect Passes": "정확한 리시브 %",
    "Passing Average": "리시브 평균",
    "Total Passes": "총 리시브",
    "Set Assists": "세트 어시스트",
    Digs: "디그",
    "Momentum Chart": "모멘텀 차트",
    "Key event": "핵심 플레이",
    "Break Point %": "브레이크 포인트 %",
    "First Ball Sideout %": "첫 볼 사이드아웃 %",
    "Sideout Transition %": "전환 사이드아웃 %",
    "FB Sideout Kills %": "첫 볼 득점 공격 %",
    "Sideout Opposition Error %": "상대 실수로 인한 사이드아웃 %",
    Plus: "플러스",
    Minus: "마이너스",
    Blocks: "블로킹 득점",
    "Positive Serve %": "긍정적 서브 %",
    Sideout: "사이드아웃",
    "Break point": "브레이크 포인트",
    "Ace / Kill": "에이스 / 득점 공격",
    "In play": "진행 중",
    Error: "실수",
    "Sideout on a kill": "득점 공격으로 끝난 사이드아웃",
    "Sideout on an error": "상대 실수로 끝난 사이드아웃",
    "Top half": "상단",
    "Bottom half": "하단",
    "The centre line steps toward the team that wins each point.": "중앙선은 각 포인트를 획득한 팀 쪽으로 이동합니다.",
    "No point-by-point data available for this match.": "이 경기에 대한 포인트별 데이터가 없습니다.",
    "No match data available.": "경기 데이터가 없습니다.",
    "This report isn't supported on this browser yet — please use Safari.": "이 보고서는 아직 이 브라우저에서 지원되지 않습니다 — Safari를 사용해 주세요.",
    "No video available for this match.": "이 경기에 대한 영상이 없습니다.",
    passes: "리시브",
    "eff.": "효율",
    Grade: "등급",
    LEFT: "왼쪽",
    CENTRE: "중앙",
    RIGHT: "오른쪽",
    SELECTED: "선택됨",
    Passers: "리시버",
    Calls: "콜",
    "SIDE OUT": "사이드아웃",
    TRANSITION: "전환",
    "No events in selection.": "현재 선택에 이벤트가 없습니다.",
    "Serve Receives By Players": "선수별 리시브",
    "Serve Receive By Zones": "구역별 리시브",
    "Dig Errors": "디그 범실",
    "Dig Average": "디그 평균",
    "Block Solos": "단독 블로킹",
    "Block Assists": "블로킹 어시스트",
    "Block Errors": "블로킹 범실",
    "Points Scored": "득점",
    TEAM: "팀",

    // Sideout report
    "ALL ROWS": "전체 로테이션",
    "ROWS ": "로테이션 ",
    "ROW ": "로테이션 ",
    "FB Kills": "FB 공격 득점",
    "Opp. Serve Errors": "상대 서브 범실",
    "FB Opp. Errors": "FB 상대 범실",
    "Kills/Blocks": "공격/블로킹",
    "Opp. Errors": "상대 범실",
    Unsuccessful: "실패",

    // Attack zones
    "No events in selected zone.":
      "선택한 구역에 이벤트가 없습니다.",
    "Show all attacks. Or click on a zone to show attacks in that zone.":
      "모든 공격을 표시합니다. 또는 구역을 클릭하면 해당 구역의 공격을 표시합니다.",
    "OPPONENTS (": "상대 (",

    // Video analysis filters
    "All Types": "모든 유형",
    Serve: "서브",
    "Serve-Receive": "리시브",
    Set: "세트",
    "Setter's Call": "세터 콜",
    "Disable this skill": "이 기술 비활성화",
    "Enable this skill": "이 기술 활성화",
    "Teams & Players": "팀 및 선수",
    "Match State": "경기 상태",
    "Custom Code": "사용자 지정 코드",
    "Search... (comma-separated for multiple)": "검색... (여러 개는 쉼표로 구분)",
    vs: "vs",
    "Rewind 60 seconds": "60초 뒤로",
    "Rewind 1 frame": "1프레임 뒤로",
    "Forward 1 frame": "1프레임 앞으로",
    "Forward 60 seconds": "60초 앞으로",
    "Previous Clip": "이전 클립",
    Replay: "다시 보기",
    "Next Clip": "다음 클립",
    Phases: "단계",
    "Score Range": "점수 범위",
    "Set Winner": "세트 승자",
    Type: "유형",
    Strength: "강도",
    "Setter Calls": "세터 콜",
    "Setter Call": "세터 콜",
    Freeball: "프리볼",
    "Close (0-2)": "박빙 (0-2)",
    "Moderate (3-5)": "보통 (3-5)",
    "Lopsided (6+)": "큰 격차 (6+)",
    "Focus Team": "관심 팀",
    Oppositions: "상대팀",
    "Focus Team Won": "관심 팀 승리",
    "Oppositions Won": "상대팀 승리",
    "Advanced Filters": "고급 필터",
    Players: "선수",
    Opponents: "상대팀",
    "Event Type": "이벤트 유형",
    "Event Result": "이벤트 결과",
    "Attack Type": "공격 유형",
    "Hit Type": "타구 유형",
    "Block Type": "블로킹 유형",
    "Attack Combo": "공격 콤보",
    Apply: "적용",
    Rotation: "로테이션",
    Skill: "기술",
    "Lead Time": "리드 타임",
    Duration: "지속 시간",
    Timing: "타이밍",
    "Go Live": "실시간으로 이동",
    "Playback has fallen behind the live broadcast — click to jump to now.": "재생이 실시간 방송보다 뒤처졌습니다 — 클릭하여 현재 시점으로 이동하세요.",
    "Video timing": "비디오 타이밍",
    "Default lead time (seconds before an event to start playback) and clip duration, per skill.": "기본 리드 타임(이벤트 재생 시작 전 초 단위)과 기술별 클립 길이.",
    Cloud: "클라우드",
    Local: "로컬",
    "Relay host": "릴레이 호스트",
    "IP or hostname of the machine running the local relay.": "로컬 릴레이를 실행 중인 기기의 IP 또는 호스트 이름.",
    Spike: "공격",
    Block: "블로킹",
    Defence: "수비",
    "All Results": "모든 결과",
    Hard: "강타",
    Soft: "연타",
    Tip: "페인트",
    "Not Specified": "지정 안 함",
    "H - High": "H - 높은 볼",
    "M- Medium": "M - 중간 볼",
    "Q - Quick": "Q - 속공",
    "T - Tense": "T - 평행 볼",
    "U - Super": "U - 슈퍼",
    "F - Fast": "F - 빠른 볼",
    "O - Other": "O - 기타",
    "B0 - No Block": "B0 - 블로킹 없음",
    "B1 - 1 Block": "B1 - 1인 블로킹",
    "B2 - 2 Blocks": "B2 - 2인 블로킹",
    "B3 - 3 Blocks": "B3 - 3인 블로킹",
    "BS - Seams": "BS - 틈새",

    // All filters panel
    ALL: "전체",
    NONE: "없음",

    // Playlist editor
    "Remove Item": "항목 제거",
    "Do you really want to remove the selected item?":
      "선택한 항목을 제거하시겠습니까?",
    "Description is required": "설명은 필수입니다",
    "Comments are required": "댓글은 필수입니다",
    "Tags are required": "태그는 필수입니다",
    "Playlist saved successfully": "재생목록이 저장되었습니다",
    "Error saving playlist": "재생목록 저장 오류",

    // Language editor
    "My saved packages": "저장한 패키지",
    "Loading shared languages…": "공유된 언어 불러오는 중…",
    "Community shared languages": "커뮤니티 공유 언어",
    by: "작성자",
    "based on": "기반",
    "or create new": "또는 새로 만들기",
    Use: "사용",
    "New Language Package": "새 언어 패키지",
    "Language Name": "언어 이름",
    "Language Code": "언어 코드",
    "Flag Emoji": "국기 이모지",
    "Base language (reference)": "기본 언어 (참조)",
    "If not English, both English and the reference language will be shown.":
      "영어가 아닌 경우 영어와 참조 언어가 함께 표시됩니다.",
    "Start Editing": "편집 시작",
    "Save Key": "키 저장",
    "AI key set": "AI 키 설정됨",
    "Set API key for AI translation":
      "AI 번역용 API 키 설정",
    "AI key ✓": "AI 키 ✓",
    "Set AI key": "AI 키 설정",
    "AI: Translate All": "AI: 전체 번역",
    "Translating…": "번역 중…",
    "AI translate section": "AI로 섹션 번역",
    "Clear All": "전체 지우기",
    "Saving…": "저장 중…",
    English: "영어",
    new: "새로 만듦",
    "Enter translation…": "번역을 입력하세요…",
    "Share with other VBLive users?":
      "다른 VBLive 사용자와 공유하시겠습니까?",
    "Would you like to share the": "다음을 공유하시겠습니까:",
    "language package with all VBLive users? Anyone can then copy and build upon it.":
      "언어 패키지를 모든 VBLive 사용자와 공유하시겠습니까? 누구나 이를 복사하여 발전시킬 수 있습니다.",
    "No, just save for me": "아니요, 저만 저장합니다",
    "Yes, share it!": "예, 공유합니다!",
    "Language package saved and shared with all VBLive users!":
      "언어 패키지가 저장되어 모든 VBLive 사용자와 공유되었습니다!",
    "Language package saved!": "언어 패키지가 저장되었습니다!",
    "Clear all translations? This cannot be undone.":
      "모든 번역을 지우시겠습니까? 이 작업은 취소할 수 없습니다.",

    // Additional UI phrases
    Teams: "팀",
    "Shared By": "공유자",
    "View Activity": "조회 활동",
    Viewer: "조회자",
    "Date / Time": "날짜 / 시간",
    "No views recorded yet.": "아직 조회 기록이 없습니다.",
    "Date / Scores": "날짜 / 스코어",
    "Stats API source": "통계 API 소스",
    "load one or more AVC VIS files": "AVC VIS 파일 하나 이상 불러오기",
    "Points to close a set": "세트 종료 점수",
    "Require 2-point advantage": "2점 차 승리 요구",
    Roster: "로스터",
    Clone: "복제",
    "Edit team": "팀 수정",
    "New player": "새 선수",
    "Search player...": "선수 검색…",
    "Match Centre": "매치 센터",
    "Points Won": "획득 점수",
    "Opp. Error": "상대 실수",
    Total: "합계",
    "Match Skills": "경기 기술",
    Dig: "디그",
    "Best Scorers": "최다 득점자",
    "Power Play": "파워 플레이",
    Match: "경기",
    "Player scouting": "선수 스카웃팅",
    "Team deep analysis": "팀 심층 분석",
    "All Players": "전체 선수",
    "All Sets": "전체 세트",
    "All Rotations": "전체 로테이션",
    "All Combos": "전체 콤비네이션",
    "All Calls": "전체 콜",
    "All Events": "전체 이벤트",
    "Select all items": "모든 항목 선택",
    "Unselect all items": "모든 항목 선택 해제",
    "Create play list": "재생목록 만들기",
    "Point by Point": "포인트별 기록",
    // i18n backfill pass — previously English-fallback-only keys
    "Access denied": "접근이 거부되었습니다",
    "Aces": "서브 에이스",
    "ACES": "서브 에이스",
    "Add report": "리포트 추가",
    "Admin": "관리자",
    "Admins": "관리자",
    "Advanced": "고급",
    "All set!": "모두 준비되었습니다!",
    "Analyse team with AI": "AI로 팀 분석",
    "Are you sure you want to delete the selected play list?": "선택한 재생 목록을 삭제하시겠습니까?",
    "Assign": "할당",
    "Assign playlist": "재생 목록 할당",
    "Assigned": "할당됨",
    "At least one admin is required.": "최소 한 명의 관리자가 필요합니다.",
    "Attack Combo / Setter Call": "공격 콤보 / 세터 콜",
    "Attack diagram": "공격 다이어그램",
    "Attacks": "공격",
    "ATTACKS": "공격",
    "Auto-detected a custom set-closing score for": "다음 항목에 대해 사용자 지정 세트 종료 점수를 자동으로 감지했습니다:",
    "BLOCK": "블로킹",
    "BLOCKS": "블로킹",
    "Beta testers": "베타 테스터",
    "Clear filter": "필터 지우기",
    "Close Set": "세트 종료",
    "Close menu": "메뉴 닫기",
    "Code": "코드",
    "Coding defaults & shortcuts": "코딩 기본값 및 단축키",
    "Continue": "계속",
    "Could not load match data. The match may no longer be available.": "경기 데이터를 불러올 수 없습니다. 경기를 더 이상 사용할 수 없을 수 있습니다.",
    "Could not load stats for these matches.": "이 경기들의 통계를 불러올 수 없습니다.",
    "Could not load this match.": "이 경기를 불러올 수 없습니다.",
    "Could not load this playlist.": "이 재생 목록을 불러올 수 없습니다.",
    "Could not load this report.": "이 리포트를 불러올 수 없습니다.",
    "Could not save the report.": "리포트를 저장할 수 없습니다.",
    "Could not share the report. Please try again.": "리포트를 공유할 수 없습니다. 다시 시도해 주세요.",
    "Court horizontal (net vertical)": "코트 가로 (네트 세로)",
    "Court vertical (net horizontal)": "코트 세로 (네트 가로)",
    "Create": "생성",
    "Create a new team from this roster": "이 명단으로 새 팀 만들기",
    "DIFFERENCE": "차이",
    "DV Code": "DV 코드",
    "Delete this report?": "이 리포트를 삭제하시겠습니까?",
    "Detected a custom set-closing score of": "사용자 지정 세트 종료 점수가 감지됨:",
    "Double Sub": "더블 서브",
    "Double Sub: now pick a bench player for the flashing OPPOSITE slot…": "더블 서브: 깜빡이는 오퍼짓 자리에 벤치 선수를 선택하세요…",
    "Double Sub: pick a bench player for the flashing SETTER slot…": "더블 서브: 깜빡이는 세터 자리에 벤치 선수를 선택하세요…",
    "Double Substitution — swap the Setter and Opposite for two incoming players": "더블 서브 — 세터와 오퍼짓을 두 명의 교체 선수로 동시에 교체",
    "Drag to draw the ball path; pause briefly to mark a touch (reception/dig/block), release to finish.": "드래그하여 공의 경로를 그립니다. 잠시 멈추면 터치(리시브/디그/블로킹)가 표시되고, 놓으면 완료됩니다.",
    "Draw": "그리기",
    "Draw & Code": "그리기 & 코딩",
    "Draw & Code — draw ball paths on the court to code the rally": "그리기 & 코딩 — 코트에 공의 경로를 그려 랠리를 코딩합니다",
    "Edit Lineup": "라인업 편집",
    "Edit Lineup — freely rearrange the starting lineup before the set begins": "라인업 편집 — 세트 시작 전에 선발 라인업을 자유롭게 재배치",
    "Emails in this list can manage beta testers and other admins.": "이 목록의 이메일은 베타 테스터와 다른 관리자를 관리할 수 있습니다.",
    "Emails in this list get early access to AI Reports and other gated features. Changes take effect immediately.": "이 목록의 이메일은 AI 리포트 및 기타 제한된 기능에 조기 액세스할 수 있습니다. 변경 사항은 즉시 적용됩니다.",
    "Enter a valid email address.": "유효한 이메일 주소를 입력하세요.",
    "Exit fullscreen": "전체 화면 종료",
    "Extended": "확장",
    "Failed to generate PDF.": "PDF 생성에 실패했습니다.",
    "Failed to save changes.": "변경 사항을 저장하지 못했습니다.",
    "Filtered by": "필터링 기준:",
    "First name": "이름",
    "For stronger security later, consider moving admin checks to a Firebase Auth custom claim instead of this email list.": "향후 더 강력한 보안을 위해, 이 이메일 목록 대신 Firebase Auth 커스텀 클레임으로 관리자 확인을 옮기는 것을 고려하세요.",
    "Fullscreen": "전체 화면",
    "Generate an AI report from these matches": "이 경기들로 AI 리포트 생성",
    "How often (in seconds) the events list is updated during a live session.": "라이브 세션 중 이벤트 목록이 업데이트되는 빈도(초).",
    "Last name": "성",
    "Leave blank to use standard rules (25 points, 15 for a 5th/deciding set)": "표준 규칙을 사용하려면 비워 두세요 (25점, 5세트/결정세트는 15점)",
    "Live session refresh interval": "라이브 세션 새로고침 간격",
    "Load VIS file(s)...": "VIS 파일 불러오기…",
    "Load this team's last-saved starting lineup (positions, setter and liberos)": "이 팀의 마지막으로 저장된 선발 라인업 불러오기 (포지션, 세터, 리베로)",
    "Loading…": "로딩 중…",
    "Manually end the current set at its current score, regardless of the configured winning score / 2-point advantage rule": "설정된 승리 점수/2점 차 규칙과 관계없이 현재 세트를 현재 점수로 수동 종료",
    "Migration finished with some issues": "일부 문제와 함께 마이그레이션이 완료되었습니다",
    "Most of your data was migrated, but some items could not be moved. You can retry now or continue and try again later.": "데이터의 대부분이 마이그레이션되었지만 일부 항목은 이동할 수 없었습니다. 지금 다시 시도하거나 계속 진행한 뒤 나중에 다시 시도할 수 있습니다.",
    "Mute": "음소거",
    "New team": "새 팀",
    "Next": "다음",
    "Nickname": "별명",
    "No Power Play": "파워 플레이 없음",
    "No admins yet.": "아직 관리자가 없습니다.",
    "No beta testers yet.": "아직 베타 테스터가 없습니다.",
    "No coded events to analyse.": "분석할 코딩된 이벤트가 없습니다.",
    "No coded events to export.": "내보낼 코딩된 이벤트가 없습니다.",
    "No matches.": "경기가 없습니다.",
    "No players added yet.": "아직 추가된 선수가 없습니다.",
    "No players recorded.": "기록된 선수가 없습니다.",
    "No players yet.": "아직 선수가 없습니다.",
    "No playlists assigned.": "할당된 재생 목록이 없습니다.",
    "No playlists found.": "재생 목록을 찾을 수 없습니다.",
    "No reports saved.": "저장된 리포트가 없습니다.",
    "No roster players available.": "사용 가능한 명단 선수가 없습니다.",
    "No substitutions": "교체 없음",
    "No teams found.": "팀을 찾을 수 없습니다.",
    "No teams yet.": "아직 팀이 없습니다.",
    "No teams.": "팀이 없습니다.",
    "Nominate Setter": "세터 지정",
    "Notify these users by email (optional)": "이 사용자들에게 이메일로 알림 보내기 (선택사항)",
    "OPP REC": "상대 리시브",
    "OPP. ERRORS": "상대 실책",
    "Open / Download": "열기 / 다운로드",
    "Open local file": "로컬 파일 열기",
    "Open menu": "메뉴 열기",
    "Opposition Passing — use the opposition's reception formation on the diagram": "상대 리시브 — 다이어그램에 상대의 리시브 포메이션 사용",
    "Paste the report (Markdown supported)…": "리포트를 붙여넣으세요 (마크다운 지원)…",
    "Pause": "일시정지",
    "Play": "재생",
    "Playing squad": "출전 명단",
    "Playing squad is full. Remove a player from the squad before adding another.": "출전 명단이 가득 찼습니다. 다른 선수를 추가하기 전에 명단에서 한 명을 제거하세요.",
    "Please sign in to save reports.": "리포트를 저장하려면 로그인하세요.",
    "Power Play Only": "파워 플레이만",
    "Record a team error (no player) — edit later to add the player and event type": "팀 실책 기록 (선수 없음) — 나중에 선수와 이벤트 유형을 추가하도록 편집",
    "Record at least one event before making substitutions": "교체를 하기 전에 최소 하나의 이벤트를 기록하세요",
    "Redo": "다시 실행",
    "Remove": "제거",
    "Remove share": "공유 제거",
    "Remove yourself as admin? You will immediately lose access to this page.": "관리자에서 자신을 제거하시겠습니까? 이 페이지에 대한 접근 권한을 즉시 잃게 됩니다.",
    "Report saved and shared.": "리포트가 저장되고 공유되었습니다.",
    "Report saved to the player's profile.": "리포트가 선수 프로필에 저장되었습니다.",
    "Report title": "리포트 제목",
    "Reports": "리포트",
    "Result": "결과",
    "Retry": "다시 시도",
    "Return to live coding": "라이브 코딩으로 돌아가기",
    "Rotation is not permitted while making substitutions": "교체 중에는 로테이션이 허용되지 않습니다",
    "SERVE": "서브",
    "SETS": "세트",
    "Save & Share": "저장 & 공유",
    "Save this report so you can share it with other users": "다른 사용자와 공유할 수 있도록 이 리포트를 저장하세요",
    "Save this report to the player's profile": "이 리포트를 선수 프로필에 저장",
    "Save to player": "선수에 저장",
    "Saved lineup": "저장된 라인업",
    "Scroll, drag or swipe to scrub": "스크롤, 드래그 또는 스와이프하여 탐색",
    "Search existing players...": "기존 선수 검색…",
    "Search playlists…": "재생 목록 검색…",
    "Select a match video to view while entering lineups.": "라인업을 입력하는 동안 볼 경기 영상을 선택하세요.",
    "Select a player.": "선수를 선택하세요.",
    "Select a team.": "팀을 선택하세요.",
    "Setting up your account": "계정 설정 중",
    "Shaded rows are played under a Power Play — HP/AP marks the calling team, bold gold scores are bonus (double) points.": "음영 처리된 행은 파워 플레이 중에 진행된 것입니다 — HP/AP는 선언한 팀을 표시하며, 굵은 금색 점수는 보너스(2배) 점수입니다.",
    "Shared by": "공유자:",
    "Shared with me": "나에게 공유됨",
    "Show help": "도움말 표시",
    "Show public playlists": "공개 재생 목록 표시",
    "Standard": "표준",
    "Substitutions made — tap to view": "교체가 이루어졌습니다 — 탭하여 보기",
    "Tap the blocking player.": "블로킹하는 선수를 탭하세요.",
    "Team colour": "팀 색상",
    "Team name": "팀 이름",
    "That email is already in the list.": "해당 이메일은 이미 목록에 있습니다.",
    "The report is too large to save. Try a single match or fewer sets.": "리포트가 너무 커서 저장할 수 없습니다. 단일 경기 또는 더 적은 세트로 시도하세요.",
    "This match has no Power Play calls.": "이 경기에는 파워 플레이 콜이 없습니다.",
    "This player has no name to save the report against.": "이 선수는 리포트를 저장할 이름이 없습니다.",
    "This team has no designated setter. Pick the player who is setting.": "이 팀에는 지정된 세터가 없습니다. 세트를 하는 선수를 선택하세요.",
    "Toggle video player": "비디오 플레이어 전환",
    "Unmute": "음소거 해제",
    "Untitled playlist": "제목 없는 재생 목록",
    "Video URL…": "비디오 URL…",
    "We're moving your data to a faster, more reliable home. This happens only once.": "데이터를 더 빠르고 안정적인 곳으로 이전하고 있습니다. 이 작업은 한 번만 수행됩니다.",
    "You don't have permission to view this page.": "이 페이지를 볼 권한이 없습니다.",
    "Your data has been migrated successfully.": "데이터가 성공적으로 마이그레이션되었습니다.",
    "Your teams and players will appear here after your data is migrated.": "데이터가 마이그레이션되면 팀과 선수가 여기에 표시됩니다.",
    "bonus point": "보너스 점수",
    "bonus points": "보너스 점수",
    "event": "이벤트",
    "events": "이벤트",
    "file(s).": "개 파일.",
    "is not on this team's roster.": "이 팀의 명단에 없습니다.",
    "loaded": "불러옴",
    "matches": "경기",
    "more": "더",
    "players": "명의 선수",
    "points (no value was entered) — applied automatically.": "점 (값이 입력되지 않음) — 자동으로 적용되었습니다.",
    "roster": "명단",
    "seconds": "초",
    "selected": "선택됨",
    "session(s)?": "개 세션을?",
    "showing": "표시 중",
    "tournaments": "토너먼트",
    "…and": "…그리고",
    "● marks which team served the point.": "●는 어느 팀이 해당 포인트에서 서브했는지 표시합니다.",
    // i18n backfill pass (Catapult + misc)
    "Centimetres (cm)": "센티미터 (cm)",
    "Inches (in)": "인치 (in)",
    "Jump height units": "점프 높이 단위",
    "Choose how Catapult jump heights are displayed and filtered.": "Catapult 점프 높이를 표시하고 필터링하는 방법을 선택하세요.",
    "Select Catapult Focus file...": "Catapult Focus 파일 선택…",
    "Select a Catapult Focus CSV export to view jump data for this match.": "이 경기의 점프 데이터를 보려면 Catapult Focus CSV 내보내기 파일을 선택하세요.",
    "Loaded from saved match data": "저장된 경기 데이터에서 불러옴",
    "No jump data found in this file.": "이 파일에서 점프 데이터를 찾을 수 없습니다.",
    "Could not read this file. Please check it is a Catapult Focus CSV export.": "이 파일을 읽을 수 없습니다. Catapult Focus CSV 내보내기 파일인지 확인하세요.",
    "Could not save jump data with this match.": "이 경기에 점프 데이터를 저장할 수 없습니다.",
    "Jump data saved with this match.": "이 경기에 점프 데이터가 저장되었습니다.",
    "Show players with no match stats": "경기 기록이 없는 선수 표시",
    "hidden": "숨김",
    "jumps": "점프",
    "periods": "구간",
    "Sync offset": "동기화 오프셋",
    "matched": "일치",
    "match events now show jump height/duration in Video Analysis": "경기 이벤트에 이제 비디오 분석에서 점프 높이/지속 시간이 표시됩니다",
    "Team": "팀",
    "Team A": "팀 A",
    "Team B": "팀 B",
    "Which team is this Catapult file for?": "이 Catapult 파일은 어느 팀을 위한 것인가요?",
    "We couldn't confidently match the players in this Focus file to one team. Please select which team it belongs to.": "이 Focus 파일의 선수들을 한 팀으로 확실하게 매칭할 수 없었습니다. 어느 팀에 속하는지 선택해 주세요.",
    "This match has no video attached yet.": "이 경기에는 아직 연결된 영상이 없습니다.",
    "No synced jump events found for this player.": "이 선수에 대해 동기화된 점프 이벤트를 찾을 수 없습니다.",
    "No match loaded.": "불러온 경기가 없습니다.",
    "avg": "평균",
    "max": "최대",
    "Sync": "동기화",
    "Jump Report": "점프 리포트",
    "Watch Video": "영상 보기",
    "Save PDF": "PDF 저장",
    "Generated by VBLive": "VBLive에서 생성됨",
    "Deep analysis of {team} from ball-by-ball event data, covering serving, reception, setting, attack, blocking, defence, rotations and opponent tendencies. Best used with the Anthropic (Claude) provider.": "{team}의 서브, 리시브, 세트, 공격, 블로킹, 수비, 로테이션 및 상대 성향을 아우르는 랠리별 이벤트 데이터 기반의 심층 분석입니다. Anthropic(Claude) 제공자와 함께 사용하는 것이 가장 좋습니다.",
    "teams": "팀",
  },

  ja: {
    // Information page
    Information: "情報",
    "Release notes": "リリースノート",
    "Contact us": "お問い合わせ",
    "Questions, feedback or suggestions are most welcome.": "ご質問・ご意見・ご提案をお待ちしております。",
    // DV match coding
    Lineups: "ラインアップ",
    Point: "得点",
    Video: "ビデオ",
    Defaults: "デフォルト",
    "[team][NN][skill][type?][eval][zoneFrom?[szFrom?][zoneTo?[szTo?]]]": "[チーム][NN][スキル][タイプ?][評価][開始ゾーン?[開始サブゾーン?][終了ゾーン?[終了サブゾーン?]]]",
    Vertical: "縦",
    Horizontal: "横",
    "Swap sides": "サイド交換",
    Subs: "交代",
    Substitutions: "交代",
    Court: "コート",
    Home: "ホーム",
    Away: "アウェイ",
    Games: "セット",
    Enter: "入力",
    Undo: "元に戻す",
    "Match saved": "試合を保存しました",
    "Please sign in to save this match.": "この試合を保存するにはログインしてください。",
    "Nothing to save yet.": "まだ保存するものがありません。",
    "Could not save the match. Please try again.": "試合を保存できませんでした。もう一度お試しください。",
    Discard: "変更を破棄",
    "Resumed autosaved coding": "自動保存されたコーディングを再開しました",
    "Changes discarded": "変更を破棄しました",
    "Discard all changes and revert to the starting point?":
      "すべての変更を破棄して開始時点に戻しますか？",
    Clear: "クリア",
    "Prev set": "前のセット",
    "Next set": "次のセット",
    "Match Setup": "試合設定",
    "First serve": "最初のサーブ",
    "Start Match": "試合開始",
    "Resume Match": "試合再開",
    Skip: "スキップ",
    "Select an event then drag to draw ball path": "イベントを選択してからドラッグしてボールの軌道を描きます",
    "Drag on court to set ball path": "コート上でドラッグしてボールの軌道を設定します",
    "No events yet – enter a DV code above and press Enter.": "イベントはまだありません – 上にDVコードを入力して入力を押してください。",
    "DataVolley code reference": "DataVolleyコードリファレンス",
    Format: "形式",
    Skills: "スキル",
    Evaluation: "評価",
    "Skill / type omission": "スキル / タイプの省略",
    "Skill and/or type may be omitted; defaults fill in the rest.": "スキルやタイプは省略できます。残りはデフォルト値で補完されます。",
    "Dot-coding (S→R and A→B pairs)": "ドットコーディング（S→RおよびA→Bのペア）",
    "Chaining & aliases": "連結とエイリアス",
    "team:": "チーム：",
    "omit → defaults to A": "省略 → デフォルトはA",
    "NN: 1 or 2 digit jersey, e.g. 1, 01, 12": "NN：1桁または2桁の背番号、例：1、1、0、、112",
    "sub-zone: a b c d appended to zone digit, e.g. 5b or 5b1a": "サブゾーン：ゾーン数字に a b c d を付加、例：5b または 5b1a",
    "S Serve · R Reception · E Set · A Attack · B Block · D Dig · F Freeball": "S サーブ · R レセプション · E セット · A アタック · B ブロック · D ディグ · F フリーボール",
    "# kill/ace · + positive · ! ok · - negative · / overpass · = error": "# 得点/エース · + ポジティブ · ! 普通 · - ネガティブ · / オーバーパス · = ミス",
    "E.g.": "例：",
    "serve with skill=A (default), type=H, eval=+, zone=5.": "skill=A（デフォルト）、type=H、eval=+、zone=5 でサーブ。",
    "type-only (no skill marker), defaults skill from context.": "タイプのみ（スキルマーカーなし）、スキルは文脈からデフォルト。",
    "serve coded, reception auto-linked.": "サーブをコード化、レセプションを自動リンク。",
    "attack coded, block auto-linked.": "アタックをコード化、ブロックを自動リンク。",
    "Dot connects the reception eval to the corresponding serve eval via the serve→receive mapping.": "ドットはサーブ→レセプションの対応を介して、レセプション評価を対応するサーブ評価に接続します。",
    "two events in one string.": "1つの文字列に2つのイベント。",
    "manual point A/B.": "手動得点 A/B。",
    "serve ace/error.": "サーブのエース/ミス。",
    "serve with eval.": "評価付きのサーブ。",
    // Stats, sideouts, filters & video tooltips
    "Block Kills": "ブロック得点",
    "Side Out Percentage": "サイドアウト率",
    "All First Ball": "すべての第一球",
    "First Ball Kills": "第一球の得点",
    "Good Serve": "良いサーブ",
    "Break Points": "ブレイクポイント",
    Game: "セット",
    Row: "ポジション",
    "First Ball Sideouts": "第一球サイドアウト",
    "All Sideouts": "すべてのサイドアウト",
    All: "すべて",
    None: "なし",
    Receiver: "レシーバー",
    "# Passes": "レセプション数",
    "Perfect %": "パーフェクト率",
    "Positive %": "ポジティブ率",
    "Sideout %": "サイドアウト率",
    "FB Sideout %": "第一球サイドアウト率",
    "Pass Hits": "レセプション後の攻撃",
    "Pass Hit Kills": "レセプション後の得点",
    "Pass Hit Errors": "レセプション後のミス",
    "Show Passes": "レセプションを表示",
    "Show Attacks": "攻撃を表示",
    "Error Pass": "レセプションミス",
    "1 Pass": "レセプション1",
    "2 Pass": "レセプション2",
    "Perfect Pass": "パーフェクトレセプション",
    "Successful SO": "サイドアウト成功",
    "First Ball SO": "第一球サイドアウト",
    "Unsuccessful SO": "サイドアウト失敗",
    RECEIVES: "レセプション",
    Sets: "セット",
    Rotations: "ローテーション",
    "Attack Combos": "攻撃コンビネーション",
    Stages: "ステージ",
    Display: "表示",
    Setters: "セッター",
    Attackers: "アタッカー",
    Results: "結果",
    Source: "ソース",
    Cones: "ゾーン",
    "Select None": "選択を解除",
    "All Serves": "すべてのサーブ",
    "All Returns": "すべてのレセプション",
    Filters: "フィルター",
    "Reset all filters": "すべてのフィルターをリセット",
    "Show video clips of filtered attacks": "フィルターした攻撃の動画クリップを表示",
    "Spikes for the selected player": "選択した選手のスパイク",
    "Click on the zones to view video of attacks": "ゾーンをクリックして攻撃の動画を表示",
    "Expand All": "すべて展開",
    "Collapse All": "すべて折りたたむ",
    "Import Radar File": "Radar ファイルをインポート",
    "Select local video file": "ローカルの動画ファイルを選択",
    "Save to database": "データベースに保存",
    "Load and play online video": "オンライン動画を読み込んで再生",
    "Synchronise video with events": "動画をイベントと同期",
    "Rewind 10 seconds": "10秒戻る",
    "Rewind 1 second": "1秒戻る",
    "Forward 1 second": "1秒進む",
    "Forward 10 seconds": "10秒進む",
    "Set timing for events": "イベントのタイミングを設定",

    // Navigation
    Matches: "試合",
    Live: "ライブ",
    Import: "インポート",
    Playlists: "プレイリスト",
    "New Match": "新規試合",
    // New Match / Match Coding gate
    "Match Coding": "試合コーディング",
    "Match Coding — Coming Soon": "試合コーディング — 近日公開",
    "Live match coding is on the way. Here's a preview of what's coming.":
      "ライブ試合コーディングを準備中です。こちらは近日公開予定の機能のプレビューです。",
    Settings: "設定",
    Language: "言語",
    "Your profile": "プロフィール",
    "Sign out": "サインアウト",
    "Translation Editor": "翻訳エディター",

    // Common actions
    Save: "保存",
    Cancel: "キャンセル",
    Delete: "削除",
    Share: "共有",
    Edit: "編集",
    New: "新規",
    Refresh: "更新",
    Search: "検索",
    Reset: "リセット",
    Close: "閉じる",
    "Sub for": "交代",
    "No players available": "利用可能な選手なし",
    "Show full roster": "全ロースターを表示",
    "Available only": "利用可能のみ",
    "Maximum substitutions reached": "交代の上限に達しました",
    Back: "戻る",
    "Download PDF": "PDFをダウンロード",
    "Loading...": "読み込み中…",
    "Select All": "すべて選択",
    "Unselect All": "すべて選択解除",
    Yes: "はい",
    No: "いいえ",
    Add: "追加",
    Load: "読み込む",
    Confirm: "確認",
    Open: "開く",
    show: "表示",

    // Auth
    "Sign In": "サインイン",
    "Sign Up": "新規登録",
    "Sign Out": "サインアウト",
    Email: "メールアドレス",
    Password: "パスワード",
    "Confirm Password": "パスワードの確認",
    "Forgot password?": "パスワードをお忘れですか？",
    "Forgot Password": "パスワードを忘れた",
    "Don't have an account?": "アカウントをお持ちでないですか？",
    "Sign up for free!": "無料で登録！",
    "All ready signed up?": "すでに登録済みですか？",
    "Error Signing In": "サインインエラー",
    "Signed up successfully": "登録が完了しました",
    "Error signing up": "登録エラー",
    "Enter your email": "メールアドレスを入力",
    "Send Reset Email": "リセットメールを送信",
    "Email was sent to": "メールの送信先:",
    "Could not send reset email":
      "リセットメールを送信できませんでした",

    // Matches list
    "Team 1": "チーム1",
    "Team 2": "チーム2",
    Tournament: "大会",
    Scores: "スコア",
    Date: "日付",
    Actions: "操作",
    Owner: "所有者",
    Description: "説明",
    Comments: "コメント",
    Tags: "タグ",
    "Add player": "選手を追加",
    "Add team": "チームを追加",
    "Edit playlist": "プレイリストを編集",
    "Merge Teams": "チームを統合",
    "Roster differences detected between teams:": "チーム間でロスターの差異が検出されました：",
    "Review the roster in the next step before merging.": "統合前に次のステップでロスターを確認してください。",
    "At least 2 team records must exist in your Teams list for the selected matches.": "選択した試合のチームリストに少なくとも2件のチームレコードが必要です。",
    "Merge...": "統合...",
    "Select 2 or more team records to merge into one.": "統合するチームレコードを2つ以上選択してください。",
    "Filter teams...": "チームを絞り込む...",
    "teams selected": "チームを選択済み",
    "Select Teams": "チームを選択",
    "Reconcile Roster": "ロスターを統合",
    "Confirm Merge": "統合を確認",
    "Merged team name": "統合後のチーム名",
    "shirt number(s) have conflicting names — review below.": "背番号に名前の競合があります — 以下を確認してください。",
    "Shirt": "背番号",
    "players included": "人の選手を含む",
    "No players to reconcile.": "統合する選手がいません。",
    "Use this name": "この名前を使用",
    "Create merged team": "統合チームを作成",
    "with": "選手、",
    "duplicate team records": "件の重複チームレコード",
    "Consolidate duplicate player records and reassign playlist links": "重複選手レコードを統合しプレイリストリンクを再割り当て",
    "This cannot be undone.": "この操作は元に戻せません。",
    "Teams being merged:": "統合するチーム：",
    "Step": "ステップ",
    "of": "/",
    "Merging...": "統合中...",
    "Players & Teams": "選手とチーム",
    "No players or teams assigned.": "選手またはチームが割り当てられていません。",
    "Search players...": "選手を検索...",
    "Search teams...": "チームを検索...",
    "Saving...": "保存中...",
    "My Matches": "自分の試合",
    "Shared Matches": "共有された試合",
    "Search team...": "チームを検索…",
    "All Teams": "すべてのチーム",
    "Multi Match Reports": "複数試合レポート",
    "Delete match": "試合を削除",
    "Remove from my shared matches": "共有された試合から削除",
    "Share match": "試合を共有",
    "Edit match": "試合を編集",
    "Open video analysis": "ビデオ分析を開く",
    "Delete Match": "試合を削除",
    "Are you sure you want to delete the selected match?":
      "選択した試合を削除してもよろしいですか？",
    "No matches selected.": "試合が選択されていません。",

    // New Match
    "← Back": "← 戻る",
    "Team A (Home)": "チームA（ホーム）",
    "Team B (Away)": "チームB（アウェイ）",
    "Select team…": "チームを選択…",
    "Tournament (optional)": "大会（任意）",
    "Tournament / competition name": "大会 / 競技名",
    "Match date": "試合日",
    "Start Coding →": "コーディング開始 →",

    // Reports
    Summary: "概要",
    "Box Score": "スタッツ表",
    "Side-outs Report": "サイドアウトレポート",
    "Serve Receives": "サーブレシーブ",
    "Attack Zones": "アタックゾーン",
    "Hitting Charts": "アタックチャート",
    "Video Analysis": "ビデオ分析",
    Dashboard: "ダッシュボード",
    Charts: "チャート",
    "AI Report": "AIレポート",
    // AI Reports page
    "AI Reports": "AIレポート",
    "AI Reports — Coming Soon": "AIレポート — 近日公開",
    "AI-powered match reports and deep analysis are on the way. Here are some sample reports to show what's coming.":
      "AIによる試合レポートと詳細分析を準備中です。こちらはその一例となるサンプルレポートです。",
    "Preview not available.": "プレビューを利用できません。",
    "Open sample": "サンプルを開く",
    "No matches were loaded. Select one or more matches and try again.":
      "試合が読み込まれていません。1つ以上の試合を選択して、もう一度お試しください。",
    "Attack Analysis (English)": "攻撃分析（英語）",
    "Blocking Analysis (Vietnamese)": "ブロック分析（ベトナム語）",
    "Opponent Tendencies (Japanese)": "相手の傾向（日本語）",
    // Deep AI analysis
    "Deep AI Analysis": "AI詳細分析",
    "AI Deep Analysis": "AIディープ分析",
    "Load at least one match with coded events to generate the analysis.": "分析を生成するには、コード化されたイベントを含む試合を1つ以上読み込んでください。",
    "Run the deep analysis one section at a time to keep each request small. Edit or add questions below, then generate. Best used with the Anthropic (Claude) provider.":
      "各リクエストを小さく保つため、詳細分析はセクションごとに実行してください。下で質問を編集または追加してから生成します。Anthropic (Claude) プロバイダーとの併用を推奨します。",
    "Download CSV": "CSVをダウンロード",
    "Analysis section": "分析セクション",
    analysing: "分析中",
    "All sections (uses the most credits)": "すべてのセクション（クレジットを最も多く消費）",
    "All sections": "すべてのセクション",
    Questions: "質問",
    "one per line, edit or add your own": "1行に1つ、編集または独自に追加できます",
    "Reset to defaults": "デフォルトに戻す",
    "Add a question…": "質問を追加…",
    "Runs every section using the questions you've set for each. This sends one large request and uses the most credits.":
      "各セクションに設定した質問ですべてのセクションを実行します。1つの大きなリクエストを送信し、クレジットを最も多く消費します。",
    "Generate full analysis": "完全な分析を生成",
    "Generate section analysis": "セクション分析を生成",
    Serving: "サーブ",
    Reception: "レセプション",
    Setting: "トス",
    Attack: "アタック",
    Blocking: "ブロック",
    "Defence / Digging": "ディフェンス / ディグ",
    "Rotation Analysis": "ローテーション分析",
    "Opponent Tendencies (Scouting)": "相手の傾向（スカウティング）",
    "Overall Team Metrics": "チーム総合指標",
    "Serve efficiency (aces, errors, and pressured receptions forced).": "サーブ効率（サービスエース、ミス、そして与えたレセプションへのプレッシャー）。",
    "Which servers are most effective, and against which reception zones?": "どのサーバーが最も効果的で、どのレセプションゾーンに対してか？",
    "Which serve types (jump, float, jump-float) yield the best results?": "どのサーブの種類（ジャンプ、フロータ、ジャンプフロータ）が最も良い結果を生むか？",
    "Reception quality breakdown (perfect, positive, negative, error).": "レセプションの質の内訳（完璧、ポジティブ、ネガティブ、ミス）。",
    "Which players struggle most under jump serves vs. float serves?": "ジャンプサーブとフローターサーブのうち、どの選手が最も苦しんでいるか？",
    "How does poor reception correlate with side-out efficiency?": "不安定なレセプションはサイドアウト効率とどう関連するか？",
    "Set distribution by zone and by player.": "ゾーン別・選手別のトス配分。",
    "How does the setter perform under pressure (bad-pass situations)?": "セッターはプレッシャー下（不安定なパスの状況）でどのようにパフォーマンスを発揮するか？",
    "Tempo breakdown (quick, medium, high ball) and which yields the best attack outcome.": "テンポの内訳（クイック、ミディアム、ハイボール）と、どれが最も良い攻撃結果を生むか。",
    "Kill percentage and error rate per player, per rotation, and per zone.": "選手別、ローテーション別、ゾーン別の決定率とミス率。",
    "Which attack zones (pipe, cross, line) have the highest efficiency?": "どの攻撃ゾーン（パイプ、クロス、ストレート）が最も効率が高いか？",
    "How attack efficiency changes in transition vs. side-out.": "攻撃効率がトランジションとサイドアウトでどう変化するか。",
    "Block-per-set rate per player and rotation.": "選手別・ローテーション別の1セットあたりブロック数。",
    "Which opposing attack zones are most successfully blocked?": "相手のどの攻撃ゾーンが最もうまくブロックされているか？",
    "How often blocks result in kills vs. touch-outs.": "ブロックが得点につながる割合とブロックアウトの割合。",
    "Dig success rate per player and per court zone.": "選手別・コートゾーン別のディグ成功率。",
    "Which attack types (tip, roll, hard swing) are hardest to defend?": "どの攻撃タイプ（フェイント、ロール、スパイク）が最も守りにくいか？",
    "How defensive efficiency affects transition attack success.": "守備効率がトランジション攻撃の成功にどう影響するか。",
    "Which rotations have the highest and lowest point differential?": "どのローテーションの得失点差が最も高く、最も低いか？",
    "Side-out and break-point percentages per rotation.": "ローテーション別のサイドアウト率とブレイクポイント率。",
    "Rotation-specific vulnerabilities (e.g. a weak serving or passing rotation).": "ローテーション固有の弱点（例：サーブやパスが弱いローテーション）。",
    "The opponent's most frequently used attack zones and combinations.": "相手が最も頻繁に使う攻撃ゾーンとコンビネーション。",
    "The opponent's go-to attackers in high-pressure situations.": "プレッシャーのかかる場面で相手が頼りにするアタッカー。",
    "The serve-receive patterns the opponent favours.": "相手が好むサーブレシーブのパターン。",
    "Overall rally-win probability in different score situations.": "さまざまなスコア状況における全体的なラリー勝率。",
    "How performance changes in sets 1–3 vs. sets 4–5 (fatigue/pressure effects).": "第1〜3セットと第4〜5セットでパフォーマンスがどう変化するか（疲労・プレッシャーの影響）。",
    "The correlation between first-ball side-out percentage and match outcome.": "ファーストボールのサイドアウト率と試合結果の相関。",
    Report: "レポート",

    // Import page
    "Load DVW file(s)...": "DVWファイルを読み込む…",
    "Load PSVB file(s)...": "PSVBファイルを読み込む…",
    "Load VSM file(s)...": "VSMファイルを読み込む…",
    "Load playlist file...": "プレイリストファイルを読み込む…",
    "load one or more DataVolley files":
      "1つ以上のDataVolleyファイルを読み込む",
    "load one or more VBStats files":
      "1つ以上のVBStatsファイルを読み込む",
    "load one or more VolleyStation VSM files":
      "1つ以上のVolleyStation VSMファイルを読み込む",
    "Convert DVW": "DVWを変換",
    "VSM": "VSM",
    "Convert DVW to VSM...": "DVWをVSMに変換…",
    "pick a DataVolley .dvw file to download as .vsm":
      ".vsmとしてダウンロードするDataVolleyの.dvwファイルを選択",
    "Convert VSM to DVW...": "VSMをDVWに変換…",
    "pick a VolleyStation .vsm file to download as .dvw":
      ".dvwとしてダウンロードするVolleyStationの.vsmファイルを選択",
    "Converting…": "変換中…",
    "Converted": "変換済み",
    "Failed to convert": "変換に失敗しました",
    "Beta testers only — coming soon for everyone else.":
      "ベータテスターのみ利用可能 — 他のユーザーには近日公開予定です。",
    "load a playlist": "プレイリストを読み込む",
    "Use Shift-JIS Encoder": "Shift-JISエンコーダーを使用",
    "Importing…": "インポート中…",
    "Save to Database?": "データベースに保存しますか？",
    "Always save imports automatically": "インポートを常に自動保存する",
    "View Only": "閲覧のみ",
    "Save to Database": "データベースに保存",
    "Error saving to database": "データベースへの保存に失敗しました",
    "Saved to database": "データベースに保存しました",
    "Saving to database…": "データベースに保存中…",
    "Upload them to the database now?":
      "今すぐデータベースにアップロードしますか？",

    // Settings / Options
    Theme: "テーマ",
    Options: "オプション",
    "Rotation naming convention": "ローテーションの命名規則",
    "Choose how rotations are labelled in reports and analysis.":
      "レポートや分析でローテーションをどのように表示するかを選択します。",
    "R1–R6 (rotation sequence)": "R1–R6（ローテーション順）",
    "S1–S6 (setter court position)": "S1–S6（セッターのコート位置）",
    "R1: setter in position 1, R2: setter in 6, R3: setter in 5, R4: setter in 4, R5: setter in 3, R6: setter in 2.":
      "R1: セッターが1番、R2: セッターが6番、R3: セッターが5番、R4: セッターが4番、R5: セッターが3番、R6: セッターが2番。",
    "S1: setter in 1, S2: setter in 2, S3: setter in 3, S4: setter in 4, S5: setter in 5, S6: setter in 6.":
      "S1: セッターが1番、S2: セッターが2番、S3: セッターが3番、S4: セッターが4番、S5: セッターが5番、S6: セッターが6番。",
    "Labels:": "ラベル:",
    "Current theme:": "現在のテーマ:",
    "Choose the language for the app interface.":
      "アプリのインターフェース言語を選択します。",
    "Create your own language package or edit a community one, with AI-assisted translation.":
      "AI支援翻訳で独自の言語パッケージを作成するか、コミュニティのものを編集できます。",

    // About / Footer
    "Copyright © Perana Sports {year}. All rights reserved.":
      "Copyright © Perana Sports {year}. All rights reserved.",
    "VBLive is free to use right now. However Perana Sports reserves the right to charge a subscription fee in due course.":
      "VBLiveは現在無料でご利用いただけます。ただし、Perana Sportsは将来的に購読料を請求する権利を留保します。",
    "Querries / suggestions are most welcomed.":
      "ご質問・ご提案を歓迎します。",

    // Live
    "Please enter server name to search":
      "検索するサーバー名を入力してください",
    "Broadcast Server": "配信サーバー",
    "Enter DVMateLive Server Name": "DVMateLiveサーバー名を入力",
    LIVE: "ライブ",

    // Playlists list
    "Delete Playlist": "プレイリストを削除",
    "Are you sure you want to delete the selected playlist?":
      "選択したプレイリストを削除してもよろしいですか？",
    "My Playlists": "自分のプレイリスト",
    "Shared Playlists": "共有プレイリスト",
    "Tags:": "タグ:",
    "Select...": "選択…",
    "All Tags": "すべてのタグ",

    // Share
    "User already added": "ユーザーは既に追加されています",
    "Please enter a valid email address":
      "有効なメールアドレスを入力してください",
    "Please select at least one user to share with":
      "共有するユーザーを1人以上選択してください",
    "Not Shared": "未共有",
    "Available for everyone": "全員に公開",
    "Available to selected users":
      "選択したユーザーに公開",
    "Enter VBLive user email address":
      "VBLiveユーザーのメールアドレスを入力",
    "Selected users:": "選択したユーザー:",
    "Save List": "リストを保存",
    "Clear List": "リストをクリア",
    "Clear Users List": "ユーザーリストをクリア",
    "Do you really want to clear all users from list?":
      "リストからすべてのユーザーを削除してもよろしいですか？",
    "Do you want to save changes before closing?":
      "閉じる前に変更を保存しますか？",
    "Enter name to save list to:":
      "リストを保存する名前を入力してください:",
    "Enter name to save list to":
      "リストを保存する名前を入力してください",

    // AI panels
    "AI settings": "AI設定",
    "Setup needed": "設定が必要です",
    "Generate report": "レポートを生成",
    "Generate AI report": "AIレポートを生成",
    "Generate general report": "一般レポートを生成",
    "Translate to…": "翻訳先…",
    "Follow-up questions": "追加の質問",
    "Ask a follow-up question…": "追加の質問を入力…",
    "Ask a follow-up question": "追加の質問をする",
    "AI provider": "AIプロバイダー",
    Model: "モデル",
    Hide: "非表示",
    Show: "表示",
    "Your key is stored only in this browser and sent directly to the selected provider.":
      "キーはこのブラウザーにのみ保存され、選択したプロバイダーに直接送信されます。",
    "Please enter your API key.": "APIキーを入力してください。",
    "Not enough data to analyse.":
      "分析するデータが不足しています。",
    "Select a player with recorded spikes first.":
      "まずスパイクが記録された選手を選択してください。",
    Player: "選手",
    "Select a player…": "選手を選択…",
    "No players with recorded spikes in the selected matches.":
      "選択した試合にスパイクが記録された選手がいません。",
    "Analyse with AI": "AIで分析",
    "AI scouting report": "AIスカウティングレポート",
    "Q:": "質問:",
    Ask: "質問",
    "e.g. Which rotation should we target on serve?":
      "例: サーブでどのローテーションを狙うべきか？",
    "Saved questions:": "保存した質問:",
    "Click to ask again": "もう一度質問するにはクリック",
    "Remove saved question": "保存した質問を削除",

    // AI language options
    Spanish: "スペイン語",
    French: "フランス語",
    German: "ドイツ語",
    Italian: "イタリア語",
    Portuguese: "ポルトガル語",
    Dutch: "オランダ語",
    Polish: "ポーランド語",
    Russian: "ロシア語",
    Turkish: "トルコ語",
    Arabic: "アラビア語",
    "Chinese (Simplified)": "中国語（簡体字）",
    Japanese: "日本語",
    Korean: "韓国語",
    Vietnamese: "ベトナム語",
    Thai: "タイ語",

    // Box score
    "Show Full Headers": "完全なヘッダーを表示",
    "Show Short Headers": "短いヘッダーを表示",
    "Show Less Players": "選手を少なく表示",
    "Show All Players": "すべての選手を表示",
    "Attack Kills": "アタック得点",
    "Attack Errors": "アタックミス",
    "Total Attempts": "総試行数",
    "Hitting Efficiency": "アタック効果率",
    "Serve Aces": "サービスエース",
    "Serve Errors": "サーブミス",
    "Pass Errors": "レセプションミス",
    "% Perfect Passes": "完璧なレセプション率",
    "Passing Average": "レセプション平均",
    "Total Passes": "総レセプション",
    "Set Assists": "セットアシスト",
    Digs: "ディグ",
    "Momentum Chart": "モメンタムチャート",
    "Key event": "決定プレー",
    "Break Point %": "ブレイクポイント %",
    "First Ball Sideout %": "ファーストボールサイドアウト %",
    "Sideout Transition %": "トランジションサイドアウト %",
    "FB Sideout Kills %": "ファーストボール決定率",
    "Sideout Opposition Error %": "相手エラーによるサイドアウト %",
    Plus: "プラス",
    Minus: "マイナス",
    Blocks: "ブロック得点",
    "Positive Serve %": "効果的サーブ %",
    Sideout: "サイドアウト",
    "Break point": "ブレイクポイント",
    "Ace / Kill": "エース / 決定打",
    "In play": "プレー中",
    Error: "エラー",
    "Sideout on a kill": "決定打によるサイドアウト",
    "Sideout on an error": "相手エラーによるサイドアウト",
    "Top half": "上半分",
    "Bottom half": "下半分",
    "The centre line steps toward the team that wins each point.": "中央線は各ポイントを獲得したチームの方向に動きます。",
    "No point-by-point data available for this match.": "この試合のポイントごとのデータがありません。",
    "No match data available.": "試合データがありません。",
    "This report isn't supported on this browser yet — please use Safari.": "このレポートはこのブラウザではまだサポートされていません。Safari をご使用ください。",
    "No video available for this match.": "この試合の動画はありません。",
    passes: "レセプション",
    "eff.": "効率",
    Grade: "評価",
    LEFT: "左",
    CENTRE: "中央",
    RIGHT: "右",
    SELECTED: "選択済み",
    Passers: "レシーバー",
    Calls: "コール",
    "SIDE OUT": "サイドアウト",
    TRANSITION: "トランジション",
    "No events in selection.": "現在の選択にイベントがありません。",
    "Serve Receives By Players": "選手別サーブレシーブ",
    "Serve Receive By Zones": "ゾーン別サーブレシーブ",
    "Dig Errors": "ディグミス",
    "Dig Average": "ディグ平均",
    "Block Solos": "シングルブロック",
    "Block Assists": "ブロックアシスト",
    "Block Errors": "ブロックミス",
    "Points Scored": "得点",
    TEAM: "チーム",

    // Sideout report
    "ALL ROWS": "全ローテーション",
    "ROWS ": "ローテーション ",
    "ROW ": "ローテーション ",
    "FB Kills": "FBアタック得点",
    "Opp. Serve Errors": "相手サーブミス",
    "FB Opp. Errors": "FB相手ミス",
    "Kills/Blocks": "アタック/ブロック",
    "Opp. Errors": "相手ミス",
    Unsuccessful: "失敗",

    // Attack zones
    "No events in selected zone.":
      "選択したゾーンにイベントがありません。",
    "Show all attacks. Or click on a zone to show attacks in that zone.":
      "すべてのアタックを表示します。またはゾーンをクリックすると、そのゾーンのアタックを表示します。",
    "OPPONENTS (": "相手 (",

    // Video analysis filters
    "All Types": "すべての種類",
    Serve: "サーブ",
    "Serve-Receive": "サーブレシーブ",
    Set: "セット",
    "Setter's Call": "セッターのコール",
    "Disable this skill": "このスキルを無効にする",
    "Enable this skill": "このスキルを有効にする",
    "Teams & Players": "チームと選手",
    "Match State": "試合状況",
    "Custom Code": "カスタムコード",
    "Search... (comma-separated for multiple)": "検索…（複数はカンマ区切り）",
    vs: "vs",
    "Rewind 60 seconds": "60秒戻る",
    "Rewind 1 frame": "1フレーム戻る",
    "Forward 1 frame": "1フレーム進む",
    "Forward 60 seconds": "60秒進む",
    "Previous Clip": "前のクリップ",
    Replay: "リプレイ",
    "Next Clip": "次のクリップ",
    Phases: "フェーズ",
    "Score Range": "スコア範囲",
    "Set Winner": "セット勝者",
    Type: "タイプ",
    Strength: "強さ",
    "Setter Calls": "セッターのコール",
    "Setter Call": "セッターのコール",
    Freeball: "フリーボール",
    "Close (0-2)": "僅差 (0-2)",
    "Moderate (3-5)": "中間 (3-5)",
    "Lopsided (6+)": "大差 (6+)",
    "Focus Team": "注目チーム",
    Oppositions: "対戦相手",
    "Focus Team Won": "注目チームの勝利",
    "Oppositions Won": "対戦相手の勝利",
    "Advanced Filters": "高度なフィルター",
    Players: "選手",
    Opponents: "対戦相手",
    "Event Type": "イベントタイプ",
    "Event Result": "イベント結果",
    "Attack Type": "アタックタイプ",
    "Hit Type": "ヒットタイプ",
    "Block Type": "ブロックタイプ",
    "Attack Combo": "アタックコンボ",
    Apply: "適用",
    Rotation: "ローテーション",
    Skill: "スキル",
    "Lead Time": "リードタイム",
    Duration: "長さ",
    Timing: "タイミング",
    "Go Live": "ライブに戻る",
    "Playback has fallen behind the live broadcast — click to jump to now.": "再生がライブ配信より遅れています — クリックして現在の時点にジャンプします。",
    "Video timing": "動画タイミング",
    "Default lead time (seconds before an event to start playback) and clip duration, per skill.": "デフォルトのリードタイム（イベント再生開始までの秒数）とスキルごとのクリップの長さ。",
    Cloud: "クラウド",
    Local: "ローカル",
    "Relay host": "リレーホスト",
    "IP or hostname of the machine running the local relay.": "ローカルリレーを実行しているマシンのIPまたはホスト名。",
    Spike: "スパイク",
    Block: "ブロック",
    Defence: "ディフェンス",
    "All Results": "すべての結果",
    Hard: "強打",
    Soft: "軟打",
    Tip: "フェイント",
    "Not Specified": "指定なし",
    "H - High": "H - ハイ",
    "M- Medium": "M - ミディアム",
    "Q - Quick": "Q - クイック",
    "T - Tense": "T - 平行",
    "U - Super": "U - スーパー",
    "F - Fast": "F - ファスト",
    "O - Other": "O - その他",
    "B0 - No Block": "B0 - ブロックなし",
    "B1 - 1 Block": "B1 - 1枚ブロック",
    "B2 - 2 Blocks": "B2 - 2枚ブロック",
    "B3 - 3 Blocks": "B3 - 3枚ブロック",
    "BS - Seams": "BS - 継ぎ目",

    // All filters panel
    ALL: "すべて",
    NONE: "なし",

    // Playlist editor
    "Remove Item": "項目を削除",
    "Do you really want to remove the selected item?":
      "選択した項目を削除してもよろしいですか？",
    "Description is required": "説明は必須です",
    "Comments are required": "コメントは必須です",
    "Tags are required": "タグは必須です",
    "Playlist saved successfully": "プレイリストを保存しました",
    "Error saving playlist": "プレイリストの保存エラー",

    // Language editor
    "My saved packages": "保存したパッケージ",
    "Loading shared languages…": "共有言語を読み込み中…",
    "Community shared languages": "コミュニティ共有言語",
    by: "作成者",
    "based on": "ベース",
    "or create new": "または新規作成",
    Use: "使用",
    "New Language Package": "新しい言語パッケージ",
    "Language Name": "言語名",
    "Language Code": "言語コード",
    "Flag Emoji": "国旗の絵文字",
    "Base language (reference)": "ベース言語（参照）",
    "If not English, both English and the reference language will be shown.":
      "英語でない場合、英語と参照言語の両方が表示されます。",
    "Start Editing": "編集を開始",
    "Save Key": "キーを保存",
    "AI key set": "AIキー設定済み",
    "Set API key for AI translation":
      "AI翻訳用のAPIキーを設定",
    "AI key ✓": "AIキー ✓",
    "Set AI key": "AIキーを設定",
    "AI: Translate All": "AI: すべて翻訳",
    "Translating…": "翻訳中…",
    "AI translate section": "AIでセクションを翻訳",
    "Clear All": "すべてクリア",
    "Saving…": "保存中…",
    English: "英語",
    new: "新規",
    "Enter translation…": "翻訳を入力…",
    "Share with other VBLive users?":
      "他のVBLiveユーザーと共有しますか？",
    "Would you like to share the": "次を共有しますか:",
    "language package with all VBLive users? Anyone can then copy and build upon it.":
      "言語パッケージをすべてのVBLiveユーザーと共有しますか？誰でもコピーして発展させることができます。",
    "No, just save for me": "いいえ、自分用に保存のみ",
    "Yes, share it!": "はい、共有します！",
    "Language package saved and shared with all VBLive users!":
      "言語パッケージを保存し、すべてのVBLiveユーザーと共有しました！",
    "Language package saved!": "言語パッケージを保存しました！",
    "Clear all translations? This cannot be undone.":
      "すべての翻訳をクリアしますか？この操作は元に戻せません。",

    // Additional UI phrases
    Teams: "チーム",
    "Shared By": "共有者",
    "View Activity": "閲覧アクティビティ",
    Viewer: "閲覧者",
    "Date / Time": "日付 / 時間",
    "No views recorded yet.": "まだ閲覧記録がありません。",
    "Date / Scores": "日付 / スコア",
    "Stats API source": "統計APIソース",
    "load one or more AVC VIS files": "AVC VISファイルを1つ以上読み込む",
    "Points to close a set": "セット終了に必要な得点",
    "Require 2-point advantage": "2点差を必須にする",
    Roster: "ロースター",
    Clone: "複製",
    "Edit team": "チームを編集",
    "New player": "新規選手",
    "Search player...": "選手を検索…",
    "Match Centre": "マッチセンター",
    "Points Won": "獲得ポイント",
    "Opp. Error": "相手のエラー",
    Total: "合計",
    "Match Skills": "マッチスキル",
    Dig: "ディグ",
    "Best Scorers": "得点ランキング",
    "Power Play": "パワープレー",
    Match: "試合",
    "Player scouting": "選手スカウティング",
    "Team deep analysis": "チーム詳細分析",
    "All Players": "全選手",
    "All Sets": "全セット",
    "All Rotations": "全ローテーション",
    "All Combos": "全コンビネーション",
    "All Calls": "全コール",
    "All Events": "全イベント",
    "Select all items": "すべて選択",
    "Unselect all items": "すべて選択解除",
    "Create play list": "プレイリストを作成",
    "Point by Point": "ポイントバイポイント",
    // i18n backfill pass — previously English-fallback-only keys
    "Access denied": "アクセスが拒否されました",
    "Aces": "サービスエース",
    "ACES": "サービスエース",
    "Add report": "レポートを追加",
    "Admin": "管理者",
    "Admins": "管理者一覧",
    "Advanced": "詳細",
    "All set!": "準備完了！",
    "Analyse team with AI": "AIでチームを分析",
    "Are you sure you want to delete the selected play list?": "選択したプレイリストを削除してもよろしいですか？",
    "Assign": "割り当て",
    "Assign playlist": "プレイリストを割り当て",
    "Assigned": "割り当て済み",
    "At least one admin is required.": "少なくとも1人の管理者が必要です。",
    "Attack Combo / Setter Call": "アタックコンビ／セッターコール",
    "Attack diagram": "アタック図",
    "Attacks": "アタック",
    "ATTACKS": "アタック",
    "Auto-detected a custom set-closing score for": "次に対してカスタムのセット終了スコアが自動検出されました：",
    "BLOCK": "ブロック",
    "BLOCKS": "ブロック",
    "Beta testers": "ベータテスター",
    "Clear filter": "フィルターをクリア",
    "Close Set": "セットを終了",
    "Close menu": "メニューを閉じる",
    "Code": "コード",
    "Coding defaults & shortcuts": "コーディングのデフォルトとショートカット",
    "Continue": "続ける",
    "Could not load match data. The match may no longer be available.": "試合データを読み込めませんでした。試合が利用できなくなっている可能性があります。",
    "Could not load stats for these matches.": "これらの試合の統計を読み込めませんでした。",
    "Could not load this match.": "この試合を読み込めませんでした。",
    "Could not load this playlist.": "このプレイリストを読み込めませんでした。",
    "Could not load this report.": "このレポートを読み込めませんでした。",
    "Could not save the report.": "レポートを保存できませんでした。",
    "Could not share the report. Please try again.": "レポートを共有できませんでした。もう一度お試しください。",
    "Court horizontal (net vertical)": "コート横向き（ネット縦）",
    "Court vertical (net horizontal)": "コート縦向き（ネット横）",
    "Create": "作成",
    "Create a new team from this roster": "この名簿から新しいチームを作成",
    "DIFFERENCE": "差",
    "DV Code": "DVコード",
    "Delete this report?": "このレポートを削除しますか？",
    "Detected a custom set-closing score of": "カスタムのセット終了スコアが検出されました：",
    "Double Sub": "ダブル交代",
    "Double Sub: now pick a bench player for the flashing OPPOSITE slot…": "ダブル交代：点滅しているオポジット枠にベンチの選手を選んでください…",
    "Double Sub: pick a bench player for the flashing SETTER slot…": "ダブル交代：点滅しているセッター枠にベンチの選手を選んでください…",
    "Double Substitution — swap the Setter and Opposite for two incoming players": "ダブル交代 — セッターとオポジットを2人の選手と同時に交代",
    "Drag to draw the ball path; pause briefly to mark a touch (reception/dig/block), release to finish.": "ドラッグしてボールの軌道を描きます。少し止めるとタッチ（レシーブ／ディグ／ブロック）がマークされ、離すと完了します。",
    "Draw": "描画",
    "Draw & Code": "描画＆コーディング",
    "Draw & Code — draw ball paths on the court to code the rally": "描画＆コーディング — コート上にボールの軌道を描いてラリーをコーディングします",
    "Edit Lineup": "ラインナップを編集",
    "Edit Lineup — freely rearrange the starting lineup before the set begins": "ラインナップを編集 — セット開始前にスターティングラインナップを自由に並べ替え",
    "Emails in this list can manage beta testers and other admins.": "このリストのメールアドレスはベータテスターや他の管理者を管理できます。",
    "Emails in this list get early access to AI Reports and other gated features. Changes take effect immediately.": "このリストのメールアドレスはAIレポートや他の制限付き機能に早期アクセスできます。変更は即座に反映されます。",
    "Enter a valid email address.": "有効なメールアドレスを入力してください。",
    "Exit fullscreen": "フルスクリーンを終了",
    "Extended": "拡張",
    "Failed to generate PDF.": "PDFの生成に失敗しました。",
    "Failed to save changes.": "変更を保存できませんでした。",
    "Filtered by": "フィルター条件：",
    "First name": "名",
    "For stronger security later, consider moving admin checks to a Firebase Auth custom claim instead of this email list.": "将来的なセキュリティ強化のため、このメールリストの代わりにFirebase Authのカスタムクレームに管理者チェックを移行することを検討してください。",
    "Fullscreen": "フルスクリーン",
    "Generate an AI report from these matches": "これらの試合からAIレポートを生成",
    "How often (in seconds) the events list is updated during a live session.": "ライブセッション中にイベントリストが更新される頻度（秒）。",
    "Last name": "姓",
    "Leave blank to use standard rules (25 points, 15 for a 5th/deciding set)": "標準ルールを使用する場合は空欄のままにしてください（25点、5セット目/決勝セットは15点）",
    "Live session refresh interval": "ライブセッションの更新間隔",
    "Load VIS file(s)...": "VISファイルを読み込む…",
    "Load this team's last-saved starting lineup (positions, setter and liberos)": "このチームの最後に保存されたスターティングラインナップ（ポジション、セッター、リベロ）を読み込む",
    "Loading…": "読み込み中…",
    "Manually end the current set at its current score, regardless of the configured winning score / 2-point advantage rule": "設定された勝利スコア／2点差ルールに関わらず、現在のセットを現在のスコアで手動終了します",
    "Migration finished with some issues": "一部問題がありましたが移行が完了しました",
    "Most of your data was migrated, but some items could not be moved. You can retry now or continue and try again later.": "データの大部分は移行されましたが、一部の項目は移動できませんでした。今すぐ再試行するか、続行して後でもう一度試してください。",
    "Mute": "ミュート",
    "New team": "新しいチーム",
    "Next": "次へ",
    "Nickname": "ニックネーム",
    "No Power Play": "パワープレイなし",
    "No admins yet.": "管理者はまだいません。",
    "No beta testers yet.": "ベータテスターはまだいません。",
    "No coded events to analyse.": "分析するコーディング済みイベントがありません。",
    "No coded events to export.": "エクスポートするコーディング済みイベントがありません。",
    "No matches.": "試合がありません。",
    "No players added yet.": "まだ選手が追加されていません。",
    "No players recorded.": "記録された選手がいません。",
    "No players yet.": "まだ選手がいません。",
    "No playlists assigned.": "割り当てられたプレイリストがありません。",
    "No playlists found.": "プレイリストが見つかりません。",
    "No reports saved.": "保存されたレポートがありません。",
    "No roster players available.": "利用可能な名簿選手がいません。",
    "No substitutions": "交代なし",
    "No teams found.": "チームが見つかりません。",
    "No teams yet.": "まだチームがありません。",
    "No teams.": "チームがありません。",
    "Nominate Setter": "セッターを指名",
    "Notify these users by email (optional)": "これらのユーザーにメールで通知する（任意）",
    "OPP REC": "相手レシーブ",
    "OPP. ERRORS": "相手のミス",
    "Open / Download": "開く／ダウンロード",
    "Open local file": "ローカルファイルを開く",
    "Open menu": "メニューを開く",
    "Opposition Passing — use the opposition's reception formation on the diagram": "相手のレセプション — 図に相手のレセプション隊形を使用",
    "Paste the report (Markdown supported)…": "レポートを貼り付けてください（Markdown対応）…",
    "Pause": "一時停止",
    "Play": "再生",
    "Playing squad": "試合メンバー",
    "Playing squad is full. Remove a player from the squad before adding another.": "試合メンバーが満員です。別の選手を追加する前に、メンバーから1人削除してください。",
    "Please sign in to save reports.": "レポートを保存するにはサインインしてください。",
    "Power Play Only": "パワープレイのみ",
    "Record a team error (no player) — edit later to add the player and event type": "チームエラーを記録（選手なし）— 後で選手とイベントタイプを追加するために編集してください",
    "Record at least one event before making substitutions": "交代を行う前に少なくとも1つのイベントを記録してください",
    "Redo": "やり直し",
    "Remove": "削除",
    "Remove share": "共有を削除",
    "Remove yourself as admin? You will immediately lose access to this page.": "自分自身を管理者から削除しますか？このページへのアクセス権をすぐに失います。",
    "Report saved and shared.": "レポートが保存され共有されました。",
    "Report saved to the player's profile.": "レポートが選手プロフィールに保存されました。",
    "Report title": "レポートタイトル",
    "Reports": "レポート",
    "Result": "結果",
    "Retry": "再試行",
    "Return to live coding": "ライブコーディングに戻る",
    "Rotation is not permitted while making substitutions": "交代中はローテーションできません",
    "SERVE": "サーブ",
    "SETS": "セット",
    "Save & Share": "保存＆共有",
    "Save this report so you can share it with other users": "他のユーザーと共有できるようにこのレポートを保存します",
    "Save this report to the player's profile": "このレポートを選手プロフィールに保存",
    "Save to player": "選手に保存",
    "Saved lineup": "保存済みラインナップ",
    "Scroll, drag or swipe to scrub": "スクロール、ドラッグ、またはスワイプでシーク",
    "Search existing players...": "既存の選手を検索…",
    "Search playlists…": "プレイリストを検索…",
    "Select a match video to view while entering lineups.": "ラインナップ入力中に視聴する試合動画を選択してください。",
    "Select a player.": "選手を選択してください。",
    "Select a team.": "チームを選択してください。",
    "Setting up your account": "アカウントを設定しています",
    "Shaded rows are played under a Power Play — HP/AP marks the calling team, bold gold scores are bonus (double) points.": "網掛けされた行はパワープレイ中にプレーされたものです — HP/APはコールしたチームを示し、太字の金色のスコアはボーナス（2倍）ポイントです。",
    "Shared by": "共有者：",
    "Shared with me": "自分と共有済み",
    "Show help": "ヘルプを表示",
    "Show public playlists": "公開プレイリストを表示",
    "Standard": "標準",
    "Substitutions made — tap to view": "交代が行われました — タップして表示",
    "Tap the blocking player.": "ブロックする選手をタップしてください。",
    "Team colour": "チームカラー",
    "Team name": "チーム名",
    "That email is already in the list.": "そのメールアドレスはすでにリストにあります。",
    "The report is too large to save. Try a single match or fewer sets.": "レポートが大きすぎて保存できません。1試合、またはセット数を減らして試してください。",
    "This match has no Power Play calls.": "この試合にはパワープレイのコールがありません。",
    "This player has no name to save the report against.": "この選手にはレポートを保存する名前がありません。",
    "This team has no designated setter. Pick the player who is setting.": "このチームにはセッターが指定されていません。セットをしている選手を選んでください。",
    "Toggle video player": "動画プレーヤーを切り替え",
    "Unmute": "ミュート解除",
    "Untitled playlist": "無題のプレイリスト",
    "Video URL…": "動画URL…",
    "We're moving your data to a faster, more reliable home. This happens only once.": "データをより高速で信頼性の高い場所に移動しています。これは一度だけ行われます。",
    "You don't have permission to view this page.": "このページを表示する権限がありません。",
    "Your data has been migrated successfully.": "データの移行が完了しました。",
    "Your teams and players will appear here after your data is migrated.": "データの移行後、ここにチームと選手が表示されます。",
    "bonus point": "ボーナスポイント",
    "bonus points": "ボーナスポイント",
    "event": "イベント",
    "events": "イベント",
    "file(s).": "個のファイル。",
    "is not on this team's roster.": "このチームの名簿にいません。",
    "loaded": "読み込み済み",
    "matches": "試合",
    "more": "その他",
    "players": "選手",
    "points (no value was entered) — applied automatically.": "点（値が入力されていません）— 自動的に適用されました。",
    "roster": "名簿",
    "seconds": "秒",
    "selected": "選択済み",
    "session(s)?": "個のセッション？",
    "showing": "表示中",
    "tournaments": "大会",
    "…and": "…そして",
    "● marks which team served the point.": "●はどちらのチームがそのポイントをサーブしたかを示します。",
    // i18n backfill pass (Catapult + misc)
    "Centimetres (cm)": "センチメートル (cm)",
    "Inches (in)": "インチ (in)",
    "Jump height units": "ジャンプの高さの単位",
    "Choose how Catapult jump heights are displayed and filtered.": "Catapultのジャンプの高さの表示方法とフィルター方法を選択します。",
    "Select Catapult Focus file...": "Catapult Focusファイルを選択…",
    "Select a Catapult Focus CSV export to view jump data for this match.": "このマッチのジャンプデータを表示するには、Catapult FocusのCSVエクスポートファイルを選択してください。",
    "Loaded from saved match data": "保存された試合データから読み込みました",
    "No jump data found in this file.": "このファイルにジャンプデータが見つかりませんでした。",
    "Could not read this file. Please check it is a Catapult Focus CSV export.": "このファイルを読み込めませんでした。Catapult FocusのCSVエクスポートであることを確認してください。",
    "Could not save jump data with this match.": "このマッチにジャンプデータを保存できませんでした。",
    "Jump data saved with this match.": "このマッチにジャンプデータを保存しました。",
    "Show players with no match stats": "試合統計のない選手を表示",
    "hidden": "非表示",
    "jumps": "ジャンプ",
    "periods": "期間",
    "Sync offset": "同期オフセット",
    "matched": "一致",
    "match events now show jump height/duration in Video Analysis": "件の試合イベントがビデオ分析でジャンプの高さ/時間を表示するようになりました",
    "Team": "チーム",
    "Team A": "チームA",
    "Team B": "チームB",
    "Which team is this Catapult file for?": "このCatapultファイルはどちらのチームのものですか？",
    "We couldn't confidently match the players in this Focus file to one team. Please select which team it belongs to.": "このFocusファイルの選手をどちらのチームか確実に判定できませんでした。所属するチームを選択してください。",
    "This match has no video attached yet.": "この試合にはまだ動画が追加されていません。",
    "No synced jump events found for this player.": "この選手の同期されたジャンプイベントが見つかりませんでした。",
    "No match loaded.": "試合が読み込まれていません。",
    "avg": "平均",
    "max": "最大",
    "Sync": "同期",
    "Jump Report": "ジャンプレポート",
    "Watch Video": "動画を見る",
    "Save PDF": "PDFを保存",
    "Generated by VBLive": "VBLive生成",
    "Deep analysis of {team} from ball-by-ball event data, covering serving, reception, setting, attack, blocking, defence, rotations and opponent tendencies. Best used with the Anthropic (Claude) provider.": "{team}のサーブ、レシーブ、セット、アタック、ブロック、ディフェンス、ローテーション、相手の傾向を網羅した、ラリーごとのイベントデータに基づく詳細分析です。Anthropic（Claude）プロバイダーでの使用を推奨します。",
    "teams": "チーム",
  },

  vi: {
    // Information page
    Information: "Thông tin",
    "Release notes": "Ghi chú phát hành",
    "Contact us": "Liên hệ",
    "Questions, feedback or suggestions are most welcome.": "Mọi câu hỏi, phản hồi hoặc góp ý đều được hoan nghênh.",
    // DV match coding
    Lineups: "Đội hình",
    Point: "Điểm",
    Video: "Video",
    Defaults: "Mặc định",
    "[team][NN][skill][type?][eval][zoneFrom?[szFrom?][zoneTo?[szTo?]]]": "[đội][NN][kỹ năng][loại?][đánh giá][vùngTừ?[vpTừ?][vùngĐến?[vpĐến?]]]",
    Vertical: "Dọc",
    Horizontal: "Ngang",
    "Swap sides": "Đổi sân",
    Subs: "Thay người",
    Substitutions: "Thay người",
    Court: "Sân",
    Home: "Nhà",
    Away: "Khách",
    Games: "Set",
    Enter: "Nhập",
    Undo: "Hoàn tác",
    "Match saved": "Đã lưu trận đấu",
    "Please sign in to save this match.": "Vui lòng đăng nhập để lưu trận đấu này.",
    "Nothing to save yet.": "Chưa có gì để lưu.",
    "Could not save the match. Please try again.": "Không thể lưu trận đấu. Vui lòng thử lại.",
    Discard: "Hủy thay đổi",
    "Resumed autosaved coding": "Đã khôi phục phiên ghi mã tự động lưu",
    "Changes discarded": "Đã hủy các thay đổi",
    "Discard all changes and revert to the starting point?":
      "Hủy tất cả thay đổi và quay lại điểm bắt đầu?",
    Clear: "Xóa",
    "Prev set": "Set trước",
    "Next set": "Set tiếp theo",
    "Match Setup": "Thiết lập trận đấu",
    "First serve": "Giao bóng đầu tiên",
    "Start Match": "Bắt đầu trận đấu",
    "Resume Match": "Tiếp tục trận đấu",
    Skip: "Bỏ qua",
    "Select an event then drag to draw ball path": "Chọn một sự kiện rồi kéo để vẽ đường bóng",
    "Drag on court to set ball path": "Kéo trên sân để đặt đường bóng",
    "No events yet – enter a DV code above and press Enter.": "Chưa có sự kiện nào – nhập mã DV ở trên và nhấn Nhập.",
    "DataVolley code reference": "Tham chiếu mã DataVolley",
    Format: "Định dạng",
    Skills: "Kỹ năng",
    Evaluation: "Đánh giá",
    "Skill / type omission": "Bỏ qua kỹ năng / loại",
    "Skill and/or type may be omitted; defaults fill in the rest.": "Có thể bỏ qua kỹ năng và/hoặc loại; giá trị mặc định sẽ điền phần còn lại.",
    "Dot-coding (S→R and A→B pairs)": "Mã hóa dấu chấm (cặp S→R và A→B)",
    "Chaining & aliases": "Chuỗi & bí danh",
    "team:": "đội:",
    "omit → defaults to A": "bỏ qua → mặc định A",
    "NN: 1 or 2 digit jersey, e.g. 1, 01, 12": "NN: số áo 1 hoặc 2 chữ số, ví dụ 1, 01, 12",
    "sub-zone: a b c d appended to zone digit, e.g. 5b or 5b1a": "vùng phụ: a b c d thêm vào chữ số vùng, ví dụ 5b hoặc 5b1a",
    "S Serve · R Reception · E Set · A Attack · B Block · D Dig · F Freeball": "S Giao bóng · R Đỡ bước 1 · E Chuyền · A Tấn công · B Chắn · D Phòng thủ · F Bóng tự do",
    "# kill/ace · + positive · ! ok · - negative · / overpass · = error": "# điểm/ace · + tích cực · ! ổn · - tiêu cực · / chuyền hỏng · = lỗi",
    "E.g.": "Ví dụ",
    "serve with skill=A (default), type=H, eval=+, zone=5.": "giao bóng với skill=A (mặc định), type=H, eval=+, zone=5.",
    "type-only (no skill marker), defaults skill from context.": "chỉ loại (không có ký hiệu kỹ năng), kỹ năng mặc định theo ngữ cảnh.",
    "serve coded, reception auto-linked.": "giao bóng đã mã hóa, đỡ bước 1 tự động liên kết.",
    "attack coded, block auto-linked.": "tấn công đã mã hóa, chắn bóng tự động liên kết.",
    "Dot connects the reception eval to the corresponding serve eval via the serve→receive mapping.": "Dấu chấm kết nối đánh giá đỡ bước 1 với đánh giá giao bóng tương ứng qua ánh xạ giao bóng→đỡ bước 1.",
    "two events in one string.": "hai sự kiện trong một chuỗi.",
    "manual point A/B.": "điểm thủ công A/B.",
    "serve ace/error.": "ace/lỗi giao bóng.",
    "serve with eval.": "giao bóng kèm đánh giá.",
    // Stats, sideouts, filters & video tooltips
    "Block Kills": "Điểm chắn bóng",
    "Side Out Percentage": "Tỷ lệ side out",
    "All First Ball": "Tất cả bóng đầu",
    "First Ball Kills": "Điểm bóng đầu",
    "Good Serve": "Phát bóng tốt",
    "Break Points": "Điểm break",
    Game: "Set",
    Row: "Vị trí",
    "First Ball Sideouts": "Side out bóng đầu",
    "All Sideouts": "Tất cả side out",
    All: "Tất cả",
    None: "Không",
    Receiver: "Người đỡ bóng",
    "# Passes": "Số lần đỡ",
    "Perfect %": "% hoàn hảo",
    "Positive %": "% tích cực",
    "Sideout %": "% side out",
    "FB Sideout %": "% side out bóng đầu",
    "Pass Hits": "Tấn công sau khi đỡ",
    "Pass Hit Kills": "Điểm sau khi đỡ",
    "Pass Hit Errors": "Lỗi sau khi đỡ",
    "Show Passes": "Hiện các pha đỡ",
    "Show Attacks": "Hiện các pha tấn công",
    "Error Pass": "Đỡ lỗi",
    "1 Pass": "Đỡ 1",
    "2 Pass": "Đỡ 2",
    "Perfect Pass": "Đỡ hoàn hảo",
    "Successful SO": "Side out thành công",
    "First Ball SO": "Side out bóng đầu",
    "Unsuccessful SO": "Side out thất bại",
    RECEIVES: "ĐỠ BÓNG",
    Sets: "Các set",
    Rotations: "Xoay vòng",
    "Attack Combos": "Phối hợp tấn công",
    Stages: "Giai đoạn",
    Display: "Hiển thị",
    Setters: "Chuyền hai",
    Attackers: "Tay đập",
    Results: "Kết quả",
    Source: "Nguồn",
    Cones: "Khu vực",
    "Select None": "Bỏ chọn tất cả",
    "All Serves": "Tất cả phát bóng",
    "All Returns": "Tất cả đỡ bóng",
    Filters: "Bộ lọc",
    "Reset all filters": "Đặt lại tất cả bộ lọc",
    "Show video clips of filtered attacks": "Hiện video các pha tấn công đã lọc",
    "Spikes for the selected player": "Các pha đập của cầu thủ đã chọn",
    "Click on the zones to view video of attacks": "Nhấp vào các khu vực để xem video tấn công",
    "Expand All": "Mở rộng tất cả",
    "Collapse All": "Thu gọn tất cả",
    "Import Radar File": "Nhập tệp Radar",
    "Select local video file": "Chọn tệp video cục bộ",
    "Save to database": "Lưu vào cơ sở dữ liệu",
    "Load and play online video": "Tải và phát video trực tuyến",
    "Synchronise video with events": "Đồng bộ video với các sự kiện",
    "Rewind 10 seconds": "Tua lại 10 giây",
    "Rewind 1 second": "Tua lại 1 giây",
    "Forward 1 second": "Tua tới 1 giây",
    "Forward 10 seconds": "Tua tới 10 giây",
    "Set timing for events": "Đặt thời gian cho các sự kiện",

    // Navigation
    Matches: "Trận đấu",
    Live: "Trực tiếp",
    Import: "Nhập",
    Playlists: "Playlists",
    "New Match": "Trận đấu mới",
    // New Match / Match Coding gate
    "Match Coding": "Ghi mã trận đấu",
    "Match Coding — Coming Soon": "Ghi mã trận đấu — Sắp ra mắt",
    "Live match coding is on the way. Here's a preview of what's coming.":
      "Ghi mã trận đấu trực tiếp sắp ra mắt. Dưới đây là bản xem trước về những gì sắp tới.",
    Settings: "Cài đặt",
    Language: "Ngôn ngữ",
    "Your profile": "Hồ sơ của bạn",
    "Sign out": "Đăng xuất",
    "Translation Editor": "Trình chỉnh sửa bản dịch",

    // Common actions
    Save: "Lưu",
    Cancel: "Hủy",
    Delete: "Xóa",
    Share: "Chia sẻ",
    Edit: "Chỉnh sửa",
    New: "Mới",
    Refresh: "Làm mới",
    Search: "Tìm kiếm",
    Reset: "Đặt lại",
    Close: "Đóng",
    "Sub for": "Thay cho",
    "No players available": "Không có cầu thủ khả dụng",
    "Show full roster": "Hiện toàn bộ đội hình",
    "Available only": "Chỉ khả dụng",
    "Maximum substitutions reached": "Đã đạt số lần thay người tối đa",
    Back: "Quay lại",
    "Download PDF": "Tải PDF",
    "Loading...": "Đang tải…",
    "Select All": "Chọn tất cả",
    "Unselect All": "Bỏ chọn tất cả",
    Yes: "Có",
    No: "Không",
    Add: "Thêm",
    Load: "Tải",
    Confirm: "Xác nhận",
    Open: "Mở",
    show: "hiển thị",

    // Auth
    "Sign In": "Đăng nhập",
    "Sign Up": "Đăng ký",
    "Sign Out": "Đăng xuất",
    Email: "Email",
    Password: "Mật khẩu",
    "Confirm Password": "Xác nhận mật khẩu",
    "Forgot password?": "Quên mật khẩu?",
    "Forgot Password": "Quên mật khẩu",
    "Don't have an account?": "Chưa có tài khoản?",
    "Sign up for free!": "Đăng ký miễn phí!",
    "All ready signed up?": "Đã đăng ký rồi?",
    "Error Signing In": "Lỗi đăng nhập",
    "Signed up successfully": "Đăng ký thành công",
    "Error signing up": "Lỗi đăng ký",
    "Enter your email": "Nhập email của bạn",
    "Send Reset Email": "Gửi email đặt lại",
    "Email was sent to": "Email đã được gửi tới",
    "Could not send reset email":
      "Không thể gửi email đặt lại",

    // Matches list
    "Team 1": "Đội 1",
    "Team 2": "Đội 2",
    Tournament: "Giải đấu",
    Scores: "Tỷ số",
    Date: "Ngày",
    Actions: "Thao tác",
    Owner: "Chủ sở hữu",
    Description: "Mô tả",
    Comments: "Nhận xét",
    Tags: "Thẻ",
    "Add player": "Thêm cầu thủ",
    "Add team": "Thêm đội",
    "Edit playlist": "Chỉnh sửa danh sách phát",
    "Merge Teams": "Gộp đội",
    "Roster differences detected between teams:": "Phát hiện sự khác biệt trong danh sách cầu thủ giữa các đội:",
    "Review the roster in the next step before merging.": "Xem lại danh sách cầu thủ ở bước tiếp theo trước khi gộp.",
    "At least 2 team records must exist in your Teams list for the selected matches.": "Phải có ít nhất 2 bản ghi đội trong danh sách của bạn cho các trận đấu đã chọn.",
    "Merge...": "Gộp...",
    "Select 2 or more team records to merge into one.": "Chọn 2 hoặc nhiều bản ghi đội để gộp thành một.",
    "Filter teams...": "Lọc đội...",
    "teams selected": "đội được chọn",
    "Select Teams": "Chọn đội",
    "Reconcile Roster": "Đối chiếu danh sách",
    "Confirm Merge": "Xác nhận gộp",
    "Merged team name": "Tên đội đã gộp",
    "shirt number(s) have conflicting names — review below.": "số áo có tên xung đột — xem bên dưới.",
    "Shirt": "Số áo",
    "players included": "cầu thủ được bao gồm",
    "No players to reconcile.": "Không có cầu thủ nào để đối chiếu.",
    "Use this name": "Dùng tên này",
    "Create merged team": "Tạo đội đã gộp",
    "with": "với",
    "duplicate team records": "bản ghi đội trùng lặp",
    "Consolidate duplicate player records and reassign playlist links": "Hợp nhất bản ghi cầu thủ trùng lặp và gán lại liên kết danh sách phát",
    "This cannot be undone.": "Hành động này không thể hoàn tác.",
    "Teams being merged:": "Các đội đang được gộp:",
    "Step": "Bước",
    "of": "trên",
    "Merging...": "Đang gộp...",
    "Players & Teams": "Cầu thủ và đội",
    "No players or teams assigned.": "Chưa liên kết cầu thủ hoặc đội nào.",
    "Search players...": "Tìm kiếm cầu thủ...",
    "Search teams...": "Tìm kiếm đội...",
    "Saving...": "Đang lưu...",
    "My Matches": "Trận đấu của tôi",
    "Shared Matches": "Trận đấu được chia sẻ",
    "Search team...": "Tìm đội…",
    "All Teams": "Tất cả các đội",
    "Multi Match Reports": "Báo cáo nhiều trận",
    "Delete match": "Xóa trận đấu",
    "Remove from my shared matches": "Xóa khỏi danh sách trận đấu được chia sẻ của tôi",
    "Share match": "Chia sẻ trận đấu",
    "Edit match": "Chỉnh sửa trận đấu",
    "Open video analysis": "Mở phân tích video",
    "Delete Match": "Xóa trận đấu",
    "Are you sure you want to delete the selected match?":
      "Bạn có chắc muốn xóa trận đấu đã chọn không?",
    "No matches selected.": "Chưa chọn trận đấu nào.",

    // New Match
    "← Back": "← Quay lại",
    "Team A (Home)": "Đội A (sân nhà)",
    "Team B (Away)": "Đội B (sân khách)",
    "Select team…": "Chọn đội…",
    "Tournament (optional)": "Giải đấu (tùy chọn)",
    "Tournament / competition name": "Tên giải đấu / cuộc thi",
    "Match date": "Ngày thi đấu",
    "Start Coding →": "Bắt đầu ghi nhận →",

    // Reports
    Summary: "Tóm tắt",
    "Box Score": "Bảng thống kê",
    "Side-outs Report": "Báo cáo giành quyền giao bóng",
    "Serve Receives": "Đỡ giao bóng",
    "Attack Zones": "Khu vực tấn công",
    "Hitting Charts": "Biểu đồ tấn công",
    "Video Analysis": "Phân tích video",
    Dashboard: "Bảng điều khiển",
    Charts: "Biểu đồ",
    "AI Report": "Báo cáo AI",
    // AI Reports page
    "AI Reports": "Báo cáo AI",
    "AI Reports — Coming Soon": "Báo cáo AI — Sắp ra mắt",
    "AI-powered match reports and deep analysis are on the way. Here are some sample reports to show what's coming.":
      "Báo cáo trận đấu và phân tích chuyên sâu bằng AI sắp ra mắt. Dưới đây là một số báo cáo mẫu để bạn xem trước.",
    "Preview not available.": "Không có bản xem trước.",
    "Open sample": "Mở mẫu",
    "No matches were loaded. Select one or more matches and try again.":
      "Chưa có trận đấu nào được tải. Hãy chọn một hoặc nhiều trận đấu rồi thử lại.",
    "Attack Analysis (English)": "Phân tích tấn công (Tiếng Anh)",
    "Blocking Analysis (Vietnamese)": "Phân tích chắn bóng (Tiếng Việt)",
    "Opponent Tendencies (Japanese)": "Xu hướng đối thủ (Tiếng Nhật)",
    // Deep AI analysis
    "Deep AI Analysis": "Phân tích AI chuyên sâu",
    "AI Deep Analysis": "Phân tích chuyên sâu bằng AI",
    "Load at least one match with coded events to generate the analysis.": "Tải ít nhất một trận đấu có sự kiện đã mã hóa để tạo phân tích.",
    "Run the deep analysis one section at a time to keep each request small. Edit or add questions below, then generate. Best used with the Anthropic (Claude) provider.":
      "Chạy phân tích chuyên sâu theo từng phần để mỗi yêu cầu nhỏ gọn. Chỉnh sửa hoặc thêm câu hỏi bên dưới, sau đó tạo. Nên dùng với nhà cung cấp Anthropic (Claude).",
    "Download CSV": "Tải xuống CSV",
    "Analysis section": "Phần phân tích",
    analysing: "đang phân tích",
    "All sections (uses the most credits)": "Tất cả các phần (dùng nhiều tín dụng nhất)",
    "All sections": "Tất cả các phần",
    Questions: "Câu hỏi",
    "one per line, edit or add your own": "mỗi dòng một câu, chỉnh sửa hoặc thêm câu của bạn",
    "Reset to defaults": "Đặt lại mặc định",
    "Add a question…": "Thêm câu hỏi…",
    "Runs every section using the questions you've set for each. This sends one large request and uses the most credits.":
      "Chạy tất cả các phần với những câu hỏi bạn đã đặt cho mỗi phần. Thao tác này gửi một yêu cầu lớn và dùng nhiều tín dụng nhất.",
    "Generate full analysis": "Tạo phân tích đầy đủ",
    "Generate section analysis": "Tạo phân tích phần",
    Serving: "Phát bóng",
    Reception: "Đỡ bước một",
    Setting: "Chuyền hai",
    Attack: "Tấn công",
    Blocking: "Chắn bóng",
    "Defence / Digging": "Phòng thủ / Cứu bóng",
    "Rotation Analysis": "Phân tích xoay vòng",
    "Opponent Tendencies (Scouting)": "Xu hướng đối thủ (trinh sát)",
    "Overall Team Metrics": "Chỉ số tổng thể của đội",
    "Serve efficiency (aces, errors, and pressured receptions forced).": "Hiệu quả phát bóng (điểm trực tiếp, lỗi và những pha đỡ bị ép).",
    "Which servers are most effective, and against which reception zones?": "Những người phát bóng nào hiệu quả nhất, và nhắm vào những khu vực đỡ bóng nào?",
    "Which serve types (jump, float, jump-float) yield the best results?": "Những kiểu phát bóng nào (nhảy phát, phát lá, nhảy phát lá) cho kết quả tốt nhất?",
    "Reception quality breakdown (perfect, positive, negative, error).": "Phân tích chất lượng đỡ bóng (hoàn hảo, tích cực, tiêu cực, lỗi).",
    "Which players struggle most under jump serves vs. float serves?": "Những cầu thủ nào gặp khó khăn nhất trước phát bóng nhảy so với phát lá?",
    "How does poor reception correlate with side-out efficiency?": "Đỡ bóng kém liên quan thế nào đến hiệu quả giành lại quyền phát bóng?",
    "Set distribution by zone and by player.": "Phân bổ chuyền hai theo khu vực và theo cầu thủ.",
    "How does the setter perform under pressure (bad-pass situations)?": "Chuyền hai thi đấu thế nào khi bị áp lực (tình huống đỡ bóng xấu)?",
    "Tempo breakdown (quick, medium, high ball) and which yields the best attack outcome.": "Phân tích nhịp độ (bóng nhanh, bóng trung, bóng cao) và nhịp nào cho kết quả tấn công tốt nhất.",
    "Kill percentage and error rate per player, per rotation, and per zone.": "Tỷ lệ dứt điểm và tỷ lệ lỗi theo cầu thủ, theo lượt xoay và theo khu vực.",
    "Which attack zones (pipe, cross, line) have the highest efficiency?": "Những khu vực tấn công nào (pipe, chéo, biên) có hiệu quả cao nhất?",
    "How attack efficiency changes in transition vs. side-out.": "Hiệu quả tấn công thay đổi thế nào trong pha chuyển đổi so với pha giành lại quyền phát bóng.",
    "Block-per-set rate per player and rotation.": "Số pha chắn bóng mỗi hiệp theo cầu thủ và lượt xoay.",
    "Which opposing attack zones are most successfully blocked?": "Những khu vực tấn công nào của đối thủ bị chắn thành công nhất?",
    "How often blocks result in kills vs. touch-outs.": "Mức độ thường xuyên pha chắn bóng thành điểm so với chạm tay ra ngoài.",
    "Dig success rate per player and per court zone.": "Tỷ lệ cứu bóng thành công theo cầu thủ và theo khu vực sân.",
    "Which attack types (tip, roll, hard swing) are hardest to defend?": "Những kiểu tấn công nào (bỏ nhỏ, đập nhẹ, đập mạnh) khó phòng thủ nhất?",
    "How defensive efficiency affects transition attack success.": "Hiệu quả phòng thủ ảnh hưởng thế nào đến thành công của pha tấn công chuyển đổi.",
    "Which rotations have the highest and lowest point differential?": "Những lượt xoay nào có hiệu số điểm cao nhất và thấp nhất?",
    "Side-out and break-point percentages per rotation.": "Tỷ lệ giành lại quyền phát bóng và điểm break theo lượt xoay.",
    "Rotation-specific vulnerabilities (e.g. a weak serving or passing rotation).": "Điểm yếu của từng lượt xoay (ví dụ lượt xoay yếu khi phát bóng hoặc đỡ bóng).",
    "The opponent's most frequently used attack zones and combinations.": "Những khu vực tấn công và miếng phối hợp mà đối thủ dùng nhiều nhất.",
    "The opponent's go-to attackers in high-pressure situations.": "Những tay đập chủ lực của đối thủ trong các tình huống áp lực cao.",
    "The serve-receive patterns the opponent favours.": "Những sơ đồ đỡ phát bóng mà đối thủ ưa dùng.",
    "Overall rally-win probability in different score situations.": "Xác suất thắng pha bóng tổng thể trong các tình huống tỷ số khác nhau.",
    "How performance changes in sets 1–3 vs. sets 4–5 (fatigue/pressure effects).": "Phong độ thay đổi thế nào ở các hiệp 1–3 so với hiệp 4–5 (ảnh hưởng của mệt mỏi/áp lực).",
    "The correlation between first-ball side-out percentage and match outcome.": "Mối tương quan giữa tỷ lệ giành lại quyền phát bóng ở quả đầu và kết quả trận đấu.",
    Report: "Báo cáo",

    // Import page
    "Load DVW file(s)...": "Tải tệp DVW…",
    "Load PSVB file(s)...": "Tải tệp PSVB…",
    "Load VSM file(s)...": "Tải tệp VSM…",
    "Load playlist file...": "Tải tệp playlist…",
    "load one or more DataVolley files":
      "tải một hoặc nhiều tệp DataVolley",
    "load one or more VBStats files":
      "tải một hoặc nhiều tệp VBStats",
    "load one or more VolleyStation VSM files":
      "tải một hoặc nhiều tệp VolleyStation VSM",
    "Convert DVW": "Chuyển đổi DVW",
    "VSM": "VSM",
    "Convert DVW to VSM...": "Chuyển đổi DVW sang VSM…",
    "pick a DataVolley .dvw file to download as .vsm":
      "chọn một tệp DataVolley .dvw để tải xuống dưới dạng .vsm",
    "Convert VSM to DVW...": "Chuyển đổi VSM sang DVW…",
    "pick a VolleyStation .vsm file to download as .dvw":
      "chọn một tệp VolleyStation .vsm để tải xuống dưới dạng .dvw",
    "Converting…": "Đang chuyển đổi…",
    "Converted": "Đã chuyển đổi",
    "Failed to convert": "Chuyển đổi thất bại",
    "Beta testers only — coming soon for everyone else.":
      "Chỉ dành cho người thử nghiệm beta — sắp ra mắt cho mọi người.",
    "load a playlist": "tải một playlist",
    "Use Shift-JIS Encoder": "Dùng bộ mã hóa Shift-JIS",
    "Importing…": "Đang nhập…",
    "Save to Database?": "Lưu vào cơ sở dữ liệu?",
    "Always save imports automatically": "Luôn tự động lưu khi nhập",
    "View Only": "Chỉ xem",
    "Save to Database": "Lưu vào cơ sở dữ liệu",
    "Error saving to database": "Lỗi khi lưu vào cơ sở dữ liệu",
    "Saved to database": "Đã lưu vào cơ sở dữ liệu",
    "Saving to database…": "Đang lưu vào cơ sở dữ liệu…",
    "Upload them to the database now?":
      "Tải chúng lên cơ sở dữ liệu ngay bây giờ?",

    // Settings / Options
    Theme: "Giao diện",
    Options: "Tùy chọn",
    "Rotation naming convention": "Quy ước đặt tên xoay vòng",
    "Choose how rotations are labelled in reports and analysis.":
      "Chọn cách gắn nhãn các vòng xoay trong báo cáo và phân tích.",
    "R1–R6 (rotation sequence)": "R1–R6 (trình tự xoay vòng)",
    "S1–S6 (setter court position)": "S1–S6 (vị trí chuyền hai trên sân)",
    "R1: setter in position 1, R2: setter in 6, R3: setter in 5, R4: setter in 4, R5: setter in 3, R6: setter in 2.":
      "R1: chuyền hai ở vị trí 1, R2: chuyền hai ở 6, R3: chuyền hai ở 5, R4: chuyền hai ở 4, R5: chuyền hai ở 3, R6: chuyền hai ở 2.",
    "S1: setter in 1, S2: setter in 2, S3: setter in 3, S4: setter in 4, S5: setter in 5, S6: setter in 6.":
      "S1: chuyền hai ở 1, S2: chuyền hai ở 2, S3: chuyền hai ở 3, S4: chuyền hai ở 4, S5: chuyền hai ở 5, S6: chuyền hai ở 6.",
    "Labels:": "Nhãn:",
    "Current theme:": "Giao diện hiện tại:",
    "Choose the language for the app interface.":
      "Chọn ngôn ngữ cho giao diện ứng dụng.",
    "Create your own language package or edit a community one, with AI-assisted translation.":
      "Tạo gói ngôn ngữ của riêng bạn hoặc chỉnh sửa gói của cộng đồng, với bản dịch hỗ trợ bởi AI.",

    // About / Footer
    "Copyright © Perana Sports {year}. All rights reserved.":
      "Bản quyền © Perana Sports {year}. Bảo lưu mọi quyền.",
    "VBLive is free to use right now. However Perana Sports reserves the right to charge a subscription fee in due course.":
      "VBLive hiện miễn phí sử dụng. Tuy nhiên, Perana Sports bảo lưu quyền thu phí đăng ký vào thời điểm thích hợp.",
    "Querries / suggestions are most welcomed.":
      "Rất hoan nghênh các câu hỏi / góp ý.",

    // Live
    "Please enter server name to search":
      "Vui lòng nhập tên máy chủ để tìm kiếm",
    "Broadcast Server": "Máy chủ phát sóng",
    "Enter DVMateLive Server Name": "Nhập tên máy chủ DVMateLive",
    LIVE: "TRỰC TIẾP",

    // Playlists list
    "Delete Playlist": "Xóa Playlist",
    "Are you sure you want to delete the selected playlist?":
      "Bạn có chắc muốn xóa playlist đã chọn không?",
    "My Playlists": "Playlist của tôi",
    "Shared Playlists": "Playlist được chia sẻ",
    "Tags:": "Thẻ:",
    "Select...": "Chọn…",
    "All Tags": "Tất cả các thẻ",

    // Share
    "User already added": "Người dùng đã được thêm",
    "Please enter a valid email address":
      "Vui lòng nhập địa chỉ email hợp lệ",
    "Please select at least one user to share with":
      "Vui lòng chọn ít nhất một người dùng để chia sẻ",
    "Not Shared": "Chưa chia sẻ",
    "Available for everyone": "Có sẵn cho mọi người",
    "Available to selected users":
      "Có sẵn cho người dùng đã chọn",
    "Enter VBLive user email address":
      "Nhập địa chỉ email của người dùng VBLive",
    "Selected users:": "Người dùng đã chọn:",
    "Save List": "Lưu danh sách",
    "Clear List": "Xóa danh sách",
    "Clear Users List": "Xóa danh sách người dùng",
    "Do you really want to clear all users from list?":
      "Bạn có thực sự muốn xóa tất cả người dùng khỏi danh sách không?",
    "Do you want to save changes before closing?":
      "Bạn có muốn lưu thay đổi trước khi đóng không?",
    "Enter name to save list to:":
      "Nhập tên để lưu danh sách:",
    "Enter name to save list to":
      "Nhập tên để lưu danh sách",

    // AI panels
    "AI settings": "Cài đặt AI",
    "Setup needed": "Cần thiết lập",
    "Generate report": "Tạo báo cáo",
    "Generate AI report": "Tạo báo cáo AI",
    "Generate general report": "Tạo báo cáo tổng quát",
    "Translate to…": "Dịch sang…",
    "Follow-up questions": "Câu hỏi tiếp theo",
    "Ask a follow-up question…": "Đặt một câu hỏi tiếp theo…",
    "Ask a follow-up question": "Đặt một câu hỏi tiếp theo",
    "AI provider": "Nhà cung cấp AI",
    Model: "Mô hình",
    Hide: "Ẩn",
    Show: "Hiển thị",
    "Your key is stored only in this browser and sent directly to the selected provider.":
      "Khóa của bạn chỉ được lưu trong trình duyệt này và gửi trực tiếp đến nhà cung cấp đã chọn.",
    "Please enter your API key.": "Vui lòng nhập khóa API của bạn.",
    "Not enough data to analyse.":
      "Không đủ dữ liệu để phân tích.",
    "Select a player with recorded spikes first.":
      "Hãy chọn một cầu thủ có ghi nhận pha đập bóng trước.",
    Player: "Cầu thủ",
    "Select a player…": "Chọn một cầu thủ…",
    "No players with recorded spikes in the selected matches.":
      "Không có cầu thủ nào được ghi nhận pha đập bóng trong các trận đã chọn.",
    "Analyse with AI": "Phân tích bằng AI",
    "AI scouting report": "Báo cáo trinh sát AI",
    "Q:": "H:",
    Ask: "Hỏi",
    "e.g. Which rotation should we target on serve?":
      "vd: Chúng ta nên nhắm vào vòng xoay nào khi giao bóng?",
    "Saved questions:": "Câu hỏi đã lưu:",
    "Click to ask again": "Nhấp để hỏi lại",
    "Remove saved question": "Xóa câu hỏi đã lưu",

    // AI language options
    Spanish: "Tiếng Tây Ban Nha",
    French: "Tiếng Pháp",
    German: "Tiếng Đức",
    Italian: "Tiếng Ý",
    Portuguese: "Tiếng Bồ Đào Nha",
    Dutch: "Tiếng Hà Lan",
    Polish: "Tiếng Ba Lan",
    Russian: "Tiếng Nga",
    Turkish: "Tiếng Thổ Nhĩ Kỳ",
    Arabic: "Tiếng Ả Rập",
    "Chinese (Simplified)": "Tiếng Trung (giản thể)",
    Japanese: "Tiếng Nhật",
    Korean: "Tiếng Hàn",
    Vietnamese: "Tiếng Việt",
    Thai: "Tiếng Thái",

    // Box score
    "Show Full Headers": "Hiển thị tiêu đề đầy đủ",
    "Show Short Headers": "Hiển thị tiêu đề ngắn",
    "Show Less Players": "Hiển thị ít cầu thủ hơn",
    "Show All Players": "Hiển thị tất cả cầu thủ",
    "Attack Kills": "Điểm tấn công",
    "Attack Errors": "Lỗi tấn công",
    "Total Attempts": "Tổng số lần thử",
    "Hitting Efficiency": "Hiệu suất tấn công",
    "Serve Aces": "Điểm trực tiếp từ giao bóng",
    "Serve Errors": "Lỗi giao bóng",
    "Pass Errors": "Lỗi đỡ bóng",
    "% Perfect Passes": "% đỡ bóng hoàn hảo",
    "Passing Average": "Trung bình đỡ bóng",
    "Total Passes": "Tổng số lần đỡ bóng",
    "Set Assists": "Kiến tạo chuyền hai",
    Digs: "Cứu bóng",
    "Momentum Chart": "Biểu đồ Momentum",
    "Key event": "Pha bóng quyết định",
    "Break Point %": "% điểm break",
    "First Ball Sideout %": "% side out bóng đầu tiên",
    "Sideout Transition %": "% side out chuyển đổi",
    "FB Sideout Kills %": "% ghi điểm bóng đầu tiên",
    "Sideout Opposition Error %": "% side out do lỗi đối phương",
    Plus: "Cộng",
    Minus: "Trừ",
    Blocks: "Điểm chắn bóng",
    "Positive Serve %": "% giao bóng hiệu quả",
    Sideout: "Side out",
    "Break point": "Điểm break",
    "Ace / Kill": "Ace / Đập ghi điểm",
    "In play": "Đang thi đấu",
    Error: "Lỗi",
    "Sideout on a kill": "Side out nhờ đập ghi điểm",
    "Sideout on an error": "Side out nhờ lỗi đối phương",
    "Top half": "Nửa trên",
    "Bottom half": "Nửa dưới",
    "The centre line steps toward the team that wins each point.": "Đường trung tâm di chuyển về phía đội giành được điểm đó.",
    "No point-by-point data available for this match.": "Không có dữ liệu từng điểm cho trận đấu này.",
    "No match data available.": "Không có dữ liệu trận đấu.",
    "This report isn't supported on this browser yet — please use Safari.": "Báo cáo này chưa được hỗ trợ trên trình duyệt này — vui lòng sử dụng Safari.",
    "No video available for this match.": "Không có video cho trận đấu này.",
    passes: "lần đỡ",
    "eff.": "hiệu suất",
    Grade: "Điểm",
    LEFT: "TRÁI",
    CENTRE: "GIỮA",
    RIGHT: "PHẢI",
    SELECTED: "ĐÃ CHỌN",
    Passers: "Người đỡ bóng",
    Calls: "Lệnh gọi",
    "SIDE OUT": "SIDE OUT",
    TRANSITION: "CHUYỂN ĐỔI",
    "No events in selection.": "Không có sự kiện nào trong lựa chọn hiện tại.",
    "Serve Receives By Players": "Đỡ giao bóng theo cầu thủ",
    "Serve Receive By Zones": "Đỡ giao bóng theo khu vực",
    "Dig Errors": "Lỗi cứu bóng",
    "Dig Average": "Trung bình cứu bóng",
    "Block Solos": "Chắn bóng đơn",
    "Block Assists": "Hỗ trợ chắn bóng",
    "Block Errors": "Lỗi chắn bóng",
    "Points Scored": "Điểm ghi được",
    TEAM: "ĐỘI",

    // Sideout report
    "ALL ROWS": "TẤT CẢ CÁC VÒNG",
    "ROWS ": "CÁC VÒNG ",
    "ROW ": "VÒNG ",
    "FB Kills": "Điểm tấn công FB",
    "Opp. Serve Errors": "Lỗi giao bóng đối thủ",
    "FB Opp. Errors": "Lỗi đối thủ FB",
    "Kills/Blocks": "Tấn công/Chắn bóng",
    "Opp. Errors": "Lỗi đối thủ",
    Unsuccessful: "Không thành công",

    // Attack zones
    "No events in selected zone.":
      "Không có sự kiện nào trong khu vực đã chọn.",
    "Show all attacks. Or click on a zone to show attacks in that zone.":
      "Hiển thị tất cả các pha tấn công. Hoặc nhấp vào một khu vực để hiển thị các pha tấn công trong khu vực đó.",
    "OPPONENTS (": "ĐỐI THỦ (",

    // Video analysis filters
    "All Types": "Tất cả các loại",
    Serve: "Giao bóng",
    "Serve-Receive": "Đỡ giao bóng",
    Set: "Chuyền hai",
    "Setter's Call": "Hiệu lệnh chuyền hai",
    "Disable this skill": "Tắt kỹ năng này",
    "Enable this skill": "Bật kỹ năng này",
    "Teams & Players": "Đội và Vận động viên",
    "Match State": "Trạng thái trận đấu",
    "Custom Code": "Mã tùy chỉnh",
    "Search... (comma-separated for multiple)": "Tìm kiếm... (cách nhau bằng dấu phẩy cho nhiều mục)",
    vs: "vs",
    "Rewind 60 seconds": "Tua lại 60 giây",
    "Rewind 1 frame": "Tua lại 1 khung hình",
    "Forward 1 frame": "Tua tới 1 khung hình",
    "Forward 60 seconds": "Tua tới 60 giây",
    "Previous Clip": "Đoạn trước",
    Replay: "Phát lại",
    "Next Clip": "Đoạn tiếp theo",
    Phases: "Giai đoạn",
    "Score Range": "Khoảng cách điểm số",
    "Set Winner": "Đội thắng set",
    Type: "Loại",
    Strength: "Lực đánh",
    "Setter Calls": "Hiệu lệnh chuyền hai",
    "Setter Call": "Hiệu lệnh chuyền hai",
    Freeball: "Bóng tự do",
    "Close (0-2)": "Sít sao (0-2)",
    "Moderate (3-5)": "Vừa phải (3-5)",
    "Lopsided (6+)": "Chênh lệch lớn (6+)",
    "Focus Team": "Đội theo dõi",
    Oppositions: "Đối thủ",
    "Focus Team Won": "Đội theo dõi thắng",
    "Oppositions Won": "Đối thủ thắng",
    "Advanced Filters": "Bộ lọc nâng cao",
    Players: "Cầu thủ",
    Opponents: "Đối thủ",
    "Event Type": "Loại sự kiện",
    "Event Result": "Kết quả sự kiện",
    "Attack Type": "Loại tấn công",
    "Hit Type": "Loại cú đánh",
    "Block Type": "Loại chắn bóng",
    "Attack Combo": "Tổ hợp tấn công",
    Apply: "Áp dụng",
    Rotation: "Vòng xoay",
    Skill: "Kỹ năng",
    "Lead Time": "Thời gian dẫn trước",
    Duration: "Thời lượng",
    Timing: "Thời điểm",
    "Go Live": "Chuyển đến trực tiếp",
    "Playback has fallen behind the live broadcast — click to jump to now.": "Video đang phát chậm hơn so với buổi phát trực tiếp — nhấn để chuyển đến thời điểm hiện tại.",
    "Video timing": "Thời điểm video",
    "Default lead time (seconds before an event to start playback) and clip duration, per skill.": "Thời gian dẫn trước mặc định (số giây trước sự kiện để bắt đầu phát) và thời lượng đoạn clip, theo từng kỹ năng.",
    Cloud: "Đám mây",
    Local: "Cục bộ",
    "Relay host": "Máy chủ relay",
    "IP or hostname of the machine running the local relay.": "Địa chỉ IP hoặc tên máy chủ của máy đang chạy relay cục bộ.",
    Spike: "Đập bóng",
    Block: "Chắn bóng",
    Defence: "Phòng thủ",
    "All Results": "Tất cả kết quả",
    Hard: "Mạnh",
    Soft: "Nhẹ",
    Tip: "Bỏ nhỏ",
    "Not Specified": "Không xác định",
    "H - High": "H - Cao",
    "M- Medium": "M - Trung bình",
    "Q - Quick": "Q - Nhanh",
    "T - Tense": "T - Căng",
    "U - Super": "U - Siêu",
    "F - Fast": "F - Tốc độ",
    "O - Other": "O - Khác",
    "B0 - No Block": "B0 - Không chắn",
    "B1 - 1 Block": "B1 - 1 người chắn",
    "B2 - 2 Blocks": "B2 - 2 người chắn",
    "B3 - 3 Blocks": "B3 - 3 người chắn",
    "BS - Seams": "BS - Khoảng trống",

    // All filters panel
    ALL: "TẤT CẢ",
    NONE: "KHÔNG",

    // Playlist editor
    "Remove Item": "Xóa mục",
    "Do you really want to remove the selected item?":
      "Bạn có thực sự muốn xóa mục đã chọn không?",
    "Description is required": "Mô tả là bắt buộc",
    "Comments are required": "Nhận xét là bắt buộc",
    "Tags are required": "Thẻ là bắt buộc",
    "Playlist saved successfully": "Đã lưu playlist thành công",
    "Error saving playlist": "Lỗi khi lưu playlist",

    // Language editor
    "My saved packages": "Gói đã lưu của tôi",
    "Loading shared languages…": "Đang tải các ngôn ngữ được chia sẻ…",
    "Community shared languages": "Ngôn ngữ được cộng đồng chia sẻ",
    by: "bởi",
    "based on": "dựa trên",
    "or create new": "hoặc tạo mới",
    Use: "Sử dụng",
    "New Language Package": "Gói ngôn ngữ mới",
    "Language Name": "Tên ngôn ngữ",
    "Language Code": "Mã ngôn ngữ",
    "Flag Emoji": "Biểu tượng cờ",
    "Base language (reference)": "Ngôn ngữ gốc (tham chiếu)",
    "If not English, both English and the reference language will be shown.":
      "Nếu không phải tiếng Anh, cả tiếng Anh và ngôn ngữ tham chiếu sẽ được hiển thị.",
    "Start Editing": "Bắt đầu chỉnh sửa",
    "Save Key": "Lưu khóa",
    "AI key set": "Đã đặt khóa AI",
    "Set API key for AI translation":
      "Đặt khóa API cho bản dịch AI",
    "AI key ✓": "Khóa AI ✓",
    "Set AI key": "Đặt khóa AI",
    "AI: Translate All": "AI: Dịch tất cả",
    "Translating…": "Đang dịch…",
    "AI translate section": "Dịch phần này bằng AI",
    "Clear All": "Xóa tất cả",
    "Saving…": "Đang lưu…",
    English: "Tiếng Anh",
    new: "mới",
    "Enter translation…": "Nhập bản dịch…",
    "Share with other VBLive users?":
      "Chia sẻ với người dùng VBLive khác?",
    "Would you like to share the": "Bạn có muốn chia sẻ",
    "language package with all VBLive users? Anyone can then copy and build upon it.":
      "gói ngôn ngữ với tất cả người dùng VBLive không? Khi đó bất kỳ ai cũng có thể sao chép và phát triển thêm.",
    "No, just save for me": "Không, chỉ lưu cho tôi",
    "Yes, share it!": "Có, chia sẻ ngay!",
    "Language package saved and shared with all VBLive users!":
      "Đã lưu gói ngôn ngữ và chia sẻ với tất cả người dùng VBLive!",
    "Language package saved!": "Đã lưu gói ngôn ngữ!",
    "Clear all translations? This cannot be undone.":
      "Xóa tất cả bản dịch? Không thể hoàn tác thao tác này.",

    // Additional UI phrases
    Teams: "Đội",
    "Shared By": "Người chia sẻ",
    "View Activity": "Hoạt động xem",
    Viewer: "Người xem",
    "Date / Time": "Ngày / Giờ",
    "No views recorded yet.": "Chưa có lượt xem nào được ghi nhận.",
    "Date / Scores": "Ngày / Tỷ số",
    "Stats API source": "Nguồn API thống kê",
    "load one or more AVC VIS files": "tải một hoặc nhiều tệp AVC VIS",
    "Points to close a set": "Điểm để kết thúc hiệp",
    "Require 2-point advantage": "Yêu cầu cách biệt 2 điểm",
    Roster: "Danh sách cầu thủ",
    Clone: "Nhân bản",
    "Edit team": "Chỉnh sửa đội",
    "New player": "Cầu thủ mới",
    "Search player...": "Tìm kiếm cầu thủ…",
    "Match Centre": "Trung tâm trận đấu",
    "Points Won": "Điểm thắng",
    "Opp. Error": "Lỗi đối phương",
    Total: "Tổng",
    "Match Skills": "Kỹ năng trận đấu",
    Dig: "Phòng thủ",
    "Best Scorers": "Cầu thủ ghi điểm xuất sắc nhất",
    "Power Play": "Power Play",
    Match: "Trận đấu",
    "Player scouting": "Trinh sát cầu thủ",
    "Team deep analysis": "Phân tích chuyên sâu đội",
    "All Players": "Tất cả cầu thủ",
    "All Sets": "Tất cả các hiệp",
    "All Rotations": "Tất cả đội hình",
    "All Combos": "Tất cả tổ hợp tấn công",
    "All Calls": "Tất cả lệnh gọi",
    "All Events": "Tất cả sự kiện",
    "Select all items": "Chọn tất cả mục",
    "Unselect all items": "Bỏ chọn tất cả mục",
    "Create play list": "Tạo danh sách phát",
    "Point by Point": "Diễn biến từng điểm",
    // i18n backfill pass — previously English-fallback-only keys
    "Access denied": "Truy cập bị từ chối",
    "Aces": "Điểm trực tiếp",
    "ACES": "ĐIỂM TRỰC TIẾP",
    "Add report": "Thêm báo cáo",
    "Admin": "Quản trị",
    "Admins": "Quản trị viên",
    "Advanced": "Nâng cao",
    "All set!": "Đã sẵn sàng!",
    "Analyse team with AI": "Phân tích đội bằng AI",
    "Are you sure you want to delete the selected play list?": "Bạn có chắc muốn xóa danh sách phát đã chọn không?",
    "Assign": "Gán",
    "Assign playlist": "Gán danh sách phát",
    "Assigned": "Đã gán",
    "At least one admin is required.": "Cần ít nhất một quản trị viên.",
    "Attack Combo / Setter Call": "Tổ hợp tấn công / Lệnh gọi chuyền 2",
    "Attack diagram": "Sơ đồ tấn công",
    "Attacks": "Tấn công",
    "ATTACKS": "TẤN CÔNG",
    "Auto-detected a custom set-closing score for": "Đã tự động phát hiện điểm kết thúc set tùy chỉnh cho",
    "BLOCK": "CHẮN BÓNG",
    "BLOCKS": "CHẮN BÓNG",
    "Beta testers": "Người thử nghiệm beta",
    "Clear filter": "Xóa bộ lọc",
    "Close Set": "Kết thúc set",
    "Close menu": "Đóng menu",
    "Code": "Mã",
    "Coding defaults & shortcuts": "Mặc định và phím tắt mã hóa",
    "Continue": "Tiếp tục",
    "Could not load match data. The match may no longer be available.": "Không thể tải dữ liệu trận đấu. Trận đấu có thể không còn khả dụng.",
    "Could not load stats for these matches.": "Không thể tải thống kê cho các trận đấu này.",
    "Could not load this match.": "Không thể tải trận đấu này.",
    "Could not load this playlist.": "Không thể tải danh sách phát này.",
    "Could not load this report.": "Không thể tải báo cáo này.",
    "Could not save the report.": "Không thể lưu báo cáo.",
    "Could not share the report. Please try again.": "Không thể chia sẻ báo cáo. Vui lòng thử lại.",
    "Court horizontal (net vertical)": "Sân ngang (lưới dọc)",
    "Court vertical (net horizontal)": "Sân dọc (lưới ngang)",
    "Create": "Tạo",
    "Create a new team from this roster": "Tạo đội mới từ danh sách này",
    "DIFFERENCE": "CHÊNH LỆCH",
    "DV Code": "Mã DV",
    "Delete this report?": "Xóa báo cáo này?",
    "Detected a custom set-closing score of": "Đã phát hiện điểm kết thúc set tùy chỉnh là",
    "Double Sub": "Thay hai người",
    "Double Sub: now pick a bench player for the flashing OPPOSITE slot…": "Thay hai người: bây giờ chọn một cầu thủ dự bị cho vị trí chủ công đối diện đang nhấp nháy…",
    "Double Sub: pick a bench player for the flashing SETTER slot…": "Thay hai người: chọn một cầu thủ dự bị cho vị trí chuyền 2 đang nhấp nháy…",
    "Double Substitution — swap the Setter and Opposite for two incoming players": "Thay hai người — đổi chuyền 2 và chủ công đối diện cùng lúc bằng hai cầu thủ vào sân",
    "Drag to draw the ball path; pause briefly to mark a touch (reception/dig/block), release to finish.": "Kéo để vẽ đường đi của bóng; dừng ngắn để đánh dấu một lần chạm (đỡ bước 1/cứu bóng/chắn bóng), thả ra để hoàn tất.",
    "Draw": "Vẽ",
    "Draw & Code": "Vẽ & Mã hóa",
    "Draw & Code — draw ball paths on the court to code the rally": "Vẽ & Mã hóa — vẽ đường đi của bóng trên sân để mã hóa pha bóng",
    "Edit Lineup": "Chỉnh sửa đội hình",
    "Edit Lineup — freely rearrange the starting lineup before the set begins": "Chỉnh sửa đội hình — tự do sắp xếp lại đội hình xuất phát trước khi set bắt đầu",
    "Emails in this list can manage beta testers and other admins.": "Email trong danh sách này có thể quản lý người thử nghiệm beta và các quản trị viên khác.",
    "Emails in this list get early access to AI Reports and other gated features. Changes take effect immediately.": "Email trong danh sách này được truy cập sớm vào Báo cáo AI và các tính năng giới hạn khác. Thay đổi có hiệu lực ngay lập tức.",
    "Enter a valid email address.": "Nhập địa chỉ email hợp lệ.",
    "Exit fullscreen": "Thoát toàn màn hình",
    "Extended": "Mở rộng",
    "Failed to generate PDF.": "Tạo PDF không thành công.",
    "Failed to save changes.": "Lưu thay đổi không thành công.",
    "Filtered by": "Lọc theo",
    "First name": "Tên",
    "For stronger security later, consider moving admin checks to a Firebase Auth custom claim instead of this email list.": "Để bảo mật mạnh hơn sau này, hãy cân nhắc chuyển việc kiểm tra quản trị viên sang custom claim của Firebase Auth thay vì danh sách email này.",
    "Fullscreen": "Toàn màn hình",
    "Generate an AI report from these matches": "Tạo báo cáo AI từ các trận đấu này",
    "How often (in seconds) the events list is updated during a live session.": "Tần suất (tính bằng giây) danh sách sự kiện được cập nhật trong phiên trực tiếp.",
    "Last name": "Họ",
    "Leave blank to use standard rules (25 points, 15 for a 5th/deciding set)": "Để trống để sử dụng luật tiêu chuẩn (25 điểm, 15 điểm cho set thứ 5/quyết định)",
    "Live session refresh interval": "Khoảng thời gian làm mới phiên trực tiếp",
    "Load VIS file(s)...": "Tải tệp VIS…",
    "Load this team's last-saved starting lineup (positions, setter and liberos)": "Tải đội hình xuất phát đã lưu gần nhất của đội này (vị trí, chuyền 2 và libero)",
    "Loading…": "Đang tải…",
    "Manually end the current set at its current score, regardless of the configured winning score / 2-point advantage rule": "Kết thúc thủ công set hiện tại với tỷ số hiện tại, bất kể quy tắc điểm thắng / cách biệt 2 điểm đã cấu hình",
    "Migration finished with some issues": "Di chuyển dữ liệu hoàn tất với một số vấn đề",
    "Most of your data was migrated, but some items could not be moved. You can retry now or continue and try again later.": "Phần lớn dữ liệu của bạn đã được di chuyển, nhưng một số mục không thể di chuyển được. Bạn có thể thử lại ngay hoặc tiếp tục và thử lại sau.",
    "Mute": "Tắt tiếng",
    "New team": "Đội mới",
    "Next": "Tiếp theo",
    "Nickname": "Biệt danh",
    "No Power Play": "Không có Power Play",
    "No admins yet.": "Chưa có quản trị viên nào.",
    "No beta testers yet.": "Chưa có người thử nghiệm beta nào.",
    "No coded events to analyse.": "Không có sự kiện đã mã hóa để phân tích.",
    "No coded events to export.": "Không có sự kiện đã mã hóa để xuất.",
    "No matches.": "Không có trận đấu nào.",
    "No players added yet.": "Chưa thêm cầu thủ nào.",
    "No players recorded.": "Không có cầu thủ nào được ghi nhận.",
    "No players yet.": "Chưa có cầu thủ nào.",
    "No playlists assigned.": "Chưa gán danh sách phát nào.",
    "No playlists found.": "Không tìm thấy danh sách phát nào.",
    "No reports saved.": "Chưa lưu báo cáo nào.",
    "No roster players available.": "Không có cầu thủ nào trong danh sách.",
    "No substitutions": "Không có thay người",
    "No teams found.": "Không tìm thấy đội nào.",
    "No teams yet.": "Chưa có đội nào.",
    "No teams.": "Không có đội nào.",
    "Nominate Setter": "Chỉ định chuyền 2",
    "Notify these users by email (optional)": "Thông báo cho những người dùng này qua email (tùy chọn)",
    "OPP REC": "ĐỠ BƯỚC 1 ĐỐI PHƯƠNG",
    "OPP. ERRORS": "LỖI ĐỐI PHƯƠNG",
    "Open / Download": "Mở / Tải xuống",
    "Open local file": "Mở tệp cục bộ",
    "Open menu": "Mở menu",
    "Opposition Passing — use the opposition's reception formation on the diagram": "Đỡ bước 1 của đối phương — sử dụng đội hình đỡ bước 1 của đối phương trên sơ đồ",
    "Paste the report (Markdown supported)…": "Dán báo cáo (hỗ trợ Markdown)…",
    "Pause": "Tạm dừng",
    "Play": "Phát",
    "Playing squad": "Danh sách thi đấu",
    "Playing squad is full. Remove a player from the squad before adding another.": "Danh sách thi đấu đã đầy. Hãy xóa một cầu thủ khỏi danh sách trước khi thêm cầu thủ khác.",
    "Please sign in to save reports.": "Vui lòng đăng nhập để lưu báo cáo.",
    "Power Play Only": "Chỉ Power Play",
    "Record a team error (no player) — edit later to add the player and event type": "Ghi nhận lỗi của đội (không có cầu thủ) — chỉnh sửa sau để thêm cầu thủ và loại sự kiện",
    "Record at least one event before making substitutions": "Ghi nhận ít nhất một sự kiện trước khi thay người",
    "Redo": "Làm lại",
    "Remove": "Xóa",
    "Remove share": "Xóa chia sẻ",
    "Remove yourself as admin? You will immediately lose access to this page.": "Tự xóa mình khỏi vai trò quản trị viên? Bạn sẽ mất quyền truy cập trang này ngay lập tức.",
    "Report saved and shared.": "Đã lưu và chia sẻ báo cáo.",
    "Report saved to the player's profile.": "Đã lưu báo cáo vào hồ sơ cầu thủ.",
    "Report title": "Tiêu đề báo cáo",
    "Reports": "Báo cáo",
    "Result": "Kết quả",
    "Retry": "Thử lại",
    "Return to live coding": "Quay lại mã hóa trực tiếp",
    "Rotation is not permitted while making substitutions": "Không được phép xoay vòng khi đang thay người",
    "SERVE": "PHÁT BÓNG",
    "SETS": "SET",
    "Save & Share": "Lưu & Chia sẻ",
    "Save this report so you can share it with other users": "Lưu báo cáo này để bạn có thể chia sẻ với người dùng khác",
    "Save this report to the player's profile": "Lưu báo cáo này vào hồ sơ cầu thủ",
    "Save to player": "Lưu cho cầu thủ",
    "Saved lineup": "Đội hình đã lưu",
    "Scroll, drag or swipe to scrub": "Cuộn, kéo hoặc vuốt để tua",
    "Search existing players...": "Tìm cầu thủ hiện có…",
    "Search playlists…": "Tìm danh sách phát…",
    "Select a match video to view while entering lineups.": "Chọn video trận đấu để xem trong khi nhập đội hình.",
    "Select a player.": "Chọn một cầu thủ.",
    "Select a team.": "Chọn một đội.",
    "Setting up your account": "Đang thiết lập tài khoản của bạn",
    "Shaded rows are played under a Power Play — HP/AP marks the calling team, bold gold scores are bonus (double) points.": "Các hàng được tô bóng là các pha bóng diễn ra trong Power Play — HP/AP đánh dấu đội gọi, điểm số vàng đậm là điểm thưởng (gấp đôi).",
    "Shared by": "Chia sẻ bởi",
    "Shared with me": "Được chia sẻ với tôi",
    "Show help": "Hiện trợ giúp",
    "Show public playlists": "Hiện danh sách phát công khai",
    "Standard": "Tiêu chuẩn",
    "Substitutions made — tap to view": "Đã thực hiện thay người — chạm để xem",
    "Tap the blocking player.": "Chạm vào cầu thủ chắn bóng.",
    "Team colour": "Màu đội",
    "Team name": "Tên đội",
    "That email is already in the list.": "Email đó đã có trong danh sách.",
    "The report is too large to save. Try a single match or fewer sets.": "Báo cáo quá lớn để lưu. Hãy thử với một trận đấu hoặc ít set hơn.",
    "This match has no Power Play calls.": "Trận đấu này không có lệnh gọi Power Play nào.",
    "This player has no name to save the report against.": "Cầu thủ này không có tên để lưu báo cáo.",
    "This team has no designated setter. Pick the player who is setting.": "Đội này chưa có chuyền 2 được chỉ định. Chọn cầu thủ đang chuyền 2.",
    "Toggle video player": "Bật/tắt trình phát video",
    "Unmute": "Bật tiếng",
    "Untitled playlist": "Danh sách phát chưa đặt tên",
    "Video URL…": "URL video…",
    "We're moving your data to a faster, more reliable home. This happens only once.": "Chúng tôi đang chuyển dữ liệu của bạn đến nơi nhanh hơn, đáng tin cậy hơn. Việc này chỉ xảy ra một lần.",
    "You don't have permission to view this page.": "Bạn không có quyền xem trang này.",
    "Your data has been migrated successfully.": "Dữ liệu của bạn đã được di chuyển thành công.",
    "Your teams and players will appear here after your data is migrated.": "Các đội và cầu thủ của bạn sẽ xuất hiện ở đây sau khi dữ liệu được di chuyển.",
    "bonus point": "điểm thưởng",
    "bonus points": "điểm thưởng",
    "event": "sự kiện",
    "events": "sự kiện",
    "file(s).": "tệp.",
    "is not on this team's roster.": "không có trong danh sách của đội này.",
    "loaded": "đã tải",
    "matches": "trận đấu",
    "more": "nữa",
    "players": "cầu thủ",
    "points (no value was entered) — applied automatically.": "điểm (không nhập giá trị) — đã áp dụng tự động.",
    "roster": "danh sách",
    "seconds": "giây",
    "selected": "đã chọn",
    "session(s)?": "phiên?",
    "showing": "hiển thị",
    "tournaments": "giải đấu",
    "…and": "…và",
    "● marks which team served the point.": "● đánh dấu đội nào đã phát bóng ở điểm đó.",
    // i18n backfill pass (Catapult + misc)
    "Centimetres (cm)": "Xentimét (cm)",
    "Inches (in)": "Inch (in)",
    "Jump height units": "Đơn vị chiều cao bật nhảy",
    "Choose how Catapult jump heights are displayed and filtered.": "Chọn cách hiển thị và lọc chiều cao bật nhảy Catapult.",
    "Select Catapult Focus file...": "Chọn tệp Catapult Focus…",
    "Select a Catapult Focus CSV export to view jump data for this match.": "Chọn một tệp xuất CSV Catapult Focus để xem dữ liệu bật nhảy của trận đấu này.",
    "Loaded from saved match data": "Đã tải từ dữ liệu trận đấu đã lưu",
    "No jump data found in this file.": "Không tìm thấy dữ liệu bật nhảy trong tệp này.",
    "Could not read this file. Please check it is a Catapult Focus CSV export.": "Không thể đọc tệp này. Vui lòng kiểm tra xem đây có phải là tệp xuất CSV Catapult Focus hay không.",
    "Could not save jump data with this match.": "Không thể lưu dữ liệu bật nhảy cùng với trận đấu này.",
    "Jump data saved with this match.": "Đã lưu dữ liệu bật nhảy cùng với trận đấu này.",
    "Show players with no match stats": "Hiển thị cầu thủ không có số liệu thống kê trận đấu",
    "hidden": "đã ẩn",
    "jumps": "lần bật nhảy",
    "periods": "giai đoạn",
    "Sync offset": "Độ lệch đồng bộ",
    "matched": "khớp",
    "match events now show jump height/duration in Video Analysis": "sự kiện trận đấu giờ đây hiển thị chiều cao/thời gian bật nhảy trong Phân tích Video",
    "Team": "Đội",
    "Team A": "Đội A",
    "Team B": "Đội B",
    "Which team is this Catapult file for?": "Tệp Catapult này dành cho đội nào?",
    "We couldn't confidently match the players in this Focus file to one team. Please select which team it belongs to.": "Chúng tôi không thể xác định chắc chắn các cầu thủ trong tệp Focus này thuộc đội nào. Vui lòng chọn đội mà tệp này thuộc về.",
    "This match has no video attached yet.": "Trận đấu này chưa có video đính kèm.",
    "No synced jump events found for this player.": "Không tìm thấy sự kiện bật nhảy đã đồng bộ cho cầu thủ này.",
    "No match loaded.": "Chưa tải trận đấu nào.",
    "avg": "TB",
    "max": "tối đa",
    "Sync": "Đồng bộ",
    "Jump Report": "Báo cáo bật nhảy",
    "Watch Video": "Xem Video",
    "Save PDF": "Lưu PDF",
    "Generated by VBLive": "Được tạo bởi VBLive",
    "Deep analysis of {team} from ball-by-ball event data, covering serving, reception, setting, attack, blocking, defence, rotations and opponent tendencies. Best used with the Anthropic (Claude) provider.": "Phân tích chuyên sâu về {team} từ dữ liệu sự kiện từng pha bóng, bao gồm giao bóng, đỡ bước một, chuyền hai, tấn công, chắn bóng, phòng thủ, xoay vòng và xu hướng đối thủ. Tốt nhất khi sử dụng với nhà cung cấp Anthropic (Claude).",
    "teams": "đội",
  },

  th: {
    // Information page
    Information: "ข้อมูล",
    "Release notes": "บันทึกการเผยแพร่",
    "Contact us": "ติดต่อเรา",
    "Questions, feedback or suggestions are most welcome.": "ยินดีรับคำถาม ข้อเสนอแนะ หรือคำติชม",
    // DV match coding
    Lineups: "ผู้เล่นตัวจริง",
    Point: "คะแนน",
    Video: "วิดีโอ",
    Defaults: "ค่าเริ่มต้น",
    "[team][NN][skill][type?][eval][zoneFrom?[szFrom?][zoneTo?[szTo?]]]": "[ทีม][NN][ทักษะ][ประเภท?][ประเมิน][โซนจาก?[ซจ?][โซนถึง?[ซถ?]]]",
    Vertical: "แนวตั้ง",
    Horizontal: "แนวนอน",
    "Swap sides": "สลับฝั่ง",
    Subs: "เปลี่ยนตัว",
    Substitutions: "เปลี่ยนตัว",
    Court: "สนาม",
    Home: "เจ้าบ้าน",
    Away: "ทีมเยือน",
    Games: "เซต",
    Enter: "ป้อน",
    Undo: "เลิกทำ",
    "Match saved": "บันทึกการแข่งขันแล้ว",
    "Please sign in to save this match.": "โปรดเข้าสู่ระบบเพื่อบันทึกการแข่งขันนี้",
    "Nothing to save yet.": "ยังไม่มีอะไรให้บันทึก",
    "Could not save the match. Please try again.": "ไม่สามารถบันทึกการแข่งขันได้ โปรดลองอีกครั้ง",
    Discard: "ละทิ้งการเปลี่ยนแปลง",
    "Resumed autosaved coding": "กลับมาทำการบันทึกโค้ดที่บันทึกอัตโนมัติต่อ",
    "Changes discarded": "ละทิ้งการเปลี่ยนแปลงแล้ว",
    "Discard all changes and revert to the starting point?":
      "ละทิ้งการเปลี่ยนแปลงทั้งหมดและกลับไปยังจุดเริ่มต้นหรือไม่?",
    Clear: "ล้าง",
    "Prev set": "เซตก่อนหน้า",
    "Next set": "เซตถัดไป",
    "Match Setup": "ตั้งค่าการแข่งขัน",
    "First serve": "เสิร์ฟแรก",
    "Start Match": "เริ่มการแข่งขัน",
    "Resume Match": "ดำเนินการแข่งขันต่อ",
    Skip: "ข้าม",
    "Select an event then drag to draw ball path": "เลือกเหตุการณ์แล้วลากเพื่อวาดเส้นทางลูกบอล",
    "Drag on court to set ball path": "ลากบนสนามเพื่อกำหนดเส้นทางลูกบอล",
    "No events yet – enter a DV code above and press Enter.": "ยังไม่มีเหตุการณ์ – ป้อนรหัส DV ด้านบนแล้วกดป้อน",
    "DataVolley code reference": "ข้อมูลอ้างอิงรหัส DataVolley",
    Format: "รูปแบบ",
    Skills: "ทักษะ",
    Evaluation: "การประเมิน",
    "Skill / type omission": "การละเว้นทักษะ / ประเภท",
    "Skill and/or type may be omitted; defaults fill in the rest.": "สามารถละเว้นทักษะและ/หรือประเภทได้ ส่วนที่เหลือจะเติมด้วยค่าเริ่มต้น",
    "Dot-coding (S→R and A→B pairs)": "การเข้ารหัสด้วยจุด (คู่ S→R และ A→B)",
    "Chaining & aliases": "การเชื่อมต่อและนามแฝง",
    "team:": "ทีม:",
    "omit → defaults to A": "ละเว้น → ค่าเริ่มต้นคือ A",
    "NN: 1 or 2 digit jersey, e.g. 1, 01, 12": "NN: หมายเลขเสื้อ 1 หรือ 2 หลัก เช่น 1, 01, 12",
    "sub-zone: a b c d appended to zone digit, e.g. 5b or 5b1a": "โซนย่อย: a b c d ต่อท้ายเลขโซน เช่น 5b หรือ 5b1a",
    "S Serve · R Reception · E Set · A Attack · B Block · D Dig · F Freeball": "S เสิร์ฟ · R รับลูก · E เซ็ต · A ตบ · B บล็อก · D ขุด · F ลูกฟรี",
    "# kill/ace · + positive · ! ok · - negative · / overpass · = error": "# ทำแต้ม/เอซ · + ดี · ! พอใช้ · - แย่ · / ข้ามเน็ต · = เสีย",
    "E.g.": "เช่น",
    "serve with skill=A (default), type=H, eval=+, zone=5.": "เสิร์ฟด้วย skill=A (ค่าเริ่มต้น), type=H, eval=+, zone=5",
    "type-only (no skill marker), defaults skill from context.": "เฉพาะประเภท (ไม่มีเครื่องหมายทักษะ) ทักษะใช้ค่าเริ่มต้นจากบริบท",
    "serve coded, reception auto-linked.": "เข้ารหัสเสิร์ฟแล้ว เชื่อมโยงการรับลูกอัตโนมัติ",
    "attack coded, block auto-linked.": "เข้ารหัสการตบแล้ว เชื่อมโยงการบล็อกอัตโนมัติ",
    "Dot connects the reception eval to the corresponding serve eval via the serve→receive mapping.": "จุดเชื่อมการประเมินการรับลูกเข้ากับการประเมินการเสิร์ฟที่สอดคล้องผ่านการแมป เสิร์ฟ→รับลูก",
    "two events in one string.": "สองเหตุการณ์ในสตริงเดียว",
    "manual point A/B.": "ทำแต้มด้วยตนเอง A/B",
    "serve ace/error.": "เอซ/เสียเสิร์ฟ",
    "serve with eval.": "เสิร์ฟพร้อมการประเมิน",
    // Stats, sideouts, filters & video tooltips
    "Block Kills": "แต้มจากการบล็อก",
    "Side Out Percentage": "เปอร์เซ็นต์ไซด์เอาท์",
    "All First Ball": "ลูกแรกทั้งหมด",
    "First Ball Kills": "แต้มจากลูกแรก",
    "Good Serve": "เสิร์ฟดี",
    "Break Points": "แต้มเบรก",
    Game: "เซต",
    Row: "ตำแหน่ง",
    "First Ball Sideouts": "ไซด์เอาท์ลูกแรก",
    "All Sideouts": "ไซด์เอาท์ทั้งหมด",
    All: "ทั้งหมด",
    None: "ไม่มี",
    Receiver: "ผู้รับลูก",
    "# Passes": "จำนวนการรับ",
    "Perfect %": "% สมบูรณ์แบบ",
    "Positive %": "% ดี",
    "Sideout %": "% ไซด์เอาท์",
    "FB Sideout %": "% ไซด์เอาท์ลูกแรก",
    "Pass Hits": "การตบหลังรับลูก",
    "Pass Hit Kills": "แต้มหลังรับลูก",
    "Pass Hit Errors": "ข้อผิดพลาดหลังรับลูก",
    "Show Passes": "แสดงการรับลูก",
    "Show Attacks": "แสดงการรุก",
    "Error Pass": "รับลูกเสีย",
    "1 Pass": "รับลูก 1",
    "2 Pass": "รับลูก 2",
    "Perfect Pass": "รับลูกสมบูรณ์แบบ",
    "Successful SO": "ไซด์เอาท์สำเร็จ",
    "First Ball SO": "ไซด์เอาท์ลูกแรก",
    "Unsuccessful SO": "ไซด์เอาท์ไม่สำเร็จ",
    RECEIVES: "การรับลูก",
    Sets: "เซต",
    Rotations: "การหมุน",
    "Attack Combos": "คอมโบการรุก",
    Stages: "ช่วง",
    Display: "การแสดงผล",
    Setters: "ตัวเซ็ต",
    Attackers: "ตัวรุก",
    Results: "ผลลัพธ์",
    Source: "แหล่งที่มา",
    Cones: "โซน",
    "Select None": "ไม่เลือกทั้งหมด",
    "All Serves": "เสิร์ฟทั้งหมด",
    "All Returns": "การรับทั้งหมด",
    Filters: "ตัวกรอง",
    "Reset all filters": "รีเซ็ตตัวกรองทั้งหมด",
    "Show video clips of filtered attacks": "แสดงคลิปวิดีโอของการรุกที่กรองไว้",
    "Spikes for the selected player": "การตบของผู้เล่นที่เลือก",
    "Click on the zones to view video of attacks": "คลิกที่โซนเพื่อดูวิดีโอการรุก",
    "Expand All": "ขยายทั้งหมด",
    "Collapse All": "ยุบทั้งหมด",
    "Import Radar File": "นำเข้าไฟล์ Radar",
    "Select local video file": "เลือกไฟล์วิดีโอในเครื่อง",
    "Save to database": "บันทึกลงฐานข้อมูล",
    "Load and play online video": "โหลดและเล่นวิดีโอออนไลน์",
    "Synchronise video with events": "ซิงค์วิดีโอกับเหตุการณ์",
    "Rewind 10 seconds": "ย้อนกลับ 10 วินาที",
    "Rewind 1 second": "ย้อนกลับ 1 วินาที",
    "Forward 1 second": "ไปข้างหน้า 1 วินาที",
    "Forward 10 seconds": "ไปข้างหน้า 10 วินาที",
    "Set timing for events": "ตั้งเวลาสำหรับเหตุการณ์",

    // Navigation
    Matches: "การแข่งขัน",
    Live: "ถ่ายทอดสด",
    Import: "นำเข้า",
    Playlists: "เพลย์ลิสต์",
    "New Match": "การแข่งขันใหม่",
    // New Match / Match Coding gate
    "Match Coding": "การบันทึกการแข่งขัน",
    "Match Coding — Coming Soon": "การบันทึกการแข่งขัน — เร็ว ๆ นี้",
    "Live match coding is on the way. Here's a preview of what's coming.":
      "การบันทึกการแข่งขันแบบสดกำลังจะมาถึง นี่คือตัวอย่างของสิ่งที่กำลังจะมา",
    Settings: "การตั้งค่า",
    Language: "ภาษา",
    "Your profile": "โปรไฟล์ของคุณ",
    "Sign out": "ออกจากระบบ",
    "Translation Editor": "ตัวแก้ไขคำแปล",

    // Common actions
    Save: "บันทึก",
    Cancel: "ยกเลิก",
    Delete: "ลบ",
    Share: "แชร์",
    Edit: "แก้ไข",
    New: "ใหม่",
    Refresh: "รีเฟรช",
    Search: "ค้นหา",
    Reset: "รีเซ็ต",
    Close: "ปิด",
    "Sub for": "เปลี่ยนตัว",
    "No players available": "ไม่มีผู้เล่นที่พร้อม",
    "Show full roster": "แสดงรายชื่อทั้งหมด",
    "Available only": "เฉพาะที่พร้อม",
    "Maximum substitutions reached": "ถึงจำนวนเปลี่ยนตัวสูงสุดแล้ว",
    Back: "ย้อนกลับ",
    "Download PDF": "ดาวน์โหลด PDF",
    "Loading...": "กำลังโหลด…",
    "Select All": "เลือกทั้งหมด",
    "Unselect All": "ยกเลิกการเลือกทั้งหมด",
    Yes: "ใช่",
    No: "ไม่",
    Add: "เพิ่ม",
    Load: "โหลด",
    Confirm: "ยืนยัน",
    Open: "เปิด",
    show: "แสดง",

    // Auth
    "Sign In": "เข้าสู่ระบบ",
    "Sign Up": "สมัครสมาชิก",
    "Sign Out": "ออกจากระบบ",
    Email: "อีเมล",
    Password: "รหัสผ่าน",
    "Confirm Password": "ยืนยันรหัสผ่าน",
    "Forgot password?": "ลืมรหัสผ่าน?",
    "Forgot Password": "ลืมรหัสผ่าน",
    "Don't have an account?": "ยังไม่มีบัญชี?",
    "Sign up for free!": "สมัครฟรี!",
    "All ready signed up?": "สมัครแล้ว?",
    "Error Signing In": "เข้าสู่ระบบผิดพลาด",
    "Signed up successfully": "สมัครสมาชิกสำเร็จ",
    "Error signing up": "สมัครสมาชิกผิดพลาด",
    "Enter your email": "กรอกอีเมลของคุณ",
    "Send Reset Email": "ส่งอีเมลรีเซ็ต",
    "Email was sent to": "ส่งอีเมลไปยัง",
    "Could not send reset email":
      "ไม่สามารถส่งอีเมลรีเซ็ตได้",

    // Matches list
    "Team 1": "ทีม 1",
    "Team 2": "ทีม 2",
    Tournament: "ทัวร์นาเมนต์",
    Scores: "คะแนน",
    Date: "วันที่",
    Actions: "การดำเนินการ",
    Owner: "เจ้าของ",
    Description: "คำอธิบาย",
    Comments: "ความคิดเห็น",
    Tags: "แท็ก",
    "Add player": "เพิ่มผู้เล่น",
    "Add team": "เพิ่มทีม",
    "Edit playlist": "แก้ไขเพลย์ลิสต์",
    "Merge Teams": "รวมทีม",
    "Roster differences detected between teams:": "ตรวจพบความแตกต่างในรายชื่อระหว่างทีม:",
    "Review the roster in the next step before merging.": "ตรวจสอบรายชื่อในขั้นตอนถัดไปก่อนรวม",
    "At least 2 team records must exist in your Teams list for the selected matches.": "ต้องมีบันทึกทีมอย่างน้อย 2 รายการในรายการทีมของคุณสำหรับแมตช์ที่เลือก",
    "Merge...": "รวม...",
    "Select 2 or more team records to merge into one.": "เลือกบันทึกทีม 2 รายการขึ้นไปเพื่อรวมเป็นหนึ่งเดียว",
    "Filter teams...": "กรองทีม...",
    "teams selected": "ทีมที่เลือก",
    "Select Teams": "เลือกทีม",
    "Reconcile Roster": "กระทบยอดรายชื่อ",
    "Confirm Merge": "ยืนยันการรวม",
    "Merged team name": "ชื่อทีมที่รวมแล้ว",
    "shirt number(s) have conflicting names — review below.": "หมายเลขเสื้อมีชื่อขัดแย้ง — ตรวจสอบด้านล่าง",
    "Shirt": "หมายเลขเสื้อ",
    "players included": "ผู้เล่นที่รวม",
    "No players to reconcile.": "ไม่มีผู้เล่นที่ต้องกระทบยอด",
    "Use this name": "ใช้ชื่อนี้",
    "Create merged team": "สร้างทีมที่รวมแล้ว",
    "with": "พร้อม",
    "duplicate team records": "รายการทีมที่ซ้ำกัน",
    "Consolidate duplicate player records and reassign playlist links": "รวมบันทึกผู้เล่นที่ซ้ำกันและกำหนดลิงก์เพลย์ลิสต์ใหม่",
    "This cannot be undone.": "การดำเนินการนี้ไม่สามารถยกเลิกได้",
    "Teams being merged:": "ทีมที่กำลังรวม:",
    "Step": "ขั้นตอน",
    "of": "จาก",
    "Merging...": "กำลังรวม...",
    "Players & Teams": "ผู้เล่นและทีม",
    "No players or teams assigned.": "ไม่มีผู้เล่นหรือทีมที่กำหนด",
    "Search players...": "ค้นหาผู้เล่น...",
    "Search teams...": "ค้นหาทีม...",
    "Saving...": "กำลังบันทึก...",
    "My Matches": "การแข่งขันของฉัน",
    "Shared Matches": "การแข่งขันที่แชร์",
    "Search team...": "ค้นหาทีม…",
    "All Teams": "ทุกทีม",
    "Multi Match Reports": "รายงานหลายการแข่งขัน",
    "Delete match": "ลบการแข่งขัน",
    "Remove from my shared matches": "นำออกจากการแข่งขันที่แชร์ของฉัน",
    "Share match": "แชร์การแข่งขัน",
    "Edit match": "แก้ไขการแข่งขัน",
    "Open video analysis": "เปิดการวิเคราะห์วิดีโอ",
    "Delete Match": "ลบการแข่งขัน",
    "Are you sure you want to delete the selected match?":
      "คุณแน่ใจหรือไม่ว่าต้องการลบการแข่งขันที่เลือก?",
    "No matches selected.": "ยังไม่ได้เลือกการแข่งขัน",

    // New Match
    "← Back": "← ย้อนกลับ",
    "Team A (Home)": "ทีม A (เจ้าบ้าน)",
    "Team B (Away)": "ทีม B (ทีมเยือน)",
    "Select team…": "เลือกทีม…",
    "Tournament (optional)": "ทัวร์นาเมนต์ (ไม่บังคับ)",
    "Tournament / competition name": "ชื่อทัวร์นาเมนต์ / การแข่งขัน",
    "Match date": "วันที่แข่งขัน",
    "Start Coding →": "เริ่มบันทึกข้อมูล →",

    // Reports
    Summary: "สรุป",
    "Box Score": "ตารางสถิติ",
    "Side-outs Report": "รายงานไซด์เอาต์",
    "Serve Receives": "การรับลูกเสิร์ฟ",
    "Attack Zones": "โซนการรุก",
    "Hitting Charts": "แผนภูมิการรุก",
    "Video Analysis": "การวิเคราะห์วิดีโอ",
    Dashboard: "แดชบอร์ด",
    Charts: "แผนภูมิ",
    "AI Report": "รายงาน AI",
    // AI Reports page
    "AI Reports": "รายงาน AI",
    "AI Reports — Coming Soon": "รายงาน AI — เร็ว ๆ นี้",
    "AI-powered match reports and deep analysis are on the way. Here are some sample reports to show what's coming.":
      "รายงานการแข่งขันและการวิเคราะห์เชิงลึกด้วย AI กำลังจะมาถึง นี่คือรายงานตัวอย่างเพื่อให้ดูก่อน",
    "Preview not available.": "ไม่สามารถแสดงตัวอย่างได้",
    "Open sample": "เปิดตัวอย่าง",
    "No matches were loaded. Select one or more matches and try again.":
      "ยังไม่ได้โหลดการแข่งขัน เลือกการแข่งขันหนึ่งรายการขึ้นไปแล้วลองอีกครั้ง",
    "Attack Analysis (English)": "การวิเคราะห์การรุก (อังกฤษ)",
    "Blocking Analysis (Vietnamese)": "การวิเคราะห์การบล็อก (เวียดนาม)",
    "Opponent Tendencies (Japanese)": "แนวโน้มของคู่แข่ง (ญี่ปุ่น)",
    // Deep AI analysis
    "Deep AI Analysis": "การวิเคราะห์เชิงลึกด้วย AI",
    "AI Deep Analysis": "การวิเคราะห์เชิงลึกโดย AI",
    "Load at least one match with coded events to generate the analysis.": "โหลดอย่างน้อยหนึ่งแมตช์ที่มีเหตุการณ์ที่เข้ารหัสแล้วเพื่อสร้างการวิเคราะห์",
    "Run the deep analysis one section at a time to keep each request small. Edit or add questions below, then generate. Best used with the Anthropic (Claude) provider.":
      "เรียกใช้การวิเคราะห์เชิงลึกทีละส่วนเพื่อให้แต่ละคำขอมีขนาดเล็ก แก้ไขหรือเพิ่มคำถามด้านล่าง แล้วจึงสร้าง แนะนำให้ใช้กับผู้ให้บริการ Anthropic (Claude)",
    "Download CSV": "ดาวน์โหลด CSV",
    "Analysis section": "ส่วนการวิเคราะห์",
    analysing: "กำลังวิเคราะห์",
    "All sections (uses the most credits)": "ทุกส่วน (ใช้เครดิตมากที่สุด)",
    "All sections": "ทุกส่วน",
    Questions: "คำถาม",
    "one per line, edit or add your own": "บรรทัดละหนึ่งข้อ แก้ไขหรือเพิ่มของคุณเอง",
    "Reset to defaults": "รีเซ็ตเป็นค่าเริ่มต้น",
    "Add a question…": "เพิ่มคำถาม…",
    "Runs every section using the questions you've set for each. This sends one large request and uses the most credits.":
      "เรียกใช้ทุกส่วนด้วยคำถามที่คุณตั้งไว้สำหรับแต่ละส่วน การทำเช่นนี้จะส่งคำขอขนาดใหญ่หนึ่งครั้งและใช้เครดิตมากที่สุด",
    "Generate full analysis": "สร้างการวิเคราะห์แบบเต็ม",
    "Generate section analysis": "สร้างการวิเคราะห์ส่วน",
    Serving: "การเสิร์ฟ",
    Reception: "การรับลูก",
    Setting: "การเซ็ต",
    Attack: "การตบ",
    Blocking: "การบล็อก",
    "Defence / Digging": "การรับ / การขุดลูก",
    "Rotation Analysis": "การวิเคราะห์การหมุนตำแหน่ง",
    "Opponent Tendencies (Scouting)": "แนวโน้มของคู่แข่ง (การสอดแนม)",
    "Overall Team Metrics": "ตัวชี้วัดรวมของทีม",
    "Serve efficiency (aces, errors, and pressured receptions forced).": "ประสิทธิภาพการเสิร์ฟ (แต้ม ข้อผิดพลาด และการบังคับให้รับลูกยาก)",
    "Which servers are most effective, and against which reception zones?": "ผู้เสิร์ฟคนใดมีประสิทธิภาพมากที่สุด และต่อโซนรับลูกใด?",
    "Which serve types (jump, float, jump-float) yield the best results?": "การเสิร์ฟประเภทใด (กระโดดตบ ลอย กระโดดลอย) ให้ผลลัพธ์ดีที่สุด?",
    "Reception quality breakdown (perfect, positive, negative, error).": "การแจกแจงคุณภาพการรับลูก (สมบูรณ์ ดี แย่ ผิดพลาด)",
    "Which players struggle most under jump serves vs. float serves?": "ผู้เล่นคนใดมีปัญหามากที่สุดกับลูกเสิร์ฟกระโดดเทียบกับลูกเสิร์ฟลอย?",
    "How does poor reception correlate with side-out efficiency?": "การรับลูกที่ไม่ดีสัมพันธ์กับประสิทธิภาพการไซด์เอาต์อย่างไร?",
    "Set distribution by zone and by player.": "การกระจายการเซ็ตตามโซนและตามผู้เล่น",
    "How does the setter perform under pressure (bad-pass situations)?": "ตัวเซ็ตเล่นได้ดีเพียงใดภายใต้แรงกดดัน (สถานการณ์รับลูกไม่ดี)?",
    "Tempo breakdown (quick, medium, high ball) and which yields the best attack outcome.": "การแจกแจงจังหวะ (เร็ว กลาง ลูกสูง) และจังหวะใดให้ผลการรุกดีที่สุด",
    "Kill percentage and error rate per player, per rotation, and per zone.": "เปอร์เซ็นต์การทำคะแนนและอัตราข้อผิดพลาดต่อผู้เล่น ต่อรอบ และต่อโซน",
    "Which attack zones (pipe, cross, line) have the highest efficiency?": "โซนการรุกใด (ไปป์ ทแยง ลายเส้น) มีประสิทธิภาพสูงสุด?",
    "How attack efficiency changes in transition vs. side-out.": "ประสิทธิภาพการรุกเปลี่ยนอย่างไรในช่วงเปลี่ยนสถานะเทียบกับไซด์เอาต์",
    "Block-per-set rate per player and rotation.": "อัตราการบล็อกต่อเซ็ตต่อผู้เล่นและต่อรอบ",
    "Which opposing attack zones are most successfully blocked?": "โซนการรุกของคู่แข่งโซนใดถูกบล็อกได้สำเร็จมากที่สุด?",
    "How often blocks result in kills vs. touch-outs.": "การบล็อกส่งผลเป็นแต้มบ่อยแค่ไหนเทียบกับการบล็อกออก",
    "Dig success rate per player and per court zone.": "อัตราความสำเร็จของการขุดลูกต่อผู้เล่นและต่อโซนในสนาม",
    "Which attack types (tip, roll, hard swing) are hardest to defend?": "การรุกประเภทใด (หยอด บอลโรล ตบแรง) ป้องกันยากที่สุด?",
    "How defensive efficiency affects transition attack success.": "ประสิทธิภาพการรับส่งผลต่อความสำเร็จของการรุกช่วงเปลี่ยนสถานะอย่างไร",
    "Which rotations have the highest and lowest point differential?": "ตำแหน่งการหมุนใดมีผลต่างแต้มสูงสุดและต่ำสุด?",
    "Side-out and break-point percentages per rotation.": "เปอร์เซ็นต์ไซด์เอาต์และเบรกพอยต์ต่อตำแหน่งการหมุน",
    "Rotation-specific vulnerabilities (e.g. a weak serving or passing rotation).": "จุดอ่อนเฉพาะตำแหน่งการหมุน (เช่น ตำแหน่งที่เสิร์ฟหรือรับลูกได้ไม่ดี)",
    "The opponent's most frequently used attack zones and combinations.": "โซนการรุกและรูปแบบการเล่นที่คู่แข่งใช้บ่อยที่สุด",
    "The opponent's go-to attackers in high-pressure situations.": "ตัวรุกหลักของคู่แข่งในสถานการณ์กดดันสูง",
    "The serve-receive patterns the opponent favours.": "รูปแบบการรับลูกเสิร์ฟที่คู่แข่งถนัด",
    "Overall rally-win probability in different score situations.": "ความน่าจะเป็นของการชนะแรลลีโดยรวมในสถานการณ์คะแนนต่างๆ",
    "How performance changes in sets 1–3 vs. sets 4–5 (fatigue/pressure effects).": "ฟอร์มเปลี่ยนไปอย่างไรในเซ็ต 1–3 เทียบกับเซ็ต 4–5 (ผลจากความล้า/แรงกดดัน)",
    "The correlation between first-ball side-out percentage and match outcome.": "ความสัมพันธ์ระหว่างเปอร์เซ็นต์ไซด์เอาต์ลูกแรกกับผลการแข่งขัน",
    Report: "รายงาน",

    // Import page
    "Load DVW file(s)...": "โหลดไฟล์ DVW…",
    "Load PSVB file(s)...": "โหลดไฟล์ PSVB…",
    "Load VSM file(s)...": "โหลดไฟล์ VSM…",
    "Load playlist file...": "โหลดไฟล์เพลย์ลิสต์…",
    "load one or more DataVolley files":
      "โหลดไฟล์ DataVolley หนึ่งไฟล์ขึ้นไป",
    "load one or more VBStats files":
      "โหลดไฟล์ VBStats หนึ่งไฟล์ขึ้นไป",
    "load one or more VolleyStation VSM files":
      "โหลดไฟล์ VolleyStation VSM หนึ่งไฟล์ขึ้นไป",
    "Convert DVW": "แปลง DVW",
    "VSM": "VSM",
    "Convert DVW to VSM...": "แปลง DVW เป็น VSM…",
    "pick a DataVolley .dvw file to download as .vsm":
      "เลือกไฟล์ DataVolley .dvw เพื่อดาวน์โหลดเป็น .vsm",
    "Convert VSM to DVW...": "แปลง VSM เป็น DVW…",
    "pick a VolleyStation .vsm file to download as .dvw":
      "เลือกไฟล์ VolleyStation .vsm เพื่อดาวน์โหลดเป็น .dvw",
    "Converting…": "กำลังแปลง…",
    "Converted": "แปลงแล้ว",
    "Failed to convert": "แปลงไม่สำเร็จ",
    "Beta testers only — coming soon for everyone else.":
      "สำหรับผู้ทดสอบเบต้าเท่านั้น — เร็ว ๆ นี้สำหรับทุกคน",
    "load a playlist": "โหลดเพลย์ลิสต์",
    "Use Shift-JIS Encoder": "ใช้ตัวเข้ารหัส Shift-JIS",
    "Importing…": "กำลังนำเข้า…",
    "Save to Database?": "บันทึกลงฐานข้อมูล?",
    "Always save imports automatically": "บันทึกการนำเข้าอัตโนมัติเสมอ",
    "View Only": "ดูเท่านั้น",
    "Save to Database": "บันทึกลงฐานข้อมูล",
    "Error saving to database": "เกิดข้อผิดพลาดในการบันทึกลงฐานข้อมูล",
    "Saved to database": "บันทึกลงฐานข้อมูลแล้ว",
    "Saving to database…": "กำลังบันทึกลงฐานข้อมูล…",
    "Upload them to the database now?":
      "อัปโหลดไปยังฐานข้อมูลตอนนี้หรือไม่?",

    // Settings / Options
    Theme: "ธีม",
    Options: "ตัวเลือก",
    "Rotation naming convention": "แบบแผนการตั้งชื่อการหมุน",
    "Choose how rotations are labelled in reports and analysis.":
      "เลือกวิธีกำกับการหมุนในรายงานและการวิเคราะห์",
    "R1–R6 (rotation sequence)": "R1–R6 (ลำดับการหมุน)",
    "S1–S6 (setter court position)": "S1–S6 (ตำแหน่งตัวเซ็ตในสนาม)",
    "R1: setter in position 1, R2: setter in 6, R3: setter in 5, R4: setter in 4, R5: setter in 3, R6: setter in 2.":
      "R1: ตัวเซ็ตอยู่ตำแหน่ง 1, R2: ตัวเซ็ตอยู่ 6, R3: ตัวเซ็ตอยู่ 5, R4: ตัวเซ็ตอยู่ 4, R5: ตัวเซ็ตอยู่ 3, R6: ตัวเซ็ตอยู่ 2",
    "S1: setter in 1, S2: setter in 2, S3: setter in 3, S4: setter in 4, S5: setter in 5, S6: setter in 6.":
      "S1: ตัวเซ็ตอยู่ 1, S2: ตัวเซ็ตอยู่ 2, S3: ตัวเซ็ตอยู่ 3, S4: ตัวเซ็ตอยู่ 4, S5: ตัวเซ็ตอยู่ 5, S6: ตัวเซ็ตอยู่ 6",
    "Labels:": "ป้ายกำกับ:",
    "Current theme:": "ธีมปัจจุบัน:",
    "Choose the language for the app interface.":
      "เลือกภาษาสำหรับอินเทอร์เฟซแอป",
    "Create your own language package or edit a community one, with AI-assisted translation.":
      "สร้างชุดภาษาของคุณเองหรือแก้ไขของชุมชน ด้วยการแปลที่ช่วยเหลือโดย AI",

    // About / Footer
    "Copyright © Perana Sports {year}. All rights reserved.":
      "ลิขสิทธิ์ © Perana Sports {year} สงวนลิขสิทธิ์ทั้งหมด",
    "VBLive is free to use right now. However Perana Sports reserves the right to charge a subscription fee in due course.":
      "VBLive ใช้งานได้ฟรีในขณะนี้ อย่างไรก็ตาม Perana Sports ขอสงวนสิทธิ์ในการเรียกเก็บค่าสมาชิกในอนาคต",
    "Querries / suggestions are most welcomed.":
      "ยินดีรับคำถาม / ข้อเสนอแนะ",

    // Live
    "Please enter server name to search":
      "กรุณากรอกชื่อเซิร์ฟเวอร์เพื่อค้นหา",
    "Broadcast Server": "เซิร์ฟเวอร์ถ่ายทอด",
    "Enter DVMateLive Server Name": "กรอกชื่อเซิร์ฟเวอร์ DVMateLive",
    LIVE: "ถ่ายทอดสด",

    // Playlists list
    "Delete Playlist": "ลบเพลย์ลิสต์",
    "Are you sure you want to delete the selected playlist?":
      "คุณแน่ใจหรือไม่ว่าต้องการลบเพลย์ลิสต์ที่เลือก?",
    "My Playlists": "เพลย์ลิสต์ของฉัน",
    "Shared Playlists": "เพลย์ลิสต์ที่แชร์",
    "Tags:": "แท็ก:",
    "Select...": "เลือก…",
    "All Tags": "แท็กทั้งหมด",

    // Share
    "User already added": "เพิ่มผู้ใช้แล้ว",
    "Please enter a valid email address":
      "กรุณากรอกที่อยู่อีเมลที่ถูกต้อง",
    "Please select at least one user to share with":
      "กรุณาเลือกผู้ใช้อย่างน้อยหนึ่งคนเพื่อแชร์",
    "Not Shared": "ไม่ได้แชร์",
    "Available for everyone": "เปิดให้ทุกคน",
    "Available to selected users":
      "เปิดให้ผู้ใช้ที่เลือก",
    "Enter VBLive user email address":
      "กรอกที่อยู่อีเมลของผู้ใช้ VBLive",
    "Selected users:": "ผู้ใช้ที่เลือก:",
    "Save List": "บันทึกรายการ",
    "Clear List": "ล้างรายการ",
    "Clear Users List": "ล้างรายการผู้ใช้",
    "Do you really want to clear all users from list?":
      "คุณต้องการลบผู้ใช้ทั้งหมดออกจากรายการจริงหรือ?",
    "Do you want to save changes before closing?":
      "คุณต้องการบันทึกการเปลี่ยนแปลงก่อนปิดหรือไม่?",
    "Enter name to save list to:":
      "กรอกชื่อเพื่อบันทึกรายการ:",
    "Enter name to save list to":
      "กรอกชื่อเพื่อบันทึกรายการ",

    // AI panels
    "AI settings": "การตั้งค่า AI",
    "Setup needed": "ต้องตั้งค่า",
    "Generate report": "สร้างรายงาน",
    "Generate AI report": "สร้างรายงาน AI",
    "Generate general report": "สร้างรายงานทั่วไป",
    "Translate to…": "แปลเป็น…",
    "Follow-up questions": "คำถามเพิ่มเติม",
    "Ask a follow-up question…": "ถามคำถามเพิ่มเติม…",
    "Ask a follow-up question": "ถามคำถามเพิ่มเติม",
    "AI provider": "ผู้ให้บริการ AI",
    Model: "โมเดล",
    Hide: "ซ่อน",
    Show: "แสดง",
    "Your key is stored only in this browser and sent directly to the selected provider.":
      "คีย์ของคุณจะถูกเก็บไว้เฉพาะในเบราว์เซอร์นี้และส่งตรงไปยังผู้ให้บริการที่เลือก",
    "Please enter your API key.": "กรุณากรอกคีย์ API ของคุณ",
    "Not enough data to analyse.":
      "ข้อมูลไม่เพียงพอสำหรับการวิเคราะห์",
    "Select a player with recorded spikes first.":
      "กรุณาเลือกผู้เล่นที่มีการบันทึกการตบก่อน",
    Player: "ผู้เล่น",
    "Select a player…": "เลือกผู้เล่น…",
    "No players with recorded spikes in the selected matches.":
      "ไม่มีผู้เล่นที่มีการบันทึกการตบในการแข่งขันที่เลือก",
    "Analyse with AI": "วิเคราะห์ด้วย AI",
    "AI scouting report": "รายงานสกัดข้อมูลด้วย AI",
    "Q:": "ถาม:",
    Ask: "ถาม",
    "e.g. Which rotation should we target on serve?":
      "เช่น เราควรมุ่งเป้าการหมุนใดในการเสิร์ฟ?",
    "Saved questions:": "คำถามที่บันทึกไว้:",
    "Click to ask again": "คลิกเพื่อถามอีกครั้ง",
    "Remove saved question": "ลบคำถามที่บันทึกไว้",

    // AI language options
    Spanish: "สเปน",
    French: "ฝรั่งเศส",
    German: "เยอรมัน",
    Italian: "อิตาลี",
    Portuguese: "โปรตุเกส",
    Dutch: "ดัตช์",
    Polish: "โปแลนด์",
    Russian: "รัสเซีย",
    Turkish: "ตุรกี",
    Arabic: "อาหรับ",
    "Chinese (Simplified)": "จีน (ตัวย่อ)",
    Japanese: "ญี่ปุ่น",
    Korean: "เกาหลี",
    Vietnamese: "เวียดนาม",
    Thai: "ไทย",

    // Box score
    "Show Full Headers": "แสดงหัวข้อแบบเต็ม",
    "Show Short Headers": "แสดงหัวข้อแบบสั้น",
    "Show Less Players": "แสดงผู้เล่นน้อยลง",
    "Show All Players": "แสดงผู้เล่นทั้งหมด",
    "Attack Kills": "แต้มจากการรุก",
    "Attack Errors": "ความผิดพลาดในการรุก",
    "Total Attempts": "จำนวนครั้งทั้งหมด",
    "Hitting Efficiency": "ประสิทธิภาพการรุก",
    "Serve Aces": "เสิร์ฟได้แต้ม",
    "Serve Errors": "เสิร์ฟเสีย",
    "Pass Errors": "รับลูกเสีย",
    "% Perfect Passes": "% การรับลูกสมบูรณ์",
    "Passing Average": "ค่าเฉลี่ยการรับลูก",
    "Total Passes": "การรับลูกทั้งหมด",
    "Set Assists": "การเซ็ตช่วยทำแต้ม",
    Digs: "การขุดลูก",
    "Momentum Chart": "กราฟโมเมนตัม",
    "Key event": "จังหวะสำคัญ",
    "Break Point %": "% เบรกพอยท์",
    "First Ball Sideout %": "% ไซด์เอาท์ลูกแรก",
    "Sideout Transition %": "% ไซด์เอาท์จากลูกต่อเนื่อง",
    "FB Sideout Kills %": "% การตบทำแต้มลูกแรก",
    "Sideout Opposition Error %": "% ไซด์เอาท์จากความผิดพลาดคู่แข่ง",
    Plus: "บวก",
    Minus: "ลบ",
    Blocks: "แต้มบล็อก",
    "Positive Serve %": "% การเสิร์ฟที่ได้เปรียบ",
    Sideout: "ไซด์เอาท์",
    "Break point": "เบรกพอยท์",
    "Ace / Kill": "เอซ / การตบทำแต้ม",
    "In play": "อยู่ในการเล่น",
    Error: "ความผิดพลาด",
    "Sideout on a kill": "ไซด์เอาท์จากการตบทำแต้ม",
    "Sideout on an error": "ไซด์เอาท์จากความผิดพลาดคู่แข่ง",
    "Top half": "ครึ่งบน",
    "Bottom half": "ครึ่งล่าง",
    "The centre line steps toward the team that wins each point.": "เส้นกึ่งกลางจะขยับไปทางทีมที่ได้แต้มในแต่ละลูก",
    "No point-by-point data available for this match.": "ไม่มีข้อมูลรายแต้มสำหรับแมตช์นี้",
    "No match data available.": "ไม่มีข้อมูลการแข่งขัน",
    "This report isn't supported on this browser yet — please use Safari.": "รายงานนี้ยังไม่รองรับเบราว์เซอร์นี้ — โปรดใช้ Safari",
    "No video available for this match.": "ไม่มีวิดีโอสำหรับแมตช์นี้",
    passes: "การรับ",
    "eff.": "ประสิทธิภาพ",
    Grade: "เกรด",
    LEFT: "ซ้าย",
    CENTRE: "กลาง",
    RIGHT: "ขวา",
    SELECTED: "ที่เลือก",
    Passers: "ผู้รับลูก",
    Calls: "คอล",
    "SIDE OUT": "ไซด์เอาท์",
    TRANSITION: "ทรานซิชัน",
    "No events in selection.": "ไม่มีเหตุการณ์ในตัวเลือกปัจจุบัน",
    "Serve Receives By Players": "การรับลูกเสิร์ฟตามผู้เล่น",
    "Serve Receive By Zones": "การรับลูกเสิร์ฟตามโซน",
    "Dig Errors": "ความผิดพลาดในการขุดลูก",
    "Dig Average": "ค่าเฉลี่ยการขุดลูก",
    "Block Solos": "การบล็อกเดี่ยว",
    "Block Assists": "การบล็อกช่วย",
    "Block Errors": "ความผิดพลาดในการบล็อก",
    "Points Scored": "แต้มที่ทำได้",
    TEAM: "ทีม",

    // Sideout report
    "ALL ROWS": "ทุกแถว",
    "ROWS ": "แถว ",
    "ROW ": "แถว ",
    "FB Kills": "แต้มจากการรุก FB",
    "Opp. Serve Errors": "เสิร์ฟเสียของคู่แข่ง",
    "FB Opp. Errors": "ความผิดพลาดคู่แข่ง FB",
    "Kills/Blocks": "การรุก/การบล็อก",
    "Opp. Errors": "ความผิดพลาดคู่แข่ง",
    Unsuccessful: "ไม่สำเร็จ",

    // Attack zones
    "No events in selected zone.":
      "ไม่มีเหตุการณ์ในโซนที่เลือก",
    "Show all attacks. Or click on a zone to show attacks in that zone.":
      "แสดงการรุกทั้งหมด หรือคลิกที่โซนเพื่อแสดงการรุกในโซนนั้น",
    "OPPONENTS (": "คู่แข่ง (",

    // Video analysis filters
    "All Types": "ทุกประเภท",
    Serve: "เสิร์ฟ",
    "Serve-Receive": "รับลูกเสิร์ฟ",
    Set: "เซ็ต",
    "Setter's Call": "การเรียกของตัวเซ็ต",
    "Disable this skill": "ปิดใช้งานทักษะนี้",
    "Enable this skill": "เปิดใช้งานทักษะนี้",
    "Teams & Players": "ทีมและผู้เล่น",
    "Match State": "สถานะการแข่งขัน",
    "Custom Code": "โค้ดกำหนดเอง",
    "Search... (comma-separated for multiple)": "ค้นหา... (คั่นด้วยจุลภาคสำหรับหลายค่า)",
    vs: "พบกับ",
    "Rewind 60 seconds": "ย้อนกลับ 60 วินาที",
    "Rewind 1 frame": "ย้อนกลับ 1 เฟรม",
    "Forward 1 frame": "ไปข้างหน้า 1 เฟรม",
    "Forward 60 seconds": "ไปข้างหน้า 60 วินาที",
    "Previous Clip": "คลิปก่อนหน้า",
    Replay: "เล่นซ้ำ",
    "Next Clip": "คลิปถัดไป",
    Phases: "ระยะ",
    "Score Range": "ช่วงคะแนน",
    "Set Winner": "ผู้ชนะเซต",
    Type: "ประเภท",
    Strength: "ความแรง",
    "Setter Calls": "การเรียกของตัวเซ็ต",
    "Setter Call": "การเรียกของตัวเซ็ต",
    Freeball: "ลูกฟรี",
    "Close (0-2)": "สูสี (0-2)",
    "Moderate (3-5)": "ปานกลาง (3-5)",
    "Lopsided (6+)": "ห่างมาก (6+)",
    "Focus Team": "ทีมที่สนใจ",
    Oppositions: "ฝ่ายตรงข้าม",
    "Focus Team Won": "ทีมที่สนใจชนะ",
    "Oppositions Won": "ฝ่ายตรงข้ามชนะ",
    "Advanced Filters": "ตัวกรองขั้นสูง",
    Players: "นักกีฬา",
    Opponents: "คู่ต่อสู้",
    "Event Type": "ประเภทเหตุการณ์",
    "Event Result": "ผลลัพธ์เหตุการณ์",
    "Attack Type": "ประเภทการตบ",
    "Hit Type": "ประเภทการตี",
    "Block Type": "ประเภทการบล็อก",
    "Attack Combo": "ชุดคอมโบการตบ",
    Apply: "นำไปใช้",
    Rotation: "การหมุนตัว",
    Skill: "ทักษะ",
    "Lead Time": "เวลานำหน้า",
    Duration: "ระยะเวลา",
    Timing: "การจับเวลา",
    "Go Live": "ไปที่การถ่ายทอดสด",
    "Playback has fallen behind the live broadcast — click to jump to now.": "การเล่นล่าช้ากว่าการถ่ายทอดสด — คลิกเพื่อข้ามไปยังปัจจุบัน",
    "Video timing": "การจับเวลาวิดีโอ",
    "Default lead time (seconds before an event to start playback) and clip duration, per skill.": "เวลานำหน้าเริ่มต้น (จำนวนวินาทีก่อนเหตุการณ์เพื่อเริ่มเล่น) และระยะเวลาคลิปสำหรับแต่ละทักษะ",
    Cloud: "คลาวด์",
    Local: "ท้องถิ่น",
    "Relay host": "โฮสต์รีเลย์",
    "IP or hostname of the machine running the local relay.": "ไอพีหรือชื่อโฮสต์ของเครื่องที่รันรีเลย์ภายในเครื่อง",
    Spike: "ตบ",
    Block: "บล็อก",
    Defence: "รับ",
    "All Results": "ทุกผลลัพธ์",
    Hard: "ตบหนัก",
    Soft: "ตบเบา",
    Tip: "หยอด",
    "Not Specified": "ไม่ระบุ",
    "H - High": "H - ลูกสูง",
    "M- Medium": "M - ลูกกลาง",
    "Q - Quick": "Q - ลูกเร็ว",
    "T - Tense": "T - ลูกพุ่ง",
    "U - Super": "U - ซูเปอร์",
    "F - Fast": "F - ลูกไว",
    "O - Other": "O - อื่นๆ",
    "B0 - No Block": "B0 - ไม่มีบล็อก",
    "B1 - 1 Block": "B1 - บล็อก 1 คน",
    "B2 - 2 Blocks": "B2 - บล็อก 2 คน",
    "B3 - 3 Blocks": "B3 - บล็อก 3 คน",
    "BS - Seams": "BS - ช่องว่าง",

    // All filters panel
    ALL: "ทั้งหมด",
    NONE: "ไม่มี",

    // Playlist editor
    "Remove Item": "ลบรายการ",
    "Do you really want to remove the selected item?":
      "คุณต้องการลบรายการที่เลือกจริงหรือ?",
    "Description is required": "ต้องระบุคำอธิบาย",
    "Comments are required": "ต้องระบุความคิดเห็น",
    "Tags are required": "ต้องระบุแท็ก",
    "Playlist saved successfully": "บันทึกเพลย์ลิสต์สำเร็จ",
    "Error saving playlist": "เกิดข้อผิดพลาดในการบันทึกเพลย์ลิสต์",

    // Language editor
    "My saved packages": "ชุดที่บันทึกของฉัน",
    "Loading shared languages…": "กำลังโหลดภาษาที่แชร์…",
    "Community shared languages": "ภาษาที่แชร์โดยชุมชน",
    by: "โดย",
    "based on": "อ้างอิงจาก",
    "or create new": "หรือสร้างใหม่",
    Use: "ใช้",
    "New Language Package": "ชุดภาษาใหม่",
    "Language Name": "ชื่อภาษา",
    "Language Code": "รหัสภาษา",
    "Flag Emoji": "อิโมจิธง",
    "Base language (reference)": "ภาษาพื้นฐาน (อ้างอิง)",
    "If not English, both English and the reference language will be shown.":
      "หากไม่ใช่ภาษาอังกฤษ จะแสดงทั้งภาษาอังกฤษและภาษาอ้างอิง",
    "Start Editing": "เริ่มแก้ไข",
    "Save Key": "บันทึกคีย์",
    "AI key set": "ตั้งคีย์ AI แล้ว",
    "Set API key for AI translation":
      "ตั้งคีย์ API สำหรับการแปลด้วย AI",
    "AI key ✓": "คีย์ AI ✓",
    "Set AI key": "ตั้งคีย์ AI",
    "AI: Translate All": "AI: แปลทั้งหมด",
    "Translating…": "กำลังแปล…",
    "AI translate section": "แปลส่วนนี้ด้วย AI",
    "Clear All": "ล้างทั้งหมด",
    "Saving…": "กำลังบันทึก…",
    English: "อังกฤษ",
    new: "ใหม่",
    "Enter translation…": "กรอกคำแปล…",
    "Share with other VBLive users?":
      "แชร์กับผู้ใช้ VBLive คนอื่นหรือไม่?",
    "Would you like to share the": "คุณต้องการแชร์",
    "language package with all VBLive users? Anyone can then copy and build upon it.":
      "ชุดภาษากับผู้ใช้ VBLive ทุกคนหรือไม่? จากนั้นใครก็ตามสามารถคัดลอกและต่อยอดได้",
    "No, just save for me": "ไม่ บันทึกเฉพาะของฉัน",
    "Yes, share it!": "ใช่ แชร์เลย!",
    "Language package saved and shared with all VBLive users!":
      "บันทึกชุดภาษาและแชร์กับผู้ใช้ VBLive ทุกคนแล้ว!",
    "Language package saved!": "บันทึกชุดภาษาแล้ว!",
    "Clear all translations? This cannot be undone.":
      "ล้างคำแปลทั้งหมด? การดำเนินการนี้ไม่สามารถยกเลิกได้",

    // Additional UI phrases
    Teams: "ทีม",
    "Shared By": "แชร์โดย",
    "View Activity": "กิจกรรมการดู",
    Viewer: "ผู้ดู",
    "Date / Time": "วันที่ / เวลา",
    "No views recorded yet.": "ยังไม่มีการบันทึกการดู",
    "Date / Scores": "วันที่ / คะแนน",
    "Stats API source": "แหล่งที่มา API สถิติ",
    "load one or more AVC VIS files": "โหลดไฟล์ AVC VIS หนึ่งไฟล์ขึ้นไป",
    "Points to close a set": "คะแนนที่ใช้ปิดเซต",
    "Require 2-point advantage": "ต้องนำ 2 คะแนน",
    Roster: "รายชื่อนักกีฬา",
    Clone: "ทำสำเนา",
    "Edit team": "แก้ไขทีม",
    "New player": "นักกีฬาใหม่",
    "Search player...": "ค้นหานักกีฬา…",
    "Match Centre": "ศูนย์การแข่งขัน",
    "Points Won": "คะแนนที่ได้",
    "Opp. Error": "ความผิดพลาดของคู่แข่ง",
    Total: "รวม",
    "Match Skills": "ทักษะการแข่งขัน",
    Dig: "การรับลูกป้องกัน",
    "Best Scorers": "ผู้ทำคะแนนสูงสุด",
    "Power Play": "Power Play",
    Match: "การแข่งขัน",
    "Player scouting": "การสอดแนมนักกีฬา",
    "Team deep analysis": "การวิเคราะห์ทีมเชิงลึก",
    "All Players": "นักกีฬาทั้งหมด",
    "All Sets": "เซตทั้งหมด",
    "All Rotations": "การหมุนตำแหน่งทั้งหมด",
    "All Combos": "คอมโบทั้งหมด",
    "All Calls": "คอลทั้งหมด",
    "All Events": "เหตุการณ์ทั้งหมด",
    "Select all items": "เลือกทั้งหมด",
    "Unselect all items": "ยกเลิกการเลือกทั้งหมด",
    "Create play list": "สร้างเพลย์ลิสต์",
    "Point by Point": "ทีละแต้ม",
    // i18n backfill pass — previously English-fallback-only keys
    "Access denied": "ปฏิเสธการเข้าถึง",
    "Aces": "เอซ",
    "ACES": "เอซ",
    "Add report": "เพิ่มรายงาน",
    "Admin": "ผู้ดูแลระบบ",
    "Admins": "ผู้ดูแลระบบ",
    "Advanced": "ขั้นสูง",
    "All set!": "พร้อมแล้ว!",
    "Analyse team with AI": "วิเคราะห์ทีมด้วย AI",
    "Are you sure you want to delete the selected play list?": "คุณแน่ใจหรือไม่ว่าต้องการลบเพลย์ลิสต์ที่เลือก?",
    "Assign": "กำหนด",
    "Assign playlist": "กำหนดเพลย์ลิสต์",
    "Assigned": "กำหนดแล้ว",
    "At least one admin is required.": "ต้องมีผู้ดูแลระบบอย่างน้อยหนึ่งคน",
    "Attack Combo / Setter Call": "คอมโบโจมตี / การเรียกของตัวเซต",
    "Attack diagram": "แผนภาพการโจมตี",
    "Attacks": "การโจมตี",
    "ATTACKS": "การโจมตี",
    "Auto-detected a custom set-closing score for": "ตรวจพบคะแนนปิดเซตแบบกำหนดเองโดยอัตโนมัติสำหรับ",
    "BLOCK": "บล็อก",
    "BLOCKS": "บล็อก",
    "Beta testers": "ผู้ทดสอบเบต้า",
    "Clear filter": "ล้างตัวกรอง",
    "Close Set": "ปิดเซต",
    "Close menu": "ปิดเมนู",
    "Code": "โค้ด",
    "Coding defaults & shortcuts": "ค่าเริ่มต้นและทางลัดการบันทึกข้อมูล",
    "Continue": "ดำเนินการต่อ",
    "Could not load match data. The match may no longer be available.": "ไม่สามารถโหลดข้อมูลการแข่งขันได้ การแข่งขันอาจไม่สามารถใช้งานได้แล้ว",
    "Could not load stats for these matches.": "ไม่สามารถโหลดสถิติสำหรับการแข่งขันเหล่านี้ได้",
    "Could not load this match.": "ไม่สามารถโหลดการแข่งขันนี้ได้",
    "Could not load this playlist.": "ไม่สามารถโหลดเพลย์ลิสต์นี้ได้",
    "Could not load this report.": "ไม่สามารถโหลดรายงานนี้ได้",
    "Could not save the report.": "ไม่สามารถบันทึกรายงานได้",
    "Could not share the report. Please try again.": "ไม่สามารถแชร์รายงานได้ โปรดลองอีกครั้ง",
    "Court horizontal (net vertical)": "สนามแนวนอน (ตาข่ายแนวตั้ง)",
    "Court vertical (net horizontal)": "สนามแนวตั้ง (ตาข่ายแนวนอน)",
    "Create": "สร้าง",
    "Create a new team from this roster": "สร้างทีมใหม่จากรายชื่อนี้",
    "DIFFERENCE": "ผลต่าง",
    "DV Code": "โค้ด DV",
    "Delete this report?": "ลบรายงานนี้หรือไม่?",
    "Detected a custom set-closing score of": "ตรวจพบคะแนนปิดเซตแบบกำหนดเองที่",
    "Double Sub": "เปลี่ยนตัวคู่",
    "Double Sub: now pick a bench player for the flashing OPPOSITE slot…": "เปลี่ยนตัวคู่: ตอนนี้เลือกผู้เล่นสำรองสำหรับตำแหน่งตัวตีตรงข้ามที่กะพริบอยู่…",
    "Double Sub: pick a bench player for the flashing SETTER slot…": "เปลี่ยนตัวคู่: เลือกผู้เล่นสำรองสำหรับตำแหน่งตัวเซตที่กะพริบอยู่…",
    "Double Substitution — swap the Setter and Opposite for two incoming players": "เปลี่ยนตัวคู่ — สลับตัวเซตและตัวตีตรงข้ามด้วยผู้เล่นที่เข้ามาสองคน",
    "Drag to draw the ball path; pause briefly to mark a touch (reception/dig/block), release to finish.": "ลากเพื่อวาดเส้นทางลูกบอล หยุดสั้นๆ เพื่อทำเครื่องหมายการสัมผัสบอล (รับลูก/เซฟ/บล็อก) ปล่อยเพื่อเสร็จสิ้น",
    "Draw": "วาด",
    "Draw & Code": "วาดและบันทึกข้อมูล",
    "Draw & Code — draw ball paths on the court to code the rally": "วาดและบันทึกข้อมูล — วาดเส้นทางลูกบอลบนสนามเพื่อบันทึกข้อมูลการแลกลูก",
    "Edit Lineup": "แก้ไขรายชื่อตัวจริง",
    "Edit Lineup — freely rearrange the starting lineup before the set begins": "แก้ไขรายชื่อตัวจริง — จัดเรียงรายชื่อตัวจริงใหม่ได้อย่างอิสระก่อนเริ่มเซต",
    "Emails in this list can manage beta testers and other admins.": "อีเมลในรายการนี้สามารถจัดการผู้ทดสอบเบต้าและผู้ดูแลระบบอื่นๆ ได้",
    "Emails in this list get early access to AI Reports and other gated features. Changes take effect immediately.": "อีเมลในรายการนี้จะได้รับสิทธิ์เข้าถึงรายงาน AI และฟีเจอร์จำกัดอื่นๆ ก่อนใคร การเปลี่ยนแปลงจะมีผลทันที",
    "Enter a valid email address.": "กรอกที่อยู่อีเมลที่ถูกต้อง",
    "Exit fullscreen": "ออกจากโหมดเต็มหน้าจอ",
    "Extended": "ขยาย",
    "Failed to generate PDF.": "สร้าง PDF ไม่สำเร็จ",
    "Failed to save changes.": "บันทึกการเปลี่ยนแปลงไม่สำเร็จ",
    "Filtered by": "กรองตาม",
    "First name": "ชื่อ",
    "For stronger security later, consider moving admin checks to a Firebase Auth custom claim instead of this email list.": "เพื่อความปลอดภัยที่แข็งแกร่งขึ้นในภายหลัง ควรพิจารณาย้ายการตรวจสอบผู้ดูแลระบบไปใช้ custom claim ของ Firebase Auth แทนรายการอีเมลนี้",
    "Fullscreen": "เต็มหน้าจอ",
    "Generate an AI report from these matches": "สร้างรายงาน AI จากการแข่งขันเหล่านี้",
    "How often (in seconds) the events list is updated during a live session.": "ความถี่ (เป็นวินาที) ในการอัปเดตรายการเหตุการณ์ระหว่างเซสชันสด",
    "Last name": "นามสกุล",
    "Leave blank to use standard rules (25 points, 15 for a 5th/deciding set)": "เว้นว่างเพื่อใช้กฎมาตรฐาน (25 แต้ม, 15 แต้มสำหรับเซตที่ 5/เซตตัดสิน)",
    "Live session refresh interval": "ช่วงเวลาการรีเฟรชเซสชันสด",
    "Load VIS file(s)...": "โหลดไฟล์ VIS…",
    "Load this team's last-saved starting lineup (positions, setter and liberos)": "โหลดรายชื่อตัวจริงที่บันทึกล่าสุดของทีมนี้ (ตำแหน่ง ตัวเซต และลิเบโร่)",
    "Loading…": "กำลังโหลด…",
    "Manually end the current set at its current score, regardless of the configured winning score / 2-point advantage rule": "จบเซตปัจจุบันด้วยตนเองที่คะแนนปัจจุบัน โดยไม่คำนึงถึงคะแนนชนะที่ตั้งค่าไว้ / กฎการนำ 2 แต้ม",
    "Migration finished with some issues": "การย้ายข้อมูลเสร็จสิ้นโดยมีปัญหาบางประการ",
    "Most of your data was migrated, but some items could not be moved. You can retry now or continue and try again later.": "ข้อมูลส่วนใหญ่ของคุณถูกย้ายแล้ว แต่บางรายการไม่สามารถย้ายได้ คุณสามารถลองใหม่ตอนนี้หรือดำเนินการต่อแล้วลองใหม่ภายหลัง",
    "Mute": "ปิดเสียง",
    "New team": "ทีมใหม่",
    "Next": "ถัดไป",
    "Nickname": "ชื่อเล่น",
    "No Power Play": "ไม่มี Power Play",
    "No admins yet.": "ยังไม่มีผู้ดูแลระบบ",
    "No beta testers yet.": "ยังไม่มีผู้ทดสอบเบต้า",
    "No coded events to analyse.": "ไม่มีเหตุการณ์ที่บันทึกข้อมูลไว้ให้วิเคราะห์",
    "No coded events to export.": "ไม่มีเหตุการณ์ที่บันทึกข้อมูลไว้ให้ส่งออก",
    "No matches.": "ไม่มีการแข่งขัน",
    "No players added yet.": "ยังไม่มีการเพิ่มผู้เล่น",
    "No players recorded.": "ไม่มีผู้เล่นที่บันทึกไว้",
    "No players yet.": "ยังไม่มีผู้เล่น",
    "No playlists assigned.": "ไม่มีเพลย์ลิสต์ที่กำหนด",
    "No playlists found.": "ไม่พบเพลย์ลิสต์",
    "No reports saved.": "ไม่มีรายงานที่บันทึกไว้",
    "No roster players available.": "ไม่มีผู้เล่นในรายชื่อที่สามารถใช้ได้",
    "No substitutions": "ไม่มีการเปลี่ยนตัว",
    "No teams found.": "ไม่พบทีม",
    "No teams yet.": "ยังไม่มีทีม",
    "No teams.": "ไม่มีทีม",
    "Nominate Setter": "กำหนดตัวเซต",
    "Notify these users by email (optional)": "แจ้งเตือนผู้ใช้เหล่านี้ทางอีเมล (ไม่บังคับ)",
    "OPP REC": "รับลูกฝ่ายตรงข้าม",
    "OPP. ERRORS": "ข้อผิดพลาดฝ่ายตรงข้าม",
    "Open / Download": "เปิด / ดาวน์โหลด",
    "Open local file": "เปิดไฟล์ในเครื่อง",
    "Open menu": "เปิดเมนู",
    "Opposition Passing — use the opposition's reception formation on the diagram": "การรับลูกของฝ่ายตรงข้าม — ใช้รูปแบบการรับลูกของฝ่ายตรงข้ามบนแผนภาพ",
    "Paste the report (Markdown supported)…": "วางรายงาน (รองรับ Markdown)…",
    "Pause": "หยุดชั่วคราว",
    "Play": "เล่น",
    "Playing squad": "รายชื่อผู้เล่นในนัด",
    "Playing squad is full. Remove a player from the squad before adding another.": "รายชื่อผู้เล่นในนัดเต็มแล้ว โปรดลบผู้เล่นออกจากรายชื่อก่อนเพิ่มผู้เล่นคนอื่น",
    "Please sign in to save reports.": "กรุณาเข้าสู่ระบบเพื่อบันทึกรายงาน",
    "Power Play Only": "เฉพาะ Power Play",
    "Record a team error (no player) — edit later to add the player and event type": "บันทึกข้อผิดพลาดของทีม (ไม่มีผู้เล่น) — แก้ไขภายหลังเพื่อเพิ่มผู้เล่นและประเภทเหตุการณ์",
    "Record at least one event before making substitutions": "บันทึกเหตุการณ์อย่างน้อยหนึ่งเหตุการณ์ก่อนทำการเปลี่ยนตัว",
    "Redo": "ทำซ้ำ",
    "Remove": "ลบออก",
    "Remove share": "ลบการแชร์",
    "Remove yourself as admin? You will immediately lose access to this page.": "ลบตัวเองออกจากผู้ดูแลระบบหรือไม่? คุณจะสูญเสียสิทธิ์เข้าถึงหน้านี้ทันที",
    "Report saved and shared.": "บันทึกและแชร์รายงานแล้ว",
    "Report saved to the player's profile.": "บันทึกรายงานลงในโปรไฟล์ผู้เล่นแล้ว",
    "Report title": "ชื่อรายงาน",
    "Reports": "รายงาน",
    "Result": "ผลลัพธ์",
    "Retry": "ลองอีกครั้ง",
    "Return to live coding": "กลับไปบันทึกข้อมูลสด",
    "Rotation is not permitted while making substitutions": "ไม่อนุญาตให้หมุนตำแหน่งขณะทำการเปลี่ยนตัว",
    "SERVE": "เสิร์ฟ",
    "SETS": "เซต",
    "Save & Share": "บันทึกและแชร์",
    "Save this report so you can share it with other users": "บันทึกรายงานนี้เพื่อให้คุณสามารถแชร์กับผู้ใช้อื่นได้",
    "Save this report to the player's profile": "บันทึกรายงานนี้ลงในโปรไฟล์ผู้เล่น",
    "Save to player": "บันทึกให้ผู้เล่น",
    "Saved lineup": "รายชื่อตัวจริงที่บันทึกไว้",
    "Scroll, drag or swipe to scrub": "เลื่อน ลาก หรือปัดเพื่อเลื่อนตำแหน่ง",
    "Search existing players...": "ค้นหาผู้เล่นที่มีอยู่…",
    "Search playlists…": "ค้นหาเพลย์ลิสต์…",
    "Select a match video to view while entering lineups.": "เลือกวิดีโอการแข่งขันเพื่อดูขณะกรอกรายชื่อตัวจริง",
    "Select a player.": "เลือกผู้เล่น",
    "Select a team.": "เลือกทีม",
    "Setting up your account": "กำลังตั้งค่าบัญชีของคุณ",
    "Shaded rows are played under a Power Play — HP/AP marks the calling team, bold gold scores are bonus (double) points.": "แถวที่แรเงาคือช่วงที่เล่นภายใต้ Power Play — HP/AP ระบุทีมที่เรียกใช้ คะแนนสีทองตัวหนาคือคะแนนโบนัส (สองเท่า)",
    "Shared by": "แชร์โดย",
    "Shared with me": "แชร์กับฉัน",
    "Show help": "แสดงวิธีใช้",
    "Show public playlists": "แสดงเพลย์ลิสต์สาธารณะ",
    "Standard": "มาตรฐาน",
    "Substitutions made — tap to view": "มีการเปลี่ยนตัว — แตะเพื่อดู",
    "Tap the blocking player.": "แตะผู้เล่นที่บล็อก",
    "Team colour": "สีประจำทีม",
    "Team name": "ชื่อทีม",
    "That email is already in the list.": "อีเมลนี้มีอยู่ในรายการแล้ว",
    "The report is too large to save. Try a single match or fewer sets.": "รายงานมีขนาดใหญ่เกินไปที่จะบันทึก ลองใช้การแข่งขันเดียวหรือจำนวนเซตที่น้อยลง",
    "This match has no Power Play calls.": "การแข่งขันนี้ไม่มีการเรียก Power Play",
    "This player has no name to save the report against.": "ผู้เล่นคนนี้ไม่มีชื่อสำหรับบันทึกรายงาน",
    "This team has no designated setter. Pick the player who is setting.": "ทีมนี้ยังไม่มีตัวเซตที่กำหนดไว้ เลือกผู้เล่นที่กำลังเซตอยู่",
    "Toggle video player": "สลับตัวเล่นวิดีโอ",
    "Unmute": "เปิดเสียง",
    "Untitled playlist": "เพลย์ลิสต์ไม่มีชื่อ",
    "Video URL…": "URL วิดีโอ…",
    "We're moving your data to a faster, more reliable home. This happens only once.": "เรากำลังย้ายข้อมูลของคุณไปยังที่ที่เร็วและเชื่อถือได้มากขึ้น การดำเนินการนี้เกิดขึ้นเพียงครั้งเดียว",
    "You don't have permission to view this page.": "คุณไม่มีสิทธิ์ดูหน้านี้",
    "Your data has been migrated successfully.": "ย้ายข้อมูลของคุณสำเร็จแล้ว",
    "Your teams and players will appear here after your data is migrated.": "ทีมและผู้เล่นของคุณจะปรากฏที่นี่หลังจากย้ายข้อมูลของคุณแล้ว",
    "bonus point": "แต้มโบนัส",
    "bonus points": "แต้มโบนัส",
    "event": "เหตุการณ์",
    "events": "เหตุการณ์",
    "file(s).": "ไฟล์",
    "is not on this team's roster.": "ไม่อยู่ในรายชื่อของทีมนี้",
    "loaded": "โหลดแล้ว",
    "matches": "การแข่งขัน",
    "more": "เพิ่มเติม",
    "players": "ผู้เล่น",
    "points (no value was entered) — applied automatically.": "แต้ม (ไม่ได้ป้อนค่า) — นำไปใช้โดยอัตโนมัติ",
    "roster": "รายชื่อ",
    "seconds": "วินาที",
    "selected": "เลือกแล้ว",
    "session(s)?": "เซสชัน?",
    "showing": "แสดง",
    "tournaments": "ทัวร์นาเมนต์",
    "…and": "…และ",
    "● marks which team served the point.": "● ระบุว่าทีมใดเป็นผู้เสิร์ฟในแต้มนั้น",
    // i18n backfill pass (Catapult + misc)
    "Centimetres (cm)": "เซนติเมตร (ซม.)",
    "Inches (in)": "นิ้ว (in)",
    "Jump height units": "หน่วยความสูงการกระโดด",
    "Choose how Catapult jump heights are displayed and filtered.": "เลือกวิธีแสดงและกรองความสูงการกระโดดของ Catapult",
    "Select Catapult Focus file...": "เลือกไฟล์ Catapult Focus…",
    "Select a Catapult Focus CSV export to view jump data for this match.": "เลือกไฟล์ส่งออก CSV ของ Catapult Focus เพื่อดูข้อมูลการกระโดดของแมตช์นี้",
    "Loaded from saved match data": "โหลดจากข้อมูลแมตช์ที่บันทึกไว้",
    "No jump data found in this file.": "ไม่พบข้อมูลการกระโดดในไฟล์นี้",
    "Could not read this file. Please check it is a Catapult Focus CSV export.": "ไม่สามารถอ่านไฟล์นี้ได้ โปรดตรวจสอบว่าเป็นไฟล์ส่งออก CSV ของ Catapult Focus",
    "Could not save jump data with this match.": "ไม่สามารถบันทึกข้อมูลการกระโดดกับแมตช์นี้ได้",
    "Jump data saved with this match.": "บันทึกข้อมูลการกระโดดกับแมตช์นี้แล้ว",
    "Show players with no match stats": "แสดงผู้เล่นที่ไม่มีสถิติการแข่งขัน",
    "hidden": "ซ่อนอยู่",
    "jumps": "การกระโดด",
    "periods": "ช่วงเวลา",
    "Sync offset": "ค่าชดเชยการซิงค์",
    "matched": "ตรงกัน",
    "match events now show jump height/duration in Video Analysis": "เหตุการณ์ในแมตช์ตอนนี้แสดงความสูง/ระยะเวลาการกระโดดใน การวิเคราะห์วิดีโอ",
    "Team": "ทีม",
    "Team A": "ทีม A",
    "Team B": "ทีม B",
    "Which team is this Catapult file for?": "ไฟล์ Catapult นี้สำหรับทีมใด?",
    "We couldn't confidently match the players in this Focus file to one team. Please select which team it belongs to.": "เราไม่สามารถจับคู่ผู้เล่นในไฟล์ Focus นี้กับทีมใดทีมหนึ่งได้อย่างมั่นใจ โปรดเลือกว่าไฟล์นี้เป็นของทีมใด",
    "This match has no video attached yet.": "แมตช์นี้ยังไม่มีวิดีโอแนบมา",
    "No synced jump events found for this player.": "ไม่พบเหตุการณ์การกระโดดที่ซิงค์แล้วสำหรับผู้เล่นคนนี้",
    "No match loaded.": "ยังไม่ได้โหลดแมตช์",
    "avg": "เฉลี่ย",
    "max": "สูงสุด",
    "Sync": "ซิงค์",
    "Jump Report": "รายงานการกระโดด",
    "Watch Video": "ดูวิดีโอ",
    "Save PDF": "บันทึก PDF",
    "Generated by VBLive": "สร้างโดย VBLive",
    "Deep analysis of {team} from ball-by-ball event data, covering serving, reception, setting, attack, blocking, defence, rotations and opponent tendencies. Best used with the Anthropic (Claude) provider.": "การวิเคราะห์เชิงลึกของ {team} จากข้อมูลเหตุการณ์แบบลูกต่อลูก ครอบคลุมการเสิร์ฟ การรับลูก การเซ็ต การตบ การบล็อก การป้องกัน การหมุนตำแหน่ง และแนวโน้มของคู่ต่อสู้ แนะนำให้ใช้กับผู้ให้บริการ Anthropic (Claude)",
    "teams": "ทีม",
  },
  id: {
    // Information page
    Information: "Informasi",
    "Release notes": "Catatan rilis",
    "Contact us": "Hubungi kami",
    "Questions, feedback or suggestions are most welcome.": "Pertanyaan, masukan, atau saran sangat kami terima.",
    // DV match coding
    Lineups: "Susunan pemain",
    Point: "Poin",
    Video: "Video",
    Defaults: "Bawaan",
    "[team][NN][skill][type?][eval][zoneFrom?[szFrom?][zoneTo?[szTo?]]]": "[tim][NN][keterampilan][tipe?][evaluasi][zonaDari?[szDari?][zonaKe?[szKe?]]]",
    Vertical: "Vertikal",
    Horizontal: "Horizontal",
    "Swap sides": "Tukar sisi",
    Subs: "Pergantian",
    Substitutions: "Pergantian",
    Court: "Lapangan",
    Home: "Tuan rumah",
    Away: "Tamu",
    Games: "Set",
    Enter: "Masukkan",
    Undo: "Urungkan",
    "Match saved": "Pertandingan disimpan",
    "Please sign in to save this match.": "Silakan masuk untuk menyimpan pertandingan ini.",
    "Nothing to save yet.": "Belum ada yang bisa disimpan.",
    "Could not save the match. Please try again.": "Tidak dapat menyimpan pertandingan. Silakan coba lagi.",
    Discard: "Buang perubahan",
    "Resumed autosaved coding": "Melanjutkan pencatatan yang tersimpan otomatis",
    "Changes discarded": "Perubahan dibuang",
    "Discard all changes and revert to the starting point?":
      "Buang semua perubahan dan kembali ke titik awal?",
    Clear: "Bersihkan",
    "Prev set": "Set sebelumnya",
    "Next set": "Set berikutnya",
    "Match Setup": "Pengaturan Pertandingan",
    "First serve": "Servis pertama",
    "Start Match": "Mulai Pertandingan",
    "Resume Match": "Lanjutkan Pertandingan",
    Skip: "Lewati",
    "Select an event then drag to draw ball path": "Pilih peristiwa lalu seret untuk menggambar jalur bola",
    "Drag on court to set ball path": "Seret di lapangan untuk menetapkan jalur bola",
    "No events yet – enter a DV code above and press Enter.": "Belum ada peristiwa – masukkan kode DV di atas lalu tekan Enter.",
    "DataVolley code reference": "Referensi kode DataVolley",
    Format: "Format",
    Skills: "Keterampilan",
    Evaluation: "Evaluasi",
    "Skill / type omission": "Penghilangan keterampilan / tipe",
    "Skill and/or type may be omitted; defaults fill in the rest.": "Keterampilan dan/atau tipe boleh dihilangkan; nilai bawaan akan mengisi sisanya.",
    "Dot-coding (S→R and A→B pairs)": "Pengkodean titik (pasangan S→R dan A→B)",
    "Chaining & aliases": "Perangkaian & alias",
    "team:": "tim:",
    "omit → defaults to A": "hilangkan → bawaan ke A",
    "NN: 1 or 2 digit jersey, e.g. 1, 01, 12": "NN: nomor jersey 1 atau 2 digit, mis. 1, 01, 12",
    "sub-zone: a b c d appended to zone digit, e.g. 5b or 5b1a": "sub-zona: a b c d ditambahkan ke angka zona, mis. 5b atau 5b1a",
    "S Serve · R Reception · E Set · A Attack · B Block · D Dig · F Freeball": "S Servis · R Terima · E Umpan · A Serang · B Blok · D Gali · F Bola bebas",
    "# kill/ace · + positive · ! ok · - negative · / overpass · = error": "# poin/ace · + positif · ! cukup · - negatif · / overpass · = kesalahan",
    "E.g.": "Mis.",
    "serve with skill=A (default), type=H, eval=+, zone=5.": "servis dengan skill=A (bawaan), type=H, eval=+, zone=5.",
    "type-only (no skill marker), defaults skill from context.": "hanya tipe (tanpa penanda keterampilan), keterampilan diambil dari konteks.",
    "serve coded, reception auto-linked.": "servis tercatat, terima otomatis tertaut.",
    "attack coded, block auto-linked.": "serangan tercatat, blok otomatis tertaut.",
    "Dot connects the reception eval to the corresponding serve eval via the serve→receive mapping.": "Titik menghubungkan evaluasi terima dengan evaluasi servis terkait melalui pemetaan servis→terima.",
    "two events in one string.": "dua peristiwa dalam satu string.",
    "manual point A/B.": "poin manual A/B.",
    "serve ace/error.": "ace/kesalahan servis.",
    "serve with eval.": "servis dengan evaluasi.",
    // Stats, sideouts, filters & video tooltips
    "Block Kills": "Poin Blok",
    "Side Out Percentage": "Persentase Side Out",
    "All First Ball": "Semua Bola Pertama",
    "First Ball Kills": "Poin Bola Pertama",
    "Good Serve": "Servis Bagus",
    "Break Points": "Break Point",
    Game: "Set",
    Row: "Posisi",
    "First Ball Sideouts": "Side Out Bola Pertama",
    "All Sideouts": "Semua Side Out",
    All: "Semua",
    None: "Tidak ada",
    Receiver: "Penerima",
    "# Passes": "Jumlah Terima",
    "Perfect %": "% Sempurna",
    "Positive %": "% Positif",
    "Sideout %": "% Side Out",
    "FB Sideout %": "% Side Out Bola Pertama",
    "Pass Hits": "Serangan Setelah Terima",
    "Pass Hit Kills": "Poin Setelah Terima",
    "Pass Hit Errors": "Kesalahan Setelah Terima",
    "Show Passes": "Tampilkan Terima",
    "Show Attacks": "Tampilkan Serangan",
    "Error Pass": "Terima Salah",
    "1 Pass": "Terima 1",
    "2 Pass": "Terima 2",
    "Perfect Pass": "Terima Sempurna",
    "Successful SO": "Side Out Berhasil",
    "First Ball SO": "Side Out Bola Pertama",
    "Unsuccessful SO": "Side Out Gagal",
    RECEIVES: "TERIMA",
    Sets: "Set",
    Rotations: "Rotasi",
    "Attack Combos": "Kombo Serangan",
    Stages: "Tahap",
    Display: "Tampilan",
    Setters: "Pengumpan",
    Attackers: "Penyerang",
    Results: "Hasil",
    Source: "Sumber",
    Cones: "Zona",
    "Select None": "Batalkan semua",
    "All Serves": "Semua Servis",
    "All Returns": "Semua Terima",
    Filters: "Filter",
    "Reset all filters": "Atur ulang semua filter",
    "Show video clips of filtered attacks": "Tampilkan klip video serangan yang difilter",
    "Spikes for the selected player": "Smes untuk pemain terpilih",
    "Click on the zones to view video of attacks": "Klik pada zona untuk melihat video serangan",
    "Expand All": "Perluas semua",
    "Collapse All": "Ciutkan semua",
    "Import Radar File": "Impor File Radar",
    "Select local video file": "Pilih file video lokal",
    "Save to database": "Simpan ke basis data",
    "Load and play online video": "Muat dan putar video daring",
    "Synchronise video with events": "Sinkronkan video dengan peristiwa",
    "Rewind 10 seconds": "Mundur 10 detik",
    "Rewind 1 second": "Mundur 1 detik",
    "Forward 1 second": "Maju 1 detik",
    "Forward 10 seconds": "Maju 10 detik",
    "Set timing for events": "Atur waktu untuk peristiwa",

    // Navigation
    Matches: "Pertandingan",
    Live: "Langsung",
    Import: "Impor",
    Playlists: "Daftar putar",
    "New Match": "Pertandingan Baru",
    // New Match / Match Coding gate
    "Match Coding": "Pencatatan Pertandingan",
    "Match Coding — Coming Soon": "Pencatatan Pertandingan — Segera Hadir",
    "Live match coding is on the way. Here's a preview of what's coming.":
      "Pencatatan pertandingan langsung akan segera hadir. Berikut pratinjau yang akan datang.",
    Settings: "Pengaturan",
    Language: "Bahasa",
    "Your profile": "Profil Anda",
    "Sign out": "Keluar",
    "Translation Editor": "Editor Terjemahan",

    // Common actions
    Save: "Simpan",
    Cancel: "Batal",
    Delete: "Hapus",
    Share: "Bagikan",
    Edit: "Ubah",
    New: "Baru",
    Refresh: "Segarkan",
    Search: "Cari",
    Reset: "Atur ulang",
    Close: "Tutup",
    "Sub for": "Ganti untuk",
    "No players available": "Tidak ada pemain tersedia",
    "Show full roster": "Tampilkan daftar lengkap",
    "Available only": "Hanya yang tersedia",
    "Maximum substitutions reached": "Pergantian maksimum tercapai",
    Back: "Kembali",
    "Download PDF": "Unduh PDF",
    "Loading...": "Memuat…",
    "Select All": "Pilih semua",
    "Unselect All": "Batalkan semua",
    Yes: "Ya",
    No: "Tidak",
    Add: "Tambah",
    Load: "Muat",
    Confirm: "Konfirmasi",
    Open: "Buka",
    show: "tampilkan",

    // Auth
    "Sign In": "Masuk",
    "Sign Up": "Daftar",
    "Sign Out": "Keluar",
    Email: "Email",
    Password: "Kata sandi",
    "Confirm Password": "Konfirmasi kata sandi",
    "Forgot password?": "Lupa kata sandi?",
    "Forgot Password": "Lupa Kata Sandi",
    "Don't have an account?": "Belum punya akun?",
    "Sign up for free!": "Daftar gratis!",
    "All ready signed up?": "Sudah mendaftar?",
    "Error Signing In": "Kesalahan saat masuk",
    "Signed up successfully": "Berhasil mendaftar",
    "Error signing up": "Kesalahan saat mendaftar",
    "Enter your email": "Masukkan email Anda",
    "Send Reset Email": "Kirim email atur ulang",
    "Email was sent to": "Email telah dikirim ke",
    "Could not send reset email":
      "Tidak dapat mengirim email atur ulang",

    // Matches list
    "Team 1": "Tim 1",
    "Team 2": "Tim 2",
    Tournament: "Turnamen",
    Scores: "Skor",
    Date: "Tanggal",
    Actions: "Tindakan",
    Owner: "Pemilik",
    Description: "Deskripsi",
    Comments: "Komentar",
    Tags: "Tag",
    "Add player": "Tambah pemain",
    "Add team": "Tambah tim",
    "Edit playlist": "Ubah daftar putar",
    "Merge Teams": "Gabungkan Tim",
    "Roster differences detected between teams:": "Perbedaan skuad terdeteksi antar tim:",
    "Review the roster in the next step before merging.": "Tinjau skuad pada langkah berikutnya sebelum menggabungkan.",
    "At least 2 team records must exist in your Teams list for the selected matches.": "Minimal 2 catatan tim harus ada di daftar Tim Anda untuk pertandingan yang dipilih.",
    "Merge...": "Gabungkan...",
    "Select 2 or more team records to merge into one.": "Pilih 2 catatan tim atau lebih untuk digabung menjadi satu.",
    "Filter teams...": "Filter tim...",
    "teams selected": "tim terpilih",
    "Select Teams": "Pilih Tim",
    "Reconcile Roster": "Cocokkan Skuad",
    "Confirm Merge": "Konfirmasi Penggabungan",
    "Merged team name": "Nama tim gabungan",
    "shirt number(s) have conflicting names — review below.": "nomor punggung memiliki nama yang bertentangan — tinjau di bawah.",
    "Shirt": "Nomor punggung",
    "players included": "pemain termasuk",
    "No players to reconcile.": "Tidak ada pemain untuk dicocokkan.",
    "Use this name": "Gunakan nama ini",
    "Create merged team": "Buat tim gabungan",
    "with": "dengan",
    "duplicate team records": "catatan tim duplikat",
    "Consolidate duplicate player records and reassign playlist links": "Konsolidasikan catatan pemain duplikat dan tetapkan ulang tautan daftar putar",
    "This cannot be undone.": "Tindakan ini tidak dapat dibatalkan.",
    "Teams being merged:": "Tim yang digabungkan:",
    "Step": "Langkah",
    "of": "dari",
    "Merging...": "Menggabungkan...",
    "Players & Teams": "Pemain & Tim",
    "No players or teams assigned.": "Tidak ada pemain atau tim yang ditetapkan.",
    "Search players...": "Cari pemain...",
    "Search teams...": "Cari tim...",
    "Saving...": "Menyimpan...",
    "My Matches": "Pertandingan Saya",
    "Shared Matches": "Pertandingan Dibagikan",
    "Search team...": "Cari tim…",
    "All Teams": "Semua Tim",
    "Multi Match Reports": "Laporan Banyak Pertandingan",
    "Delete match": "Hapus pertandingan",
    "Remove from my shared matches": "Hapus dari pertandingan yang dibagikan ke saya",
    "Share match": "Bagikan pertandingan",
    "Edit match": "Ubah pertandingan",
    "Open video analysis": "Buka analisis video",
    "Delete Match": "Hapus Pertandingan",
    "Are you sure you want to delete the selected match?":
      "Anda yakin ingin menghapus pertandingan yang dipilih?",
    "No matches selected.": "Tidak ada pertandingan yang dipilih.",

    // New Match
    "← Back": "← Kembali",
    "Team A (Home)": "Tim A (Tuan rumah)",
    "Team B (Away)": "Tim B (Tamu)",
    "Select team…": "Pilih tim…",
    "Tournament (optional)": "Turnamen (opsional)",
    "Tournament / competition name": "Nama turnamen / kompetisi",
    "Match date": "Tanggal pertandingan",
    "Start Coding →": "Mulai Mencatat →",

    // Reports
    Summary: "Ringkasan",
    "Box Score": "Tabel Statistik",
    "Side-outs Report": "Laporan Side Out",
    "Serve Receives": "Terima Servis",
    "Attack Zones": "Zona Serangan",
    "Hitting Charts": "Diagram Serangan",
    "Video Analysis": "Analisis Video",
    Dashboard: "Dasbor",
    Charts: "Diagram",
    "AI Report": "Laporan AI",
    // AI Reports page
    "AI Reports": "Laporan AI",
    "AI Reports — Coming Soon": "Laporan AI — Segera Hadir",
    "AI-powered match reports and deep analysis are on the way. Here are some sample reports to show what's coming.":
      "Laporan pertandingan dan analisis mendalam bertenaga AI akan segera hadir. Berikut beberapa contoh laporan untuk menunjukkan yang akan datang.",
    "Preview not available.": "Pratinjau tidak tersedia.",
    "Open sample": "Buka contoh",
    "No matches were loaded. Select one or more matches and try again.":
      "Tidak ada pertandingan yang dimuat. Pilih satu atau beberapa pertandingan lalu coba lagi.",
    "Attack Analysis (English)": "Analisis Serangan (Inggris)",
    "Blocking Analysis (Vietnamese)": "Analisis Blok (Vietnam)",
    "Opponent Tendencies (Japanese)": "Kecenderungan Lawan (Jepang)",
    // Deep AI analysis
    "Deep AI Analysis": "Analisis AI Mendalam",
    "AI Deep Analysis": "Analisis Mendalam AI",
    "Load at least one match with coded events to generate the analysis.": "Muat setidaknya satu pertandingan dengan peristiwa tercatat untuk membuat analisis.",
    "Run the deep analysis one section at a time to keep each request small. Edit or add questions below, then generate. Best used with the Anthropic (Claude) provider.":
      "Jalankan analisis mendalam satu bagian pada satu waktu agar tiap permintaan tetap kecil. Ubah atau tambahkan pertanyaan di bawah, lalu buat. Paling baik digunakan dengan penyedia Anthropic (Claude).",
    "Download CSV": "Unduh CSV",
    "Analysis section": "Bagian analisis",
    analysing: "menganalisis",
    "All sections (uses the most credits)": "Semua bagian (menggunakan kredit terbanyak)",
    "All sections": "Semua bagian",
    Questions: "Pertanyaan",
    "one per line, edit or add your own": "satu per baris, ubah atau tambahkan milik Anda",
    "Reset to defaults": "Atur ulang ke bawaan",
    "Add a question…": "Tambahkan pertanyaan…",
    "Runs every section using the questions you've set for each. This sends one large request and uses the most credits.":
      "Menjalankan setiap bagian menggunakan pertanyaan yang Anda tetapkan untuk masing-masing. Ini mengirim satu permintaan besar dan menggunakan kredit terbanyak.",
    "Generate full analysis": "Buat analisis lengkap",
    "Generate section analysis": "Buat analisis bagian",
    Serving: "Servis",
    Reception: "Terima Servis",
    Setting: "Umpan",
    Attack: "Serangan",
    Blocking: "Blok",
    "Defence / Digging": "Bertahan / Menggali",
    "Rotation Analysis": "Analisis Rotasi",
    "Opponent Tendencies (Scouting)": "Kecenderungan Lawan (Pemantauan)",
    "Overall Team Metrics": "Metrik Tim Keseluruhan",
    "Serve efficiency (aces, errors, and pressured receptions forced).": "Efisiensi servis (ace, kesalahan, dan tekanan pada penerimaan).",
    "Which servers are most effective, and against which reception zones?": "Server mana yang paling efektif, dan terhadap zona terima yang mana?",
    "Which serve types (jump, float, jump-float) yield the best results?": "Jenis servis mana (jump, float, jump-float) yang memberi hasil terbaik?",
    "Reception quality breakdown (perfect, positive, negative, error).": "Rincian kualitas terima (sempurna, positif, negatif, kesalahan).",
    "Which players struggle most under jump serves vs. float serves?": "Pemain mana yang paling kesulitan menghadapi jump serve vs. float serve?",
    "How does poor reception correlate with side-out efficiency?": "Bagaimana terima yang buruk berkorelasi dengan efisiensi side out?",
    "Set distribution by zone and by player.": "Distribusi umpan berdasarkan zona dan pemain.",
    "How does the setter perform under pressure (bad-pass situations)?": "Bagaimana kinerja pengumpan di bawah tekanan (situasi terima buruk)?",
    "Tempo breakdown (quick, medium, high ball) and which yields the best attack outcome.": "Rincian tempo (cepat, sedang, bola tinggi) dan mana yang memberi hasil serangan terbaik.",
    "Kill percentage and error rate per player, per rotation, and per zone.": "Persentase poin dan tingkat kesalahan per pemain, per rotasi, dan per zona.",
    "Which attack zones (pipe, cross, line) have the highest efficiency?": "Zona serangan mana (pipe, cross, line) yang paling efisien?",
    "How attack efficiency changes in transition vs. side-out.": "Bagaimana efisiensi serangan berubah dalam transisi vs. side out.",
    "Block-per-set rate per player and rotation.": "Rasio blok per set per pemain dan rotasi.",
    "Which opposing attack zones are most successfully blocked?": "Zona serangan lawan mana yang paling berhasil diblok?",
    "How often blocks result in kills vs. touch-outs.": "Seberapa sering blok menghasilkan poin vs. touch-out.",
    "Dig success rate per player and per court zone.": "Tingkat keberhasilan gali per pemain dan per zona lapangan.",
    "Which attack types (tip, roll, hard swing) are hardest to defend?": "Jenis serangan mana (tip, roll, smes keras) yang paling sulit dipertahankan?",
    "How defensive efficiency affects transition attack success.": "Bagaimana efisiensi bertahan memengaruhi keberhasilan serangan transisi.",
    "Which rotations have the highest and lowest point differential?": "Rotasi mana yang memiliki selisih poin tertinggi dan terendah?",
    "Side-out and break-point percentages per rotation.": "Persentase side out dan break point per rotasi.",
    "Rotation-specific vulnerabilities (e.g. a weak serving or passing rotation).": "Kelemahan khusus rotasi (mis. rotasi servis atau terima yang lemah).",
    "The opponent's most frequently used attack zones and combinations.": "Zona serangan dan kombinasi yang paling sering digunakan lawan.",
    "The opponent's go-to attackers in high-pressure situations.": "Penyerang andalan lawan dalam situasi tekanan tinggi.",
    "The serve-receive patterns the opponent favours.": "Pola terima servis yang disukai lawan.",
    "Overall rally-win probability in different score situations.": "Probabilitas memenangkan reli secara keseluruhan dalam berbagai situasi skor.",
    "How performance changes in sets 1–3 vs. sets 4–5 (fatigue/pressure effects).": "Bagaimana kinerja berubah di set 1–3 vs. set 4–5 (efek kelelahan/tekanan).",
    "The correlation between first-ball side-out percentage and match outcome.": "Korelasi antara persentase side out bola pertama dengan hasil pertandingan.",
    Report: "Laporan",

    // Import page
    "Load DVW file(s)...": "Muat file DVW…",
    "Load PSVB file(s)...": "Muat file PSVB…",
    "Load VSM file(s)...": "Muat file VSM…",
    "Load playlist file...": "Muat file daftar putar…",
    "load one or more DataVolley files":
      "muat satu atau beberapa file DataVolley",
    "load one or more VBStats files":
      "muat satu atau beberapa file VBStats",
    "load one or more VolleyStation VSM files":
      "muat satu atau beberapa file VolleyStation VSM",
    "Convert DVW": "Konversi DVW",
    "VSM": "VSM",
    "Convert DVW to VSM...": "Konversi DVW ke VSM…",
    "pick a DataVolley .dvw file to download as .vsm":
      "pilih file DataVolley .dvw untuk diunduh sebagai .vsm",
    "Convert VSM to DVW...": "Konversi VSM ke DVW…",
    "pick a VolleyStation .vsm file to download as .dvw":
      "pilih file VolleyStation .vsm untuk diunduh sebagai .dvw",
    "Converting…": "Mengonversi…",
    "Converted": "Dikonversi",
    "Failed to convert": "Konversi gagal",
    "Beta testers only — coming soon for everyone else.":
      "Hanya untuk penguji beta — segera hadir untuk semua orang.",
    "load a playlist": "muat daftar putar",
    "Use Shift-JIS Encoder": "Gunakan Encoder Shift-JIS",
    "Importing…": "Mengimpor…",
    "Save to Database?": "Simpan ke basis data?",
    "Always save imports automatically": "Selalu simpan impor secara otomatis",
    "View Only": "Hanya lihat",
    "Save to Database": "Simpan ke Basis Data",
    "Error saving to database": "Kesalahan menyimpan ke basis data",
    "Saved to database": "Tersimpan ke basis data",
    "Saving to database…": "Menyimpan ke basis data…",
    "Upload them to the database now?":
      "Unggah ke basis data sekarang?",

    // Settings / Options
    Theme: "Tema",
    Options: "Opsi",
    "Rotation naming convention": "Konvensi penamaan rotasi",
    "Choose how rotations are labelled in reports and analysis.":
      "Pilih cara rotasi diberi label dalam laporan dan analisis.",
    "R1–R6 (rotation sequence)": "R1–R6 (urutan rotasi)",
    "S1–S6 (setter court position)": "S1–S6 (posisi pengumpan di lapangan)",
    "R1: setter in position 1, R2: setter in 6, R3: setter in 5, R4: setter in 4, R5: setter in 3, R6: setter in 2.":
      "R1: pengumpan di posisi 1, R2: pengumpan di 6, R3: pengumpan di 5, R4: pengumpan di 4, R5: pengumpan di 3, R6: pengumpan di 2.",
    "S1: setter in 1, S2: setter in 2, S3: setter in 3, S4: setter in 4, S5: setter in 5, S6: setter in 6.":
      "S1: pengumpan di 1, S2: pengumpan di 2, S3: pengumpan di 3, S4: pengumpan di 4, S5: pengumpan di 5, S6: pengumpan di 6.",
    "Labels:": "Label:",
    "Current theme:": "Tema saat ini:",
    "Choose the language for the app interface.":
      "Pilih bahasa untuk antarmuka aplikasi.",
    "Create your own language package or edit a community one, with AI-assisted translation.":
      "Buat paket bahasa Anda sendiri atau ubah paket komunitas, dengan terjemahan berbantuan AI.",

    // About / Footer
    "Copyright © Perana Sports {year}. All rights reserved.":
      "Hak Cipta © Perana Sports {year}. Seluruh hak dilindungi.",
    "VBLive is free to use right now. However Perana Sports reserves the right to charge a subscription fee in due course.":
      "VBLive gratis digunakan saat ini. Namun Perana Sports berhak mengenakan biaya langganan pada waktunya.",
    "Querries / suggestions are most welcomed.":
      "Pertanyaan / saran sangat kami terima.",

    // Live
    "Please enter server name to search":
      "Silakan masukkan nama server untuk mencari",
    "Broadcast Server": "Server Siaran",
    "Enter DVMateLive Server Name": "Masukkan Nama Server DVMateLive",
    LIVE: "LANGSUNG",

    // Playlists list
    "Delete Playlist": "Hapus Daftar Putar",
    "Are you sure you want to delete the selected playlist?":
      "Anda yakin ingin menghapus daftar putar yang dipilih?",
    "My Playlists": "Daftar Putar Saya",
    "Shared Playlists": "Daftar Putar Dibagikan",
    "Tags:": "Tag:",
    "Select...": "Pilih…",
    "All Tags": "Semua Tag",

    // Share
    "User already added": "Pengguna sudah ditambahkan",
    "Please enter a valid email address":
      "Silakan masukkan alamat email yang valid",
    "Please select at least one user to share with":
      "Silakan pilih setidaknya satu pengguna untuk dibagikan",
    "Not Shared": "Tidak Dibagikan",
    "Available for everyone": "Tersedia untuk semua orang",
    "Available to selected users":
      "Tersedia untuk pengguna terpilih",
    "Enter VBLive user email address":
      "Masukkan alamat email pengguna VBLive",
    "Selected users:": "Pengguna terpilih:",
    "Save List": "Simpan Daftar",
    "Clear List": "Bersihkan Daftar",
    "Clear Users List": "Bersihkan Daftar Pengguna",
    "Do you really want to clear all users from list?":
      "Anda benar-benar ingin menghapus semua pengguna dari daftar?",
    "Do you want to save changes before closing?":
      "Apakah Anda ingin menyimpan perubahan sebelum menutup?",
    "Enter name to save list to:":
      "Masukkan nama untuk menyimpan daftar:",
    "Enter name to save list to":
      "Masukkan nama untuk menyimpan daftar",

    // AI panels
    "AI settings": "Pengaturan AI",
    "Setup needed": "Perlu penyiapan",
    "Generate report": "Buat laporan",
    "Generate AI report": "Buat laporan AI",
    "Generate general report": "Buat laporan umum",
    "Translate to…": "Terjemahkan ke…",
    "Follow-up questions": "Pertanyaan lanjutan",
    "Ask a follow-up question…": "Ajukan pertanyaan lanjutan…",
    "Ask a follow-up question": "Ajukan pertanyaan lanjutan",
    "AI provider": "Penyedia AI",
    Model: "Model",
    Hide: "Sembunyikan",
    Show: "Tampilkan",
    "Your key is stored only in this browser and sent directly to the selected provider.":
      "Kunci Anda hanya disimpan di peramban ini dan dikirim langsung ke penyedia yang dipilih.",
    "Please enter your API key.": "Silakan masukkan kunci API Anda.",
    "Not enough data to analyse.":
      "Data tidak cukup untuk dianalisis.",
    "Select a player with recorded spikes first.":
      "Pilih pemain dengan smes tercatat terlebih dahulu.",
    Player: "Pemain",
    "Select a player…": "Pilih pemain…",
    "No players with recorded spikes in the selected matches.":
      "Tidak ada pemain dengan smes tercatat dalam pertandingan yang dipilih.",
    "Analyse with AI": "Analisis dengan AI",
    "AI scouting report": "Laporan pemantauan AI",
    "Q:": "T:",
    Ask: "Tanya",
    "e.g. Which rotation should we target on serve?":
      "mis. Rotasi mana yang harus kita sasar saat servis?",
    "Saved questions:": "Pertanyaan tersimpan:",
    "Click to ask again": "Klik untuk bertanya lagi",
    "Remove saved question": "Hapus pertanyaan tersimpan",

    // AI language options
    Spanish: "Spanyol",
    French: "Prancis",
    German: "Jerman",
    Italian: "Italia",
    Portuguese: "Portugis",
    Dutch: "Belanda",
    Polish: "Polandia",
    Russian: "Rusia",
    Turkish: "Turki",
    Arabic: "Arab",
    "Chinese (Simplified)": "Tionghoa (Sederhana)",
    Japanese: "Jepang",
    Korean: "Korea",
    Vietnamese: "Vietnam",
    Thai: "Thailand",

    // Box score
    "Show Full Headers": "Tampilkan Judul Lengkap",
    "Show Short Headers": "Tampilkan Judul Singkat",
    "Show Less Players": "Tampilkan Lebih Sedikit Pemain",
    "Show All Players": "Tampilkan Semua Pemain",
    "Attack Kills": "Poin Serangan",
    "Attack Errors": "Kesalahan Serangan",
    "Total Attempts": "Total Percobaan",
    "Hitting Efficiency": "Efisiensi Serangan",
    "Serve Aces": "Ace Servis",
    "Serve Errors": "Kesalahan Servis",
    "Pass Errors": "Kesalahan Terima",
    "% Perfect Passes": "% Terima Sempurna",
    "Passing Average": "Rata-rata Terima",
    "Total Passes": "Total Terima",
    "Set Assists": "Asis Umpan",
    Digs: "Gali",
    "Momentum Chart": "Grafik Momentum",
    "Key event": "Aksi kunci",
    "Break Point %": "% Break Point",
    "First Ball Sideout %": "% Side Out Bola Pertama",
    "Sideout Transition %": "% Side Out Transisi",
    "FB Sideout Kills %": "% Serangan Poin Bola Pertama",
    "Sideout Opposition Error %": "% Side Out dari Kesalahan Lawan",
    Plus: "Plus",
    Minus: "Minus",
    Blocks: "Poin Blok",
    "Positive Serve %": "% Servis Positif",
    Sideout: "Side Out",
    "Break point": "Break Point",
    "Ace / Kill": "Ace / Serangan Poin",
    "In play": "Sedang Bermain",
    Error: "Kesalahan",
    "Sideout on a kill": "Side Out dari Serangan Poin",
    "Sideout on an error": "Side Out dari Kesalahan Lawan",
    "Top half": "Separuh Atas",
    "Bottom half": "Separuh Bawah",
    "The centre line steps toward the team that wins each point.": "Garis tengah bergerak ke arah tim yang memenangkan setiap poin.",
    "No point-by-point data available for this match.": "Tidak ada data poin demi poin untuk pertandingan ini.",
    "No match data available.": "Tidak ada data pertandingan.",
    "This report isn't supported on this browser yet — please use Safari.": "Laporan ini belum didukung di browser ini — silakan gunakan Safari.",
    "No video available for this match.": "Tidak ada video untuk pertandingan ini.",
    passes: "Terima",
    "eff.": "eff.",
    Grade: "Nilai",
    LEFT: "KIRI",
    CENTRE: "TENGAH",
    RIGHT: "KANAN",
    SELECTED: "TERPILIH",
    Passers: "Penerima",
    Calls: "Panggilan",
    "SIDE OUT": "SIDE OUT",
    TRANSITION: "TRANSISI",
    "No events in selection.": "Tidak ada peristiwa dalam pilihan saat ini.",
    "Serve Receives By Players": "Terima Servis per Pemain",
    "Serve Receive By Zones": "Terima Servis per Zona",
    "Dig Errors": "Kesalahan Gali",
    "Dig Average": "Rata-rata Gali",
    "Block Solos": "Blok Tunggal",
    "Block Assists": "Blok Ganda",
    "Block Errors": "Kesalahan Blok",
    "Points Scored": "Poin Diraih",
    TEAM: "TIM",

    // Sideout report
    "ALL ROWS": "SEMUA BARIS",
    "ROWS ": "BARIS ",
    "ROW ": "BARIS ",
    "FB Kills": "Poin Serangan FB",
    "Opp. Serve Errors": "Kesalahan Servis Lawan",
    "FB Opp. Errors": "Kesalahan Lawan FB",
    "Kills/Blocks": "Serangan/Blok",
    "Opp. Errors": "Kesalahan Lawan",
    Unsuccessful: "Gagal",

    // Attack zones
    "No events in selected zone.":
      "Tidak ada peristiwa di zona yang dipilih.",
    "Show all attacks. Or click on a zone to show attacks in that zone.":
      "Tampilkan semua serangan. Atau klik pada zona untuk menampilkan serangan di zona tersebut.",
    "OPPONENTS (": "LAWAN (",

    // Video analysis filters
    "All Types": "Semua Jenis",
    Serve: "Servis",
    "Serve-Receive": "Terima Servis",
    Set: "Umpan",
    "Setter's Call": "Panggilan Pengumpan",
    "Disable this skill": "Nonaktifkan keterampilan ini",
    "Enable this skill": "Aktifkan keterampilan ini",
    "Teams & Players": "Tim & Pemain",
    "Match State": "Status Pertandingan",
    "Custom Code": "Kode Kustom",
    "Search... (comma-separated for multiple)": "Cari... (pisahkan dengan koma untuk beberapa)",
    vs: "vs",
    "Rewind 60 seconds": "Mundur 60 detik",
    "Rewind 1 frame": "Mundur 1 frame",
    "Forward 1 frame": "Maju 1 frame",
    "Forward 60 seconds": "Maju 60 detik",
    "Previous Clip": "Klip Sebelumnya",
    Replay: "Putar Ulang",
    "Next Clip": "Klip Berikutnya",
    Phases: "Fase",
    "Score Range": "Rentang Skor",
    "Set Winner": "Pemenang Set",
    Type: "Jenis",
    Strength: "Kekuatan",
    "Setter Calls": "Panggilan Pengumpan",
    "Setter Call": "Panggilan Pengumpan",
    Freeball: "Bola bebas",
    "Close (0-2)": "Ketat (0-2)",
    "Moderate (3-5)": "Sedang (3-5)",
    "Lopsided (6+)": "Selisih Besar (6+)",
    "Focus Team": "Tim Fokus",
    Oppositions: "Lawan",
    "Focus Team Won": "Tim Fokus Menang",
    "Oppositions Won": "Lawan Menang",
    "Advanced Filters": "Filter Lanjutan",
    Players: "Pemain",
    Opponents: "Lawan",
    "Event Type": "Jenis Peristiwa",
    "Event Result": "Hasil Peristiwa",
    "Attack Type": "Jenis Serangan",
    "Hit Type": "Jenis Pukulan",
    "Block Type": "Jenis Blok",
    "Attack Combo": "Kombo Serangan",
    Apply: "Terapkan",
    Rotation: "Rotasi",
    Skill: "Keterampilan",
    "Lead Time": "Waktu Awalan",
    Duration: "Durasi",
    Timing: "Pengaturan Waktu",
    "Go Live": "Ke Siaran Langsung",
    "Playback has fallen behind the live broadcast — click to jump to now.": "Pemutaran tertinggal dari siaran langsung — klik untuk lompat ke sekarang.",
    "Video timing": "Pengaturan waktu video",
    "Default lead time (seconds before an event to start playback) and clip duration, per skill.": "Waktu awalan bawaan (detik sebelum peristiwa untuk memulai pemutaran) dan durasi klip, per keterampilan.",
    Cloud: "Awan",
    Local: "Lokal",
    "Relay host": "Host relai",
    "IP or hostname of the machine running the local relay.": "IP atau nama host mesin yang menjalankan relai lokal.",
    Spike: "Smes",
    Block: "Blok",
    Defence: "Bertahan",
    "All Results": "Semua Hasil",
    Hard: "Keras",
    Soft: "Lembut",
    Tip: "Tip",
    "Not Specified": "Tidak ditentukan",
    "H - High": "H - Tinggi",
    "M- Medium": "M - Sedang",
    "Q - Quick": "Q - Cepat",
    "T - Tense": "T - Menegang",
    "U - Super": "U - Super",
    "F - Fast": "F - Kilat",
    "O - Other": "O - Lainnya",
    "B0 - No Block": "B0 - Tanpa Blok",
    "B1 - 1 Block": "B1 - 1 Blok",
    "B2 - 2 Blocks": "B2 - 2 Blok",
    "B3 - 3 Blocks": "B3 - 3 Blok",
    "BS - Seams": "BS - Celah",

    // All filters panel
    ALL: "SEMUA",
    NONE: "TIDAK ADA",

    // Playlist editor
    "Remove Item": "Hapus Item",
    "Do you really want to remove the selected item?":
      "Anda benar-benar ingin menghapus item yang dipilih?",
    "Description is required": "Deskripsi wajib diisi",
    "Comments are required": "Komentar wajib diisi",
    "Tags are required": "Tag wajib diisi",
    "Playlist saved successfully": "Daftar putar berhasil disimpan",
    "Error saving playlist": "Kesalahan menyimpan daftar putar",

    // Language editor
    "My saved packages": "Paket tersimpan saya",
    "Loading shared languages…": "Memuat bahasa yang dibagikan…",
    "Community shared languages": "Bahasa yang dibagikan komunitas",
    by: "oleh",
    "based on": "berdasarkan",
    "or create new": "atau buat baru",
    Use: "Gunakan",
    "New Language Package": "Paket Bahasa Baru",
    "Language Name": "Nama Bahasa",
    "Language Code": "Kode Bahasa",
    "Flag Emoji": "Emoji Bendera",
    "Base language (reference)": "Bahasa dasar (referensi)",
    "If not English, both English and the reference language will be shown.":
      "Jika bukan bahasa Inggris, baik bahasa Inggris maupun bahasa referensi akan ditampilkan.",
    "Start Editing": "Mulai Mengubah",
    "Save Key": "Simpan Kunci",
    "AI key set": "Kunci AI diatur",
    "Set API key for AI translation":
      "Atur kunci API untuk terjemahan AI",
    "AI key ✓": "Kunci AI ✓",
    "Set AI key": "Atur kunci AI",
    "AI: Translate All": "AI: Terjemahkan Semua",
    "Translating…": "Menerjemahkan…",
    "AI translate section": "Terjemahkan bagian dengan AI",
    "Clear All": "Bersihkan Semua",
    "Saving…": "Menyimpan…",
    English: "Inggris",
    new: "baru",
    "Enter translation…": "Masukkan terjemahan…",
    "Share with other VBLive users?":
      "Bagikan dengan pengguna VBLive lain?",
    "Would you like to share the": "Apakah Anda ingin membagikan",
    "language package with all VBLive users? Anyone can then copy and build upon it.":
      "paket bahasa dengan semua pengguna VBLive? Siapa pun kemudian dapat menyalin dan mengembangkannya.",
    "No, just save for me": "Tidak, simpan untuk saya saja",
    "Yes, share it!": "Ya, bagikan!",
    "Language package saved and shared with all VBLive users!":
      "Paket bahasa disimpan dan dibagikan dengan semua pengguna VBLive!",
    "Language package saved!": "Paket bahasa disimpan!",
    "Clear all translations? This cannot be undone.":
      "Bersihkan semua terjemahan? Tindakan ini tidak dapat dibatalkan.",

    // Additional UI phrases
    Teams: "Tim",
    "Shared By": "Dibagikan oleh",
    "View Activity": "Aktivitas Tampilan",
    Viewer: "Penonton",
    "Date / Time": "Tanggal / Waktu",
    "No views recorded yet.": "Belum ada tampilan yang tercatat.",
    "Date / Scores": "Tanggal / Skor",
    "Stats API source": "Sumber API Statistik",
    "load one or more AVC VIS files": "muat satu atau lebih berkas AVC VIS",
    "Points to close a set": "Poin untuk menutup set",
    "Require 2-point advantage": "Wajibkan selisih 2 poin",
    Roster: "Daftar Pemain",
    Clone: "Duplikat",
    "Edit team": "Edit Tim",
    "New player": "Pemain Baru",
    "Search player...": "Cari pemain…",
    "Match Centre": "Pusat Pertandingan",
    "Points Won": "Poin Dimenangkan",
    "Opp. Error": "Kesalahan Lawan",
    Total: "Total",
    "Match Skills": "Keterampilan Pertandingan",
    Dig: "Pertahanan",
    "Best Scorers": "Pencetak Poin Terbaik",
    "Power Play": "Power Play",
    Match: "Pertandingan",
    "Player scouting": "Pengintaian Pemain",
    "Team deep analysis": "Analisis Mendalam Tim",
    "All Players": "Semua Pemain",
    "All Sets": "Semua Set",
    "All Rotations": "Semua Rotasi",
    "All Combos": "Semua Kombinasi",
    "All Calls": "Semua Panggilan",
    "All Events": "Semua Peristiwa",
    "Select all items": "Pilih semua item",
    "Unselect all items": "Batalkan pilihan semua item",
    "Create play list": "Buat daftar putar",
    "Point by Point": "Poin demi Poin",
    // i18n backfill pass — previously English-fallback-only keys
    "Access denied": "Akses ditolak",
    "Aces": "Ace",
    "ACES": "ACE",
    "Add report": "Tambah laporan",
    "Admin": "Admin",
    "Admins": "Admin",
    "Advanced": "Lanjutan",
    "All set!": "Semua siap!",
    "Analyse team with AI": "Analisis tim dengan AI",
    "Are you sure you want to delete the selected play list?": "Yakin ingin menghapus daftar putar yang dipilih?",
    "Assign": "Tetapkan",
    "Assign playlist": "Tetapkan daftar putar",
    "Assigned": "Ditetapkan",
    "At least one admin is required.": "Diperlukan setidaknya satu admin.",
    "Attack Combo / Setter Call": "Kombinasi serangan / Panggilan set-upper",
    "Attack diagram": "Diagram serangan",
    "Attacks": "Serangan",
    "ATTACKS": "SERANGAN",
    "Auto-detected a custom set-closing score for": "Secara otomatis mendeteksi skor penutup set khusus untuk",
    "BLOCK": "BLOK",
    "BLOCKS": "BLOK",
    "Beta testers": "Penguji beta",
    "Clear filter": "Hapus filter",
    "Close Set": "Tutup set",
    "Close menu": "Tutup menu",
    "Code": "Kode",
    "Coding defaults & shortcuts": "Default & pintasan pengkodean",
    "Continue": "Lanjutkan",
    "Could not load match data. The match may no longer be available.": "Tidak dapat memuat data pertandingan. Pertandingan mungkin sudah tidak tersedia.",
    "Could not load stats for these matches.": "Tidak dapat memuat statistik untuk pertandingan ini.",
    "Could not load this match.": "Tidak dapat memuat pertandingan ini.",
    "Could not load this playlist.": "Tidak dapat memuat daftar putar ini.",
    "Could not load this report.": "Tidak dapat memuat laporan ini.",
    "Could not save the report.": "Tidak dapat menyimpan laporan.",
    "Could not share the report. Please try again.": "Tidak dapat membagikan laporan. Silakan coba lagi.",
    "Court horizontal (net vertical)": "Lapangan horizontal (net vertikal)",
    "Court vertical (net horizontal)": "Lapangan vertikal (net horizontal)",
    "Create": "Buat",
    "Create a new team from this roster": "Buat tim baru dari daftar pemain ini",
    "DIFFERENCE": "SELISIH",
    "DV Code": "Kode DV",
    "Delete this report?": "Hapus laporan ini?",
    "Detected a custom set-closing score of": "Mendeteksi skor penutup set khusus sebesar",
    "Double Sub": "Substitusi ganda",
    "Double Sub: now pick a bench player for the flashing OPPOSITE slot…": "Substitusi ganda: sekarang pilih pemain cadangan untuk slot OPPOSITE yang berkedip…",
    "Double Sub: pick a bench player for the flashing SETTER slot…": "Substitusi ganda: pilih pemain cadangan untuk slot SETTER yang berkedip…",
    "Double Substitution — swap the Setter and Opposite for two incoming players": "Substitusi ganda — tukar Setter dan Opposite dengan dua pemain pengganti",
    "Drag to draw the ball path; pause briefly to mark a touch (reception/dig/block), release to finish.": "Seret untuk menggambar jalur bola; jeda sebentar untuk menandai sentuhan (reception/dig/block), lepaskan untuk selesai.",
    "Draw": "Gambar",
    "Draw & Code": "Gambar & Kode",
    "Draw & Code — draw ball paths on the court to code the rally": "Gambar & Kode — gambar jalur bola di lapangan untuk mengkode reli",
    "Edit Lineup": "Edit susunan pemain",
    "Edit Lineup — freely rearrange the starting lineup before the set begins": "Edit susunan pemain — atur ulang susunan awal secara bebas sebelum set dimulai",
    "Emails in this list can manage beta testers and other admins.": "Email dalam daftar ini dapat mengelola penguji beta dan admin lainnya.",
    "Emails in this list get early access to AI Reports and other gated features. Changes take effect immediately.": "Email dalam daftar ini mendapatkan akses awal ke Laporan AI dan fitur terbatas lainnya. Perubahan berlaku segera.",
    "Enter a valid email address.": "Masukkan alamat email yang valid.",
    "Exit fullscreen": "Keluar layar penuh",
    "Extended": "Diperluas",
    "Failed to generate PDF.": "Gagal membuat PDF.",
    "Failed to save changes.": "Gagal menyimpan perubahan.",
    "Filtered by": "Difilter berdasarkan",
    "First name": "Nama depan",
    "For stronger security later, consider moving admin checks to a Firebase Auth custom claim instead of this email list.": "Untuk keamanan yang lebih kuat nanti, pertimbangkan memindahkan pemeriksaan admin ke custom claim Firebase Auth alih-alih daftar email ini.",
    "Fullscreen": "Layar penuh",
    "Generate an AI report from these matches": "Buat laporan AI dari pertandingan ini",
    "How often (in seconds) the events list is updated during a live session.": "Seberapa sering (dalam detik) daftar peristiwa diperbarui selama sesi langsung.",
    "Last name": "Nama belakang",
    "Leave blank to use standard rules (25 points, 15 for a 5th/deciding set)": "Biarkan kosong untuk menggunakan aturan standar (25 poin, 15 untuk set ke-5/penentu)",
    "Live session refresh interval": "Interval penyegaran sesi langsung",
    "Load VIS file(s)...": "Muat file VIS…",
    "Load this team's last-saved starting lineup (positions, setter and liberos)": "Muat susunan awal tim yang terakhir disimpan (posisi, setter, dan libero)",
    "Loading…": "Memuat…",
    "Manually end the current set at its current score, regardless of the configured winning score / 2-point advantage rule": "Akhiri set saat ini secara manual pada skor saat ini, terlepas dari skor kemenangan yang dikonfigurasi / aturan keunggulan 2 poin",
    "Migration finished with some issues": "Migrasi selesai dengan beberapa masalah",
    "Most of your data was migrated, but some items could not be moved. You can retry now or continue and try again later.": "Sebagian besar data Anda telah dimigrasikan, tetapi beberapa item tidak dapat dipindahkan. Anda dapat mencoba lagi sekarang atau melanjutkan dan mencoba lagi nanti.",
    "Mute": "Bisukan",
    "New team": "Tim baru",
    "Next": "Berikutnya",
    "Nickname": "Nama panggilan",
    "No Power Play": "Tidak ada Power Play",
    "No admins yet.": "Belum ada admin.",
    "No beta testers yet.": "Belum ada penguji beta.",
    "No coded events to analyse.": "Tidak ada peristiwa yang dikode untuk dianalisis.",
    "No coded events to export.": "Tidak ada peristiwa yang dikode untuk diekspor.",
    "No matches.": "Tidak ada pertandingan.",
    "No players added yet.": "Belum ada pemain yang ditambahkan.",
    "No players recorded.": "Tidak ada pemain yang tercatat.",
    "No players yet.": "Belum ada pemain.",
    "No playlists assigned.": "Tidak ada daftar putar yang ditetapkan.",
    "No playlists found.": "Tidak ditemukan daftar putar.",
    "No reports saved.": "Tidak ada laporan yang disimpan.",
    "No roster players available.": "Tidak ada pemain daftar yang tersedia.",
    "No substitutions": "Tidak ada substitusi",
    "No teams found.": "Tidak ditemukan tim.",
    "No teams yet.": "Belum ada tim.",
    "No teams.": "Tidak ada tim.",
    "Nominate Setter": "Tunjuk set-upper",
    "Notify these users by email (optional)": "Beri tahu pengguna ini melalui email (opsional)",
    "OPP REC": "RESEPSI LAWAN",
    "OPP. ERRORS": "KESALAHAN LAWAN",
    "Open / Download": "Buka / Unduh",
    "Open local file": "Buka file lokal",
    "Open menu": "Buka menu",
    "Opposition Passing — use the opposition's reception formation on the diagram": "Reception lawan — gunakan formasi reception lawan pada diagram",
    "Paste the report (Markdown supported)…": "Tempel laporan (mendukung Markdown)…",
    "Pause": "Jeda",
    "Play": "Putar",
    "Playing squad": "Skuad pertandingan",
    "Playing squad is full. Remove a player from the squad before adding another.": "Skuad pertandingan sudah penuh. Hapus satu pemain dari skuad sebelum menambahkan yang lain.",
    "Please sign in to save reports.": "Silakan masuk untuk menyimpan laporan.",
    "Power Play Only": "Hanya Power Play",
    "Record a team error (no player) — edit later to add the player and event type": "Catat kesalahan tim (tanpa pemain) — edit nanti untuk menambahkan pemain dan jenis peristiwa",
    "Record at least one event before making substitutions": "Catat setidaknya satu peristiwa sebelum melakukan substitusi",
    "Redo": "Ulangi",
    "Remove": "Hapus",
    "Remove share": "Hapus berbagi",
    "Remove yourself as admin? You will immediately lose access to this page.": "Hapus diri Anda sebagai admin? Anda akan segera kehilangan akses ke halaman ini.",
    "Report saved and shared.": "Laporan disimpan dan dibagikan.",
    "Report saved to the player's profile.": "Laporan disimpan ke profil pemain.",
    "Report title": "Judul laporan",
    "Reports": "Laporan",
    "Result": "Hasil",
    "Retry": "Coba lagi",
    "Return to live coding": "Kembali ke pengkodean langsung",
    "Rotation is not permitted while making substitutions": "Rotasi tidak diizinkan saat melakukan substitusi",
    "SERVE": "SERVIS",
    "SETS": "SET",
    "Save & Share": "Simpan & Bagikan",
    "Save this report so you can share it with other users": "Simpan laporan ini agar Anda dapat membagikannya dengan pengguna lain",
    "Save this report to the player's profile": "Simpan laporan ini ke profil pemain",
    "Save to player": "Simpan ke pemain",
    "Saved lineup": "Susunan pemain tersimpan",
    "Scroll, drag or swipe to scrub": "Gulir, seret, atau geser untuk mencari posisi",
    "Search existing players...": "Cari pemain yang ada…",
    "Search playlists…": "Cari daftar putar…",
    "Select a match video to view while entering lineups.": "Pilih video pertandingan untuk ditonton saat memasukkan susunan pemain.",
    "Select a player.": "Pilih pemain.",
    "Select a team.": "Pilih tim.",
    "Setting up your account": "Menyiapkan akun Anda",
    "Shaded rows are played under a Power Play — HP/AP marks the calling team, bold gold scores are bonus (double) points.": "Baris yang diberi warna dimainkan di bawah Power Play — HP/AP menandai tim yang memanggil, skor emas tebal adalah poin bonus (ganda).",
    "Shared by": "Dibagikan oleh",
    "Shared with me": "Dibagikan kepada saya",
    "Show help": "Tampilkan bantuan",
    "Show public playlists": "Tampilkan daftar putar publik",
    "Standard": "Standar",
    "Substitutions made — tap to view": "Substitusi dilakukan — ketuk untuk melihat",
    "Tap the blocking player.": "Ketuk pemain yang melakukan blok.",
    "Team colour": "Warna tim",
    "Team name": "Nama tim",
    "That email is already in the list.": "Email tersebut sudah ada di daftar.",
    "The report is too large to save. Try a single match or fewer sets.": "Laporan terlalu besar untuk disimpan. Coba satu pertandingan atau lebih sedikit set.",
    "This match has no Power Play calls.": "Pertandingan ini tidak memiliki panggilan Power Play.",
    "This player has no name to save the report against.": "Pemain ini tidak memiliki nama untuk menyimpan laporan.",
    "This team has no designated setter. Pick the player who is setting.": "Tim ini tidak memiliki setter yang ditunjuk. Pilih pemain yang sedang menjadi setter.",
    "Toggle video player": "Alihkan pemutar video",
    "Unmute": "Aktifkan suara",
    "Untitled playlist": "Daftar putar tanpa judul",
    "Video URL…": "URL video…",
    "We're moving your data to a faster, more reliable home. This happens only once.": "Kami memindahkan data Anda ke tempat yang lebih cepat dan andal. Ini hanya terjadi sekali.",
    "You don't have permission to view this page.": "Anda tidak memiliki izin untuk melihat halaman ini.",
    "Your data has been migrated successfully.": "Data Anda berhasil dimigrasikan.",
    "Your teams and players will appear here after your data is migrated.": "Tim dan pemain Anda akan muncul di sini setelah data Anda dimigrasikan.",
    "bonus point": "poin bonus",
    "bonus points": "poin bonus",
    "event": "peristiwa",
    "events": "peristiwa",
    "file(s).": "berkas.",
    "is not on this team's roster.": "tidak ada di daftar pemain tim ini.",
    "loaded": "dimuat",
    "matches": "pertandingan",
    "more": "lainnya",
    "players": "pemain",
    "points (no value was entered) — applied automatically.": "poin (tidak ada nilai yang dimasukkan) — diterapkan secara otomatis.",
    "roster": "daftar pemain",
    "seconds": "detik",
    "selected": "dipilih",
    "session(s)?": "sesi?",
    "showing": "menampilkan",
    "tournaments": "turnamen",
    "…and": "…dan",
    "● marks which team served the point.": "● menandai tim mana yang melakukan servis pada poin tersebut.",
  },
};

/**
 * Sections that group the translatable UI strings for the editor.
 * Every key here is an English string that appears in the app.
 */
for (const [language, phrases] of Object.entries(vssTranslations)) {
  Object.assign(translations[language], phrases);
}

export const TRANSLATION_SECTIONS = [
  { section: "VolleyStation Reports", keys: VSS_TRANSLATION_KEYS },
  {
    section: "Navigation",
    keys: [
      "Matches",
      "Live",
      "Import",
      "Playlists",
      "New Match",
      "Settings",
      "Language",
      "Your profile",
      "Sign out",
    ],
  },
  {
    section: "Common Actions",
    keys: [
      "Save",
      "Cancel",
      "Delete",
      "Edit",
      "Share",
      "New",
      "Refresh",
      "Search",
      "Reset",
      "Close",
      "Back",
      "Download PDF",
      "Loading...",
      "Select All",
      "Unselect All",
      "Yes",
      "No",
      "Add",
      "Load",
      "Confirm",
      "show",
    ],
  },
  {
    section: "Auth",
    keys: [
      "Sign In",
      "Sign Up",
      "Sign Out",
      "Email",
      "Password",
      "Confirm Password",
      "Forgot password?",
      "Forgot Password",
      "Don't have an account?",
      "Sign up for free!",
      "All ready signed up?",
      "Error Signing In",
      "Signed up successfully",
      "Error signing up",
      "Enter your email",
      "Send Reset Email",
      "Email was sent to",
      "Could not send reset email",
    ],
  },
  {
    section: "Matches List",
    keys: [
      "Team 1",
      "Team 2",
      "Tournament",
      "Scores",
      "Date",
      "Actions",
      "Owner",
      "Description",
      "My Matches",
      "Shared Matches",
      "Search team...",
      "All Teams",
      "Multi Match Reports",
      "Delete match",
      "Share match",
      "Edit match",
      "Open video analysis",
      "Delete Match",
      "Are you sure you want to delete the selected match?",
      "No matches selected.",
    ],
  },
  {
    section: "New Match",
    keys: [
      "New Match",
      "← Back",
      "Team A (Home)",
      "Team B (Away)",
      "Select team…",
      "Tournament (optional)",
      "Tournament / competition name",
      "Match date",
      "Start Coding →",
    ],
  },
  {
    section: "Reports",
    keys: [
      "Summary",
      "Box Score",
      "Side-outs Report",
      "Serve Receives",
      "Serve Receives By Players",
      "Serve Receive By Zones",
      "Attack Zones",
      "Hitting Charts",
      "Video Analysis",
      "Dashboard",
      "Charts",
      "AI Report",
      "Report",
    ],
  },
  {
    section: "Import Page",
    keys: [
      "Load DVW file(s)...",
      "Load PSVB file(s)...",
      "Load VSM file(s)...",
      "Load playlist file...",
      "load one or more DataVolley files",
      "load one or more VBStats files",
      "load one or more VolleyStation VSM files",
      "load a playlist",
      "Use Shift-JIS Encoder",
      "Importing…",
      "Save to Database?",
      "Always save imports automatically",
      "View Only",
      "Save to Database",
      "Error saving to database",
      "Saved to database",
      "Saving to database…",
      "Upload them to the database now?",
    ],
  },
  {
    section: "Settings",
    keys: [
      "Theme",
      "Options",
      "Rotation naming convention",
      "Choose how rotations are labelled in reports and analysis.",
      "R1–R6 (rotation sequence)",
      "S1–S6 (setter court position)",
      "Labels:",
      "Current theme:",
      "Choose the language for the app interface.",
      "Create your own language package or edit a community one, with AI-assisted translation.",
    ],
  },
  {
    section: "About",
    keys: [
      "Copyright © Perana Sports {year}. All rights reserved.",
      "VBLive is free to use right now. However Perana Sports reserves the right to charge a subscription fee in due course.",
      "Querries / suggestions are most welcomed.",
    ],
  },
  {
    section: "Live",
    keys: [
      "Please enter server name to search",
      "Broadcast Server",
      "Enter DVMateLive Server Name",
      "LIVE",
    ],
  },
  {
    section: "Playlists",
    keys: [
      "Delete Playlist",
      "Are you sure you want to delete the selected playlist?",
      "My Playlists",
      "Shared Playlists",
      "Tags:",
      "Select...",
      "All Tags",
      "Remove Item",
      "Do you really want to remove the selected item?",
      "Description is required",
      "Comments are required",
      "Tags are required",
      "Playlist saved successfully",
      "Error saving playlist",
    ],
  },
  {
    section: "Share",
    keys: [
      "User already added",
      "Please enter a valid email address",
      "Please select at least one user to share with",
      "Not Shared",
      "Available for everyone",
      "Available to selected users",
      "Enter VBLive user email address",
      "Selected users:",
      "Save List",
      "Clear List",
      "Clear Users List",
      "Do you really want to clear all users from list?",
      "Do you want to save changes before closing?",
      "Enter name to save list to:",
      "Enter name to save list to",
    ],
  },
  {
    section: "AI Reports",
    keys: [
      "AI settings",
      "Setup needed",
      "Generate report",
      "Generate AI report",
      "Translate to…",
      "Follow-up questions",
      "Ask a follow-up question…",
      "Ask a follow-up question",
      "AI provider",
      "Model",
      "Hide",
      "Show",
      "Your key is stored only in this browser and sent directly to the selected provider.",
      "Please enter your API key.",
      "Not enough data to analyse.",
      "Select a player with recorded spikes first.",
      "Player",
      "Select a player…",
      "No players with recorded spikes in the selected matches.",
      "Analyse with AI",
      "AI scouting report",
      "Q:",
      "Ask",
      "e.g. Which rotation should we target on serve?",
      "Saved questions:",
      "Click to ask again",
      "Remove saved question",
      "Spanish",
      "French",
      "German",
      "Italian",
      "Portuguese",
      "Dutch",
      "Polish",
      "Russian",
      "Turkish",
      "Arabic",
      "Chinese (Simplified)",
      "Japanese",
      "Korean",
      "Vietnamese",
      "Thai",
    ],
  },
  {
    section: "Box Score",
    keys: [
      "Show Full Headers",
      "Show Short Headers",
      "Show Less Players",
      "Show All Players",
      "Attack Kills",
      "Attack Errors",
      "Total Attempts",
      "Hitting Efficiency",
      "Serve Aces",
      "Serve Errors",
      "Pass Errors",
      "% Perfect Passes",
      "Passing Average",
      "Total Passes",
      "Set Assists",
      "Digs",
      "Dig Errors",
      "Dig Average",
      "Block Solos",
      "Block Assists",
      "Block Errors",
      "Points Scored",
      "TEAM",
    ],
  },
  {
    section: "Sideouts & Zones",
    keys: [
      "ALL ROWS",
      "ROWS ",
      "ROW ",
      "FB Kills",
      "Opp. Serve Errors",
      "FB Opp. Errors",
      "Kills/Blocks",
      "Opp. Errors",
      "Unsuccessful",
      "No events in selected zone.",
      "No events in selection.",
      "Show all attacks. Or click on a zone to show attacks in that zone.",
      "OPPONENTS (",
      "Grade",
      "LEFT",
      "CENTRE",
      "RIGHT",
      "SELECTED",
      "Passers",
      "Calls",
      "SIDE OUT",
      "TRANSITION",
      "passes",
      "eff.",
      "Reset",
    ],
  },
  {
    section: "Video Filters",
    keys: [
      "All Types",
      "Serve",
      "Serve-Receive",
      "Set",
      "Setter's Call",
      "Spike",
      "Block",
      "Defence",
      "All Results",
      "Hard",
      "Soft",
      "Tip",
      "Not Specified",
      "H - High",
      "M- Medium",
      "Q - Quick",
      "T - Tense",
      "U - Super",
      "F - Fast",
      "O - Other",
      "B0 - No Block",
      "B1 - 1 Block",
      "B2 - 2 Blocks",
      "B3 - 3 Blocks",
      "BS - Seams",
      "ALL",
      "NONE",
    ],
  },
  {
    section: "Language Editor",
    keys: [
      "Translation Editor",
      "My saved packages",
      "Loading shared languages…",
      "Community shared languages",
      "by",
      "based on",
      "or create new",
      "Use",
      "New Language Package",
      "Language Name",
      "Language Code",
      "Flag Emoji",
      "Base language (reference)",
      "If not English, both English and the reference language will be shown.",
      "Start Editing",
      "← Back",
      "Save Key",
      "AI key set",
      "Set API key for AI translation",
      "AI key ✓",
      "Set AI key",
      "AI: Translate All",
      "Translating…",
      "AI translate section",
      "Clear All",
      "Saving…",
      "English",
      "new",
      "New",
      "Enter translation…",
      "Share with other VBLive users?",
      "Would you like to share the",
      "language package with all VBLive users? Anyone can then copy and build upon it.",
      "No, just save for me",
      "Yes, share it!",
      "Language package saved and shared with all VBLive users!",
      "Language package saved!",
      "Clear all translations? This cannot be undone.",
    ],
    // i18n backfill pass (Catapult + misc)
    "Centimetres (cm)": "Sentimeter (cm)",
    "Inches (in)": "Inci (in)",
    "Jump height units": "Satuan tinggi lompatan",
    "Choose how Catapult jump heights are displayed and filtered.": "Pilih cara tinggi lompatan Catapult ditampilkan dan disaring.",
    "Select Catapult Focus file...": "Pilih file Catapult Focus…",
    "Select a Catapult Focus CSV export to view jump data for this match.": "Pilih file ekspor CSV Catapult Focus untuk melihat data lompatan pertandingan ini.",
    "Loaded from saved match data": "Dimuat dari data pertandingan yang disimpan",
    "No jump data found in this file.": "Tidak ditemukan data lompatan dalam file ini.",
    "Could not read this file. Please check it is a Catapult Focus CSV export.": "Tidak dapat membaca file ini. Pastikan ini adalah ekspor CSV Catapult Focus.",
    "Could not save jump data with this match.": "Tidak dapat menyimpan data lompatan dengan pertandingan ini.",
    "Jump data saved with this match.": "Data lompatan disimpan dengan pertandingan ini.",
    "Show players with no match stats": "Tampilkan pemain tanpa statistik pertandingan",
    "hidden": "disembunyikan",
    "jumps": "lompatan",
    "periods": "periode",
    "Sync offset": "Offset sinkronisasi",
    "matched": "cocok",
    "match events now show jump height/duration in Video Analysis": "peristiwa pertandingan kini menampilkan tinggi/durasi lompatan di Analisis Video",
    "Team": "Tim",
    "Team A": "Tim A",
    "Team B": "Tim B",
    "Which team is this Catapult file for?": "File Catapult ini untuk tim yang mana?",
    "We couldn't confidently match the players in this Focus file to one team. Please select which team it belongs to.": "Kami tidak dapat mencocokkan pemain dalam file Focus ini dengan satu tim secara pasti. Silakan pilih tim pemilik file ini.",
    "This match has no video attached yet.": "Pertandingan ini belum memiliki video yang terlampir.",
    "No synced jump events found for this player.": "Tidak ditemukan peristiwa lompatan yang disinkronkan untuk pemain ini.",
    "No match loaded.": "Tidak ada pertandingan yang dimuat.",
    "avg": "rata-rata",
    "max": "maks.",
    "Sync": "Sinkronisasi",
    "Jump Report": "Laporan Lompatan",
    "Watch Video": "Tonton Video",
    "Save PDF": "Simpan PDF",
    "Generated by VBLive": "Dibuat oleh VBLive",
    "Deep analysis of {team} from ball-by-ball event data, covering serving, reception, setting, attack, blocking, defence, rotations and opponent tendencies. Best used with the Anthropic (Claude) provider.": "Analisis mendalam {team} dari data peristiwa per reli, mencakup servis, penerimaan, umpan, serangan, blok, pertahanan, rotasi, dan kecenderungan lawan. Paling baik digunakan dengan penyedia Anthropic (Claude).",
    "teams": "tim",
  },
];
